import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

const RESOURCES = {
  en: {
    translation: {
      nav_menu: "Menu",
      nav_book: "Book a table",
      nav_reviews: "Reviews",
      nav_about: "About",
      nav_account: "My account",
      nav_signin: "Sign in",
      cart_open: "Open cart",
      cart_title: "Your order",
      cart_empty: "Your cart is empty",
      cart_subtotal: "Subtotal",
      cart_continue: "Continue",
      cart_pickup_date: "Pickup date",
      cart_pickup_time: "Pickup time",
      cart_pay_on_pickup: "Pay on pickup",
      cart_pay_now: "Pay now",
      cart_place_order: "Place order",
      add_to_order: "Add to order",
      menu_search_ph: "Search the menu…",
      menu_chef_specials: "Chef Specials",
      menu_under_kcal: "Under",
      menu_kcal_unit: "kcal",
      menu_clear_filters: "Clear",
      menu_dietary_label: "Dietary",
      menu_no_matches: "No matches",
      menu_relax_filters: "Try a different search or relax your filters.",
      menu_clear_all: "Clear all filters",
      book_title: "Book a table",
      book_name: "Name",
      book_email: "Email",
      book_phone: "Phone",
      book_guests: "Guests",
      book_date: "Date",
      book_time: "Time",
      book_requests: "Special requests",
      book_submit: "Confirm booking",
      footer_rights: "All rights reserved.",
      lang_label: "Language",
      welcome_back: "Welcome back",
      sign_out: "Sign out",

      // ── Login / Auth flow ───────────────────────────────
      login_heading: "Sign in",
      login_email_label: "Email",
      login_password_label: "Password",
      login_forgot_password: "Forgot password?",
      login_submit: "Sign in",
      login_signing_in: "Signing in…",
      login_or_no_password: "Or, no password",
      login_magic_button: "Email me a sign-in link",
      login_magic_sending: "Sending link…",
      login_magic_sent_body: "We emailed {{email}} a sign-in link. It expires in 15 minutes. Check your inbox (and spam) — click the link and you're in.",
      login_new_here: "New here?",
      login_create_account: "Create an account",
      login_welcome_prefix: "Signed in — welcome, ",
      login_welcome_back_prefix: "Welcome back, ",
      login_google_failed: "Google sign-in failed. Try again.",
      login_enter_email_first: "Enter your email first — we'll send the link there.",
      login_magic_toast_sent: "Check your inbox — we sent a one-time sign-in link.",
      login_magic_toast_failed: "Couldn't send the link — try again in a moment.",
      login_redirecting: "Redirecting…",
      login_continue_google: "Continue with Google",
      login_continue_apple: "Continue with Apple",
      login_apple_failed: "Couldn't start the Apple sign-in flow.",
      login_passkey_button: "Sign in with passkey / Face ID / Touch ID",
      login_passkey_waiting: "Waiting for device…",
      login_passkey_cancelled: "Passkey prompt cancelled.",
      login_passkey_failed: "Passkey sign-in failed.",
      login_passkey_saved: "Passkey saved. Face ID / Touch ID will sign you in next time.",
      login_passkey_registration_cancelled: "Registration cancelled.",
      login_passkey_register_failed: "Couldn't save the passkey.",
      login_passkey_enable_biometric: "Enable Face ID / Touch ID for this account",
      login_passkey_add_security_key: "Add a passkey (security key)",

      // ── HustlePlaza sales page (public / marketing) ───────────────
      // Nav
      plaza_nav_features:     "Features",
      plaza_nav_rewards:      "Rewards",
      plaza_nav_how:          "How it works",
      plaza_nav_roadmap:      "Roadmap",
      plaza_nav_faq:          "FAQ",

      // Hero
      plaza_hero_badge:       "Australian owned",
      plaza_hero_kicker:      "One cart. Every reward.",
      plaza_hero_title_1:     "Shop Aussie.",
      plaza_hero_title_2:     "Earn everywhere.",
      plaza_hero_sub:         "Thousands of Aussie vendors in one basket. Every dollar you spend earns HustlePoints — redeemable at restaurants, cafés, hotels, hairdressers, pet groomers, and any business in the HustleSuite network.",
      plaza_cta_start:        "Join free — takes 30 seconds",
      plaza_cta_signin:       "Sign in",
      plaza_cta_shop:         "Browse the store",
      plaza_hero_stat_1_v:    "12,847",
      plaza_hero_stat_1_l:    "products live",
      plaza_hero_stat_2_v:    "340+",
      plaza_hero_stat_2_l:    "Aussie vendors",
      plaza_hero_stat_3_v:    "$4.2M",
      plaza_hero_stat_3_l:    "rewards paid",
      plaza_hero_stat_4_v:    "19",
      plaza_hero_stat_4_l:    "categories",

      // Problem
      plaza_problem_kicker:   "The problem with shopping today",
      plaza_problem_title:    "Ten tabs. Ten passwords. Zero loyalty.",
      plaza_problem_sub:      "You already juggle Amazon, eBay, Kogan, Kmart, Woolies, Coles, Bunnings, JB, Officeworks and half a dozen boutique stores. You spend hours comparing prices — then get nothing back but a receipt.",
      plaza_problem_1:        "Prices you can't compare across sites",
      plaza_problem_2:        "Overseas sellers pretending to be local",
      plaza_problem_3:        "Loyalty points trapped in a single store",
      plaza_problem_4:        "Fake reviews from bots and drop-shippers",

      // Solution
      plaza_solution_kicker:  "What HustlePlaza does differently",
      plaza_solution_title:   "The last cart you'll open all day.",
      plaza_solution_sub:     "One search. One cart. One checkout. Every purchase pays you back in HustlePoints that spend across our whole ecosystem — not just this shop.",
      plaza_solution_1_t:     "One cart, every vendor",
      plaza_solution_1_b:     "Shop across every Aussie seller on the platform and pay once at checkout. Delivery consolidated where possible.",
      plaza_solution_2_t:     "Verified locals only",
      plaza_solution_2_b:     "Every seller is identity-verified before they can list. No bots, no fakes, no drop-shipping mystery.",
      plaza_solution_3_t:     "Rewards that actually travel",
      plaza_solution_3_b:     "Points earned here spend at any HustleSuite business — cafés, hotels, hairdressers, restaurants, pet groomers.",
      plaza_solution_4_t:     "Live tracking, one screen",
      plaza_solution_4_b:     "Every parcel from every vendor tracked on one page. Notifications by email and SMS at every step.",

      // Rewards flywheel
      plaza_rewards_kicker:   "The HustlePoints flywheel",
      plaza_rewards_title:    "Earn while you shop. Spend anywhere in the network.",
      plaza_rewards_sub:      "Every $1 spent on HustlePlaza earns HustlePoints. Redeem them at any restaurant, café, hotel, hairdresser, or service business that runs on HustleSuite — anywhere across the network.",
      plaza_rewards_step_1:   "Shop the marketplace",
      plaza_rewards_step_1_b: "Buy anything from a T-shirt to a fridge.",
      plaza_rewards_step_2:   "Earn HustlePoints instantly",
      plaza_rewards_step_2_b: "Points appear the moment your order ships — no cards, no punch cards, no expiry games.",
      plaza_rewards_step_3:   "Spend across the network",
      plaza_rewards_step_3_b: "Redeem at cafés, restaurants, hotels, hairdressers, pet groomers, tradies.",
      plaza_rewards_step_4:   "Or cash out",
      plaza_rewards_step_4_b: "Convert to bank transfer once you clear $50.",

      // Categories
      plaza_cats_kicker:      "Shop by category",
      plaza_cats_title:       "19 categories. Australian vendors. Live stock.",
      plaza_cats_sub:         "Browse a slice of what's live right now — or sign up and shop the full 12,000+ product catalogue.",
      plaza_cats_cta:         "See all categories",

      // Featured vendors
      plaza_vendors_kicker:   "Real Aussie sellers",
      plaza_vendors_title:    "Meet the makers, growers and family shops.",
      plaza_vendors_sub:      "Every vendor on HustlePlaza is ABN-verified, based in Australia, and personally onboarded. No overseas passthroughs pretending to be local.",

      // How it works
      plaza_how_kicker:       "How it works",
      plaza_how_title:        "Three steps. One cart. Rewards forever.",
      plaza_how_1_title:      "Join in 30 seconds",
      plaza_how_1_body:       "Verify your email and browse instantly. Add a phone number when you're ready to check out.",
      plaza_how_2_title:      "Fill your cart",
      plaza_how_2_body:       "Shop across every vendor. One cart, one delivery view, one checkout. Payment via Stripe or PayPal.",
      plaza_how_3_title:      "Earn & spend anywhere",
      plaza_how_3_body:       "HustlePoints appear the moment your order ships. Spend them at any HustleSuite business, or cash out at $50.",

      // Compare table
      plaza_compare_kicker:   "How we stack up",
      plaza_compare_title:    "HustlePlaza vs the giants",
      plaza_compare_col_us:   "HustlePlaza",
      plaza_compare_col_amz:  "Amazon AU",
      plaza_compare_col_ebay: "eBay AU",
      plaza_compare_col_kmart:"Kmart / Big W",
      plaza_compare_row_1:    "Australian-owned",
      plaza_compare_row_2:    "Single cart across every seller",
      plaza_compare_row_3:    "Rewards spendable OUTSIDE this store",
      plaza_compare_row_4:    "Rewards cash-out to your bank",
      plaza_compare_row_5:    "Aussie-hosted customer support",

      // Trust
      plaza_trust_kicker:     "Why we verify",
      plaza_trust_title:      "Real customers. Real reviews.",
      plaza_trust_body:       "We verify your email at sign-up, your mobile number before checkout, and require a live photo check before you can post reviews, chat with vendors, or make high-value purchases. It slows fraudsters down and keeps prices low for you.",
      plaza_trust_1:          "Email verification to join",
      plaza_trust_2:          "SMS verification to check out",
      plaza_trust_3:          "Live photo verification for reviews and chat",
      plaza_trust_4:          "Vendor identity checks at onboarding",

      // Testimonials
      plaza_test_kicker:      "What our early members say",
      plaza_test_title:       "Straight from real customers.",
      plaza_test_1_body:      "I used my HustlePlaza points to pay for dinner at a café near work. Genuinely didn't believe it would work until the waitress smiled and said 'you're all set'.",
      plaza_test_1_name:      "Priya S.",
      plaza_test_2_body:      "Six vendors, one delivery window, one confirmation email. I'd normally have six passwords and six tracking numbers. This just works.",
      plaza_test_2_name:      "Marcus T.",
      plaza_test_3_body:      "As a small honey producer we finally get paid same-day, real reviews, and no 40% cut. HustlePlaza took me from 'side hustle' to full-time in eight months.",
      plaza_test_3_name:      "Kate & Dave",

      // FAQ
      plaza_faq_kicker:       "Answers",
      plaza_faq_title:        "The questions we get every week",
      plaza_faq_1_q:          "Is HustlePlaza actually Australian-owned?",
      plaza_faq_1_a:          "Yes. HustlePlaza is 100% Australian-owned, hosted on Australian servers, run by an Australian team. Every vendor goes through identity verification before their first listing can go live.",
      plaza_faq_2_q:          "How do the rewards work?",
      plaza_faq_2_a:          "Every dollar you spend on HustlePlaza earns you HustlePoints. Points appear the moment your order ships and never expire. Spend them at any business that runs on HustleSuite, or cash them out to your bank once you've saved enough.",
      plaza_faq_3_q:          "Why do you verify photos?",
      plaza_faq_3_a:          "So the reviews you read are real. Photo verification is a live-selfie check done via your phone camera — the same technique banks use — and it stops bots, fake accounts and hostile actors from gaming the platform.",
      plaza_faq_4_q:          "What if my order doesn't arrive?",
      plaza_faq_4_a:          "Every order is backed by our HustleGuarantee: if the parcel doesn't turn up or isn't as described, we refund you within 3 business days, then chase the vendor. You're never left out of pocket.",
      plaza_faq_5_q:          "Is my card information safe?",
      plaza_faq_5_a:          "We never see or store your card number. All payments run through Stripe (PCI-DSS Level 1) or PayPal, both with 3D Secure authentication that protects you against fraudulent charges on stolen cards.",
      plaza_faq_6_q:          "Do you ship internationally?",
      plaza_faq_6_a:          "Not yet. HustlePlaza is Australia-only at launch — that's how we can promise same-week delivery on most items and support in your timezone. NZ is on the 2026 roadmap.",
      plaza_faq_7_q:          "What does it cost to join?",
      plaza_faq_7_a:          "Nothing. Membership is free forever. Vendors pay a small commission per sale — buyers pay $0 to join, $0 to earn, $0 to redeem.",
      plaza_faq_8_q:          "Can I sell on HustlePlaza?",
      plaza_faq_8_a:          "Absolutely. Head to hustlesuite.au to onboard as a vendor — same-day approval for verified Aussie businesses, plus your first 30 days commission-free.",

      // Final CTA
      plaza_final_kicker:     "Founder pricing — limited seats",
      plaza_final_title:      "Be one of the first — lock in founder pricing.",
      plaza_final_sub:        "Register today as a HustleSuite Member. You'll be the first to know when each new feature ships, and your account will keep founder-tier rewards and pricing for life.",
      plaza_final_bonus:      "Founders never lose their founder rate. Free forever, cancel any time.",
      plaza_final_cta:        "Register as a Founder Member",
      plaza_final_disclosure: "No card required. Email + mobile verification only.",

      // Coming soon / Roadmap
      plaza_roadmap_kicker:   "What we're building next",
      plaza_roadmap_title:    "Six tools built to take work off your plate.",
      plaza_roadmap_sub:      "HustleSuite is more than a shop — it's a full productivity layer. Here's what's on the runway.",
      plaza_roadmap_soon:     "Shipping soon",
      plaza_roadmap_next:     "Next in the queue",
      plaza_roadmap_1_t:      "HustleDictate — speech-to-text on any form",
      plaza_roadmap_1_b:      "Chrome extension. Press Alt+M, speak, and any input on any website fills itself. Real estate applications, invoices, NDIS paperwork — done in half the time.",
      plaza_roadmap_2_t:      "HustleConnect Edit-Anywhere",
      plaza_roadmap_2_b:      "For WordPress owners: edit your live site from any page with a single hotkey. No more wp-admin round trips.",
      plaza_roadmap_3_t:      "Save-to-HustleSuite universal button",
      plaza_roadmap_3_b:      "Right-click any webpage — property listing, article, product, restaurant — and it lands in the right module ready for you to action.",
      plaza_roadmap_4_t:      "HustlePlaza price-checker",
      plaza_roadmap_4_b:      "Browse Amazon, eBay or Kmart — a floating chip shows the same product on HustlePlaza with your rewards preview.",
      plaza_roadmap_5_t:      "Review harvester",
      plaza_roadmap_5_b:      "Auto-sync new Google, Facebook and TripAdvisor reviews to your HustleSuite dashboard. Saves ~20 minutes per week.",
      plaza_roadmap_6_t:      "HustleSuite Sidebar",
      plaza_roadmap_6_b:      "Chrome side-panel with live orders, KPIs, quick-post and quick-invoice. Your whole business in a strip beside every tab.",

      // Footer
      plaza_footer_owned:     "Australian owned & hosted",
      plaza_footer_terms:     "Terms",
      plaza_footer_privacy:   "Privacy",
      plaza_footer_contact:   "Contact",
      plaza_footer_vendors:   "Sell on HustlePlaza",
      plaza_footer_help:      "Help centre",
      plaza_footer_tagline:   "Part of the HustleSuite family — one login, every service.",

      // ── Verification / join gate popup ───────────────────────────
      verify_gate_title:      "Almost there — quick sign-up",
      verify_gate_body:       "HustlePlaza is a shopping cart offering many benefits including redeemable rewards that can be used, not only within this platform but with any other Business using the HustlePlaza platform or any other Business Registered with HustleSuite. Some of these Businesses will include Restaurants, Cafés, Hotels, Hairdressers, and even Pet Grooming. To continue offering great prices and services, we need to ensure you are real and verify you. Press the Join Button to continue.",
      verify_gate_join:       "Join HustleSuite",
      verify_gate_signin:     "I already have an account",
      verify_gate_cancel:     "Not now",

      verify_email_title:     "Verify your email",
      verify_email_body:      "We'll send a 6-digit code to your inbox.",
      verify_email_placeholder: "you@example.com",
      verify_email_send:      "Send code",
      verify_email_code_ph:   "6-digit code",
      verify_email_check:     "Verify",
      verify_email_sent:      "Code sent — check your inbox.",

      verify_sms_title:       "Verify your phone",
      verify_sms_body:        "Add a mobile number so we can text you order updates and confirm large purchases.",
      verify_sms_placeholder: "+61412345678",
      verify_sms_send:        "Send SMS",
      verify_sms_check:       "Verify",
      verify_sms_sent:        "Code sent — check your phone.",

      verify_photo_title:     "Photo verification",
      verify_photo_body:      "Scan the QR code with your phone to take a live selfie. Required for chat, high-value purchases, and posting in Meetup / Tackle.",
      verify_photo_waiting:   "Waiting for your phone…",
      verify_photo_verified:  "Photo verified — you're all set.",

      /* ── HustleTravel salespage (hustletravel.au) ────────────── */
      travel_cta_start:       "Join free",
      travel_hero_kicker:     "HustleTravel",
      travel_hero_title_1:    "One membership.",
      travel_hero_title_2:    "Everywhere Australia.",
      travel_hero_sub:        "Hotels, flights, experiences, road-trip planning and car hire — booked at the best available rate and paying you HustlePoints on every dollar spent.",
      travel_cta_plan:        "Plan a road trip",
      travel_cta_hotels:      "Search hotels",
      travel_svc_hotels_t:    "Hotels & apartments",
      travel_svc_hotels_b:    "Compare 200+ booking sites in one place — Trivago-powered metasearch.",
      travel_svc_flights_t:   "Flights",
      travel_svc_flights_b:   "Domestic and international, with real-time seat maps and fare rules.",
      travel_svc_experiences_t: "Experiences & tours",
      travel_svc_experiences_b: "Reef days, cellar-door tastings, live music, kid-friendly excursions.",
      travel_svc_cars_t:      "Car hire",
      travel_svc_cars_b:      "Every major brand plus local independents — pickup at any Australian airport.",
      travel_svc_insurance_t: "Travel insurance",
      travel_svc_insurance_b: "Underwritten by APRA-regulated Australian insurers. Compare in one screen.",
      travel_svc_roadtrip_t:  "Road-trip planner",
      travel_svc_roadtrip_b:  "EV range-aware itineraries with chargers, overnight stops and cost estimates.",
      travel_final_kicker:    "Founder pricing",
      travel_final_title:     "Lock in Founder-tier rewards for life.",
      travel_final_sub:       "Register once with HustleSuite. Book everything — hotels, flights, experiences, insurance — on HustleTravel and earn HustlePoints spendable across every business in the network.",
      travel_final_cta:       "Register as a Founder Member",
      travel_final_disclosure: "Best-rate policy: results ordered by best value to the traveller, not commission.",

      /* ── HustleDictate salespage (hustledictate.au) ──────────── */
      dictate_hero_kicker:    "HustleDictate",
      dictate_hero_title_1:   "Speak.",
      dictate_hero_title_2:   "Any form. Any website.",
      dictate_hero_sub:       "The one Chrome extension that turns any text field into a professional-grade voice dictation microphone — powered by Whisper.",
      dictate_cta_install:    "Add to Chrome",
      dictate_uc_re_t:        "Real-estate tenancy notes",
      dictate_uc_re_b:        "Dictate inspection reports and tenant messages straight into your PM system.",
      dictate_uc_ndis_t:      "NDIS progress notes",
      dictate_uc_ndis_b:      "Speak the shift note while the memory is fresh — hands-free, no typing.",
      dictate_uc_invoice_t:   "Invoice descriptions",
      dictate_uc_invoice_b:   "Line-item descriptions dictated straight into Xero, MYOB or QuickBooks.",
      dictate_uc_email_t:     "Long-form email replies",
      dictate_uc_email_b:     "Talk out replies in Gmail, Outlook or any webmail — three times faster than typing.",
      dictate_final_kicker:   "Free for Founder Members",
      dictate_final_title:    "Every keyboard is now a microphone.",
      dictate_final_sub:      "Free for HustleSuite Founder Members. $5/mo otherwise — cancel any time.",
      dictate_final_cta:      "Add to Chrome — free",

      /* ── HustleFinder additions ───────────────────────────────── */
      "finder.title":          "HustleFinder",
      "finder.tagline":        "Find the nearest public place — real data, Australia-wide.",
      "finder.search_placeholder": "Search places, streets or suburbs…",
      "finder.hint":           "Start typing above, tap a category, or use \"Near me\".",
      "finder.near_me":        "Near me",
      "finder.searching":      "Searching…",
      "finder.suburb.placeholder": "Filter by suburb (e.g. Darwin, NT)…",
      "finder.suburb.clear":   "Clear",
      "finder.search_in":      "Search in {{cat}}",
      "finder.empty":          "No results — try another category or search term.",
      "finder.meta.title":     "HustleFinder — free Aussie suburb directory",
      "finder.meta.desc":      "Search local businesses, chargers, cafés, toilets and more across every Australian suburb — 100% free.",
      "finder.footer.credits": "Data credits & attribution",
      "finder.footer.terms":   "Terms",
      "finder.cat.toilet":     "Toilets",
      "finder.cat.fountain":   "Drinking Fountains",
      "finder.cat.transport":  "Transport",
      "finder.cat.park":       "Parks",
      "finder.cat.medical":    "Medical",
      "finder.cat.school":     "Schools",
      "finder.cat.library":    "Libraries",
      "finder.cat.petrol":     "Petrol",
      "finder.cat.kidzone":    "Kid Zone",
      "finder.cat.puzzles":    "Puzzles",
      "finder.cat.roadtrip":   "Road trip",
      "finder.hotels.title":   "Find a hotel nearby",
      "finder.hotels.sub":     "Compares 200+ booking sites in one search — powered by Trivago.",
      "finder.hotels.where":   "Where",
      "finder.hotels.where_ph": "City or suburb (e.g. Byron Bay, NSW)",
      "finder.hotels.checkin": "Check-in",
      "finder.hotels.checkout": "Check-out",
      "finder.hotels.adults":  "Adults",
      "finder.hotels.search":  "Search hotels",
      "finder.hotels.disclosure": "Opens Trivago — we may earn commission on bookings.",

      // ── POI type / subtype labels (OSM tag → display) ──
      // Falls back to the humanised English string when a key is
      // missing, so obscure OSM tags still render legibly.
      "finder.poi_type.transport_stop":  "transport stop",
      "finder.poi_type.bus_stop":         "bus stop",
      "finder.poi_type.train_station":    "train station",
      "finder.poi_type.tram_stop":        "tram stop",
      "finder.poi_type.ferry_terminal":   "ferry terminal",
      "finder.poi_type.subway_entrance":  "subway entrance",
      "finder.poi_type.taxi":             "taxi",
      "finder.poi_type.post_office":      "post office",
      "finder.poi_type.toilet":           "toilet",
      "finder.poi_type.toilets":          "toilets",
      "finder.poi_type.drinking_water":   "drinking water",
      "finder.poi_type.drinking_fountain": "drinking fountain",
      "finder.poi_type.park":             "park",
      "finder.poi_type.playground":       "playground",
      "finder.poi_type.picnic_site":      "picnic site",
      "finder.poi_type.dog_park":         "dog park",
      "finder.poi_type.school":           "school",
      "finder.poi_type.library":          "library",
      "finder.poi_type.university":       "university",
      "finder.poi_type.kindergarten":     "kindergarten",
      "finder.poi_type.hospital":         "hospital",
      "finder.poi_type.doctor":           "doctor",
      "finder.poi_type.doctors":          "doctors",
      "finder.poi_type.dentist":          "dentist",
      "finder.poi_type.pharmacy":         "pharmacy",
      "finder.poi_type.clinic":           "clinic",
      "finder.poi_type.fuel":             "petrol station",
      "finder.poi_type.charging_station": "EV charger",
      "finder.poi_type.parking":          "parking",
      "finder.poi_type.atm":              "ATM",
      "finder.poi_type.bank":             "bank",
      "finder.poi_type.cafe":             "café",
      "finder.poi_type.restaurant":       "restaurant",
      "finder.poi_type.fast_food":        "fast food",
      "finder.poi_type.bar":              "bar",
      "finder.poi_type.pub":              "pub",
      "finder.poi_type.supermarket":      "supermarket",
      "finder.poi_type.marketplace":      "marketplace",
      "finder.poi_type.shop":             "shop",
      "finder.poi_type.convenience":      "convenience store",
      "finder.poi_type.hotel":            "hotel",
      "finder.poi_type.motel":            "motel",
      "finder.poi_type.hostel":           "hostel",
      "finder.poi_type.guest_house":      "guest house",
      "finder.poi_type.caravan_site":     "caravan park",
      "finder.poi_type.camp_site":        "camp site",
      "finder.poi_type.attraction":       "attraction",
      "finder.poi_type.viewpoint":        "viewpoint",
      "finder.poi_type.museum":           "museum",
      "finder.poi_type.artwork":          "artwork",
      "finder.poi_type.information":      "information",
      "finder.poi_type.beach":            "beach",
      "finder.poi_type.swimming_pool":    "swimming pool",
      "finder.poi_type.sports_centre":    "sports centre",
      "finder.poi_type.gym":              "gym",
      "finder.poi_type.police":           "police",
      "finder.poi_type.fire_station":     "fire station",
      "finder.poi_type.place_of_worship": "place of worship",
      "finder.poi_type.church":           "church",
      "finder.poi_type.community_centre": "community centre",
          /* AUTO-TRANSLATED rt.* START */
      "rt.brand_name": "Roadtrip Planner",
      "rt.page_title": "Aussie EV Road-trip Planner",
      "rt.from": "From",
      "rt.to": "To",
      "rt.from_placeholder": "Suburb, town or city (e.g. Newcastle NSW)",
      "rt.to_placeholder": "Suburb, town or city (e.g. Hobart TAS)",
      "rt.stop": "STOP",
      "rt.stop_placeholder": "Intermediate stop",
      "rt.add_stop": "Add stop",
      "rt.upgrade_pro": "Upgrade to Pro for up to 20 stops",
      "rt.reverse": "Reverse (calculate way home)",
      "rt.from_current": "From current location",
      "rt.range_per_charge": "Range per charge (km)",
      "rt.connector": "Connector",
      "rt.connector_any": "Any",
      "rt.drive_hours_day": "Drive hours/day",
      "rt.accommodation": "Accommodation",
      "rt.accom_any": "Any (BnB · Hotel · Caravan)",
      "rt.accom_bnb": "BnB",
      "rt.accom_hotel": "Hotel",
      "rt.accom_caravan": "Caravan Park",
      "rt.min_rating": "Minimum rating",
      "rt.any_rating": "Any rating",
      "rt.only_5star": "5★ only",
      "rt.schedule": "Schedule:",
      "rt.depart_at": "Depart at",
      "rt.arrive_by": "Arrive by",
      "rt.hotel_checkin": "Hotel check-in",
      "rt.hotel_checkout": "Hotel check-out",
      "rt.overnight_hint": "Overnight legs won't schedule you outside these hours.",
      "rt.whos_in_car": "Who's in the car?",
      "rt.adults": "Adults",
      "rt.kids": "Kids",
      "rt.ages": "Ages",
      "rt.show_attractions": "Show tourist attractions",
      "rt.show_kids": "Show kid-friendly stops",
      "rt.overnight_stays": "Overnight stays",
      "rt.fast_chargers_only": "Fast chargers only",
      "rt.hotel_must_charger": "Hotel must have charger",
      "rt.pet_only": "Pet-friendly stays only",
      "rt.safety": "Safety",
      "rt.safety_90": "90% (flat)",
      "rt.safety_85": "85% (default)",
      "rt.safety_80": "80% (hills / towing)",
      "rt.safety_70": "70% (heavy load)",
      "rt.save_trip": "Save trip",
      "rt.plan_my_trip": "Plan my trip",
      "rt.refresh_trip": "Refresh trip",
      "rt.planning": "Planning…",
      "rt.resume_title": "Resume trip from here",
      "rt.resume_body": "We captured your current location. Enter the remaining range shown on your dash so we plan the next leg against what you actually have — not a full charge.",
      "rt.remaining_range": "Remaining range (km)",
      "rt.cancel": "Cancel",
      "rt.replan_from_here": "Replan from here",
      "rt.save_modal_title": "Name this trip",
      "rt.save_modal_body": "Saved trips live under your profile — reopen any time and hit Replan.",
      "rt.save_name_placeholder": "e.g. Brisbane → Sydney via Byron",
      "rt.your_saved_trips": "Your saved trips",
      "rt.custom_location": "Custom location",
      "rt.trip_summary": "Trip summary",
      "rt.itinerary": "Itinerary",
      "rt.no_stops_needed": "No stops needed — the whole trip fits inside your range.",
      "rt.chargers_in_corridor": "chargers in corridor",
      "rt.planned_stops": "planned stops",
      "rt.overnight_stop": "overnight stop",
      "rt.overnight_stops": "overnight stops",
      "rt.suggested_detours": "Suggested detours — tick to add",
      "rt.replan_with_detours": "Replan with detours",
      "rt.attractions_label": "Attraction",
      "rt.kids_label": "Kids",
      "rt.km_from_start": "km from start",
      "rt.open_driving": "Open driving mode",
      "rt.big_font_hint": "Big-font, cradle-friendly. Works with CarPlay/Android Auto browser mirror.",
      "rt.check_price_book": "Check price & book on Trivago ↗",
      "rt.visit_site": "Visit site ↗",
      "rt.charger_onsite": "Charger on-site",
      "rt.leg_exceeds_range": "This leg exceeds your set range — you would run out of power before reaching this charger. Reduce range assumption or add an intermediate top-up.",
      "rt.add_waypoint_replan": "Add as waypoint & replan",
      /* AUTO-TRANSLATED rt.* END */
                      /* AUTO-TRANSLATED hm.* START */
      "hm.nav.hustlemeet": "HustleMeet",
      "hm.nav.circles": "Circles",
      "hm.nav.matchmaker": "Matchmaker",
      "hm.nav.verified": "Verified Members",
      "hm.nav.meet": "Meet",
      "hm.nav.overview": "HustleMeetups",
      "hm.overview.page_title": "HustleMeetups — three warm ways to find your people.",
      "hm.overview.kicker": "Part of HustleSuite",
      "hm.overview.hero_title": "Verified People. Real Groups. Real Connections.",
      "hm.overview.hero_title_accent": "Real Connections.",
      "hm.overview.hero_sub": "Because meeting people should start with knowing they're real.",
      "hm.overview.hero_short_desc": "HustleMeetups brings verified people together through real-world and online groups across Australia. Join a group that interests you, see upcoming meetups and choose the events you want to attend. Meetups can happen anywhere — restaurants, cafés, hotels, conference rooms, parks, live music venues or online by video.\n\nFrom professional networking and business groups to dinners, social outings, music, walking groups and shared interests, HustleMeetups makes it easier to meet people and build genuine connections.\n\nEvery HustleMeetups member is verified, helping create a safer, more trusted community.\n\nFind your group. Choose your meetup. Meet real people.",
      "hm.overview.tile_meet_kicker": "HustleMeet",
      "hm.overview.tile_meet_title": "Find your people. Do the thing.",
      "hm.overview.tile_meet_body": "Australia's home for interest-first groups. Runners, coders, investors, walkers, dancers, breakfast clubs, book crews — hundreds of groups, real events every week. Show up once, or every week.",
      "hm.overview.tile_meet_cta": "Explore groups",
      "hm.overview.tile_circles_kicker": "Circles",
      "hm.overview.tile_circles_title": "Your crew. A new table every time.",
      "hm.overview.tile_circles_body": "Bring your regular group of mates. We book a new restaurant, plan the trivia, deal the cards. You just show up. The tightest way to keep an existing crew close — without anyone having to organise it.",
      "hm.overview.tile_circles_cta": "Start a Circle",
      "hm.overview.tile_matchmaker_kicker": "Matchmaker",
      "hm.overview.tile_matchmaker_title": "AI picks the table. You show up.",
      "hm.overview.tile_matchmaker_body": "Take a 10-minute quiz. Overnight, we seat you with strangers who feel like old friends by dessert. Different table every time.",
      "hm.overview.tile_matchmaker_cta": "Take the quiz",
      "hm.overview.trust_kicker": "Why it works",
      "hm.overview.trust_title": "Trust first. Table second.",
      "hm.overview.trust_1_title": "Verified members",
      "hm.overview.trust_1_body": "Every group starts with a real ID check + reliability score. No throwaway accounts.",
      "hm.overview.trust_2_title": "Real hosts",
      "hm.overview.trust_2_body": "Every group is led by a vetted host who owns the vibe, sets the rhythm, and looks after the room.",
      "hm.overview.trust_3_title": "Friendship first",
      "hm.overview.trust_3_body": "Everything here is designed for real friendships, not dating. Ever.",
      "hm.overview.trust_4_title": "Zero-tolerance moderation",
      "hm.overview.trust_4_body": "One report, one review, one decision. Bad actors are removed the same night.",
      "hm.overview.footer_line": "Part of HustleSuite — Australian owned & hosted.",
      "hm.landing.page_title": "HustleMeet · Find your people. Do the thing.",
      "hm.landing.kicker": "Australia's interest network",
      "hm.landing.hero_title": "Find your people. Do the thing.",
      "hm.landing.hero_title_accent": "your people",
      "hm.landing.hero_sub": "HustleMeet is Australia's home for interest-first groups. Software devs debugging over lunch. Long-course runners at 6am. Investors meeting monthly. Walking groups, dance crews, breakfast clubs, board-game nights. Pick a group, book the next event, and turn up.",
      "hm.landing.cta_explore_circles": "Browse groups",
      "hm.landing.cta_try_matchmaker": "Start your own group",
      "hm.landing.trust_verified": "Verified members",
      "hm.landing.trust_venues": "Vetted event hosts",
      "hm.landing.trust_friendship": "Friendship first — never dating",
      "hm.landing.trust_moderation": "Zero-tolerance moderation",
      "hm.landing.subbrand_circles_kicker": "Circles",
      "hm.landing.subbrand_circles_title": "Already got your crew? Give them a permanent home.",
      "hm.landing.subbrand_circles_body": "Circles is for the group of mates who already meet up — book club, poker night, running crew, work friends. We handle the venue, the reminders, and the roster. You just show up.",
      "hm.landing.subbrand_circles_b1": "Rotating venues, one running roster",
      "hm.landing.subbrand_circles_b2": "Auto-reminders + easy RSVPs",
      "hm.landing.subbrand_circles_b3": "Trivia, cards & themed nights on tap",
      "hm.landing.subbrand_circles_cta": "Start a Circle",
      "hm.landing.subbrand_matchmaker_kicker": "Matchmaker",
      "hm.landing.subbrand_matchmaker_title": "AI picks the table. You show up.",
      "hm.landing.subbrand_matchmaker_body": "One 10-minute quiz — personality, values, availability. Our Matchmaker seats you with strangers who feel like old friends by dessert. Different table every time.",
      "hm.landing.subbrand_matchmaker_b1": "Big-5 personality & values scored",
      "hm.landing.subbrand_matchmaker_b2": "Overnight invites when your fit is 0.70 or higher",
      "hm.landing.subbrand_matchmaker_b3": "Learns from every dinner you have",
      "hm.landing.subbrand_matchmaker_cta": "Take the quiz",
      "hm.landing.how_kicker": "How HustleMeet works",
      "hm.landing.how_title": "Four steps. Find your group. Turn up.",
      "hm.landing.how_step1_ttl": "Pick your interests",
      "hm.landing.how_step1_sub": "Running, coding, investing, cooking, music, walking — tell us what you're into and where you live.",
      "hm.landing.how_step2_ttl": "Browse groups near you",
      "hm.landing.how_step2_sub": "Every group publishes its host, its rhythm and its next event. Read the room before you commit.",
      "hm.landing.how_step3_ttl": "RSVP to the next event",
      "hm.landing.how_step3_sub": "One tap, one calendar invite, one clean reminder the day before. Some events are free, some paid — the price is on the tin.",
      "hm.landing.how_step4_ttl": "Show up & connect",
      "hm.landing.how_step4_sub": "Meet the host, meet the crew, do the thing. Come back next week if it clicked.",
      "hm.landing.stats_kicker": "Australia's new way to meet",
      "hm.landing.stats_title": "The reason people keep coming back.",
      "hm.landing.stats_92_sub": "Feel they clicked with at least one person at the event",
      "hm.landing.stats_68_sub": "RSVP to their next event within a week",
      "hm.landing.stats_85_sub": "Stay in touch with someone from the group",
      "hm.landing.why_kicker": "Why we work",
      "hm.landing.why_title": "Real interests. Real hosts. Real events.",
      "hm.landing.why_1_ttl": "Interest-first, not location-first",
      "hm.landing.why_1_body": "The people in your group actually care about the thing you care about — that's the shortcut to real friendship.",
      "hm.landing.why_2_ttl": "Real event hosts",
      "hm.landing.why_2_body": "Every group has a vetted host who owns the vibe, sets the rhythm, and steps in if something's off. Not a Facebook admin. A real person.",
      "hm.landing.why_3_ttl": "Consistency matters",
      "hm.landing.why_3_body": "New events every week. Something to look forward to. A rhythm that turns strangers into your Tuesday-night crew.",
      "hm.landing.why_4_ttl": "Safe & secure",
      "hm.landing.why_4_body": "Verified members, public venues, zero tolerance for disrespect. Our support team is on call before, during and after every event.",
      "hm.landing.why_5_ttl": "Friendship first",
      "hm.landing.why_5_body": "This isn't dating. It's community. Real interests, real people, no swiping in the middle.",
      "hm.landing.why_6_ttl": "Small enough to matter",
      "hm.landing.why_6_body": "Most groups cap events at 4-15 seats. Big enough to spark real conversations. Small enough that nobody gets lost.",
      "hm.landing.cities_kicker": "Where we run",
      "hm.landing.cities_title": "Groups meeting across Australia & NZ.",
      "hm.landing.cities_sub_zero": "First groups opening across 15+ cities — join early and shape the room.",
      "hm.landing.cities_sub_some": "Groups live across 15+ metros. Something in your city, this week.",
      "hm.landing.cities_sub_one": "{{count}} live group · new events every week · always adding cities.",
      "hm.landing.cities_sub_many": "{{count}} live groups · new events every week · always adding cities.",
      "hm.landing.forme_kicker": "This is for you if…",
      "hm.landing.forme_title": "You want real interests. Real people. Real events.",
      "hm.landing.forme_1": "You're over the swipe-app fatigue and want to actually meet people around what you love doing.",
      "hm.landing.forme_2": "You've moved to a new city and need a warm crew, not another chat group.",
      "hm.landing.forme_3": "You value depth over broadcast — one great group beats fifty follows.",
      "hm.landing.forme_4": "You believe shared interests + a set two hours in real life is the real cheat code.",
      "hm.landing.notme_kicker": "This isn't for you if…",
      "hm.landing.notme_title": "HustleMeet doesn't work for everyone.",
      "hm.landing.notme_1": "You can't commit to showing up — hosts and members are counting on your RSVP.",
      "hm.landing.notme_2": "You're looking for a dating service — friendships come first here.",
      "hm.landing.notme_3": "You want to broadcast without meeting anyone — this only works in person.",
      "hm.landing.notme_4": "You expect endless free perks — some events cost. The price is always upfront.",
      "hm.landing.safety_kicker": "Our safety commitment",
      "hm.landing.safety_title": "Warm, but never careless.",
      "hm.landing.safety_1_ttl": "Public venues",
      "hm.landing.safety_1_body": "Every event runs at a public, staffed venue — a cafe, restaurant, park, gym or studio. Never anyone's home unless every attendee opts in.",
      "hm.landing.safety_2_ttl": "Community standards",
      "hm.landing.safety_2_body": "Zero tolerance for disrespect, harassment or dishonesty. Members who breach are removed immediately.",
      "hm.landing.safety_3_ttl": "Privacy protected",
      "hm.landing.safety_3_body": "Your details are never shared publicly. You control who you stay connected with after each event.",
      "hm.landing.safety_4_ttl": "Support on call",
      "hm.landing.safety_4_body": "Our team is available before, during and after every event if you have questions or concerns.",
      "hm.landing.bottom_title": "Your next favourite group is one tap away.",
      "hm.landing.bottom_sub": "Pick your path — join an existing group, bring your crew into a Circle, or let the Matchmaker seat you at a table that already fits.",
      "hm.landing.bottom_cta_circles": "Start a Circle",
      "hm.landing.bottom_cta_mm": "Take the Matchmaker quiz",
      "hm.circles.page_title": "HustleMeet Circles · Your crew, new venue every time",
      "hm.circles.kicker": "HustleMeet · Circles",
      "hm.circles.hero_title": "Your crew. A new table every time.",
      "hm.circles.hero_title_accent": "Your crew",
      "hm.circles.hero_sub": "Already got your mates? Circles is the easiest way to keep them close. We book the venue, plan the trivia, deal the cards. You just turn up — same crew, new night, new place.",
      "hm.circles.cta_find": "Start a Circle",
      "hm.circles.cta_host_hero": "Join an open Circle",
      "hm.circles.trust_venue": "Rotating vetted venues",
      "hm.circles.trust_party": "4-12 mates per Circle",
      "hm.circles.trust_verified": "Verified members",
      "hm.circles.trust_friendship": "Real friendships, kept close",
      "hm.circles.how_kicker": "How it works",
      "hm.circles.how_title": "Four steps. Your crew stays a crew.",
      "hm.circles.how_step1_ttl": "Start your Circle",
      "hm.circles.how_step1_sub": "Name it, invite your mates (up to 12), and pick a rhythm — weekly, fortnightly or monthly.",
      "hm.circles.how_step2_ttl": "We pick the venue",
      "hm.circles.how_step2_sub": "Every event we suggest a new spot — restaurant, trivia night, card room, wine bar. Or lock in a favourite.",
      "hm.circles.how_step3_ttl": "Everyone RSVPs in one tap",
      "hm.circles.how_step3_sub": "Auto-reminders, cost split, prepaid seats so no-shows don't wreck the plan. The bill is done before the entrée lands.",
      "hm.circles.how_step4_ttl": "Keep the rhythm",
      "hm.circles.how_step4_sub": "One night in the diary that never has to be organised again. The group tightens over months, not minutes.",
      "hm.circles.cadence_weekly_ttl": "Weekly",
      "hm.circles.cadence_weekly_sub": "Every week",
      "hm.circles.cadence_weekly_body": "For crews who want a Tuesday-night ritual — same faces, new place every time.",
      "hm.circles.cadence_fortnight_ttl": "Fortnightly",
      "hm.circles.cadence_fortnight_sub": "Every 2 weeks",
      "hm.circles.cadence_fortnight_body": "The sweet spot — regular without being demanding on busy diaries.",
      "hm.circles.cadence_monthly_ttl": "Monthly",
      "hm.circles.cadence_monthly_sub": "Every month",
      "hm.circles.cadence_monthly_body": "Special-occasion Circles — Sunday roasts, big trivia nights, degustation, poker.",
      "hm.circles.feast_kicker": "Pick your night",
      "hm.circles.feast_title": "Three ways to spend it",
      "hm.circles.feast_sub": "Every Circle picks how the night runs. Rotate them, or lock in your crew's favourite.",
      "hm.circles.feast_surprise_ttl": "Try a new restaurant",
      "hm.circles.feast_surprise_body": "We short-list three venues near you, everyone votes, kitchen preps for a full table. Cost split, prepaid seats.",
      "hm.circles.feast_preselect_ttl": "Trivia / cards / games",
      "hm.circles.feast_preselect_body": "Book a trivia night, a poker room, board-game bar or bowling lane. We handle the venue, the buy-in and the reminders.",
      "hm.circles.feast_per_event_ttl": "One-off themed night",
      "hm.circles.feast_per_event_body": "Wine tasting, chef's table, cocktail class, escape room. Something special once in a while — fully organised.",
      "hm.circles.live_kicker": "Right now",
      "hm.circles.live_title": "Circles you can join",
      "hm.circles.live_host_link": "Start your own",
      "hm.circles.live_loading": "Loading…",
      "hm.circles.live_empty_title": "Be the first Circle in your city.",
      "hm.circles.live_empty_sub": "Start one with three mates and we'll help you invite the rest — plus give you the first three venues on us.",
      "hm.circles.live_empty_cta": "Start a Circle",
      "hm.circles.host_kicker": "For Circle leaders",
      "hm.circles.host_title": "You bring the crew. We do the admin.",
      "hm.circles.host_body": "If you're the mate who always organises the night, Circles saves you hours. Auto-reminders, prepaid RSVPs, venue short-lists, cost splits, waitlist management and a cashback wallet on every night out. You just show up as a normal member.",
      "hm.circles.host_perk_1": "Auto venue short-lists — never scroll Google again",
      "hm.circles.host_perk_2": "Prepaid seats — zero no-shows",
      "hm.circles.host_perk_3": "Cost split done before dessert",
      "hm.circles.host_perk_4": "Reminders + calendar invites on autopilot",
      "hm.circles.host_perk_5": "Waitlist with auto-promote if a mate cancels",
      "hm.circles.host_perk_6": "Cashback wallet on every night out",
      "hm.circles.host_cta": "Start a Circle now",
      "hm.circles.why_kicker": "Why Circles work",
      "hm.circles.why_title": "Adult friendships need infrastructure",
      "hm.circles.why_1_ttl": "Same crew, deeper trust",
      "hm.circles.why_1_body": "Life pulls friends apart. Circles are the fixed rhythm that keeps everyone close — the same 4-12 mates, meeting on the same weeknight, no matter what.",
      "hm.circles.why_2_ttl": "Zero admin fatigue",
      "hm.circles.why_2_body": "Nobody has to be the group WhatsApp chief. We pick the venues, send the reminders, take the payments, close the loop.",
      "hm.circles.why_3_ttl": "New venue, new memory",
      "hm.circles.why_3_body": "Rotate through Australia's best small restaurants, trivia rooms and game bars. Familiar faces, fresh backdrops — the sweet spot of routine and novelty.",
      "hm.circles.bottom_title": "The night your crew looks forward to, without anyone having to plan it.",
      "hm.circles.bottom_sub": "One Circle. Same mates. New venue every time.",
      "hm.circles.bottom_cta_find": "Start a Circle",
      "hm.circles.bottom_cta_mm": "Meet new people with Matchmaker →",
      "hm.circles.card_host_pill": "Circle",
      "hm.matchmaker.page_title": "HustleMeet Matchmaker · AI picks the right table",
      "hm.matchmaker.kicker": "HustleMeet · Matchmaker",
      "hm.matchmaker.hero_title": "AI picks the table. You just show up.",
      "hm.matchmaker.hero_title_accent": "You just show up.",
      "hm.matchmaker.hero_sub": "Answer a short personality & values quiz. Our Matchmaker seats you with four to seven strangers who will feel like old friends by dessert. No swiping. No small talk. Just a shared table, ready when you are.",
      "hm.matchmaker.cta_quiz": "Take the matching quiz",
      "hm.matchmaker.cta_see_circles": "See Circles instead →",
      "hm.matchmaker.trust_big5": "Big-5 personality scored",
      "hm.matchmaker.trust_seats": "4-8 seats per table",
      "hm.matchmaker.trust_lock": "Zero-swipe · zero-photo",
      "hm.matchmaker.trust_verified": "Verified members only",
      "hm.matchmaker.how_kicker": "How it works",
      "hm.matchmaker.how_title": "Four steps to a table that already fits.",
      "hm.matchmaker.how_step1_ttl": "Take the quiz",
      "hm.matchmaker.how_step1_sub": "Ten-minute questionnaire — personality, values, dietary, availability. You do it once.",
      "hm.matchmaker.how_step2_ttl": "Overnight matching",
      "hm.matchmaker.how_step2_sub": "Our AI reads your profile and Australia's live dinner slate. When your fit score is 0.70 or higher, it drafts a personal invite.",
      "hm.matchmaker.how_step3_ttl": "Accept or decline",
      "hm.matchmaker.how_step3_sub": "You get one clean invite email with the venue, the table theme, and the three interests you share with the group.",
      "hm.matchmaker.how_step4_ttl": "It learns from you",
      "hm.matchmaker.how_step4_sub": "Every post-dinner survey tunes the model. Your next match gets smarter the more you dine.",
      "hm.matchmaker.science_kicker": "Why it works",
      "hm.matchmaker.science_title": "The Matchmaker doesn't guess — it scores.",
      "hm.matchmaker.science_sub": "Every match is a weighted composite of four factors. When the whole table averages above 0.70, we send the invites. Anything below sits in the queue.",
      "hm.matchmaker.science_1_ttl": "Personality (35%)",
      "hm.matchmaker.science_1_body": "Cosine similarity across the Big-5 personality vector — openness, conscientiousness, extraversion, agreeableness, and emotional stability.",
      "hm.matchmaker.science_2_ttl": "Values (25%)",
      "hm.matchmaker.science_2_body": "Overlap on the values that make a dinner conversation actually land — family, curiosity, growth, service, adventure.",
      "hm.matchmaker.science_3_ttl": "Interests (25%)",
      "hm.matchmaker.science_3_body": "Jaccard overlap on your specific interests — hiking Six Foot Track, Sydney film club, Melbourne coffee scene, your third book of the year.",
      "hm.matchmaker.science_4_ttl": "Availability (15%)",
      "hm.matchmaker.science_4_body": "The night you can actually make. No 7pm Tuesday invite if your calendar says gym-Tuesday-forever.",
      "hm.matchmaker.safety_kicker": "Safety by design",
      "hm.matchmaker.safety_title": "The Matchmaker is quietly cautious.",
      "hm.matchmaker.safety_1_ttl": "Confidence threshold",
      "hm.matchmaker.safety_1_body": "Below a 0.70 group score, no invite goes out. A shaky match beats no dinner — until it doesn't.",
      "hm.matchmaker.safety_2_ttl": "Mutual-no blocklist",
      "hm.matchmaker.safety_2_body": "If two members privately signal 'no' after a dinner, we never seat them together again.",
      "hm.matchmaker.safety_3_ttl": "Auto-kill switch",
      "hm.matchmaker.safety_3_body": "If 20% or more of recent surveys rate a table 2/5 or lower, invites pause for 24h while an operator reviews.",
      "hm.matchmaker.safety_4_ttl": "Community standards",
      "hm.matchmaker.safety_4_body": "Every member accepts a plain-English code of conduct. We remove people who breach it — no appeals.",
      "hm.matchmaker.safety_5_ttl": "Public venues only",
      "hm.matchmaker.safety_5_body": "Every Matchmaker dinner runs at a real restaurant with staff. Never anyone's home.",
      "hm.matchmaker.safety_6_ttl": "Post-event feedback",
      "hm.matchmaker.safety_6_body": "Your private survey shapes future matches, and can trigger a mod review of the venue or a peer.",
      "hm.matchmaker.loop_kicker": "The learning loop",
      "hm.matchmaker.loop_title": "Every dinner makes your next dinner better.",
      "hm.matchmaker.loop_body": "The Matchmaker isn't static. After each event you submit a short private survey — how the conversation felt, who you'd dine with again, whether the venue delivered. Those signals feed back into the model and reshape every future match.",
      "hm.matchmaker.loop_1": "Peer \"dine again\" votes strengthen your pairings",
      "hm.matchmaker.loop_2": "Mutual-no votes silently retire pairs",
      "hm.matchmaker.loop_3": "Venue & conversation ratings tune the seat mix",
      "hm.matchmaker.loop_4": "Reliability score protects members from repeat no-shows",
      "hm.matchmaker.loop_5": "Learning bias reinforces good matches month over month",
      "hm.matchmaker.loop_6": "You stay in control — opt-out or reset your profile anytime",
      "hm.matchmaker.vs_kicker": "Which is right for me?",
      "hm.matchmaker.vs_title": "Matchmaker or Circles?",
      "hm.matchmaker.vs_mm_ttl": "Pick me if I want variety.",
      "hm.matchmaker.vs_mm_1": "A different table, city, cuisine every time",
      "hm.matchmaker.vs_mm_2": "AI-curated group of 4-8 compatible strangers",
      "hm.matchmaker.vs_mm_3": "Weekly rhythm, no host commitment",
      "hm.matchmaker.vs_mm_4": "Great if you're new to a city or travelling",
      "hm.matchmaker.vs_mm_cta": "Take the matching quiz",
      "hm.matchmaker.vs_circles_ttl": "Pick me if I want a regular.",
      "hm.matchmaker.vs_circles_1": "Same host · same venue · same weeknight",
      "hm.matchmaker.vs_circles_2": "A group that turns into your Wednesday tradition",
      "hm.matchmaker.vs_circles_3": "Human-curated by an experienced host",
      "hm.matchmaker.vs_circles_4": "Great if you've just moved and want roots",
      "hm.matchmaker.vs_circles_cta": "Browse Circles",
      "hm.matchmaker.bottom_title": "One quiz. Every dinner after that already fits.",
      "hm.matchmaker.bottom_sub": "You'll get matched inside a fortnight. It's the easiest \"yes\" you'll make this month.",
      "hm.matchmaker.bottom_cta": "Take the matching quiz",
      "hm.matchmaker.bottom_note": "No credit card · 10 minutes · Unmatch anytime",
      /* AUTO-TRANSLATED hm.* END */
    },
  },
  it: {
    translation: {
      /* AUTO-TRANSLATED it START */
      plaza_nav_features: "Funzionalità",
      plaza_nav_rewards: "Premi",
      plaza_nav_how: "Come funziona",
      plaza_nav_roadmap: "Roadmap",
      plaza_nav_faq: "FAQ",
      plaza_hero_badge: "Australian owned",
      plaza_hero_kicker: "Un carrello. Tutti i premi.",
      plaza_hero_title_1: "Acquista australiano.",
      plaza_hero_title_2: "Guadagna ovunque.",
      plaza_hero_sub: "Migliaia di venditori australiani in un unico posto. Ogni dollaro speso ti fa guadagnare HustlePoints — riscattabili presso ristoranti, caffè, hotel, parrucchieri, toelettatori per animali e qualsiasi attività della rete HustleSuite.",
      plaza_cta_start: "Iscriviti gratis — ci vogliono 30 secondi",
      plaza_cta_signin: "Accedi",
      plaza_cta_shop: "Sfoglia il negozio",
      plaza_hero_stat_1_v: "12.847",
      plaza_hero_stat_1_l: "prodotti disponibili",
      plaza_hero_stat_2_v: "340+",
      plaza_hero_stat_2_l: "venditori australiani",
      plaza_hero_stat_3_v: "$4,2M",
      plaza_hero_stat_3_l: "premi pagati",
      plaza_hero_stat_4_v: "19",
      plaza_hero_stat_4_l: "categorie",
      plaza_problem_kicker: "Il problema dello shopping oggi",
      plaza_problem_title: "Dieci schede. Dieci password. Zero fedeltà.",
      plaza_problem_sub: "Già destreggiarti tra Amazon AU, eBay AU, Kogan, Kmart, Woolies, Coles, Bunnings, JB, Officeworks e mezza dozzina di negozi boutique. Passi ore a confrontare i prezzi — e alla fine non ottieni altro che uno scontrino.",
      plaza_problem_1: "Prezzi impossibili da confrontare tra i siti",
      plaza_problem_2: "Venditori esteri che fingono di essere locali",
      plaza_problem_3: "Punti fedeltà intrappolati in un singolo negozio",
      plaza_problem_4: "Recensioni false da bot e drop-shipper",
      plaza_solution_kicker: "Cosa fa di diverso HustlePlaza",
      plaza_solution_title: "L'ultimo carrello che aprirai oggi.",
      plaza_solution_sub: "Una ricerca. Un carrello. Un checkout. Ogni acquisto ti ripaga in HustlePoints spendibili in tutto il nostro ecosistema — non solo in questo negozio.",
      plaza_solution_1_t: "Un carrello, tutti i venditori",
      plaza_solution_1_b: "Acquista da ogni venditore australiano sulla piattaforma e paga una volta sola al checkout. Consegna consolidata dove possibile.",
      plaza_solution_2_t: "Solo locali verificati",
      plaza_solution_2_b: "Ogni venditore viene verificato prima di poter pubblicare. Niente bot, niente falsi, niente misteri da drop-shipping.",
      plaza_solution_3_t: "Premi che viaggiano davvero",
      plaza_solution_3_b: "I punti guadagnati qui si spendono presso qualsiasi attività HustleSuite — caffè, hotel, parrucchieri, ristoranti, toelettatori.",
      plaza_solution_4_t: "Tracciamento dal vivo, una schermata",
      plaza_solution_4_b: "Ogni pacco di ogni venditore tracciato su una pagina. Notifiche via email e SMS ad ogni passo.",
      plaza_rewards_kicker: "Il circolo virtuoso degli HustlePoints",
      plaza_rewards_title: "Guadagna mentre fai acquisti. Spendi ovunque nella rete.",
      plaza_rewards_sub: "Ogni $1 speso su HustlePlaza ti fa guadagnare HustlePoints. Riscattali presso qualsiasi ristorante, caffè, hotel, parrucchiere o attività di servizi che usa HustleSuite — ovunque nella rete.",
      plaza_rewards_step_1: "Acquista sul marketplace",
      plaza_rewards_step_1_b: "Compra qualsiasi cosa da una maglietta a un frigorifero.",
      plaza_rewards_step_2: "Guadagna HustlePoints istantaneamente",
      plaza_rewards_step_2_b: "I punti appaiono nel momento in cui il tuo ordine viene spedito — niente tessere, niente schede da timbrare, niente giochi di scadenza.",
      plaza_rewards_step_3: "Spendi nella rete",
      plaza_rewards_step_3_b: "Riscatta presso caffè, ristoranti, hotel, parrucchieri, toelettatori, artigiani.",
      plaza_rewards_step_4: "Oppure incassa",
      plaza_rewards_step_4_b: "Converti in bonifico bancario una volta raggiunto i $50.",
      plaza_cats_kicker: "Acquista per categoria",
      plaza_cats_title: "19 categorie. Venditori australiani. Stock disponibile.",
      plaza_cats_sub: "Sfoglia una selezione di ciò che è disponibile ora — oppure iscriviti e acquista dal catalogo completo di oltre 12.000 prodotti.",
      plaza_cats_cta: "Vedi tutte le categorie",
      plaza_vendors_kicker: "Veri venditori australiani",
      plaza_vendors_title: "Incontra i produttori, i coltivatori e i negozi familiari.",
      plaza_vendors_sub: "Ogni venditore su HustlePlaza è verificato tramite ABN, ha sede in Australia ed è stato selezionato personalmente. Nessun intermediario estero che finge di essere locale.",
      plaza_how_kicker: "Come funziona",
      plaza_how_title: "Tre passaggi. Un carrello. Premi per sempre.",
      plaza_how_1_title: "Iscriviti in 30 secondi",
      plaza_how_1_body: "Verifica la tua email e inizia a navigare subito. Aggiungi un numero di telefono quando sei pronto per il checkout.",
      plaza_how_2_title: "Riempi il carrello",
      plaza_how_2_body: "Acquista da ogni venditore. Un carrello, una vista consegna, un checkout. Pagamento tramite Stripe o PayPal.",
      plaza_how_3_title: "Guadagna e spendi ovunque",
      plaza_how_3_body: "Gli HustlePoints appaiono nel momento in cui il tuo ordine viene spedito. Spendili presso qualsiasi attività HustleSuite, o incassali a $50.",
      plaza_compare_kicker: "Come ci confrontiamo",
      plaza_compare_title: "HustlePlaza vs i giganti",
      plaza_compare_col_us: "HustlePlaza",
      plaza_compare_col_amz: "Amazon AU",
      plaza_compare_col_ebay: "eBay AU",
      plaza_compare_col_kmart: "Kmart / Big W",
      plaza_compare_row_1: "Proprietà australiana",
      plaza_compare_row_2: "Carrello unico per tutti i venditori",
      plaza_compare_row_3: "Premi spendibili FUORI da questo negozio",
      plaza_compare_row_4: "Premi incassabili sul conto bancario",
      plaza_compare_row_5: "Assistenza clienti ospitata in Australia",
      plaza_trust_kicker: "Perché verifichiamo",
      plaza_trust_title: "Clienti veri. Recensioni vere.",
      plaza_trust_body: "Verifichiamo la tua email all'iscrizione, il tuo numero di cellulare prima del checkout e richiediamo una foto in tempo reale prima che tu possa pubblicare recensioni, chattare con i venditori o fare acquisti di alto valore. Questo rallenta i truffatori e mantiene i prezzi bassi per te.",
      plaza_trust_1: "Verifica email per iscriversi",
      plaza_trust_2: "Verifica SMS per fare il checkout",
      plaza_trust_3: "Verifica foto dal vivo per recensioni e chat",
      plaza_trust_4: "Controlli identità venditori all'onboarding",
      plaza_test_kicker: "Cosa dicono i nostri primi membri",
      plaza_test_title: "Direttamente dai clienti reali.",
      plaza_test_1_body: "Ho usato i miei punti HustlePlaza per pagare la cena in un caffè vicino al lavoro. Non ci credevo davvero finché la cameriera non ha sorriso e detto 'è tutto a posto'.",
      plaza_test_1_name: "Priya S.",
      plaza_test_2_body: "Sei venditori, una finestra di consegna, una email di conferma. Normalmente avrei avuto sei password e sei numeri di tracciamento. Questo funziona e basta.",
      plaza_test_2_name: "Marcus T.",
      plaza_test_3_body: "Come piccolo produttore di miele finalmente veniamo pagati lo stesso giorno, riceviamo recensioni vere e nessun taglio del 40%. HustlePlaza mi ha portato da 'attività secondaria' a tempo pieno in otto mesi.",
      plaza_test_3_name: "Kate & Dave",
      plaza_faq_kicker: "Risposte",
      plaza_faq_title: "Le domande che riceviamo ogni settimana",
      plaza_faq_1_q: "HustlePlaza è davvero di proprietà australiana?",
      plaza_faq_1_a: "Sì. HustlePlaza è al 100% di proprietà australiana, ospitato su server australiani, gestito da un team australiano. Ogni venditore viene sottoposto a verifica dell'identità prima che la sua prima inserzione possa essere pubblicata.",
      plaza_faq_2_q: "Come funzionano i premi?",
      plaza_faq_2_a: "Ogni dollaro speso su HustlePlaza ti fa guadagnare HustlePoints. I punti appaiono nel momento in cui il tuo ordine viene spedito e non scadono mai. Spendili presso qualsiasi attività che usa HustleSuite, o incassali sul tuo conto bancario una volta raggiunta la soglia.",
      plaza_faq_3_q: "Perché verificate le foto?",
      plaza_faq_3_a: "Perché le recensioni che leggi siano vere. La verifica foto è un controllo selfie dal vivo fatto tramite la fotocamera del telefono — la stessa tecnica usata dalle banche — e blocca bot, account falsi e attori ostili dal manipolare la piattaforma.",
      plaza_faq_4_q: "Cosa succede se il mio ordine non arriva?",
      plaza_faq_4_a: "Ogni ordine è coperto dalla nostra HustleGuarantee: se il pacco non arriva o non corrisponde alla descrizione, ti rimborsiamo entro 3 giorni lavorativi, poi ci occupiamo noi del venditore. Non rimarrai mai a mani vuote.",
      plaza_faq_5_q: "I dati della mia carta sono al sicuro?",
      plaza_faq_5_a: "Non vediamo né memorizziamo mai il numero della tua carta. Tutti i pagamenti vengono elaborati tramite Stripe (PCI-DSS Level 1) o PayPal, entrambi con autenticazione 3D Secure che ti protegge da addebiti fraudolenti su carte rubate.",
      plaza_faq_6_q: "Spedite a livello internazionale?",
      plaza_faq_6_a: "Non ancora. HustlePlaza al lancio è solo per l'Australia — è così che possiamo promettere la consegna entro la settimana per la maggior parte degli articoli e assistenza nel tuo fuso orario. La Nuova Zelanda è nella roadmap 2026.",
      plaza_faq_7_q: "Quanto costa iscriversi?",
      plaza_faq_7_a: "Niente. L'iscrizione è gratuita per sempre. I venditori pagano una piccola commissione per vendita — gli acquirenti pagano $0 per iscriversi, $0 per guadagnare, $0 per riscattare.",
      plaza_faq_8_q: "Posso vendere su HustlePlaza?",
      plaza_faq_8_a: "Assolutamente. Vai su hustlesuite.au per registrarti come venditore — approvazione in giornata per attività australiane verificate, più i primi 30 giorni senza commissioni.",
      plaza_final_kicker: "Prezzo Founder — posti limitati",
      plaza_final_title: "Sii tra i primi — blocca il prezzo Founder.",
      plaza_final_sub: "Registrati oggi come HustleSuite Member. Sarai il primo a sapere quando ogni nuova funzionalità viene rilasciata, e il tuo account manterrà premi e prezzi di livello Founder a vita.",
      plaza_final_bonus: "I Founder non perdono mai la loro tariffa Founder. Gratis per sempre, cancella in qualsiasi momento.",
      plaza_final_cta: "Registrati come Founder Member",
      plaza_final_disclosure: "Carta non richiesta. Solo verifica email + cellulare.",
      plaza_roadmap_kicker: "Cosa stiamo costruendo dopo",
      plaza_roadmap_title: "Sei strumenti progettati per toglierti lavoro dalle spalle.",
      plaza_roadmap_sub: "HustleSuite è più di un negozio — è un livello completo di produttività. Ecco cosa c'è in arrivo.",
      plaza_roadmap_soon: "In arrivo a breve",
      plaza_roadmap_next: "Prossimo in coda",
      plaza_roadmap_1_t: "HustleDictate — da voce a testo su qualsiasi modulo",
      plaza_roadmap_1_b: "Estensione Chrome. Premi Alt+M, parla e qualsiasi campo di input su qualsiasi sito web si compila da solo. Applicazioni immobiliari, fatture, documenti NDIS — fatti in metà tempo.",
      plaza_roadmap_2_t: "HustleConnect Edit-Anywhere",
      plaza_roadmap_2_b: "Per proprietari WordPress: modifica il tuo sito live da qualsiasi pagina con un singolo tasto rapido. Niente più giri su wp-admin.",
      plaza_roadmap_3_t: "Pulsante universale Salva-su-HustleSuite",
      plaza_roadmap_3_b: "Clic destro su qualsiasi pagina web — annuncio immobiliare, articolo, prodotto, ristorante — e finisce nel modulo giusto pronto per l'azione.",
      plaza_roadmap_4_t: "Confronta-prezzi HustlePlaza",
      plaza_roadmap_4_b: "Naviga su Amazon AU, eBay AU o Kmart / Big W — un chip fluttuante mostra lo stesso prodotto su HustlePlaza con l'anteprima dei tuoi premi.",
      plaza_roadmap_5_t: "Raccoglitore recensioni",
      plaza_roadmap_5_b: "Sincronizza automaticamente le nuove recensioni da Google, Facebook e TripAdvisor alla tua dashboard HustleSuite. Risparmia ~20 minuti a settimana.",
      plaza_roadmap_6_t: "Barra laterale HustleSuite",
      plaza_roadmap_6_b: "Pannello laterale Chrome con ordini live, KPI, pubblicazione rapida e fattura rapida. Tutta la tua attività in una striscia accanto a ogni scheda.",
      plaza_footer_owned: "Di proprietà e ospitato in Australia",
      plaza_footer_terms: "Termini",
      plaza_footer_privacy: "Privacy",
      plaza_footer_contact: "Contatti",
      plaza_footer_vendors: "Vendi su HustlePlaza",
      plaza_footer_help: "Centro assistenza",
      plaza_footer_tagline: "Parte della famiglia HustleSuite — un login, ogni servizio.",
      verify_gate_title: "Quasi fatto — registrazione veloce",
      verify_gate_body: "HustlePlaza è un carrello della spesa che offre molti vantaggi inclusi premi riscattabili utilizzabili non solo all'interno di questa piattaforma ma anche presso qualsiasi altra attività che usa la piattaforma HustlePlaza o qualsiasi altra attività registrata con HustleSuite. Alcune di queste attività includono ristoranti, caffè, hotel, parrucchieri e persino toelettatura per animali. Per continuare a offrire ottimi prezzi e servizi, dobbiamo assicurarci che tu sia reale e verificarti. Premi il pulsante Iscriviti per continuare.",
      verify_gate_join: "Iscriviti a HustleSuite",
      verify_gate_signin: "Ho già un account",
      verify_gate_cancel: "Non ora",
      verify_email_title: "Verifica la tua email",
      verify_email_body: "Invieremo un codice a 6 cifre alla tua casella di posta.",
      verify_email_placeholder: "tu@esempio.com",
      verify_email_send: "Invia codice",
      verify_email_code_ph: "Codice a 6 cifre",
      verify_email_check: "Verifica",
      verify_email_sent: "Codice inviato — controlla la tua casella di posta.",
      verify_sms_title: "Verifica il tuo telefono",
      verify_sms_body: "Aggiungi un numero di cellulare così possiamo inviarti aggiornamenti sugli ordini via SMS e confermare acquisti di importo elevato.",
      verify_sms_placeholder: "+61412345678",
      verify_sms_send: "Invia SMS",
      verify_sms_check: "Verifica",
      verify_sms_sent: "Codice inviato — controlla il tuo telefono.",
      verify_photo_title: "Verifica foto",
      verify_photo_body: "Scansiona il codice QR con il telefono per scattare un selfie dal vivo. Richiesto per chat, acquisti di alto valore e pubblicazioni in Meetup / Tackle.",
      verify_photo_waiting: "In attesa del tuo telefono…",
      verify_photo_verified: "Foto verificata — sei pronto.",
      travel_cta_start: "Iscriviti gratis",
      travel_hero_kicker: "HustleTravel",
      travel_hero_title_1: "Un'iscrizione.",
      travel_hero_title_2: "Tutta l'Australia.",
      travel_hero_sub: "Hotel, voli, esperienze, pianificazione road trip e noleggio auto — prenotati alla miglior tariffa disponibile e ti ripagano in HustlePoints per ogni dollaro speso.",
      travel_cta_plan: "Pianifica un road trip",
      travel_cta_hotels: "Cerca hotel",
      travel_svc_hotels_t: "Hotel e appartamenti",
      travel_svc_hotels_b: "Confronta oltre 200 siti di prenotazione in un'unica ricerca — metasearch alimentato da Trivago.",
      travel_svc_flights_t: "Voli",
      travel_svc_flights_b: "Nazionali e internazionali, con mappe dei posti in tempo reale e regole tariffarie.",
      travel_svc_experiences_t: "Esperienze e tour",
      travel_svc_experiences_b: "Giornate sulla barriera corallina, degustazioni in cantina, musica dal vivo, escursioni per famiglie.",
      travel_svc_cars_t: "Noleggio auto",
      travel_svc_cars_b: "Tutti i grandi marchi più indipendenti locali — ritiro in qualsiasi aeroporto australiano.",
      travel_svc_insurance_t: "Assicurazione viaggio",
      travel_svc_insurance_b: "Sottoscritta da assicuratori australiani regolamentati APRA. Confronta in una schermata.",
      travel_svc_roadtrip_t: "Pianificatore road trip",
      travel_svc_roadtrip_b: "Itinerari consapevoli dell'autonomia EV con caricatori, soste notturne e stime dei costi.",
      travel_final_kicker: "Prezzo Founder",
      travel_final_title: "Blocca i premi di livello Founder a vita.",
      travel_final_sub: "Registrati una volta con HustleSuite. Prenota tutto — hotel, voli, esperienze, assicurazioni — su HustleTravel e guadagna HustlePoints spendibili presso ogni attività della rete.",
      travel_final_cta: "Registrati come Founder Member",
      travel_final_disclosure: "Politica miglior tariffa: risultati ordinati per miglior valore per il viaggiatore, non per commissione.",
      dictate_hero_kicker: "HustleDictate",
      dictate_hero_title_1: "Parla.",
      dictate_hero_title_2: "Qualsiasi modulo. Qualsiasi sito web.",
      dictate_hero_sub: "L'unica estensione Chrome che trasforma qualsiasi campo di testo in un microfono di dettatura di livello professionale — alimentato da Whisper.",
      dictate_cta_install: "Aggiungi a Chrome",
      dictate_uc_re_t: "Note di locazione immobiliare",
      dictate_uc_re_b: "Detta rapporti di ispezione e messaggi agli inquilini direttamente nel tuo sistema PM.",
      dictate_uc_ndis_t: "Note di progresso NDIS",
      dictate_uc_ndis_b: "Parla la nota del turno mentre il ricordo è fresco — a mani libere, senza digitare.",
      dictate_uc_invoice_t: "Descrizioni fattura",
      dictate_uc_invoice_b: "Descrizioni delle voci dettate direttamente in Xero, MYOB o QuickBooks.",
      dictate_uc_email_t: "Risposte email lunghe",
      dictate_uc_email_b: "Parla le risposte in Gmail, Outlook o qualsiasi webmail — tre volte più veloce che digitare.",
      dictate_final_kicker: "Gratis per i Founder Member",
      dictate_final_title: "Ogni tastiera è ora un microfono.",
      dictate_final_sub: "Gratis per HustleSuite Founder Members. $5/mese altrimenti — cancella in qualsiasi momento.",
      dictate_final_cta: "Aggiungi a Chrome — gratis",
      "finder.cat.roadtrip": "Road trip",
      "finder.hotels.title": "Trova un hotel nelle vicinanze",
      "finder.hotels.sub": "Confronta oltre 200 siti di prenotazione in una ricerca — alimentato da Trivago.",
      "finder.hotels.where": "Dove",
      "finder.hotels.where_ph": "Città o sobborgo (es. Byron Bay, NSW)",
      "finder.hotels.checkin": "Check-in",
      "finder.hotels.checkout": "Check-out",
      "finder.hotels.adults": "Adulti",
      "finder.hotels.search": "Cerca hotel",
      "finder.hotels.disclosure": "Apre Trivago — potremmo guadagnare una commissione sulle prenotazioni.",
      /* AUTO-TRANSLATED it END */
      nav_menu: "Menù",
      nav_book: "Prenota un tavolo",
      nav_reviews: "Recensioni",
      nav_about: "Chi siamo",
      nav_account: "Il mio account",
      nav_signin: "Accedi",
      cart_open: "Apri carrello",
      cart_title: "Il tuo ordine",
      cart_empty: "Il carrello è vuoto",
      cart_subtotal: "Subtotale",
      cart_continue: "Continua",
      cart_pickup_date: "Data di ritiro",
      cart_pickup_time: "Ora di ritiro",
      cart_pay_on_pickup: "Paga al ritiro",
      cart_pay_now: "Paga ora",
      cart_place_order: "Conferma ordine",
      add_to_order: "Aggiungi al carrello",
      menu_search_ph: "Cerca nel menù…",
      menu_chef_specials: "Specialità dello chef",
      menu_under_kcal: "Sotto",
      menu_kcal_unit: "kcal",
      menu_clear_filters: "Cancella",
      menu_dietary_label: "Dietetico",
      menu_no_matches: "Nessun risultato",
      menu_relax_filters: "Prova una ricerca diversa o riduci i filtri.",
      menu_clear_all: "Cancella tutti i filtri",
      book_title: "Prenota un tavolo",
      book_name: "Nome",
      book_email: "Email",
      book_phone: "Telefono",
      book_guests: "Ospiti",
      book_date: "Data",
      book_time: "Ora",
      book_requests: "Richieste speciali",
      book_submit: "Conferma prenotazione",
      footer_rights: "Tutti i diritti riservati.",
      lang_label: "Lingua",
      welcome_back: "Bentornato",
      sign_out: "Esci",
    
      /* AUTO-TRANSLATED finder.* (added) */
      "finder.title": "HustleFinder",
      "finder.tagline": "Trova il luogo pubblico più vicino — dati reali, in tutta l'Australia.",
      "finder.search_placeholder": "Cerca luoghi, strade o sobborghi…",
      "finder.hint": "Inizia a digitare sopra, tocca una categoria o usa \"Vicino a me\".",
      "finder.near_me": "Vicino a me",
      "finder.searching": "Ricerca in corso…",
      "finder.suburb.placeholder": "Filtra per sobborgo (es. Darwin, NT)…",
      "finder.suburb.clear": "Cancella",
      "finder.search_in": "Cerca in {{cat}}",
      "finder.empty": "Nessun risultato — prova un'altra categoria o termine di ricerca.",
      "finder.meta.title": "HustleFinder — directory gratuita dei sobborghi australiani",
      "finder.meta.desc": "Cerca attività locali, stazioni di ricarica, bar, servizi igienici e altro in ogni sobborgo australiano — 100% gratuito.",
      "finder.footer.credits": "Crediti e attribuzioni dati",
      "finder.footer.terms": "Termini",
      "finder.cat.toilet": "Servizi igienici",
      "finder.cat.fountain": "Fontanelle",
      "finder.cat.transport": "Trasporti",
      "finder.cat.park": "Parchi",
      "finder.cat.medical": "Servizi medici",
      "finder.cat.school": "Scuole",
      "finder.cat.library": "Biblioteche",
      "finder.cat.petrol": "Benzina",
      "finder.cat.kidzone": "Area bimbi",
      "finder.cat.puzzles": "Rompicapi",
      /* AUTO-TRANSLATED rt.* START */
      "rt.brand_name": "Roadtrip Planner",
      "rt.page_title": "Pianificatore Road-trip EV Aussie",
      "rt.from": "Da",
      "rt.to": "A",
      "rt.from_placeholder": "Quartiere, comune o città (es. Newcastle NSW)",
      "rt.to_placeholder": "Quartiere, comune o città (es. Hobart TAS)",
      "rt.stop": "STOP",
      "rt.stop_placeholder": "Sosta intermedia",
      "rt.add_stop": "Aggiungi sosta",
      "rt.upgrade_pro": "Passa a Pro per un massimo di 20 soste",
      "rt.reverse": "Inverti (calcola il ritorno)",
      "rt.from_current": "Dalla posizione attuale",
      "rt.range_per_charge": "Autonomia per ricarica (km)",
      "rt.connector": "Connettore",
      "rt.connector_any": "Qualsiasi",
      "rt.drive_hours_day": "Ore di guida/giorno",
      "rt.accommodation": "Alloggio",
      "rt.accom_any": "Qualsiasi (BnB · Hotel · Caravan)",
      "rt.accom_bnb": "BnB",
      "rt.accom_hotel": "Hotel",
      "rt.accom_caravan": "Parco Caravan",
      "rt.min_rating": "Valutazione minima",
      "rt.any_rating": "Qualsiasi valutazione",
      "rt.only_5star": "Solo 5★",
      "rt.schedule": "Programma:",
      "rt.depart_at": "Partenza alle",
      "rt.arrive_by": "Arrivo entro",
      "rt.hotel_checkin": "Check-in hotel",
      "rt.hotel_checkout": "Check-out hotel",
      "rt.overnight_hint": "Le tappe notturne non saranno programmate fuori da questi orari.",
      "rt.whos_in_car": "Chi c'è in auto?",
      "rt.adults": "Adulti",
      "rt.kids": "Bambini",
      "rt.ages": "Età",
      "rt.show_attractions": "Mostra attrazioni turistiche",
      "rt.show_kids": "Mostra soste per bambini",
      "rt.overnight_stays": "Pernottamenti",
      "rt.fast_chargers_only": "Solo ricarica rapida",
      "rt.hotel_must_charger": "L'hotel deve avere una ricarica",
      "rt.pet_only": "Solo alloggi per animali",
      "rt.safety": "Sicurezza",
      "rt.safety_90": "90% (pianura)",
      "rt.safety_85": "85% (predefinito)",
      "rt.safety_80": "80% (colline / traino)",
      "rt.safety_70": "70% (carico pesante)",
      "rt.save_trip": "Salva viaggio",
      "rt.plan_my_trip": "Pianifica viaggio",
      "rt.refresh_trip": "Aggiorna viaggio",
      "rt.planning": "Pianificazione in corso…",
      "rt.resume_title": "Riprendi viaggio da qui",
      "rt.resume_body": "Abbiamo rilevato la tua posizione attuale. Inserisci l'autonomia residua sul cruscotto per pianificare la prossima tappa in base alla carica reale, non a quella completa.",
      "rt.remaining_range": "Autonomia residua (km)",
      "rt.cancel": "Annulla",
      "rt.replan_from_here": "Ripianifica da qui",
      "rt.save_modal_title": "Dai un nome a questo viaggio",
      "rt.save_modal_body": "I viaggi salvati sono nel tuo profilo — riaprili quando vuoi e clicca su Ripianifica.",
      "rt.save_name_placeholder": "es. Brisbane → Sydney via Byron",
      "rt.your_saved_trips": "I tuoi viaggi salvati",
      "rt.custom_location": "Posizione personalizzata",
      "rt.trip_summary": "Riepilogo viaggio",
      "rt.itinerary": "Itinerario",
      "rt.no_stops_needed": "Nessuna sosta necessaria — l'intero viaggio rientra nell'autonomia.",
      "rt.chargers_in_corridor": "colonnine nel percorso",
      "rt.planned_stops": "soste pianificate",
      "rt.overnight_stop": "sosta notturna",
      "rt.overnight_stops": "soste notturne",
      "rt.suggested_detours": "Deviazioni suggerite — seleziona per aggiungere",
      "rt.replan_with_detours": "Ripianifica con deviazioni",
      "rt.attractions_label": "Attrazione",
      "rt.kids_label": "Bambini",
      "rt.km_from_start": "km dall'inizio",
      "rt.open_driving": "Apri modalità guida",
      "rt.big_font_hint": "Caratteri grandi, ottimizzato per supporto auto. Funziona con mirroring CarPlay/Android Auto.",
      "rt.check_price_book": "Controlla prezzi e prenota su Trivago ↗",
      "rt.visit_site": "Visita il sito ↗",
      "rt.charger_onsite": "Ricarica in loco",
      "rt.leg_exceeds_range": "Questa tappa supera l'autonomia impostata — rimarresti senza energia prima di raggiungere la colonnina. Riduci l'autonomia stimata o aggiungi una ricarica intermedia.",
      "rt.add_waypoint_replan": "Aggiungi tappa e ripianifica",
      /* AUTO-TRANSLATED rt.* END */
                      /* AUTO-TRANSLATED hm.* START */
      "hm.nav.hustlemeet": "HustleMeet",
      "hm.nav.circles": "Circles",
      "hm.nav.matchmaker": "Matchmaker",
      "hm.nav.meet": "Incontra",
      "hm.nav.overview": "HustleMeetups",
      "hm.overview.page_title": "HustleMeetups — tre modi calorosi per trovare la tua gente.",
      "hm.overview.kicker": "Parte di HustleSuite",
      "hm.overview.hero_title": "Tre modi calorosi per trovare la tua gente.",
      "hm.overview.hero_title_accent": "la tua gente",
      "hm.overview.hero_sub": "HustleMeetups è il modo più gentile in Australia per costruire amicizie reali — scegli l'atmosfera che fa per te. Che tu voglia un gruppo di corsa settimanale, una cena mensile con la tua cerchia attuale, o un tavolo di volti nuovi abbinati dall'AI, c'è un posto per te.",
      "hm.overview.tile_meet_kicker": "HustleMeet",
      "hm.overview.tile_meet_title": "Trova la tua gente. Dedicati alle tue passioni.",
      "hm.overview.tile_meet_body": "Il punto di riferimento in Australia per i gruppi basati sugli interessi. Runner, programmatori, investitori, camminatori, ballerini, club della colazione, gruppi di lettura — centinaia di gruppi, eventi reali ogni settimana. Partecipa una volta, o ogni settimana.",
      "hm.overview.tile_meet_cta": "Esplora i gruppi",
      "hm.overview.tile_circles_kicker": "Circles",
      "hm.overview.tile_circles_title": "La tua cerchia. Un nuovo tavolo ogni volta.",
      "hm.overview.tile_circles_body": "Porta il tuo gruppo abituale di amici. Noi prenotiamo un nuovo ristorante, organizziamo il trivia, distribuiamo le carte. Tu devi solo presentarti. Il modo più stretto per tenere vicina una cerchia esistente — senza che nessuno debba organizzarla.",
      "hm.overview.tile_circles_cta": "Inizia un Circle",
      "hm.overview.tile_matchmaker_kicker": "Matchmaker",
      "hm.overview.tile_matchmaker_title": "L'AI sceglie il tavolo. Tu ti presenti.",
      "hm.overview.tile_matchmaker_body": "Rispondi a un quiz di 10 minuti. In una notte, ti facciamo sedere con sconosciuti che entro il dessert sembreranno vecchi amici. Un tavolo diverso ogni volta.",
      "hm.overview.tile_matchmaker_cta": "Fai il quiz",
      "hm.overview.trust_kicker": "Perché funziona",
      "hm.overview.trust_title": "Prima la fiducia. Poi il tavolo.",
      "hm.overview.trust_1_title": "Membri verificati",
      "hm.overview.trust_1_body": "Ogni gruppo inizia con un controllo dell'identità reale + un punteggio di affidabilità. Niente account usa e getta.",
      "hm.overview.trust_2_title": "Host reali",
      "hm.overview.trust_2_body": "Ogni gruppo è guidato da un host selezionato che cura l'atmosfera, stabilisce il ritmo e si prende cura della sala.",
      "hm.overview.trust_3_title": "Prima l'amicizia",
      "hm.overview.trust_3_body": "Tutto qui è progettato per amicizie reali, non per appuntamenti. Mai.",
      "hm.overview.trust_4_title": "Moderazione a tolleranza zero",
      "hm.overview.trust_4_body": "Una segnalazione, una revisione, una decisione. Chi si comporta male viene rimosso la sera stessa.",
      "hm.overview.footer_line": "Parte di HustleSuite — di proprietà e ospitato in Australia.",
      "hm.landing.page_title": "HustleMeet · Trova la tua gente. Dedicati alle tue passioni.",
      "hm.landing.kicker": "La rete di interessi dell'Australia",
      "hm.landing.hero_title": "Trova la tua gente. Dedicati alle tue passioni.",
      "hm.landing.hero_title_accent": "la tua gente",
      "hm.landing.hero_sub": "HustleMeet è il punto di riferimento in Australia per i gruppi basati sugli interessi. Sviluppatori software che fanno debugging a pranzo. Runner di lunga distanza alle 6 del mattino. Investitori che si incontrano mensilmente. Gruppi di cammino, crew di ballo, club della colazione, serate di giochi da tavolo. Scegli un gruppo, prenota il prossimo evento e presentati.",
      "hm.landing.cta_explore_circles": "Sfoglia i gruppi",
      "hm.landing.cta_try_matchmaker": "Inizia il tuo gruppo",
      "hm.landing.trust_verified": "Membri verificati",
      "hm.landing.trust_venues": "Host di eventi selezionati",
      "hm.landing.trust_friendship": "Prima l'amicizia — mai appuntamenti",
      "hm.landing.trust_moderation": "Moderazione a tolleranza zero",
      "hm.landing.subbrand_circles_kicker": "Circles",
      "hm.landing.subbrand_circles_title": "Hai già la tua cerchia? Dalle una casa permanente.",
      "hm.landing.subbrand_circles_body": "Circles è per il gruppo di amici che si incontra già — club del libro, serata poker, crew di corsa, colleghi. Gestiamo noi il locale, i promemoria e i turni. Tu devi solo presentarti.",
      "hm.landing.subbrand_circles_b1": "Locali a rotazione, un solo elenco partecipanti",
      "hm.landing.subbrand_circles_b2": "Promemoria automatici + RSVP facili",
      "hm.landing.subbrand_circles_b3": "Trivia, carte e serate a tema sempre disponibili",
      "hm.landing.subbrand_circles_cta": "Inizia un Circle",
      "hm.landing.subbrand_matchmaker_kicker": "Matchmaker",
      "hm.landing.subbrand_matchmaker_title": "L'AI sceglie il tavolo. Tu ti presenti.",
      "hm.landing.subbrand_matchmaker_body": "Un quiz di 10 minuti — personalità, valori, disponibilità. Il nostro Matchmaker ti fa sedere con sconosciuti che entro il dessert sembreranno vecchi amici. Un tavolo diverso ogni volta.",
      "hm.landing.subbrand_matchmaker_b1": "Punteggio personalità Big-5 e valori",
      "hm.landing.subbrand_matchmaker_b2": "Inviti immediati quando la tua affinità è 0.70 o superiore",
      "hm.landing.subbrand_matchmaker_b3": "Impara da ogni cena che fai",
      "hm.landing.subbrand_matchmaker_cta": "Fai il quiz",
      "hm.landing.how_kicker": "Come funziona HustleMeet",
      "hm.landing.how_title": "Quattro passaggi. Trova il tuo gruppo. Presentati.",
      "hm.landing.how_step1_ttl": "Scegli i tuoi interessi",
      "hm.landing.how_step1_sub": "Corsa, programmazione, investimenti, cucina, musica, camminata — dicci cosa ti piace e dove vivi.",
      "hm.landing.how_step2_ttl": "Sfoglia i gruppi vicino a te",
      "hm.landing.how_step2_sub": "Ogni gruppo pubblica il suo host, il suo ritmo e il suo prossimo evento. Valuta l'ambiente prima di impegnarti.",
      "hm.landing.how_step3_ttl": "Conferma la presenza al prossimo evento",
      "hm.landing.how_step3_sub": "Un tocco, un invito nel calendario, un promemoria chiaro il giorno prima. Alcuni eventi sono gratuiti, altri a pagamento — il prezzo è indicato chiaramente.",
      "hm.landing.how_step4_ttl": "Presentati e connettiti",
      "hm.landing.how_step4_sub": "Incontra l'host, incontra la crew, dedica tempo alle tue passioni. Torna la settimana successiva se c'è stata sintonia.",
      "hm.landing.stats_kicker": "Il nuovo modo di incontrarsi in Australia",
      "hm.landing.stats_title": "Il motivo per cui le persone continuano a tornare.",
      "hm.landing.stats_92_sub": "Sentono di aver legato con almeno una persona all'evento",
      "hm.landing.stats_68_sub": "Confermano la presenza al loro prossimo evento entro una settimana",
      "hm.landing.stats_85_sub": "Rimangono in contatto con qualcuno del gruppo",
      "hm.landing.why_kicker": "Perché funzioniamo",
      "hm.landing.why_title": "Interessi reali. Host reali. Eventi reali.",
      "hm.landing.why_1_ttl": "Prima gli interessi, non la posizione",
      "hm.landing.why_1_body": "Le persone nel tuo gruppo si interessano davvero a ciò che sta a cuore a te — questa è la scorciatoia per l'amicizia vera.",
      "hm.landing.why_2_ttl": "Host di eventi reali",
      "hm.landing.why_2_body": "Ogni gruppo ha un host selezionato che cura l'atmosfera, stabilisce il ritmo e interviene se qualcosa non va. Non un amministratore di Facebook. Una persona reale.",
      "hm.landing.why_3_ttl": "La costanza è importante",
      "hm.landing.why_3_body": "Nuovi eventi ogni settimana. Qualcosa da aspettare con impazienza. Un ritmo che trasforma gli sconosciuti nella tua crew del martedì sera.",
      "hm.landing.why_4_ttl": "Sicuro e protetto",
      "hm.landing.why_4_body": "Membri verificati, locali pubblici, tolleranza zero per la mancanza di rispetto. Il nostro team di supporto è disponibile prima, durante e dopo ogni evento.",
      "hm.landing.why_5_ttl": "Prima l'amicizia",
      "hm.landing.why_5_body": "Questo non è dating. È comunità. Interessi reali, persone reali, nessuno swipe di mezzo.",
      "hm.landing.why_6_ttl": "Abbastanza piccolo da contare",
      "hm.landing.why_6_body": "La maggior parte dei gruppi limita gli eventi a 4-15 posti. Abbastanza grande per innescare conversazioni reali. Abbastanza piccolo perché nessuno si perda.",
      "hm.landing.cities_kicker": "Dove operiamo",
      "hm.landing.cities_title": "Gruppi che si incontrano in tutta l'Australia & NZ.",
      "hm.landing.cities_sub_zero": "Primi gruppi in apertura in oltre 15 città — unisciti presto e plasma l'ambiente.",
      "hm.landing.cities_sub_some": "Gruppi attivi in oltre 15 aree metropolitane. Qualcosa nella tua città, questa settimana.",
      "hm.landing.cities_sub_one": "{{count}} gruppo attivo · nuovi eventi ogni settimana · aggiungiamo sempre nuove città.",
      "hm.landing.cities_sub_many": "{{count}} gruppi attivi · nuovi eventi ogni settimana · aggiungiamo sempre nuove città.",
      "hm.landing.forme_kicker": "Questo fa per te se…",
      "hm.landing.forme_title": "Vuoi interessi reali. Persone reali. Eventi reali.",
      "hm.landing.forme_1": "Sei stanco della fatica delle app di swipe e vuoi incontrare persone davvero attorno a ciò che ami fare.",
      "hm.landing.forme_2": "Ti sei trasferito in una nuova città e hai bisogno di una crew calorosa, non di un altro gruppo chat.",
      "hm.landing.forme_3": "Dai valore alla profondità rispetto alla diffusione — un grande gruppo batte cinquanta follower.",
      "hm.landing.forme_4": "Credi che interessi condivisi + due ore stabilite nella vita reale siano la marcia in più.",
      "hm.landing.notme_kicker": "Questo non fa per te se…",
      "hm.landing.notme_title": "HustleMeet non funziona per tutti.",
      "hm.landing.notme_1": "Non puoi impegnarti a presentarti — host e membri contano sulla tua presenza.",
      "hm.landing.notme_2": "Stai cercando un servizio di incontri — qui le amicizie vengono prima di tutto.",
      "hm.landing.notme_3": "Vuoi comunicare senza incontrare nessuno — questo funziona solo di persona.",
      "hm.landing.notme_4": "Ti aspetti infiniti vantaggi gratuiti — alcuni eventi hanno un costo. Il prezzo è sempre anticipato.",
      "hm.landing.safety_kicker": "Il nostro impegno per la sicurezza",
      "hm.landing.safety_title": "Calorosi, ma mai imprudenti.",
      "hm.landing.safety_1_ttl": "Locali pubblici",
      "hm.landing.safety_1_body": "Ogni evento si svolge in un locale pubblico con personale — un caffè, ristorante, parco, palestra o studio. Mai a casa di qualcuno a meno che ogni partecipante non acconsenta.",
      "hm.landing.safety_2_ttl": "Standard della comunità",
      "hm.landing.safety_2_body": "Tolleranza zero per mancanza di rispetto, molestie o disonestà. I membri che violano le regole vengono rimossi immediatamente.",
      "hm.landing.safety_3_ttl": "Privacy protetta",
      "hm.landing.safety_3_body": "I tuoi dati non vengono mai condivisi pubblicamente. Tu controlli con chi rimanere in contatto dopo ogni evento.",
      "hm.landing.safety_4_ttl": "Supporto disponibile",
      "hm.landing.safety_4_body": "Il nostro team è disponibile prima, durante e dopo ogni evento se hai domande o preoccupazioni.",
      "hm.landing.bottom_title": "Il tuo prossimo gruppo preferito è a un tocco di distanza.",
      "hm.landing.bottom_sub": "Scegli la tua strada — unisciti a un gruppo esistente, porta la tua crew in un Circle, o lascia che il Matchmaker ti faccia sedere a un tavolo già adatto a te.",
      "hm.landing.bottom_cta_circles": "Inizia un Circle",
      "hm.landing.bottom_cta_mm": "Fai il quiz del Matchmaker",
      "hm.circles.page_title": "HustleMeet Circles · La tua cerchia, un nuovo locale ogni volta",
      "hm.circles.kicker": "HustleMeet · Circles",
      "hm.circles.hero_title": "La tua cerchia. Un nuovo tavolo ogni volta.",
      "hm.circles.hero_title_accent": "La tua cerchia",
      "hm.circles.hero_sub": "Hai già i tuoi amici? Circles è il modo più semplice per tenerli vicini. Noi prenotiamo il locale, organizziamo il trivia, distribuiamo le carte. Tu devi solo presentarti — stessa crew, nuova serata, nuovo posto.",
      "hm.circles.cta_find": "Inizia un Circle",
      "hm.circles.cta_host_hero": "Unisciti a un Circle aperto",
      "hm.circles.trust_venue": "Locali selezionati a rotazione",
      "hm.circles.trust_party": "4-12 amici per Circle",
      "hm.circles.trust_verified": "Membri verificati",
      "hm.circles.trust_friendship": "Amicizie reali, tenute vicine",
      "hm.circles.how_kicker": "How it works",
      "hm.circles.how_title": "Quattro passaggi. La tua crew rimane una crew.",
      "hm.circles.how_step1_ttl": "Inizia il tuo Circle",
      "hm.circles.how_step1_sub": "Dale un nome, invita i tuoi amici (fino a 12) e scegli un ritmo — settimanale, quindicinale o mensile.",
      "hm.circles.how_step2_ttl": "Noi scegliamo il locale",
      "hm.circles.how_step2_sub": "Per ogni evento suggeriamo un nuovo posto — ristorante, serata trivia, sala da gioco, wine bar. Oppure prenota il tuo preferito.",
      "hm.circles.how_step3_ttl": "Tutti confermano con un tocco",
      "hm.circles.how_step3_sub": "Promemoria automatici, divisione dei costi, posti prepagati così i no-show non rovinano i piani. Il conto è sistemato prima che arrivi l'antipasto.",
      "hm.circles.how_step4_ttl": "Mantieni il ritmo",
      "hm.circles.how_step4_sub": "Una serata in agenda che non dovrà mai più essere organizzata. Il gruppo si stringe nel corso dei mesi, non dei minuti.",
      "hm.circles.cadence_weekly_ttl": "Settimanale",
      "hm.circles.cadence_weekly_sub": "Ogni settimana",
      "hm.circles.cadence_weekly_body": "Per le crew che vogliono un rituale del martedì sera — stessi volti, nuovo posto ogni volta.",
      "hm.circles.cadence_fortnight_ttl": "Quindicinale",
      "hm.circles.cadence_fortnight_sub": "Ogni 2 settimane",
      "hm.circles.cadence_fortnight_body": "Il punto di equilibrio — regolare senza essere troppo impegnativo per le agende piene.",
      "hm.circles.cadence_monthly_ttl": "Mensile",
      "hm.circles.cadence_monthly_sub": "Ogni mese",
      "hm.circles.cadence_monthly_body": "Circles per occasioni speciali — arrosti della domenica, grandi serate trivia, degustazioni, poker.",
      "hm.circles.feast_kicker": "Scegli la tua serata",
      "hm.circles.feast_title": "Tre modi per trascorrerla",
      "hm.circles.feast_sub": "Ogni Circle sceglie come si svolge la serata. Ruotateli, o puntate sul preferito della vostra crew.",
      "hm.circles.feast_surprise_ttl": "Prova un nuovo ristorante",
      "hm.circles.feast_surprise_body": "Selezioniamo tre locali vicino a te, tutti votano, la cucina si prepara per un tavolo completo. Costi divisi, posti prepagati.",
      "hm.circles.feast_preselect_ttl": "Trivia / carte / giochi",
      "hm.circles.feast_preselect_body": "Prenota una serata trivia, una sala da poker, un bar di giochi da tavolo o una pista da bowling. Gestiamo noi il locale, la quota di iscrizione e i promemoria.",
      "hm.circles.feast_per_event_ttl": "Serata a tema una tantum",
      "hm.circles.feast_per_event_body": "Degustazione di vini, tavolo dello chef, corso di cocktail, escape room. Qualcosa di speciale ogni tanto — completamente organizzato.",
      "hm.circles.live_kicker": "Proprio ora",
      "hm.circles.live_title": "Circles a cui puoi unirti",
      "hm.circles.live_host_link": "Inizia il tuo",
      "hm.circles.live_loading": "Caricamento…",
      "hm.circles.live_empty_title": "Sii il primo Circle nella tua città.",
      "hm.circles.live_empty_sub": "Inizia con tre amici e ti aiuteremo a invitare gli altri — in più ti offriamo i primi tre locali.",
      "hm.circles.live_empty_cta": "Inizia un Circle",
      "hm.circles.host_kicker": "Per i leader dei Circle",
      "hm.circles.host_title": "Tu porti la crew. Noi facciamo l'amministrazione.",
      "hm.circles.host_body": "Se sei l'amico che organizza sempre la serata, Circles ti fa risparmiare ore. Promemoria automatici, RSVP prepagate, liste di locali, divisione dei costi, gestione della lista d'attesa e un portafoglio cashback su ogni serata fuori. Tu ti presenti semplicemente come un normale membro.",
      "hm.circles.host_perk_1": "Liste di locali automatiche — mai più ricerche infinite su Google",
      "hm.circles.host_perk_2": "Posti prepagati — zero no-show",
      "hm.circles.host_perk_3": "Divisione dei costi fatta prima del dessert",
      "hm.circles.host_perk_4": "Promemoria + inviti in calendario in pilota automatico",
      "hm.circles.host_perk_5": "Lista d'attesa con promozione automatica se un amico annulla",
      "hm.circles.host_perk_6": "Portafoglio cashback su ogni serata fuori",
      "hm.circles.host_cta": "Inizia un Circle ora",
      "hm.circles.why_kicker": "Perché i Circles funzionano",
      "hm.circles.why_title": "Le amicizie tra adulti hanno bisogno di infrastruttura",
      "hm.circles.why_1_ttl": "Stessa crew, fiducia più profonda",
      "hm.circles.why_1_body": "La vita allontana gli amici. I Circles sono il ritmo fisso che tiene tutti vicini — gli stessi 4-12 amici, che si incontrano nella stessa sera della settimana, qualunque cosa succeda.",
      "hm.circles.why_2_ttl": "Zero fatica amministrativa",
      "hm.circles.why_2_body": "Nessuno deve essere il capo del gruppo WhatsApp. Noi scegliamo i locali, inviamo i promemoria, prendiamo i pagamenti, chiudiamo il cerchio.",
      "hm.circles.why_3_ttl": "Nuovo locale, nuovi ricordi",
      "hm.circles.why_3_body": "Ruota tra i migliori piccoli ristoranti d'Australia, sale trivia e game bar. Volti familiari, sfondi nuovi — il punto d'incontro ideale tra routine e novità.",
      "hm.circles.bottom_title": "La serata che la tua crew aspetta con impazienza, senza che nessuno debba pianificarla.",
      "hm.circles.bottom_sub": "Un Circle. Stessi amici. Un nuovo locale ogni volta.",
      "hm.circles.bottom_cta_find": "Inizia un Circle",
      "hm.circles.bottom_cta_mm": "Incontra nuove persone con Matchmaker →",
      "hm.circles.card_host_pill": "Circle",
      "hm.matchmaker.page_title": "HustleMeet Matchmaker · L'AI sceglie il tavolo giusto",
      "hm.matchmaker.kicker": "HustleMeet · Matchmaker",
      "hm.matchmaker.hero_title": "L'AI sceglie il tavolo. Tu ti presenti e basta.",
      "hm.matchmaker.hero_title_accent": "Tu ti presenti e basta.",
      "hm.matchmaker.hero_sub": "Rispondi a un breve quiz sulla personalità e sui valori. Il nostro Matchmaker ti fa sedere con un numero da quattro a sette sconosciuti che sembreranno vecchi amici entro il dessert. Niente swipe. Niente chiacchiere di circostanza. Solo un tavolo condiviso, pronto quando lo sei tu.",
      "hm.matchmaker.cta_quiz": "Fai il quiz di affinità",
      "hm.matchmaker.cta_see_circles": "Vedi Circles invece →",
      "hm.matchmaker.trust_big5": "Personalità Big-5 valutata",
      "hm.matchmaker.trust_seats": "4-8 posti per tavolo",
      "hm.matchmaker.trust_lock": "Zero-swipe · zero-foto",
      "hm.matchmaker.trust_verified": "Solo membri verificati",
      "hm.matchmaker.how_kicker": "How it works",
      "hm.matchmaker.how_title": "Quattro passaggi per un tavolo già adatto a te.",
      "hm.matchmaker.how_step1_ttl": "Fai il quiz",
      "hm.matchmaker.how_step1_sub": "Questionario di dieci minuti — personalità, valori, dieta, disponibilità. Lo fai una volta sola.",
      "hm.matchmaker.how_step2_ttl": "Abbinamento notturno",
      "hm.matchmaker.how_step2_sub": "La nostra AI legge il tuo profilo e l'elenco delle cene attive in Australia. Quando il tuo punteggio di affinità è 0.70 o superiore, prepara un invito personale.",
      "hm.matchmaker.how_step3_ttl": "Accetta o rifiuta",
      "hm.matchmaker.how_step3_sub": "Ricevi un'email di invito chiara con il locale, il tema del tavolo e i tre interessi che condividi con il gruppo.",
      "hm.matchmaker.how_step4_ttl": "Impara da te",
      "hm.matchmaker.how_step4_sub": "Ogni sondaggio post-cena affina il modello. Il tuo prossimo abbinamento diventa più intelligente più ceni.",
      "hm.matchmaker.science_kicker": "Perché funziona",
      "hm.matchmaker.science_title": "Il Matchmaker non tira a indovinare — assegna un punteggio.",
      "hm.matchmaker.science_sub": "Ogni abbinamento è un insieme pesato di quattro fattori. Quando l'intero tavolo ha una media superiore a 0.70, inviamo gli inviti. Qualsiasi valore inferiore rimane in coda.",
      "hm.matchmaker.science_1_ttl": "Personalità (35%)",
      "hm.matchmaker.science_1_body": "Somiglianza del coseno attraverso il vettore della personalità Big-5 — apertura, coscienziosità, estroversione, gradevolezza e stabilità emotiva.",
      "hm.matchmaker.science_2_ttl": "Valori (25%)",
      "hm.matchmaker.science_2_body": "Sovrapposizione sui valori che rendono una conversazione a cena davvero efficace — famiglia, curiosità, crescita, servizio, avventura.",
      "hm.matchmaker.science_3_ttl": "Interessi (25%)",
      "hm.matchmaker.science_3_body": "Sovrapposizione di Jaccard sui tuoi interessi specifici — escursione sul Six Foot Track, film club di Sydney, scena del caffè di Melbourne, il tuo terzo libro dell'anno.",
      "hm.matchmaker.science_4_ttl": "Disponibilità (15%)",
      "hm.matchmaker.science_4_body": "La serata in cui puoi davvero partecipare. Nessun invito per martedì alle 19:00 se il tuo calendario dice palestra-ogni-martedì.",
      "hm.matchmaker.safety_kicker": "Sicurezza per design",
      "hm.matchmaker.safety_title": "Il Matchmaker è discretamente cauto.",
      "hm.matchmaker.safety_1_ttl": "Soglia di confidenza",
      "hm.matchmaker.safety_1_body": "Al di sotto di un punteggio di gruppo di 0.70, non parte alcun invito. Un abbinamento incerto è peggio di nessuna cena.",
      "hm.matchmaker.safety_2_ttl": "Blocklist reciproca",
      "hm.matchmaker.safety_2_body": "Se due membri segnalano privatamente \"no\" dopo una cena, non li faremo mai più sedere insieme.",
      "hm.matchmaker.safety_3_ttl": "Interruttore di spegnimento automatico",
      "hm.matchmaker.safety_3_body": "Se il 20% o più dei sondaggi recenti valuta un tavolo 2/5 o meno, gli inviti si interrompono per 24 ore mentre un operatore esamina la situazione.",
      "hm.matchmaker.safety_4_ttl": "Standard della comunità",
      "hm.matchmaker.safety_4_body": "Ogni membro accetta un codice di condotta in un linguaggio semplice. Rimuoviamo le persone che lo violano — senza appello.",
      "hm.matchmaker.safety_5_ttl": "Solo locali pubblici",
      "hm.matchmaker.safety_5_body": "Ogni cena del Matchmaker si svolge in un vero ristorante con personale. Mai a casa di qualcuno.",
      "hm.matchmaker.safety_6_ttl": "Feedback post-evento",
      "hm.matchmaker.safety_6_body": "Il tuo sondaggio privato modella i futuri abbinamenti e può innescare una revisione da parte dei moderatori del locale o di un partecipante.",
      "hm.matchmaker.loop_kicker": "Il ciclo di apprendimento",
      "hm.matchmaker.loop_title": "Ogni cena rende migliore la tua cena successiva.",
      "hm.matchmaker.loop_body": "Il Matchmaker non è statico. Dopo ogni evento invii un breve sondaggio privato — come ti è sembrata la conversazione, con chi ceneresti di nuovo, se il locale è stato all'altezza. Questi segnali tornano nel modello e rimodellano ogni futuro abbinamento.",
      "hm.matchmaker.loop_1": "I voti \"cena di nuovo\" dei partecipanti rafforzano i tuoi abbinamenti",
      "hm.matchmaker.loop_2": "I voti \"no\" reciproci ritirano silenziosamente le coppie",
      "hm.matchmaker.loop_3": "Le valutazioni sul locale e sulla conversazione affinano il mix dei posti",
      "hm.matchmaker.loop_4": "Il punteggio di affidabilità protegge i membri dai no-show ripetuti",
      "hm.matchmaker.loop_5": "Il bias di apprendimento rafforza i buoni abbinamenti mese dopo mese",
      "hm.matchmaker.loop_6": "Mantieni il controllo — disattiva o resetta il tuo profilo in qualsiasi momento",
      "hm.matchmaker.vs_kicker": "Quale è adatto a me?",
      "hm.matchmaker.vs_title": "Matchmaker o Circles?",
      "hm.matchmaker.vs_mm_ttl": "Scegli me se voglio varietà.",
      "hm.matchmaker.vs_mm_1": "Un tavolo, una città, una cucina diversa ogni volta",
      "hm.matchmaker.vs_mm_2": "Gruppo curato dall'AI di 4-8 sconosciuti compatibili",
      "hm.matchmaker.vs_mm_3": "Ritmo settimanale, nessun impegno come host",
      "hm.matchmaker.vs_mm_4": "Ottimo se sei nuovo in una città o in viaggio",
      "hm.matchmaker.vs_mm_cta": "Fai il quiz di affinità",
      "hm.matchmaker.vs_circles_ttl": "Scegli me se voglio un appuntamento fisso.",
      "hm.matchmaker.vs_circles_1": "Stesso host · stesso locale · stessa sera della settimana",
      "hm.matchmaker.vs_circles_2": "Un gruppo che diventa la tua tradizione del mercoledì",
      "hm.matchmaker.vs_circles_3": "Curato da un host umano esperto",
      "hm.matchmaker.vs_circles_4": "Ottimo se ti sei appena trasferito e vuoi mettere radici",
      "hm.matchmaker.vs_circles_cta": "Sfoglia i Circles",
      "hm.matchmaker.bottom_title": "Un quiz. Ogni cena successiva è già quella giusta.",
      "hm.matchmaker.bottom_sub": "Sarai abbinato entro due settimane. È il \"sì\" più facile che dirai questo mese.",
      "hm.matchmaker.bottom_cta": "Fai il quiz di affinità",
      "hm.matchmaker.bottom_note": "Nessuna carta di credito · 10 minuti · Annulla l'abbinamento in qualsiasi momento",
      /* AUTO-TRANSLATED hm.* END */
    },
  },
  vi: {
    translation: {
      /* AUTO-TRANSLATED vi START */
      plaza_nav_features: "Tính năng",
      plaza_nav_rewards: "Phần thưởng",
      plaza_nav_how: "Cách hoạt động",
      plaza_nav_roadmap: "Lộ trình",
      plaza_nav_faq: "Câu hỏi thường gặp",
      plaza_hero_badge: "Sở hữu bởi người Úc",
      plaza_hero_kicker: "Một giỏ hàng. Mọi phần thưởng.",
      plaza_hero_title_1: "Mua sắm Úc.",
      plaza_hero_title_2: "Kiếm điểm mọi nơi.",
      plaza_hero_sub: "Hàng nghìn nhà bán hàng Úc trong một rổ. Mỗi đô la bạn chi tiêu đều kiếm được HustlePoints — có thể đổi tại nhà hàng, quán cà phê, khách sạn, salon tóc, cửa hàng chăm sóc thú cưng và bất kỳ doanh nghiệp nào trong mạng lưới HustleSuite.",
      plaza_cta_start: "Tham gia miễn phí — chỉ mất 30 giây",
      plaza_cta_signin: "Đăng nhập",
      plaza_cta_shop: "Duyệt cửa hàng",
      plaza_hero_stat_1_v: "12.847",
      plaza_hero_stat_1_l: "sản phẩm đang hoạt động",
      plaza_hero_stat_2_v: "340+",
      plaza_hero_stat_2_l: "nhà bán hàng Úc",
      plaza_hero_stat_3_v: "$4,2M",
      plaza_hero_stat_3_l: "phần thưởng đã trả",
      plaza_hero_stat_4_v: "19",
      plaza_hero_stat_4_l: "danh mục",
      plaza_problem_kicker: "Vấn đề với việc mua sắm ngày nay",
      plaza_problem_title: "Mười tab. Mười mật khẩu. Không trung thành.",
      plaza_problem_sub: "Bạn đã phải quản lý Amazon, eBay, Kogan, Kmart, Woolies, Coles, Bunnings, JB, Officeworks và một nửa tá cửa hàng nhỏ khác. Bạn dành hàng giờ so sánh giá — rồi chẳng nhận được gì ngoài hoá đơn.",
      plaza_problem_1: "Giá cả không thể so sánh giữa các trang web",
      plaza_problem_2: "Người bán ở nước ngoài giả vờ là người địa phương",
      plaza_problem_3: "Điểm thưởng bị khóa trong một cửa hàng duy nhất",
      plaza_problem_4: "Đánh giá giả từ bot và người bán thả hàng",
      plaza_solution_kicker: "HustlePlaza khác biệt như thế nào",
      plaza_solution_title: "Giỏ hàng cuối cùng bạn mở cả ngày.",
      plaza_solution_sub: "Một tìm kiếm. Một giỏ hàng. Một thanh toán. Mỗi giao dịch mua trả lại cho bạn HustlePoints có thể chi tiêu trên toàn bộ hệ sinh thái của chúng tôi — không chỉ cửa hàng này.",
      plaza_solution_1_t: "Một giỏ hàng, mọi nhà bán",
      plaza_solution_1_b: "Mua sắm từ mọi người bán Úc trên nền tảng và thanh toán một lần. Giao hàng được hợp nhất khi có thể.",
      plaza_solution_2_t: "Chỉ người địa phương đã xác minh",
      plaza_solution_2_b: "Mỗi người bán đều được xác minh danh tính trước khi có thể đăng sản phẩm. Không có bot, không giả mạo, không bí ẩn về thả hàng.",
      plaza_solution_3_t: "Phần thưởng thực sự di chuyển được",
      plaza_solution_3_b: "Điểm kiếm được ở đây có thể chi tiêu tại bất kỳ doanh nghiệp HustleSuite nào — quán cà phê, khách sạn, salon tóc, nhà hàng, cửa hàng chăm sóc thú cưng.",
      plaza_solution_4_t: "Theo dõi trực tiếp, một màn hình",
      plaza_solution_4_b: "Mọi kiện hàng từ mọi nhà bán đều được theo dõi trên một trang. Thông báo qua email và SMS tại mọi bước.",
      plaza_rewards_kicker: "Vòng quay HustlePoints",
      plaza_rewards_title: "Kiếm khi mua sắm. Chi tiêu mọi nơi trong mạng lưới.",
      plaza_rewards_sub: "Mỗi $1 chi tiêu trên HustlePlaza kiếm được HustlePoints. Đổi chúng tại bất kỳ nhà hàng, quán cà phê, khách sạn, salon tóc hoặc doanh nghiệp dịch vụ nào chạy trên HustleSuite — mọi nơi trong mạng lưới.",
      plaza_rewards_step_1: "Mua sắm trên thị trường",
      plaza_rewards_step_1_b: "Mua bất cứ thứ gì từ áo phông đến tủ lạnh.",
      plaza_rewards_step_2: "Kiếm HustlePoints ngay lập tức",
      plaza_rewards_step_2_b: "Điểm xuất hiện ngay khi đơn hàng được giao — không cần thẻ, không cần thẻ đục lỗ, không có trò chơi hết hạn.",
      plaza_rewards_step_3: "Chi tiêu trên toàn mạng lưới",
      plaza_rewards_step_3_b: "Đổi tại quán cà phê, nhà hàng, khách sạn, salon tóc, cửa hàng chăm sóc thú cưng, thợ.",
      plaza_rewards_step_4: "Hoặc rút tiền mặt",
      plaza_rewards_step_4_b: "Chuyển đổi sang chuyển khoản ngân hàng khi đạt $50.",
      plaza_cats_kicker: "Mua sắm theo danh mục",
      plaza_cats_title: "19 danh mục. Nhà bán hàng Úc. Hàng tồn kho trực tiếp.",
      plaza_cats_sub: "Duyệt qua một phần những gì đang hoạt động ngay bây giờ — hoặc đăng ký và mua sắm toàn bộ danh mục hơn 12.000 sản phẩm.",
      plaza_cats_cta: "Xem tất cả danh mục",
      plaza_vendors_kicker: "Người bán Úc thực sự",
      plaza_vendors_title: "Gặp gỡ các nhà sản xuất, người trồng và cửa hàng gia đình.",
      plaza_vendors_sub: "Mọi nhà bán hàng trên HustlePlaza đều được xác minh ABN, có trụ sở tại Úc và được giới thiệu trực tiếp. Không có người trung gian ở nước ngoài giả vờ là người địa phương.",
      plaza_how_kicker: "Cách hoạt động",
      plaza_how_title: "Ba bước. Một giỏ hàng. Phần thưởng mãi mãi.",
      plaza_how_1_title: "Tham gia trong 30 giây",
      plaza_how_1_body: "Xác minh email của bạn và duyệt ngay lập tức. Thêm số điện thoại khi bạn sẵn sàng thanh toán.",
      plaza_how_2_title: "Lấp đầy giỏ hàng",
      plaza_how_2_body: "Mua sắm từ mọi nhà bán. Một giỏ hàng, một hiển thị giao hàng, một thanh toán. Thanh toán qua Stripe hoặc PayPal.",
      plaza_how_3_title: "Kiếm & chi tiêu mọi nơi",
      plaza_how_3_body: "HustlePoints xuất hiện ngay khi đơn hàng được giao. Chi tiêu chúng tại bất kỳ doanh nghiệp HustleSuite nào, hoặc rút tiền mặt ở mức $50.",
      plaza_compare_kicker: "So sánh của chúng tôi",
      plaza_compare_title: "HustlePlaza so với các gã khổng lồ",
      plaza_compare_col_us: "HustlePlaza",
      plaza_compare_col_amz: "Amazon AU",
      plaza_compare_col_ebay: "eBay AU",
      plaza_compare_col_kmart: "Kmart / Big W",
      plaza_compare_row_1: "Sở hữu bởi người Úc",
      plaza_compare_row_2: "Giỏ hàng duy nhất cho mọi người bán",
      plaza_compare_row_3: "Phần thưởng có thể chi tiêu NGOÀI cửa hàng này",
      plaza_compare_row_4: "Rút tiền phần thưởng vào ngân hàng",
      plaza_compare_row_5: "Hỗ trợ khách hàng tại Úc",
      plaza_trust_kicker: "Tại sao chúng tôi xác minh",
      plaza_trust_title: "Khách hàng thực. Đánh giá thực.",
      plaza_trust_body: "Chúng tôi xác minh email của bạn khi đăng ký, số điện thoại di động trước khi thanh toán và yêu cầu kiểm tra ảnh trực tiếp trước khi bạn có thể đăng đánh giá, trò chuyện với nhà bán hoặc mua hàng có giá trị cao. Điều này làm chậm kẻ lừa đảo và giữ giá thấp cho bạn.",
      plaza_trust_1: "Xác minh email để tham gia",
      plaza_trust_2: "Xác minh SMS để thanh toán",
      plaza_trust_3: "Xác minh ảnh trực tiếp cho đánh giá và trò chuyện",
      plaza_trust_4: "Kiểm tra danh tính nhà bán khi giới thiệu",
      plaza_test_kicker: "Thành viên ban đầu nói gì",
      plaza_test_title: "Thẳng từ khách hàng thực sự.",
      plaza_test_1_body: "Tôi đã dùng điểm HustlePlaza để trả tiền bữa tối tại quán cà phê gần nơi làm việc. Thực sự không tin nó sẽ hoạt động cho đến khi cô phục vụ mỉm cười và nói 'bạn đã xong'.",
      plaza_test_1_name: "Priya S.",
      plaza_test_2_body: "Sáu nhà bán, một khung thời gian giao hàng, một email xác nhận. Thông thường tôi sẽ có sáu mật khẩu và sáu số theo dõi. Cái này chỉ hoạt động.",
      plaza_test_2_name: "Marcus T.",
      plaza_test_3_body: "Là một nhà sản xuất mật ong nhỏ, cuối cùng chúng tôi được trả cùng ngày, đánh giá thực và không bị cắt 40%. HustlePlaza đưa tôi từ 'công việc phụ' đến toàn thời gian trong tám tháng.",
      plaza_test_3_name: "Kate & Dave",
      plaza_faq_kicker: "Câu trả lời",
      plaza_faq_title: "Những câu hỏi chúng tôi nhận mỗi tuần",
      plaza_faq_1_q: "HustlePlaza có thực sự thuộc sở hữu của người Úc không?",
      plaza_faq_1_a: "Có. HustlePlaza 100% thuộc sở hữu của người Úc, lưu trữ trên máy chủ Úc, điều hành bởi đội ngũ Úc. Mọi nhà bán đều trải qua xác minh danh tính trước khi danh sách đầu tiên có thể hoạt động.",
      plaza_faq_2_q: "Phần thưởng hoạt động như thế nào?",
      plaza_faq_2_a: "Mỗi đô la bạn chi tiêu trên HustlePlaza kiếm cho bạn HustlePoints. Điểm xuất hiện ngay khi đơn hàng được giao và không bao giờ hết hạn. Chi tiêu chúng tại bất kỳ doanh nghiệp nào chạy trên HustleSuite, hoặc rút tiền mặt vào ngân hàng khi bạn đã tiết kiệm đủ.",
      plaza_faq_3_q: "Tại sao bạn xác minh ảnh?",
      plaza_faq_3_a: "Để các đánh giá bạn đọc là thực. Xác minh ảnh là kiểm tra selfie trực tiếp được thực hiện qua camera điện thoại của bạn — cùng kỹ thuật mà ngân hàng sử dụng — và nó ngăn chặn bot, tài khoản giả và kẻ xấu khỏi lợi dụng nền tảng.",
      plaza_faq_4_q: "Điều gì xảy ra nếu đơn hàng không đến?",
      plaza_faq_4_a: "Mọi đơn hàng đều được bảo vệ bởi HustleGuarantee của chúng tôi: nếu kiện hàng không đến hoặc không như mô tả, chúng tôi hoàn tiền cho bạn trong vòng 3 ngày làm việc, sau đó truy đuổi nhà bán. Bạn sẽ không bao giờ bị thiệt hại.",
      plaza_faq_5_q: "Thông tin thẻ của tôi có an toàn không?",
      plaza_faq_5_a: "Chúng tôi không bao giờ nhìn thấy hoặc lưu trữ số thẻ của bạn. Tất cả thanh toán chạy qua Stripe (PCI-DSS Cấp 1) hoặc PayPal, cả hai đều có xác thực 3D Secure bảo vệ bạn khỏi các khoản phí gian lận trên thẻ bị đánh cắp.",
      plaza_faq_6_q: "Bạn có giao hàng quốc tế không?",
      plaza_faq_6_a: "Chưa. HustlePlaza chỉ ở Úc khi ra mắt — đó là cách chúng tôi có thể hứa giao hàng cùng tuần cho hầu hết các mặt hàng và hỗ trợ trong múi giờ của bạn. NZ nằm trong lộ trình 2026.",
      plaza_faq_7_q: "Tham gia tốn bao nhiêu?",
      plaza_faq_7_a: "Không có gì. Thành viên miễn phí mãi mãi. Nhà bán trả hoa hồng nhỏ cho mỗi lần bán — người mua trả $0 để tham gia, $0 để kiếm, $0 để đổi.",
      plaza_faq_8_q: "Tôi có thể bán trên HustlePlaza không?",
      plaza_faq_8_a: "Hoàn toàn có thể. Truy cập hustlesuite.au để giới thiệu là nhà bán — phê duyệt cùng ngày cho doanh nghiệp Úc đã xác minh, cộng với 30 ngày đầu miễn phí hoa hồng.",
      plaza_final_kicker: "Giá người sáng lập — chỗ giới hạn",
      plaza_final_title: "Hãy là một trong những người đầu tiên — khóa giá người sáng lập.",
      plaza_final_sub: "Đăng ký hôm nay với tư cách Thành viên HustleSuite. Bạn sẽ là người đầu tiên biết khi mỗi tính năng mới được ra mắt, và tài khoản của bạn sẽ giữ phần thưởng và giá cấp người sáng lập trọn đời.",
      plaza_final_bonus: "Người sáng lập không bao giờ mất mức giá người sáng lập. Miễn phí mãi mãi, hủy bất cứ lúc nào.",
      plaza_final_cta: "Đăng ký làm Thành viên Người sáng lập",
      plaza_final_disclosure: "Không cần thẻ. Chỉ xác minh Email + di động.",
      plaza_roadmap_kicker: "Những gì chúng tôi đang xây dựng tiếp theo",
      plaza_roadmap_title: "Sáu công cụ được xây dựng để giảm bớt công việc của bạn.",
      plaza_roadmap_sub: "HustleSuite không chỉ là một cửa hàng — đó là một lớp năng suất đầy đủ. Đây là những gì đang trên đường băng.",
      plaza_roadmap_soon: "Sắp ra mắt",
      plaza_roadmap_next: "Tiếp theo trong hàng đợi",
      plaza_roadmap_1_t: "HustleDictate — giọng nói thành văn bản trên bất kỳ biểu mẫu nào",
      plaza_roadmap_1_b: "Tiện ích mở rộng Chrome. Nhấn Alt+M, nói, và bất kỳ đầu vào nào trên bất kỳ trang web nào tự điền. Đơn xin bất động sản, hóa đơn, giấy tờ NDIS — hoàn thành trong nửa thời gian.",
      plaza_roadmap_2_t: "HustleConnect Edit-Anywhere",
      plaza_roadmap_2_b: "Dành cho chủ sở hữu WordPress: chỉnh sửa trang web trực tiếp của bạn từ bất kỳ trang nào chỉ bằng một phím nóng. Không còn chuyển vòng wp-admin nữa.",
      plaza_roadmap_3_t: "Nút lưu vào HustleSuite phổ quát",
      plaza_roadmap_3_b: "Nhấp chuột phải vào bất kỳ trang web nào — danh sách bất động sản, bài viết, sản phẩm, nhà hàng — và nó sẽ nằm trong mô-đun phù hợp sẵn sàng để bạn hành động.",
      plaza_roadmap_4_t: "Công cụ kiểm tra giá HustlePlaza",
      plaza_roadmap_4_b: "Duyệt Amazon, eBay hoặc Kmart — một chip nổi hiển thị cùng sản phẩm trên HustlePlaza với bản xem trước phần thưởng của bạn.",
      plaza_roadmap_5_t: "Công cụ thu thập đánh giá",
      plaza_roadmap_5_b: "Tự động đồng bộ đánh giá Google, Facebook và TripAdvisor mới vào bảng điều khiển HustleSuite của bạn. Tiết kiệm ~20 phút mỗi tuần.",
      plaza_roadmap_6_t: "Thanh bên HustleSuite",
      plaza_roadmap_6_b: "Bảng bên Chrome với đơn hàng trực tiếp, KPI, đăng nhanh và hóa đơn nhanh. Toàn bộ doanh nghiệp của bạn trong một dải bên cạnh mọi tab.",
      plaza_footer_owned: "Sở hữu & lưu trữ bởi người Úc",
      plaza_footer_terms: "Điều khoản",
      plaza_footer_privacy: "Quyền riêng tư",
      plaza_footer_contact: "Liên hệ",
      plaza_footer_vendors: "Bán trên HustlePlaza",
      plaza_footer_help: "Trung tâm trợ giúp",
      plaza_footer_tagline: "Một phần của gia đình HustleSuite — một đăng nhập, mọi dịch vụ.",
      verify_gate_title: "Gần xong rồi — đăng ký nhanh",
      verify_gate_body: "HustlePlaza là giỏ hàng mua sắm cung cấp nhiều lợi ích bao gồm phần thưởng có thể đổi, có thể được sử dụng không chỉ trong nền tảng này mà với bất kỳ Doanh nghiệp nào khác sử dụng nền tảng HustlePlaza hoặc bất kỳ Doanh nghiệp nào Đăng ký với HustleSuite. Một số Doanh nghiệp này sẽ bao gồm Nhà hàng, Quán cà phê, Khách sạn, Salon tóc và thậm chí cả Chăm sóc Thú cưng. Để tiếp tục cung cấp giá cả và dịch vụ tuyệt vời, chúng tôi cần đảm bảo bạn là người thật và xác minh bạn. Nhấn nút Tham gia để tiếp tục.",
      verify_gate_join: "Tham gia HustleSuite",
      verify_gate_signin: "Tôi đã có tài khoản",
      verify_gate_cancel: "Không phải bây giờ",
      verify_email_title: "Xác minh email của bạn",
      verify_email_body: "Chúng tôi sẽ gửi mã 6 chữ số đến hộp thư của bạn.",
      verify_email_placeholder: "ban@vitri.com",
      verify_email_send: "Gửi mã",
      verify_email_code_ph: "Mã 6 chữ số",
      verify_email_check: "Xác minh",
      verify_email_sent: "Mã đã gửi — kiểm tra hộp thư của bạn.",
      verify_sms_title: "Xác minh điện thoại của bạn",
      verify_sms_body: "Thêm số điện thoại di động để chúng tôi có thể nhắn tin cho bạn cập nhật đơn hàng và xác nhận giao dịch mua lớn.",
      verify_sms_placeholder: "+61412345678",
      verify_sms_send: "Gửi SMS",
      verify_sms_check: "Xác minh",
      verify_sms_sent: "Mã đã gửi — kiểm tra điện thoại của bạn.",
      verify_photo_title: "Xác minh ảnh",
      verify_photo_body: "Quét mã QR bằng điện thoại của bạn để chụp selfie trực tiếp. Bắt buộc để trò chuyện, mua hàng có giá trị cao và đăng trong Meetup / Tackle.",
      verify_photo_waiting: "Đang đợi điện thoại của bạn…",
      verify_photo_verified: "Ảnh đã được xác minh — bạn đã sẵn sàng.",
      travel_cta_start: "Tham gia miễn phí",
      travel_hero_kicker: "HustleTravel",
      travel_hero_title_1: "Một thành viên.",
      travel_hero_title_2: "Khắp nơi Úc.",
      travel_hero_sub: "Khách sạn, chuyến bay, trải nghiệm, lập kế hoạch chuyến đi đường và thuê xe — đặt với mức giá tốt nhất hiện có và trả cho bạn HustlePoints cho mỗi đô la chi tiêu.",
      travel_cta_plan: "Lên kế hoạch chuyến đi đường",
      travel_cta_hotels: "Tìm khách sạn",
      travel_svc_hotels_t: "Khách sạn & căn hộ",
      travel_svc_hotels_b: "So sánh hơn 200 trang đặt phòng trong một nơi — metasearch được hỗ trợ bởi Trivago.",
      travel_svc_flights_t: "Chuyến bay",
      travel_svc_flights_b: "Trong nước và quốc tế, với sơ đồ chỗ ngồi thời gian thực và quy tắc giá vé.",
      travel_svc_experiences_t: "Trải nghiệm & tour",
      travel_svc_experiences_b: "Ngày ở rạn san hô, nếm rượu tại hầm, nhạc sống, các chuyến du ngoạn thân thiện với trẻ em.",
      travel_svc_cars_t: "Thuê xe",
      travel_svc_cars_b: "Mọi thương hiệu lớn cộng với các công ty độc lập địa phương — nhận tại bất kỳ sân bay nào của Úc.",
      travel_svc_insurance_t: "Bảo hiểm du lịch",
      travel_svc_insurance_b: "Được bảo lãnh bởi các công ty bảo hiểm Úc được quy định bởi APRA. So sánh trên một màn hình.",
      travel_svc_roadtrip_t: "Công cụ lập kế hoạch chuyến đi đường",
      travel_svc_roadtrip_b: "Hành trình nhận biết phạm vi EV với bộ sạc, điểm dừng qua đêm và ước tính chi phí.",
      travel_final_kicker: "Giá người sáng lập",
      travel_final_title: "Khóa phần thưởng cấp Người sáng lập trọn đời.",
      travel_final_sub: "Đăng ký một lần với HustleSuite. Đặt mọi thứ — khách sạn, chuyến bay, trải nghiệm, bảo hiểm — trên HustleTravel và kiếm HustlePoints có thể chi tiêu trên mọi doanh nghiệp trong mạng lưới.",
      travel_final_cta: "Đăng ký làm Thành viên Người sáng lập",
      travel_final_disclosure: "Chính sách giá tốt nhất: kết quả được sắp xếp theo giá trị tốt nhất cho khách du lịch, không phải hoa hồng.",
      dictate_hero_kicker: "HustleDictate",
      dictate_hero_title_1: "Nói.",
      dictate_hero_title_2: "Bất kỳ biểu mẫu nào. Bất kỳ trang web nào.",
      dictate_hero_sub: "Tiện ích mở rộng Chrome duy nhất biến bất kỳ trường văn bản nào thành micrô chính tả giọng nói cấp chuyên nghiệp — được hỗ trợ bởi Whisper.",
      dictate_cta_install: "Thêm vào Chrome",
      dictate_uc_re_t: "Ghi chú thuê bất động sản",
      dictate_uc_re_b: "Chính tả báo cáo kiểm tra và tin nhắn người thuê thẳng vào hệ thống PM của bạn.",
      dictate_uc_ndis_t: "Ghi chú tiến độ NDIS",
      dictate_uc_ndis_b: "Nói ghi chú ca làm trong khi ký ức còn tươi — rảnh tay, không gõ.",
      dictate_uc_invoice_t: "Mô tả hóa đơn",
      dictate_uc_invoice_b: "Mô tả từng mục được chính tả thẳng vào Xero, MYOB hoặc QuickBooks.",
      dictate_uc_email_t: "Trả lời email dài",
      dictate_uc_email_b: "Nói ra câu trả lời trong Gmail, Outlook hoặc bất kỳ webmail nào — nhanh gấp ba lần so với gõ.",
      dictate_final_kicker: "Miễn phí cho Thành viên Người sáng lập",
      dictate_final_title: "Mọi bàn phím giờ đây là micrô.",
      dictate_final_sub: "Miễn phí cho Thành viên Người sáng lập HustleSuite. $5/tháng nếu không — hủy bất cứ lúc nào.",
      dictate_final_cta: "Thêm vào Chrome — miễn phí",
      "finder.cat.roadtrip": "Chuyến đi đường",
      "finder.hotels.title": "Tìm khách sạn gần đây",
      "finder.hotels.sub": "So sánh hơn 200 trang đặt phòng trong một lần tìm kiếm — được hỗ trợ bởi Trivago.",
      "finder.hotels.where": "Ở đâu",
      "finder.hotels.where_ph": "Thành phố hoặc vùng ngoại ô (ví dụ Byron Bay, NSW)",
      "finder.hotels.checkin": "Nhận phòng",
      "finder.hotels.checkout": "Trả phòng",
      "finder.hotels.adults": "Người lớn",
      "finder.hotels.search": "Tìm khách sạn",
      "finder.hotels.disclosure": "Mở Trivago — chúng tôi có thể kiếm hoa hồng từ việc đặt phòng.",
      /* AUTO-TRANSLATED vi END */
      nav_menu: "Thực đơn",
      nav_book: "Đặt bàn",
      nav_reviews: "Đánh giá",
      nav_about: "Giới thiệu",
      nav_account: "Tài khoản",
      nav_signin: "Đăng nhập",
      cart_open: "Mở giỏ hàng",
      cart_title: "Đơn hàng của bạn",
      cart_empty: "Giỏ hàng trống",
      cart_subtotal: "Tạm tính",
      cart_continue: "Tiếp tục",
      cart_pickup_date: "Ngày nhận",
      cart_pickup_time: "Giờ nhận",
      cart_pay_on_pickup: "Thanh toán khi nhận",
      cart_pay_now: "Thanh toán ngay",
      cart_place_order: "Đặt hàng",
      add_to_order: "Thêm vào đơn",
      menu_search_ph: "Tìm món…",
      menu_chef_specials: "Đặc biệt của bếp trưởng",
      menu_under_kcal: "Dưới",
      menu_kcal_unit: "kcal",
      menu_clear_filters: "Xoá",
      menu_dietary_label: "Chế độ ăn",
      menu_no_matches: "Không có kết quả",
      menu_relax_filters: "Hãy thử tìm khác hoặc bỏ bớt bộ lọc.",
      menu_clear_all: "Xoá tất cả bộ lọc",
      book_title: "Đặt bàn",
      book_name: "Họ tên",
      book_email: "Email",
      book_phone: "Số điện thoại",
      book_guests: "Số khách",
      book_date: "Ngày",
      book_time: "Giờ",
      book_requests: "Yêu cầu đặc biệt",
      book_submit: "Xác nhận đặt bàn",
      footer_rights: "Đã đăng ký bản quyền.",
      lang_label: "Ngôn ngữ",
      welcome_back: "Chào mừng trở lại",
      sign_out: "Đăng xuất",
    
      /* AUTO-TRANSLATED finder.* (added) */
      "finder.title": "HustleFinder",
      "finder.tagline": "Tìm địa điểm công cộng gần nhất — dữ liệu thực tế, toàn nước Úc.",
      "finder.search_placeholder": "Tìm kiếm địa điểm, đường phố hoặc khu vực…",
      "finder.hint": "Bắt đầu nhập ở trên, chọn danh mục hoặc dùng \"Gần tôi\".",
      "finder.near_me": "Gần tôi",
      "finder.searching": "Đang tìm kiếm…",
      "finder.suburb.placeholder": "Lọc theo khu vực (vd: Darwin, NT)…",
      "finder.suburb.clear": "Xóa",
      "finder.search_in": "Tìm kiếm trong {{cat}}",
      "finder.empty": "Không có kết quả — thử danh mục hoặc từ khóa khác.",
      "finder.meta.title": "HustleFinder — danh bạ khu vực Úc miễn phí",
      "finder.meta.desc": "Tìm kiếm doanh nghiệp địa phương, trạm sạc, quán cà phê, nhà vệ sinh và nhiều hơn nữa trên khắp các khu vực Úc — 100% miễn phí.",
      "finder.footer.credits": "Nguồn dữ liệu & ghi nhận",
      "finder.footer.terms": "Điều khoản",
      "finder.cat.toilet": "Nhà vệ sinh",
      "finder.cat.fountain": "Vòi nước uống",
      "finder.cat.transport": "Giao thông",
      "finder.cat.park": "Công viên",
      "finder.cat.medical": "Y tế",
      "finder.cat.school": "Trường học",
      "finder.cat.library": "Thư viện",
      "finder.cat.petrol": "Xăng dầu",
      "finder.cat.kidzone": "Khu vui chơi trẻ em",
      "finder.cat.puzzles": "Trò chơi ô chữ",
      /* AUTO-TRANSLATED rt.* START */
      "rt.brand_name": "Roadtrip Planner",
      "rt.page_title": "Lập kế hoạch Road-trip Xe điện Úc",
      "rt.from": "Từ",
      "rt.to": "Đến",
      "rt.from_placeholder": "Phường/xã, thị trấn hoặc thành phố (vd: Newcastle NSW)",
      "rt.to_placeholder": "Phường/xã, thị trấn hoặc thành phố (vd: Hobart TAS)",
      "rt.stop": "DỪNG",
      "rt.stop_placeholder": "Điểm dừng trung gian",
      "rt.add_stop": "Thêm điểm dừng",
      "rt.upgrade_pro": "Nâng cấp lên Pro để thêm tới 20 điểm dừng",
      "rt.reverse": "Đảo ngược (tính đường về)",
      "rt.from_current": "Từ vị trí hiện tại",
      "rt.range_per_charge": "Quãng đường mỗi lần sạc (km)",
      "rt.connector": "Cổng sạc",
      "rt.connector_any": "Bất kỳ",
      "rt.drive_hours_day": "Giờ lái xe/ngày",
      "rt.accommodation": "Chỗ nghỉ",
      "rt.accom_any": "Bất kỳ (BnB · Khách sạn · Caravan)",
      "rt.accom_bnb": "BnB",
      "rt.accom_hotel": "Khách sạn",
      "rt.accom_caravan": "Khu cắm trại Caravan",
      "rt.min_rating": "Đánh giá tối thiểu",
      "rt.any_rating": "Mọi đánh giá",
      "rt.only_5star": "Chỉ 5★",
      "rt.schedule": "Lịch trình:",
      "rt.depart_at": "Khởi hành lúc",
      "rt.arrive_by": "Đến nơi lúc",
      "rt.hotel_checkin": "Nhận phòng khách sạn",
      "rt.hotel_checkout": "Trả phòng khách sạn",
      "rt.overnight_hint": "Các chặng qua đêm sẽ không được xếp lịch ngoài khung giờ này.",
      "rt.whos_in_car": "Ai có mặt trong xe?",
      "rt.adults": "Người lớn",
      "rt.kids": "Trẻ em",
      "rt.ages": "Độ tuổi",
      "rt.show_attractions": "Hiện điểm tham quan du lịch",
      "rt.show_kids": "Hiện điểm dừng cho trẻ em",
      "rt.overnight_stays": "Điểm nghỉ qua đêm",
      "rt.fast_chargers_only": "Chỉ trạm sạc nhanh",
      "rt.hotel_must_charger": "Khách sạn phải có trạm sạc",
      "rt.pet_only": "Chỉ chỗ nghỉ cho phép thú cưng",
      "rt.safety": "Độ an toàn",
      "rt.safety_90": "90% (đường bằng)",
      "rt.safety_85": "85% (mặc định)",
      "rt.safety_80": "80% (đồi núi / kéo xe)",
      "rt.safety_70": "70% (tải nặng)",
      "rt.save_trip": "Lưu chuyến đi",
      "rt.plan_my_trip": "Lập kế hoạch",
      "rt.refresh_trip": "Làm mới kế hoạch",
      "rt.planning": "Đang lập kế hoạch…",
      "rt.resume_title": "Tiếp tục chuyến đi từ đây",
      "rt.resume_body": "Chúng tôi đã ghi nhận vị trí của bạn. Hãy nhập quãng đường còn lại hiển thị trên bảng điều khiển để chúng tôi lập kế hoạch cho chặng tiếp theo dựa trên thực tế — thay vì sạc đầy.",
      "rt.remaining_range": "Quãng đường còn lại (km)",
      "rt.cancel": "Hủy",
      "rt.replan_from_here": "Lập lại kế hoạch từ đây",
      "rt.save_modal_title": "Đặt tên chuyến đi",
      "rt.save_modal_body": "Chuyến đi đã lưu nằm trong hồ sơ của bạn — mở lại bất cứ lúc nào và nhấn Lập lại kế hoạch.",
      "rt.save_name_placeholder": "vd: Brisbane → Sydney qua Byron",
      "rt.your_saved_trips": "Chuyến đi đã lưu của bạn",
      "rt.custom_location": "Vị trí tùy chỉnh",
      "rt.trip_summary": "Tóm tắt chuyến đi",
      "rt.itinerary": "Lịch trình chi tiết",
      "rt.no_stops_needed": "Không cần dừng — toàn bộ chuyến đi nằm trong phạm vi sạc của bạn.",
      "rt.chargers_in_corridor": "trạm sạc trên hành trình",
      "rt.planned_stops": "điểm dừng đã lập kế hoạch",
      "rt.overnight_stop": "điểm nghỉ qua đêm",
      "rt.overnight_stops": "điểm nghỉ qua đêm",
      "rt.suggested_detours": "Gợi ý đường vòng — đánh dấu để thêm",
      "rt.replan_with_detours": "Lập lại kế hoạch kèm đường vòng",
      "rt.attractions_label": "Điểm tham quan",
      "rt.kids_label": "Trẻ em",
      "rt.km_from_start": "km tính từ điểm bắt đầu",
      "rt.open_driving": "Mở chế độ lái xe",
      "rt.big_font_hint": "Chữ lớn, dễ nhìn khi đặt trên giá đỡ. Hỗ trợ phản chiếu trình duyệt CarPlay/Android Auto.",
      "rt.check_price_book": "Kiểm tra giá & đặt phòng trên Trivago ↗",
      "rt.visit_site": "Truy cập trang web ↗",
      "rt.charger_onsite": "Có trạm sạc tại chỗ",
      "rt.leg_exceeds_range": "Chặng này vượt quá quãng đường đã thiết lập — bạn sẽ hết pin trước khi đến trạm sạc này. Hãy giảm mức quãng đường giả định hoặc thêm một điểm sạc trung gian.",
      "rt.add_waypoint_replan": "Thêm làm điểm trung chuyển & lập lại kế hoạch",
      /* AUTO-TRANSLATED rt.* END */
                      /* AUTO-TRANSLATED hm.* START */
      "hm.nav.hustlemeet": "HustleMeet",
      "hm.nav.circles": "Circles",
      "hm.nav.matchmaker": "Matchmaker",
      "hm.nav.meet": "Meet",
      "hm.nav.overview": "HustleMeetups",
      "hm.overview.page_title": "HustleMeetups — ba cách ấm áp để tìm thấy những người bạn đồng điệu.",
      "hm.overview.kicker": "Một phần của HustleSuite",
      "hm.overview.hero_title": "Ba cách ấm áp để tìm thấy những người bạn đồng điệu.",
      "hm.overview.hero_title_accent": "những người bạn",
      "hm.overview.hero_sub": "HustleMeetups là cách tử tế nhất tại Australia để xây dựng những tình bạn thực sự — hãy chọn phong cách phù hợp với bạn. Cho dù bạn muốn một nhóm chạy bộ hàng tuần, một bữa tối hàng tháng với hội bạn thân, hay một bàn tiệc được ghép đôi bằng AI với những gương mặt mới, luôn có một chỗ dành cho bạn.",
      "hm.overview.tile_meet_kicker": "HustleMeet",
      "hm.overview.tile_meet_title": "Tìm thấy hội bạn. Làm điều mình yêu.",
      "hm.overview.tile_meet_body": "Ngôi nhà của các nhóm ưu tiên sở thích tại Australia. Người chạy bộ, lập trình viên, nhà đầu tư, người đi bộ, vũ công, câu lạc bộ ăn sáng, nhóm đọc sách — hàng trăm nhóm, sự kiện thực tế mỗi tuần. Tham gia một lần, hoặc mỗi tuần.",
      "hm.overview.tile_meet_cta": "Khám phá các nhóm",
      "hm.overview.tile_circles_kicker": "Circles",
      "hm.overview.tile_circles_title": "Hội bạn cũ. Một bàn tiệc mới mỗi lần.",
      "hm.overview.tile_circles_body": "Đi cùng nhóm bạn thân của bạn. Chúng tôi đặt một nhà hàng mới, lên kế hoạch giải đố, chuẩn bị bài tây. Bạn chỉ cần xuất hiện. Cách gắn kết nhất để giữ hội bạn hiện tại luôn gần gũi — mà không ai phải vất vả tổ chức.",
      "hm.overview.tile_circles_cta": "Bắt đầu một Circle",
      "hm.overview.tile_matchmaker_kicker": "Matchmaker",
      "hm.overview.tile_matchmaker_title": "AI chọn bàn. Bạn chỉ cần đến.",
      "hm.overview.tile_matchmaker_body": "Làm một bài trắc nghiệm 10 phút. Chỉ sau một đêm, chúng tôi sẽ xếp bạn ngồi cùng những người lạ mà bạn sẽ cảm thấy thân thuộc như bạn cũ ngay khi đến món tráng miệng. Mỗi lần là một bàn tiệc khác nhau.",
      "hm.overview.tile_matchmaker_cta": "Làm trắc nghiệm",
      "hm.overview.trust_kicker": "Tại sao nó hiệu quả",
      "hm.overview.trust_title": "Tin tưởng trước. Bàn tiệc sau.",
      "hm.overview.trust_1_title": "Thành viên đã xác thực",
      "hm.overview.trust_1_body": "Mọi nhóm đều bắt đầu bằng việc kiểm tra ID thật + điểm tin cậy. Không có tài khoản ảo.",
      "hm.overview.trust_2_title": "Host thực thụ",
      "hm.overview.trust_2_body": "Mọi nhóm đều được dẫn dắt bởi một host đã được kiểm duyệt, người giữ nhịp không khí và chăm sóc mọi người trong phòng.",
      "hm.overview.trust_3_title": "Tình bạn là trên hết",
      "hm.overview.trust_3_body": "Mọi thứ ở đây được thiết kế cho tình bạn thực sự, không phải hẹn hò. Tuyệt đối không.",
      "hm.overview.trust_4_title": "Điều phối không khoan nhượng",
      "hm.overview.trust_4_body": "Một báo cáo, một đánh giá, một quyết định. Những cá nhân có hành vi xấu sẽ bị mời ra khỏi hệ thống ngay trong đêm.",
      "hm.overview.footer_line": "Một phần của HustleSuite — Sở hữu & vận hành tại Australia.",
      "hm.landing.page_title": "HustleMeet · Tìm thấy hội bạn. Làm điều mình yêu.",
      "hm.landing.kicker": "Mạng lưới sở thích của Australia",
      "hm.landing.hero_title": "Tìm thấy hội bạn. Làm điều mình yêu.",
      "hm.landing.hero_title_accent": "những người bạn",
      "hm.landing.hero_sub": "HustleMeet là ngôi nhà của các nhóm ưu tiên sở thích tại Australia. Các lập trình viên sửa lỗi bên bữa trưa. Những người chạy bộ đường dài lúc 6 giờ sáng. Các nhà đầu tư họp mặt hàng tháng. Nhóm đi bộ, đội nhảy, câu lạc bộ ăn sáng, đêm chơi board-game. Chọn một nhóm, đặt sự kiện tiếp theo, và có mặt.",
      "hm.landing.cta_explore_circles": "Xem các nhóm",
      "hm.landing.cta_try_matchmaker": "Bắt đầu nhóm của riêng bạn",
      "hm.landing.trust_verified": "Thành viên đã xác thực",
      "hm.landing.trust_venues": "Host sự kiện đã được kiểm duyệt",
      "hm.landing.trust_friendship": "Tình bạn là trên hết — không bao giờ hẹn hò",
      "hm.landing.trust_moderation": "Điều phối không khoan nhượng",
      "hm.landing.subbrand_circles_kicker": "Circles",
      "hm.landing.subbrand_circles_title": "Đã có hội bạn thân? Hãy cho họ một ngôi nhà lâu dài.",
      "hm.landing.subbrand_circles_body": "Circles dành cho những nhóm bạn đã thường xuyên gặp gỡ — câu lạc bộ sách, đêm chơi poker, đội chạy bộ, bạn đồng nghiệp. Chúng tôi lo địa điểm, nhắc nhở và danh sách tham gia. Bạn chỉ cần xuất hiện.",
      "hm.landing.subbrand_circles_b1": "Thay đổi địa điểm, danh sách tham gia cố định",
      "hm.landing.subbrand_circles_b2": "Tự động nhắc nhở + xác nhận RSVP dễ dàng",
      "hm.landing.subbrand_circles_b3": "Giải đố, bài tây & các đêm theo chủ đề luôn sẵn sàng",
      "hm.landing.subbrand_circles_cta": "Bắt đầu một Circle",
      "hm.landing.subbrand_matchmaker_kicker": "Matchmaker",
      "hm.landing.subbrand_matchmaker_title": "AI chọn bàn. Bạn chỉ cần đến.",
      "hm.landing.subbrand_matchmaker_body": "Một bài trắc nghiệm 10 phút — tính cách, giá trị, thời gian rảnh. Matchmaker của chúng tôi xếp bạn ngồi cùng những người lạ mà bạn sẽ cảm thấy thân thuộc như bạn cũ ngay khi đến món tráng miệng. Mỗi lần là một bàn tiệc khác nhau.",
      "hm.landing.subbrand_matchmaker_b1": "Chấm điểm tính cách Big-5 & các giá trị",
      "hm.landing.subbrand_matchmaker_b2": "Lời mời gửi đến ngay khi độ phù hợp đạt 0.70 trở lên",
      "hm.landing.subbrand_matchmaker_b3": "Học hỏi từ mỗi bữa tối bạn tham gia",
      "hm.landing.subbrand_matchmaker_cta": "Làm trắc nghiệm",
      "hm.landing.how_kicker": "HustleMeet hoạt động như thế nào",
      "hm.landing.how_title": "Bốn bước. Tìm thấy nhóm của bạn. Xuất hiện.",
      "hm.landing.how_step1_ttl": "Chọn sở thích của bạn",
      "hm.landing.how_step1_sub": "Chạy bộ, lập trình, đầu tư, nấu ăn, âm nhạc, đi bộ — hãy cho chúng tôi biết bạn thích gì và bạn sống ở đâu.",
      "hm.landing.how_step2_ttl": "Xem các nhóm gần bạn",
      "hm.landing.how_step2_sub": "Mỗi nhóm đều công khai host, nhịp điệu sinh hoạt và sự kiện tiếp theo. Tìm hiểu không khí trước khi bạn cam kết.",
      "hm.landing.how_step3_ttl": "Xác nhận RSVP cho sự kiện tiếp theo",
      "hm.landing.how_step3_sub": "Một lần chạm, một lời mời trên lịch, một lời nhắc rõ ràng vào ngày hôm trước. Một số sự kiện miễn phí, một số có phí — giá cả luôn minh bạch.",
      "hm.landing.how_step4_ttl": "Xuất hiện & kết nối",
      "hm.landing.how_step4_sub": "Gặp gỡ host, gặp gỡ mọi người, làm điều mình yêu. Quay lại vào tuần tới nếu bạn thấy hợp.",
      "hm.landing.stats_kicker": "Cách gặp gỡ mới của Australia",
      "hm.landing.stats_title": "Lý do mọi người luôn quay lại.",
      "hm.landing.stats_92_sub": "Cảm thấy họ tìm được ít nhất một người hợp cạ tại sự kiện",
      "hm.landing.stats_68_sub": "Xác nhận RSVP cho sự kiện tiếp theo của họ trong vòng một tuần",
      "hm.landing.stats_85_sub": "Giữ liên lạc với ai đó từ nhóm",
      "hm.landing.why_kicker": "Tại sao chúng tôi thành công",
      "hm.landing.why_title": "Sở thích thật. Host thật. Sự kiện thật.",
      "hm.landing.why_1_ttl": "Sở thích trước, vị trí sau",
      "hm.landing.why_1_body": "Những người trong nhóm của bạn thực sự quan tâm đến điều bạn quan tâm — đó là con đường tắt dẫn đến tình bạn thực sự.",
      "hm.landing.why_2_ttl": "Host sự kiện thực thụ",
      "hm.landing.why_2_body": "Mỗi nhóm có một host đã được kiểm duyệt, người giữ nhịp không khí và can thiệp nếu có điều gì không ổn. Không phải một admin Facebook. Mà là một người thật.",
      "hm.landing.why_3_ttl": "Sự nhất quán rất quan trọng",
      "hm.landing.why_3_body": "Sự kiện mới mỗi tuần. Một điều gì đó để mong đợi. Một nhịp điệu biến những người lạ thành hội bạn tối thứ Ba của bạn.",
      "hm.landing.why_4_ttl": "An toàn & bảo mật",
      "hm.landing.why_4_body": "Thành viên đã xác thực, địa điểm công cộng, không khoan nhượng với sự thiếu tôn trọng. Đội ngũ hỗ trợ của chúng tôi luôn sẵn sàng trước, trong và sau mỗi sự kiện.",
      "hm.landing.why_5_ttl": "Tình bạn là trên hết",
      "hm.landing.why_5_body": "Đây không phải là hẹn hò. Đây là cộng đồng. Sở thích thật, con người thật, không có chuyện quẹt trái quẹt phải.",
      "hm.landing.why_6_ttl": "Đủ nhỏ để gắn kết",
      "hm.landing.why_6_body": "Hầu hết các nhóm giới hạn sự kiện ở mức 4-15 chỗ ngồi. Đủ lớn để khơi gợi những cuộc trò chuyện thực sự. Đủ nhỏ để không ai bị bỏ lạc lõng.",
      "hm.landing.cities_kicker": "Nơi chúng tôi hoạt động",
      "hm.landing.cities_title": "Các nhóm đang gặp mặt khắp Australia & NZ.",
      "hm.landing.cities_sub_zero": "Những nhóm đầu tiên đang mở tại hơn 15 thành phố — hãy tham gia sớm để định hình không gian.",
      "hm.landing.cities_sub_some": "Các nhóm đang hoạt động tại hơn 15 khu vực đô thị. Luôn có điều gì đó ở thành phố của bạn tuần này.",
      "hm.landing.cities_sub_one": "{{count}} nhóm đang hoạt động · sự kiện mới mỗi tuần · liên tục thêm các thành phố mới.",
      "hm.landing.cities_sub_many": "{{count}} nhóm đang hoạt động · sự kiện mới mỗi tuần · liên tục thêm các thành phố mới.",
      "hm.landing.forme_kicker": "Điều này dành cho bạn nếu…",
      "hm.landing.forme_title": "Bạn muốn sở thích thật. Con người thật. Sự kiện thật.",
      "hm.landing.forme_1": "Bạn đã quá mệt mỏi với các ứng dụng quẹt và muốn thực sự gặp gỡ mọi người xoay quanh những điều bạn yêu thích.",
      "hm.landing.forme_2": "Bạn mới chuyển đến một thành phố mới và cần một hội bạn ấm áp, chứ không phải một nhóm chat khác.",
      "hm.landing.forme_3": "Bạn coi trọng chiều sâu hơn là sự phô trương — một nhóm tuyệt vời còn hơn cả ngàn lượt theo dõi.",
      "hm.landing.forme_4": "Bạn tin rằng sở thích chung + hai giờ cố định ngoài đời thực là bí quyết thực sự.",
      "hm.landing.notme_kicker": "Điều này không dành cho bạn nếu…",
      "hm.landing.notme_title": "HustleMeet không dành cho tất cả mọi người.",
      "hm.landing.notme_1": "Bạn không thể cam kết xuất hiện — host và các thành viên khác đang trông chờ vào sự xác nhận của bạn.",
      "hm.landing.notme_2": "Bạn đang tìm kiếm một dịch vụ hẹn hò — tình bạn luôn được ưu tiên hàng đầu tại đây.",
      "hm.landing.notme_3": "Bạn muốn quảng bá bản thân mà không gặp gỡ ai — điều này chỉ hiệu quả khi gặp mặt trực tiếp.",
      "hm.landing.notme_4": "Bạn mong đợi vô vàn đặc quyền miễn phí — một số sự kiện có chi phí. Giá cả luôn được công khai trước.",
      "hm.landing.safety_kicker": "Cam kết an toàn của chúng tôi",
      "hm.landing.safety_title": "Ấm áp, nhưng không bao giờ lơ là.",
      "hm.landing.safety_1_ttl": "Địa điểm công cộng",
      "hm.landing.safety_1_body": "Mọi sự kiện đều diễn ra tại địa điểm công cộng có nhân viên — quán cà phê, nhà hàng, công viên, phòng gym hoặc studio. Không bao giờ diễn ra tại nhà riêng trừ khi mọi người tham gia đều đồng ý.",
      "hm.landing.safety_2_ttl": "Tiêu chuẩn cộng đồng",
      "hm.landing.safety_2_body": "Không khoan nhượng đối với sự thiếu tôn trọng, quấy rối hoặc gian dối. Thành viên vi phạm sẽ bị mời ra ngay lập tức.",
      "hm.landing.safety_3_ttl": "Bảo vệ quyền riêng tư",
      "hm.landing.safety_3_body": "Thông tin chi tiết của bạn không bao giờ được chia sẻ công khai. Bạn kiểm soát việc mình sẽ kết nối với ai sau mỗi sự kiện.",
      "hm.landing.safety_4_ttl": "Hỗ trợ sẵn sàng",
      "hm.landing.safety_4_body": "Đội ngũ của chúng tôi luôn sẵn sàng trước, trong và sau mỗi sự kiện nếu bạn có câu hỏi hoặc lo ngại.",
      "hm.landing.bottom_title": "Nhóm yêu thích tiếp theo của bạn chỉ cách một lần chạm.",
      "hm.landing.bottom_sub": "Chọn con đường của bạn — tham gia một nhóm hiện có, đưa hội bạn của bạn vào một Circle, hoặc để Matchmaker xếp bạn vào một bàn tiệc đã được chọn lọc sẵn.",
      "hm.landing.bottom_cta_circles": "Bắt đầu một Circle",
      "hm.landing.bottom_cta_mm": "Làm trắc nghiệm Matchmaker",
      "hm.circles.page_title": "HustleMeet Circles · Hội bạn cũ, địa điểm mới mỗi lần",
      "hm.circles.kicker": "HustleMeet · Circles",
      "hm.circles.hero_title": "Hội bạn cũ. Một bàn tiệc mới mỗi lần.",
      "hm.circles.hero_title_accent": "Hội bạn cũ",
      "hm.circles.hero_sub": "Đã có hội bạn thân? Circles là cách dễ nhất để giữ họ luôn gần gũi. Chúng tôi đặt địa điểm, lên kế hoạch giải đố, chuẩn bị bài tây. Bạn chỉ cần xuất hiện — vẫn hội bạn đó, một đêm mới, một nơi mới.",
      "hm.circles.cta_find": "Bắt đầu một Circle",
      "hm.circles.cta_host_hero": "Tham gia một Circle đang mở",
      "hm.circles.trust_venue": "Thay đổi các địa điểm đã kiểm duyệt",
      "hm.circles.trust_party": "4-12 người bạn mỗi Circle",
      "hm.circles.trust_verified": "Thành viên đã xác thực",
      "hm.circles.trust_friendship": "Tình bạn thực sự, luôn gắn kết",
      "hm.circles.how_kicker": "Cách thức hoạt động",
      "hm.circles.how_title": "Bốn bước. Hội bạn của bạn vẫn là một hội.",
      "hm.circles.how_step1_ttl": "Bắt đầu Circle của bạn",
      "hm.circles.how_step1_sub": "Đặt tên, mời bạn bè (tối đa 12 người), và chọn nhịp điệu — hàng tuần, hai tuần một lần hoặc hàng tháng.",
      "hm.circles.how_step2_ttl": "Chúng tôi chọn địa điểm",
      "hm.circles.how_step2_sub": "Mỗi sự kiện chúng tôi đề xuất một điểm mới — nhà hàng, đêm giải đố, phòng chơi bài, quán rượu vang. Hoặc chọn cố định một nơi yêu thích.",
      "hm.circles.how_step3_ttl": "Mọi người xác nhận RSVP chỉ với một lần chạm",
      "hm.circles.how_step3_sub": "Tự động nhắc nhở, chia hóa đơn, trả trước chỗ ngồi để việc vắng mặt không làm hỏng kế hoạch. Mọi thứ xong xuôi trước khi món khai vị dọn ra.",
      "hm.circles.how_step4_ttl": "Duy trì nhịp điệu",
      "hm.circles.how_step4_sub": "Một đêm trong nhật ký không bao giờ phải tốn công tổ chức nữa. Nhóm sẽ gắn kết hơn qua nhiều tháng, không chỉ vài phút.",
      "hm.circles.cadence_weekly_ttl": "Hàng tuần",
      "hm.circles.cadence_weekly_sub": "Mỗi tuần",
      "hm.circles.cadence_weekly_body": "Dành cho những hội bạn muốn có một nghi thức tối thứ Ba — vẫn những gương mặt đó, nhưng nơi chốn mới mỗi lần.",
      "hm.circles.cadence_fortnight_ttl": "Hai tuần một lần",
      "hm.circles.cadence_fortnight_sub": "Mỗi 2 tuần",
      "hm.circles.cadence_fortnight_body": "Điểm rơi hoàn hảo — thường xuyên nhưng không quá áp lực cho những lịch trình bận rộn.",
      "hm.circles.cadence_monthly_ttl": "Hàng tháng",
      "hm.circles.cadence_monthly_sub": "Mỗi tháng",
      "hm.circles.cadence_monthly_body": "Các Circle cho dịp đặc biệt — tiệc nướng Chủ Nhật, đêm giải đố lớn, thưởng thức ẩm thực, poker.",
      "hm.circles.feast_kicker": "Chọn đêm của bạn",
      "hm.circles.feast_title": "Ba cách để tận hưởng",
      "hm.circles.feast_sub": "Mỗi Circle tự chọn cách đêm đó diễn ra. Thay đổi luân phiên, hoặc chọn cố định phong cách yêu thích của hội bạn.",
      "hm.circles.feast_surprise_ttl": "Thử một nhà hàng mới",
      "hm.circles.feast_surprise_body": "Chúng tôi chọn ra ba địa điểm gần bạn, mọi người bình chọn, nhà bếp chuẩn bị cho một bàn đầy đủ. Chia sẻ chi phí, chỗ ngồi trả trước.",
      "hm.circles.feast_preselect_ttl": "Giải đố / Bài tây / Trò chơi",
      "hm.circles.feast_preselect_body": "Đặt một đêm giải đố, phòng poker, quán board-game hoặc sân bowling. Chúng tôi lo địa điểm, phí tham gia và nhắc nhở.",
      "hm.circles.feast_per_event_ttl": "Đêm theo chủ đề một lần",
      "hm.circles.feast_per_event_body": "Nếm rượu vang, bàn tiệc của đầu bếp, lớp học cocktail, phòng thoát hiểm. Một điều gì đó đặc biệt thỉnh thoảng diễn ra — được tổ chức trọn gói.",
      "hm.circles.live_kicker": "Ngay bây giờ",
      "hm.circles.live_title": "Các Circle bạn có thể tham gia",
      "hm.circles.live_host_link": "Bắt đầu nhóm của riêng bạn",
      "hm.circles.live_loading": "Đang tải…",
      "hm.circles.live_empty_title": "Hãy là Circle đầu tiên tại thành phố của bạn.",
      "hm.circles.live_empty_sub": "Bắt đầu với ba người bạn và chúng tôi sẽ giúp bạn mời những người còn lại — đồng thời tặng bạn ba địa điểm đầu tiên.",
      "hm.circles.live_empty_cta": "Bắt đầu một Circle",
      "hm.circles.host_kicker": "Dành cho những người dẫn dắt Circle",
      "hm.circles.host_title": "Bạn mang đến hội bạn. Chúng tôi lo phần quản lý.",
      "hm.circles.host_body": "Nếu bạn là người bạn luôn phải đứng ra tổ chức các buổi đi chơi, Circles sẽ giúp bạn tiết kiệm hàng giờ đồng hồ. Tự động nhắc nhở, RSVP trả trước, danh sách rút gọn địa điểm, chia sẻ chi phí, quản lý danh sách chờ và ví hoàn tiền cho mỗi đêm đi chơi. Bạn chỉ cần xuất hiện như một thành viên bình thường.",
      "hm.circles.host_perk_1": "Danh sách địa điểm tự động — không bao giờ phải lướt Google nữa",
      "hm.circles.host_perk_2": "Chỗ ngồi trả trước — không lo vắng mặt phút chót",
      "hm.circles.host_perk_3": "Chia chi phí xong xuôi trước khi ăn món tráng miệng",
      "hm.circles.host_perk_4": "Nhắc nhở + mời trên lịch hoàn toàn tự động",
      "hm.circles.host_perk_5": "Danh sách chờ tự động đôn lên nếu có bạn hủy lịch",
      "hm.circles.host_perk_6": "Ví hoàn tiền cho mỗi đêm đi chơi",
      "hm.circles.host_cta": "Bắt đầu một Circle ngay",
      "hm.circles.why_kicker": "Tại sao Circles hiệu quả",
      "hm.circles.why_title": "Tình bạn trưởng thành cần một hạ tầng",
      "hm.circles.why_1_ttl": "Vẫn hội bạn đó, tin tưởng sâu sắc hơn",
      "hm.circles.why_1_body": "Cuộc sống kéo bạn bè rời xa nhau. Circles là nhịp điệu cố định giữ mọi người gần lại — vẫn 4-12 người bạn đó, gặp nhau vào cùng một tối trong tuần, bất kể điều gì.",
      "hm.circles.why_2_ttl": "Không còn mệt mỏi vì quản lý",
      "hm.circles.why_2_body": "Không ai phải làm trưởng nhóm WhatsApp nữa. Chúng tôi chọn địa điểm, gửi lời nhắc, nhận thanh toán, hoàn tất quy trình.",
      "hm.circles.why_3_ttl": "Địa điểm mới, kỷ niệm mới",
      "hm.circles.why_3_body": "Luân phiên qua những nhà hàng nhỏ, phòng giải đố và quán game tốt nhất tại Australia. Những gương mặt thân quen, bối cảnh tươi mới — điểm giao thoa hoàn hảo giữa thói quen và sự mới lạ.",
      "hm.circles.bottom_title": "Đêm mà hội bạn của bạn mong chờ, mà không ai phải vất vả lên kế hoạch.",
      "hm.circles.bottom_sub": "Một Circle. Những người bạn cũ. Địa điểm mới mỗi lần.",
      "hm.circles.bottom_cta_find": "Bắt đầu một Circle",
      "hm.circles.bottom_cta_mm": "Gặp gỡ những người mới với Matchmaker →",
      "hm.circles.card_host_pill": "Circle",
      "hm.matchmaker.page_title": "HustleMeet Matchmaker · AI chọn đúng bàn tiệc",
      "hm.matchmaker.kicker": "HustleMeet · Matchmaker",
      "hm.matchmaker.hero_title": "AI chọn bàn. Bạn chỉ cần đến.",
      "hm.matchmaker.hero_title_accent": "Bạn chỉ cần đến.",
      "hm.matchmaker.hero_sub": "Trả lời một bài trắc nghiệm ngắn về tính cách & giá trị. Matchmaker của chúng tôi xếp bạn ngồi cùng bốn đến bảy người lạ, những người sẽ mang lại cảm giác như bạn cũ ngay khi đến món tráng miệng. Không quẹt. Không xã giao hời hợt. Chỉ là một bàn tiệc chung, sẵn sàng khi bạn sẵn sàng.",
      "hm.matchmaker.cta_quiz": "Làm trắc nghiệm ghép đôi",
      "hm.matchmaker.cta_see_circles": "Xem Circles thay thế →",
      "hm.matchmaker.trust_big5": "Chấm điểm tính cách Big-5",
      "hm.matchmaker.trust_seats": "4-8 chỗ mỗi bàn",
      "hm.matchmaker.trust_lock": "Không quẹt · không ảnh",
      "hm.matchmaker.trust_verified": "Chỉ thành viên đã xác thực",
      "hm.matchmaker.how_kicker": "Cách thức hoạt động",
      "hm.matchmaker.how_title": "Bốn bước để đến với một bàn tiệc đã được chọn lọc sẵn.",
      "hm.matchmaker.how_step1_ttl": "Làm trắc nghiệm",
      "hm.matchmaker.how_step1_sub": "Bản câu hỏi 10 phút — tính cách, giá trị, ăn kiêng, thời gian rảnh. Bạn chỉ làm một lần duy nhất.",
      "hm.matchmaker.how_step2_ttl": "Ghép đôi qua đêm",
      "hm.matchmaker.how_step2_sub": "AI của chúng tôi đọc hồ sơ của bạn và danh sách bữa tối hiện có tại Australia. Khi điểm phù hợp đạt 0.70 trở lên, nó sẽ soạn một lời mời cá nhân.",
      "hm.matchmaker.how_step3_ttl": "Chấp nhận hoặc Từ chối",
      "hm.matchmaker.how_step3_sub": "Bạn nhận được một email mời rõ ràng với địa điểm, chủ đề bàn tiệc và ba sở thích chung mà bạn chia sẻ với nhóm.",
      "hm.matchmaker.how_step4_ttl": "Hệ thống tự học hỏi",
      "hm.matchmaker.how_step4_sub": "Mỗi khảo sát sau bữa tối sẽ tinh chỉnh mô hình. Lần ghép đôi tiếp theo sẽ thông minh hơn sau mỗi lần bạn đi ăn.",
      "hm.matchmaker.science_kicker": "Tại sao nó hiệu quả",
      "hm.matchmaker.science_title": "Matchmaker không đoán mò — nó chấm điểm.",
      "hm.matchmaker.science_sub": "Mỗi lần ghép đôi là một tổ hợp trọng số của bốn yếu tố. Khi cả bàn đạt trung bình trên 0.70, chúng tôi gửi lời mời. Bất cứ điều gì thấp hơn sẽ nằm trong hàng đợi.",
      "hm.matchmaker.science_1_ttl": "Tính cách (35%)",
      "hm.matchmaker.science_1_body": "Độ tương đồng Cosine qua các vector tính cách Big-5 — sự cởi mở, sự tận tâm, sự hướng ngoại, sự dễ chịu và sự ổn định cảm xúc.",
      "hm.matchmaker.science_2_ttl": "Giá trị (25%)",
      "hm.matchmaker.science_2_body": "Sự trùng khớp về các giá trị giúp cuộc trò chuyện trên bàn ăn thực sự gắn kết — gia đình, sự tò mò, sự phát triển, sự phục vụ, sự phiêu lưu.",
      "hm.matchmaker.science_3_ttl": "Sở thích (25%)",
      "hm.matchmaker.science_3_body": "Sự trùng khớp Jaccard về các sở thích cụ thể của bạn — đi bộ đường dài Six Foot Track, câu lạc bộ phim Sydney, văn hóa cà phê Melbourne, cuốn sách thứ ba trong năm của bạn.",
      "hm.matchmaker.science_4_ttl": "Thời gian rảnh (15%)",
      "hm.matchmaker.science_4_body": "Tối mà bạn thực sự có thể tham gia. Sẽ không có lời mời lúc 7 giờ tối thứ Ba nếu lịch của bạn ghi là đi gym-tối-thứ-Ba-mãi-mãi.",
      "hm.matchmaker.safety_kicker": "An toàn từ khâu thiết kế",
      "hm.matchmaker.safety_title": "Matchmaker luôn thận trọng trong thầm lặng.",
      "hm.matchmaker.safety_1_ttl": "Ngưỡng tin cậy",
      "hm.matchmaker.safety_1_body": "Dưới mức điểm nhóm 0.70, không có lời mời nào được gửi đi. Một sự ghép đôi không chắc chắn thà không có bữa tối còn hơn.",
      "hm.matchmaker.safety_2_ttl": "Danh sách chặn từ hai phía",
      "hm.matchmaker.safety_2_body": "Nếu hai thành viên riêng tư báo hiệu 'không' sau một bữa tối, chúng tôi sẽ không bao giờ xếp họ ngồi cùng nhau nữa.",
      "hm.matchmaker.safety_3_ttl": "Nút dừng tự động",
      "hm.matchmaker.safety_3_body": "Nếu 20% hoặc nhiều hơn các khảo sát gần đây đánh giá một bàn tiệc 2/5 sao hoặc thấp hơn, việc gửi lời mời sẽ tạm dừng trong 24 giờ để người điều hành xem xét.",
      "hm.matchmaker.safety_4_ttl": "Tiêu chuẩn cộng đồng",
      "hm.matchmaker.safety_4_body": "Mỗi thành viên đều chấp nhận một bộ quy tắc ứng xử bằng ngôn ngữ dễ hiểu. Chúng tôi loại bỏ những người vi phạm — không kháng cáo.",
      "hm.matchmaker.safety_5_ttl": "Chỉ các địa điểm công cộng",
      "hm.matchmaker.safety_5_body": "Mọi bữa tối Matchmaker đều diễn ra tại một nhà hàng thực thụ có nhân viên. Không bao giờ tại nhà riêng.",
      "hm.matchmaker.safety_6_ttl": "Phản hồi sau sự kiện",
      "hm.matchmaker.safety_6_body": "Khảo sát riêng tư của bạn định hình các lần ghép đôi trong tương lai và có thể kích hoạt việc xem xét lại địa điểm hoặc một thành viên khác.",
      "hm.matchmaker.loop_kicker": "Vòng lặp học tập",
      "hm.matchmaker.loop_title": "Mỗi bữa tối làm cho bữa tối tiếp theo tuyệt vời hơn.",
      "hm.matchmaker.loop_body": "Matchmaker không đứng yên. Sau mỗi sự kiện, bạn gửi một khảo sát riêng tư ngắn — cảm nhận về cuộc trò chuyện, bạn muốn đi ăn với ai lần nữa, địa điểm có đạt yêu cầu không. Những tín hiệu đó phản hồi lại mô hình và tái định hình mọi lần ghép đôi trong tương lai.",
      "hm.matchmaker.loop_1": "Các lượt bình chọn \"muốn ăn cùng lần nữa\" củng cố sự ghép đôi của bạn",
      "hm.matchmaker.loop_2": "Các lượt bình chọn \"không\" từ hai phía sẽ âm thầm loại bỏ các cặp",
      "hm.matchmaker.loop_3": "Xếp hạng địa điểm & cuộc trò chuyện điều chỉnh sự kết hợp chỗ ngồi",
      "hm.matchmaker.loop_4": "Điểm tin cậy bảo vệ thành viên khỏi những người hay vắng mặt",
      "hm.matchmaker.loop_5": "Thiên kiến học tập củng cố các lần ghép đôi tốt qua từng tháng",
      "hm.matchmaker.loop_6": "Bạn luôn nắm quyền kiểm soát — hủy tham gia hoặc đặt lại hồ sơ bất cứ lúc nào",
      "hm.matchmaker.vs_kicker": "Cái nào phù hợp với tôi?",
      "hm.matchmaker.vs_title": "Matchmaker hay Circles?",
      "hm.matchmaker.vs_mm_ttl": "Chọn tôi nếu tôi muốn sự đa dạng.",
      "hm.matchmaker.vs_mm_1": "Một bàn tiệc, thành phố, phong cách ẩm thực khác nhau mỗi lần",
      "hm.matchmaker.vs_mm_2": "Nhóm 4-8 người lạ tương thích được AI tuyển chọn",
      "hm.matchmaker.vs_mm_3": "Nhịp điệu hàng tuần, không cần cam kết làm host",
      "hm.matchmaker.vs_mm_4": "Tuyệt vời nếu bạn mới đến thành phố hoặc đang đi du lịch",
      "hm.matchmaker.vs_mm_cta": "Làm trắc nghiệm ghép đôi",
      "hm.matchmaker.vs_circles_ttl": "Chọn tôi nếu tôi muốn sự ổn định.",
      "hm.matchmaker.vs_circles_1": "Cùng một host · cùng địa điểm · cùng tối trong tuần",
      "hm.matchmaker.vs_circles_2": "Một nhóm trở thành truyền thống tối thứ Tư của bạn",
      "hm.matchmaker.vs_circles_3": "Được tuyển chọn bởi một host giàu kinh nghiệm",
      "hm.matchmaker.vs_circles_4": "Tuyệt vời nếu bạn mới chuyển đến và muốn gắn bó lâu dài",
      "hm.matchmaker.vs_circles_cta": "Xem các Circle",
      "hm.matchmaker.bottom_title": "Một bài trắc nghiệm. Mọi bữa tối sau đó đều đã được chọn lọc sẵn.",
      "hm.matchmaker.bottom_sub": "Bạn sẽ được ghép đôi trong vòng hai tuần. Đó là cái gật đầu dễ dàng nhất mà bạn sẽ thực hiện trong tháng này.",
      "hm.matchmaker.bottom_cta": "Làm trắc nghiệm ghép đôi",
      "hm.matchmaker.bottom_note": "Không cần thẻ tín dụng · 10 phút · Hủy ghép cặp bất cứ lúc nào",
      /* AUTO-TRANSLATED hm.* END */
    },
  },
  ar: {
    translation: {
      /* AUTO-TRANSLATED ar START */
      plaza_nav_features: "الميزات",
      plaza_nav_rewards: "المكافآت",
      plaza_nav_how: "كيف يعمل",
      plaza_nav_roadmap: "خارطة الطريق",
      plaza_nav_faq: "الأسئلة الشائعة",
      plaza_hero_badge: "مملوكة أسترالياً",
      plaza_hero_kicker: "سلة واحدة. كل المكافآت.",
      plaza_hero_title_1: "تسوق محلياً.",
      plaza_hero_title_2: "اكسب في كل مكان.",
      plaza_hero_sub: "آلاف البائعين الأستراليين في سلة واحدة. كل دولار تنفقه يكسبك HustlePoints — قابلة للاستبدال في المطاعم والمقاهي والفنادق ومصففي الشعر ومراكز العناية بالحيوانات الأليفة وأي عمل تجاري في شبكة HustleSuite.",
      plaza_cta_start: "انضم مجاناً — يستغرق 30 ثانية",
      plaza_cta_signin: "تسجيل الدخول",
      plaza_cta_shop: "تصفح المتجر",
      plaza_hero_stat_1_v: "12,847",
      plaza_hero_stat_1_l: "منتج متوفر",
      plaza_hero_stat_2_v: "340+",
      plaza_hero_stat_2_l: "بائع أسترالي",
      plaza_hero_stat_3_v: "$4.2M",
      plaza_hero_stat_3_l: "مكافآت مدفوعة",
      plaza_hero_stat_4_v: "19",
      plaza_hero_stat_4_l: "فئة",
      plaza_problem_kicker: "المشكلة مع التسوق اليوم",
      plaza_problem_title: "عشر علامات تبويب. عشر كلمات مرور. ولاء صفر.",
      plaza_problem_sub: "أنت تتنقل بالفعل بين Amazon و eBay و Kogan و Kmart و Woolies و Coles و Bunnings و JB و Officeworks ونصف دزينة من المتاجر المتخصصة. تقضي ساعات في مقارنة الأسعار — ثم لا تحصل على شيء سوى إيصال.",
      plaza_problem_1: "أسعار لا يمكن مقارنتها عبر المواقع",
      plaza_problem_2: "بائعون أجانب يتظاهرون بأنهم محليون",
      plaza_problem_3: "نقاط الولاء محاصرة في متجر واحد",
      plaza_problem_4: "مراجعات مزيفة من الروبوتات وموزعي الشحن",
      plaza_solution_kicker: "ما يفعله HustlePlaza بشكل مختلف",
      plaza_solution_title: "آخر سلة ستفتحها طوال اليوم.",
      plaza_solution_sub: "بحث واحد. سلة واحدة. دفع واحد. كل عملية شراء تعيد لك HustlePoints قابلة للإنفاق عبر نظامنا البيئي بالكامل — وليس فقط هذا المتجر.",
      plaza_solution_1_t: "سلة واحدة، كل البائعين",
      plaza_solution_1_b: "تسوق من كل بائع أسترالي على المنصة وادفع مرة واحدة عند الدفع. يتم دمج التوصيل حيثما أمكن.",
      plaza_solution_2_t: "محليون موثقون فقط",
      plaza_solution_2_b: "يتم التحقق من هوية كل بائع قبل أن يتمكن من عرض المنتجات. لا روبوتات، لا تزييف، لا غموض في الشحن المباشر.",
      plaza_solution_3_t: "مكافآت تنتقل فعلياً",
      plaza_solution_3_b: "النقاط المكتسبة هنا تُنفق في أي عمل تجاري في HustleSuite — المقاهي والفنادق ومصففي الشعر والمطاعم ومراكز العناية بالحيوانات الأليفة.",
      plaza_solution_4_t: "تتبع مباشر، شاشة واحدة",
      plaza_solution_4_b: "يتم تتبع كل طرد من كل بائع على صفحة واحدة. إشعارات عبر البريد الإلكتروني والرسائل النصية في كل خطوة.",
      plaza_rewards_kicker: "دائرة HustlePoints",
      plaza_rewards_title: "اكسب أثناء التسوق. أنفق في أي مكان في الشبكة.",
      plaza_rewards_sub: "كل $1 تنفقه على HustlePlaza يكسبك HustlePoints. استبدلها في أي مطعم أو مقهى أو فندق أو مصفف شعر أو عمل خدمي يعمل على HustleSuite — في أي مكان عبر الشبكة.",
      plaza_rewards_step_1: "تسوق في السوق",
      plaza_rewards_step_1_b: "اشتر أي شيء من تيشيرت إلى ثلاجة.",
      plaza_rewards_step_2: "اكسب HustlePoints فوراً",
      plaza_rewards_step_2_b: "تظهر النقاط بمجرد شحن طلبك — لا بطاقات، لا بطاقات ثقب، لا ألعاب انتهاء صلاحية.",
      plaza_rewards_step_3: "أنفق عبر الشبكة",
      plaza_rewards_step_3_b: "استبدل في المقاهي والمطاعم والفنادق ومصففي الشعر ومراكز العناية بالحيوانات الأليفة والحرفيين.",
      plaza_rewards_step_4: "أو سحب نقدي",
      plaza_rewards_step_4_b: "حوّل إلى تحويل بنكي بمجرد وصولك إلى $50.",
      plaza_cats_kicker: "تسوق حسب الفئة",
      plaza_cats_title: "19 فئة. بائعون أستراليون. مخزون حي.",
      plaza_cats_sub: "تصفح نموذجاً مما هو متوفر الآن — أو سجل وتسوق في كتالوج المنتجات الكامل الذي يضم أكثر من 12,000 منتج.",
      plaza_cats_cta: "عرض جميع الفئات",
      plaza_vendors_kicker: "بائعون أستراليون حقيقيون",
      plaza_vendors_title: "تعرف على الصُنّاع والمزارعين والمتاجر العائلية.",
      plaza_vendors_sub: "كل بائع على HustlePlaza تم التحقق من رقم ABN الخاص به، ومقره في أستراليا، وتم تسجيله شخصياً. لا عابرين من الخارج يتظاهرون بأنهم محليون.",
      plaza_how_kicker: "كيف يعمل",
      plaza_how_title: "ثلاث خطوات. سلة واحدة. مكافآت إلى الأبد.",
      plaza_how_1_title: "انضم في 30 ثانية",
      plaza_how_1_body: "تحقق من بريدك الإلكتروني وتصفح فوراً. أضف رقم هاتف عندما تكون مستعداً للدفع.",
      plaza_how_2_title: "املأ سلتك",
      plaza_how_2_body: "تسوق من كل بائع. سلة واحدة، عرض توصيل واحد، دفع واحد. الدفع عبر Stripe أو PayPal.",
      plaza_how_3_title: "اكسب وأنفق في أي مكان",
      plaza_how_3_body: "تظهر HustlePoints بمجرد شحن طلبك. أنفقها في أي عمل تجاري في HustleSuite، أو سحب نقدي عند $50.",
      plaza_compare_kicker: "كيف نتفوق",
      plaza_compare_title: "HustlePlaza مقابل العمالقة",
      plaza_compare_col_us: "HustlePlaza",
      plaza_compare_col_amz: "Amazon AU",
      plaza_compare_col_ebay: "eBay AU",
      plaza_compare_col_kmart: "Kmart / Big W",
      plaza_compare_row_1: "مملوكة أسترالياً",
      plaza_compare_row_2: "سلة واحدة عبر كل بائع",
      plaza_compare_row_3: "مكافآت قابلة للإنفاق خارج هذا المتجر",
      plaza_compare_row_4: "سحب نقدي للمكافآت إلى حسابك البنكي",
      plaza_compare_row_5: "دعم عملاء مستضاف في أستراليا",
      plaza_trust_kicker: "لماذا نتحقق",
      plaza_trust_title: "عملاء حقيقيون. مراجعات حقيقية.",
      plaza_trust_body: "نتحقق من بريدك الإلكتروني عند التسجيل، ورقم هاتفك المحمول قبل الدفع، ونطلب فحص صورة مباشرة قبل أن تتمكن من نشر المراجعات أو الدردشة مع البائعين أو إجراء عمليات شراء عالية القيمة. هذا يبطئ المحتالين ويبقي الأسعار منخفضة لك.",
      plaza_trust_1: "التحقق من البريد الإلكتروني للانضمام",
      plaza_trust_2: "التحقق عبر الرسائل النصية للدفع",
      plaza_trust_3: "التحقق من الصورة المباشرة للمراجعات والدردشة",
      plaza_trust_4: "فحوصات هوية البائع عند التسجيل",
      plaza_test_kicker: "ما يقوله أعضاؤنا الأوائل",
      plaza_test_title: "مباشرة من العملاء الحقيقيين.",
      plaza_test_1_body: "استخدمت نقاط HustlePlaza الخاصة بي لدفع ثمن العشاء في مقهى بالقرب من العمل. حقاً لم أصدق أنه سيعمل حتى ابتسمت النادلة وقالت 'كل شيء جاهز'.",
      plaza_test_1_name: "Priya S.",
      plaza_test_2_body: "ستة بائعين، نافذة توصيل واحدة، بريد تأكيد واحد. عادةً سيكون لدي ستة كلمات مرور وستة أرقام تتبع. هذا يعمل ببساطة.",
      plaza_test_2_name: "Marcus T.",
      plaza_test_3_body: "كمنتج عسل صغير، أخيراً نحصل على الدفع في نفس اليوم، ومراجعات حقيقية، ولا خصم 40٪. نقلني HustlePlaza من 'عمل جانبي' إلى بدوام كامل في ثمانية أشهر.",
      plaza_test_3_name: "Kate & Dave",
      plaza_faq_kicker: "إجابات",
      plaza_faq_title: "الأسئلة التي نتلقاها كل أسبوع",
      plaza_faq_1_q: "هل HustlePlaza مملوكة فعلاً لأستراليين؟",
      plaza_faq_1_a: "نعم. HustlePlaza مملوكة بنسبة 100٪ لأستراليين، مستضافة على خوادم أسترالية، تديرها فريق أسترالي. كل بائع يخضع للتحقق من الهوية قبل أن يتمكن من نشر أول قائمة له.",
      plaza_faq_2_q: "كيف تعمل المكافآت؟",
      plaza_faq_2_a: "كل دولار تنفقه على HustlePlaza يكسبك HustlePoints. تظهر النقاط بمجرد شحن طلبك ولا تنتهي صلاحيتها أبداً. أنفقها في أي عمل تجاري يعمل على HustleSuite، أو سحبها نقداً إلى حسابك البنكي بمجرد توفيرك ما يكفي.",
      plaza_faq_3_q: "لماذا تتحققون من الصور؟",
      plaza_faq_3_a: "لكي تكون المراجعات التي تقرأها حقيقية. التحقق من الصورة هو فحص صورة شخصية مباشرة يتم عبر كاميرا هاتفك — نفس التقنية التي تستخدمها البنوك — ويوقف الروبوتات والحسابات المزيفة والجهات الفاعلة العدائية من التلاعب بالمنصة.",
      plaza_faq_4_q: "ماذا لو لم يصل طلبي؟",
      plaza_faq_4_a: "كل طلب مدعوم بضمان HustleGuarantee: إذا لم تصل الطرد أو لم يكن كما هو موصوف، نعيد إليك المبلغ خلال 3 أيام عمل، ثم نتابع مع البائع. لن تكون أبداً خارج الجيب.",
      plaza_faq_5_q: "هل معلومات بطاقتي آمنة؟",
      plaza_faq_5_a: "نحن لا نرى أو نخزن رقم بطاقتك أبداً. جميع المدفوعات تتم عبر Stripe (PCI-DSS المستوى 1) أو PayPal، كلاهما مع مصادقة 3D Secure التي تحميك من الرسوم الاحتيالية على البطاقات المسروقة.",
      plaza_faq_6_q: "هل تشحنون دولياً؟",
      plaza_faq_6_a: "ليس بعد. HustlePlaza متاح في أستراليا فقط عند الإطلاق — وهذا كيف يمكننا الوعد بالتسليم في نفس الأسبوع لمعظم العناصر والدعم في منطقتك الزمنية. نيوزيلندا على خارطة الطريق 2026.",
      plaza_faq_7_q: "كم تكلفة الانضمام؟",
      plaza_faq_7_a: "لا شيء. العضوية مجانية إلى الأبد. يدفع البائعون عمولة صغيرة لكل عملية بيع — المشترون يدفعون $0 للانضمام، $0 للكسب، $0 للاستبدال.",
      plaza_faq_8_q: "هل يمكنني البيع على HustlePlaza؟",
      plaza_faq_8_a: "بالتأكيد. توجه إلى hustlesuite.au للتسجيل كبائع — موافقة في نفس اليوم للشركات الأسترالية الموثقة، بالإضافة إلى أول 30 يوماً بدون عمولة.",
      plaza_final_kicker: "أسعار المؤسسين — مقاعد محدودة",
      plaza_final_title: "كن واحداً من الأوائل — احجز أسعار المؤسسين.",
      plaza_final_sub: "سجل اليوم كعضو في HustleSuite. ستكون أول من يعرف عندما يتم شحن كل ميزة جديدة، وسيحتفظ حسابك بمكافآت وأسعار مستوى المؤسسين مدى الحياة.",
      plaza_final_bonus: "المؤسسون لا يفقدون سعر المؤسسين أبداً. مجاني إلى الأبد، الإلغاء في أي وقت.",
      plaza_final_cta: "التسجيل كعضو مؤسس",
      plaza_final_disclosure: "لا حاجة لبطاقة. التحقق من البريد الإلكتروني والهاتف المحمول فقط.",
      plaza_roadmap_kicker: "ما نبنيه بعد ذلك",
      plaza_roadmap_title: "ستة أدوات مصممة لرفع العمل عن كاهلك.",
      plaza_roadmap_sub: "HustleSuite أكثر من مجرد متجر — إنها طبقة إنتاجية كاملة. إليك ما هو على المدرج.",
      plaza_roadmap_soon: "قريباً",
      plaza_roadmap_next: "التالي في قائمة الانتظار",
      plaza_roadmap_1_t: "HustleDictate — تحويل الكلام إلى نص على أي نموذج",
      plaza_roadmap_1_b: "امتداد Chrome. اضغط على Alt+M، تحدث، وأي إدخال على أي موقع ويب يملأ نفسه. طلبات العقارات والفواتير وأوراق NDIS — تنجز في نصف الوقت.",
      plaza_roadmap_2_t: "HustleConnect التحرير في أي مكان",
      plaza_roadmap_2_b: "لمالكي WordPress: حرر موقعك المباشر من أي صفحة بضغطة مفتاح واحدة. لا مزيد من رحلات wp-admin.",
      plaza_roadmap_3_t: "زر الحفظ إلى HustleSuite العام",
      plaza_roadmap_3_b: "انقر بزر الماوس الأيمن على أي صفحة ويب — قائمة عقارات، مقالة، منتج، مطعم — وتصل إلى الوحدة الصحيحة جاهزة للعمل عليها.",
      plaza_roadmap_4_t: "فاحص أسعار HustlePlaza",
      plaza_roadmap_4_b: "تصفح Amazon أو eBay أو Kmart — تظهر رقاقة عائمة نفس المنتج على HustlePlaza مع معاينة مكافآتك.",
      plaza_roadmap_5_t: "جامع المراجعات",
      plaza_roadmap_5_b: "مزامنة تلقائية للمراجعات الجديدة من Google و Facebook و TripAdvisor إلى لوحة تحكم HustleSuite الخاصة بك. توفير ~20 دقيقة في الأسبوع.",
      plaza_roadmap_6_t: "الشريط الجانبي HustleSuite",
      plaza_roadmap_6_b: "لوحة جانبية في Chrome مع الطلبات المباشرة ومؤشرات الأداء الرئيسية والنشر السريع والفاتورة السريعة. عملك بالكامل في شريط بجانب كل علامة تبويب.",
      plaza_footer_owned: "مملوكة ومستضافة في أستراليا",
      plaza_footer_terms: "الشروط",
      plaza_footer_privacy: "الخصوصية",
      plaza_footer_contact: "اتصل بنا",
      plaza_footer_vendors: "البيع على HustlePlaza",
      plaza_footer_help: "مركز المساعدة",
      plaza_footer_tagline: "جزء من عائلة HustleSuite — تسجيل دخول واحد، كل الخدمات.",
      verify_gate_title: "تقريباً هناك — تسجيل سريع",
      verify_gate_body: "HustlePlaza هي عربة تسوق تقدم العديد من الفوائد بما في ذلك المكافآت القابلة للاستبدال التي يمكن استخدامها، ليس فقط داخل هذه المنصة ولكن مع أي عمل تجاري آخر يستخدم منصة HustlePlaza أو أي عمل تجاري آخر مسجل في HustleSuite. بعض هذه الأعمال ستشمل المطاعم والمقاهي والفنادق ومصففي الشعر وحتى العناية بالحيوانات الأليفة. لمواصلة تقديم أسعار وخدمات رائعة، نحتاج إلى التأكد من أنك حقيقي والتحقق منك. اضغط على زر الانضمام للمتابعة.",
      verify_gate_join: "الانضمام إلى HustleSuite",
      verify_gate_signin: "لدي حساب بالفعل",
      verify_gate_cancel: "ليس الآن",
      verify_email_title: "تحقق من بريدك الإلكتروني",
      verify_email_body: "سنرسل رمزاً مكوناً من 6 أرقام إلى صندوق الوارد الخاص بك.",
      verify_email_placeholder: "you@example.com",
      verify_email_send: "إرسال الرمز",
      verify_email_code_ph: "رمز مكون من 6 أرقام",
      verify_email_check: "تحقق",
      verify_email_sent: "تم إرسال الرمز — تحقق من صندوق الوارد الخاص بك.",
      verify_sms_title: "تحقق من هاتفك",
      verify_sms_body: "أضف رقم هاتف محمول حتى نتمكن من إرسال رسائل نصية إليك بتحديثات الطلب وتأكيد المشتريات الكبيرة.",
      verify_sms_placeholder: "+61412345678",
      verify_sms_send: "إرسال رسالة نصية",
      verify_sms_check: "تحقق",
      verify_sms_sent: "تم إرسال الرمز — تحقق من هاتفك.",
      verify_photo_title: "التحقق من الصورة",
      verify_photo_body: "امسح رمز QR بهاتفك لالتقاط صورة شخصية مباشرة. مطلوب للدردشة والمشتريات عالية القيمة والنشر في Meetup / Tackle.",
      verify_photo_waiting: "في انتظار هاتفك…",
      verify_photo_verified: "تم التحقق من الصورة — كل شيء جاهز.",
      travel_cta_start: "انضم مجاناً",
      travel_hero_kicker: "HustleTravel",
      travel_hero_title_1: "عضوية واحدة.",
      travel_hero_title_2: "في كل أنحاء أستراليا.",
      travel_hero_sub: "فنادق ورحلات طيران وتجارب وتخطيط رحلات برية وتأجير سيارات — محجوزة بأفضل سعر متاح وتدفع لك HustlePoints على كل دولار تنفقه.",
      travel_cta_plan: "خطط لرحلة برية",
      travel_cta_hotels: "ابحث عن فنادق",
      travel_svc_hotels_t: "فنادق وشقق",
      travel_svc_hotels_b: "قارن 200+ موقع حجز في مكان واحد — بحث تلوي مدعوم من Trivago.",
      travel_svc_flights_t: "رحلات الطيران",
      travel_svc_flights_b: "داخلية ودولية، مع خرائط المقاعد في الوقت الفعلي وقواعد الأسعار.",
      travel_svc_experiences_t: "التجارب والجولات",
      travel_svc_experiences_b: "أيام الشعاب المرجانية، تذوق النبيذ، الموسيقى الحية، الرحلات الملائمة للأطفال.",
      travel_svc_cars_t: "تأجير السيارات",
      travel_svc_cars_b: "كل العلامات التجارية الكبرى بالإضافة إلى المستقلين المحليين — الاستلام في أي مطار أسترالي.",
      travel_svc_insurance_t: "تأمين السفر",
      travel_svc_insurance_b: "مضمون من قبل شركات تأمين أسترالية منظمة من APRA. قارن على شاشة واحدة.",
      travel_svc_roadtrip_t: "مخطط الرحلات البرية",
      travel_svc_roadtrip_b: "مسارات رحلة واعية بنطاق السيارات الكهربائية مع الشواحن ومحطات التوقف الليلية وتقديرات التكلفة.",
      travel_final_kicker: "أسعار المؤسسين",
      travel_final_title: "احجز مكافآت مستوى المؤسسين مدى الحياة.",
      travel_final_sub: "سجل مرة واحدة مع HustleSuite. احجز كل شيء — الفنادق والرحلات الجوية والتجارب والتأمين — على HustleTravel واكسب HustlePoints قابلة للإنفاق عبر كل عمل تجاري في الشبكة.",
      travel_final_cta: "التسجيل كعضو مؤسس",
      travel_final_disclosure: "سياسة أفضل سعر: النتائج مرتبة حسب أفضل قيمة للمسافر، وليس العمولة.",
      dictate_hero_kicker: "HustleDictate",
      dictate_hero_title_1: "تحدث.",
      dictate_hero_title_2: "أي نموذج. أي موقع ويب.",
      dictate_hero_sub: "امتداد Chrome الوحيد الذي يحول أي حقل نص إلى ميكروفون إملاء صوتي احترافي — مدعوم بـ Whisper.",
      dictate_cta_install: "إضافة إلى Chrome",
      dictate_uc_re_t: "ملاحظات إيجار العقارات",
      dictate_uc_re_b: "أملِ تقارير التفتيش ورسائل المستأجرين مباشرة إلى نظام إدارة الممتلكات الخاص بك.",
      dictate_uc_ndis_t: "ملاحظات تقدم NDIS",
      dictate_uc_ndis_b: "تحدث بملاحظة النوبة بينما الذاكرة طازجة — بدون استخدام اليدين، بدون كتابة.",
      dictate_uc_invoice_t: "أوصاف الفواتير",
      dictate_uc_invoice_b: "أوصاف بنود الفاتورة المملاة مباشرة إلى Xero أو MYOB أو QuickBooks.",
      dictate_uc_email_t: "ردود البريد الإلكتروني الطويلة",
      dictate_uc_email_b: "تحدث بالردود في Gmail أو Outlook أو أي بريد ويب — أسرع ثلاث مرات من الكتابة.",
      dictate_final_kicker: "مجاني لأعضاء المؤسسين",
      dictate_final_title: "كل لوحة مفاتيح هي الآن ميكروفون.",
      dictate_final_sub: "مجاني لأعضاء HustleSuite المؤسسين. $5/شهر خلاف ذلك — الإلغاء في أي وقت.",
      dictate_final_cta: "إضافة إلى Chrome — مجاناً",
      "finder.cat.roadtrip": "رحلة برية",
      "finder.hotels.title": "ابحث عن فندق قريب",
      "finder.hotels.sub": "يقارن 200+ موقع حجز في بحث واحد — مدعوم من Trivago.",
      "finder.hotels.where": "أين",
      "finder.hotels.where_ph": "مدينة أو ضاحية (مثلاً Byron Bay, NSW)",
      "finder.hotels.checkin": "تسجيل الوصول",
      "finder.hotels.checkout": "تسجيل المغادرة",
      "finder.hotels.adults": "البالغون",
      "finder.hotels.search": "ابحث عن فنادق",
      "finder.hotels.disclosure": "يفتح Trivago — قد نكسب عمولة على الحجوزات.",
      /* AUTO-TRANSLATED ar END */
      nav_menu: "القائمة",
      nav_book: "احجز طاولة",
      nav_reviews: "التقييمات",
      nav_about: "من نحن",
      nav_account: "حسابي",
      nav_signin: "تسجيل الدخول",
      cart_open: "فتح السلة",
      cart_title: "طلبك",
      cart_empty: "السلة فارغة",
      cart_subtotal: "المجموع الفرعي",
      cart_continue: "متابعة",
      cart_pickup_date: "تاريخ الاستلام",
      cart_pickup_time: "وقت الاستلام",
      cart_pay_on_pickup: "ادفع عند الاستلام",
      cart_pay_now: "ادفع الآن",
      cart_place_order: "تأكيد الطلب",
      add_to_order: "إضافة إلى الطلب",
      menu_search_ph: "ابحث في القائمة…",
      menu_chef_specials: "أطباق الشيف",
      menu_under_kcal: "أقل من",
      menu_kcal_unit: "سعرة",
      menu_clear_filters: "مسح",
      menu_dietary_label: "النظام الغذائي",
      menu_no_matches: "لا توجد نتائج",
      menu_relax_filters: "جرّب بحثًا آخر أو خفّف المرشحات.",
      menu_clear_all: "مسح جميع المرشحات",
      book_title: "احجز طاولة",
      book_name: "الاسم",
      book_email: "البريد الإلكتروني",
      book_phone: "الهاتف",
      book_guests: "عدد الضيوف",
      book_date: "التاريخ",
      book_time: "الوقت",
      book_requests: "طلبات خاصة",
      book_submit: "تأكيد الحجز",
      footer_rights: "جميع الحقوق محفوظة.",
      lang_label: "اللغة",
      welcome_back: "مرحبًا بعودتك",
      sign_out: "تسجيل الخروج",
    
      /* AUTO-TRANSLATED finder.* (added) */
      "finder.title": "HustleFinder",
      "finder.tagline": "ابحث عن أقرب مكان عام — بيانات حقيقية، في جميع أنحاء أستراليا.",
      "finder.search_placeholder": "ابحث عن أماكن أو شوارع أو ضواحي…",
      "finder.hint": "ابدأ الكتابة أعلاه، أو انقر على فئة، أو استخدم \"بالقرب مني\".",
      "finder.near_me": "بالقرب مني",
      "finder.searching": "جارٍ البحث…",
      "finder.suburb.placeholder": "تصفية حسب الضاحية (مثل Darwin, NT)…",
      "finder.suburb.clear": "مسح",
      "finder.search_in": "البحث في {{cat}}",
      "finder.empty": "لا توجد نتائج — جرب فئة أو مصطلح بحث آخر.",
      "finder.meta.title": "HustleFinder — دليل الضواحي الأسترالي المجاني",
      "finder.meta.desc": "ابحث عن الشركات المحلية والشواحن والمقاهي والمراحيض والمزيد في كل ضاحية أسترالية — مجاني 100%.",
      "finder.footer.credits": "أرصدة البيانات والإسناد",
      "finder.footer.terms": "الشروط",
      "finder.cat.toilet": "مراحيض",
      "finder.cat.fountain": "نوافير الشرب",
      "finder.cat.transport": "النقل",
      "finder.cat.park": "المتنزهات",
      "finder.cat.medical": "طبي",
      "finder.cat.school": "المدارس",
      "finder.cat.library": "المكتبات",
      "finder.cat.petrol": "البنزين",
      "finder.cat.kidzone": "منطقة الأطفال",
      "finder.cat.puzzles": "الألغاز",
      /* AUTO-TRANSLATED rt.* START */
      "rt.brand_name": "Roadtrip Planner",
      "rt.page_title": "مخطط الرحلات البرية للسيارات الكهربائية في أستراليا",
      "rt.from": "من",
      "rt.to": "إلى",
      "rt.from_placeholder": "الضاحية، البلدة أو المدينة (مثل Newcastle NSW)",
      "rt.to_placeholder": "الضاحية، البلدة أو المدينة (مثل Hobart TAS)",
      "rt.stop": "توقف",
      "rt.stop_placeholder": "نقطة توقف وسيطة",
      "rt.add_stop": "إضافة توقف",
      "rt.upgrade_pro": "رقِّ إلى Pro للحصول على ما يصل إلى 20 توقفاً",
      "rt.reverse": "عكس (حساب طريق العودة)",
      "rt.from_current": "من الموقع الحالي",
      "rt.range_per_charge": "المدى لكل شحنة (كم)",
      "rt.connector": "الموصل",
      "rt.connector_any": "الكل",
      "rt.drive_hours_day": "ساعات القيادة/يوم",
      "rt.accommodation": "السكن",
      "rt.accom_any": "الكل (BnB · فندق · كرفان)",
      "rt.accom_bnb": "BnB",
      "rt.accom_hotel": "فندق",
      "rt.accom_caravan": "منتزه كرفانات",
      "rt.min_rating": "الحد الأدنى للتقييم",
      "rt.any_rating": "أي تقييم",
      "rt.only_5star": "5★ فقط",
      "rt.schedule": "الجدول الزمني:",
      "rt.depart_at": "المغادرة في",
      "rt.arrive_by": "الوصول بحلول",
      "rt.hotel_checkin": "تسجيل دخول الفندق",
      "rt.hotel_checkout": "تسجيل خروج الفندق",
      "rt.overnight_hint": "المراحل التي تتضمن مبيت لن تُجدول خارج هذه الساعات.",
      "rt.whos_in_car": "من في السيارة؟",
      "rt.adults": "بالغون",
      "rt.kids": "أطفال",
      "rt.ages": "الأعمار",
      "rt.show_attractions": "عرض المعالم السياحية",
      "rt.show_kids": "عرض أماكن مناسبة للأطفال",
      "rt.overnight_stays": "إقامات المبيت",
      "rt.fast_chargers_only": "شواحن سريعة فقط",
      "rt.hotel_must_charger": "يجب توفر شاحن في الفندق",
      "rt.pet_only": "إقامات تسمح بالحيوانات الأليفة فقط",
      "rt.safety": "الأمان",
      "rt.safety_90": "90% (أرض مسطحة)",
      "rt.safety_85": "85% (افتراضي)",
      "rt.safety_80": "80% (تلال / سحب)",
      "rt.safety_70": "70% (حمولة ثقيلة)",
      "rt.save_trip": "حفظ الرحلة",
      "rt.plan_my_trip": "خطط لرحلتي",
      "rt.refresh_trip": "تحديث الرحلة",
      "rt.planning": "جاري التخطيط…",
      "rt.resume_title": "استئناف الرحلة من هنا",
      "rt.resume_body": "لقد حددنا موقعك الحالي. أدخل المدى المتبقي الموضح في لوحة القيادة لنخطط للمرحلة التالية بناءً على ما لديك فعلياً — وليس شحنة كاملة.",
      "rt.remaining_range": "المدى المتبقي (كم)",
      "rt.cancel": "إلغاء",
      "rt.replan_from_here": "إعادة التخطيط من هنا",
      "rt.save_modal_title": "تسمية هذه الرحلة",
      "rt.save_modal_body": "توجد الرحلات المحفوظة في ملفك الشخصي — يمكنك فتحها في أي وقت والضغط على إعادة التخطيط.",
      "rt.save_name_placeholder": "مثال: Brisbane → Sydney عبر Byron",
      "rt.your_saved_trips": "رحلاتك المحفوظة",
      "rt.custom_location": "موقع مخصص",
      "rt.trip_summary": "ملخص الرحلة",
      "rt.itinerary": "خط السير",
      "rt.no_stops_needed": "لا داعي للتوقف — الرحلة كاملة تقع ضمن مدى سيارتك.",
      "rt.chargers_in_corridor": "شواحن في المسار",
      "rt.planned_stops": "توقفات مخططة",
      "rt.overnight_stop": "توقف مبيت",
      "rt.overnight_stops": "توقفات مبيت",
      "rt.suggested_detours": "انحرافات مقترحة — حدد للإضافة",
      "rt.replan_with_detours": "إعادة التخطيط مع الانحرافات",
      "rt.attractions_label": "معلم سياحي",
      "rt.kids_label": "أطفال",
      "rt.km_from_start": "كم من البداية",
      "rt.open_driving": "فتح وضع القيادة",
      "rt.big_font_hint": "خط كبير، مناسب للحامل. يعمل مع خاصية عكس المتصفح لـ CarPlay/Android Auto.",
      "rt.check_price_book": "تحقق من السعر واحجز على Trivago ↗",
      "rt.visit_site": "زيارة الموقع ↗",
      "rt.charger_onsite": "شاحن في الموقع",
      "rt.leg_exceeds_range": "هذه المرحلة تتجاوز المدى المحدد — سينفد الشحن قبل الوصول لهذا الشاحن. قلل المدى المفترض أو أضف شحنة وسيطة.",
      "rt.add_waypoint_replan": "إضافة كنقطة طريق وإعادة التخطيط",
      /* AUTO-TRANSLATED rt.* END */
                      /* AUTO-TRANSLATED hm.* START */
      "hm.nav.hustlemeet": "HustleMeet",
      "hm.nav.circles": "Circles",
      "hm.nav.matchmaker": "Matchmaker",
      "hm.nav.meet": "التقِ",
      "hm.nav.overview": "HustleMeetups",
      "hm.overview.page_title": "HustleMeetups — ثلاث طرق دافئة لتجد ناسك.",
      "hm.overview.kicker": "جزء من HustleSuite",
      "hm.overview.hero_title": "ثلاث طرق دافئة لتجد ناسك.",
      "hm.overview.hero_title_accent": "ناسك",
      "hm.overview.hero_sub": "HustleMeetups هي ألطف وسيلة في Australia لبناء صداقات حقيقية — اختر الأجواء التي تناسبك. سواء كنت ترغب في مجموعة جري أسبوعية، أو عشاء شهري مع شلتك الحالية، أو طاولة منسقة بالذكاء الاصطناعي من وجوه جديدة، فهناك دائماً مكان لك.",
      "hm.overview.tile_meet_kicker": "HustleMeet",
      "hm.overview.tile_meet_title": "جد ناسك. افعل ما تحب.",
      "hm.overview.tile_meet_body": "منصة Australia للمجموعات القائمة على الاهتمامات. عداؤون، مبرمجون، مستثمرون، هواة مشي، راقصون، نوادي إفطار، وفرق قراءة — مئات المجموعات، وفعاليات حقيقية كل أسبوع. احضر مرة واحدة، أو كل أسبوع.",
      "hm.overview.tile_meet_cta": "استكشف المجموعات",
      "hm.overview.tile_circles_kicker": "Circles",
      "hm.overview.tile_circles_title": "شلتك. طاولة جديدة في كل مرة.",
      "hm.overview.tile_circles_body": "أحضر مجموعتك المعتادة من الأصدقاء. نحن نحجز مطعماً جديداً، ونخطط للتريفيا، ونوزع الأوراق. أنت فقط تحضر. هي الطريقة الأفضل لإبقاء الشلة قريبة — دون أن يضطر أحد لتنظيم ذلك.",
      "hm.overview.tile_circles_cta": "ابدأ Circle",
      "hm.overview.tile_matchmaker_kicker": "Matchmaker",
      "hm.overview.tile_matchmaker_title": "الذكاء الاصطناعي يختار الطاولة. أنت فقط تحضر.",
      "hm.overview.tile_matchmaker_body": "خض اختباراً لمدة 10 دقائق. خلال ليلة وضحاها، نجمعك على طاولة مع غرباء ستشعر أنهم أصدقاء قدامى بحلول وقت الحلوى. طاولة مختلفة في كل مرة.",
      "hm.overview.tile_matchmaker_cta": "خض الاختبار",
      "hm.overview.trust_kicker": "لماذا تنجح التجربة",
      "hm.overview.trust_title": "الثقة أولاً. الطاولة ثانياً.",
      "hm.overview.trust_1_title": "أعضاء موثقون",
      "hm.overview.trust_1_body": "تبدأ كل مجموعة بالتحقق من الهوية الحقيقية + درجة الموثوقية. لا وجود للحسابات الوهمية.",
      "hm.overview.trust_2_title": "منظمون حقيقيون",
      "hm.overview.trust_2_body": "تتم قيادة كل مجموعة من قبل منظم معتمد يدير الأجواء، ويحدد الإيقاع، ويهتم بالحضور.",
      "hm.overview.trust_3_title": "الصداقة أولاً",
      "hm.overview.trust_3_body": "كل شيء هنا مصمم للصداقات الحقيقية، وليس للمواعدة. أبداً.",
      "hm.overview.trust_4_title": "رقابة صارمة",
      "hm.overview.trust_4_body": "بلاغ واحد، مراجعة واحدة، قرار واحد. يتم استبعاد الأشخاص المسيئين في نفس الليلة.",
      "hm.overview.footer_line": "جزء من HustleSuite — مملوك ومستضاف في Australia.",
      "hm.landing.page_title": "HustleMeet · جد ناسك. افعل ما تحب.",
      "hm.landing.kicker": "شبكة الاهتمامات في Australia",
      "hm.landing.hero_title": "جد ناسك. افعل ما تحب.",
      "hm.landing.hero_title_accent": "ناسك",
      "hm.landing.hero_sub": "HustleMeet هو موطن المجموعات القائمة على الاهتمامات في Australia. مبرمجو برمجيات يحلون المشاكل خلال الغداء. عداؤو المسافات الطويلة في السادسة صباحاً. مستثمرون يجتمعون شهرياً. مجموعات مشي، فرق رقص، نوادي إفطار، وليالي ألعاب لوحية. اختر مجموعة، احجز الفعالية القادمة، واحضر.",
      "hm.landing.cta_explore_circles": "تصفح المجموعات",
      "hm.landing.cta_try_matchmaker": "ابدأ مجموعتك الخاصة",
      "hm.landing.trust_verified": "أعضاء موثقون",
      "hm.landing.trust_venues": "منظمو فعاليات معتمدون",
      "hm.landing.trust_friendship": "الصداقة أولاً — لا للمواعدة",
      "hm.landing.trust_moderation": "رقابة صارمة",
      "hm.landing.subbrand_circles_kicker": "Circles",
      "hm.landing.subbrand_circles_title": "لديك شلتك بالفعل؟ امنحهم موطناً دائماً.",
      "hm.landing.subbrand_circles_body": "Circles مخصص لمجموعة الأصدقاء الذين يجتمعون بالفعل — نادي كتاب، ليلة بوكر، فريق جري، أو أصدقاء العمل. نحن نتولى الحجز، والتذكيرات، والقائمة. أنت فقط تحضر.",
      "hm.landing.subbrand_circles_b1": "أماكن متغيرة، قائمة حضور واحدة",
      "hm.landing.subbrand_circles_b2": "تذكيرات تلقائية + تأكيد حضور سهل",
      "hm.landing.subbrand_circles_b3": "تريفيا، أوراق لعب، وليالٍ ذات طابع خاص جاهزة لك",
      "hm.landing.subbrand_circles_cta": "ابدأ Circle",
      "hm.landing.subbrand_matchmaker_kicker": "Matchmaker",
      "hm.landing.subbrand_matchmaker_title": "الذكاء الاصطناعي يختار الطاولة. أنت فقط تحضر.",
      "hm.landing.subbrand_matchmaker_body": "اختبار واحد لمدة 10 دقائق — الشخصية، القيم، والتوفر. يقوم Matchmaker الخاص بنا بجمعك مع غرباء ستشعر أنهم أصدقاء قدامى بحلول وقت الحلوى. طاولة مختلفة في كل مرة.",
      "hm.landing.subbrand_matchmaker_b1": "تقييم الشخصية والقيم وفق مقياس Big-5",
      "hm.landing.subbrand_matchmaker_b2": "دعوات فورية عندما تصل نسبة التوافق إلى 0.70 أو أكثر",
      "hm.landing.subbrand_matchmaker_b3": "يتعلم من كل عشاء تخوضه",
      "hm.landing.subbrand_matchmaker_cta": "خض الاختبار",
      "hm.landing.how_kicker": "كيف يعمل HustleMeet",
      "hm.landing.how_title": "أربع خطوات. جد مجموعتك. احضر.",
      "hm.landing.how_step1_ttl": "اختر اهتماماتك",
      "hm.landing.how_step1_sub": "الجري، البرمجة، الاستثمار، الطبخ، الموسيقى، المشي — أخبرنا بما تحب وأين تعيش.",
      "hm.landing.how_step2_ttl": "تصفح المجموعات القريبة منك",
      "hm.landing.how_step2_sub": "تنشر كل مجموعة منظمها، وإيقاع لقاءاتها، وفعاليتها القادمة. افهم الأجواء قبل الالتزام.",
      "hm.landing.how_step3_ttl": "أكد حضورك للفعالية القادمة",
      "hm.landing.how_step3_sub": "نقرة واحدة، دعوة تقويم واحدة، وتذكير واضح في اليوم السابق. بعض الفعاليات مجانية، وبعضها مدفوع — السعر معلن بوضوح.",
      "hm.landing.how_step4_ttl": "احضر وتواصل",
      "hm.landing.how_step4_sub": "قابل المنظم، قابل الفريق، وافعل ما تحب. عد في الأسبوع التالي إذا شعرت بالانسجام.",
      "hm.landing.stats_kicker": "طريقة Australia الجديدة للتعارف",
      "hm.landing.stats_title": "السبب الذي يجعل الناس يعودون دائماً.",
      "hm.landing.stats_92_sub": "شعروا بانسجام مع شخص واحد على الأقل في الفعالية",
      "hm.landing.stats_68_sub": "أكدوا حضورهم للفعالية القادمة خلال أسبوع",
      "hm.landing.stats_85_sub": "بقوا على تواصل مع شخص ما من المجموعة",
      "hm.landing.why_kicker": "لماذا ننجح",
      "hm.landing.why_title": "اهتمامات حقيقية. منظمون حقيقيون. فعاليات حقيقية.",
      "hm.landing.why_1_ttl": "الاهتمام أولاً، وليس الموقع فقط",
      "hm.landing.why_1_body": "الأشخاص في مجموعتك يهتمون فعلاً بالأشياء التي تهمك — وهذا هو الطريق المختصر لصداقة حقيقية.",
      "hm.landing.why_2_ttl": "منظمو فعاليات حقيقيون",
      "hm.landing.why_2_body": "لكل مجموعة منظم معتمد يدير الأجواء، ويحدد الإيقاع، ويتدخل إذا حدث خطأ ما. ليس مجرد مدير مجموعة على Facebook، بل شخص حقيقي.",
      "hm.landing.why_3_ttl": "الاستمرارية تهم",
      "hm.landing.why_3_body": "فعاليات جديدة كل أسبوع. شيء تتطلع إليه. إيقاع يحول الغرباء إلى شلتك المفضلة لليالي الثلاثاء.",
      "hm.landing.why_4_ttl": "آمن ومحمي",
      "hm.landing.why_4_body": "أعضاء موثقون، أماكن عامة، ولا تهاون مع عدم الاحترام. فريق الدعم لدينا متاح قبل وخلال وبعد كل فعالية.",
      "hm.landing.why_5_ttl": "الصداقة أولاً",
      "hm.landing.why_5_body": "هذه ليست مواعدة، بل مجتمع. اهتمامات حقيقية، أشخاص حقيقيون، وبدون عمليات التمرير العشوائية.",
      "hm.landing.why_6_ttl": "صغيرة بما يكفي لتكون مؤثرة",
      "hm.landing.why_6_body": "معظم المجموعات تحدد الحضور بـ 4-15 مقعداً. كبيرة بما يكفي لإثارة محادثات حقيقية، وصغيرة بما يكفي لكي لا يشعر أحد بالتهميش.",
      "hm.landing.cities_kicker": "أين نتواجد",
      "hm.landing.cities_title": "مجموعات تلتقي في أنحاء Australia و NZ.",
      "hm.landing.cities_sub_zero": "المجموعات الأولى تفتتح في أكثر من 15 مدينة — انضم مبكراً وساهم في تشكيل المجموعة.",
      "hm.landing.cities_sub_some": "المجموعات نشطة في أكثر من 15 منطقة حضرية. هناك شيء ما في مدينتك هذا الأسبوع.",
      "hm.landing.cities_sub_one": "{{count}} مجموعة نشطة · فعاليات جديدة كل أسبوع · نضيف مدناً باستمرار.",
      "hm.landing.cities_sub_many": "{{count}} مجموعات نشطة · فعاليات جديدة كل أسبوع · نضيف مدناً باستمرار.",
      "hm.landing.forme_kicker": "هذا المكان لك إذا كنت…",
      "hm.landing.forme_title": "تريد اهتمامات حقيقية. أشخاصاً حقيقيين. فعاليات حقيقية.",
      "hm.landing.forme_1": "سئمت من إرهاق تطبيقات التمرير وتريد مقابلة أشخاص حول ما تحب فعله حقاً.",
      "hm.landing.forme_2": "انتقلت إلى مدينة جديدة وتحتاج إلى شلة دافئة، وليس مجرد مجموعة دردشة أخرى.",
      "hm.landing.forme_3": "تقدر العمق أكثر من الانتشار — مجموعة واحدة رائعة أفضل من خمسين متابعة.",
      "hm.landing.forme_4": "تؤمن بأن الاهتمامات المشتركة + ساعتين محددتين في الحياة الواقعية هما السر الحقيقي.",
      "hm.landing.notme_kicker": "هذا المكان ليس لك إذا كنت…",
      "hm.landing.notme_title": "HustleMeet لا يناسب الجميع.",
      "hm.landing.notme_1": "لا يمكنك الالتزام بالحضور — فالمنظمون والأعضاء يعتمدون على تأكيد حضورك.",
      "hm.landing.notme_2": "تبحث عن خدمة مواعدة — الصداقات تأتي أولاً هنا.",
      "hm.landing.notme_3": "تريد النشر دون مقابلة أحد — هذا النظام لا يعمل إلا وجهاً لوجه.",
      "hm.landing.notme_4": "تتوقع مزايا مجانية لا تنتهي — بعض الفعاليات لها تكلفة، والسعر دائماً معلن مسبقاً.",
      "hm.landing.safety_kicker": "التزامنا بالأمان",
      "hm.landing.safety_title": "دافئ، ولكن ليس مهملاً.",
      "hm.landing.safety_1_ttl": "أماكن عامة",
      "hm.landing.safety_1_body": "تقام كل فعالية في مكان عام مجهز بطاقم عمل — مقهى، مطعم، منتزه، صالة ألعاب رياضية أو استوديو. لا تقام في المنازل أبداً إلا إذا وافق كل الحاضرين.",
      "hm.landing.safety_2_ttl": "معايير المجتمع",
      "hm.landing.safety_2_body": "لا تهاون مع قلة الاحترام أو المضايقة أو عدم الأمانة. يتم استبعاد الأعضاء الذين يخالفون ذلك فوراً.",
      "hm.landing.safety_3_ttl": "حماية الخصوصية",
      "hm.landing.safety_3_body": "لا تتم مشاركة تفاصيلك علناً أبداً. أنت تتحكم فيمن تبقى على تواصل معه بعد كل فعالية.",
      "hm.landing.safety_4_ttl": "دعم متاح",
      "hm.landing.safety_4_body": "فريقنا متاح قبل وخلال وبعد كل فعالية إذا كان لديك أي أسئلة أو مخاوف.",
      "hm.landing.bottom_title": "مجموعتك المفضلة القادمة على بعد نقرة واحدة.",
      "hm.landing.bottom_sub": "اختر طريقك — انضم إلى مجموعة موجودة، أحضر شلتك إلى Circle، أو دع Matchmaker يجمعك على طاولة تناسبك بالفعل.",
      "hm.landing.bottom_cta_circles": "ابدأ Circle",
      "hm.landing.bottom_cta_mm": "خض اختبار Matchmaker",
      "hm.circles.page_title": "HustleMeet Circles · شلتك، مكان جديد في كل مرة",
      "hm.circles.kicker": "HustleMeet · Circles",
      "hm.circles.hero_title": "شلتك. طاولة جديدة في كل مرة.",
      "hm.circles.hero_title_accent": "شلتك",
      "hm.circles.hero_sub": "لديك شلتك بالفعل؟ Circles هي أسهل طريقة لإبقائهم بالقرب. نحن نحجز المكان، ونخطط للتريفيا، ونوزع الأوراق. أنت فقط تحضر — نفس الشلة، ليلة جديدة، ومكان جديد.",
      "hm.circles.cta_find": "ابدأ Circle",
      "hm.circles.cta_host_hero": "انضم إلى Circle مفتوحة",
      "hm.circles.trust_venue": "أماكن معتمدة متغيرة",
      "hm.circles.trust_party": "4-12 صديقاً لكل Circle",
      "hm.circles.trust_verified": "أعضاء موثقون",
      "hm.circles.trust_friendship": "صداقات حقيقية، تبقى قريبة",
      "hm.circles.how_kicker": "كيف يعمل",
      "hm.circles.how_title": "أربع خطوات. لتبقى شلتك شلة.",
      "hm.circles.how_step1_ttl": "ابدأ Circle الخاصة بك",
      "hm.circles.how_step1_sub": "سمِّها، ادعُ أصدقاءك (حتى 12 شخصاً)، واختر إيقاعاً — أسبوعياً، كل أسبوعين أو شهرياً.",
      "hm.circles.how_step2_ttl": "نحن نختار المكان",
      "hm.circles.how_step2_sub": "في كل فعالية، نقترح مكاناً جديداً — مطعم، ليلة تريفيا، غرفة ألعاب ورق، أو ركن مشروبات. أو يمكنك تثبيت مكانك المفضل.",
      "hm.circles.how_step3_ttl": "الجميع يؤكد الحضور بنقرة واحدة",
      "hm.circles.how_step3_sub": "تذكيرات تلقائية، تقسيم التكلفة، ومقاعد مسبقة الدفع حتى لا يفسد الغياب الخطة. الحساب ينتهي قبل وصول الطبق الأول.",
      "hm.circles.how_step4_ttl": "حافظ على الإيقاع",
      "hm.circles.how_step4_sub": "ليلة واحدة في الجدول لن تضطر لتنظيمها مرة أخرى. الروابط تقوى عبر الشهور، وليس الدقائق.",
      "hm.circles.cadence_weekly_ttl": "أسبوعي",
      "hm.circles.cadence_weekly_sub": "كل أسبوع",
      "hm.circles.cadence_weekly_body": "للشلل التي تريد طقوساً لليالي الثلاثاء — نفس الوجوه، ومكان جديد في كل مرة.",
      "hm.circles.cadence_fortnight_ttl": "كل أسبوعين",
      "hm.circles.cadence_fortnight_sub": "كل أسبوعين",
      "hm.circles.cadence_fortnight_body": "التوازن المثالي — لقاء منتظم دون أن يكون مرهقاً لجداول المواعيد المزدحمة.",
      "hm.circles.cadence_monthly_ttl": "شهري",
      "hm.circles.cadence_monthly_sub": "كل شهر",
      "hm.circles.cadence_monthly_body": "Circles للمناسبات الخاصة — حفلات شواء الأحد، ليالي التريفيا الكبرى، التذوق الفاخر، أو البوكر.",
      "hm.circles.feast_kicker": "اختر ليلتك",
      "hm.circles.feast_title": "ثلاث طرق لتمضيتها",
      "hm.circles.feast_sub": "تختار كل Circle كيف تدار الليلة. بدّلوا بينها، أو ثبتوا المفضل لشلتكم.",
      "hm.circles.feast_surprise_ttl": "تجربة مطعم جديد",
      "hm.circles.feast_surprise_body": "نحن نرشح ثلاثة أماكن قريبة منكم، الجميع يصوت، والمطبخ يستعد لطاولة كاملة. التكلفة مقسمة، والمقاعد مدفوعة مسبقاً.",
      "hm.circles.feast_preselect_ttl": "تريفيا / أوراق لعب / ألعاب",
      "hm.circles.feast_preselect_body": "احجز ليلة تريفيا، غرفة بوكر، مقهى ألعاب لوحية أو صالة بولينج. نحن نتولى المكان، ورسوم الاشتراك، والتذكيرات.",
      "hm.circles.feast_per_event_ttl": "ليلة ذات طابع خاص لمرة واحدة",
      "hm.circles.feast_per_event_body": "تذوق مشروبات، مائدة الشيف، دروس كوكتيل، أو غرف الهروب. شيء خاص بين الحين والآخر — منظم بالكامل.",
      "hm.circles.live_kicker": "الآن",
      "hm.circles.live_title": "Circles يمكنك الانضمام إليها",
      "hm.circles.live_host_link": "ابدأ مجموعتك الخاصة",
      "hm.circles.live_loading": "جارٍ التحميل…",
      "hm.circles.live_empty_title": "كن صاحب أول Circle في مدينتك.",
      "hm.circles.live_empty_sub": "ابدأ واحدة مع ثلاثة أصدقاء وسنساعدك في دعوة البقية — بالإضافة إلى منحكم أول ثلاثة أماكن على حسابنا.",
      "hm.circles.live_empty_cta": "ابدأ Circle",
      "hm.circles.host_kicker": "لقادة الـ Circles",
      "hm.circles.host_title": "أنت تحضر الشلة. ونحن نتولى المهام الإدارية.",
      "hm.circles.host_body": "إذا كنت أنت الصديق الذي ينظم الليالي دائماً، فإن Circles توفر عليك الساعات. تذكيرات تلقائية، تأكيدات حضور مدفوعة مسبقاً، ترشيحات أماكن، تقسيم التكاليف، إدارة قائمة الانتظار، ومحفظة استرداد نقدي في كل ليلة خروج. أنت فقط تحضر كعضو عادي.",
      "hm.circles.host_perk_1": "ترشيحات تلقائية للأماكن — لا داعي للبحث في Google مجدداً",
      "hm.circles.host_perk_2": "مقاعد مدفوعة مسبقاً — لا حالات غياب مفاجئة",
      "hm.circles.host_perk_3": "تقسيم التكلفة يتم قبل الحلوى",
      "hm.circles.host_perk_4": "تذكيرات + دعوات تقويم تعمل آلياً",
      "hm.circles.host_perk_5": "قائمة انتظار مع ترقية تلقائية إذا ألغى صديق حجزه",
      "hm.circles.host_perk_6": "محفظة استرداد نقدي في كل ليلة خروج",
      "hm.circles.host_cta": "ابدأ Circle الآن",
      "hm.circles.why_kicker": "لماذا تنجح Circles",
      "hm.circles.why_title": "صداقات الكبار تحتاج إلى تنظيم",
      "hm.circles.why_1_ttl": "نفس الشلة، ثقة أعمق",
      "hm.circles.why_1_body": "الحياة تفرق الأصدقاء. Circles هي الإيقاع الثابت الذي يبقي الجميع قريبين — نفس الأصدقاء الـ 4-12، يلتقون في نفس الليلة الأسبوعية، مهما حدث.",
      "hm.circles.why_2_ttl": "لا تعب في التنظيم",
      "hm.circles.why_2_body": "لا أحد يضطر ليكون رئيس مجموعة WhatsApp. نحن نختار الأماكن، نرسل التذكيرات، نجمع المدفوعات، وننهي كل التفاصيل.",
      "hm.circles.why_3_ttl": "مكان جديد، ذكرى جديدة",
      "hm.circles.why_3_body": "تنقل بين أفضل المطاعم الصغيرة في Australia، وغرف التريفيا، ومقاهي الألعاب. وجوه مألوفة، وخلفيات جديدة — المزيج المثالي بين الروتين والتجديد.",
      "hm.circles.bottom_title": "الليلة التي تتطلع إليها شلتك، دون أن يضطر أحد للتخطيط لها.",
      "hm.circles.bottom_sub": "Circle واحدة. نفس الأصدقاء. مكان جديد في كل مرة.",
      "hm.circles.bottom_cta_find": "ابدأ Circle",
      "hm.circles.bottom_cta_mm": "قابل أشخاصاً جدداً مع Matchmaker →",
      "hm.circles.card_host_pill": "Circle",
      "hm.matchmaker.page_title": "HustleMeet Matchmaker · الذكاء الاصطناعي يختار الطاولة المناسبة",
      "hm.matchmaker.kicker": "HustleMeet · Matchmaker",
      "hm.matchmaker.hero_title": "الذكاء الاصطناعي يختار الطاولة. أنت فقط تحضر.",
      "hm.matchmaker.hero_title_accent": "أنت فقط تحضر.",
      "hm.matchmaker.hero_sub": "أجب على اختبار قصير للشخصية والقيم. سيقوم Matchmaker بجمعك مع أربعة إلى سبعة غرباء سيشعرون وكأنهم أصدقاء قدامى بحلول وقت الحلوى. لا تمرير، لا محادثات قصيرة مملة. فقط طاولة مشتركة، جاهزة عندما تكون أنت مستعداً.",
      "hm.matchmaker.cta_quiz": "خض اختبار المطابقة",
      "hm.matchmaker.cta_see_circles": "شاهد Circles بدلاً من ذلك →",
      "hm.matchmaker.trust_big5": "تقييم الشخصية وفق Big-5",
      "hm.matchmaker.trust_seats": "4-8 مقاعد لكل طاولة",
      "hm.matchmaker.trust_lock": "لا تمرير · لا صور",
      "hm.matchmaker.trust_verified": "أعضاء موثقون فقط",
      "hm.matchmaker.how_kicker": "كيف يعمل",
      "hm.matchmaker.how_title": "أربع خطوات لطاولة تناسبك بالفعل.",
      "hm.matchmaker.how_step1_ttl": "خض الاختبار",
      "hm.matchmaker.how_step1_sub": "استبيان لمدة عشر دقائق — الشخصية، القيم، النظام الغذائي، والتوفر. تفعله مرة واحدة فقط.",
      "hm.matchmaker.how_step2_ttl": "مطابقة ليلية",
      "hm.matchmaker.how_step2_sub": "يقرأ ذكاؤنا الاصطناعي ملفك الشخصي وقائمة العشاء المباشرة في Australia. عندما تصل درجة توافقك إلى 0.70 أو أكثر، يرسل دعوة شخصية.",
      "hm.matchmaker.how_step3_ttl": "القبول أو الرفض",
      "hm.matchmaker.how_step3_sub": "تصلك رسالة دعوة واحدة واضحة تحتوي على المكان، وطابع الطاولة، والاهتمامات الثلاثة التي تشاركها مع المجموعة.",
      "hm.matchmaker.how_step4_ttl": "يتعلم منك",
      "hm.matchmaker.how_step4_sub": "كل استطلاع بعد العشاء يحسن النموذج. مطابقتك القادمة تصبح أذكى كلما تناولت العشاء أكثر.",
      "hm.matchmaker.science_kicker": "لماذا ينجح",
      "hm.matchmaker.science_title": "Matchmaker لا يخمن — بل يقيس.",
      "hm.matchmaker.science_sub": "كل مطابقة هي مزيج مرجح من أربعة عوامل. عندما يتجاوز متوسط الطاولة بالكامل 0.70، نرسل الدعوات. أي شيء أقل من ذلك يبقى في قائمة الانتظار.",
      "hm.matchmaker.science_1_ttl": "الشخصية (35%)",
      "hm.matchmaker.science_1_body": "تشابه جيب التمام عبر متجه الشخصية Big-5 — الانفتاح، والضمير، والانبساط، والتوافق، والاستقرار العاطفي.",
      "hm.matchmaker.science_2_ttl": "القيم (25%)",
      "hm.matchmaker.science_2_body": "التداخل في القيم التي تجعل محادثة العشاء ممتعة فعلاً — العائلة، الفضول، النمو، الخدمة، والمغامرة.",
      "hm.matchmaker.science_3_ttl": "الاهتمامات (25%)",
      "hm.matchmaker.science_3_body": "تداخل جاكارد في اهتماماتك المحددة — التنزه في Six Foot Track، نادي السينما في Sydney، مشهد القهوة في Melbourne، أو كتابك الثالث لهذا العام.",
      "hm.matchmaker.science_4_ttl": "التوفر (15%)",
      "hm.matchmaker.science_4_body": "الليلة التي يمكنك الحضور فيها حقاً. لن تصلك دعوة ليوم الثلاثاء الساعة 7 مساءً إذا كان تقويمك يقول \"نادي رياضي كل ثلاثاء\".",
      "hm.matchmaker.safety_kicker": "الأمان في التصميم",
      "hm.matchmaker.safety_title": "Matchmaker حذر وهادئ.",
      "hm.matchmaker.safety_1_ttl": "حد الثقة",
      "hm.matchmaker.safety_1_body": "أقل من درجة 0.70 للمجموعة، لا تخرج أي دعوة. المطابقة المهزوزة أسوأ من عدم وجود عشاء.",
      "hm.matchmaker.safety_2_ttl": "قائمة الحظر المتبادل",
      "hm.matchmaker.safety_2_body": "إذا أشار عضوان سراً بـ \"لا\" بعد العشاء، فلن نجمعهما معاً مرة أخرى أبداً.",
      "hm.matchmaker.safety_3_ttl": "مفتاح الإيقاف التلقائي",
      "hm.matchmaker.safety_3_body": "إذا قام 20% أو أكثر من الاستطلاعات الأخيرة بتقييم طاولة بـ 2/5 أو أقل، تتوقف الدعوات لمدة 24 ساعة بينما يراجعها المختص.",
      "hm.matchmaker.safety_4_ttl": "معايير المجتمع",
      "hm.matchmaker.safety_4_body": "يوافق كل عضو على مدونة سلوك واضحة. نستبعد الأشخاص الذين يخالفونها — دون استئناف.",
      "hm.matchmaker.safety_5_ttl": "أماكن عامة فقط",
      "hm.matchmaker.safety_5_body": "يقام كل عشاء Matchmaker في مطعم حقيقي مع طاقم عمل. لا يتم أبداً في منزل أحد.",
      "hm.matchmaker.safety_6_ttl": "ملاحظات ما بعد الفعالية",
      "hm.matchmaker.safety_6_body": "استطلاعك الخاص يشكل المطابقات المستقبلية، ويمكن أن يؤدي إلى مراجعة المكان أو أحد الأقران.",
      "hm.matchmaker.loop_kicker": "حلقة التعلم",
      "hm.matchmaker.loop_title": "كل عشاء يجعل عشاءك القادم أفضل.",
      "hm.matchmaker.loop_body": "Matchmaker ليس ساكناً. بعد كل فعالية، ترسل استطلاعاً خاصاً قصيراً — كيف كان شعورك تجاه المحادثة، من تود تناول العشاء معه مرة أخرى، وهل كان المكان جيداً. هذه الإشارات تغذي النموذج وتعيد تشكيل كل مطابقة مستقبلية.",
      "hm.matchmaker.loop_1": "أصوات الأقران بـ \"تناول العشاء مرة أخرى\" تقوي روابطكم",
      "hm.matchmaker.loop_2": "أصوات الرفض المتبادل تستبعد الأزواج بصمت",
      "hm.matchmaker.loop_3": "تقييمات المكان والمحادثة تضبط مزيج المقاعد",
      "hm.matchmaker.loop_4": "درجة الموثوقية تحمي الأعضاء من تكرار حالات عدم الحضور",
      "hm.matchmaker.loop_5": "انحياز التعلم يعزز المطابقات الجيدة شهراً بعد شهر",
      "hm.matchmaker.loop_6": "أنت المتحكم — يمكنك الانسحاب أو إعادة ضبط ملفك الشخصي في أي وقت",
      "hm.matchmaker.vs_kicker": "أيهما يناسبني؟",
      "hm.matchmaker.vs_title": "Matchmaker أم Circles؟",
      "hm.matchmaker.vs_mm_ttl": "اخترني إذا كنت أريد التنوع.",
      "hm.matchmaker.vs_mm_1": "طاولة مختلفة، مدينة مختلفة، ومطبخ مختلف في كل مرة",
      "hm.matchmaker.vs_mm_2": "مجموعة مختارة بالذكاء الاصطناعي من 4-8 غرباء متوافقين",
      "hm.matchmaker.vs_mm_3": "إيقاع أسبوعي، دون التزام بالتنظيم",
      "hm.matchmaker.vs_mm_4": "رائع إذا كنت جديداً في المدينة أو مسافراً",
      "hm.matchmaker.vs_mm_cta": "خض اختبار المطابقة",
      "hm.matchmaker.vs_circles_ttl": "اخترني إذا كنت أريد الاستقرار.",
      "hm.matchmaker.vs_circles_1": "نفس المنظم · نفس المكان · نفس الليلة الأسبوعية",
      "hm.matchmaker.vs_circles_2": "مجموعة تتحول إلى تقليد يوم الأربعاء الخاص بك",
      "hm.matchmaker.vs_circles_3": "منسقة بشرياً من قبل منظم خبير",
      "hm.matchmaker.vs_circles_4": "رائع إذا كنت قد انتقلت حديثاً وتريد بناء جذور",
      "hm.matchmaker.vs_circles_cta": "تصفح Circles",
      "hm.matchmaker.bottom_title": "اختبار واحد. وكل عشاء بعده سيكون مناسباً لك.",
      "hm.matchmaker.bottom_sub": "ستتم مطابقتك خلال أسبوعين. إنه أسهل قرار ستتخذه هذا الشهر.",
      "hm.matchmaker.bottom_cta": "خض اختبار المطابقة",
      "hm.matchmaker.bottom_note": "لا تتطلب بطاقة ائتمان · 10 دقائق · إلغاء المطابقة في أي وقت",
      /* AUTO-TRANSLATED hm.* END */
    },
  },
  "zh-Hant": {
    translation: {
      nav_menu: "菜單",
      nav_book: "預訂座位",
      nav_reviews: "評價",
      nav_about: "關於我們",
      nav_account: "我的帳戶",
      nav_signin: "登入",
      cart_open: "打開購物車",
      cart_title: "您的訂單",
      cart_empty: "購物車是空的",
      cart_subtotal: "小計",
      cart_continue: "繼續",
      cart_pickup_date: "取餐日期",
      cart_pickup_time: "取餐時間",
      cart_pay_on_pickup: "取餐時付款",
      cart_pay_now: "立即付款",
      cart_place_order: "提交訂單",
      add_to_order: "加入訂單",
      menu_search_ph: "搜尋菜單…",
      menu_chef_specials: "主廚推薦",
      menu_under_kcal: "低於",
      menu_kcal_unit: "千卡",
      menu_clear_filters: "清除",
      menu_dietary_label: "飲食",
      menu_no_matches: "沒有匹配項",
      menu_relax_filters: "請嘗試其他搜尋或放寬篩選條件。",
      menu_clear_all: "清除所有篩選",
      book_title: "預訂座位",
      book_name: "姓名",
      book_email: "電子郵箱",
      book_phone: "電話",
      book_guests: "用餐人數",
      book_date: "日期",
      book_time: "時間",
      book_requests: "特殊要求",
      book_submit: "確認預訂",
      footer_rights: "版權所有。",
      lang_label: "語言",
      welcome_back: "歡迎回來",
      sign_out: "登出",
    },
  },
  pa: {
    translation: {
      nav_menu: "ਮੀਨੂ",
      nav_book: "ਟੇਬਲ ਬੁੱਕ ਕਰੋ",
      nav_reviews: "ਸਮੀਖਿਆਵਾਂ",
      nav_about: "ਸਾਡੇ ਬਾਰੇ",
      nav_account: "ਮੇਰਾ ਖਾਤਾ",
      nav_signin: "ਸਾਈਨ ਇਨ",
      cart_open: "ਕਾਰਟ ਖੋਲ੍ਹੋ",
      cart_title: "ਤੁਹਾਡਾ ਆਰਡਰ",
      cart_empty: "ਕਾਰਟ ਖਾਲੀ ਹੈ",
      cart_subtotal: "ਉਪ-ਜੋੜ",
      cart_continue: "ਜਾਰੀ ਰੱਖੋ",
      cart_pickup_date: "ਪਿਕਅੱਪ ਮਿਤੀ",
      cart_pickup_time: "ਪਿਕਅੱਪ ਸਮਾਂ",
      cart_pay_on_pickup: "ਪਿਕਅੱਪ ਵੇਲੇ ਅਦਾਇਗੀ",
      cart_pay_now: "ਹੁਣੇ ਅਦਾਇਗੀ ਕਰੋ",
      cart_place_order: "ਆਰਡਰ ਦਿਓ",
      add_to_order: "ਆਰਡਰ ਵਿੱਚ ਸ਼ਾਮਲ ਕਰੋ",
      menu_search_ph: "ਮੀਨੂ ਖੋਜੋ…",
      menu_chef_specials: "ਸ਼ੈੱਫ ਸਪੈਸ਼ਲ",
      menu_under_kcal: "ਤੋਂ ਘੱਟ",
      menu_kcal_unit: "ਕੇਸੀਏਲ",
      menu_clear_filters: "ਸਾਫ਼ ਕਰੋ",
      menu_dietary_label: "ਖੁਰਾਕ",
      menu_no_matches: "ਕੋਈ ਮਿਲਾਨ ਨਹੀਂ",
      menu_relax_filters: "ਕੋਈ ਹੋਰ ਖੋਜ ਅਜ਼ਮਾਓ ਜਾਂ ਫਿਲਟਰ ਘਟਾਓ।",
      menu_clear_all: "ਸਾਰੇ ਫਿਲਟਰ ਸਾਫ਼ ਕਰੋ",
      book_title: "ਟੇਬਲ ਬੁੱਕ ਕਰੋ",
      book_name: "ਨਾਮ",
      book_email: "ਈਮੇਲ",
      book_phone: "ਫ਼ੋਨ",
      book_guests: "ਮਹਿਮਾਨ",
      book_date: "ਮਿਤੀ",
      book_time: "ਸਮਾਂ",
      book_requests: "ਖਾਸ ਬੇਨਤੀਆਂ",
      book_submit: "ਬੁਕਿੰਗ ਦੀ ਪੁਸ਼ਟੀ ਕਰੋ",
      footer_rights: "ਸਾਰੇ ਹੱਕ ਰਾਖਵੇਂ ਹਨ।",
      lang_label: "ਭਾਸ਼ਾ",
      welcome_back: "ਵਾਪਸੀ ਉੱਤੇ ਜੀ ਆਇਆਂ ਨੂੰ",
      sign_out: "ਸਾਈਨ ਆਉਟ",
    
      /* AUTO-TRANSLATED finder.* (added) */
      "finder.title": "HustleFinder",
      "finder.tagline": "ਨਜ਼ਦੀਕੀ ਜਨਤਕ ਸਥਾਨ ਲੱਭੋ — ਅਸਲ ਡਾਟਾ, ਪੂਰੇ ਆਸਟ੍ਰੇਲੀਆ ਵਿੱਚ।",
      "finder.search_placeholder": "ਸਥਾਨ, ਗਲੀਆਂ ਜਾਂ ਉਪਨਗਰ ਖੋਜੋ…",
      "finder.hint": "ਉੱਪਰ ਟਾਈਪ ਕਰਨਾ ਸ਼ੁਰੂ ਕਰੋ, ਕੋਈ ਸ਼੍ਰੇਣੀ ਚੁਣੋ, ਜਾਂ \"ਮੇਰੇ ਨੇੜੇ\" ਵਰਤੋ।",
      "finder.near_me": "ਮੇਰੇ ਨੇੜੇ",
      "finder.searching": "ਖੋਜ ਰਿਹਾ ਹੈ…",
      "finder.suburb.placeholder": "ਉਪਨਗਰ ਦੁਆਰਾ ਫਿਲਟਰ ਕਰੋ (ਜਿਵੇਂ Darwin, NT)…",
      "finder.suburb.clear": "ਸਾਫ਼ ਕਰੋ",
      "finder.search_in": "{{cat}} ਵਿੱਚ ਖੋਜੋ",
      "finder.empty": "ਕੋਈ ਨਤੀਜੇ ਨਹੀਂ — ਕੋਈ ਹੋਰ ਸ਼੍ਰੇਣੀ ਜਾਂ ਖੋਜ ਸ਼ਬਦ ਵਰਤੋ।",
      "finder.meta.title": "HustleFinder — ਮੁਫਤ Aussie ਉਪਨਗਰ ਡਾਇਰੈਕਟਰੀ",
      "finder.meta.desc": "ਹਰੇਕ ਆਸਟ੍ਰੇਲੀਆਈ ਉਪਨਗਰ ਵਿੱਚ ਸਥਾਨਕ ਕਾਰੋਬਾਰ, ਚਾਰਜਰ, ਕੈਫੇ, ਟਾਇਲਟ ਅਤੇ ਹੋਰ ਬਹੁਤ ਕੁਝ ਖੋਜੋ — 100% ਮੁਫਤ।",
      "finder.footer.credits": "ਡਾਟਾ ਕ੍ਰੈਡਿਟ ਅਤੇ ਐਟ੍ਰੀਬਿਊਸ਼ਨ",
      "finder.footer.terms": "ਸ਼ਰਤਾਂ",
      "finder.cat.toilet": "ਟਾਇਲਟ",
      "finder.cat.fountain": "ਪੀਣ ਵਾਲੇ ਫੁਹਾਰੇ",
      "finder.cat.transport": "ਆਵਾਜਾਈ",
      "finder.cat.park": "ਪਾਰਕ",
      "finder.cat.medical": "ਮੈਡੀਕਲ",
      "finder.cat.school": "ਸਕੂਲ",
      "finder.cat.library": "ਲਾਇਬ੍ਰੇਰੀਆਂ",
      "finder.cat.petrol": "ਪੈਟਰੋਲ",
      "finder.cat.kidzone": "Kid Zone",
      "finder.cat.puzzles": "ਬੁਝਾਰਤਾਂ",
      "finder.cat.roadtrip": "ਰੋਡ ਟ੍ਰਿੱਪ",
      "finder.hotels.title": "ਨੇੜੇ ਇੱਕ ਹੋਟਲ ਲੱਭੋ",
      "finder.hotels.sub": "ਇੱਕ ਖੋਜ ਵਿੱਚ 200+ ਬੁਕਿੰਗ ਸਾਈਟਾਂ ਦੀ ਤੁਲਨਾ ਕਰਦਾ ਹੈ — Trivago ਦੁਆਰਾ ਸੰਚਾਲਿਤ।",
      "finder.hotels.where": "ਕਿੱਥੇ",
      "finder.hotels.where_ph": "ਸ਼ਹਿਰ ਜਾਂ ਉਪਨਗਰ (ਜਿਵੇਂ Byron Bay, NSW)",
      "finder.hotels.checkin": "ਚੈੱਕ-ਇਨ",
      "finder.hotels.checkout": "ਚੈੱਕ-ਆਊਟ",
      "finder.hotels.adults": "ਬਾਲਗ",
      "finder.hotels.search": "ਹੋਟਲ ਖੋਜੋ",
      "finder.hotels.disclosure": "Trivago ਖੋਲ੍ਹਦਾ ਹੈ — ਅਸੀਂ ਬੁਕਿੰਗਾਂ 'ਤੇ ਕਮਿਸ਼ਨ ਕਮਾ ਸਕਦੇ ਹਾਂ।",
      /* AUTO-TRANSLATED rt.* START */
      "rt.brand_name": "Roadtrip Planner",
      "rt.page_title": "ਆਸਟ੍ਰੇਲੀਆਈ EV ਰੋਡ-ਟ੍ਰਿਪ ਪਲੈਨਰ",
      "rt.from": "ਤੋਂ",
      "rt.to": "ਤੱਕ",
      "rt.from_placeholder": "ਉਪਨਗਰ, ਕਸਬਾ ਜਾਂ ਸ਼ਹਿਰ (ਜਿਵੇਂ ਕਿ Newcastle NSW)",
      "rt.to_placeholder": "ਉਪਨਗਰ, ਕਸਬਾ ਜਾਂ ਸ਼ਹਿਰ (ਜਿਵੇਂ ਕਿ Hobart TAS)",
      "rt.stop": "ਸਟਾਪ",
      "rt.stop_placeholder": "ਵਿਚਕਾਰਲਾ ਸਟਾਪ",
      "rt.add_stop": "ਸਟਾਪ ਜੋੜੋ",
      "rt.upgrade_pro": "20 ਸਟਾਪਾਂ ਲਈ Pro ਵਿੱਚ ਅੱਪਗ੍ਰੇਡ ਕਰੋ",
      "rt.reverse": "ਉਲਟਾ (ਘਰ ਵਾਪਸੀ ਦਾ ਰਸਤਾ)",
      "rt.from_current": "ਮੌਜੂਦਾ ਟਿਕਾਣੇ ਤੋਂ",
      "rt.range_per_charge": "ਪ੍ਰਤੀ ਚਾਰਜ ਰੇਂਜ (km)",
      "rt.connector": "ਕਨੈਕਟਰ",
      "rt.connector_any": "ਕੋਈ ਵੀ",
      "rt.drive_hours_day": "ਡਰਾਈਵਿੰਗ ਘੰਟੇ/ਦਿਨ",
      "rt.accommodation": "ਰਿਹਾਇਸ਼",
      "rt.accom_any": "ਕੋਈ ਵੀ (BnB · Hotel · Caravan)",
      "rt.accom_bnb": "BnB",
      "rt.accom_hotel": "Hotel",
      "rt.accom_caravan": "Caravan Park",
      "rt.min_rating": "ਘੱਟੋ-ਘੱਟ ਰੇਟਿੰਗ",
      "rt.any_rating": "ਕੋਈ ਵੀ ਰੇਟਿੰਗ",
      "rt.only_5star": "ਸਿਰਫ਼ 5★",
      "rt.schedule": "ਸ਼ਡਿਊਲ:",
      "rt.depart_at": "ਰਵਾਨਾ ਹੋਣ ਦਾ ਸਮਾਂ",
      "rt.arrive_by": "ਪਹੁੰਚਣ ਦਾ ਸਮਾਂ",
      "rt.hotel_checkin": "Hotel ਚੈੱਕ-ਇਨ",
      "rt.hotel_checkout": "Hotel ਚੈੱਕ-ਆਊਟ",
      "rt.overnight_hint": "ਰਾਤ ਦੇ ਪੜਾਅ ਇਨ੍ਹਾਂ ਘੰਟਿਆਂ ਤੋਂ ਬਾਹਰ ਸ਼ਡਿਊਲ ਨਹੀਂ ਕੀਤੇ ਜਾਣਗੇ।",
      "rt.whos_in_car": "ਕਾਰ ਵਿੱਚ ਕੌਣ ਹੈ?",
      "rt.adults": "ਬਾਲਗ",
      "rt.kids": "ਬੱਚੇ",
      "rt.ages": "ਉਮਰਾਂ",
      "rt.show_attractions": "ਸੈਲਾਨੀ ਆਕਰਸ਼ਣ ਦਿਖਾਓ",
      "rt.show_kids": "ਬੱਚਿਆਂ ਦੇ ਅਨੁਕੂਲ ਸਟਾਪ ਦਿਖਾਓ",
      "rt.overnight_stays": "ਰਾਤ ਦਾ ਠਹਿਰਾਅ",
      "rt.fast_chargers_only": "ਸਿਰਫ਼ ਫਾਸਟ ਚਾਰਜਰ",
      "rt.hotel_must_charger": "Hotel ਵਿੱਚ ਚਾਰਜਰ ਹੋਣਾ ਲਾਜ਼ਮੀ ਹੈ",
      "rt.pet_only": "ਸਿਰਫ਼ ਪਾਲਤੂ ਜਾਨਵਰਾਂ ਲਈ ਅਨੁਕੂਲ ਥਾਵਾਂ",
      "rt.safety": "ਸੁਰੱਖਿਆ",
      "rt.safety_90": "90% (ਪੱਧਰੀ ਸੜਕ)",
      "rt.safety_85": "85% (ਡਿਫਾਲਟ)",
      "rt.safety_80": "80% (ਪਹਾੜੀਆਂ / ਟੋਇੰਗ)",
      "rt.safety_70": "70% (ਭਾਰੀ ਲੋਡ)",
      "rt.save_trip": "ਟ੍ਰਿਪ ਸੇਵ ਕਰੋ",
      "rt.plan_my_trip": "ਮੇਰਾ ਟ੍ਰਿਪ ਪਲੈਨ ਕਰੋ",
      "rt.refresh_trip": "ਟ੍ਰਿਪ ਰਿਫ੍ਰੈਸ਼ ਕਰੋ",
      "rt.planning": "ਪਲੈਨਿੰਗ ਹੋ ਰਹੀ ਹੈ…",
      "rt.resume_title": "ਇੱਥੋਂ ਟ੍ਰਿਪ ਦੁਬਾਰਾ ਸ਼ੁਰੂ ਕਰੋ",
      "rt.resume_body": "ਅਸੀਂ ਤੁਹਾਡਾ ਮੌਜੂਦਾ ਟਿਕਾਣਾ ਪ੍ਰਾਪਤ ਕਰ ਲਿਆ ਹੈ। ਆਪਣੇ ਡੈਸ਼ 'ਤੇ ਦਿਖਾਈ ਗਈ ਬਾਕੀ ਰੇਂਜ ਦਰਜ ਕਰੋ ਤਾਂ ਜੋ ਅਸੀਂ ਅਗਲੇ ਪੜਾਅ ਦੀ ਯੋਜਨਾ ਪੂਰੇ ਚਾਰਜ ਦੀ ਬਜਾਏ ਤੁਹਾਡੇ ਕੋਲ ਮੌਜੂਦ ਅਸਲ ਚਾਰਜ ਅਨੁਸਾਰ ਬਣਾਈਏ।",
      "rt.remaining_range": "ਬਾਕੀ ਰੇਂਜ (km)",
      "rt.cancel": "ਰੱਦ ਕਰੋ",
      "rt.replan_from_here": "ਇੱਥੋਂ ਦੁਬਾਰਾ ਪਲੈਨ ਕਰੋ",
      "rt.save_modal_title": "ਇਸ ਟ੍ਰਿਪ ਨੂੰ ਨਾਮ ਦਿਓ",
      "rt.save_modal_body": "ਸੇਵ ਕੀਤੇ ਟ੍ਰਿਪ ਤੁਹਾਡੀ ਪ੍ਰੋਫਾਈਲ ਵਿੱਚ ਰਹਿੰਦੇ ਹਨ — ਕਿਸੇ ਵੀ ਸਮੇਂ ਦੁਬਾਰਾ ਖੋਲ੍ਹੋ ਅਤੇ Replan 'ਤੇ ਕਲਿੱਕ ਕਰੋ।",
      "rt.save_name_placeholder": "ਜਿਵੇਂ ਕਿ Brisbane → Sydney via Byron",
      "rt.your_saved_trips": "ਤੁਹਾਡੇ ਸੇਵ ਕੀਤੇ ਟ੍ਰਿਪ",
      "rt.custom_location": "ਕਸਟਮ ਟਿਕਾਣਾ",
      "rt.trip_summary": "ਟ੍ਰਿਪ ਦਾ ਸਾਰ",
      "rt.itinerary": "ਯਾਤਰਾ ਦਾ ਵੇਰਵਾ",
      "rt.no_stops_needed": "ਕਿਸੇ ਸਟਾਪ ਦੀ ਲੋੜ ਨਹੀਂ — ਪੂਰਾ ਟ੍ਰਿਪ ਤੁਹਾਡੀ ਰੇਂਜ ਦੇ ਅੰਦਰ ਹੈ।",
      "rt.chargers_in_corridor": "ਰਾਹ ਵਿੱਚ ਚਾਰਜਰ",
      "rt.planned_stops": "ਪਲੈਨ ਕੀਤੇ ਸਟਾਪ",
      "rt.overnight_stop": "ਰਾਤ ਦਾ ਸਟਾਪ",
      "rt.overnight_stops": "ਰਾਤ ਦੇ ਸਟਾਪ",
      "rt.suggested_detours": "ਸੁਝਾਏ ਗਏ ਹੋਰ ਰਸਤੇ — ਜੋੜਨ ਲਈ ਟਿਕ ਕਰੋ",
      "rt.replan_with_detours": "ਹੋਰ ਰਸਤਿਆਂ ਨਾਲ ਦੁਬਾਰਾ ਪਲੈਨ ਕਰੋ",
      "rt.attractions_label": "ਆਕਰਸ਼ਣ",
      "rt.kids_label": "ਬੱਚੇ",
      "rt.km_from_start": "ਸ਼ੁਰੂਆਤ ਤੋਂ km",
      "rt.open_driving": "ਡਰਾਈਵਿੰਗ ਮੋਡ ਖੋਲ੍ਹੋ",
      "rt.big_font_hint": "ਵੱਡੇ ਫੌਂਟ, ਕ੍ਰੈਡਲ-ਫ੍ਰੈਂਡਲੀ। CarPlay/Android Auto ਬ੍ਰਾਊਜ਼ਰ ਮਿਰਰ ਦੇ ਨਾਲ ਕੰਮ ਕਰਦਾ ਹੈ।",
      "rt.check_price_book": "ਕੀਮਤ ਦੇਖੋ ਅਤੇ Trivago 'ਤੇ ਬੁੱਕ ਕਰੋ ↗",
      "rt.visit_site": "ਸਾਈਟ ਦੇਖੋ ↗",
      "rt.charger_onsite": "ਸਾਈਟ 'ਤੇ ਚਾਰਜਰ",
      "rt.leg_exceeds_range": "ਇਹ ਪੜਾਅ ਤੁਹਾਡੀ ਨਿਰਧਾਰਤ ਰੇਂਜ ਤੋਂ ਵੱਧ ਹੈ — ਤੁਸੀਂ ਇਸ ਚਾਰਜਰ ਤੱਕ ਪਹੁੰਚਣ ਤੋਂ ਪਹਿਲਾਂ ਹੀ ਚਾਰਜ ਖਤਮ ਕਰ ਬੈਠੋਗੇ। ਰੇਂਜ ਦਾ ਅੰਦਾਜ਼ਾ ਘਟਾਓ ਜਾਂ ਵਿਚਕਾਰ ਕੋਈ ਹੋਰ ਚਾਰਜਿੰਗ ਸਟਾਪ ਜੋੜੋ।",
      "rt.add_waypoint_replan": "ਵੇਅ-ਪੁਆਇੰਟ ਵਜੋਂ ਜੋੜੋ ਅਤੇ ਦੁਬਾਰਾ ਪਲੈਨ ਕਰੋ",
      /* AUTO-TRANSLATED rt.* END */
                      /* AUTO-TRANSLATED hm.* START */
      "hm.nav.hustlemeet": "HustleMeet",
      "hm.nav.circles": "Circles",
      "hm.nav.matchmaker": "Matchmaker",
      "hm.nav.meet": "ਮਿਲੋ",
      "hm.nav.overview": "HustleMeetups",
      "hm.overview.page_title": "HustleMeetups — ਆਪਣੇ ਲੋਕਾਂ ਨੂੰ ਲੱਭਣ ਦੇ ਤਿੰਨ ਨਿੱਘੇ ਤਰੀਕੇ।",
      "hm.overview.kicker": "HustleSuite ਦਾ ਹਿੱਸਾ",
      "hm.overview.hero_title": "ਆਪਣੇ ਲੋਕਾਂ ਨੂੰ ਲੱਭਣ ਦੇ ਤਿੰਨ ਨਿੱਘੇ ਤਰੀਕੇ।",
      "hm.overview.hero_title_accent": "ਤੁਹਾਡੇ ਲੋਕ",
      "hm.overview.hero_sub": "HustleMeetups Australia ਵਿੱਚ ਅਸਲ ਦੋਸਤੀ ਬਣਾਉਣ ਦਾ ਸਭ ਤੋਂ ਪਿਆਰਾ ਤਰੀਕਾ ਹੈ — ਉਹ ਮਾਹੌਲ ਚੁਣੋ ਜੋ ਤੁਹਾਨੂੰ ਸਹੀ ਲੱਗੇ। ਭਾਵੇਂ ਤੁਸੀਂ ਹਫਤਾਵਾਰੀ ਰਨਿੰਗ ਗਰੁੱਪ ਚਾਹੁੰਦੇ ਹੋ, ਆਪਣੇ ਮੌਜੂਦਾ ਸਾਥੀਆਂ ਨਾਲ ਮਹੀਨਾਵਾਰ ਰਾਤ ਦਾ ਖਾਣਾ, ਜਾਂ AI-ਦੁਆਰਾ ਮਿਲਾਏ ਗਏ ਨਵੇਂ ਚਿਹਰਿਆਂ ਵਾਲਾ ਟੇਬਲ, ਇੱਥੇ ਹਰ ਕਿਸੇ ਲਈ ਰਾਹ ਹੈ।",
      "hm.overview.tile_meet_kicker": "HustleMeet",
      "hm.overview.tile_meet_title": "ਆਪਣੇ ਲੋਕਾਂ ਨੂੰ ਲੱਭੋ। ਆਪਣਾ ਸ਼ੌਕ ਪੂਰਾ ਕਰੋ।",
      "hm.overview.tile_meet_body": "ਦਿਲਚਸਪੀ-ਅਧਾਰਿਤ ਗਰੁੱਪਾਂ ਲਈ Australia ਦਾ ਘਰ। ਦੌੜਾਕ, ਕੋਡਰ, ਨਿਵੇਸ਼ਕ, ਸੈਰ ਕਰਨ ਵਾਲੇ, ਡਾਂਸਰ, ਬ੍ਰੇਕਫਾਸਟ ਕਲੱਬ, ਬੁੱਕ ਕਰੂ — ਸੈਂਕੜੇ ਗਰੁੱਪ, ਹਰ ਹਫਤੇ ਅਸਲ ਇਵੈਂਟਸ। ਇੱਕ ਵਾਰ ਆਓ, ਜਾਂ ਹਰ ਹਫਤੇ।",
      "hm.overview.tile_meet_cta": "ਗਰੁੱਪ ਦੇਖੋ",
      "hm.overview.tile_circles_kicker": "Circles",
      "hm.overview.tile_circles_title": "ਤੁਹਾਡੇ ਸਾਥੀ। ਹਰ ਵਾਰ ਇੱਕ ਨਵਾਂ ਟੇਬਲ।",
      "hm.overview.tile_circles_body": "ਆਪਣੇ ਸਾਥੀਆਂ ਦੇ ਨਿਯਮਤ ਗਰੁੱਪ ਨੂੰ ਨਾਲ ਲਿਆਓ। ਅਸੀਂ ਇੱਕ ਨਵਾਂ ਰੈਸਟੋਰੈਂਟ ਬੁੱਕ ਕਰਦੇ ਹਾਂ, ਟ੍ਰੀਵੀਆ ਦੀ ਯੋਜਨਾ ਬਣਾਉਂਦੇ ਹਾਂ, ਅਤੇ ਕਾਰਡਾਂ ਦਾ ਪ੍ਰਬੰਧ ਕਰਦੇ ਹਾਂ। ਤੁਸੀਂ ਬੱਸ ਪਹੁੰਚੋ। ਮੌਜੂਦਾ ਸਾਥੀਆਂ ਨੂੰ ਨੇੜੇ ਰੱਖਣ ਦਾ ਸਭ ਤੋਂ ਵਧੀਆ ਤਰੀਕਾ — ਕਿਸੇ ਨੂੰ ਵੀ ਪ੍ਰਬੰਧ ਕਰਨ ਦੀ ਲੋੜ ਨਹੀਂ ਪੈਂਦੀ।",
      "hm.overview.tile_circles_cta": "ਇੱਕ Circle ਸ਼ੁਰੂ ਕਰੋ",
      "hm.overview.tile_matchmaker_kicker": "Matchmaker",
      "hm.overview.tile_matchmaker_title": "AI ਟੇਬਲ ਚੁਣਦਾ ਹੈ। ਤੁਸੀਂ ਬੱਸ ਪਹੁੰਚੋ।",
      "hm.overview.tile_matchmaker_body": "10-ਮਿੰਟ ਦਾ ਕੁਇਜ਼ ਲਓ। ਰਾਤੋ-ਰਾਤ, ਅਸੀਂ ਤੁਹਾਨੂੰ ਅਜਿਹੇ ਅਜਨਬੀਆਂ ਨਾਲ ਬਿਠਾਉਂਦੇ ਹਾਂ ਜੋ ਮਿੱਠੇ (dessert) ਤੱਕ ਪਹੁੰਚਦਿਆਂ ਪੁਰਾਣੇ ਦੋਸਤਾਂ ਵਰਗੇ ਲੱਗਣ ਲੱਗ ਪੈਂਦੇ ਹਨ। ਹਰ ਵਾਰ ਵੱਖਰਾ ਟੇਬਲ।",
      "hm.overview.tile_matchmaker_cta": "ਕੁਇਜ਼ ਲਓ",
      "hm.overview.trust_kicker": "ਇਹ ਕਿਉਂ ਕੰਮ ਕਰਦਾ ਹੈ",
      "hm.overview.trust_title": "ਭਰੋਸਾ ਪਹਿਲਾਂ। ਟੇਬਲ ਬਾਅਦ ਵਿੱਚ।",
      "hm.overview.trust_1_title": "ਵੈਰੀਫਾਈਡ ਮੈਂਬਰ",
      "hm.overview.trust_1_body": "ਹਰ ਗਰੁੱਪ ਅਸਲ ID ਚੈੱਕ + ਭਰੋਸੇਯੋਗਤਾ ਸਕੋਰ ਨਾਲ ਸ਼ੁਰੂ ਹੁੰਦਾ ਹੈ। ਕੋਈ ਫਾਲਤੂ ਖਾਤੇ ਨਹੀਂ।",
      "hm.overview.trust_2_title": "ਅਸਲ ਹੋਸਟ",
      "hm.overview.trust_2_body": "ਹਰ ਗਰੁੱਪ ਦੀ ਅਗਵਾਈ ਇੱਕ ਜਾਂਚੇ ਹੋਏ ਹੋਸਟ ਦੁਆਰਾ ਕੀਤੀ ਜਾਂਦੀ ਹੈ ਜੋ ਮਾਹੌਲ ਨੂੰ ਸੰਭਾਲਦਾ ਹੈ, ਲੈਅ ਨਿਰਧਾਰਤ ਕਰਦਾ ਹੈ, ਅਤੇ ਸਾਰਿਆਂ ਦਾ ਖਿਆਲ ਰੱਖਦਾ ਹੈ।",
      "hm.overview.trust_3_title": "ਦੋਸਤੀ ਪਹਿਲਾਂ",
      "hm.overview.trust_3_body": "ਇੱਥੇ ਸਭ ਕੁਝ ਅਸਲ ਦੋਸਤੀ ਲਈ ਤਿਆਰ ਕੀਤਾ ਗਿਆ ਹੈ, ਡੇਟਿੰਗ ਲਈ ਨਹੀਂ। ਕਦੇ ਵੀ ਨਹੀਂ।",
      "hm.overview.trust_4_title": "ਜ਼ੀਰੋ-ਟੋਲਰੈਂਸ ਮਾਡਰੇਸ਼ਨ",
      "hm.overview.trust_4_body": "ਇੱਕ ਰਿਪੋਰਟ, ਇੱਕ ਰੀਵਿਊ, ਇੱਕ ਫੈਸਲਾ। ਗਲਤ ਵਿਵਹਾਰ ਕਰਨ ਵਾਲਿਆਂ ਨੂੰ ਉਸੇ ਰਾਤ ਹਟਾ ਦਿੱਤਾ ਜਾਂਦਾ ਹੈ।",
      "hm.overview.footer_line": "HustleSuite ਦਾ ਹਿੱਸਾ — Australian ਮਾਲਕੀ ਅਤੇ ਹੋਸਟਿੰਗ।",
      "hm.landing.page_title": "HustleMeet · ਆਪਣੇ ਲੋਕਾਂ ਨੂੰ ਲੱਭੋ। ਆਪਣਾ ਸ਼ੌਕ ਪੂਰਾ ਕਰੋ।",
      "hm.landing.kicker": "Australia ਦਾ ਇੰਟਰੈਸਟ ਨੈੱਟਵਰਕ",
      "hm.landing.hero_title": "ਆਪਣੇ ਲੋਕਾਂ ਨੂੰ ਲੱਭੋ। ਆਪਣਾ ਸ਼ੌਕ ਪੂਰਾ ਕਰੋ।",
      "hm.landing.hero_title_accent": "ਤੁਹਾਡੇ ਲੋਕ",
      "hm.landing.hero_sub": "HustleMeet ਦਿਲਚਸਪੀ-ਅਧਾਰਿਤ ਗਰੁੱਪਾਂ ਲਈ Australia ਦਾ ਘਰ ਹੈ। ਲੰਚ 'ਤੇ ਡੀਬੱਗਿੰਗ ਕਰਦੇ ਸਾਫਟਵੇਅਰ ਡਿਵੈਲਪਰ। ਸਵੇਰੇ 6 ਵਜੇ ਲੰਬੀ ਦੂਰੀ ਦੇ ਦੌੜਾਕ। ਮਹੀਨਾਵਾਰ ਮਿਲਣ ਵਾਲੇ ਨਿਵੇਸ਼ਕ। ਸੈਰ ਕਰਨ ਵਾਲੇ ਗਰੁੱਪ, ਡਾਂਸ ਕਰੂ, ਬ੍ਰੇਕਫਾਸਟ ਕਲੱਬ, ਬੋਰਡ-ਗੇਮ ਨਾਈਟਸ। ਇੱਕ ਗਰੁੱਪ ਚੁਣੋ, ਅਗਲਾ ਇਵੈਂਟ ਬੁੱਕ ਕਰੋ, ਅਤੇ ਪਹੁੰਚੋ।",
      "hm.landing.cta_explore_circles": "ਗਰੁੱਪ ਦੇਖੋ",
      "hm.landing.cta_try_matchmaker": "ਆਪਣਾ ਗਰੁੱਪ ਸ਼ੁਰੂ ਕਰੋ",
      "hm.landing.trust_verified": "ਵੈਰੀਫਾਈਡ ਮੈਂਬਰ",
      "hm.landing.trust_venues": "ਜਾਂਚੇ ਹੋਏ ਇਵੈਂਟ ਹੋਸਟ",
      "hm.landing.trust_friendship": "ਦੋਸਤੀ ਪਹਿਲਾਂ — ਡੇਟਿੰਗ ਕਦੇ ਨਹੀਂ",
      "hm.landing.trust_moderation": "ਜ਼ੀਰੋ-ਟੋਲਰੈਂਸ ਮਾਡਰੇਸ਼ਨ",
      "hm.landing.subbrand_circles_kicker": "Circles",
      "hm.landing.subbrand_circles_title": "ਪਹਿਲਾਂ ਹੀ ਤੁਹਾਡੇ ਸਾਥੀ ਹਨ? ਉਨ੍ਹਾਂ ਨੂੰ ਇੱਕ ਸਥਾਈ ਘਰ ਦਿਓ।",
      "hm.landing.subbrand_circles_body": "Circles ਉਨ੍ਹਾਂ ਦੋਸਤਾਂ ਦੇ ਗਰੁੱਪ ਲਈ ਹੈ ਜੋ ਪਹਿਲਾਂ ਹੀ ਮਿਲਦੇ ਹਨ — ਬੁੱਕ ਕਲੱਬ, ਪੋਕਰ ਨਾਈਟ, ਰਨਿੰਗ ਕਰੂ, ਕੰਮ ਦੇ ਦੋਸਤ। ਅਸੀਂ ਜਗ੍ਹਾ, ਰੀਮਾਈਂਡਰ ਅਤੇ ਰੋਸਟਰ ਸੰਭਾਲਦੇ ਹਾਂ। ਤੁਸੀਂ ਬੱਸ ਪਹੁੰਚੋ।",
      "hm.landing.subbrand_circles_b1": "ਬਦਲਦੀਆਂ ਜਗ੍ਹਾਵਾਂ, ਇੱਕ ਚੱਲਦਾ ਰੋਸਟਰ",
      "hm.landing.subbrand_circles_b2": "ਆਟੋ-ਰੀਮਾਈਂਡਰ + ਆਸਾਨ RSVP",
      "hm.landing.subbrand_circles_b3": "ਟ੍ਰੀਵੀਆ, ਕਾਰਡ ਅਤੇ ਥੀਮਡ ਨਾਈਟਸ ਤਿਆਰ ਹਨ",
      "hm.landing.subbrand_circles_cta": "ਇੱਕ Circle ਸ਼ੁਰੂ ਕਰੋ",
      "hm.landing.subbrand_matchmaker_kicker": "Matchmaker",
      "hm.landing.subbrand_matchmaker_title": "AI ਟੇਬਲ ਚੁਣਦਾ ਹੈ। ਤੁਸੀਂ ਬੱਸ ਪਹੁੰਚੋ।",
      "hm.landing.subbrand_matchmaker_body": "ਇੱਕ 10-ਮਿੰਟ ਦਾ ਕੁਇਜ਼ — ਸ਼ਖਸੀਅਤ, ਕਦਰਾਂ-ਕੀਮਤਾਂ, ਉਪਲਬਧਤਾ। ਸਾਡਾ Matchmaker ਤੁਹਾਨੂੰ ਅਜਿਹੇ ਅਜਨਬੀਆਂ ਨਾਲ ਬਿਠਾਉਂਦਾ ਹੈ ਜੋ ਮਿੱਠੇ ਤੱਕ ਪਹੁੰਚਦਿਆਂ ਪੁਰਾਣੇ ਦੋਸਤਾਂ ਵਰਗੇ ਲੱਗਣ ਲੱਗ ਪੈਂਦੇ ਹਨ। ਹਰ ਵਾਰ ਵੱਖਰਾ ਟੇਬਲ।",
      "hm.landing.subbrand_matchmaker_b1": "Big-5 ਸ਼ਖਸੀਅਤ ਅਤੇ ਕਦਰਾਂ-ਕੀਮਤਾਂ ਦਾ ਸਕੋਰ",
      "hm.landing.subbrand_matchmaker_b2": "ਜਦੋਂ ਤੁਹਾਡਾ ਫਿੱਟ 0.70 ਜਾਂ ਵੱਧ ਹੋਵੇ ਤਾਂ ਰਾਤੋ-ਰਾਤ ਸੱਦੇ",
      "hm.landing.subbrand_matchmaker_b3": "ਤੁਹਾਡੇ ਹਰ ਖਾਣੇ ਤੋਂ ਸਿੱਖਦਾ ਹੈ",
      "hm.landing.subbrand_matchmaker_cta": "ਕੁਇਜ਼ ਲਓ",
      "hm.landing.how_kicker": "HustleMeet ਕਿਵੇਂ ਕੰਮ ਕਰਦਾ ਹੈ",
      "hm.landing.how_title": "ਚਾਰ ਕਦਮ। ਆਪਣਾ ਗਰੁੱਪ ਲੱਭੋ। ਪਹੁੰਚੋ।",
      "hm.landing.how_step1_ttl": "ਆਪਣੀਆਂ ਦਿਲਚਸਪੀਆਂ ਚੁਣੋ",
      "hm.landing.how_step1_sub": "ਦੌੜਨਾ, ਕੋਡਿੰਗ, ਨਿਵੇਸ਼ ਕਰਨਾ, ਖਾਣਾ ਪਕਾਉਣਾ, ਸੰਗੀਤ, ਸੈਰ ਕਰਨਾ — ਸਾਨੂੰ ਦੱਸੋ ਕਿ ਤੁਹਾਨੂੰ ਕੀ ਪਸੰਦ ਹੈ ਅਤੇ ਤੁਸੀਂ ਕਿੱਥੇ ਰਹਿੰਦੇ ਹੋ।",
      "hm.landing.how_step2_ttl": "ਆਪਣੇ ਨੇੜੇ ਦੇ ਗਰੁੱਪ ਦੇਖੋ",
      "hm.landing.how_step2_sub": "ਹਰ ਗਰੁੱਪ ਆਪਣੇ ਹੋਸਟ, ਆਪਣੀ ਲੈਅ ਅਤੇ ਆਪਣੇ ਅਗਲੇ ਇਵੈਂਟ ਬਾਰੇ ਦੱਸਦਾ ਹੈ। ਜੁੜਨ ਤੋਂ ਪਹਿਲਾਂ ਮਾਹੌਲ ਨੂੰ ਸਮਝੋ।",
      "hm.landing.how_step3_ttl": "ਅਗਲੇ ਇਵੈਂਟ ਲਈ RSVP ਕਰੋ",
      "hm.landing.how_step3_sub": "ਇੱਕ ਟੈਪ, ਇੱਕ ਕੈਲੰਡਰ ਸੱਦਾ, ਇੱਕ ਦਿਨ ਪਹਿਲਾਂ ਸਾਫ਼ ਰੀਮਾਈਂਡਰ। ਕੁਝ ਇਵੈਂਟਸ ਮੁਫਤ ਹਨ, ਕੁਝ ਪੇਡ — ਕੀਮਤ ਪਹਿਲਾਂ ਹੀ ਦੱਸੀ ਹੁੰਦੀ ਹੈ।",
      "hm.landing.how_step4_ttl": "ਪਹੁੰਚੋ ਅਤੇ ਜੁੜੋ",
      "hm.landing.how_step4_sub": "ਹੋਸਟ ਨੂੰ ਮਿਲੋ, ਕਰੂ ਨੂੰ ਮਿਲੋ, ਆਪਣਾ ਸ਼ੌਕ ਪੂਰਾ ਕਰੋ। ਜੇਕਰ ਗੱਲ ਬਣ ਜਾਵੇ ਤਾਂ ਅਗਲੇ ਹਫਤੇ ਫਿਰ ਆਓ।",
      "hm.landing.stats_kicker": "Australia ਦਾ ਮਿਲਣ ਦਾ ਨਵਾਂ ਤਰੀਕਾ",
      "hm.landing.stats_title": "ਇਹ ਕਾਰਨ ਹੈ ਕਿ ਲੋਕ ਵਾਰ-ਵਾਰ ਵਾਪਸ ਆਉਂਦੇ ਹਨ।",
      "hm.landing.stats_92_sub": "ਮਹਿਸੂਸ ਕਰਦੇ ਹਨ ਕਿ ਉਹ ਇਵੈਂਟ ਵਿੱਚ ਘੱਟੋ-ਘੱਟ ਇੱਕ ਵਿਅਕਤੀ ਨਾਲ ਜੁੜੇ ਹਨ",
      "hm.landing.stats_68_sub": "ਇੱਕ ਹਫ਼ਤੇ ਦੇ ਅੰਦਰ ਆਪਣੇ ਅਗਲੇ ਇਵੈਂਟ ਲਈ RSVP ਕਰਦੇ ਹਨ",
      "hm.landing.stats_85_sub": "ਗਰੁੱਪ ਦੇ ਕਿਸੇ ਵਿਅਕਤੀ ਨਾਲ ਸੰਪਰਕ ਵਿੱਚ ਰਹਿੰਦੇ ਹਨ",
      "hm.landing.why_kicker": "ਅਸੀਂ ਕਿਉਂ ਕੰਮ ਕਰਦੇ ਹਾਂ",
      "hm.landing.why_title": "ਅਸਲ ਦਿਲਚਸਪੀਆਂ। ਅਸਲ ਹੋਸਟ। ਅਸਲ ਇਵੈਂਟਸ।",
      "hm.landing.why_1_ttl": "ਦਿਲਚਸਪੀ ਪਹਿਲਾਂ, ਸਥਾਨ ਬਾਅਦ ਵਿੱਚ",
      "hm.landing.why_1_body": "ਤੁਹਾਡੇ ਗਰੁੱਪ ਦੇ ਲੋਕ ਅਸਲ ਵਿੱਚ ਉਸ ਚੀਜ਼ ਦੀ ਪਰਵਾਹ ਕਰਦੇ ਹਨ ਜਿਸਦੀ ਤੁਸੀਂ ਕਰਦੇ ਹੋ — ਇਹ ਅਸਲ ਦੋਸਤੀ ਦਾ ਸਭ ਤੋਂ ਛੋਟਾ ਰਸਤਾ ਹੈ।",
      "hm.landing.why_2_ttl": "ਅਸਲ ਇਵੈਂਟ ਹੋਸਟ",
      "hm.landing.why_2_body": "ਹਰ ਗਰੁੱਪ ਦਾ ਇੱਕ ਜਾਂਚਿਆ ਹੋਇਆ ਹੋਸਟ ਹੁੰਦਾ ਹੈ ਜੋ ਮਾਹੌਲ ਨੂੰ ਸੰਭਾਲਦਾ ਹੈ, ਲੈਅ ਨਿਰਧਾਰਤ ਕਰਦਾ ਹੈ, ਅਤੇ ਜੇਕਰ ਕੁਝ ਗਲਤ ਹੋਵੇ ਤਾਂ ਦਖਲ ਦਿੰਦਾ ਹੈ। ਕੋਈ Facebook ਐਡਮਿਨ ਨਹੀਂ। ਇੱਕ ਅਸਲ ਵਿਅਕਤੀ।",
      "hm.landing.why_3_ttl": "ਨਿਰੰਤਰਤਾ ਮਹੱਤਵ ਰੱਖਦੀ ਹੈ",
      "hm.landing.why_3_body": "ਹਰ ਹਫ਼ਤੇ ਨਵੇਂ ਇਵੈਂਟਸ। ਕੁਝ ਅਜਿਹਾ ਜਿਸਦੀ ਤੁਸੀਂ ਉਡੀਕ ਕਰ ਸਕਦੇ ਹੋ। ਇੱਕ ਅਜਿਹੀ ਲੈਅ ਜੋ ਅਜਨਬੀਆਂ ਨੂੰ ਤੁਹਾਡੇ ਮੰਗਲਵਾਰ ਦੀ ਰਾਤ ਦੇ ਸਾਥੀ ਬਣਾ ਦਿੰਦੀ ਹੈ।",
      "hm.landing.why_4_ttl": "ਸੁਰੱਖਿਅਤ ਅਤੇ ਭਰੋਸੇਮੰਦ",
      "hm.landing.why_4_body": "ਵੈਰੀਫਾਈਡ ਮੈਂਬਰ, ਜਨਤਕ ਸਥਾਨ, ਬਦਸਲੂਕੀ ਲਈ ਜ਼ੀਰੋ ਟੋਲਰੈਂਸ। ਸਾਡੀ ਸਪੋਰਟ ਟੀਮ ਹਰ ਇਵੈਂਟ ਤੋਂ ਪਹਿਲਾਂ, ਦੌਰਾਨ ਅਤੇ ਬਾਅਦ ਵਿੱਚ ਤਿਆਰ ਰਹਿੰਦੀ ਹੈ।",
      "hm.landing.why_5_ttl": "ਦੋਸਤੀ ਪਹਿਲਾਂ",
      "hm.landing.why_5_body": "ਇਹ ਡੇਟਿੰਗ ਨਹੀਂ ਹੈ। ਇਹ ਕਮਿਊਨਿਟੀ ਹੈ। ਅਸਲ ਦਿਲਚਸਪੀਆਂ, ਅਸਲ ਲੋਕ, ਵਿਚਕਾਰ ਕੋਈ ਸਵਾਈਪਿੰਗ ਨਹੀਂ।",
      "hm.landing.why_6_ttl": "ਇੰਨਾ ਛੋਟਾ ਕਿ ਮਹੱਤਵ ਰੱਖੇ",
      "hm.landing.why_6_body": "ਜ਼ਿਆਦਾਤਰ ਗਰੁੱਪਾਂ ਵਿੱਚ ਇਵੈਂਟ ਸੀਟਾਂ 4-15 ਤੱਕ ਸੀਮਿਤ ਹੁੰਦੀਆਂ ਹਨ। ਅਸਲ ਗੱਲਬਾਤ ਲਈ ਕਾਫ਼ੀ ਵੱਡਾ। ਇੰਨਾ ਛੋਟਾ ਕਿ ਕੋਈ ਗੁਆਚ ਨਾ ਜਾਵੇ।",
      "hm.landing.cities_kicker": "ਅਸੀਂ ਕਿੱਥੇ ਚੱਲਦੇ ਹਾਂ",
      "hm.landing.cities_title": "Australia ਅਤੇ NZ ਵਿੱਚ ਗਰੁੱਪ ਮਿਲ ਰਹੇ ਹਨ।",
      "hm.landing.cities_sub_zero": "15+ ਸ਼ਹਿਰਾਂ ਵਿੱਚ ਪਹਿਲੇ ਗਰੁੱਪ ਖੁੱਲ੍ਹ ਰਹੇ ਹਨ — ਜਲਦੀ ਸ਼ਾਮਲ ਹੋਵੋ ਅਤੇ ਮਾਹੌਲ ਬਣਾਓ।",
      "hm.landing.cities_sub_some": "ਗਰੁੱਪ 15+ ਸ਼ਹਿਰਾਂ ਵਿੱਚ ਲਾਈਵ ਹਨ। ਤੁਹਾਡੇ ਸ਼ਹਿਰ ਵਿੱਚ ਕੁਝ ਨਾ ਕੁਝ, ਇਸੇ ਹਫ਼ਤੇ।",
      "hm.landing.cities_sub_one": "{{count}} ਲਾਈਵ ਗਰੁੱਪ · ਹਰ ਹਫ਼ਤੇ ਨਵੇਂ ਇਵੈਂਟਸ · ਹਮੇਸ਼ਾ ਨਵੇਂ ਸ਼ਹਿਰ ਜੋੜ ਰਹੇ ਹਾਂ।",
      "hm.landing.cities_sub_many": "{{count}} ਲਾਈਵ ਗਰੁੱਪ · ਹਰ ਹਫ਼ਤੇ ਨਵੇਂ ਇਵੈਂਟਸ · ਹਮੇਸ਼ਾ ਨਵੇਂ ਸ਼ਹਿਰ ਜੋੜ ਰਹੇ ਹਾਂ।",
      "hm.landing.forme_kicker": "ਇਹ ਤੁਹਾਡੇ ਲਈ ਹੈ ਜੇ…",
      "hm.landing.forme_title": "ਤੁਸੀਂ ਅਸਲ ਦਿਲਚਸਪੀਆਂ ਚਾਹੁੰਦੇ ਹੋ। ਅਸਲ ਲੋਕ। ਅਸਲ ਇਵੈਂਟਸ।",
      "hm.landing.forme_1": "ਤੁਸੀਂ ਸਵਾਈਪ-ਐਪਸ ਤੋਂ ਥੱਕ ਗਏ ਹੋ ਅਤੇ ਅਸਲ ਵਿੱਚ ਉਨ੍ਹਾਂ ਲੋਕਾਂ ਨੂੰ ਮਿਲਣਾ ਚਾਹੁੰਦੇ ਹੋ ਜੋ ਤੁਹਾਡੀਆਂ ਪਸੰਦ ਦੀਆਂ ਚੀਜ਼ਾਂ ਕਰਦੇ ਹਨ।",
      "hm.landing.forme_2": "ਤੁਸੀਂ ਕਿਸੇ ਨਵੇਂ ਸ਼ਹਿਰ ਵਿੱਚ ਆਏ ਹੋ ਅਤੇ ਤੁਹਾਨੂੰ ਇੱਕ ਨਿੱਘੇ ਸਾਥੀਆਂ ਦੀ ਲੋੜ ਹੈ, ਕਿਸੇ ਹੋਰ ਚੈਟ ਗਰੁੱਪ ਦੀ ਨਹੀਂ।",
      "hm.landing.forme_3": "ਤੁਸੀਂ ਦਿਖਾਵੇ ਦੀ ਬਜਾਏ ਡੂੰਘਾਈ ਨੂੰ ਮਹੱਤਵ ਦਿੰਦੇ ਹੋ — ਇੱਕ ਵਧੀਆ ਗਰੁੱਪ ਪੰਜਾਹ ਫਾਲੋਅਰਜ਼ ਤੋਂ ਬਿਹਤਰ ਹੈ।",
      "hm.landing.forme_4": "ਤੁਹਾਡਾ ਮੰਨਣਾ ਹੈ ਕਿ ਸਾਂਝੀਆਂ ਦਿਲਚਸਪੀਆਂ + ਅਸਲ ਜ਼ਿੰਦਗੀ ਵਿੱਚ ਤੈਅ ਕੀਤੇ ਦੋ ਘੰਟੇ ਹੀ ਅਸਲੀ ਰਾਜ਼ ਹਨ।",
      "hm.landing.notme_kicker": "ਇਹ ਤੁਹਾਡੇ ਲਈ ਨਹੀਂ ਹੈ ਜੇ…",
      "hm.landing.notme_title": "HustleMeet ਹਰ ਕਿਸੇ ਲਈ ਕੰਮ ਨਹੀਂ ਕਰਦਾ।",
      "hm.landing.notme_1": "ਤੁਸੀਂ ਪਹੁੰਚਣ ਦਾ ਵਾਅਦਾ ਨਹੀਂ ਕਰ ਸਕਦੇ — ਹੋਸਟ ਅਤੇ ਮੈਂਬਰ ਤੁਹਾਡੇ RSVP 'ਤੇ ਭਰੋਸਾ ਕਰ ਰਹੇ ਹਨ।",
      "hm.landing.notme_2": "ਤੁਸੀਂ ਡੇਟਿੰਗ ਸਰਵਿਸ ਲੱਭ ਰਹੇ ਹੋ — ਇੱਥੇ ਦੋਸਤੀ ਪਹਿਲਾਂ ਆਉਂਦੀ ਹੈ।",
      "hm.landing.notme_3": "ਤੁਸੀਂ ਕਿਸੇ ਨੂੰ ਮਿਲੇ ਬਿਨਾਂ ਸਿਰਫ ਪ੍ਰਚਾਰ ਕਰਨਾ ਚਾਹੁੰਦੇ ਹੋ — ਇਹ ਸਿਰਫ ਆਹਮੋ-ਸਾਹਮਣੇ ਮਿਲ ਕੇ ਕੰਮ ਕਰਦਾ ਹੈ।",
      "hm.landing.notme_4": "ਤੁਸੀਂ ਬੇਅੰਤ ਮੁਫਤ ਫਾਇਦਿਆਂ ਦੀ ਉਮੀਦ ਕਰਦੇ ਹੋ — ਕੁਝ ਇਵੈਂਟਸ ਦੀ ਕੀਮਤ ਹੁੰਦੀ ਹੈ। ਕੀਮਤ ਹਮੇਸ਼ਾ ਪਹਿਲਾਂ ਦੱਸੀ ਜਾਂਦੀ ਹੈ।",
      "hm.landing.safety_kicker": "ਸਾਡੀ ਸੁਰੱਖਿਆ ਪ੍ਰਤੀਬੱਧਤਾ",
      "hm.landing.safety_title": "ਨਿੱਘਾ, ਪਰ ਕਦੇ ਲਾਪਰਵਾਹ ਨਹੀਂ।",
      "hm.landing.safety_1_ttl": "ਜਨਤਕ ਸਥਾਨ",
      "hm.landing.safety_1_body": "ਹਰ ਇਵੈਂਟ ਜਨਤਕ, ਸਟਾਫ ਵਾਲੀ ਜਗ੍ਹਾ 'ਤੇ ਹੁੰਦਾ ਹੈ — ਇੱਕ ਕੈਫੇ, ਰੈਸਟੋਰੈਂਟ, ਪਾਰਕ, ਜਿਮ ਜਾਂ ਸਟੂਡੀਓ। ਕਦੇ ਵੀ ਕਿਸੇ ਦੇ ਘਰ ਨਹੀਂ ਜਦੋਂ ਤੱਕ ਹਰ ਸ਼ਾਮਲ ਹੋਣ ਵਾਲਾ ਸਹਿਮਤ ਨਾ ਹੋਵੇ।",
      "hm.landing.safety_2_ttl": "ਕਮਿਊਨਿਟੀ ਸਟੈਂਡਰਡ",
      "hm.landing.safety_2_body": "ਬਦਸਲੂਕੀ, ਪਰੇਸ਼ਾਨੀ ਜਾਂ ਬੇਈਮਾਨੀ ਲਈ ਜ਼ੀਰੋ ਟੋਲਰੈਂਸ। ਉਲੰਘਣਾ ਕਰਨ ਵਾਲੇ ਮੈਂਬਰਾਂ ਨੂੰ ਤੁਰੰਤ ਹਟਾ ਦਿੱਤਾ ਜਾਂਦਾ ਹੈ।",
      "hm.landing.safety_3_ttl": "ਪ੍ਰਾਈਵੇਸੀ ਸੁਰੱਖਿਅਤ",
      "hm.landing.safety_3_body": "ਤੁਹਾਡੇ ਵੇਰਵੇ ਕਦੇ ਵੀ ਜਨਤਕ ਤੌਰ 'ਤੇ ਸਾਂਝੇ ਨਹੀਂ ਕੀਤੇ ਜਾਂਦੇ। ਤੁਸੀਂ ਕੰਟਰੋਲ ਕਰਦੇ ਹੋ ਕਿ ਹਰ ਇਵੈਂਟ ਤੋਂ ਬਾਅਦ ਤੁਸੀਂ ਕਿਸ ਨਾਲ ਜੁੜੇ ਰਹਿਣਾ ਚਾਹੁੰਦੇ ਹੋ।",
      "hm.landing.safety_4_ttl": "ਸਪੋਰਟ ਹਾਜ਼ਰ ਹੈ",
      "hm.landing.safety_4_body": "ਜੇਕਰ ਤੁਹਾਡੇ ਕੋਈ ਸਵਾਲ ਜਾਂ ਚਿੰਤਾਵਾਂ ਹਨ, ਤਾਂ ਸਾਡੀ ਟੀਮ ਹਰ ਇਵੈਂਟ ਤੋਂ ਪਹਿਲਾਂ, ਦੌਰਾਨ ਅਤੇ ਬਾਅਦ ਵਿੱਚ ਉਪਲਬਧ ਹੈ।",
      "hm.landing.bottom_title": "ਤੁਹਾਡਾ ਅਗਲਾ ਪਸੰਦੀਦਾ ਗਰੁੱਪ ਸਿਰਫ ਇੱਕ ਟੈਪ ਦੂਰ ਹੈ।",
      "hm.landing.bottom_sub": "ਆਪਣਾ ਰਸਤਾ ਚੁਣੋ — ਕਿਸੇ ਮੌਜੂਦਾ ਗਰੁੱਪ ਵਿੱਚ ਸ਼ਾਮਲ ਹੋਵੋ, ਆਪਣੇ ਸਾਥੀਆਂ ਨੂੰ ਇੱਕ Circle ਵਿੱਚ ਲਿਆਓ, ਜਾਂ Matchmaker ਨੂੰ ਤੁਹਾਨੂੰ ਇੱਕ ਅਜਿਹੇ ਟੇਬਲ 'ਤੇ ਬਿਠਾਉਣ ਦਿਓ ਜੋ ਤੁਹਾਡੇ ਲਈ ਸਹੀ ਹੋਵੇ।",
      "hm.landing.bottom_cta_circles": "ਇੱਕ Circle ਸ਼ੁਰੂ ਕਰੋ",
      "hm.landing.bottom_cta_mm": "Matchmaker ਕੁਇਜ਼ ਲਓ",
      "hm.circles.page_title": "HustleMeet Circles · ਤੁਹਾਡੇ ਸਾਥੀ, ਹਰ ਵਾਰ ਨਵੀਂ ਜਗ੍ਹਾ",
      "hm.circles.kicker": "HustleMeet · Circles",
      "hm.circles.hero_title": "ਤੁਹਾਡੇ ਸਾਥੀ। ਹਰ ਵਾਰ ਇੱਕ ਨਵਾਂ ਟੇਬਲ।",
      "hm.circles.hero_title_accent": "ਤੁਹਾਡੇ ਸਾਥੀ",
      "hm.circles.hero_sub": "ਪਹਿਲਾਂ ਹੀ ਤੁਹਾਡੇ ਸਾਥੀ ਹਨ? Circles ਉਨ੍ਹਾਂ ਨੂੰ ਨੇੜੇ ਰੱਖਣ ਦਾ ਸਭ ਤੋਂ ਸੌਖਾ ਤਰੀਕਾ ਹੈ। ਅਸੀਂ ਜਗ੍ਹਾ ਬੁੱਕ ਕਰਦੇ ਹਾਂ, ਟ੍ਰੀਵੀਆ ਦੀ ਯੋਜਨਾ ਬਣਾਉਂਦੇ ਹਾਂ, ਕਾਰਡਾਂ ਦਾ ਪ੍ਰਬੰਧ ਕਰਦੇ ਹਾਂ। ਤੁਸੀਂ ਬੱਸ ਪਹੁੰਚੋ — ਉਹੀ ਸਾਥੀ, ਨਵੀਂ ਰਾਤ, ਨਵੀਂ ਜਗ੍ਹਾ।",
      "hm.circles.cta_find": "ਇੱਕ Circle ਸ਼ੁਰੂ ਕਰੋ",
      "hm.circles.cta_host_hero": "ਇੱਕ ਓਪਨ Circle ਵਿੱਚ ਸ਼ਾਮਲ ਹੋਵੋ",
      "hm.circles.trust_venue": "ਬਦਲਦੀਆਂ ਜਾਂਚੀਆਂ ਹੋਈਆਂ ਜਗ੍ਹਾਵਾਂ",
      "hm.circles.trust_party": "4-12 ਸਾਥੀ ਪ੍ਰਤੀ Circle",
      "hm.circles.trust_verified": "ਵੈਰੀਫਾਈਡ ਮੈਂਬਰ",
      "hm.circles.trust_friendship": "ਅਸਲ ਦੋਸਤੀ, ਰੱਖੀ ਨੇੜੇ",
      "hm.circles.how_kicker": "ਇਹ ਕਿਵੇਂ ਕੰਮ ਕਰਦਾ ਹੈ",
      "hm.circles.how_title": "ਚਾਰ ਕਦਮ। ਤੁਹਾਡੇ ਸਾਥੀ ਸਾਥੀ ਹੀ ਰਹਿੰਦੇ ਹਨ।",
      "hm.circles.how_step1_ttl": "ਆਪਣਾ Circle ਸ਼ੁਰੂ ਕਰੋ",
      "hm.circles.how_step1_sub": "ਇਸ ਨੂੰ ਨਾਮ ਦਿਓ, ਆਪਣੇ ਸਾਥੀਆਂ ਨੂੰ ਸੱਦਾ ਦਿਓ (12 ਤੱਕ), ਅਤੇ ਇੱਕ ਲੈਅ ਚੁਣੋ — ਹਫ਼ਤਾਵਾਰੀ, ਪੰਦਰਵਾੜੇ ਜਾਂ ਮਹੀਨਾਵਾਰ।",
      "hm.circles.how_step2_ttl": "ਅਸੀਂ ਜਗ੍ਹਾ ਚੁਣਦੇ ਹਾਂ",
      "hm.circles.how_step2_sub": "ਹਰ ਇਵੈਂਟ ਲਈ ਅਸੀਂ ਇੱਕ ਨਵੀਂ ਥਾਂ ਸੁਝਾਉਂਦੇ ਹਾਂ — ਰੈਸਟੋਰੈਂਟ, ਟ੍ਰੀਵੀਆ ਨਾਈਟ, ਕਾਰਡ ਰੂਮ, ਵਾਈਨ ਬਾਰ। ਜਾਂ ਆਪਣੀ ਪਸੰਦੀਦਾ ਜਗ੍ਹਾ ਪੱਕੀ ਕਰੋ।",
      "hm.circles.how_step3_ttl": "ਹਰ ਕੋਈ ਇੱਕ ਟੈਪ ਵਿੱਚ RSVP ਕਰਦਾ ਹੈ",
      "hm.circles.how_step3_sub": "ਆਟੋ-ਰੀਮਾਈਂਡਰ, ਖਰਚੇ ਦੀ ਵੰਡ, ਪ੍ਰੀਪੇਡ ਸੀਟਾਂ ਤਾਂ ਜੋ ਅਚਾਨਕ ਨਾ ਆਉਣ ਵਾਲੇ ਯੋਜਨਾ ਨੂੰ ਖਰਾਬ ਨਾ ਕਰਨ। ਬਿੱਲ ਦਾ ਕੰਮ ਖਾਣਾ ਆਉਣ ਤੋਂ ਪਹਿਲਾਂ ਹੀ ਹੋ ਜਾਂਦਾ ਹੈ।",
      "hm.circles.how_step4_ttl": "ਲੈਅ ਬਣਾਈ ਰੱਖੋ",
      "hm.circles.how_step4_sub": "ਡਾਇਰੀ ਵਿੱਚ ਇੱਕ ਅਜਿਹੀ ਰਾਤ ਜਿਸ ਨੂੰ ਕਦੇ ਦੁਬਾਰਾ ਸੰਗਠਿਤ ਕਰਨ ਦੀ ਲੋੜ ਨਹੀਂ ਪੈਂਦੀ। ਗਰੁੱਪ ਮਿੰਟਾਂ ਵਿੱਚ ਨਹੀਂ, ਮਹੀਨਿਆਂ ਵਿੱਚ ਪੱਕਾ ਹੁੰਦਾ ਹੈ।",
      "hm.circles.cadence_weekly_ttl": "ਹਫ਼ਤਾਵਾਰੀ",
      "hm.circles.cadence_weekly_sub": "ਹਰ ਹਫ਼ਤੇ",
      "hm.circles.cadence_weekly_body": "ਉਨ੍ਹਾਂ ਸਾਥੀਆਂ ਲਈ ਜੋ ਮੰਗਲਵਾਰ ਦੀ ਰਾਤ ਦਾ ਰਿਵਾਜ ਚਾਹੁੰਦੇ ਹਨ — ਉਹੀ ਚਿਹਰੇ, ਹਰ ਵਾਰ ਨਵੀਂ ਜਗ੍ਹਾ।",
      "hm.circles.cadence_fortnight_ttl": "ਪੰਦਰਵਾੜੇ",
      "hm.circles.cadence_fortnight_sub": "ਹਰ 2 ਹਫ਼ਤਿਆਂ ਬਾਅਦ",
      "hm.circles.cadence_fortnight_body": "ਸਹੀ ਸੰਤੁਲਨ — ਰੁਝੇਵੇਂ ਭਰੀ ਜ਼ਿੰਦਗੀ ਵਿੱਚ ਬਹੁਤ ਜ਼ਿਆਦਾ ਦਬਾਅ ਪਾਏ ਬਿਨਾਂ ਨਿਯਮਤ।",
      "hm.circles.cadence_monthly_ttl": "ਮਹੀਨਾਵਾਰ",
      "hm.circles.cadence_monthly_sub": "ਹਰ ਮਹੀਨੇ",
      "hm.circles.cadence_monthly_body": "ਖਾਸ ਮੌਕਿਆਂ ਵਾਲੇ Circles — ਐਤਵਾਰ ਦੇ ਰੋਸਟ, ਵੱਡੀਆਂ ਟ੍ਰੀਵੀਆ ਨਾਈਟਸ, ਡਿਗਸਟੇਸ਼ਨ, ਪੋਕਰ।",
      "hm.circles.feast_kicker": "ਆਪਣੀ ਰਾਤ ਚੁਣੋ",
      "hm.circles.feast_title": "ਇਸ ਨੂੰ ਬਿਤਾਉਣ ਦੇ ਤਿੰਨ ਤਰੀਕੇ",
      "hm.circles.feast_sub": "ਹਰ Circle ਚੁਣਦਾ ਹੈ ਕਿ ਰਾਤ ਕਿਵੇਂ ਚੱਲੇਗੀ। ਉਨ੍ਹਾਂ ਨੂੰ ਬਦਲਦੇ ਰਹੋ, ਜਾਂ ਆਪਣੇ ਸਾਥੀਆਂ ਦੀ ਪਸੰਦ ਨੂੰ ਪੱਕਾ ਕਰੋ।",
      "hm.circles.feast_surprise_ttl": "ਨਵਾਂ ਰੈਸਟੋਰੈਂਟ ਅਜ਼ਮਾਓ",
      "hm.circles.feast_surprise_body": "ਅਸੀਂ ਤੁਹਾਡੇ ਨੇੜੇ ਦੀਆਂ ਤਿੰਨ ਜਗ੍ਹਾਵਾਂ ਦੀ ਸੂਚੀ ਬਣਾਉਂਦੇ ਹਾਂ, ਹਰ ਕੋਈ ਵੋਟ ਪਾਉਂਦਾ ਹੈ, ਰਸੋਈ ਪੂਰੇ ਟੇਬਲ ਲਈ ਤਿਆਰੀ ਕਰਦੀ ਹੈ। ਖਰਚੇ ਦੀ ਵੰਡ, ਪ੍ਰੀਪੇਡ ਸੀਟਾਂ।",
      "hm.circles.feast_preselect_ttl": "ਟ੍ਰੀਵੀਆ / ਕਾਰਡ / ਗੇਮਾਂ",
      "hm.circles.feast_preselect_body": "ਇੱਕ ਟ੍ਰੀਵੀਆ ਨਾਈਟ, ਪੋਕਰ ਰੂਮ, ਬੋਰਡ-ਗੇਮ ਬਾਰ ਜਾਂ ਬੌਲਿੰਗ ਲੇਨ ਬੁੱਕ ਕਰੋ। ਅਸੀਂ ਜਗ੍ਹਾ, ਐਂਟਰੀ ਫੀਸ ਅਤੇ ਰੀਮਾਈਂਡਰ ਸੰਭਾਲਦੇ ਹਾਂ।",
      "hm.circles.feast_per_event_ttl": "ਇੱਕ ਵਾਰੀ ਥੀਮਡ ਨਾਈਟ",
      "hm.circles.feast_per_event_body": "ਵਾਈਨ ਟੇਸਟਿੰਗ, ਸ਼ੈੱਫ ਟੇਬਲ, ਕਾਕਟੇਲ ਕਲਾਸ, ਐਸਕੇਪ ਰੂਮ। ਕਦੇ-ਕਦਾਈਂ ਕੁਝ ਖਾਸ — ਪੂਰੀ ਤਰ੍ਹਾਂ ਸੰਗਠਿਤ।",
      "hm.circles.live_kicker": "ਇਸ ਵੇਲੇ",
      "hm.circles.live_title": "Circles ਜਿਨ੍ਹਾਂ ਵਿੱਚ ਤੁਸੀਂ ਸ਼ਾਮਲ ਹੋ ਸਕਦੇ ਹੋ",
      "hm.circles.live_host_link": "ਆਪਣਾ ਖੁਦ ਦਾ ਸ਼ੁਰੂ ਕਰੋ",
      "hm.circles.live_loading": "ਲੋਡ ਹੋ ਰਿਹਾ ਹੈ…",
      "hm.circles.live_empty_title": "ਆਪਣੇ ਸ਼ਹਿਰ ਵਿੱਚ ਪਹਿਲੇ Circle ਬਣੋ।",
      "hm.circles.live_empty_sub": "ਤਿੰਨ ਸਾਥੀਆਂ ਨਾਲ ਇੱਕ ਸ਼ੁਰੂ ਕਰੋ ਅਤੇ ਅਸੀਂ ਬਾਕੀਆਂ ਨੂੰ ਸੱਦਾ ਦੇਣ ਵਿੱਚ ਮਦਦ ਕਰਾਂਗੇ — ਨਾਲ ਹੀ ਪਹਿਲੀਆਂ ਤਿੰਨ ਜਗ੍ਹਾਵਾਂ ਸਾਡੇ ਵੱਲੋਂ।",
      "hm.circles.live_empty_cta": "ਇੱਕ Circle ਸ਼ੁਰੂ ਕਰੋ",
      "hm.circles.host_kicker": "Circle ਲੀਡਰਾਂ ਲਈ",
      "hm.circles.host_title": "ਤੁਸੀਂ ਸਾਥੀਆਂ ਨੂੰ ਲਿਆਓ। ਅਸੀਂ ਐਡਮਿਨ ਦਾ ਕੰਮ ਕਰਾਂਗੇ।",
      "hm.circles.host_body": "ਜੇਕਰ ਤੁਸੀਂ ਉਹ ਦੋਸਤ ਹੋ ਜੋ ਹਮੇਸ਼ਾ ਰਾਤ ਦਾ ਪ੍ਰਬੰਧ ਕਰਦਾ ਹੈ, ਤਾਂ Circles ਤੁਹਾਡੇ ਘੰਟੇ ਬਚਾਉਂਦਾ ਹੈ। ਆਟੋ-ਰੀਮਾਈਂਡਰ, ਪ੍ਰੀਪੇਡ RSVP, ਜਗ੍ਹਾਵਾਂ ਦੀ ਸੂਚੀ, ਖਰਚੇ ਦੀ ਵੰਡ, ਵੇਟਲਿਸਟ ਪ੍ਰਬੰਧਨ ਅਤੇ ਹਰ ਰਾਤ ਦੇ ਖਾਣੇ 'ਤੇ ਕੈਸ਼ਬੈਕ ਵਾਲਿਟ। ਤੁਸੀਂ ਬੱਸ ਇੱਕ ਆਮ ਮੈਂਬਰ ਵਾਂਗ ਪਹੁੰਚੋ।",
      "hm.circles.host_perk_1": "ਆਟੋ ਵੇਨਿਊ ਲਿਸਟ — ਦੁਬਾਰਾ ਕਦੇ Google ਸਰਚ ਨਾ ਕਰੋ",
      "hm.circles.host_perk_2": "ਪ੍ਰੀਪੇਡ ਸੀਟਾਂ — ਅਚਾਨਕ ਗੈਰ-ਹਾਜ਼ਰੀ ਜ਼ੀਰੋ",
      "hm.circles.host_perk_3": "ਖਰਚੇ ਦੀ ਵੰਡ ਮਿੱਠੇ ਤੋਂ ਪਹਿਲਾਂ ਹੀ ਖਤਮ",
      "hm.circles.host_perk_4": "ਆਟੋਪਾਇਲਟ 'ਤੇ ਰੀਮਾਈਂਡਰ + ਕੈਲੰਡਰ ਸੱਦੇ",
      "hm.circles.host_perk_5": "ਵੇਟਲਿਸਟ ਅਤੇ ਕਿਸੇ ਦੇ ਕੈਂਸਲ ਕਰਨ 'ਤੇ ਆਟੋ-ਪ੍ਰੋਮੋਟ",
      "hm.circles.host_perk_6": "ਹਰ ਰਾਤ ਬਾਹਰ ਜਾਣ 'ਤੇ ਕੈਸ਼ਬੈਕ ਵਾਲਿਟ",
      "hm.circles.host_cta": "ਹੁਣੇ ਇੱਕ Circle ਸ਼ੁਰੂ ਕਰੋ",
      "hm.circles.why_kicker": "Circles ਕਿਉਂ ਕੰਮ ਕਰਦੇ ਹਨ",
      "hm.circles.why_title": "ਬਾਲਗ ਦੋਸਤੀ ਨੂੰ ਬੁਨਿਆਦੀ ਢਾਂਚੇ ਦੀ ਲੋੜ ਹੁੰਦੀ ਹੈ",
      "hm.circles.why_1_ttl": "ਉਹੀ ਸਾਥੀ, ਡੂੰਘਾ ਭਰੋਸਾ",
      "hm.circles.why_1_body": "ਜ਼ਿੰਦਗੀ ਦੋਸਤਾਂ ਨੂੰ ਦੂਰ ਕਰ ਦਿੰਦੀ ਹੈ। Circles ਇੱਕ ਅਜਿਹੀ ਪੱਕੀ ਲੈਅ ਹੈ ਜੋ ਸਾਰਿਆਂ ਨੂੰ ਨੇੜੇ ਰੱਖਦੀ ਹੈ — ਉਹੀ 4-12 ਸਾਥੀ, ਹਰ ਹਫਤੇ ਉਸੇ ਰਾਤ ਨੂੰ ਮਿਲਣਾ, ਭਾਵੇਂ ਕੁਝ ਵੀ ਹੋਵੇ।",
      "hm.circles.why_2_ttl": "ਐਡਮਿਨ ਦੀ ਕੋਈ ਥਕਾਵਟ ਨਹੀਂ",
      "hm.circles.why_2_body": "ਕਿਸੇ ਨੂੰ ਵੀ ਗਰੁੱਪ ਦਾ WhatsApp ਚੀਫ ਬਣਨ ਦੀ ਲੋੜ ਨਹੀਂ ਹੈ। ਅਸੀਂ ਜਗ੍ਹਾ ਚੁਣਦੇ ਹਾਂ, ਰੀਮਾਈਂਡਰ ਭੇਜਦੇ ਹਾਂ, ਭੁਗਤਾਨ ਲੈਂਦੇ ਹਾਂ ਅਤੇ ਗੱਲ ਸਿਰੇ ਚਾੜ੍ਹਦੇ ਹਾਂ।",
      "hm.circles.why_3_ttl": "ਨਵੀਂ ਜਗ੍ਹਾ, ਨਵੀਂ ਯਾਦ",
      "hm.circles.why_3_body": "Australia ਦੇ ਸਭ ਤੋਂ ਵਧੀਆ ਛੋਟੇ ਰੈਸਟੋਰੈਂਟਾਂ, ਟ੍ਰੀਵੀਆ ਰੂਮਾਂ ਅਤੇ ਗੇਮ ਬਾਰਾਂ ਦਾ ਆਨੰਦ ਲਓ। ਜਾਣੇ-ਪਛਾਣੇ ਚਿਹਰੇ, ਨਵੇਂ ਮਾਹੌਲ — ਰੁਟੀਨ ਅਤੇ ਨਵੀਨਤਾ ਦਾ ਸਹੀ ਸੁਮੇਲ।",
      "hm.circles.bottom_title": "ਉਹ ਰਾਤ ਜਿਸਦੀ ਤੁਹਾਡੇ ਸਾਥੀ ਉਡੀਕ ਕਰਦੇ ਹਨ, ਬਿਨਾਂ ਕਿਸੇ ਦੀ ਯੋਜਨਾ ਬਣਾਏ।",
      "hm.circles.bottom_sub": "ਇੱਕ Circle। ਉਹੀ ਸਾਥੀ। ਹਰ ਵਾਰ ਨਵੀਂ ਜਗ੍ਹਾ।",
      "hm.circles.bottom_cta_find": "ਇੱਕ Circle ਸ਼ੁਰੂ ਕਰੋ",
      "hm.circles.bottom_cta_mm": "Matchmaker ਨਾਲ ਨਵੇਂ ਲੋਕਾਂ ਨੂੰ ਮਿਲੋ →",
      "hm.circles.card_host_pill": "Circle",
      "hm.matchmaker.page_title": "HustleMeet Matchmaker · AI ਸਹੀ ਟੇਬਲ ਚੁਣਦਾ ਹੈ",
      "hm.matchmaker.kicker": "HustleMeet · Matchmaker",
      "hm.matchmaker.hero_title": "AI ਟੇਬਲ ਚੁਣਦਾ ਹੈ। ਤੁਸੀਂ ਬੱਸ ਪਹੁੰਚੋ।",
      "hm.matchmaker.hero_title_accent": "ਤੁਸੀਂ ਬੱਸ ਪਹੁੰਚੋ।",
      "hm.matchmaker.hero_sub": "ਸ਼ਖਸੀਅਤ ਅਤੇ ਕਦਰਾਂ-ਕੀਮਤਾਂ ਬਾਰੇ ਇੱਕ ਛੋਟਾ ਕੁਇਜ਼ ਦਿਓ। ਸਾਡਾ Matchmaker ਤੁਹਾਨੂੰ ਚਾਰ ਤੋਂ ਸੱਤ ਅਜਨਬੀਆਂ ਨਾਲ ਬਿਠਾਉਂਦਾ ਹੈ ਜੋ ਮਿੱਠੇ ਤੱਕ ਪਹੁੰਚਦਿਆਂ ਪੁਰਾਣੇ ਦੋਸਤਾਂ ਵਰਗੇ ਲੱਗਣਗੇ। ਕੋਈ ਸਵਾਈਪਿੰਗ ਨਹੀਂ। ਕੋਈ ਫਾਲਤੂ ਗੱਲਾਂ ਨਹੀਂ। ਬੱਸ ਇੱਕ ਸਾਂਝਾ ਟੇਬਲ, ਜਦੋਂ ਤੁਸੀਂ ਤਿਆਰ ਹੋਵੋ।",
      "hm.matchmaker.cta_quiz": "ਮੈਚਿੰਗ ਕੁਇਜ਼ ਲਓ",
      "hm.matchmaker.cta_see_circles": "ਇਸਦੀ ਬਜਾਏ Circles ਦੇਖੋ →",
      "hm.matchmaker.trust_big5": "Big-5 ਸ਼ਖਸੀਅਤ ਦਾ ਸਕੋਰ",
      "hm.matchmaker.trust_seats": "ਪ੍ਰਤੀ ਟੇਬਲ 4-8 ਸੀਟਾਂ",
      "hm.matchmaker.trust_lock": "ਜ਼ੀਰੋ-ਸਵਾਈਪ · ਜ਼ੀਰੋ-ਫੋਟੋ",
      "hm.matchmaker.trust_verified": "ਸਿਰਫ ਵੈਰੀਫਾਈਡ ਮੈਂਬਰ",
      "hm.matchmaker.how_kicker": "ਇਹ ਕਿਵੇਂ ਕੰਮ ਕਰਦਾ ਹੈ",
      "hm.matchmaker.how_title": "ਇੱਕ ਅਜਿਹੇ ਟੇਬਲ ਲਈ ਚਾਰ ਕਦਮ ਜੋ ਪਹਿਲਾਂ ਹੀ ਸਹੀ ਹੈ।",
      "hm.matchmaker.how_step1_ttl": "ਕੁਇਜ਼ ਲਓ",
      "hm.matchmaker.how_step1_sub": "ਦਸ મਿੰਟ ਦੀ ਪ੍ਰਸ਼ਨਾਵਲੀ — ਸ਼ਖਸੀਅਤ, ਕਦਰਾਂ-ਕੀਮਤਾਂ, ਖੁਰਾਕ, ਉਪਲਬਧਤਾ। ਤੁਸੀਂ ਇਹ ਸਿਰਫ ਇੱਕ ਵਾਰ ਕਰਦੇ ਹੋ।",
      "hm.matchmaker.how_step2_ttl": "ਰਾਤੋ-ਰਾਤ ਮੈਚਿੰਗ",
      "hm.matchmaker.how_step2_sub": "ਸਾਡਾ AI ਤੁਹਾਡੀ ਪ੍ਰੋਫਾਈਲ ਅਤੇ Australia ਦੇ ਲਾਈਵ ਡਿਨਰ ਪਲਾਨ ਪੜ੍ਹਦਾ ਹੈ। ਜਦੋਂ ਤੁਹਾਡਾ ਫਿੱਟ ਸਕੋਰ 0.70 ਜਾਂ ਵੱਧ ਹੁੰਦਾ ਹੈ, ਤਾਂ ਇਹ ਇੱਕ ਨਿੱਜੀ ਸੱਦਾ ਤਿਆਰ ਕਰਦਾ ਹੈ।",
      "hm.matchmaker.how_step3_ttl": "ਸਵੀਕਾਰ ਕਰੋ ਜਾਂ ਅਸਵੀਕਾਰ ਕਰੋ",
      "hm.matchmaker.how_step3_sub": "ਤੁਹਾਨੂੰ ਜਗ੍ਹਾ, ਟੇਬਲ ਦੇ ਥੀਮ ਅਤੇ ਗਰੁੱਪ ਨਾਲ ਸਾਂਝੀਆਂ ਤਿੰਨ ਦਿਲਚਸਪੀਆਂ ਦੇ ਨਾਲ ਇੱਕ ਸਾਫ਼ ਸੱਦਾ ਈਮੇਲ ਮਿਲਦੀ ਹੈ।",
      "hm.matchmaker.how_step4_ttl": "ਇਹ ਤੁਹਾਡੇ ਤੋਂ ਸਿੱਖਦਾ ਹੈ",
      "hm.matchmaker.how_step4_sub": "ਡਿਨਰ ਤੋਂ ਬਾਅਦ ਦਾ ਹਰ ਸਰਵੇਖਣ ਮਾਡਲ ਨੂੰ ਬਿਹਤਰ ਬਣਾਉਂਦਾ ਹੈ। ਜਿੰਨੀ ਵਾਰ ਤੁਸੀਂ ਖਾਣਾ ਖਾਂਦੇ ਹੋ, ਤੁਹਾਡਾ ਅਗਲਾ ਮੈਚ ਓਨਾ ਹੀ ਸਮਾਰਟ ਹੁੰਦਾ ਜਾਂਦਾ ਹੈ।",
      "hm.matchmaker.science_kicker": "ਇਹ ਕਿਉਂ ਕੰਮ ਕਰਦਾ ਹੈ",
      "hm.matchmaker.science_title": "Matchmaker ਅੰਦਾਜ਼ਾ ਨਹੀਂ ਲਗਾਉਂਦਾ — ਇਹ ਸਕੋਰ ਕਰਦਾ ਹੈ।",
      "hm.matchmaker.science_sub": "ਹਰ ਮੈਚ ਚਾਰ ਕਾਰਕਾਂ ਦਾ ਇੱਕ ਸੰਤੁਲਿਤ ਮਿਸ਼ਰਣ ਹੁੰਦਾ ਹੈ। ਜਦੋਂ ਪੂਰਾ ਟੇਬਲ ਔਸਤਨ 0.70 ਤੋਂ ਉੱਪਰ ਹੁੰਦਾ ਹੈ, ਅਸੀਂ ਸੱਦੇ ਭੇਜਦੇ ਹਾਂ। ਇਸ ਤੋਂ ਹੇਠਾਂ ਵਾਲੇ ਕਤਾਰ ਵਿੱਚ ਰਹਿੰਦੇ ਹਨ।",
      "hm.matchmaker.science_1_ttl": "ਸ਼ਖਸੀਅਤ (35%)",
      "hm.matchmaker.science_1_body": "Big-5 ਸ਼ਖਸੀਅਤ ਵੈਕਟਰ ਵਿੱਚ ਕੋਸਾਈਨ ਸਮਾਨਤਾ — ਖੁੱਲ੍ਹੇਪਨ, ਇਮਾਨਦਾਰੀ, ਮਿਲਣਸਾਰਤਾ, ਸਹਿਮਤੀ ਅਤੇ ਭਾਵਨਾਤਮਕ ਸਥਿਰਤਾ।",
      "hm.matchmaker.science_2_ttl": "ਕਦਰਾਂ-ਕੀਮਤਾਂ (25%)",
      "hm.matchmaker.science_2_body": "ਉਨ੍ਹਾਂ ਕਦਰਾਂ-ਕੀਮਤਾਂ ਦੀ ਸਾਂਝ ਜੋ ਡਿਨਰ ਦੀ ਗੱਲਬਾਤ ਨੂੰ ਅਸਲ ਬਣਾਉਂਦੀਆਂ ਹਨ — ਪਰਿਵਾਰ, ਉਤਸੁਕਤਾ, ਵਿਕਾਸ, ਸੇਵਾ, ਸਾਹਸ।",
      "hm.matchmaker.science_3_ttl": "ਦਿਲਚਸਪੀਆਂ (25%)",
      "hm.matchmaker.science_3_body": "ਤੁਹਾਡੀਆਂ ਖਾਸ ਦਿਲਚਸਪੀਆਂ 'ਤੇ ਜੈਕਾਰਡ ਓਵਰਲੈਪ — Six Foot Track 'ਤੇ ਹਾਈਕਿੰਗ, Sydney ਫਿਲਮ ਕਲੱਬ, Melbourne ਕੌਫੀ ਸੀਨ, ਸਾਲ ਦੀ ਤੁਹਾਡੀ ਤੀਜੀ ਕਿਤਾਬ।",
      "hm.matchmaker.science_4_ttl": "ਉਪਲਬਧਤਾ (15%)",
      "hm.matchmaker.science_4_body": "ਉਹ ਰਾਤ ਜਦੋਂ ਤੁਸੀਂ ਅਸਲ ਵਿੱਚ ਆ ਸਕਦੇ ਹੋ। ਮੰਗਲਵਾਰ ਸ਼ਾਮ 7 ਵਜੇ ਦਾ ਕੋਈ ਸੱਦਾ ਨਹੀਂ ਜੇਕਰ ਤੁਹਾਡਾ ਕੈਲੰਡਰ ਹਮੇਸ਼ਾ ਲਈ ਜਿਮ-ਮੰਗਲਵਾਰ ਕਹਿੰਦਾ ਹੈ।",
      "hm.matchmaker.safety_kicker": "ਡਿਜ਼ਾਈਨ ਦੁਆਰਾ ਸੁਰੱਖਿਆ",
      "hm.matchmaker.safety_title": "Matchmaker ਚੁੱਪ-ਚਾਪ ਸਾਵਧਾਨ ਰਹਿੰਦਾ ਹੈ।",
      "hm.matchmaker.safety_1_ttl": "ਭਰੋਸੇ ਦੀ ਸੀਮਾ",
      "hm.matchmaker.safety_1_body": "0.70 ਗਰੁੱਪ ਸਕੋਰ ਤੋਂ ਹੇਠਾਂ, ਕੋਈ ਸੱਦਾ ਨਹੀਂ ਭੇਜਿਆ ਜਾਂਦਾ। ਇੱਕ ਕਮਜ਼ੋਰ ਮੈਚ ਨਾਲੋਂ ਡਿਨਰ ਨਾ ਹੋਣਾ ਬਿਹਤਰ ਹੈ।",
      "hm.matchmaker.safety_2_ttl": "ਆਪਸੀ-ਨਾ ਦੀ ਬਲਾਕਲਿਸਟ",
      "hm.matchmaker.safety_2_body": "ਜੇਕਰ ਦੋ ਮੈਂਬਰ ਡਿਨਰ ਤੋਂ ਬਾਅਦ ਨਿੱਜੀ ਤੌਰ 'ਤੇ 'ਨਹੀਂ' ਦਾ ਸੰਕੇਤ ਦਿੰਦੇ ਹਨ, ਤਾਂ ਅਸੀਂ ਉਨ੍ਹਾਂ ਨੂੰ ਕਦੇ ਵੀ ਇਕੱਠੇ ਨਹੀਂ ਬਿਠਾਉਂਦੇ।",
      "hm.matchmaker.safety_3_ttl": "ਆਟੋ-ਕਿਲ ਸਵਿੱਚ",
      "hm.matchmaker.safety_3_body": "ਜੇਕਰ 20% ਜਾਂ ਇਸ ਤੋਂ ਵੱਧ ਹਾਲੀਆ ਸਰਵੇਖਣ ਕਿਸੇ ਟੇਬਲ ਨੂੰ 2/5 ਜਾਂ ਇਸ ਤੋਂ ਘੱਟ ਰੇਟ ਕਰਦੇ ਹਨ, ਤਾਂ ਸੱਦੇ 24 ਘੰਟਿਆਂ ਲਈ ਰੋਕ ਦਿੱਤੇ ਜਾਂਦੇ ਹਨ ਜਦੋਂ ਤੱਕ ਕੋਈ ਕਰਮਚਾਰੀ ਸਮੀਖਿਆ ਨਹੀਂ ਕਰਦਾ।",
      "hm.matchmaker.safety_4_ttl": "ਕਮਿਊਨਿਟੀ ਸਟੈਂਡਰਡ",
      "hm.matchmaker.safety_4_body": "ਹਰ ਮੈਂਬਰ ਇੱਕ ਸਪਸ਼ਟ ਆਚਾਰ ਸੰਹਿਤਾ ਨੂੰ ਸਵੀਕਾਰ ਕਰਦਾ ਹੈ। ਅਸੀਂ ਉਲੰਘਣਾ ਕਰਨ ਵਾਲੇ ਲੋਕਾਂ ਨੂੰ ਹਟਾ ਦਿੰਦੇ ਹਾਂ — ਕੋਈ ਅਪੀਲ ਨਹੀਂ।",
      "hm.matchmaker.safety_5_ttl": "ਸਿਰਫ ਜਨਤਕ ਸਥਾਨ",
      "hm.matchmaker.safety_5_body": "ਹਰ Matchmaker ਡਿਨਰ ਸਟਾਫ ਵਾਲੇ ਇੱਕ ਅਸਲ ਰੈਸਟੋਰੈਂਟ ਵਿੱਚ ਹੁੰਦਾ ਹੈ। ਕਦੇ ਵੀ ਕਿਸੇ ਦੇ ਘਰ ਨਹੀਂ।",
      "hm.matchmaker.safety_6_ttl": "ਇਵੈਂਟ ਤੋਂ ਬਾਅਦ ਫੀਡਬੈਕ",
      "hm.matchmaker.safety_6_body": "ਤੁਹਾਡਾ ਨਿੱਜੀ ਸਰਵੇਖਣ ਭਵਿੱਖ ਦੇ ਮੈਚਾਂ ਨੂੰ ਨਿਰਧਾਰਤ ਕਰਦਾ ਹੈ, ਅਤੇ ਜਗ੍ਹਾ ਜਾਂ ਕਿਸੇ ਮੈਂਬਰ ਦੀ ਸਮੀਖਿਆ ਲਈ ਪ੍ਰੇਰਿਤ ਕਰ ਸਕਦਾ ਹੈ।",
      "hm.matchmaker.loop_kicker": "ਸਿੱਖਣ ਦਾ ਚੱਕਰ",
      "hm.matchmaker.loop_title": "ਹਰ ਡਿਨਰ ਤੁਹਾਡੇ ਅਗਲੇ ਡਿਨਰ ਨੂੰ ਬਿਹਤਰ ਬਣਾਉਂਦਾ ਹੈ।",
      "hm.matchmaker.loop_body": "Matchmaker ਸਥਿਰ ਨਹੀਂ ਹੈ। ਹਰ ਇਵੈਂਟ ਤੋਂ ਬਾਅਦ ਤੁਸੀਂ ਇੱਕ ਛੋਟਾ ਨਿੱਜੀ ਸਰਵੇਖਣ ਜਮ੍ਹਾਂ ਕਰਦੇ ਹੋ — ਗੱਲਬਾਤ ਕਿਵੇਂ ਮਹਿਸੂਸ ਹੋਈ, ਤੁਸੀਂ ਕਿਸ ਨਾਲ ਦੁਬਾਰਾ ਖਾਣਾ ਖਾਣਾ ਚਾਹੋਗੇ, ਕੀ ਜਗ੍ਹਾ ਵਧੀਆ ਸੀ। ਉਹ ਸੰਕੇਤ ਮਾਡਲ ਨੂੰ ਫੀਡਬੈਕ ਦਿੰਦੇ ਹਨ ਅਤੇ ਹਰ ਭਵਿੱਖ ਦੇ ਮੈਚ ਨੂੰ ਨਵਾਂ ਰੂਪ ਦਿੰਦੇ ਹਨ।",
      "hm.matchmaker.loop_1": "ਸਾਥੀਆਂ ਦੀਆਂ \"ਦੁਬਾਰਾ ਡਿਨਰ\" ਵੋਟਾਂ ਤੁਹਾਡੀ ਜੋੜੀ ਨੂੰ ਮਜ਼ਬੂਤ ਕਰਦੀਆਂ ਹਨ",
      "hm.matchmaker.loop_2": "ਆਪਸੀ-ਨਾ ਦੀਆਂ ਵੋਟਾਂ ਚੁੱਪ-ਚਾਪ ਜੋੜਿਆਂ ਨੂੰ ਹਟਾ ਦਿੰਦੀਆਂ ਹਨ",
      "hm.matchmaker.loop_3": "ਜਗ੍ਹਾ ਅਤੇ ਗੱਲਬਾਤ ਦੀਆਂ ਰੇਟਿੰਗਾਂ ਸੀਟਾਂ ਦੇ ਮਿਸ਼ਰਣ ਨੂੰ ਠੀਕ ਕਰਦੀਆਂ ਹਨ",
      "hm.matchmaker.loop_4": "ਭਰੋਸੇਯੋਗਤਾ ਸਕੋਰ ਮੈਂਬਰਾਂ ਨੂੰ ਵਾਰ-ਵਾਰ ਗੈਰ-ਹਾਜ਼ਰ ਰਹਿਣ ਵਾਲਿਆਂ ਤੋਂ ਬਚਾਉਂਦਾ ਹੈ",
      "hm.matchmaker.loop_5": "ਸਿੱਖਣ ਦਾ ਤਰੀਕਾ ਮਹੀਨੇ ਦਰ ਮਹੀਨੇ ਚੰਗੇ ਮੈਚਾਂ ਨੂੰ ਮਜ਼ਬੂਤ ਕਰਦਾ ਹੈ",
      "hm.matchmaker.loop_6": "ਤੁਸੀਂ ਕੰਟਰੋਲ ਵਿੱਚ ਰਹਿੰਦੇ ਹੋ — ਕਿਸੇ ਵੀ ਸਮੇਂ ਆਪਣੀ ਪ੍ਰੋਫਾਈਲ ਨੂੰ ਹਟਾਓ ਜਾਂ ਰੀਸੈਟ ਕਰੋ",
      "hm.matchmaker.vs_kicker": "ਮੇਰੇ ਲਈ ਕਿਹੜਾ ਸਹੀ ਹੈ?",
      "hm.matchmaker.vs_title": "Matchmaker ਜਾਂ Circles?",
      "hm.matchmaker.vs_mm_ttl": "ਮੈਨੂੰ ਚੁਣੋ ਜੇਕਰ ਮੈਂ ਵੰਨ-ਸੁਵੰਨਤਾ ਚਾਹੁੰਦਾ ਹਾਂ।",
      "hm.matchmaker.vs_mm_1": "ਹਰ ਵਾਰ ਇੱਕ ਵੱਖਰਾ ਟੇਬਲ, ਸ਼ਹਿਰ, ਪਕਵਾਨ",
      "hm.matchmaker.vs_mm_2": "AI ਦੁਆਰਾ ਤਿਆਰ ਕੀਤਾ ਗਿਆ 4-8 ਅਨੁਕੂਲ ਅਜਨਬੀਆਂ ਦਾ ਗਰੁੱਪ",
      "hm.matchmaker.vs_mm_3": "ਹਫ਼ਤਾਵਾਰੀ ਲੈਅ, ਹੋਸਟ ਦੀ ਕੋਈ ਜ਼ਿੰਮੇਵਾਰੀ ਨਹੀਂ",
      "hm.matchmaker.vs_mm_4": "ਵਧੀਆ ਹੈ ਜੇਕਰ ਤੁਸੀਂ ਸ਼ਹਿਰ ਵਿੱਚ ਨਵੇਂ ਹੋ ਜਾਂ ਯਾਤਰਾ ਕਰ ਰਹੇ ਹੋ",
      "hm.matchmaker.vs_mm_cta": "ਮੈਚਿੰਗ ਕੁਇਜ਼ ਲਓ",
      "hm.matchmaker.vs_circles_ttl": "ਮੈਨੂੰ ਚੁਣੋ ਜੇਕਰ ਮੈਂ ਨਿਯਮਤ ਹੋਣਾ ਚਾਹੁੰਦਾ ਹਾਂ।",
      "hm.matchmaker.vs_circles_1": "ਉਹੀ ਹੋਸਟ · ਉਹੀ ਜਗ੍ਹਾ · ਹਫ਼ਤੇ ਦੀ ਉਹੀ ਰਾਤ",
      "hm.matchmaker.vs_circles_2": "ਇੱਕ ਗਰੁੱਪ ਜੋ ਤੁਹਾਡੀ ਬੁੱਧਵਾਰ ਦੀ ਪਰੰਪਰਾ ਬਣ ਜਾਂਦਾ ਹੈ",
      "hm.matchmaker.vs_circles_3": "ਤਜਰਬੇਕਾਰ ਹੋਸਟ ਦੁਆਰਾ ਇਨਸਾਨੀ ਤੌਰ 'ਤੇ ਤਿਆਰ ਕੀਤਾ ਗਿਆ",
      "hm.matchmaker.vs_circles_4": "ਵਧੀਆ ਹੈ ਜੇਕਰ ਤੁਸੀਂ ਹੁਣੇ ਹੀ ਆਏ ਹੋ ਅਤੇ ਪੱਕੇ ਰਿਸ਼ਤੇ ਚਾਹੁੰਦੇ ਹੋ",
      "hm.matchmaker.vs_circles_cta": "Circles ਦੇਖੋ",
      "hm.matchmaker.bottom_title": "ਇੱਕ ਕੁਇਜ਼। ਉਸ ਤੋਂ ਬਾਅਦ ਹਰ ਡਿਨਰ ਪਹਿਲਾਂ ਹੀ ਸਹੀ ਹੁੰਦਾ ਹੈ।",
      "hm.matchmaker.bottom_sub": "ਤੁਹਾਨੂੰ ਪੰਦਰਵਾੜੇ ਦੇ ਅੰਦਰ ਮੈਚ ਮਿਲ ਜਾਵੇਗਾ। ਇਹ ਇਸ ਮਹੀਨੇ ਦਾ ਸਭ ਤੋਂ ਸੌਖਾ \"ਹਾਂ\" ਹੋਵੇਗਾ।",
      "hm.matchmaker.bottom_cta": "ਮੈਚਿੰਗ ਕੁਇਜ਼ ਲਓ",
      "hm.matchmaker.bottom_note": "ਕੋਈ ਕ੍ਰੈਡਿਟ ਕਾਰਡ ਨਹੀਂ · 10 ਮਿੰਟ · ਕਿਸੇ ਵੀ ਸਮੇਂ ਅਨਮੈਚ ਕਰੋ",
      /* AUTO-TRANSLATED hm.* END */
    },
  },
  el: {
    translation: {
      nav_menu: "Μενού",
      nav_book: "Κράτηση τραπεζιού",
      nav_reviews: "Κριτικές",
      nav_about: "Σχετικά",
      nav_account: "Ο λογαριασμός μου",
      nav_signin: "Σύνδεση",
      cart_open: "Άνοιγμα καλαθιού",
      cart_title: "Η παραγγελία σας",
      cart_empty: "Το καλάθι είναι άδειο",
      cart_subtotal: "Μερικό σύνολο",
      cart_continue: "Συνέχεια",
      cart_pickup_date: "Ημερομηνία παραλαβής",
      cart_pickup_time: "Ώρα παραλαβής",
      cart_pay_on_pickup: "Πληρωμή στην παραλαβή",
      cart_pay_now: "Πληρωμή τώρα",
      cart_place_order: "Υποβολή παραγγελίας",
      add_to_order: "Προσθήκη",
      menu_search_ph: "Αναζήτηση στο μενού…",
      menu_chef_specials: "Προτάσεις σεφ",
      menu_under_kcal: "Κάτω από",
      menu_kcal_unit: "kcal",
      menu_clear_filters: "Καθαρισμός",
      menu_dietary_label: "Διατροφή",
      menu_no_matches: "Δεν βρέθηκαν αποτελέσματα",
      menu_relax_filters: "Δοκιμάστε άλλη αναζήτηση ή χαλαρώστε τα φίλτρα.",
      menu_clear_all: "Καθαρισμός όλων",
      book_title: "Κράτηση τραπεζιού",
      book_name: "Όνομα",
      book_email: "Email",
      book_phone: "Τηλέφωνο",
      book_guests: "Άτομα",
      book_date: "Ημερομηνία",
      book_time: "Ώρα",
      book_requests: "Ειδικά αιτήματα",
      book_submit: "Επιβεβαίωση κράτησης",
      footer_rights: "Όλα τα δικαιώματα διατηρούνται.",
      lang_label: "Γλώσσα",
      welcome_back: "Καλώς ήρθατε πάλι",
      sign_out: "Αποσύνδεση",
    
      /* AUTO-TRANSLATED finder.* (added) */
      "finder.title": "HustleFinder",
      "finder.tagline": "Βρείτε τον πλησιέστερο δημόσιο χώρο — πραγματικά δεδομένα, σε όλη την Αυστραλία.",
      "finder.search_placeholder": "Αναζήτηση μέρη, δρόμοι ή προάστια…",
      "finder.hint": "Αρχίστε να πληκτρολογείτε παραπάνω, πατήστε μια κατηγορία ή χρησιμοποιήστε «Κοντά μου».",
      "finder.near_me": "Κοντά μου",
      "finder.searching": "Αναζήτηση…",
      "finder.suburb.placeholder": "Φιλτράρισμα ανά προάστιο (π.χ. Darwin, NT)…",
      "finder.suburb.clear": "Εκκαθάριση",
      "finder.search_in": "Αναζήτηση σε {{cat}}",
      "finder.empty": "Δεν βρέθηκαν αποτελέσματα — δοκιμάστε άλλη κατηγορία ή όρο αναζήτησης.",
      "finder.meta.title": "HustleFinder — δωρεάν κατάλογος προαστίων Αυστραλίας",
      "finder.meta.desc": "Αναζητήστε τοπικές επιχειρήσεις, φορτιστές, καφέ, τουαλέτες και άλλα σε κάθε προάστιο της Αυστραλίας — 100% δωρεάν.",
      "finder.footer.credits": "Πιστώσεις δεδομένων & αναφορά",
      "finder.footer.terms": "Όροι",
      "finder.cat.toilet": "Τουαλέτες",
      "finder.cat.fountain": "Συντριβάνια Πόσιμου Νερού",
      "finder.cat.transport": "Μεταφορές",
      "finder.cat.park": "Πάρκα",
      "finder.cat.medical": "Ιατρικά",
      "finder.cat.school": "Σχολεία",
      "finder.cat.library": "Βιβλιοθήκες",
      "finder.cat.petrol": "Βενζινάδικα",
      "finder.cat.kidzone": "Παιδική Ζώνη",
      "finder.cat.puzzles": "Παζλ",
      "finder.cat.roadtrip": "Οδικό ταξίδι",
      "finder.hotels.title": "Βρείτε ένα ξενοδοχείο κοντά",
      "finder.hotels.sub": "Συγκρίνει 200+ ιστότοπους κρατήσεων σε μία αναζήτηση — με την υποστήριξη του Trivago.",
      "finder.hotels.where": "Πού",
      "finder.hotels.where_ph": "Πόλη ή προάστιο (π.χ. Byron Bay, NSW)",
      "finder.hotels.checkin": "Check-in",
      "finder.hotels.checkout": "Check-out",
      "finder.hotels.adults": "Ενήλικες",
      "finder.hotels.search": "Αναζήτηση ξενοδοχείων",
      "finder.hotels.disclosure": "Ανοίγει το Trivago — μπορεί να κερδίσουμε προμήθεια από κρατήσεις.",
      /* AUTO-TRANSLATED rt.* START */
      "rt.brand_name": "Roadtrip Planner",
      "rt.page_title": "Σχεδιαστής Διαδρομών EV Αυστραλίας",
      "rt.from": "Από",
      "rt.to": "Προς",
      "rt.from_placeholder": "Προάστιο, κωμόπολη ή πόλη (π.χ. Newcastle NSW)",
      "rt.to_placeholder": "Προάστιο, κωμόπολη ή πόλη (π.χ. Hobart TAS)",
      "rt.stop": "ΣΤΑΣΗ",
      "rt.stop_placeholder": "Ενδιάμεση στάση",
      "rt.add_stop": "Προσθήκη στάσης",
      "rt.upgrade_pro": "Αναβαθμίστε σε Pro για έως 20 στάσεις",
      "rt.reverse": "Αντιστροφή (υπολογισμός επιστροφής)",
      "rt.from_current": "Από την τρέχουσα τοποθεσία",
      "rt.range_per_charge": "Αυτονομία ανά φόρτιση (km)",
      "rt.connector": "Σύνδεσμος",
      "rt.connector_any": "Οποιοσδήποτε",
      "rt.drive_hours_day": "Ώρες οδήγησης/ημέρα",
      "rt.accommodation": "Διαμονή",
      "rt.accom_any": "Οποιοδήποτε (BnB · Ξενοδοχείο · Caravan)",
      "rt.accom_bnb": "BnB",
      "rt.accom_hotel": "Ξενοδοχείο",
      "rt.accom_caravan": "Caravan Park",
      "rt.min_rating": "Ελάχιστη βαθμολογία",
      "rt.any_rating": "Οποιαδήποτε βαθμολογία",
      "rt.only_5star": "5★ μόνο",
      "rt.schedule": "Πρόγραμμα:",
      "rt.depart_at": "Αναχώρηση στις",
      "rt.arrive_by": "Άφιξη έως",
      "rt.hotel_checkin": "Hotel check-in",
      "rt.hotel_checkout": "Hotel check-out",
      "rt.overnight_hint": "Τα νυχτερινά σκέλη δεν θα προγραμματιστούν εκτός αυτών των ωρών.",
      "rt.whos_in_car": "Ποιοι είναι στο αυτοκίνητο;",
      "rt.adults": "Ενήλικες",
      "rt.kids": "Παιδιά",
      "rt.ages": "Ηλικίες",
      "rt.show_attractions": "Εμφάνιση αξιοθέατων",
      "rt.show_kids": "Εμφάνιση στάσεων για παιδιά",
      "rt.overnight_stays": "Διανυκτερεύσεις",
      "rt.fast_chargers_only": "Μόνο ταχυφορτιστές",
      "rt.hotel_must_charger": "Το ξενοδοχείο πρέπει να έχει φορτιστή",
      "rt.pet_only": "Μόνο μέρη φιλικά προς κατοικίδια",
      "rt.safety": "Ασφάλεια",
      "rt.safety_90": "90% (επίπεδο έδαφος)",
      "rt.safety_85": "85% (προεπιλογή)",
      "rt.safety_80": "80% (λόφοι / ρυμούλκηση)",
      "rt.safety_70": "70% (βαρύ φορτίο)",
      "rt.save_trip": "Αποθήκευση ταξιδιού",
      "rt.plan_my_trip": "Σχεδιασμός ταξιδιού",
      "rt.refresh_trip": "Ανανέωση ταξιδιού",
      "rt.planning": "Σχεδιασμός…",
      "rt.resume_title": "Συνέχιση ταξιδιού από εδώ",
      "rt.resume_body": "Εντοπίσαμε την τρέχουσα τοποθεσία σας. Εισαγάγετε την υπολειπόμενη αυτονομία που εμφανίζεται στο ταμπλό σας, ώστε να σχεδιάσουμε το επόμενο σκέλος με βάση αυτό που πραγματικά έχετε — όχι μια πλήρη φόρτιση.",
      "rt.remaining_range": "Υπολειπόμενη αυτονομία (km)",
      "rt.cancel": "Ακύρωση",
      "rt.replan_from_here": "Επανασχεδιασμός από εδώ",
      "rt.save_modal_title": "Ονομάστε αυτό το ταξίδι",
      "rt.save_modal_body": "Τα αποθηκευμένα ταξίδια βρίσκονται στο προφίλ σας — ανοίξτε τα ανά πάσα στιγμή και πατήστε Επανασχεδιασμός.",
      "rt.save_name_placeholder": "π.χ. Brisbane → Sydney μέσω Byron",
      "rt.your_saved_trips": "Τα αποθηκευμένα ταξίδια σας",
      "rt.custom_location": "Προσαρμοσμένη τοποθεσία",
      "rt.trip_summary": "Περίληψη ταξιδιού",
      "rt.itinerary": "Δρομολόγιο",
      "rt.no_stops_needed": "Δεν χρειάζονται στάσεις — ολόκληρο το ταξίδι καλύπτεται από την αυτονομία σας.",
      "rt.chargers_in_corridor": "φορτιστές στη διαδρομή",
      "rt.planned_stops": "προγραμματισμένες στάσεις",
      "rt.overnight_stop": "διανυκτέρευση",
      "rt.overnight_stops": "διανυκτερεύσεις",
      "rt.suggested_detours": "Προτεινόμενες παρακάμψεις — επιλέξτε για προσθήκη",
      "rt.replan_with_detours": "Επανασχεδιασμός με παρακάμψεις",
      "rt.attractions_label": "Αξιοθέατο",
      "rt.kids_label": "Για παιδιά",
      "rt.km_from_start": "km από την αφετηρία",
      "rt.open_driving": "Άνοιγμα λειτουργίας οδήγησης",
      "rt.big_font_hint": "Μεγάλη γραμματοσειρά, ιδανική για βάση κινητού. Λειτουργεί με CarPlay/Android Auto μέσω αντικατοπτρισμού προγράμματος περιήγησης.",
      "rt.check_price_book": "Δείτε τιμή & κράτηση στο Trivago ↗",
      "rt.visit_site": "Επίσκεψη στον ιστότοπο ↗",
      "rt.charger_onsite": "Φορτιστής στον χώρο",
      "rt.leg_exceeds_range": "Αυτό το σκέλος υπερβαίνει την αυτονομία σας — η ενέργεια θα εξαντληθεί πριν φτάσετε σε αυτόν τον φορτιστή. Μειώστε την εκτιμώμενη αυτονομία ή προσθέστε μια ενδιάμεση φόρτιση.",
      "rt.add_waypoint_replan": "Προσθήκη ως σημείο διαδρομής & επανασχεδιασμός",
      /* AUTO-TRANSLATED rt.* END */
                      /* AUTO-TRANSLATED hm.* START */
      "hm.nav.hustlemeet": "HustleMeet",
      "hm.nav.circles": "Circles",
      "hm.nav.matchmaker": "Matchmaker",
      "hm.nav.meet": "Συναντήσεις",
      "hm.nav.overview": "HustleMeetups",
      "hm.overview.page_title": "HustleMeetups — τρεις ζεστοί τρόποι για να βρείτε τους ανθρώπους σας.",
      "hm.overview.kicker": "Μέρος του HustleSuite",
      "hm.overview.hero_title": "Τρεις ζεστοί τρόποι για να βρείτε τους ανθρώπους σας.",
      "hm.overview.hero_title_accent": "τους ανθρώπους σας",
      "hm.overview.hero_sub": "Το HustleMeetups είναι ο πιο ευγενικός τρόπος στην Australia για να χτίσετε πραγματικές φιλίες — επιλέξτε το vibe που σας ταιριάζει. Είτε θέλετε μια εβδομαδιαία ομάδα τρεξίματος, ένα μηνιαίο δείπνο με την υπάρχουσα παρέα σας, ή ένα τραπέζι με νέα πρόσωπα επιλεγμένα από AI, υπάρχει ένας τρόπος για εσάς.",
      "hm.overview.tile_meet_kicker": "HustleMeet",
      "hm.overview.tile_meet_title": "Βρείτε τους ανθρώπους σας. Κάντε αυτό που αγαπάτε.",
      "hm.overview.tile_meet_body": "Το σπίτι της Australia για ομάδες με βάση τα ενδιαφέροντα. Δρομείς, προγραμματιστές, επενδυτές, περιπατητές, χορευτές, λέσχες πρωινού, ομάδες βιβλίου — εκατοντάδες ομάδες, πραγματικά γεγονότα κάθε εβδομάδα. Εμφανιστείτε μία φορά, ή κάθε εβδομάδα.",
      "hm.overview.tile_meet_cta": "Εξερευνήστε ομάδες",
      "hm.overview.tile_circles_kicker": "Circles",
      "hm.overview.tile_circles_title": "Η παρέα σας. Ένα νέο τραπέζι κάθε φορά.",
      "hm.overview.tile_circles_body": "Φέρτε την παρέα σας. Εμείς κλείνουμε ένα νέο εστιατόριο, σχεδιάζουμε το trivia, μοιράζουμε τα χαρτιά. Εσείς απλά εμφανίζεστε. Ο πιο εύκολος τρόπος να κρατήσετε μια υπάρχουσα παρέα κοντά — χωρίς να χρειάζεται να το οργανώσει κανείς.",
      "hm.overview.tile_circles_cta": "Ξεκινήστε ένα Circle",
      "hm.overview.tile_matchmaker_kicker": "Matchmaker",
      "hm.overview.tile_matchmaker_title": "Το AI επιλέγει το τραπέζι. Εσείς απλά εμφανίζεστε.",
      "hm.overview.tile_matchmaker_body": "Κάντε ένα κουίζ 10 λεπτών. Μέσα σε ένα βράδυ, σας τοποθετούμε σε ένα τραπέζι με αγνώστους που θα νιώθετε σαν παλιούς φίλους μέχρι το επιδόρπιο. Διαφορετικό τραπέζι κάθε φορά.",
      "hm.overview.tile_matchmaker_cta": "Κάντε το κουίζ",
      "hm.overview.trust_kicker": "Γιατί λειτουργεί",
      "hm.overview.trust_title": "Πρώτα η εμπιστοσύνη. Μετά το τραπέζι.",
      "hm.overview.trust_1_title": "Πιστοποιημένα μέλη",
      "hm.overview.trust_1_body": "Κάθε ομάδα ξεκινά με έλεγχο πραγματικής ταυτότητας + βαθμολογία αξιοπιστίας. Όχι ψεύτικοι λογαριασμοί.",
      "hm.overview.trust_2_title": "Πραγματικοί οικοδεσπότες",
      "hm.overview.trust_2_body": "Κάθε ομάδα καθοδηγείται από έναν εγκεκριμένο οικοδεσπότη που ελέγχει το vibe, δίνει τον ρυθμό και φροντίζει τον χώρο.",
      "hm.overview.trust_3_title": "Προτεραιότητα στη φιλία",
      "hm.overview.trust_3_body": "Όλα εδώ είναι σχεδιασμένα για πραγματικές φιλίες, όχι για γνωριμίες (dating). Ποτέ.",
      "hm.overview.trust_4_title": "Μηδενική ανοχή στην κακή συμπεριφορά",
      "hm.overview.trust_4_body": "Μία αναφορά, μία αναθεώρηση, μία απόφαση. Οι κακόβουλοι χρήστες απομακρύνονται το ίδιο βράδυ.",
      "hm.overview.footer_line": "Μέρος του HustleSuite — Αυστραλιανής ιδιοκτησίας & φιλοξενίας.",
      "hm.landing.page_title": "HustleMeet · Βρείτε τους ανθρώπους σας. Κάντε αυτό που αγαπάτε.",
      "hm.landing.kicker": "Το δίκτυο ενδιαφερόντων της Australia",
      "hm.landing.hero_title": "Βρείτε τους ανθρώπους σας. Κάντε αυτό που αγαπάτε.",
      "hm.landing.hero_title_accent": "τους ανθρώπους σας",
      "hm.landing.hero_sub": "Το HustleMeet είναι το σπίτι της Australia για ομάδες με βάση τα ενδιαφέροντα. Προγραμματιστές λογισμικού που συζητούν πάνω από το μεσημεριανό. Δρομείς μεγάλων αποστάσεων στις 6 π.μ. Επενδυτές που συναντιούνται μηνιαίως. Ομάδες πεζοπορίας, χορευτικά γκρουπ, λέσχες πρωινού, βραδιές επιτραπέζιων. Επιλέξτε μια ομάδα, κλείστε θέση για το επόμενο event και ελάτε.",
      "hm.landing.cta_explore_circles": "Περιήγηση σε ομάδες",
      "hm.landing.cta_try_matchmaker": "Ξεκινήστε τη δική σας ομάδα",
      "hm.landing.trust_verified": "Πιστοποιημένα μέλη",
      "hm.landing.trust_venues": "Εγκεκριμένοι οικοδεσπότες εκδηλώσεων",
      "hm.landing.trust_friendship": "Προτεραιότητα στη φιλία — ποτέ dating",
      "hm.landing.trust_moderation": "Μηδενική ανοχή στην κακή συμπεριφορά",
      "hm.landing.subbrand_circles_kicker": "Circles",
      "hm.landing.subbrand_circles_title": "Έχετε ήδη την παρέα σας; Δώστε της ένα μόνιμο σπίτι.",
      "hm.landing.subbrand_circles_body": "Το Circles είναι για την παρέα φίλων που συναντιέται ήδη — λέσχη βιβλίου, βραδιά πόκερ, ομάδα τρεξίματος, φίλοι από τη δουλειά. Εμείς αναλαμβάνουμε τον χώρο, τις υπενθυμίσεις και το πρόγραμμα. Εσείς απλά εμφανίζεστε.",
      "hm.landing.subbrand_circles_b1": "Εναλλασσόμενοι χώροι, ένα σταθερό πρόγραμμα",
      "hm.landing.subbrand_circles_b2": "Αυτόματες υπενθυμίσεις + εύκολα RSVP",
      "hm.landing.subbrand_circles_b3": "Trivia, χαρτιά & θεματικές βραδιές στη διάθεσή σας",
      "hm.landing.subbrand_circles_cta": "Ξεκινήστε ένα Circle",
      "hm.landing.subbrand_matchmaker_kicker": "Matchmaker",
      "hm.landing.subbrand_matchmaker_title": "Το AI επιλέγει το τραπέζι. Εσείς απλά εμφανίζεστε.",
      "hm.landing.subbrand_matchmaker_body": "Ένα κουίζ 10 λεπτών — προσωπικότητα, αξίες, διαθεσιμότητα. Το Matchmaker μας σας τοποθετεί με αγνώστους που θα νιώθετε σαν παλιούς φίλους μέχρι το επιδόρπιο. Διαφορετικό τραπέζι κάθε φορά.",
      "hm.landing.subbrand_matchmaker_b1": "Αξιολόγηση προσωπικότητας Big-5 & αξιών",
      "hm.landing.subbrand_matchmaker_b2": "Προσκλήσεις εντός της νύχτας όταν το ταίριασμα είναι 0.70 ή υψηλότερο",
      "hm.landing.subbrand_matchmaker_b3": "Μαθαίνει από κάθε δείπνο που κάνετε",
      "hm.landing.subbrand_matchmaker_cta": "Κάντε το κουίζ",
      "hm.landing.how_kicker": "Πώς λειτουργεί το HustleMeet",
      "hm.landing.how_title": "Τέσσερα βήματα. Βρείτε την ομάδα σας. Εμφανιστείτε.",
      "hm.landing.how_step1_ttl": "Επιλέξτε τα ενδιαφέροντά σας",
      "hm.landing.how_step1_sub": "Τρέξιμο, προγραμματισμός, επενδύσεις, μαγειρική, μουσική, περπάτημα — πείτε μας τι σας αρέσει και πού μένετε.",
      "hm.landing.how_step2_ttl": "Περιηγηθείτε σε ομάδες κοντά σας",
      "hm.landing.how_step2_sub": "Κάθε ομάδα δημοσιεύει τον οικοδεσπότη της, τον ρυθμό της και την επόμενη εκδήλωσή της. Δείτε το κλίμα πριν δεσμευτείτε.",
      "hm.landing.how_step3_ttl": "Κλείστε θέση για την επόμενη εκδήλωση",
      "hm.landing.how_step3_sub": "Ένα πάτημα, μία πρόσκληση στο ημερολόγιο, μία καθαρή υπενθύμιση την προηγούμενη μέρα. Ορισμένες εκδηλώσεις είναι δωρεάν, άλλες επί πληρωμή — η τιμή αναγράφεται ξεκάθαρα.",
      "hm.landing.how_step4_ttl": "Εμφανιστείτε & συνδεθείτε",
      "hm.landing.how_step4_sub": "Γνωρίστε τον οικοδεσπότη, γνωρίστε την ομάδα, κάντε αυτό που αγαπάτε. Επιστρέψτε την επόμενη εβδομάδα αν σας άρεσε.",
      "hm.landing.stats_kicker": "Ο νέος τρόπος συνάντησης στην Australia",
      "hm.landing.stats_title": "Ο λόγος που ο κόσμος συνεχίζει να επιστρέφει.",
      "hm.landing.stats_92_sub": "Νιώθουν ότι ταίριαξαν με τουλάχιστον ένα άτομο στην εκδήλωση",
      "hm.landing.stats_68_sub": "Δηλώνουν συμμετοχή στην επόμενη εκδήλωσή τους μέσα σε μια εβδομάδα",
      "hm.landing.stats_85_sub": "Κρατούν επαφή με κάποιον από την ομάδα",
      "hm.landing.why_kicker": "Γιατί πετυχαίνουμε",
      "hm.landing.why_title": "Πραγματικά ενδιαφέροντα. Πραγματικοί οικοδεσπότες. Πραγματικές εκδηλώσεις.",
      "hm.landing.why_1_ttl": "Πρώτα το ενδιαφέρον, όχι η τοποθεσία",
      "hm.landing.why_1_body": "Οι άνθρωποι στην ομάδα σας νοιάζονται πραγματικά για αυτό που νοιάζεστε και εσείς — αυτός είναι ο σύντομος δρόμος για την πραγματική φιλία.",
      "hm.landing.why_2_ttl": "Πραγματικοί οικοδεσπότες εκδηλώσεων",
      "hm.landing.why_2_body": "Κάθε ομάδα έχει έναν εγκεκριμένο οικοδεσπότη που φροντίζει το vibe, ορίζει τον ρυθμό και παρεμβαίνει αν κάτι δεν πάει καλά. Όχι ένας διαχειριστής του Facebook. Ένας πραγματικός άνθρωπος.",
      "hm.landing.why_3_ttl": "Η συνέπεια έχει σημασία",
      "hm.landing.why_3_body": "Νέες εκδηλώσεις κάθε εβδομάδα. Κάτι για να περιμένετε με ανυπομονησία. Ένας ρυθμός που μετατρέπει τους ξένους στην παρέα σας για το βράδυ της Τρίτης.",
      "hm.landing.why_4_ttl": "Ασφάλεια & σιγουριά",
      "hm.landing.why_4_body": "Πιστοποιημένα μέλη, δημόσιοι χώροι, μηδενική ανοχή στην ασέβεια. Η ομάδα υποστήριξής μας είναι διαθέσιμη πριν, κατά τη διάρκεια και μετά από κάθε εκδήλωση.",
      "hm.landing.why_5_ttl": "Προτεραιότητα στη φιλία",
      "hm.landing.why_5_body": "Αυτό δεν είναι dating. Είναι κοινότητα. Πραγματικά ενδιαφέροντα, πραγματικοί άνθρωποι, χωρίς swiping ενδιάμεσα.",
      "hm.landing.why_6_ttl": "Αρκετά μικρές ομάδες για να έχουν σημασία",
      "hm.landing.why_6_body": "Οι περισσότερες ομάδες περιορίζουν τις εκδηλώσεις σε 4-15 θέσεις. Αρκετά μεγάλες για να πυροδοτήσουν πραγματικές συζητήσεις. Αρκετά μικρές ώστε να μην χάνεται κανείς.",
      "hm.landing.cities_kicker": "Πού δραστηριοποιούμαστε",
      "hm.landing.cities_title": "Ομάδες που συναντιούνται σε όλη την Australia & NZ.",
      "hm.landing.cities_sub_zero": "Οι πρώτες ομάδες ανοίγουν σε 15+ πόλεις — γίνετε μέλος νωρίς και διαμορφώστε τον χώρο.",
      "hm.landing.cities_sub_some": "Ομάδες ενεργές σε 15+ μητροπόλεις. Κάτι στην πόλη σας, αυτή την εβδομάδα.",
      "hm.landing.cities_sub_one": "{{count}} ενεργή ομάδα · νέες εκδηλώσεις κάθε εβδομάδα · προσθέτουμε συνεχώς πόλεις.",
      "hm.landing.cities_sub_many": "{{count}} ενεργές ομάδες · νέες εκδηλώσεις κάθε εβδομάδα · προσθέτουμε συνεχώς πόλεις.",
      "hm.landing.forme_kicker": "Αυτό είναι για εσάς αν…",
      "hm.landing.forme_title": "Θέλετε πραγματικά ενδιαφέροντα. Πραγματικούς ανθρώπους. Πραγματικές εκδηλώσεις.",
      "hm.landing.forme_1": "Έχετε κουραστεί από τις εφαρμογές γνωριμιών και θέλετε πραγματικά να γνωρίσετε ανθρώπους γύρω από αυτό που αγαπάτε να κάνετε.",
      "hm.landing.forme_2": "Μετακομίσατε σε μια νέα πόλη και χρειάζεστε μια ζεστή παρέα, όχι άλλη μια ομάδα συνομιλίας.",
      "hm.landing.forme_3": "Εκτιμάτε το βάθος περισσότερο από την προβολή — μια σπουδαία ομάδα κερδίζει πενήντα followers.",
      "hm.landing.forme_4": "Πιστεύετε ότι τα κοινά ενδιαφέροντα + ένα καθορισμένο δίωρο στην πραγματική ζωή είναι το πραγματικό μυστικό.",
      "hm.landing.notme_kicker": "Αυτό δεν είναι για εσάς αν…",
      "hm.landing.notme_title": "Το HustleMeet δεν λειτουργεί για όλους.",
      "hm.landing.notme_1": "Δεν μπορείτε να δεσμευτείτε ότι θα εμφανιστείτε — οι οικοδεσπότες και τα μέλη βασίζονται στη συμμετοχή σας.",
      "hm.landing.notme_2": "Ψάχνετε για υπηρεσία γνωριμιών (dating) — εδώ οι φιλίες έρχονται πρώτες.",
      "hm.landing.notme_3": "Θέλετε να προβάλλεστε χωρίς να συναντάτε κανέναν — αυτό λειτουργεί μόνο από κοντά.",
      "hm.landing.notme_4": "Περιμένετε ατελείωτα δωρεάν προνόμια — ορισμένες εκδηλώσεις έχουν κόστος. Η τιμή είναι πάντα εκ των προτέρων γνωστή.",
      "hm.landing.safety_kicker": "Η δέσμευσή μας για την ασφάλεια",
      "hm.landing.safety_title": "Φιλικά, αλλά ποτέ απρόσεκτα.",
      "hm.landing.safety_1_ttl": "Δημόσιοι χώροι",
      "hm.landing.safety_1_body": "Κάθε εκδήλωση πραγματοποιείται σε δημόσιο χώρο με προσωπικό — καφετέρια, εστιατόριο, πάρκο, γυμναστήριο ή στούντιο. Ποτέ σε σπίτι κάποιου, εκτός αν όλοι οι παρευρισκόμενοι συμφωνήσουν.",
      "hm.landing.safety_2_ttl": "Πρότυπα κοινότητας",
      "hm.landing.safety_2_body": "Μηδενική ανοχή στην ασέβεια, την παρενόχληση ή την ανειλικρίνεια. Τα μέλη που παραβιάζουν τους κανόνες απομακρύνονται αμέσως.",
      "hm.landing.safety_3_ttl": "Προστασία ιδιωτικότητας",
      "hm.landing.safety_3_body": "Τα στοιχεία σας δεν κοινοποιούνται ποτέ δημόσια. Εσείς ελέγχετε με ποιον θα παραμείνετε συνδεδεμένοι μετά από κάθε εκδήλωση.",
      "hm.landing.safety_4_ttl": "Υποστήριξη σε ετοιμότητα",
      "hm.landing.safety_4_body": "Η ομάδα μας είναι διαθέσιμη πριν, κατά τη διάρκεια και μετά από κάθε εκδήλωση αν έχετε ερωτήσεις ή ανησυχίες.",
      "hm.landing.bottom_title": "Η επόμενη αγαπημένη σας ομάδα είναι ένα πάτημα μακριά.",
      "hm.landing.bottom_sub": "Επιλέξτε τη διαδρομή σας — γίνετε μέλος μιας υπάρχουσας ομάδας, φέρτε την παρέα σας σε ένα Circle, ή αφήστε το Matchmaker να σας καθίσει σε ένα τραπέζι που σας ταιριάζει ήδη.",
      "hm.landing.bottom_cta_circles": "Ξεκινήστε ένα Circle",
      "hm.landing.bottom_cta_mm": "Κάντε το Matchmaker κουίζ",
      "hm.circles.page_title": "HustleMeet Circles · Η παρέα σας, νέος χώρος κάθε φορά",
      "hm.circles.kicker": "HustleMeet · Circles",
      "hm.circles.hero_title": "Η παρέα σας. Ένα νέο τραπέζι κάθε φορά.",
      "hm.circles.hero_title_accent": "Η παρέα σας",
      "hm.circles.hero_sub": "Έχετε ήδη τους φίλους σας; Το Circles είναι ο ευκολότερος τρόπος να τους κρατήσετε κοντά. Εμείς κλείνουμε τον χώρο, σχεδιάζουμε το trivia, μοιράζουμε τα χαρτιά. Εσείς απλά εμφανίζεστε — ίδια παρέα, νέα βραδιά, νέο μέρος.",
      "hm.circles.cta_find": "Ξεκινήστε ένα Circle",
      "hm.circles.cta_host_hero": "Γίνετε μέλος σε ένα ανοιχτό Circle",
      "hm.circles.trust_venue": "Εναλλασσόμενοι εγκεκριμένοι χώροι",
      "hm.circles.trust_party": "4-12 φίλοι ανά Circle",
      "hm.circles.trust_verified": "Πιστοποιημένα μέλη",
      "hm.circles.trust_friendship": "Πραγματικές φιλίες, που κρατούν",
      "hm.circles.how_kicker": "Πώς λειτουργεί",
      "hm.circles.how_title": "Τέσσερα βήματα. Η παρέα σας παραμένει παρέα.",
      "hm.circles.how_step1_ttl": "Ξεκινήστε το Circle σας",
      "hm.circles.how_step1_sub": "Ονομάστε το, προσκαλέστε τους φίλους σας (έως 12) και επιλέξτε έναν ρυθμό — εβδομαδιαία, ανά δεκαπενθήμερο ή μηνιαία.",
      "hm.circles.how_step2_ttl": "Εμείς επιλέγουμε τον χώρο",
      "hm.circles.how_step2_sub": "Σε κάθε εκδήλωση προτείνουμε ένα νέο σημείο — εστιατόριο, βραδιά trivia, αίθουσα χαρτιών, wine bar. Ή κλειδώστε το αγαπημένο σας.",
      "hm.circles.how_step3_ttl": "Όλοι δηλώνουν συμμετοχή με ένα πάτημα",
      "hm.circles.how_step3_sub": "Αυτόματες υπενθυμίσεις, διαχωρισμός κόστους, προπληρωμένες θέσεις, ώστε οι ακυρώσεις της τελευταίας στιγμής να μην χαλάνε το σχέδιο. Ο λογαριασμός είναι τακτοποιημένος πριν καν έρθει το ορεκτικό.",
      "hm.circles.how_step4_ttl": "Κρατήστε τον ρυθμό",
      "hm.circles.how_step4_sub": "Μια βραδιά στο ημερολόγιο που δεν χρειάζεται να οργανωθεί ποτέ ξανά. Η ομάδα δένεται με τους μήνες, όχι με τα λεπτά.",
      "hm.circles.cadence_weekly_ttl": "Εβδομαδιαία",
      "hm.circles.cadence_weekly_sub": "Κάθε εβδομάδα",
      "hm.circles.cadence_weekly_body": "Για παρέες που θέλουν μια ιεροτελεστία το βράδυ της Τρίτης — ίδια πρόσωπα, νέο μέρος κάθε φορά.",
      "hm.circles.cadence_fortnight_ttl": "Ανά δεκαπενθήμερο",
      "hm.circles.cadence_fortnight_sub": "Κάθε 2 εβδομάδες",
      "hm.circles.cadence_fortnight_body": "Η χρυσή τομή — τακτικά χωρίς να επιβαρύνει τα πολυάσχολα προγράμματα.",
      "hm.circles.cadence_monthly_ttl": "Μηνιαία",
      "hm.circles.cadence_monthly_sub": "Κάθε μήνα",
      "hm.circles.cadence_monthly_body": "Circles για ειδικές περιστάσεις — κυριακάτικα ψητά, μεγάλες βραδιές trivia, γευσιγνωσία, πόκερ.",
      "hm.circles.feast_kicker": "Επιλέξτε τη βραδιά σας",
      "hm.circles.feast_title": "Τρεις τρόποι για να την περάσετε",
      "hm.circles.feast_sub": "Κάθε Circle επιλέγει πώς θα κυλήσει η βραδιά. Εναλλάξτε τους τρόπους, ή κλειδώστε τον αγαπημένο της παρέας σας.",
      "hm.circles.feast_surprise_ttl": "Δοκιμάστε ένα νέο εστιατόριο",
      "hm.circles.feast_surprise_body": "Επιλέγουμε τρεις χώρους κοντά σας, όλοι ψηφίζουν, η κουζίνα προετοιμάζεται για ένα γεμάτο τραπέζι. Διαχωρισμός κόστους, προπληρωμένες θέσεις.",
      "hm.circles.feast_preselect_ttl": "Trivia / χαρτιά / παιχνίδια",
      "hm.circles.feast_preselect_body": "Κλείστε μια βραδιά trivia, μια αίθουσα πόκερ, ένα μπαρ με επιτραπέζια ή μια λωρίδα μπόουλινγκ. Εμείς αναλαμβάνουμε τον χώρο, τη συμμετοχή και τις υπενθυμίσεις.",
      "hm.circles.feast_per_event_ttl": "Εφάπαξ θεματική βραδιά",
      "hm.circles.feast_per_event_body": "Γευσιγνωσία κρασιού, chef's table, μάθημα κοκτέιλ, escape room. Κάτι ιδιαίτερο μια στο τόσο — πλήρως οργανωμένο.",
      "hm.circles.live_kicker": "Αυτή τη στιγμή",
      "hm.circles.live_title": "Circles στα οποία μπορείτε να συμμετάσχετε",
      "hm.circles.live_host_link": "Ξεκινήστε το δικό σας",
      "hm.circles.live_loading": "Φόρτωση…",
      "hm.circles.live_empty_title": "Γίνετε το πρώτο Circle στην πόλη σας.",
      "hm.circles.live_empty_sub": "Ξεκινήστε ένα με τρεις φίλους και θα σας βοηθήσουμε να προσκαλέσετε τους υπόλοιπους — συν δώρο οι τρεις πρώτοι χώροι από εμάς.",
      "hm.circles.live_empty_cta": "Ξεκινήστε ένα Circle",
      "hm.circles.host_kicker": "Για τους ηγέτες των Circles",
      "hm.circles.host_title": "Εσείς φέρνετε την παρέα. Εμείς αναλαμβάνουμε τη διαχείριση.",
      "hm.circles.host_body": "Αν είστε ο φίλος που πάντα οργανώνει τη βραδιά, το Circles σας γλιτώνει ώρες. Αυτόματες υπενθυμίσεις, προπληρωμένα RSVP, λίστες χώρων, διαχωρισμός κόστους, διαχείριση λίστας αναμονής και ένα πορτοφόλι cashback για κάθε έξοδο. Εσείς απλά εμφανίζεστε ως κανονικό μέλος.",
      "hm.circles.host_perk_1": "Αυτόματες προτάσεις χώρων — μη ψάχνετε άλλο στο Google",
      "hm.circles.host_perk_2": "Προπληρωμένες θέσεις — μηδενικές ακυρώσεις",
      "hm.circles.host_perk_3": "Ο διαχωρισμός κόστους γίνεται πριν το επιδόρπιο",
      "hm.circles.host_perk_4": "Υπενθυμίσεις + προσκλήσεις ημερολογίου στον αυτόματο πιλότο",
      "hm.circles.host_perk_5": "Λίστα αναμονής με αυτόματη προώθηση αν κάποιος ακυρώσει",
      "hm.circles.host_perk_6": "Πορτοφόλι cashback σε κάθε έξοδο",
      "hm.circles.host_cta": "Ξεκινήστε ένα Circle τώρα",
      "hm.circles.why_kicker": "Γιατί τα Circles λειτουργούν",
      "hm.circles.why_title": "Οι φιλίες των ενηλίκων χρειάζονται υποδομές",
      "hm.circles.why_1_ttl": "Ίδια παρέα, βαθύτερη εμπιστοσύνη",
      "hm.circles.why_1_body": "Η ζωή απομακρύνει τους φίλους. Τα Circles είναι ο σταθερός ρυθμός που κρατά τους πάντες κοντά — οι ίδιοι 4-12 φίλοι, που συναντιούνται την ίδια καθημερινή βραδιά, ό,τι κι αν γίνει.",
      "hm.circles.why_2_ttl": "Μηδενική κούραση διαχείρισης",
      "hm.circles.why_2_body": "Κανείς δεν χρειάζεται να είναι ο αρχηγός του γκρουπ στο WhatsApp. Εμείς επιλέγουμε τους χώρους, στέλνουμε τις υπενθυμίσεις, παίρνουμε τις πληρωμές, ολοκληρώνουμε τη διαδικασία.",
      "hm.circles.why_3_ttl": "Νέος χώρος, νέα ανάμνηση",
      "hm.circles.why_3_body": "Εναλλάξτε τα καλύτερα μικρά εστιατόρια, αίθουσες trivia και μπαρ παιχνιδιών της Australia. Γνώριμα πρόσωπα, φρέσκα σκηνικά — η χρυσή τομή ρουτίνας και ανανέωσης.",
      "hm.circles.bottom_title": "Η βραδιά που η παρέα σας περιμένει με ανυπομονησία, χωρίς να χρειάζεται να τη σχεδιάσει κανείς.",
      "hm.circles.bottom_sub": "Ένα Circle. Ίδιοι φίλοι. Νέος χώρος κάθε φορά.",
      "hm.circles.bottom_cta_find": "Ξεκινήστε ένα Circle",
      "hm.circles.bottom_cta_mm": "Γνωρίστε νέους ανθρώπους με το Matchmaker →",
      "hm.circles.card_host_pill": "Circle",
      "hm.matchmaker.page_title": "HustleMeet Matchmaker · Το AI επιλέγει το σωστό τραπέζι",
      "hm.matchmaker.kicker": "HustleMeet · Matchmaker",
      "hm.matchmaker.hero_title": "Το AI επιλέγει το τραπέζι. Εσείς απλά εμφανίζεστε.",
      "hm.matchmaker.hero_title_accent": "Εσείς απλά εμφανίζεστε.",
      "hm.matchmaker.hero_sub": "Απαντήστε σε ένα σύντομο κουίζ προσωπικότητας και αξιών. Το Matchmaker μας σας τοποθετεί με τέσσερις έως επτά αγνώστους που θα νιώθετε σαν παλιούς φίλους μέχρι το επιδόρπιο. Χωρίς swiping. Χωρίς τυπικές συζητήσεις. Μόνο ένα κοινό τραπέζι, έτοιμο όταν είστε κι εσείς.",
      "hm.matchmaker.cta_quiz": "Κάντε το κουίζ αντιστοίχισης",
      "hm.matchmaker.cta_see_circles": "Δείτε τα Circles αντί για αυτό →",
      "hm.matchmaker.trust_big5": "Αξιολόγηση προσωπικότητας Big-5",
      "hm.matchmaker.trust_seats": "4-8 θέσεις ανά τραπέζι",
      "hm.matchmaker.trust_lock": "Χωρίς swiping · χωρίς φωτογραφίες",
      "hm.matchmaker.trust_verified": "Μόνο πιστοποιημένα μέλη",
      "hm.matchmaker.how_kicker": "Πώς λειτουργεί",
      "hm.matchmaker.how_title": "Τέσσερα βήματα για ένα τραπέζι που σας ταιριάζει ήδη.",
      "hm.matchmaker.how_step1_ttl": "Κάντε το κουίζ",
      "hm.matchmaker.how_step1_sub": "Ερωτηματολόγιο δέκα λεπτών — προσωπικότητα, αξίες, διατροφικές προτιμήσεις, διαθεσιμότητα. Το κάνετε μία φορά.",
      "hm.matchmaker.how_step2_ttl": "Αντιστοίχιση εντός της νύχτας",
      "hm.matchmaker.how_step2_sub": "Το AI μας διαβάζει το προφίλ σας και το τρέχον πρόγραμμα δείπνων στην Australia. Όταν η βαθμολογία ταιριάσματος είναι 0.70 ή υψηλότερη, δημιουργεί μια προσωπική πρόσκληση.",
      "hm.matchmaker.how_step3_ttl": "Αποδοχή ή απόρριψη",
      "hm.matchmaker.how_step3_sub": "Λαμβάνετε ένα καθαρό email πρόσκλησης με τον χώρο, το θέμα του τραπεζιού και τα τρία ενδιαφέροντα που μοιράζεστε με την ομάδα.",
      "hm.matchmaker.how_step4_ttl": "Μαθαίνει από εσάς",
      "hm.matchmaker.how_step4_sub": "Κάθε έρευνα μετά το δείπνο βελτιώνει το μοντέλο. Το επόμενο ταίριασμά σας γίνεται εξυπνότερο όσο περισσότερο δειπνείτε.",
      "hm.matchmaker.science_kicker": "Γιατί λειτουργεί",
      "hm.matchmaker.science_title": "Το Matchmaker δεν μαντεύει — βαθμολογεί.",
      "hm.matchmaker.science_sub": "Κάθε ταίριασμα είναι μια σταθμισμένη σύνθεση τεσσάρων παραγόντων. Όταν ο μέσος όρος όλου του τραπεζιού είναι πάνω από 0.70, στέλνουμε τις προσκλήσεις. Οτιδήποτε κάτω από αυτό παραμένει στην ουρά.",
      "hm.matchmaker.science_1_ttl": "Προσωπικότητα (35%)",
      "hm.matchmaker.science_1_body": "Ομοιότητα συνημιτόνου στο διάνυσμα προσωπικότητας Big-5 — δεκτικότητα, ευσυνειδησία, εξωστρέφεια, συνεργατικότητα και συναισθηματική σταθερότητα.",
      "hm.matchmaker.science_2_ttl": "Αξίες (25%)",
      "hm.matchmaker.science_2_body": "Επικάλυψη στις αξίες που κάνουν μια συζήτηση στο δείπνο να πετυχαίνει — οικογένεια, περιέργεια, ανάπτυξη, προσφορά, περιπέτεια.",
      "hm.matchmaker.science_3_ttl": "Ενδιαφέροντα (25%)",
      "hm.matchmaker.science_3_body": "Επικάλυψη Jaccard στα συγκεκριμένα ενδιαφέροντά σας — πεζοπορία στο Six Foot Track, κινηματογραφική λέσχη στο Sydney, σκηνή καφέ στη Melbourne, το τρίτο βιβλίο σας για φέτος.",
      "hm.matchmaker.science_4_ttl": "Διαθεσιμότητα (15%)",
      "hm.matchmaker.science_4_body": "Η βραδιά που μπορείτε πραγματικά να παρευρεθείτε. Καμία πρόσκληση για Τρίτη στις 7 μ.μ. αν το ημερολόγιό σας λέει γυμναστήριο-κάθε-Τρίτη.",
      "hm.matchmaker.safety_kicker": "Ασφάλεια εκ σχεδιασμού",
      "hm.matchmaker.safety_title": "Το Matchmaker είναι αθόρυβα προσεκτικό.",
      "hm.matchmaker.safety_1_ttl": "Όριο εμπιστοσύνης",
      "hm.matchmaker.safety_1_body": "Κάτω από μια ομαδική βαθμολογία 0.70, καμία πρόσκληση δεν αποστέλλεται. Ένα αμφίβολο ταίριασμα είναι χειρότερο από το καθόλου δείπνο.",
      "hm.matchmaker.safety_2_ttl": "Λίστα αμοιβαίου αποκλεισμού",
      "hm.matchmaker.safety_2_body": "Εάν δύο μέλη δηλώσουν ιδιωτικά «όχι» μετά από ένα δείπνο, δεν τους τοποθετούμε ποτέ ξανά μαζί.",
      "hm.matchmaker.safety_3_ttl": "Αυτόματος διακόπτης παύσης",
      "hm.matchmaker.safety_3_body": "Εάν το 20% ή περισσότερο των πρόσφατων ερευνών βαθμολογήσουν ένα τραπέζι με 2/5 ή λιγότερο, οι προσκλήσεις παύουν για 24 ώρες ενώ ένας διαχειριστής εξετάζει την κατάσταση.",
      "hm.matchmaker.safety_4_ttl": "Πρότυπα κοινότητας",
      "hm.matchmaker.safety_4_body": "Κάθε μέλος αποδέχεται έναν κώδικα δεοντολογίας σε απλά ελληνικά. Απομακρύνουμε όσους τον παραβιάζουν — χωρίς δικαίωμα έφεσης.",
      "hm.matchmaker.safety_5_ttl": "Μόνο δημόσιοι χώροι",
      "hm.matchmaker.safety_5_body": "Κάθε δείπνο Matchmaker πραγματοποιείται σε πραγματικό εστιατόριο με προσωπικό. Ποτέ σε σπίτι κάποιου.",
      "hm.matchmaker.safety_6_ttl": "Σχόλια μετά την εκδήλωση",
      "hm.matchmaker.safety_6_body": "Η ιδιωτική σας έρευνα διαμορφώνει μελλοντικά ταιριάσματα και μπορεί να προκαλέσει έλεγχο του χώρου ή ενός συμμετέχοντα από διαχειριστή.",
      "hm.matchmaker.loop_kicker": "Ο βρόχος μάθησης",
      "hm.matchmaker.loop_title": "Κάθε δείπνο κάνει το επόμενο δείπνο σας καλύτερο.",
      "hm.matchmaker.loop_body": "Το Matchmaker δεν είναι στατικό. Μετά από κάθε εκδήλωση υποβάλλετε μια σύντομη ιδιωτική έρευνα — πώς ήταν η συζήτηση, με ποιον θα δειπνούσατε ξανά, αν ο χώρος ήταν αντάξιος των προσδοκιών. Αυτά τα σήματα τροφοδοτούν το μοντέλο και αναδιαμορφώνουν κάθε μελλοντικό ταίριασμα.",
      "hm.matchmaker.loop_1": "Οι ψήφοι «δείπνο ξανά» από συνομηλίκους ενισχύουν τα ταιριάσματά σας",
      "hm.matchmaker.loop_2": "Οι αμοιβαίες αρνητικές ψήφοι αποσύρουν σιωπηλά τα ζευγάρια",
      "hm.matchmaker.loop_3": "Οι αξιολογήσεις χώρου & συζήτησης βελτιώνουν τη σύνθεση των θέσεων",
      "hm.matchmaker.loop_4": "Η βαθμολογία αξιοπιστίας προστατεύει τα μέλη από επαναλαμβανόμενες ακυρώσεις",
      "hm.matchmaker.loop_5": "Η μεροληψία μάθησης ενισχύει τα καλά ταιριάσματα μήνα με τον μήνα",
      "hm.matchmaker.loop_6": "Παραμένετε σε έλεγχο — εξαιρεθείτε ή επαναφέρετε το προφίλ σας ανά πάσα στιγμή",
      "hm.matchmaker.vs_kicker": "Ποιο είναι το σωστό για μένα;",
      "hm.matchmaker.vs_title": "Matchmaker ή Circles;",
      "hm.matchmaker.vs_mm_ttl": "Επιλέξτε εμένα αν θέλω ποικιλία.",
      "hm.matchmaker.vs_mm_1": "Διαφορετικό τραπέζι, πόλη, κουζίνα κάθε φορά",
      "hm.matchmaker.vs_mm_2": "Επιμελημένη από AI ομάδα 4-8 συμβατών αγνώστων",
      "hm.matchmaker.vs_mm_3": "Εβδομαδιαίος ρυθμός, χωρίς δέσμευση οικοδεσπότη",
      "hm.matchmaker.vs_mm_4": "Ιδανικό αν είστε νέοι σε μια πόλη ή ταξιδεύετε",
      "hm.matchmaker.vs_mm_cta": "Κάντε το κουίζ αντιστοίχισης",
      "hm.matchmaker.vs_circles_ttl": "Επιλέξτε εμένα αν θέλω κάτι σταθερό.",
      "hm.matchmaker.vs_circles_1": "Ίδιος οικοδεσπότης · ίδιος χώρος · ίδια καθημερινή βραδιά",
      "hm.matchmaker.vs_circles_2": "Μια ομάδα που μετατρέπεται στην παράδοσή σας για την Τετάρτη",
      "hm.matchmaker.vs_circles_3": "Επιμέλεια από άνθρωπο, έναν έμπειρο οικοδεσπότη",
      "hm.matchmaker.vs_circles_4": "Ιδανικό αν μόλις μετακομίσατε και θέλετε να ριζώσετε",
      "hm.matchmaker.vs_circles_cta": "Περιήγηση στα Circles",
      "hm.matchmaker.bottom_title": "Ένα κουίζ. Κάθε δείπνο μετά από αυτό σας ταιριάζει ήδη.",
      "hm.matchmaker.bottom_sub": "Θα ταιριάξετε μέσα σε ένα δεκαπενθήμερο. Είναι το πιο εύκολο «ναι» που θα πείτε αυτόν τον μήνα.",
      "hm.matchmaker.bottom_cta": "Κάντε το κουίζ αντιστοίχισης",
      "hm.matchmaker.bottom_note": "Χωρίς πιστωτική κάρτα · 10 λεπτά · Διαγραφή ανά πάσα στιγμή",
      /* AUTO-TRANSLATED hm.* END */
    },
  },
  hi: {
    translation: {
      /* AUTO-TRANSLATED hi START */
      plaza_nav_features: "विशेषताएं",
      plaza_nav_rewards: "रिवॉर्ड्स",
      plaza_nav_how: "यह कैसे काम करता है",
      plaza_nav_roadmap: "रोडमैप",
      plaza_nav_faq: "FAQ",
      plaza_hero_badge: "ऑस्ट्रेलियाई स्वामित्व",
      plaza_hero_kicker: "एक कार्ट। हर रिवॉर्ड।",
      plaza_hero_title_1: "ऑस्ट्रेलियाई खरीदें।",
      plaza_hero_title_2: "हर जगह कमाएं।",
      plaza_hero_sub: "एक ही टोकरी में हजारों ऑस्ट्रेलियाई विक्रेता। आप जो भी डॉलर खर्च करते हैं, वह HustlePoints अर्जित करता है — जिसे रेस्तरां, कैफे, होटल, हेयरड्रेसर, पालतू ग्रूमर और HustleSuite नेटवर्क के किसी भी व्यवसाय में रिडीम किया जा सकता है।",
      plaza_cta_start: "मुफ्त में जुड़ें — 30 सेकंड लगते हैं",
      plaza_cta_signin: "साइन इन करें",
      plaza_cta_shop: "स्टोर ब्राउज़ करें",
      plaza_hero_stat_1_v: "12,847",
      plaza_hero_stat_1_l: "उत्पाद लाइव",
      plaza_hero_stat_2_v: "340+",
      plaza_hero_stat_2_l: "ऑस्ट्रेलियाई विक्रेता",
      plaza_hero_stat_3_v: "$4.2M",
      plaza_hero_stat_3_l: "रिवॉर्ड्स भुगतान",
      plaza_hero_stat_4_v: "19",
      plaza_hero_stat_4_l: "श्रेणियां",
      plaza_problem_kicker: "आज की शॉपिंग के साथ समस्या",
      plaza_problem_title: "दस टैब। दस पासवर्ड। शून्य वफादारी।",
      plaza_problem_sub: "आप पहले से ही Amazon, eBay, Kogan, Kmart, Woolies, Coles, Bunnings, JB, Officeworks और आधा दर्जन बुटीक स्टोर्स के बीच तालमेल बिठा रहे हैं। आप घंटों कीमतों की तुलना करने में बिताते हैं — फिर एक रसीद के अलावा कुछ नहीं मिलता।",
      plaza_problem_1: "कीमतें जो साइट्स के बीच तुलना नहीं कर सकते",
      plaza_problem_2: "विदेशी विक्रेता स्थानीय होने का नाटक कर रहे हैं",
      plaza_problem_3: "लॉयल्टी पॉइंट्स एक ही स्टोर में फंसे हुए",
      plaza_problem_4: "बॉट्स और ड्रॉप-शिपर्स से नकली रिव्यू",
      plaza_solution_kicker: "HustlePlaza अलग तरीके से क्या करता है",
      plaza_solution_title: "आखिरी कार्ट जो आप पूरे दिन खोलेंगे।",
      plaza_solution_sub: "एक खोज। एक कार्ट। एक चेकआउट। हर खरीदारी आपको HustlePoints में वापस देती है जो हमारे पूरे इकोसिस्टम में खर्च होते हैं — केवल इस दुकान में नहीं।",
      plaza_solution_1_t: "एक कार्ट, हर विक्रेता",
      plaza_solution_1_b: "प्लेटफॉर्म पर हर ऑस्ट्रेलियाई विक्रेता से खरीदें और चेकआउट पर एक बार भुगतान करें। जहां संभव हो डिलीवरी को समेकित किया जाता है।",
      plaza_solution_2_t: "केवल सत्यापित स्थानीय",
      plaza_solution_2_b: "हर विक्रेता को लिस्ट करने से पहले पहचान-सत्यापित किया जाता है। कोई बॉट नहीं, कोई नकली नहीं, कोई ड्रॉप-शिपिंग रहस्य नहीं।",
      plaza_solution_3_t: "रिवॉर्ड्स जो वास्तव में यात्रा करते हैं",
      plaza_solution_3_b: "यहां अर्जित पॉइंट्स किसी भी HustleSuite व्यवसाय में खर्च होते हैं — कैफे, होटल, हेयरड्रेसर, रेस्तरां, पालतू ग्रूमर।",
      plaza_solution_4_t: "लाइव ट्रैकिंग, एक स्क्रीन",
      plaza_solution_4_b: "हर विक्रेता से हर पार्सल को एक पेज पर ट्रैक करें। हर कदम पर ईमेल और SMS द्वारा सूचनाएं।",
      plaza_rewards_kicker: "HustlePoints फ्लाईव्हील",
      plaza_rewards_title: "खरीदारी करते समय कमाएं। नेटवर्क में कहीं भी खर्च करें।",
      plaza_rewards_sub: "HustlePlaza पर खर्च किया गया हर $1 HustlePoints अर्जित करता है। उन्हें किसी भी रेस्तरां, कैफे, होटल, हेयरड्रेसर, या सेवा व्यवसाय पर रिडीम करें जो HustleSuite पर चलता है — नेटवर्क में कहीं भी।",
      plaza_rewards_step_1: "मार्केटप्लेस में खरीदारी करें",
      plaza_rewards_step_1_b: "टी-शर्ट से लेकर फ्रिज तक कुछ भी खरीदें।",
      plaza_rewards_step_2: "तुरंत HustlePoints अर्जित करें",
      plaza_rewards_step_2_b: "जैसे ही आपका ऑर्डर शिप होता है पॉइंट्स दिखाई देते हैं — कोई कार्ड नहीं, कोई पंच कार्ड नहीं, कोई समाप्ति गेम नहीं।",
      plaza_rewards_step_3: "नेटवर्क में खर्च करें",
      plaza_rewards_step_3_b: "कैफे, रेस्तरां, होटल, हेयरड्रेसर, पालतू ग्रूमर, ट्रेडीज़ पर रिडीम करें।",
      plaza_rewards_step_4: "या कैश आउट करें",
      plaza_rewards_step_4_b: "एक बार जब आप $50 पार कर लें तो बैंक ट्रांसफर में बदलें।",
      plaza_cats_kicker: "श्रेणी के अनुसार खरीदें",
      plaza_cats_title: "19 श्रेणियां। ऑस्ट्रेलियाई विक्रेता। लाइव स्टॉक।",
      plaza_cats_sub: "अभी लाइव चीजों का एक टुकड़ा ब्राउज़ करें — या साइन अप करें और पूर्ण 12,000+ उत्पाद कैटलॉग खरीदें।",
      plaza_cats_cta: "सभी श्रेणियां देखें",
      plaza_vendors_kicker: "वास्तविक ऑस्ट्रेलियाई विक्रेता",
      plaza_vendors_title: "निर्माताओं, उत्पादकों और पारिवारिक दुकानों से मिलें।",
      plaza_vendors_sub: "HustlePlaza पर हर विक्रेता ABN-सत्यापित है, ऑस्ट्रेलिया में स्थित है, और व्यक्तिगत रूप से ऑनबोर्ड किया गया है। कोई विदेशी पासथ्रू स्थानीय होने का नाटक नहीं कर रहे।",
      plaza_how_kicker: "यह कैसे काम करता है",
      plaza_how_title: "तीन कदम। एक कार्ट। हमेशा के लिए रिवॉर्ड्स।",
      plaza_how_1_title: "30 सेकंड में जुड़ें",
      plaza_how_1_body: "अपना ईमेल सत्यापित करें और तुरंत ब्राउज़ करें। जब आप चेकआउट के लिए तैयार हों तो फोन नंबर जोड़ें।",
      plaza_how_2_title: "अपनी कार्ट भरें",
      plaza_how_2_body: "हर विक्रेता से खरीदारी करें। एक कार्ट, एक डिलीवरी व्यू, एक चेकआउट। Stripe या PayPal के माध्यम से भुगतान।",
      plaza_how_3_title: "कहीं भी कमाएं और खर्च करें",
      plaza_how_3_body: "आपके ऑर्डर के शिप होते ही HustlePoints दिखाई देते हैं। उन्हें किसी भी HustleSuite व्यवसाय पर खर्च करें, या $50 पर कैश आउट करें।",
      plaza_compare_kicker: "हम कैसे खड़े हैं",
      plaza_compare_title: "HustlePlaza बनाम दिग्गज",
      plaza_compare_col_us: "HustlePlaza",
      plaza_compare_col_amz: "Amazon AU",
      plaza_compare_col_ebay: "eBay AU",
      plaza_compare_col_kmart: "Kmart / Big W",
      plaza_compare_row_1: "ऑस्ट्रेलियाई-स्वामित्व",
      plaza_compare_row_2: "हर विक्रेता के लिए एकल कार्ट",
      plaza_compare_row_3: "इस स्टोर के बाहर खर्च होने योग्य रिवॉर्ड्स",
      plaza_compare_row_4: "आपके बैंक में रिवॉर्ड्स कैश-आउट",
      plaza_compare_row_5: "ऑस्ट्रेलियाई-होस्टेड ग्राहक सहायता",
      plaza_trust_kicker: "हम क्यों सत्यापित करते हैं",
      plaza_trust_title: "वास्तविक ग्राहक। वास्तविक रिव्यू।",
      plaza_trust_body: "हम साइन-अप पर आपका ईमेल सत्यापित करते हैं, चेकआउट से पहले आपका मोबाइल नंबर, और उच्च-मूल्य की खरीदारी करने, रिव्यू पोस्ट करने या विक्रेताओं से चैट करने से पहले लाइव फोटो चेक की आवश्यकता होती है। यह धोखेबाजों को धीमा करता है और आपके लिए कीमतें कम रखता है।",
      plaza_trust_1: "शामिल होने के लिए ईमेल सत्यापन",
      plaza_trust_2: "चेकआउट के लिए SMS सत्यापन",
      plaza_trust_3: "रिव्यू और चैट के लिए लाइव फोटो सत्यापन",
      plaza_trust_4: "ऑनबोर्डिंग पर विक्रेता पहचान जांच",
      plaza_test_kicker: "हमारे शुरुआती सदस्य क्या कहते हैं",
      plaza_test_title: "सीधे वास्तविक ग्राहकों से।",
      plaza_test_1_body: "मैंने अपने HustlePlaza पॉइंट्स का उपयोग काम के पास एक कैफे में रात के खाने के लिए भुगतान करने के लिए किया। वास्तव में विश्वास नहीं हुआ कि यह काम करेगा जब तक वेट्रेस ने मुस्कुराते हुए कहा 'आप पूरी तरह से तैयार हैं'।",
      plaza_test_1_name: "प्रिया एस.",
      plaza_test_2_body: "छह विक्रेता, एक डिलीवरी विंडो, एक पुष्टिकरण ईमेल। मेरे पास आमतौर पर छह पासवर्ड और छह ट्रैकिंग नंबर होते। यह बस काम करता है।",
      plaza_test_2_name: "मार्कस टी.",
      plaza_test_3_body: "एक छोटे शहद उत्पादक के रूप में हमें आखिरकार उसी दिन भुगतान, वास्तविक रिव्यू, और कोई 40% कट नहीं मिलता। HustlePlaza ने मुझे आठ महीनों में 'साइड हसल' से फुल-टाइम तक पहुंचाया।",
      plaza_test_3_name: "केट और डेव",
      plaza_faq_kicker: "उत्तर",
      plaza_faq_title: "वे सवाल जो हमें हर हफ्ते मिलते हैं",
      plaza_faq_1_q: "क्या HustlePlaza वास्तव में ऑस्ट्रेलियाई-स्वामित्व है?",
      plaza_faq_1_a: "हां। HustlePlaza 100% ऑस्ट्रेलियाई-स्वामित्व है, ऑस्ट्रेलियाई सर्वरों पर होस्ट किया गया है, एक ऑस्ट्रेलियाई टीम द्वारा चलाया जाता है। हर विक्रेता अपनी पहली लिस्टिंग लाइव होने से पहले पहचान सत्यापन से गुजरता है।",
      plaza_faq_2_q: "रिवॉर्ड्स कैसे काम करते हैं?",
      plaza_faq_2_a: "HustlePlaza पर आपके द्वारा खर्च किया गया हर डॉलर आपको HustlePoints अर्जित कराता है। आपके ऑर्डर के शिप होते ही पॉइंट्स दिखाई देते हैं और कभी समाप्त नहीं होते। उन्हें किसी भी व्यवसाय पर खर्च करें जो HustleSuite पर चलता है, या एक बार जब आप पर्याप्त बचा लें तो उन्हें अपने बैंक में कैश आउट करें।",
      plaza_faq_3_q: "आप फोटो क्यों सत्यापित करते हैं?",
      plaza_faq_3_a: "ताकि आप जो रिव्यू पढ़ें वे असली हों। फोटो सत्यापन एक लाइव-सेल्फी चेक है जो आपके फोन कैमरे के माध्यम से किया जाता है — वही तकनीक जो बैंक उपयोग करते हैं — और यह बॉट्स, नकली खातों और शत्रुतापूर्ण अभिनेताओं को प्लेटफॉर्म में गेमिंग से रोकता है।",
      plaza_faq_4_q: "यदि मेरा ऑर्डर नहीं आता है तो क्या होगा?",
      plaza_faq_4_a: "हर ऑर्डर हमारी HustleGuarantee द्वारा समर्थित है: यदि पार्सल नहीं आता या जैसा वर्णित नहीं है, हम 3 व्यावसायिक दिनों के भीतर आपको रिफंड करते हैं, फिर विक्रेता का पीछा करते हैं। आप कभी भी जेब से बाहर नहीं रहते।",
      plaza_faq_5_q: "क्या मेरा कार्ड जानकारी सुरक्षित है?",
      plaza_faq_5_a: "हम आपका कार्ड नंबर कभी नहीं देखते या स्टोर नहीं करते। सभी भुगतान Stripe (PCI-DSS Level 1) या PayPal के माध्यम से चलते हैं, दोनों 3D Secure authentication के साथ जो आपको चोरी किए गए कार्ड पर धोखाधड़ी शुल्क से बचाता है।",
      plaza_faq_6_q: "क्या आप अंतरराष्ट्रीय स्तर पर शिप करते हैं?",
      plaza_faq_6_a: "अभी नहीं। HustlePlaza लॉन्च पर केवल ऑस्ट्रेलिया है — इसी तरह हम अधिकांश वस्तुओं पर समान-सप्ताह डिलीवरी और आपके समय क्षेत्र में सहायता का वादा कर सकते हैं। NZ 2026 रोडमैप पर है।",
      plaza_faq_7_q: "शामिल होने में कितना खर्च आता है?",
      plaza_faq_7_a: "कुछ नहीं। सदस्यता हमेशा के लिए मुफ्त है। विक्रेता प्रति बिक्री एक छोटा कमीशन देते हैं — खरीदारों को शामिल होने के लिए $0, कमाने के लिए $0, रिडीम करने के लिए $0 भुगतान करना पड़ता है।",
      plaza_faq_8_q: "क्या मैं HustlePlaza पर बेच सकता हूं?",
      plaza_faq_8_a: "बिल्कुल। विक्रेता के रूप में ऑनबोर्ड होने के लिए hustlesuite.au पर जाएं — सत्यापित ऑस्ट्रेलियाई व्यवसायों के लिए समान-दिन अनुमोदन, साथ ही आपके पहले 30 दिन कमीशन-मुक्त।",
      plaza_final_kicker: "संस्थापक मूल्य — सीमित सीटें",
      plaza_final_title: "पहले लोगों में से एक बनें — संस्थापक मूल्य में लॉक करें।",
      plaza_final_sub: "आज HustleSuite सदस्य के रूप में पंजीकरण करें। जब हर नई सुविधा शिप होगी तो आप पहले जानने वाले होंगे, और आपका खाता जीवन भर के लिए संस्थापक-स्तर के रिवॉर्ड्स और मूल्य निर्धारण बनाए रखेगा।",
      plaza_final_bonus: "संस्थापक कभी अपनी संस्थापक दर नहीं खोते। हमेशा के लिए मुफ्त, किसी भी समय रद्द करें।",
      plaza_final_cta: "संस्थापक सदस्य के रूप में पंजीकरण करें",
      plaza_final_disclosure: "कोई कार्ड आवश्यक नहीं। केवल ईमेल + मोबाइल सत्यापन।",
      plaza_roadmap_kicker: "हम आगे क्या बना रहे हैं",
      plaza_roadmap_title: "छह उपकरण जो आपकी प्लेट से काम उतारने के लिए बनाए गए हैं।",
      plaza_roadmap_sub: "HustleSuite केवल एक दुकान से अधिक है — यह एक पूर्ण उत्पादकता परत है। यहां बताया गया है कि रनवे पर क्या है।",
      plaza_roadmap_soon: "जल्द ही शिपिंग",
      plaza_roadmap_next: "कतार में अगला",
      plaza_roadmap_1_t: "HustleDictate — किसी भी फॉर्म पर स्पीच-टू-टेक्स्ट",
      plaza_roadmap_1_b: "Chrome एक्सटेंशन। Alt+M दबाएं, बोलें, और किसी भी वेबसाइट पर कोई भी इनपुट खुद भर जाता है। रियल एस्टेट आवेदन, चालान, NDIS कागजी कार्रवाई — आधे समय में पूरा हो गया।",
      plaza_roadmap_2_t: "HustleConnect Edit-Anywhere",
      plaza_roadmap_2_b: "WordPress मालिकों के लिए: एक हॉटकी के साथ किसी भी पेज से अपनी लाइव साइट संपादित करें। अब wp-admin राउंड ट्रिप्स नहीं।",
      plaza_roadmap_3_t: "Save-to-HustleSuite यूनिवर्सल बटन",
      plaza_roadmap_3_b: "किसी भी वेबपेज पर राइट-क्लिक करें — प्रॉपर्टी लिस्टिंग, लेख, उत्पाद, रेस्तरां — और यह आपके एक्शन के लिए तैयार सही मॉड्यूल में उतरता है।",
      plaza_roadmap_4_t: "HustlePlaza price-checker",
      plaza_roadmap_4_b: "Amazon, eBay या Kmart ब्राउज़ करें — एक फ्लोटिंग चिप आपके रिवॉर्ड्स पूर्वावलोकन के साथ HustlePlaza पर समान उत्पाद दिखाता है।",
      plaza_roadmap_5_t: "रिव्यू हार्वेस्टर",
      plaza_roadmap_5_b: "नए Google, Facebook और TripAdvisor रिव्यू को अपने HustleSuite डैशबोर्ड में ऑटो-सिंक करें। प्रति सप्ताह ~20 मिनट बचाता है।",
      plaza_roadmap_6_t: "HustleSuite Sidebar",
      plaza_roadmap_6_b: "लाइव ऑर्डर, KPI, क्विक-पोस्ट और क्विक-इनवॉइस के साथ Chrome साइड-पैनल। हर टैब के बगल में एक पट्टी में आपका पूरा व्यवसाय।",
      plaza_footer_owned: "ऑस्ट्रेलियाई स्वामित्व और होस्टेड",
      plaza_footer_terms: "शर्तें",
      plaza_footer_privacy: "गोपनीयता",
      plaza_footer_contact: "संपर्क",
      plaza_footer_vendors: "HustlePlaza पर बेचें",
      plaza_footer_help: "सहायता केंद्र",
      plaza_footer_tagline: "HustleSuite परिवार का हिस्सा — एक लॉगिन, हर सेवा।",
      verify_gate_title: "लगभग तैयार — त्वरित साइन-अप",
      verify_gate_body: "HustlePlaza एक शॉपिंग कार्ट है जो कई लाभ प्रदान करता है जिसमें रिडीम योग्य रिवॉर्ड्स शामिल हैं जो न केवल इस प्लेटफॉर्म के भीतर बल्कि HustlePlaza प्लेटफॉर्म का उपयोग करने वाले किसी भी अन्य व्यवसाय या HustleSuite के साथ पंजीकृत किसी अन्य व्यवसाय के साथ उपयोग किए जा सकते हैं। इनमें से कुछ व्यवसायों में रेस्तरां, कैफे, होटल, हेयरड्रेसर, और यहां तक कि पालतू ग्रूमिंग भी शामिल होगी। महान कीमतों और सेवाओं की पेशकश जारी रखने के लिए, हमें यह सुनिश्चित करने की आवश्यकता है कि आप वास्तविक हैं और आपको सत्यापित करें। जारी रखने के लिए Join बटन दबाएं।",
      verify_gate_join: "HustleSuite में शामिल हों",
      verify_gate_signin: "मेरे पास पहले से एक खाता है",
      verify_gate_cancel: "अभी नहीं",
      verify_email_title: "अपना ईमेल सत्यापित करें",
      verify_email_body: "हम आपके इनबॉक्स में 6-अंकों का कोड भेजेंगे।",
      verify_email_placeholder: "you@example.com",
      verify_email_send: "कोड भेजें",
      verify_email_code_ph: "6-अंकों का कोड",
      verify_email_check: "सत्यापित करें",
      verify_email_sent: "कोड भेजा गया — अपना इनबॉक्स जांचें।",
      verify_sms_title: "अपना फोन सत्यापित करें",
      verify_sms_body: "एक मोबाइल नंबर जोड़ें ताकि हम आपको ऑर्डर अपडेट टेक्स्ट कर सकें और बड़ी खरीदारी की पुष्टि कर सकें।",
      verify_sms_placeholder: "+61412345678",
      verify_sms_send: "SMS भेजें",
      verify_sms_check: "सत्यापित करें",
      verify_sms_sent: "कोड भेजा गया — अपना फोन जांचें।",
      verify_photo_title: "फोटो सत्यापन",
      verify_photo_body: "लाइव सेल्फी लेने के लिए अपने फोन से QR कोड स्कैन करें। चैट, उच्च-मूल्य की खरीदारी, और Meetup / Tackle में पोस्ट करने के लिए आवश्यक।",
      verify_photo_waiting: "आपके फोन की प्रतीक्षा कर रहे हैं…",
      verify_photo_verified: "फोटो सत्यापित — आप पूरी तरह से तैयार हैं।",
      travel_cta_start: "मुफ्त में जुड़ें",
      travel_hero_kicker: "HustleTravel",
      travel_hero_title_1: "एक सदस्यता।",
      travel_hero_title_2: "हर जगह ऑस्ट्रेलिया।",
      travel_hero_sub: "होटल, फ्लाइट्स, अनुभव, रोड-ट्रिप योजना और कार किराए पर — सर्वोत्तम उपलब्ध दर पर बुक किया गया और खर्च किए गए हर डॉलर पर आपको HustlePoints का भुगतान कर रहा है।",
      travel_cta_plan: "एक रोड ट्रिप की योजना बनाएं",
      travel_cta_hotels: "होटल खोजें",
      travel_svc_hotels_t: "होटल और अपार्टमेंट",
      travel_svc_hotels_b: "एक ही स्थान पर 200+ बुकिंग साइटों की तुलना करें — Trivago-संचालित मेटासर्च।",
      travel_svc_flights_t: "फ्लाइट्स",
      travel_svc_flights_b: "घरेलू और अंतरराष्ट्रीय, रियल-टाइम सीट मैप और किराया नियमों के साथ।",
      travel_svc_experiences_t: "अनुभव और टूर",
      travel_svc_experiences_b: "रीफ दिवस, सेलर-डोर टेस्टिंग, लाइव संगीत, बच्चों के अनुकूल भ्रमण।",
      travel_svc_cars_t: "कार किराया",
      travel_svc_cars_b: "हर प्रमुख ब्रांड और स्थानीय स्वतंत्र — किसी भी ऑस्ट्रेलियाई हवाई अड्डे पर पिकअप।",
      travel_svc_insurance_t: "यात्रा बीमा",
      travel_svc_insurance_b: "APRA-विनियमित ऑस्ट्रेलियाई बीमाकर्ताओं द्वारा अंडररिटन। एक स्क्रीन में तुलना करें।",
      travel_svc_roadtrip_t: "रोड-ट्रिप प्लानर",
      travel_svc_roadtrip_b: "चार्जर, रात भर के ठहराव और लागत अनुमानों के साथ EV रेंज-जागरूक यात्रा कार्यक्रम।",
      travel_final_kicker: "संस्थापक मूल्य",
      travel_final_title: "जीवन भर के लिए संस्थापक-स्तरीय रिवॉर्ड्स में लॉक करें।",
      travel_final_sub: "एक बार HustleSuite के साथ पंजीकरण करें। सब कुछ बुक करें — होटल, फ्लाइट्स, अनुभव, बीमा — HustleTravel पर और HustlePoints अर्जित करें जो नेटवर्क में हर व्यवसाय पर खर्च किए जा सकते हैं।",
      travel_final_cta: "संस्थापक सदस्य के रूप में पंजीकरण करें",
      travel_final_disclosure: "सर्वोत्तम दर नीति: यात्री के लिए सर्वोत्तम मूल्य के अनुसार परिणामों को आदेशित किया गया, कमीशन नहीं।",
      dictate_hero_kicker: "HustleDictate",
      dictate_hero_title_1: "बोलें।",
      dictate_hero_title_2: "कोई भी फॉर्म। कोई भी वेबसाइट।",
      dictate_hero_sub: "एक Chrome एक्सटेंशन जो किसी भी टेक्स्ट फ़ील्ड को एक पेशेवर-ग्रेड वॉइस डिक्टेशन माइक्रोफोन में बदल देता है — Whisper द्वारा संचालित।",
      dictate_cta_install: "Chrome में जोड़ें",
      dictate_uc_re_t: "रियल-एस्टेट किरायेदारी नोट्स",
      dictate_uc_re_b: "निरीक्षण रिपोर्ट और किरायेदार संदेशों को सीधे अपने PM सिस्टम में डिक्टेट करें।",
      dictate_uc_ndis_t: "NDIS प्रगति नोट्स",
      dictate_uc_ndis_b: "स्मृति ताजा होने पर शिफ्ट नोट बोलें — हाथों से मुक्त, कोई टाइपिंग नहीं।",
      dictate_uc_invoice_t: "चालान विवरण",
      dictate_uc_invoice_b: "Xero, MYOB या QuickBooks में सीधे लाइन-आइटम विवरण डिक्टेट किया गया।",
      dictate_uc_email_t: "लंबे-फॉर्म ईमेल उत्तर",
      dictate_uc_email_b: "Gmail, Outlook या किसी वेबमेल में उत्तर बोलें — टाइप करने से तीन गुना तेज।",
      dictate_final_kicker: "संस्थापक सदस्यों के लिए मुफ्त",
      dictate_final_title: "हर कीबोर्ड अब एक माइक्रोफोन है।",
      dictate_final_sub: "HustleSuite संस्थापक सदस्यों के लिए मुफ्त। अन्यथा $5/माह — किसी भी समय रद्द करें।",
      dictate_final_cta: "Chrome में जोड़ें — मुफ्त",
      "finder.cat.roadtrip": "रोड ट्रिप",
      "finder.hotels.title": "पास में एक होटल खोजें",
      "finder.hotels.sub": "एक खोज में 200+ बुकिंग साइटों की तुलना करता है — Trivago द्वारा संचालित।",
      "finder.hotels.where": "कहाँ",
      "finder.hotels.where_ph": "शहर या उपनगर (उदा. Byron Bay, NSW)",
      "finder.hotels.checkin": "चेक-इन",
      "finder.hotels.checkout": "चेक-आउट",
      "finder.hotels.adults": "वयस्क",
      "finder.hotels.search": "होटल खोजें",
      "finder.hotels.disclosure": "Trivago खोलता है — हम बुकिंग पर कमीशन कमा सकते हैं।",
      /* AUTO-TRANSLATED hi END */
      nav_menu: "मेनू",
      nav_book: "टेबल बुक करें",
      nav_reviews: "समीक्षाएँ",
      nav_about: "हमारे बारे में",
      nav_account: "मेरा खाता",
      nav_signin: "साइन इन",
      cart_open: "कार्ट खोलें",
      cart_title: "आपका ऑर्डर",
      cart_empty: "कार्ट खाली है",
      cart_subtotal: "उप-कुल",
      cart_continue: "जारी रखें",
      cart_pickup_date: "पिकअप तिथि",
      cart_pickup_time: "पिकअप समय",
      cart_pay_on_pickup: "पिकअप पर भुगतान",
      cart_pay_now: "अभी भुगतान करें",
      cart_place_order: "ऑर्डर दें",
      add_to_order: "ऑर्डर में जोड़ें",
      menu_search_ph: "मेनू में खोजें…",
      menu_chef_specials: "शेफ़ स्पेशल",
      menu_under_kcal: "से कम",
      menu_kcal_unit: "kcal",
      menu_clear_filters: "साफ़ करें",
      menu_dietary_label: "आहार",
      menu_no_matches: "कोई मिलान नहीं",
      menu_relax_filters: "कोई और खोज आज़माएँ या फ़िल्टर कम करें।",
      menu_clear_all: "सभी फ़िल्टर हटाएँ",
      book_title: "टेबल बुक करें",
      book_name: "नाम",
      book_email: "ईमेल",
      book_phone: "फ़ोन",
      book_guests: "मेहमान",
      book_date: "तिथि",
      book_time: "समय",
      book_requests: "विशेष अनुरोध",
      book_submit: "बुकिंग की पुष्टि करें",
      footer_rights: "सर्वाधिकार सुरक्षित।",
      lang_label: "भाषा",
      welcome_back: "वापसी पर स्वागत है",
      sign_out: "साइन आउट",
    
      /* AUTO-TRANSLATED finder.* (added) */
      "finder.title": "HustleFinder",
      "finder.tagline": "निकटतम सार्वजनिक स्थान खोजें — वास्तविक डेटा, पूरे ऑस्ट्रेलिया में।",
      "finder.search_placeholder": "स्थान, सड़क या उपनगर खोजें…",
      "finder.hint": "ऊपर टाइप करना शुरू करें, किसी श्रेणी पर टैप करें, या \"मेरे पास\" का उपयोग करें।",
      "finder.near_me": "मेरे पास",
      "finder.searching": "खोज रहे हैं…",
      "finder.suburb.placeholder": "उपनगर के अनुसार फ़िल्टर करें (जैसे Darwin, NT)…",
      "finder.suburb.clear": "साफ़ करें",
      "finder.search_in": "{{cat}} में खोजें",
      "finder.empty": "कोई परिणाम नहीं — कोई अन्य श्रेणी या खोज शब्द आज़माएं।",
      "finder.meta.title": "HustleFinder — मुफ़्त Aussie उपनगर निर्देशिका",
      "finder.meta.desc": "स्थानीय व्यवसाय, चार्जर, कैफ़े, शौचालय और अधिक खोजें हर ऑस्ट्रेलियाई उपनगर में — 100% मुफ़्त।",
      "finder.footer.credits": "डेटा क्रेडिट और एट्रिब्यूशन",
      "finder.footer.terms": "नियम",
      "finder.cat.toilet": "शौचालय",
      "finder.cat.fountain": "पीने के फव्वारे",
      "finder.cat.transport": "परिवहन",
      "finder.cat.park": "पार्क",
      "finder.cat.medical": "चिकित्सा",
      "finder.cat.school": "स्कूल",
      "finder.cat.library": "पुस्तकालय",
      "finder.cat.petrol": "पेट्रोल",
      "finder.cat.kidzone": "Kid Zone",
      "finder.cat.puzzles": "पहेलियाँ",
      /* AUTO-TRANSLATED rt.* START */
      "rt.brand_name": "Roadtrip Planner",
      "rt.page_title": "ऑस्ट्रेलियाई EV रोड-ट्रिप प्लानर",
      "rt.from": "यहाँ से",
      "rt.to": "यहाँ तक",
      "rt.from_placeholder": "उपनगर, कस्बा या शहर (जैसे: Newcastle NSW)",
      "rt.to_placeholder": "उपनगर, कस्बा या शहर (जैसे: Hobart TAS)",
      "rt.stop": "STOP",
      "rt.stop_placeholder": "बीच का ठहराव",
      "rt.add_stop": "स्टॉप जोड़ें",
      "rt.upgrade_pro": "20 स्टॉप के लिए Pro में अपग्रेड करें",
      "rt.reverse": "उल्टा करें (घर वापसी का रास्ता)",
      "rt.from_current": "वर्तमान स्थान से",
      "rt.range_per_charge": "प्रति चार्ज रेंज (किमी)",
      "rt.connector": "कनेक्टर",
      "rt.connector_any": "कोई भी",
      "rt.drive_hours_day": "ड्राइविंग घंटे/दिन",
      "rt.accommodation": "आवास",
      "rt.accom_any": "कोई भी (BnB · होटल · कारवां)",
      "rt.accom_bnb": "BnB",
      "rt.accom_hotel": "होटल",
      "rt.accom_caravan": "कारवां पार्क",
      "rt.min_rating": "न्यूनतम रेटिंग",
      "rt.any_rating": "कोई भी रेटिंग",
      "rt.only_5star": "केवल 5★",
      "rt.schedule": "शेड्यूल:",
      "rt.depart_at": "प्रस्थान का समय",
      "rt.arrive_by": "पहुंचने का समय",
      "rt.hotel_checkin": "होटल चेक-इन",
      "rt.hotel_checkout": "होटल चेक-आउट",
      "rt.overnight_hint": "रात के सफर इन घंटों के बाहर शेड्यूल नहीं किए जाएंगे।",
      "rt.whos_in_car": "कार में कौन है?",
      "rt.adults": "वयस्क",
      "rt.kids": "बच्चे",
      "rt.ages": "उम्र",
      "rt.show_attractions": "पर्यटन स्थल दिखाएं",
      "rt.show_kids": "बच्चों के अनुकूल स्टॉप दिखाएं",
      "rt.overnight_stays": "रात का ठहराव",
      "rt.fast_chargers_only": "केवल फास्ट चार्जर",
      "rt.hotel_must_charger": "होटल में चार्जर होना चाहिए",
      "rt.pet_only": "केवल पालतू जानवरों के अनुकूल स्थान",
      "rt.safety": "सुरक्षा",
      "rt.safety_90": "90% (समतल)",
      "rt.safety_85": "85% (डिफ़ॉल्ट)",
      "rt.safety_80": "80% (पहाड़ी / टोइंग)",
      "rt.safety_70": "70% (भारी भार)",
      "rt.save_trip": "ट्रिप सेव करें",
      "rt.plan_my_trip": "मेरी ट्रिप प्लान करें",
      "rt.refresh_trip": "ट्रिप रिफ्रेश करें",
      "rt.planning": "प्लानिंग हो रही है…",
      "rt.resume_title": "यहाँ से ट्रिप फिर से शुरू करें",
      "rt.resume_body": "हमने आपका वर्तमान स्थान ले लिया है। अपने डैशबोर्ड पर दिख रही शेष रेंज डालें ताकि हम अगले सफर की योजना आपके पास मौजूद चार्ज के आधार पर बना सकें — न कि फुल चार्ज पर।",
      "rt.remaining_range": "शेष रेंज (किमी)",
      "rt.cancel": "रद्द करें",
      "rt.replan_from_here": "यहाँ से फिर से प्लान करें",
      "rt.save_modal_title": "इस ट्रिप को नाम दें",
      "rt.save_modal_body": "सेव की गई ट्रिप आपकी प्रोफाइल में रहती हैं — किसी भी समय खोलें और Replan पर क्लिक करें।",
      "rt.save_name_placeholder": "जैसे: Brisbane → Sydney via Byron",
      "rt.your_saved_trips": "आपकी सेव की गई ट्रिप",
      "rt.custom_location": "कस्टम लोकेशन",
      "rt.trip_summary": "ट्रिप का सारांश",
      "rt.itinerary": "यात्रा कार्यक्रम",
      "rt.no_stops_needed": "किसी स्टॉप की ज़रूरत नहीं — पूरी ट्रिप आपकी रेंज के अंदर है।",
      "rt.chargers_in_corridor": "रास्ते में चार्जर",
      "rt.planned_stops": "प्लान किए गए स्टॉप",
      "rt.overnight_stop": "रात का ठहराव",
      "rt.overnight_stops": "रात के ठहराव",
      "rt.suggested_detours": "सुझाए गए रास्ते — जोड़ने के लिए टिक करें",
      "rt.replan_with_detours": "नए रास्तों के साथ फिर से प्लान करें",
      "rt.attractions_label": "आकर्षण",
      "rt.kids_label": "बच्चे",
      "rt.km_from_start": "शुरुआत से किमी",
      "rt.open_driving": "ड्राइविंग मोड खोलें",
      "rt.big_font_hint": "बड़े फॉन्ट, स्टैंड-फ्रेंडली। CarPlay/Android Auto ब्राउज़र मिरर के साथ काम करता है।",
      "rt.check_price_book": "कीमत जांचें और Trivago पर बुक करें ↗",
      "rt.visit_site": "साइट पर जाएं ↗",
      "rt.charger_onsite": "साइट पर चार्जर उपलब्ध",
      "rt.leg_exceeds_range": "यह हिस्सा आपकी निर्धारित रेंज से अधिक है — इस चार्जर तक पहुँचने से पहले आपकी बैटरी खत्म हो सकती है। रेंज कम मानकर चलें या बीच में चार्जिंग जोड़ें।",
      "rt.add_waypoint_replan": "पड़ाव के रूप में जोड़ें और फिर से प्लान करें",
      /* AUTO-TRANSLATED rt.* END */
                      /* AUTO-TRANSLATED hm.* START */
      "hm.nav.hustlemeet": "HustleMeet",
      "hm.nav.circles": "Circles",
      "hm.nav.matchmaker": "Matchmaker",
      "hm.nav.meet": "मिलें",
      "hm.nav.overview": "HustleMeetups",
      "hm.overview.page_title": "HustleMeetups — अपने लोगों को खोजने के तीन आत्मीय तरीके।",
      "hm.overview.kicker": "HustleSuite का हिस्सा",
      "hm.overview.hero_title": "अपने लोगों को खोजने के तीन आत्मीय तरीके।",
      "hm.overview.hero_title_accent": "अपने लोग",
      "hm.overview.hero_sub": "HustleMeetups Australia में सच्ची दोस्ती बनाने का सबसे सुखद तरीका है — वह वाइब चुनें जो आपको सूट करे। चाहे आप एक साप्ताहिक रनिंग ग्रुप चाहते हों, अपने मौजूदा दोस्तों के साथ मासिक डिनर, या नए चेहरों की AI-मैच की गई टेबल, यहाँ हर किसी के लिए रास्ता है।",
      "hm.overview.tile_meet_kicker": "HustleMeet",
      "hm.overview.tile_meet_title": "अपने लोगों को खोजें। वह करें जो आपको पसंद है।",
      "hm.overview.tile_meet_body": "रुचि-आधारित समूहों के लिए Australia का घर। रनर्स, कोडर्स, इन्वेस्टर्स, वॉकर्स, डांसर्स, ब्रेकफास्ट क्लब्स, बुक क्रू — सैकड़ों समूह, हर हफ्ते असली इवेंट्स। एक बार आएं, या हर हफ्ते।",
      "hm.overview.tile_meet_cta": "समूहों को एक्सप्लोर करें",
      "hm.overview.tile_circles_kicker": "Circles",
      "hm.overview.tile_circles_title": "आपकी टोली। हर बार एक नई टेबल।",
      "hm.overview.tile_circles_body": "अपने दोस्तों के पुराने ग्रुप को लाएं। हम एक नया रेस्टोरेंट बुक करते हैं, ट्रिविया प्लान करते हैं, कार्ड्स का इंतज़ाम करते हैं। आप बस पहुंच जाएं। अपनी मौजूदा टोली को करीब रखने का सबसे आसान तरीका — बिना किसी को ऑर्गनाइज़ करने की टेंशन दिए।",
      "hm.overview.tile_circles_cta": "एक Circle शुरू करें",
      "hm.overview.tile_matchmaker_kicker": "Matchmaker",
      "hm.overview.tile_matchmaker_title": "AI टेबल चुनता है। आप बस पहुंच जाएं।",
      "hm.overview.tile_matchmaker_body": "10-मिनट की एक क्विज़ लें। रातों-रात, हम आपको ऐसे अजनबियों के साथ बिठा देंगे जो डेज़र्ट आने तक पुराने दोस्तों जैसे लगने लगेंगे। हर बार अलग टेबल।",
      "hm.overview.tile_matchmaker_cta": "क्विज़ लें",
      "hm.overview.trust_kicker": "यह क्यों काम करता है",
      "hm.overview.trust_title": "भरोसा पहले। टेबल बाद में।",
      "hm.overview.trust_1_title": "वेरिफाइड मेंबर्स",
      "hm.overview.trust_1_body": "हर ग्रुप की शुरुआत असली ID चेक + रिलायबिलिटी स्कोर के साथ होती है। कोई फेक अकाउंट नहीं।",
      "hm.overview.trust_2_title": "असली होस्ट",
      "hm.overview.trust_2_body": "हर ग्रुप का नेतृत्व एक जांचे-परखे होस्ट द्वारा किया जाता है जो माहौल को संभालता है, लय तय करता है, और सबका ख्याल रखता है।",
      "hm.overview.trust_3_title": "दोस्ती सबसे पहले",
      "hm.overview.trust_3_body": "यहाँ सब कुछ सच्ची दोस्ती के लिए डिज़ाइन किया गया है, डेटिंग के लिए नहीं। कभी भी नहीं।",
      "hm.overview.trust_4_title": "ज़ीरो-टॉलरेंस मॉडरेशन",
      "hm.overview.trust_4_body": "एक रिपोर्ट, एक रिव्यू, एक फैसला। बुरा व्यवहार करने वालों को उसी रात हटा दिया जाता है।",
      "hm.overview.footer_line": "HustleSuite का हिस्सा — Australian स्वामित्व और होस्टिंग।",
      "hm.landing.page_title": "HustleMeet · अपने लोगों को खोजें। वह करें जो आपको पसंद है।",
      "hm.landing.kicker": "Australia का रुचि नेटवर्क",
      "hm.landing.hero_title": "अपने लोगों को खोजें। वह करें जो आपको पसंद है।",
      "hm.landing.hero_title_accent": "अपने लोग",
      "hm.landing.hero_sub": "रुचि-आधारित समूहों के लिए HustleMeet Australia का घर है। लंच पर डिबगिंग करते सॉफ्टवेयर डेवलपर्स। सुबह 6 बजे लॉन्ग-कोर्स रनर्स। हर महीने मिलने वाले इन्वेस्टर्स। वॉकिंग ग्रुप्स, डांस क्रू, ब्रेकफास्ट क्लब्स, बोर्ड-गेम नाइट्स। एक ग्रुप चुनें, अगला इवेंट बुक करें, और पहुंच जाएं।",
      "hm.landing.cta_explore_circles": "ग्रुप्स ब्राउज़ करें",
      "hm.landing.cta_try_matchmaker": "अपना खुद का ग्रुप शुरू करें",
      "hm.landing.trust_verified": "वेरिफाइड मेंबर्स",
      "hm.landing.trust_venues": "जांचे-परखे इवेंट होस्ट",
      "hm.landing.trust_friendship": "दोस्ती पहले — डेटिंग कभी नहीं",
      "hm.landing.trust_moderation": "ज़ीरो-टॉलरेंस मॉडरेशन",
      "hm.landing.subbrand_circles_kicker": "Circles",
      "hm.landing.subbrand_circles_title": "पहले से ही अपनी टोली है? उन्हें एक स्थायी घर दें।",
      "hm.landing.subbrand_circles_body": "Circles उन दोस्तों के ग्रुप के लिए है जो पहले से मिलते हैं — बुक क्लब, पोकर नाइट, रनिंग क्रू, वर्क फ्रेंड्स। हम वेन्यू, रिमाइंडर और रोस्टर संभालते हैं। आप बस पहुंच जाएं।",
      "hm.landing.subbrand_circles_b1": "बदलते वेन्यू, एक ही रनिंग रोस्टर",
      "hm.landing.subbrand_circles_b2": "ऑटो-रिमाइंडर + आसान RSVP",
      "hm.landing.subbrand_circles_b3": "ट्रिविया, कार्ड्स और थीम वाली नाइट्स उपलब्ध",
      "hm.landing.subbrand_circles_cta": "एक Circle शुरू करें",
      "hm.landing.subbrand_matchmaker_kicker": "Matchmaker",
      "hm.landing.subbrand_matchmaker_title": "AI टेबल चुनता है। आप बस पहुंच जाएं।",
      "hm.landing.subbrand_matchmaker_body": "एक 10-मिनट की क्विज़ — व्यक्तित्व, मूल्य, उपलब्धता। हमारा Matchmaker आपको उन अजनबियों के साथ बिठाता है जो डेज़र्ट आने तक पुराने दोस्तों जैसे लगने लगेंगे। हर बार अलग टेबल।",
      "hm.landing.subbrand_matchmaker_b1": "Big-5 व्यक्तित्व और मूल्यों का स्कोर",
      "hm.landing.subbrand_matchmaker_b2": "फिट स्कोर 0.70 या उससे अधिक होने पर रातों-रात आमंत्रण",
      "hm.landing.subbrand_matchmaker_b3": "आपके हर डिनर से सीखता है",
      "hm.landing.subbrand_matchmaker_cta": "क्विज़ लें",
      "hm.landing.how_kicker": "HustleMeet कैसे काम करता है",
      "hm.landing.how_title": "चार कदम। अपना ग्रुप खोजें। पहुंच जाएं।",
      "hm.landing.how_step1_ttl": "अपनी रुचियां चुनें",
      "hm.landing.how_step1_sub": "रनिंग, कोडिंग, इन्वेस्टिंग, कुकिंग, म्यूज़िक, वॉकिंग — हमें बताएं कि आपको क्या पसंद है और आप कहाँ रहते हैं।",
      "hm.landing.how_step2_ttl": "अपने पास के ग्रुप्स ब्राउज़ करें",
      "hm.landing.how_step2_sub": "हर ग्रुप अपने होस्ट, अपनी लय और अपने अगले इवेंट की जानकारी देता है। जुड़ने से पहले माहौल को समझें।",
      "hm.landing.how_step3_ttl": "अगले इवेंट के लिए RSVP करें",
      "hm.landing.how_step3_sub": "एक टैप, एक कैलेंडर आमंत्रण, और एक दिन पहले एक स्पष्ट रिमाइंडर। कुछ इवेंट्स फ्री हैं, कुछ पेड — जो कीमत है, वही सामने है।",
      "hm.landing.how_step4_ttl": "पहुंचें और जुड़ें",
      "hm.landing.how_step4_sub": "होस्ट से मिलें, क्रू से मिलें, वह काम करें जो आपको पसंद है। अगर बात बन जाए तो अगले हफ्ते फिर आएं।",
      "hm.landing.stats_kicker": "मिलने का Australia का नया तरीका",
      "hm.landing.stats_title": "यही कारण है कि लोग बार-बार आते हैं।",
      "hm.landing.stats_92_sub": "महसूस करते हैं कि इवेंट में कम से कम एक व्यक्ति के साथ उनकी बात बनी",
      "hm.landing.stats_68_sub": "एक हफ्ते के भीतर अपने अगले इवेंट के लिए RSVP करते हैं",
      "hm.landing.stats_85_sub": "ग्रुप के किसी व्यक्ति के साथ संपर्क में रहते हैं",
      "hm.landing.why_kicker": "हम क्यों सफल हैं",
      "hm.landing.why_title": "असली रुचियां। असली होस्ट। असली इवेंट्स।",
      "hm.landing.why_1_ttl": "रुचि पहले, स्थान बाद में",
      "hm.landing.why_1_body": "आपके ग्रुप के लोग वास्तव में उस चीज़ की परवाह करते हैं जिसकी आप परवाह करते हैं — यही सच्ची दोस्ती का शॉर्टकट है।",
      "hm.landing.why_2_ttl": "असली इवेंट होस्ट",
      "hm.landing.why_2_body": "हर ग्रुप का एक जांचा-परखा होस्ट होता है जो वाइब को संभालता है, लय तय करता है, और कुछ गलत होने पर हस्तक्षेप करता है। कोई Facebook एडमिन नहीं। एक असली इंसान।",
      "hm.landing.why_3_ttl": "निरंतरता मायने रखती है",
      "hm.landing.why_3_body": "हर हफ्ते नए इवेंट्स। कुछ ऐसा जिसका इंतज़ार रहे। एक ऐसी लय जो अजनबियों को आपकी मंगलवार-की-रात वाली टोली में बदल देती है।",
      "hm.landing.why_4_ttl": "सुरक्षित और निश्चिंत",
      "hm.landing.why_4_body": "वेरिफाइड मेंबर्स, सार्वजनिक वेन्यू, अनादर के लिए ज़ीरो टॉलरेंस। हमारी सपोर्ट टीम हर इवेंट से पहले, दौरान और बाद में उपलब्ध रहती है।",
      "hm.landing.why_5_ttl": "दोस्ती पहले",
      "hm.landing.why_5_body": "यह डेटिंग नहीं है। यह समुदाय है। असली रुचियां, असली लोग, बीच में कोई स्वाइपिंग नहीं।",
      "hm.landing.why_6_ttl": "जुड़ने के लिए काफी छोटा",
      "hm.landing.why_6_body": "ज़्यादातर ग्रुप्स इवेंट्स को 4-15 सीटों तक सीमित रखते हैं। असली बातचीत शुरू करने के लिए काफी बड़ा। इतना छोटा कि कोई खो न जाए।",
      "hm.landing.cities_kicker": "हम कहाँ काम करते हैं",
      "hm.landing.cities_title": "Australia और NZ में मिल रहे ग्रुप्स।",
      "hm.landing.cities_sub_zero": "15+ शहरों में पहले ग्रुप्स खुल रहे हैं — जल्दी जुड़ें और माहौल बनाने में मदद करें।",
      "hm.landing.cities_sub_some": "15+ महानगरों में ग्रुप्स लाइव हैं। इस हफ्ते, आपके शहर में कुछ न कुछ है।",
      "hm.landing.cities_sub_one": "{{count}} लाइव ग्रुप · हर हफ्ते नए इवेंट्स · लगातार नए शहर जोड़ रहे हैं।",
      "hm.landing.cities_sub_many": "{{count}} लाइव ग्रुप्स · हर हफ्ते नए इवेंट्स · लगातार नए शहर जोड़ रहे हैं।",
      "hm.landing.forme_kicker": "यह आपके लिए है अगर…",
      "hm.landing.forme_title": "आप असली रुचियां, असली लोग और असली इवेंट्स चाहते हैं।",
      "hm.landing.forme_1": "आप स्वाइप-ऐप की थकान से ऊब चुके हैं और वास्तव में उन लोगों से मिलना चाहते हैं जो आपकी पसंद की चीज़ों में रुचि रखते हैं।",
      "hm.landing.forme_2": "आप एक नए शहर में आए हैं और आपको एक आत्मीय टोली की ज़रूरत है, न कि किसी और चैट ग्रुप की।",
      "hm.landing.forme_3": "आप दिखावे के बजाय गहराई को महत्व देते हैं — पचास फॉलोअर्स से बेहतर एक शानदार ग्रुप है।",
      "hm.landing.forme_4": "आप मानते हैं कि साझा रुचियां + असल ज़िंदगी में तय किए गए दो घंटे ही असली जादू हैं।",
      "hm.landing.notme_kicker": "यह आपके लिए नहीं है अगर…",
      "hm.landing.notme_title": "HustleMeet हर किसी के लिए काम नहीं करता।",
      "hm.landing.notme_1": "आप आने का वादा नहीं निभा सकते — होस्ट और मेंबर्स आपके RSVP पर भरोसा कर रहे हैं।",
      "hm.landing.notme_2": "आप डेटिंग सर्विस की तलाश में हैं — यहाँ दोस्ती पहले आती है।",
      "hm.landing.notme_3": "आप बिना किसी से मिले केवल ब्रॉडकास्ट करना चाहते हैं — यह केवल व्यक्तिगत रूप से मिलने पर ही काम करता है।",
      "hm.landing.notme_4": "आप अंतहीन मुफ्त फायदों की उम्मीद करते हैं — कुछ इवेंट्स की लागत होती है। कीमत हमेशा पहले बता दी जाती है।",
      "hm.landing.safety_kicker": "हमारी सुरक्षा प्रतिबद्धता",
      "hm.landing.safety_title": "आत्मीय, लेकिन कभी लापरवाह नहीं।",
      "hm.landing.safety_1_ttl": "सार्वजनिक वेन्यू",
      "hm.landing.safety_1_body": "हर इवेंट एक सार्वजनिक, स्टाफ वाले वेन्यू पर होता है — एक कैफे, रेस्टोरेंट, पार्क, जिम या स्टूडियो। कभी किसी के घर पर नहीं, जब तक कि हर प्रतिभागी इसके लिए तैयार न हो।",
      "hm.landing.safety_2_ttl": "सामुदायिक मानक",
      "hm.landing.safety_2_body": "अनादर, उत्पीड़न या बेईमानी के लिए ज़ीरो टॉलरेंस। उल्लंघन करने वाले सदस्यों को तुरंत हटा दिया जाता है।",
      "hm.landing.safety_3_ttl": "गोपनीयता सुरक्षित",
      "hm.landing.safety_3_body": "आपकी जानकारी कभी भी सार्वजनिक रूप से साझा नहीं की जाती है। आप खुद तय करते हैं कि प्रत्येक इवेंट के बाद आप किसके साथ जुड़े रहना चाहते हैं।",
      "hm.landing.safety_4_ttl": "सपोर्ट उपलब्ध",
      "hm.landing.safety_4_body": "यदि आपके कोई प्रश्न या चिंताएं हैं, तो हमारी टीम हर इवेंट से पहले, दौरान और बाद में उपलब्ध है।",
      "hm.landing.bottom_title": "आपका अगला पसंदीदा ग्रुप बस एक टैप दूर है।",
      "hm.landing.bottom_sub": "अपना रास्ता चुनें — किसी मौजूदा ग्रुप में शामिल हों, अपनी टोली को Circle में लाएं, या Matchmaker को आपको ऐसी टेबल पर बिठाने दें जो पहले से ही फिट बैठती हो।",
      "hm.landing.bottom_cta_circles": "एक Circle शुरू करें",
      "hm.landing.bottom_cta_mm": "Matchmaker क्विज़ लें",
      "hm.circles.page_title": "HustleMeet Circles · आपकी टोली, हर बार नया वेन्यू",
      "hm.circles.kicker": "HustleMeet · Circles",
      "hm.circles.hero_title": "आपकी टोली। हर बार एक नई टेबल।",
      "hm.circles.hero_title_accent": "आपकी टोली",
      "hm.circles.hero_sub": "पहले से दोस्त हैं? Circles उन्हें करीब रखने का सबसे आसान तरीका है। हम वेन्यू बुक करते हैं, ट्रिविया प्लान करते हैं, कार्ड्स का इंतज़ाम करते हैं। आप बस पहुंच जाएं — वही टोली, नई रात, नई जगह।",
      "hm.circles.cta_find": "एक Circle शुरू करें",
      "hm.circles.cta_host_hero": "एक ओपन Circle में शामिल हों",
      "hm.circles.trust_venue": "बदलते हुए जांचे-परखे वेन्यू",
      "hm.circles.trust_party": "प्रति Circle 4-12 दोस्त",
      "hm.circles.trust_verified": "वेरिफाइड मेंबर्स",
      "hm.circles.trust_friendship": "सच्ची दोस्ती, बनी रहे करीब",
      "hm.circles.how_kicker": "यह कैसे काम करता है",
      "hm.circles.how_title": "चार कदम। आपकी टोली, टोली बनी रहती है।",
      "hm.circles.how_step1_ttl": "अपना Circle शुरू करें",
      "hm.circles.how_step1_sub": "इसे नाम दें, अपने दोस्तों को आमंत्रित करें (12 तक), और एक लय चुनें — साप्ताहिक, पाक्षिक या मासिक।",
      "hm.circles.how_step2_ttl": "हम वेन्यू चुनते हैं",
      "hm.circles.how_step2_sub": "हर इवेंट के लिए हम एक नई जगह का सुझाव देते हैं — रेस्टोरेंट, ट्रिविया नाइट, कार्ड रूम, वाइन बार। या अपनी किसी पसंदीदा जगह को लॉक करें।",
      "hm.circles.how_step3_ttl": "हर कोई एक टैप में RSVP करता है",
      "hm.circles.how_step3_sub": "ऑटो-रिमाइंडर, लागत का बँवारा, प्रीपेड सीटें ताकि आखिरी समय पर कोई मना करे तो प्लान खराब न हो। बिल एंट्री आने से पहले ही सुलझ जाता है।",
      "hm.circles.how_step4_ttl": "लय बनाए रखें",
      "hm.circles.how_step4_sub": "डायरी में एक ऐसी रात जिसे फिर कभी ऑर्गनाइज़ नहीं करना पड़ेगा। समूह मिनटों में नहीं, महीनों में और गहरा होता जाता है।",
      "hm.circles.cadence_weekly_ttl": "साप्ताहिक",
      "hm.circles.cadence_weekly_sub": "हर हफ्ते",
      "hm.circles.cadence_weekly_body": "उन टोलियों के लिए जो मंगलवार-की-रात का एक अनुष्ठान चाहते हैं — वही चेहरे, हर बार नई जगह।",
      "hm.circles.cadence_fortnight_ttl": "पाक्षिक",
      "hm.circles.cadence_fortnight_sub": "हर 2 हफ्ते में",
      "hm.circles.cadence_fortnight_body": "सबसे सटीक संतुलन — व्यस्त शेड्यूल पर दबाव डाले बिना नियमितता।",
      "hm.circles.cadence_monthly_ttl": "मासिक",
      "hm.circles.cadence_monthly_sub": "हर महीने",
      "hm.circles.cadence_monthly_body": "खास मौकों वाले Circles — संडे रोस्ट, बड़ी ट्रिविया नाइट्स, डगस्टेशन, पोकर।",
      "hm.circles.feast_kicker": "अपनी रात चुनें",
      "hm.circles.feast_title": "इसे बिताने के तीन तरीके",
      "hm.circles.feast_sub": "हर Circle चुनता है कि रात कैसे बीतेगी। उन्हें बदलते रहें, या अपनी टोली की पसंदीदा शैली को लॉक करें।",
      "hm.circles.feast_surprise_ttl": "एक नया रेस्टोरेंट ट्राई करें",
      "hm.circles.feast_surprise_body": "हम आपके पास के तीन वेन्यू को शॉर्ट-लिस्ट करते हैं, हर कोई वोट करता है, किचन पूरी टेबल के लिए तैयारी करता है। लागत का बँवारा, प्रीपेड सीटें।",
      "hm.circles.feast_preselect_ttl": "ट्रिविया / कार्ड्स / गेम्स",
      "hm.circles.feast_preselect_body": "एक ट्रिविया नाइट, पोकर रूम, बोर्ड-गेम बार या बॉलिंग लेन बुक करें। हम वेन्यू, बाय-इन और रिमाइंडर संभालते हैं।",
      "hm.circles.feast_per_event_ttl": "एक-बारगी थीम वाली रात",
      "hm.circles.feast_per_event_body": "वाइन टेस्टिंग, शेफ्स टेबल, कॉकटेल क्लास, एस्केप रूम। कभी-कभी कुछ खास — पूरी तरह से ऑर्गनाइज़्ड।",
      "hm.circles.live_kicker": "अभी",
      "hm.circles.live_title": "Circles जिनमें आप शामिल हो सकते हैं",
      "hm.circles.live_host_link": "अपना खुद का शुरू करें",
      "hm.circles.live_loading": "लोड हो रहा है…",
      "hm.circles.live_empty_title": "अपने शहर में पहले Circle बनें।",
      "hm.circles.live_empty_sub": "तीन दोस्तों के साथ एक शुरू करें और हम बाकी लोगों को आमंत्रित करने में आपकी मदद करेंगे — साथ ही पहले तीन वेन्यू हमारी ओर से।",
      "hm.circles.live_empty_cta": "एक Circle शुरू करें",
      "hm.circles.host_kicker": "Circle लीडर्स के लिए",
      "hm.circles.host_title": "आप टोली लाएं। एडमिन का काम हम करेंगे।",
      "hm.circles.host_body": "अगर आप वो दोस्त हैं जो हमेशा रात का प्लान ऑर्गनाइज़ करता है, तो Circles आपके घंटों बचाता है। ऑटो-रिमाइंडर, प्रीपेड RSVP, वेन्यू शॉर्ट-लिस्ट, लागत का बँवारा, वेटलिस्ट मैनेजमेंट और हर रात बाहर जाने पर एक कैशबैक वॉलेट। आप बस एक सामान्य सदस्य की तरह शामिल होते हैं।",
      "hm.circles.host_perk_1": "ऑटो वेन्यू शॉर्ट-लिस्ट — फिर कभी Google पर स्क्रॉल न करें",
      "hm.circles.host_perk_2": "प्रीपेड सीटें — कोई नो-शो नहीं",
      "hm.circles.host_perk_3": "डेज़र्ट से पहले ही लागत का बँवारा हो जाता है",
      "hm.circles.host_perk_4": "ऑटोपायलट पर रिमाइंडर + कैलेंडर आमंत्रण",
      "hm.circles.host_perk_5": "दोस्त के कैंसिल करने पर ऑटो-प्रमोट के साथ वेटलिस्ट",
      "hm.circles.host_perk_6": "हर रात बाहर जाने पर कैशबैक वॉलेट",
      "hm.circles.host_cta": "अभी एक Circle शुरू करें",
      "hm.circles.why_kicker": "Circles क्यों काम करते हैं",
      "hm.circles.why_title": "वयस्क दोस्ती को बुनियादी ढांचे की ज़रूरत होती है",
      "hm.circles.why_1_ttl": "वही टोली, गहरा भरोसा",
      "hm.circles.why_1_body": "ज़िंदगी दोस्तों को दूर खींच ले जाती है। Circles वह निश्चित लय है जो सबको करीब रखती है — वही 4-12 दोस्त, उसी रात को मिल रहे हैं, चाहे कुछ भी हो।",
      "hm.circles.why_2_ttl": "ज़ीरो एडमिन थकान",
      "hm.circles.why_2_body": "किसी को भी ग्रुप का WhatsApp चीफ बनने की ज़रूरत नहीं है। हम वेन्यू चुनते हैं, रिमाइंडर भेजते हैं, पेमेंट लेते हैं, सब कुछ मैनेज करते हैं।",
      "hm.circles.why_3_ttl": "नया वेन्यू, नई याद",
      "hm.circles.why_3_body": "Australia के सबसे बेहतरीन छोटे रेस्टोरेंट्स, ट्रिविया रूम्स और गेम बार्स को एक्सप्लोर करें। जाने-पहचाने चेहरे, नए नज़ारे — रूटीन और नवीनता का बेहतरीन मेल।",
      "hm.circles.bottom_title": "वह रात जिसका आपकी टोली को इंतज़ार रहता है, बिना किसी के प्लान किए।",
      "hm.circles.bottom_sub": "एक Circle। वही दोस्त। हर बार नया वेन्यू।",
      "hm.circles.bottom_cta_find": "एक Circle शुरू करें",
      "hm.circles.bottom_cta_mm": "Matchmaker के साथ नए लोगों से मिलें →",
      "hm.circles.card_host_pill": "Circle",
      "hm.matchmaker.page_title": "HustleMeet Matchmaker · AI सही टेबल चुनता है",
      "hm.matchmaker.kicker": "HustleMeet · Matchmaker",
      "hm.matchmaker.hero_title": "AI टेबल चुनता है। आप बस पहुंच जाएं।",
      "hm.matchmaker.hero_title_accent": "आप बस पहुंच जाएं।",
      "hm.matchmaker.hero_sub": "एक छोटी व्यक्तित्व और मूल्य क्विज़ का जवाब दें। हमारा Matchmaker आपको चार से सात ऐसे अजनबियों के साथ बिठाता है जो डेज़र्ट आने तक पुराने दोस्तों जैसे लगने लगेंगे। कोई स्वाइपिंग नहीं। कोई फालतू बातें नहीं। बस एक साझा टेबल, जब आप तैयार हों।",
      "hm.matchmaker.cta_quiz": "मैचिंग क्विज़ लें",
      "hm.matchmaker.cta_see_circles": "इसके बजाय Circles देखें →",
      "hm.matchmaker.trust_big5": "Big-5 व्यक्तित्व स्कोर",
      "hm.matchmaker.trust_seats": "प्रति टेबल 4-8 सीटें",
      "hm.matchmaker.trust_lock": "ज़ीरो-स्वाइप · ज़ीरो-फोटो",
      "hm.matchmaker.trust_verified": "केवल वेरिफाइड मेंबर्स",
      "hm.matchmaker.how_kicker": "यह कैसे काम करता है",
      "hm.matchmaker.how_title": "एक ऐसी टेबल के लिए चार कदम जो पहले से ही फिट बैठती है।",
      "hm.matchmaker.how_step1_ttl": "क्विज़ लें",
      "hm.matchmaker.how_step1_sub": "दस मिनट की प्रश्नावली — व्यक्तित्व, मूल्य, आहार, उपलब्धता। आपको इसे केवल एक बार करना है।",
      "hm.matchmaker.how_step2_ttl": "रातों-रात मैचिंग",
      "hm.matchmaker.how_step2_sub": "हमारा AI आपकी प्रोफ़ाइल और Australia के लाइव डिनर रोस्टर को पढ़ता है। जब आपका फिट स्कोर 0.70 या उससे अधिक होता है, तो यह एक व्यक्तिगत आमंत्रण तैयार करता है।",
      "hm.matchmaker.how_step3_ttl": "स्वीकार करें या अस्वीकार करें",
      "hm.matchmaker.how_step3_sub": "आपको वेन्यू, टेबल थीम और ग्रुप के साथ साझा की जाने वाली तीन रुचियों के साथ एक स्पष्ट आमंत्रण ईमेल मिलता है।",
      "hm.matchmaker.how_step4_ttl": "यह आपसे सीखता है",
      "hm.matchmaker.how_step4_sub": "डिनर के बाद का हर सर्वे मॉडल को और बेहतर बनाता है। आप जितना ज़्यादा डिनर करेंगे, आपका अगला मैच उतना ही स्मार्ट होगा।",
      "hm.matchmaker.science_kicker": "यह क्यों काम करता है",
      "hm.matchmaker.science_title": "Matchmaker अंदाज़ा नहीं लगाता — यह स्कोर करता है।",
      "hm.matchmaker.science_cost": "हर मैच चार कारकों का एक भारित मिश्रण है। जब पूरी टेबल का औसत 0.70 से ऊपर होता है, तो हम आमंत्रण भेजते हैं। इससे कम कुछ भी कतार में रहता है।",
      "hm.matchmaker.science_1_ttl": "व्यक्तित्व (35%)",
      "hm.matchmaker.science_1_body": "Big-5 व्यक्तित्व वेक्टर में कोसाइन सिमिलैरिटी — खुलापन, कर्तव्यनिष्ठा, बहिर्मुखता, सहमतता और भावनात्मक स्थिरता।",
      "hm.matchmaker.science_2_ttl": "मूल्य (25%)",
      "hm.matchmaker.science_2_body": "उन मूल्यों पर ओवरलैप जो डिनर की बातचीत को वास्तव में सार्थक बनाते हैं — परिवार, जिज्ञासा, विकास, सेवा, रोमांच।",
      "hm.matchmaker.science_3_ttl": "रुचियां (25%)",
      "hm.matchmaker.science_3_body": "आपकी विशिष्ट रुचियों पर जकार्ड ओवरलैप — Six Foot Track पर हाइकिंग, Sydney फिल्म क्लब, Melbourne कॉफी सीन, आपकी साल की तीसरी किताब।",
      "hm.matchmaker.science_4_ttl": "उपलब्धता (15%)",
      "hm.matchmaker.science_4_body": "वह रात जब आप वास्तव में आ सकते हैं। मंगलवार शाम 7 बजे का कोई आमंत्रण नहीं यदि आपका कैलेंडर कहता है कि मंगलवार को हमेशा जिम जाना है।",
      "hm.matchmaker.safety_kicker": "डिज़ाइन द्वारा सुरक्षा",
      "hm.matchmaker.safety_title": "Matchmaker चुपचाप सावधानी बरतता है।",
      "hm.matchmaker.safety_1_ttl": "विश्वास की सीमा",
      "hm.matchmaker.safety_1_body": "0.70 ग्रुप स्कोर से नीचे, कोई आमंत्रण नहीं भेजा जाता है। एक कमज़ोर मैच से बेहतर है डिनर न होना — जब तक कि वह सही न हो।",
      "hm.matchmaker.safety_2_ttl": "म्युचुअल-नो ब्लॉकलिस्ट",
      "hm.matchmaker.safety_2_body": "अगर दो सदस्य डिनर के बाद निजी तौर पर 'नहीं' का संकेत देते हैं, तो हम उन्हें फिर कभी साथ नहीं बिठाते।",
      "hm.matchmaker.safety_3_ttl": "ऑटो-किल स्विच",
      "hm.matchmaker.safety_3_body": "यदि 20% या अधिक हालिया सर्वे किसी टेबल को 2/5 या उससे कम रेट करते हैं, तो ऑपरेटर द्वारा समीक्षा किए जाने तक आमंत्रण 24 घंटे के लिए रुक जाते हैं।",
      "hm.matchmaker.safety_4_ttl": "सामुदायिक मानक",
      "hm.matchmaker.safety_4_body": "हर सदस्य एक सरल-अंग्रेज़ी आचार संहिता स्वीकार करता है। हम उल्लंघन करने वाले लोगों को हटा देते हैं — कोई अपील नहीं।",
      "hm.matchmaker.safety_5_ttl": "केवल सार्वजनिक वेन्यू",
      "hm.matchmaker.safety_5_body": "हर Matchmaker डिनर स्टाफ वाले एक असली रेस्टोरेंट में होता है। कभी किसी के घर पर नहीं।",
      "hm.matchmaker.safety_6_ttl": "इवेंट के बाद फीडबैक",
      "hm.matchmaker.safety_6_body": "आपका निजी सर्वे भविष्य के मैचों को आकार देता है, और वेन्यू या किसी साथी की मॉडरेशन समीक्षा को ट्रिगर कर सकता है।",
      "hm.matchmaker.loop_kicker": "लर्निंग लूप",
      "hm.matchmaker.loop_title": "हर डिनर आपके अगले डिनर को बेहतर बनाता है।",
      "hm.matchmaker.loop_body": "Matchmaker स्थिर नहीं है। प्रत्येक इवेंट के बाद आप एक छोटा निजी सर्वे सबमिट करते हैं — बातचीत कैसी रही, आप किसके साथ फिर से डिनर करना चाहेंगे, क्या वेन्यू उम्मीदों पर खरा उतरा। वे संकेत मॉडल को फीडबैक देते हैं और हर भविष्य के मैच को नया आकार देते हैं।",
      "hm.matchmaker.loop_1": "साथियों के \"फिर से डिनर करें\" वोट आपकी पेयरिंग को मज़बूत करते हैं",
      "hm.matchmaker.loop_2": "म्युचुअल-नो वोट चुपचाप जोड़ियों को हटा देते हैं",
      "hm.matchmaker.loop_3": "वेन्यू और बातचीत की रेटिंग सीट मिक्स को बेहतर बनाती है",
      "hm.matchmaker.loop_4": "रिलायबिलिटी स्कोर सदस्यों को बार-बार नो-शो से बचाता है",
      "hm.matchmaker.loop_5": "लर्निंग बायस महीने-दर-महीने अच्छे मैचों को सुदृढ़ करता है",
      "hm.matchmaker.loop_6": "आप नियंत्रण में रहते हैं — किसी भी समय ऑप्ट-आउट करें या अपनी प्रोफ़ाइल रीसेट करें",
      "hm.matchmaker.vs_kicker": "मेरे लिए कौन सा सही है?",
      "hm.matchmaker.vs_title": "Matchmaker या Circles?",
      "hm.matchmaker.vs_mm_ttl": "मुझे चुनें अगर मुझे विविधता चाहिए।",
      "hm.matchmaker.vs_mm_1": "हर बार एक अलग टेबल, शहर, व्यंजन",
      "hm.matchmaker.vs_mm_2": "4-8 अनुकूल अजनबियों का AI-क्यूरेटेड ग्रुप",
      "hm.matchmaker.vs_mm_3": "साप्ताहिक लय, कोई होस्ट प्रतिबद्धता नहीं",
      "hm.matchmaker.vs_mm_4": "शानदार अगर आप शहर में नए हैं या यात्रा कर रहे हैं",
      "hm.matchmaker.vs_mm_cta": "मैचिंग क्विज़ लें",
      "hm.matchmaker.vs_circles_ttl": "मुझे चुनें अगर मुझे नियमितता चाहिए।",
      "hm.matchmaker.vs_circles_1": "वही होस्ट · वही वेन्यू · वही रात",
      "hm.matchmaker.vs_circles_2": "एक ऐसा ग्रुप जो आपकी बुधवार की परंपरा बन जाए",
      "hm.matchmaker.vs_circles_3": "अनुभवी होस्ट द्वारा ह्यूमन-क्यूरेटेड",
      "hm.matchmaker.vs_circles_4": "शानदार अगर आप अभी-अभी आए हैं और जड़ें जमाना चाहते हैं",
      "hm.matchmaker.vs_circles_cta": "Circles ब्राउज़ करें",
      "hm.matchmaker.bottom_title": "एक क्विज़। उसके बाद हर डिनर पहले से ही फिट बैठता है।",
      "hm.matchmaker.bottom_sub": "आपको दो हफ्ते के भीतर मैच मिल जाएगा। यह इस महीने का आपका सबसे आसान \"हाँ\" होगा।",
      "hm.matchmaker.bottom_cta": "मैचिंग क्विज़ लें",
      "hm.matchmaker.bottom_note": "कोई क्रेडिट कार्ड नहीं · 10 मिनट · कभी भी अनमैच करें",
      /* AUTO-TRANSLATED hm.* END */
    },
  },
  es: {
    translation: {
      /* AUTO-TRANSLATED es START */
      plaza_nav_features: "Características",
      plaza_nav_rewards: "Recompensas",
      plaza_nav_how: "Cómo funciona",
      plaza_nav_roadmap: "Hoja de ruta",
      plaza_nav_faq: "Preguntas frecuentes",
      plaza_hero_badge: "De propiedad australiana",
      plaza_hero_kicker: "Un carrito. Todas las recompensas.",
      plaza_hero_title_1: "Compra australiano.",
      plaza_hero_title_2: "Gana en todas partes.",
      plaza_hero_sub: "Miles de vendedores australianos en una sola cesta. Cada dólar que gastas gana HustlePoints, canjeables en restaurantes, cafeterías, hoteles, peluquerías, peluquerías caninas y cualquier negocio de la red HustleSuite.",
      plaza_cta_start: "Únete gratis — toma 30 segundos",
      plaza_cta_signin: "Iniciar sesión",
      plaza_cta_shop: "Explorar la tienda",
      plaza_hero_stat_1_v: "12.847",
      plaza_hero_stat_1_l: "productos activos",
      plaza_hero_stat_2_v: "340+",
      plaza_hero_stat_2_l: "vendedores australianos",
      plaza_hero_stat_3_v: "$4,2M",
      plaza_hero_stat_3_l: "recompensas pagadas",
      plaza_hero_stat_4_v: "19",
      plaza_hero_stat_4_l: "categorías",
      plaza_problem_kicker: "El problema de comprar hoy en día",
      plaza_problem_title: "Diez pestañas. Diez contraseñas. Cero fidelidad.",
      plaza_problem_sub: "Ya haces malabarismos con Amazon, eBay, Kogan, Kmart, Woolies, Coles, Bunnings, JB, Officeworks y media docena de tiendas boutique. Pasas horas comparando precios y luego no recibes nada más que un recibo.",
      plaza_problem_1: "Precios que no puedes comparar entre sitios",
      plaza_problem_2: "Vendedores extranjeros haciéndose pasar por locales",
      plaza_problem_3: "Puntos de fidelidad atrapados en una sola tienda",
      plaza_problem_4: "Reseñas falsas de bots y dropshippers",
      plaza_solution_kicker: "Lo que HustlePlaza hace diferente",
      plaza_solution_title: "El último carrito que abrirás en todo el día.",
      plaza_solution_sub: "Una búsqueda. Un carrito. Una compra. Cada compra te devuelve HustlePoints que puedes gastar en todo nuestro ecosistema, no solo en esta tienda.",
      plaza_solution_1_t: "Un carrito, todos los vendedores",
      plaza_solution_1_b: "Compra en todos los vendedores australianos de la plataforma y paga una sola vez al finalizar la compra. Envío consolidado cuando sea posible.",
      plaza_solution_2_t: "Solo locales verificados",
      plaza_solution_2_b: "Cada vendedor tiene su identidad verificada antes de poder publicar. Sin bots, sin falsificaciones, sin misterios de dropshipping.",
      plaza_solution_3_t: "Recompensas que realmente viajan",
      plaza_solution_3_b: "Los puntos ganados aquí se gastan en cualquier negocio HustleSuite: cafeterías, hoteles, peluquerías, restaurantes, peluquerías caninas.",
      plaza_solution_4_t: "Seguimiento en vivo, una pantalla",
      plaza_solution_4_b: "Cada paquete de cada vendedor rastreado en una sola página. Notificaciones por correo electrónico y SMS en cada paso.",
      plaza_rewards_kicker: "El volante HustlePoints",
      plaza_rewards_title: "Gana mientras compras. Gasta en cualquier lugar de la red.",
      plaza_rewards_sub: "Cada $1 gastado en HustlePlaza gana HustlePoints. Canjéalos en cualquier restaurante, cafetería, hotel, peluquería o negocio de servicios que funcione con HustleSuite, en cualquier lugar de la red.",
      plaza_rewards_step_1: "Compra en el mercado",
      plaza_rewards_step_1_b: "Compra cualquier cosa, desde una camiseta hasta un refrigerador.",
      plaza_rewards_step_2: "Gana HustlePoints al instante",
      plaza_rewards_step_2_b: "Los puntos aparecen en el momento en que se envía tu pedido: sin tarjetas, sin tarjetas perforadas, sin juegos de vencimiento.",
      plaza_rewards_step_3: "Gasta en toda la red",
      plaza_rewards_step_3_b: "Canjea en cafeterías, restaurantes, hoteles, peluquerías, peluquerías caninas, comerciantes.",
      plaza_rewards_step_4: "O retira en efectivo",
      plaza_rewards_step_4_b: "Convierte a transferencia bancaria una vez que superes los $50.",
      plaza_cats_kicker: "Compra por categoría",
      plaza_cats_title: "19 categorías. Vendedores australianos. Stock en vivo.",
      plaza_cats_sub: "Explora una muestra de lo que está disponible ahora mismo, o regístrate y compra en el catálogo completo de más de 12.000 productos.",
      plaza_cats_cta: "Ver todas las categorías",
      plaza_vendors_kicker: "Vendedores australianos reales",
      plaza_vendors_title: "Conoce a los fabricantes, productores y tiendas familiares.",
      plaza_vendors_sub: "Cada vendedor en HustlePlaza tiene su ABN verificado, está ubicado en Australia y es incorporado personalmente. Sin intermediarios extranjeros haciéndose pasar por locales.",
      plaza_how_kicker: "Cómo funciona",
      plaza_how_title: "Tres pasos. Un carrito. Recompensas para siempre.",
      plaza_how_1_title: "Únete en 30 segundos",
      plaza_how_1_body: "Verifica tu correo electrónico y navega al instante. Agrega un número de teléfono cuando estés listo para finalizar la compra.",
      plaza_how_2_title: "Llena tu carrito",
      plaza_how_2_body: "Compra en todos los vendedores. Un carrito, una vista de envío, una compra. Pago a través de Stripe o PayPal.",
      plaza_how_3_title: "Gana y gasta en cualquier lugar",
      plaza_how_3_body: "Los HustlePoints aparecen en el momento en que se envía tu pedido. Gástalos en cualquier negocio HustleSuite o retíralos en efectivo a partir de $50.",
      plaza_compare_kicker: "Cómo nos comparamos",
      plaza_compare_title: "HustlePlaza vs los gigantes",
      plaza_compare_col_us: "HustlePlaza",
      plaza_compare_col_amz: "Amazon AU",
      plaza_compare_col_ebay: "eBay AU",
      plaza_compare_col_kmart: "Kmart / Big W",
      plaza_compare_row_1: "De propiedad australiana",
      plaza_compare_row_2: "Carrito único para todos los vendedores",
      plaza_compare_row_3: "Recompensas gastables FUERA de esta tienda",
      plaza_compare_row_4: "Retiro de recompensas a tu banco",
      plaza_compare_row_5: "Atención al cliente alojada en Australia",
      plaza_trust_kicker: "Por qué verificamos",
      plaza_trust_title: "Clientes reales. Reseñas reales.",
      plaza_trust_body: "Verificamos tu correo electrónico al registrarte, tu número de móvil antes de finalizar la compra, y requerimos una verificación de foto en vivo antes de que puedas publicar reseñas, chatear con vendedores o hacer compras de alto valor. Esto ralentiza a los estafadores y mantiene los precios bajos para ti.",
      plaza_trust_1: "Verificación de correo electrónico para unirse",
      plaza_trust_2: "Verificación por SMS para finalizar la compra",
      plaza_trust_3: "Verificación de foto en vivo para reseñas y chat",
      plaza_trust_4: "Verificación de identidad del vendedor al incorporarse",
      plaza_test_kicker: "Lo que dicen nuestros primeros miembros",
      plaza_test_title: "Directo de clientes reales.",
      plaza_test_1_body: "Usé mis puntos de HustlePlaza para pagar la cena en una cafetería cerca del trabajo. Realmente no creía que funcionaría hasta que la camarera sonrió y dijo 'estás listo'.",
      plaza_test_1_name: "Priya S.",
      plaza_test_2_body: "Seis vendedores, una ventana de entrega, un correo de confirmación. Normalmente tendría seis contraseñas y seis números de seguimiento. Esto simplemente funciona.",
      plaza_test_2_name: "Marcus T.",
      plaza_test_3_body: "Como pequeño productor de miel, finalmente nos pagan el mismo día, reseñas reales y sin comisión del 40%. HustlePlaza me llevó de 'negocio secundario' a tiempo completo en ocho meses.",
      plaza_test_3_name: "Kate y Dave",
      plaza_faq_kicker: "Respuestas",
      plaza_faq_title: "Las preguntas que recibimos cada semana",
      plaza_faq_1_q: "¿HustlePlaza es realmente de propiedad australiana?",
      plaza_faq_1_a: "Sí. HustlePlaza es 100% de propiedad australiana, alojado en servidores australianos, dirigido por un equipo australiano. Cada vendedor pasa por verificación de identidad antes de que su primera publicación pueda estar activa.",
      plaza_faq_2_q: "¿Cómo funcionan las recompensas?",
      plaza_faq_2_a: "Cada dólar que gastas en HustlePlaza te gana HustlePoints. Los puntos aparecen en el momento en que se envía tu pedido y nunca expiran. Gástalos en cualquier negocio que funcione con HustleSuite, o retíralos a tu banco una vez que hayas ahorrado lo suficiente.",
      plaza_faq_3_q: "¿Por qué verifican fotos?",
      plaza_faq_3_a: "Para que las reseñas que leas sean reales. La verificación de fotos es una verificación de selfie en vivo realizada a través de la cámara de tu teléfono, la misma técnica que usan los bancos, y evita que bots, cuentas falsas y actores hostiles manipulen la plataforma.",
      plaza_faq_4_q: "¿Qué pasa si mi pedido no llega?",
      plaza_faq_4_a: "Cada pedido está respaldado por nuestra HustleGuarantee: si el paquete no llega o no es como se describe, te reembolsamos dentro de 3 días hábiles y luego perseguimos al vendedor. Nunca te quedarás sin tu dinero.",
      plaza_faq_5_q: "¿Está segura la información de mi tarjeta?",
      plaza_faq_5_a: "Nunca vemos ni almacenamos el número de tu tarjeta. Todos los pagos se procesan a través de Stripe (PCI-DSS Nivel 1) o PayPal, ambos con autenticación 3D Secure que te protege contra cargos fraudulentos en tarjetas robadas.",
      plaza_faq_6_q: "¿Envían internacionalmente?",
      plaza_faq_6_a: "Todavía no. HustlePlaza es solo para Australia en el lanzamiento: así es como podemos prometer entrega en la misma semana para la mayoría de los artículos y soporte en tu zona horaria. Nueva Zelanda está en la hoja de ruta de 2026.",
      plaza_faq_7_q: "¿Cuánto cuesta unirse?",
      plaza_faq_7_a: "Nada. La membresía es gratis para siempre. Los vendedores pagan una pequeña comisión por venta: los compradores pagan $0 para unirse, $0 para ganar, $0 para canjear.",
      plaza_faq_8_q: "¿Puedo vender en HustlePlaza?",
      plaza_faq_8_a: "Absolutamente. Dirígete a hustlesuite.au para incorporarte como vendedor: aprobación el mismo día para negocios australianos verificados, además de tus primeros 30 días sin comisión.",
      plaza_final_kicker: "Precio de fundador — plazas limitadas",
      plaza_final_title: "Sé uno de los primeros — asegura el precio de fundador.",
      plaza_final_sub: "Regístrate hoy como Miembro de HustleSuite. Serás el primero en saber cuándo se lanza cada nueva función, y tu cuenta mantendrá las recompensas y precios de nivel fundador de por vida.",
      plaza_final_bonus: "Los fundadores nunca pierden su tarifa de fundador. Gratis para siempre, cancela en cualquier momento.",
      plaza_final_cta: "Registrarse como Miembro Fundador",
      plaza_final_disclosure: "No se requiere tarjeta. Solo verificación de correo electrónico y móvil.",
      plaza_roadmap_kicker: "Lo que estamos construyendo a continuación",
      plaza_roadmap_title: "Seis herramientas diseñadas para quitarte trabajo de encima.",
      plaza_roadmap_sub: "HustleSuite es más que una tienda: es una capa de productividad completa. Esto es lo que está en la pista.",
      plaza_roadmap_soon: "Próximamente",
      plaza_roadmap_next: "Siguiente en la cola",
      plaza_roadmap_1_t: "HustleDictate — voz a texto en cualquier formulario",
      plaza_roadmap_1_b: "Extensión de Chrome. Presiona Alt+M, habla y cualquier entrada en cualquier sitio web se completa sola. Solicitudes inmobiliarias, facturas, papeleo NDIS: hecho en la mitad del tiempo.",
      plaza_roadmap_2_t: "HustleConnect Editar en cualquier lugar",
      plaza_roadmap_2_b: "Para propietarios de WordPress: edita tu sitio en vivo desde cualquier página con una sola tecla. No más viajes de ida y vuelta a wp-admin.",
      plaza_roadmap_3_t: "Botón universal Guardar en HustleSuite",
      plaza_roadmap_3_b: "Haz clic derecho en cualquier página web (listado de propiedades, artículo, producto, restaurante) y llega al módulo correcto listo para actuar.",
      plaza_roadmap_4_t: "Comprobador de precios de HustlePlaza",
      plaza_roadmap_4_b: "Navega por Amazon, eBay o Kmart: un chip flotante muestra el mismo producto en HustlePlaza con tu vista previa de recompensas.",
      plaza_roadmap_5_t: "Recopilador de reseñas",
      plaza_roadmap_5_b: "Sincroniza automáticamente las nuevas reseñas de Google, Facebook y TripAdvisor a tu panel de HustleSuite. Ahorra ~20 minutos por semana.",
      plaza_roadmap_6_t: "Barra lateral de HustleSuite",
      plaza_roadmap_6_b: "Panel lateral de Chrome con pedidos en vivo, KPI, publicación rápida y factura rápida. Todo tu negocio en una franja junto a cada pestaña.",
      plaza_footer_owned: "De propiedad y alojamiento australiano",
      plaza_footer_terms: "Términos",
      plaza_footer_privacy: "Privacidad",
      plaza_footer_contact: "Contacto",
      plaza_footer_vendors: "Vende en HustlePlaza",
      plaza_footer_help: "Centro de ayuda",
      plaza_footer_tagline: "Parte de la familia HustleSuite: un inicio de sesión, todos los servicios.",
      verify_gate_title: "Casi listo — registro rápido",
      verify_gate_body: "HustlePlaza es un carrito de compras que ofrece muchos beneficios, incluidas recompensas canjeables que se pueden usar, no solo dentro de esta plataforma, sino con cualquier otro negocio que use la plataforma HustlePlaza o cualquier otro negocio registrado con HustleSuite. Algunos de estos negocios incluirán restaurantes, cafeterías, hoteles, peluquerías e incluso peluquerías caninas. Para continuar ofreciendo excelentes precios y servicios, necesitamos asegurarnos de que eres real y verificarte. Presiona el botón Unirse para continuar.",
      verify_gate_join: "Unirse a HustleSuite",
      verify_gate_signin: "Ya tengo una cuenta",
      verify_gate_cancel: "Ahora no",
      verify_email_title: "Verifica tu correo electrónico",
      verify_email_body: "Enviaremos un código de 6 dígitos a tu bandeja de entrada.",
      verify_email_placeholder: "tu@ejemplo.com",
      verify_email_send: "Enviar código",
      verify_email_code_ph: "Código de 6 dígitos",
      verify_email_check: "Verificar",
      verify_email_sent: "Código enviado — revisa tu bandeja de entrada.",
      verify_sms_title: "Verifica tu teléfono",
      verify_sms_body: "Agrega un número de móvil para que podamos enviarte actualizaciones de pedidos por mensaje de texto y confirmar compras grandes.",
      verify_sms_placeholder: "+61412345678",
      verify_sms_send: "Enviar SMS",
      verify_sms_check: "Verificar",
      verify_sms_sent: "Código enviado — revisa tu teléfono.",
      verify_photo_title: "Verificación de foto",
      verify_photo_body: "Escanea el código QR con tu teléfono para tomar una selfie en vivo. Requerido para chat, compras de alto valor y publicaciones en Meetup / Tackle.",
      verify_photo_waiting: "Esperando tu teléfono…",
      verify_photo_verified: "Foto verificada — estás listo.",
      travel_cta_start: "Únete gratis",
      travel_hero_kicker: "HustleTravel",
      travel_hero_title_1: "Una membresía.",
      travel_hero_title_2: "Australia entera.",
      travel_hero_sub: "Hoteles, vuelos, experiencias, planificación de viajes por carretera y alquiler de coches, reservados a la mejor tarifa disponible y pagándote HustlePoints por cada dólar gastado.",
      travel_cta_plan: "Planificar un viaje por carretera",
      travel_cta_hotels: "Buscar hoteles",
      travel_svc_hotels_t: "Hoteles y apartamentos",
      travel_svc_hotels_b: "Compara más de 200 sitios de reservas en un solo lugar: metabúsqueda con tecnología de Trivago.",
      travel_svc_flights_t: "Vuelos",
      travel_svc_flights_b: "Domésticos e internacionales, con mapas de asientos en tiempo real y reglas de tarifas.",
      travel_svc_experiences_t: "Experiencias y tours",
      travel_svc_experiences_b: "Días en el arrecife, catas en bodegas, música en vivo, excursiones para niños.",
      travel_svc_cars_t: "Alquiler de coches",
      travel_svc_cars_b: "Todas las marcas principales más independientes locales: recogida en cualquier aeropuerto australiano.",
      travel_svc_insurance_t: "Seguro de viaje",
      travel_svc_insurance_b: "Suscrito por aseguradoras australianas reguladas por APRA. Compara en una pantalla.",
      travel_svc_roadtrip_t: "Planificador de viajes por carretera",
      travel_svc_roadtrip_b: "Itinerarios conscientes del alcance de vehículos eléctricos con cargadores, paradas nocturnas y estimaciones de costos.",
      travel_final_kicker: "Precio de fundador",
      travel_final_title: "Asegura las recompensas de nivel fundador de por vida.",
      travel_final_sub: "Regístrate una vez con HustleSuite. Reserva todo (hoteles, vuelos, experiencias, seguro) en HustleTravel y gana HustlePoints gastables en todos los negocios de la red.",
      travel_final_cta: "Registrarse como Miembro Fundador",
      travel_final_disclosure: "Política de mejor tarifa: resultados ordenados por mejor valor para el viajero, no por comisión.",
      dictate_hero_kicker: "HustleDictate",
      dictate_hero_title_1: "Habla.",
      dictate_hero_title_2: "Cualquier formulario. Cualquier sitio web.",
      dictate_hero_sub: "La única extensión de Chrome que convierte cualquier campo de texto en un micrófono de dictado por voz de grado profesional, impulsado por Whisper.",
      dictate_cta_install: "Agregar a Chrome",
      dictate_uc_re_t: "Notas de arrendamiento inmobiliario",
      dictate_uc_re_b: "Dicta informes de inspección y mensajes de inquilinos directamente en tu sistema de gestión de propiedades.",
      dictate_uc_ndis_t: "Notas de progreso NDIS",
      dictate_uc_ndis_b: "Habla la nota del turno mientras el recuerdo está fresco: manos libres, sin escribir.",
      dictate_uc_invoice_t: "Descripciones de facturas",
      dictate_uc_invoice_b: "Descripciones de partidas dictadas directamente en Xero, MYOB o QuickBooks.",
      dictate_uc_email_t: "Respuestas de correo electrónico extensas",
      dictate_uc_email_b: "Habla las respuestas en Gmail, Outlook o cualquier correo web: tres veces más rápido que escribir.",
      dictate_final_kicker: "Gratis para Miembros Fundadores",
      dictate_final_title: "Cada teclado es ahora un micrófono.",
      dictate_final_sub: "Gratis para Miembros Fundadores de HustleSuite. $5/mes de lo contrario: cancela en cualquier momento.",
      dictate_final_cta: "Agregar a Chrome — gratis",
      "finder.cat.roadtrip": "Viaje por carretera",
      "finder.hotels.title": "Encuentra un hotel cercano",
      "finder.hotels.sub": "Compara más de 200 sitios de reservas en una sola búsqueda, con tecnología de Trivago.",
      "finder.hotels.where": "Dónde",
      "finder.hotels.where_ph": "Ciudad o suburbio (ej. Byron Bay, NSW)",
      "finder.hotels.checkin": "Entrada",
      "finder.hotels.checkout": "Salida",
      "finder.hotels.adults": "Adultos",
      "finder.hotels.search": "Buscar hoteles",
      "finder.hotels.disclosure": "Abre Trivago — podemos ganar comisión en reservas.",
      /* AUTO-TRANSLATED es END */
      nav_menu: "Carta",
      nav_book: "Reservar mesa",
      nav_reviews: "Reseñas",
      nav_about: "Sobre nosotros",
      nav_account: "Mi cuenta",
      nav_signin: "Iniciar sesión",
      cart_open: "Abrir carrito",
      cart_title: "Tu pedido",
      cart_empty: "El carrito está vacío",
      cart_subtotal: "Subtotal",
      cart_continue: "Continuar",
      cart_pickup_date: "Fecha de recogida",
      cart_pickup_time: "Hora de recogida",
      cart_pay_on_pickup: "Pagar al recoger",
      cart_pay_now: "Pagar ahora",
      cart_place_order: "Confirmar pedido",
      add_to_order: "Añadir al pedido",
      menu_search_ph: "Buscar en la carta…",
      menu_chef_specials: "Especialidades del chef",
      menu_under_kcal: "Menos de",
      menu_kcal_unit: "kcal",
      menu_clear_filters: "Limpiar",
      menu_dietary_label: "Dieta",
      menu_no_matches: "Sin resultados",
      menu_relax_filters: "Prueba otra búsqueda o relaja los filtros.",
      menu_clear_all: "Borrar todos los filtros",
      book_title: "Reservar mesa",
      book_name: "Nombre",
      book_email: "Correo",
      book_phone: "Teléfono",
      book_guests: "Comensales",
      book_date: "Fecha",
      book_time: "Hora",
      book_requests: "Peticiones especiales",
      book_submit: "Confirmar reserva",
      footer_rights: "Todos los derechos reservados.",
      lang_label: "Idioma",
      welcome_back: "Bienvenido de nuevo",
      sign_out: "Cerrar sesión",
    
      /* AUTO-TRANSLATED finder.* (added) */
      "finder.title": "HustleFinder",
      "finder.tagline": "Encuentra el lugar público más cercano — datos reales, en toda Australia.",
      "finder.search_placeholder": "Buscar lugares, calles o suburbios…",
      "finder.hint": "Comienza a escribir arriba, toca una categoría o usa \"Cerca de mí\".",
      "finder.near_me": "Cerca de mí",
      "finder.searching": "Buscando…",
      "finder.suburb.placeholder": "Filtrar por suburbio (ej. Darwin, NT)…",
      "finder.suburb.clear": "Limpiar",
      "finder.search_in": "Buscar en {{cat}}",
      "finder.empty": "Sin resultados — prueba otra categoría o término de búsqueda.",
      "finder.meta.title": "HustleFinder — directorio gratuito de suburbios australianos",
      "finder.meta.desc": "Busca negocios locales, cargadores, cafeterías, baños y más en todos los suburbios australianos — 100% gratis.",
      "finder.footer.credits": "Créditos de datos y atribución",
      "finder.footer.terms": "Términos",
      "finder.cat.toilet": "Baños",
      "finder.cat.fountain": "Fuentes de Agua Potable",
      "finder.cat.transport": "Transporte",
      "finder.cat.park": "Parques",
      "finder.cat.medical": "Médico",
      "finder.cat.school": "Escuelas",
      "finder.cat.library": "Bibliotecas",
      "finder.cat.petrol": "Gasolina",
      "finder.cat.kidzone": "Zona Infantil",
      "finder.cat.puzzles": "Rompecabezas",
      /* AUTO-TRANSLATED rt.* START */
      "rt.brand_name": "Planificador de Viajes",
      "rt.page_title": "Planificador de Viajes en VE por Australia",
      "rt.from": "Desde",
      "rt.to": "Hasta",
      "rt.from_placeholder": "Barrio, pueblo o ciudad (ej. Newcastle NSW)",
      "rt.to_placeholder": "Barrio, pueblo o ciudad (ej. Hobart TAS)",
      "rt.stop": "PARADA",
      "rt.stop_placeholder": "Parada intermedia",
      "rt.add_stop": "Añadir parada",
      "rt.upgrade_pro": "Mejora a Pro para hasta 20 paradas",
      "rt.reverse": "Invertir (calcular viaje de vuelta)",
      "rt.from_current": "Desde mi ubicación actual",
      "rt.range_per_charge": "Autonomía por carga (km)",
      "rt.connector": "Conector",
      "rt.connector_any": "Cualquiera",
      "rt.drive_hours_day": "Horas de conducción/día",
      "rt.accommodation": "Alojamiento",
      "rt.accom_any": "Cualquiera (BnB · Hotel · Caravana)",
      "rt.accom_bnb": "BnB",
      "rt.accom_hotel": "Hotel",
      "rt.accom_caravan": "Parque de caravanas",
      "rt.min_rating": "Valoración mínima",
      "rt.any_rating": "Cualquier valoración",
      "rt.only_5star": "Solo 5★",
      "rt.schedule": "Horario:",
      "rt.depart_at": "Salida a las",
      "rt.arrive_by": "Llegada a las",
      "rt.hotel_checkin": "Check-in del hotel",
      "rt.hotel_checkout": "Check-out del hotel",
      "rt.overnight_hint": "Los trayectos nocturnos no se programarán fuera de estas horas.",
      "rt.whos_in_car": "¿Quién va en el coche?",
      "rt.adults": "Adultos",
      "rt.kids": "Niños",
      "rt.ages": "Edades",
      "rt.show_attractions": "Ver atracciones turísticas",
      "rt.show_kids": "Ver paradas para niños",
      "rt.overnight_stays": "Estancias nocturnas",
      "rt.fast_chargers_only": "Solo cargadores rápidos",
      "rt.hotel_must_charger": "El hotel debe tener cargador",
      "rt.pet_only": "Solo alojamientos que admitan mascotas",
      "rt.safety": "Seguridad",
      "rt.safety_90": "90% (llano)",
      "rt.safety_85": "85% (por defecto)",
      "rt.safety_80": "80% (colinas / remolque)",
      "rt.safety_70": "70% (carga pesada)",
      "rt.save_trip": "Guardar viaje",
      "rt.plan_my_trip": "Planificar mi viaje",
      "rt.refresh_trip": "Actualizar viaje",
      "rt.planning": "Planificando…",
      "rt.resume_title": "Reanudar viaje desde aquí",
      "rt.resume_body": "Hemos detectado tu ubicación actual. Introduce la autonomía restante que muestra tu panel para que planifiquemos el siguiente tramo con lo que tienes realmente, no con una carga completa.",
      "rt.remaining_range": "Autonomía restante (km)",
      "rt.cancel": "Cancelar",
      "rt.replan_from_here": "Replanificar desde aquí",
      "rt.save_modal_title": "Ponle nombre a este viaje",
      "rt.save_modal_body": "Los viajes guardados aparecen en tu perfil; ábrelos cuando quieras y pulsa Replanificar.",
      "rt.save_name_placeholder": "ej. Brisbane → Sydney vía Byron",
      "rt.your_saved_trips": "Tus viajes guardados",
      "rt.custom_location": "Ubicación personalizada",
      "rt.trip_summary": "Resumen del viaje",
      "rt.itinerary": "Itinerario",
      "rt.no_stops_needed": "No se necesitan paradas; todo el viaje cabe en tu autonomía.",
      "rt.chargers_in_corridor": "cargadores en la ruta",
      "rt.planned_stops": "paradas planificadas",
      "rt.overnight_stop": "parada nocturna",
      "rt.overnight_stops": "paradas nocturnas",
      "rt.suggested_detours": "Desvíos sugeridos — marcar para añadir",
      "rt.replan_with_detours": "Replanificar con desvíos",
      "rt.attractions_label": "Atracción",
      "rt.kids_label": "Niños",
      "rt.km_from_start": "km desde el inicio",
      "rt.open_driving": "Abrir modo conducción",
      "rt.big_font_hint": "Letra grande, ideal para soportes. Funciona con CarPlay/Android Auto vía navegador.",
      "rt.check_price_book": "Ver precio y reservar en Trivago ↗",
      "rt.visit_site": "Visitar sitio ↗",
      "rt.charger_onsite": "Cargador en el lugar",
      "rt.leg_exceeds_range": "Este tramo supera la autonomía establecida; te quedarías sin batería antes de llegar a este cargador. Reduce la autonomía prevista o añade una recarga intermedia.",
      "rt.add_waypoint_replan": "Añadir como punto de paso y replanificar",
      /* AUTO-TRANSLATED rt.* END */
                      /* AUTO-TRANSLATED hm.* START */
      "hm.nav.hustlemeet": "HustleMeet",
      "hm.nav.circles": "Circles",
      "hm.nav.matchmaker": "Matchmaker",
      "hm.nav.meet": "Meet",
      "hm.nav.overview": "HustleMeetups",
      "hm.overview.page_title": "HustleMeetups — tres formas cálidas de encontrar a tu gente.",
      "hm.overview.kicker": "Parte de HustleSuite",
      "hm.overview.hero_title": "Tres formas cálidas de encontrar a tu gente.",
      "hm.overview.hero_title_accent": "tu gente",
      "hm.overview.hero_sub": "HustleMeetups es la forma más amable de Australia para construir amistades reales: elige la vibra que mejor te siente. Ya sea que busques un grupo de running semanal, una cena mensual con tu grupo de siempre o una mesa de caras nuevas seleccionadas por IA, hay un lugar para ti.",
      "hm.overview.tile_meet_kicker": "HustleMeet",
      "hm.overview.tile_meet_title": "Encuentra a tu gente. Haz lo que te gusta.",
      "hm.overview.tile_meet_body": "El hogar en Australia para grupos basados en intereses. Corredores, programadores, inversores, caminantes, bailarines, clubes de desayuno, grupos de lectura... cientos de grupos, eventos reales cada semana. Ven una vez o cada semana.",
      "hm.overview.tile_meet_cta": "Explorar grupos",
      "hm.overview.tile_circles_kicker": "Circles",
      "hm.overview.tile_circles_title": "Tu grupo. Una mesa nueva cada vez.",
      "hm.overview.tile_circles_body": "Trae a tu grupo habitual de amigos. Nosotros reservamos un restaurante nuevo, planeamos la trivia y repartimos las cartas. Tú solo preséntate. La forma más cercana de mantener unido a tu grupo actual, sin que nadie tenga que organizarlo.",
      "hm.overview.tile_circles_cta": "Empezar un Circle",
      "hm.overview.tile_matchmaker_kicker": "Matchmaker",
      "hm.overview.tile_matchmaker_title": "La IA elige la mesa. Tú te presentas.",
      "hm.overview.tile_matchmaker_body": "Responde un cuestionario de 10 minutos. De la noche a la mañana, te sentamos con desconocidos que para el postre se sentirán como viejos amigos. Una mesa diferente cada vez.",
      "hm.overview.tile_matchmaker_cta": "Hacer el cuestionario",
      "hm.overview.trust_kicker": "Por qué funciona",
      "hm.overview.trust_title": "Primero la confianza. Luego la mesa.",
      "hm.overview.trust_1_title": "Miembros verificados",
      "hm.overview.trust_1_body": "Cada grupo comienza con una verificación de identidad real + puntaje de confiabilidad. Sin cuentas descartables.",
      "hm.overview.trust_2_title": "Anfitriones reales",
      "hm.overview.trust_2_body": "Cada grupo es liderado por un anfitrión veterano que cuida la vibra, marca el ritmo y atiende la sala.",
      "hm.overview.trust_3_title": "La amistad es lo primero",
      "hm.overview.trust_3_body": "Todo aquí está diseñado para amistades reales, no para citas. Nunca.",
      "hm.overview.trust_4_title": "Moderación de tolerancia cero",
      "hm.overview.trust_4_body": "Un reporte, una revisión, una decisión. Los malos actores son eliminados esa misma noche.",
      "hm.overview.footer_line": "Parte de HustleSuite — de propiedad y alojamiento en Australia.",
      "hm.landing.page_title": "HustleMeet · Encuentra a tu gente. Haz lo que te gusta.",
      "hm.landing.kicker": "La red de intereses de Australia",
      "hm.landing.hero_title": "Encuentra a tu gente. Haz lo que te gusta.",
      "hm.landing.hero_title_accent": "tu gente",
      "hm.landing.hero_sub": "HustleMeet es el hogar en Australia para grupos basados en intereses. Desarrolladores de software debatiendo en el almuerzo. Corredores de larga distancia a las 6 a. m. Inversores que se reúnen mensualmente. Grupos de caminata, elencos de danza, clubes de desayuno, noches de juegos de mesa. Elige un grupo, reserva el próximo evento y asiste.",
      "hm.landing.cta_explore_circles": "Explorar grupos",
      "hm.landing.cta_try_matchmaker": "Crea tu propio grupo",
      "hm.landing.trust_verified": "Miembros verificados",
      "hm.landing.trust_venues": "Anfitriones de eventos veteranos",
      "hm.landing.trust_friendship": "Primero la amistad — nunca citas",
      "hm.landing.trust_moderation": "Moderación de tolerancia cero",
      "hm.landing.subbrand_circles_kicker": "Circles",
      "hm.landing.subbrand_circles_title": "¿Ya tienes a tu grupo? Dales un hogar permanente.",
      "hm.landing.subbrand_circles_body": "Circles es para el grupo de amigos que ya se reúnen: club de lectura, noche de póker, equipo de running, colegas del trabajo. Nosotros gestionamos el lugar, los recordatorios y la lista. Tú solo preséntate.",
      "hm.landing.subbrand_circles_b1": "Lugares rotativos, una sola lista de asistencia",
      "hm.landing.subbrand_circles_b2": "Auto-recordatorios + confirmaciones fáciles",
      "hm.landing.subbrand_circles_b3": "Trivia, cartas y noches temáticas a disposición",
      "hm.landing.subbrand_circles_cta": "Empezar un Circle",
      "hm.landing.subbrand_matchmaker_kicker": "Matchmaker",
      "hm.landing.subbrand_matchmaker_title": "La IA elige la mesa. Tú te presentas.",
      "hm.landing.subbrand_matchmaker_body": "Un cuestionario de 10 minutos: personalidad, valores, disponibilidad. Nuestro Matchmaker te sienta con desconocidos que para el postre se sentirán como viejos amigos. Una mesa diferente cada vez.",
      "hm.landing.subbrand_matchmaker_b1": "Personalidad Big-5 y valores evaluados",
      "hm.landing.subbrand_matchmaker_b2": "Invitaciones de la noche a la mañana cuando tu compatibilidad sea de 0.70 o superior",
      "hm.landing.subbrand_matchmaker_b3": "Aprende de cada cena que tienes",
      "hm.landing.subbrand_matchmaker_cta": "Hacer el cuestionario",
      "hm.landing.how_kicker": "Cómo funciona HustleMeet",
      "hm.landing.how_title": "Cuatro pasos. Encuentra tu grupo. Preséntate.",
      "hm.landing.how_step1_ttl": "Elige tus intereses",
      "hm.landing.how_step1_sub": "Running, programación, inversiones, cocina, música, caminatas — dinos qué te gusta y dónde vives.",
      "hm.landing.how_step2_ttl": "Explora grupos cerca de ti",
      "hm.landing.how_step2_sub": "Cada grupo publica su anfitrión, su ritmo y su próximo evento. Conoce el ambiente antes de comprometerte.",
      "hm.landing.how_step3_ttl": "Confirma tu asistencia al próximo evento",
      "hm.landing.how_step3_sub": "Un toque, una invitación al calendario, un recordatorio claro el día anterior. Algunos eventos son gratuitos, otros de pago; el precio es transparente.",
      "hm.landing.how_step4_ttl": "Preséntate y conecta",
      "hm.landing.how_step4_sub": "Conoce al anfitrión, conoce al grupo, haz lo que te gusta. Vuelve la próxima semana si hubo conexión.",
      "hm.landing.stats_kicker": "La nueva forma de conocer gente en Australia",
      "hm.landing.stats_title": "La razón por la que la gente sigue volviendo.",
      "hm.landing.stats_92_sub": "Sienten que conectaron con al menos una persona en el evento",
      "hm.landing.stats_68_sub": "Confirman su asistencia al próximo evento en menos de una semana",
      "hm.landing.stats_85_sub": "Se mantienen en contacto con alguien del grupo",
      "hm.landing.why_kicker": "Por qué funcionamos",
      "hm.landing.why_title": "Intereses reales. Anfitriones reales. Eventos reales.",
      "hm.landing.why_1_ttl": "Primero el interés, no la ubicación",
      "hm.landing.why_1_body": "A las personas de tu grupo realmente les importa lo mismo que a ti; ese es el atajo hacia la amistad real.",
      "hm.landing.why_2_ttl": "Anfitriones de eventos reales",
      "hm.landing.why_2_body": "Cada grupo tiene un anfitrión verificado que cuida la vibra, marca el ritmo y actúa si algo no va bien. No es un administrador de Facebook. Es una persona real.",
      "hm.landing.why_3_ttl": "La consistencia importa",
      "hm.landing.why_3_body": "Nuevos eventos cada semana. Algo que esperar con ilusión. Un ritmo que convierte a desconocidos en tu grupo de los martes por la noche.",
      "hm.landing.why_4_ttl": "Seguro y protegido",
      "hm.landing.why_4_body": "Miembros verificados, lugares públicos, tolerancia cero al irrespeto. Nuestro equipo de soporte está disponible antes, durante y después de cada evento.",
      "hm.landing.why_5_ttl": "La amistad es lo primero",
      "hm.landing.why_5_body": "Esto no es para citas. Es comunidad. Intereses reales, personas reales, sin deslizar perfiles de por medio.",
      "hm.landing.why_6_ttl": "Lo suficientemente pequeño para importar",
      "hm.landing.why_6_body": "La mayoría de los grupos limitan los eventos a 4-15 asientos. Lo bastante grande para iniciar conversaciones reales. Lo bastante pequeño para que nadie se pierda.",
      "hm.landing.cities_kicker": "Dónde operamos",
      "hm.landing.cities_title": "Grupos reuniéndose en Australia & NZ.",
      "hm.landing.cities_sub_zero": "Primeros grupos abriendo en más de 15 ciudades — únete temprano y dales forma.",
      "hm.landing.cities_sub_some": "Grupos activos en más de 15 metrópolis. Algo en tu ciudad, esta semana.",
      "hm.landing.cities_sub_one": "{{count}} grupo activo · nuevos eventos cada semana · siempre sumando ciudades.",
      "hm.landing.cities_sub_many": "{{count}} grupos activos · nuevos eventos cada semana · siempre sumando ciudades.",
      "hm.landing.forme_kicker": "Esto es para ti si…",
      "hm.landing.forme_title": "Buscas intereses reales. Personas reales. Eventos reales.",
      "hm.landing.forme_1": "Estás harto del cansancio de las apps de citas y quieres conocer gente de verdad en torno a lo que amas hacer.",
      "hm.landing.forme_2": "Te has mudado a una nueva ciudad y necesitas un grupo cálido, no otro grupo de chat.",
      "hm.landing.forme_3": "Valoras la profundidad sobre la difusión — un gran grupo le gana a cincuenta seguidores.",
      "hm.landing.forme_4": "Crees que los intereses compartidos + dos horas fijas en la vida real es el verdadero truco maestro.",
      "hm.landing.notme_kicker": "Esto no es para ti si…",
      "hm.landing.notme_title": "HustleMeet no funciona para todos.",
      "hm.landing.notme_1": "No puedes comprometerte a asistir — los anfitriones y miembros cuentan con tu confirmación.",
      "hm.landing.notme_2": "Buscas un servicio de citas — aquí las amistades son lo primero.",
      "hm.landing.notme_3": "Quieres difundir cosas sin conocer a nadie — esto solo funciona en persona.",
      "hm.landing.notme_4": "Esperas beneficios gratuitos sin fin — algunos eventos tienen costo. El precio siempre es transparente.",
      "hm.landing.safety_kicker": "Nuestro compromiso de seguridad",
      "hm.landing.safety_title": "Cálidos, pero nunca descuidados.",
      "hm.landing.safety_1_ttl": "Lugares públicos",
      "hm.landing.safety_1_body": "Cada evento se realiza en un lugar público con personal: un café, restaurante, parque, gimnasio o estudio. Nunca en casa de nadie a menos que todos los asistentes acepten.",
      "hm.landing.safety_2_ttl": "Estándares comunitarios",
      "hm.landing.safety_2_body": "Tolerancia cero al irrespeto, acoso o deshonestidad. Los miembros que incumplan son eliminados de inmediato.",
      "hm.landing.safety_3_ttl": "Privacidad protegida",
      "hm.landing.safety_3_body": "Tus datos nunca se comparten públicamente. Tú controlas con quién te mantienes conectado después de cada evento.",
      "hm.landing.safety_4_ttl": "Soporte disponible",
      "hm.landing.safety_4_body": "Nuestro equipo está disponible antes, durante y después de cada evento si tienes preguntas o inquietudes.",
      "hm.landing.bottom_title": "Tu próximo grupo favorito está a un toque de distancia.",
      "hm.landing.bottom_sub": "Elige tu camino: únete a un grupo existente, trae a tu equipo a un Circle, o deja que el Matchmaker te siente en una mesa que ya encaja contigo.",
      "hm.landing.bottom_cta_circles": "Empezar un Circle",
      "hm.landing.bottom_cta_mm": "Hacer el cuestionario de Matchmaker",
      "hm.circles.page_title": "HustleMeet Circles · Tu grupo, un lugar nuevo cada vez",
      "hm.circles.kicker": "HustleMeet · Circles",
      "hm.circles.hero_title": "Tu grupo. Una mesa nueva cada vez.",
      "hm.circles.hero_title_accent": "Tu grupo",
      "hm.circles.hero_sub": "¿Ya tienes a tus amigos? Circles es la forma más fácil de mantenerlos cerca. Nosotros reservamos el lugar, planeamos la trivia y repartimos las cartas. Tú solo preséntate: mismo grupo, nueva noche, nuevo lugar.",
      "hm.circles.cta_find": "Empezar un Circle",
      "hm.circles.cta_host_hero": "Unirse a un Circle abierto",
      "hm.circles.trust_venue": "Lugares rotativos verificados",
      "hm.circles.trust_party": "4-12 amigos por Circle",
      "hm.circles.trust_verified": "Miembros verificados",
      "hm.circles.trust_friendship": "Amistades reales, siempre cerca",
      "hm.circles.how_kicker": "Cómo funciona",
      "hm.circles.how_title": "Cuatro pasos. Tu grupo sigue siendo un grupo.",
      "hm.circles.how_step1_ttl": "Empieza tu Circle",
      "hm.circles.how_step1_sub": "Dale un nombre, invita a tus amigos (hasta 12) y elige un ritmo: semanal, quincenal o mensual.",
      "hm.circles.how_step2_ttl": "Nosotros elegimos el lugar",
      "hm.circles.how_step2_sub": "Para cada evento sugerimos un lugar nuevo: restaurante, noche de trivia, sala de cartas, bar de vinos. O asegura un favorito.",
      "hm.circles.how_step3_ttl": "Todos confirman en un toque",
      "hm.circles.how_step3_sub": "Auto-recordatorios, división de costos, asientos prepagados para que las ausencias no arruinen el plan. La cuenta está lista antes de que llegue la entrada.",
      "hm.circles.how_step4_ttl": "Mantén el ritmo",
      "hm.circles.how_step4_sub": "Una noche en la agenda que no tiene que volver a organizarse nunca más. El grupo se fortalece a lo largo de meses, no de minutos.",
      "hm.circles.cadence_weekly_ttl": "Semanal",
      "hm.circles.cadence_weekly_sub": "Cada semana",
      "hm.circles.cadence_weekly_body": "Para los grupos que quieren un ritual de martes por la noche: mismas caras, nuevo lugar cada vez.",
      "hm.circles.cadence_fortnight_ttl": "Quincenal",
      "hm.circles.cadence_fortnight_sub": "Cada 2 semanas",
      "hm.circles.cadence_fortnight_body": "El punto justo: regular sin ser demandante en agendas ocupadas.",
      "hm.circles.cadence_monthly_ttl": "Mensual",
      "hm.circles.cadence_monthly_sub": "Cada mes",
      "hm.circles.cadence_monthly_body": "Circles para ocasiones especiales: asados de domingo, grandes noches de trivia, degustaciones, póker.",
      "hm.circles.feast_kicker": "Elige tu noche",
      "hm.circles.feast_title": "Tres formas de pasarla",
      "hm.circles.feast_sub": "Cada Circle elige cómo transcurre la noche. Rótalas o fija la favorita de tu grupo.",
      "hm.circles.feast_surprise_ttl": "Prueba un restaurante nuevo",
      "hm.circles.feast_surprise_body": "Seleccionamos tres lugares cerca de ti, todos votan, la cocina se prepara para una mesa llena. Costo dividido, asientos prepagados.",
      "hm.circles.feast_preselect_ttl": "Trivia / cartas / juegos",
      "hm.circles.feast_preselect_body": "Reserva una noche de trivia, una sala de póker, un bar de juegos de mesa o una bolera. Nosotros gestionamos el lugar, la entrada y los recordatorios.",
      "hm.circles.feast_per_event_ttl": "Noche temática única",
      "hm.circles.feast_per_event_body": "Cata de vinos, mesa del chef, clase de cócteles, escape room. Algo especial de vez en cuando, totalmente organizado.",
      "hm.circles.live_kicker": "Ahora mismo",
      "hm.circles.live_title": "Circles a los que puedes unirte",
      "hm.circles.live_host_link": "Empieza el tuyo propio",
      "hm.circles.live_loading": "Cargando…",
      "hm.circles.live_empty_title": "Sé el primer Circle en tu ciudad.",
      "hm.circles.live_empty_sub": "Empieza uno con tres amigos y te ayudaremos a invitar al resto — además de regalarte los primeros tres lugares por nuestra cuenta.",
      "hm.circles.live_empty_cta": "Empezar un Circle",
      "hm.circles.host_kicker": "Para líderes de Circle",
      "hm.circles.host_title": "Tú traes al grupo. Nosotros hacemos la gestión.",
      "hm.circles.host_body": "Si eres el amigo que siempre organiza la noche, Circles te ahorra horas. Auto-recordatorios, confirmaciones prepagadas, listas de lugares, división de costos, gestión de lista de espera y una billetera de cashback en cada salida. Tú solo asistes como un miembro normal.",
      "hm.circles.host_perk_1": "Listas automáticas de lugares — no vuelvas a buscar en Google",
      "hm.circles.host_perk_2": "Asientos prepagados — cero ausencias",
      "hm.circles.host_perk_3": "División de costos hecha antes del postre",
      "hm.circles.host_perk_4": "Recordatorios + invitaciones al calendario en piloto automático",
      "hm.circles.host_perk_5": "Lista de espera con promoción automática si alguien cancela",
      "hm.circles.host_perk_6": "Billetera de cashback en cada noche de salida",
      "hm.circles.host_cta": "Empezar un Circle ahora",
      "hm.circles.why_kicker": "Por qué Circles funciona",
      "hm.circles.why_title": "Las amistades adultas necesitan infraestructura",
      "hm.circles.why_1_ttl": "Mismo grupo, confianza más profunda",
      "hm.circles.why_1_body": "La vida separa a los amigos. Los Circles son el ritmo fijo que mantiene a todos cerca: los mismos 4-12 amigos, reuniéndose la misma noche de la semana, pase lo que pase.",
      "hm.circles.why_2_ttl": "Cero fatiga administrativa",
      "hm.circles.why_2_body": "Nadie tiene que ser el jefe del grupo de WhatsApp. Elegimos los lugares, enviamos los recordatorios, recibimos los pagos, cerramos el ciclo.",
      "hm.circles.why_3_ttl": "Nuevo lugar, nuevo recuerdo",
      "hm.circles.why_3_body": "Rota por los mejores restaurantes pequeños de Australia, salas de trivia y bares de juegos. Caras familiares, escenarios frescos: el punto ideal entre rutina y novedad.",
      "hm.circles.bottom_title": "La noche que tu grupo espera con ilusión, sin que nadie tenga que planearla.",
      "hm.circles.bottom_sub": "Un Circle. Mismos amigos. Un lugar nuevo cada vez.",
      "hm.circles.bottom_cta_find": "Empezar un Circle",
      "hm.circles.bottom_cta_mm": "Conoce gente nueva con Matchmaker →",
      "hm.circles.card_host_pill": "Circle",
      "hm.matchmaker.page_title": "HustleMeet Matchmaker · La IA elige la mesa adecuada",
      "hm.matchmaker.kicker": "HustleMeet · Matchmaker",
      "hm.matchmaker.hero_title": "La IA elige la mesa. Tú solo preséntate.",
      "hm.matchmaker.hero_title_accent": "Tú solo preséntate.",
      "hm.matchmaker.hero_sub": "Responde un breve cuestionario sobre personalidad y valores. Nuestro Matchmaker te sentará con cuatro a siete desconocidos que para el postre se sentirán como viejos amigos. Sin deslizar perfiles. Sin charlas superficiales. Solo una mesa compartida, lista para cuando tú lo estés.",
      "hm.matchmaker.cta_quiz": "Hacer el cuestionario de compatibilidad",
      "hm.matchmaker.cta_see_circles": "Ver Circles en su lugar →",
      "hm.matchmaker.trust_big5": "Personalidad Big-5 evaluada",
      "hm.matchmaker.trust_seats": "4-8 asientos por mesa",
      "hm.matchmaker.trust_lock": "Cero deslizamientos · cero fotos",
      "hm.matchmaker.trust_verified": "Solo miembros verificados",
      "hm.matchmaker.how_kicker": "Cómo funciona",
      "hm.matchmaker.how_title": "Cuatro pasos para una mesa que ya encaja.",
      "hm.matchmaker.how_step1_ttl": "Haz el cuestionario",
      "hm.matchmaker.how_step1_sub": "Cuestionario de diez minutos: personalidad, valores, dieta, disponibilidad. Lo haces una sola vez.",
      "hm.matchmaker.how_step2_ttl": "Emparejamiento de la noche a la mañana",
      "hm.matchmaker.how_step2_sub": "Nuestra IA lee tu perfil y la oferta de cenas en vivo de Australia. Cuando tu puntaje de compatibilidad sea de 0.70 o superior, redacta una invitación personal.",
      "hm.matchmaker.how_step3_ttl": "Aceptar o rechazar",
      "hm.matchmaker.how_step3_sub": "Recibes un correo de invitación claro con el lugar, el tema de la mesa y los tres intereses que compartes con el grupo.",
      "hm.matchmaker.how_step4_ttl": "Aprende de ti",
      "hm.matchmaker.how_step4_sub": "Cada encuesta post-cena ajusta el modelo. Tu próximo emparejamiento será más inteligente cuantas más veces cenes.",
      "hm.matchmaker.science_kicker": "Por qué funciona",
      "hm.matchmaker.science_title": "El Matchmaker no adivina; puntúa.",
      "hm.matchmaker.science_sub": "Cada combinación es un compuesto ponderado de cuatro factores. Cuando todo el promedio de la mesa supera 0.70, enviamos las invitaciones. Cualquier valor inferior permanece en la cola.",
      "hm.matchmaker.science_1_ttl": "Personalidad (35%)",
      "hm.matchmaker.science_1_body": "Similitud de coseno a través del vector de personalidad Big-5: apertura, responsabilidad, extraversión, amabilidad y estabilidad emocional.",
      "hm.matchmaker.science_2_ttl": "Valores (25%)",
      "hm.matchmaker.science_2_body": "Solapamiento en los valores que hacen que una conversación de cena realmente fluya: familia, curiosidad, crecimiento, servicio, aventura.",
      "hm.matchmaker.science_3_ttl": "Intereses (25%)",
      "hm.matchmaker.science_3_body": "Solapamiento de Jaccard en tus intereses específicos: senderismo en Six Foot Track, club de cine de Sydney, la escena del café de Melbourne, tu tercer libro del año.",
      "hm.matchmaker.science_4_ttl": "Disponibilidad (15%)",
      "hm.matchmaker.science_4_body": "La noche en la que realmente puedes asistir. Sin invitaciones para el martes a las 7 p. m. si tu calendario dice gimnasio-martes-por-siempre.",
      "hm.matchmaker.safety_kicker": "Seguridad por diseño",
      "hm.matchmaker.safety_title": "El Matchmaker es discretamente cauteloso.",
      "hm.matchmaker.safety_1_ttl": "Umbral de confianza",
      "hm.matchmaker.safety_1_body": "Por debajo de un puntaje grupal de 0.70, no se envía ninguna invitación. Es mejor un emparejamiento sólido que una cena forzada.",
      "hm.matchmaker.safety_2_ttl": "Lista de bloqueo mutuo",
      "hm.matchmaker.safety_2_body": "Si dos miembros señalan privadamente 'no' después de una cena, nunca volveremos a sentarlos juntos.",
      "hm.matchmaker.safety_3_ttl": "Interruptor de apagado automático",
      "hm.matchmaker.safety_3_body": "Si el 20% o más de las encuestas recientes califican una mesa con 2/5 o menos, las invitaciones se pausan por 24 h mientras un operador revisa.",
      "hm.matchmaker.safety_4_ttl": "Estándares comunitarios",
      "hm.matchmaker.safety_4_body": "Cada miembro acepta un código de conducta en lenguaje claro. Eliminamos a las personas que lo infrinjan; sin apelaciones.",
      "hm.matchmaker.safety_5_ttl": "Solo lugares públicos",
      "hm.matchmaker.safety_5_body": "Cada cena de Matchmaker se realiza en un restaurante real con personal. Nunca en casa de nadie.",
      "hm.matchmaker.safety_6_ttl": "Retroalimentación post-evento",
      "hm.matchmaker.safety_6_body": "Tu encuesta privada da forma a futuros emparejamientos y puede activar una revisión de moderación del lugar o de un compañero.",
      "hm.matchmaker.loop_kicker": "El ciclo de aprendizaje",
      "hm.matchmaker.loop_title": "Cada cena hace que tu próxima cena sea mejor.",
      "hm.matchmaker.loop_body": "El Matchmaker no es estático. Después de cada evento, envías una breve encuesta privada: cómo se sintió la conversación, con quién volverías a cenar, si el lugar cumplió. Esas señales alimentan el modelo y reforman cada emparejamiento futuro.",
      "hm.matchmaker.loop_1": "Los votos de compañeros para \"cenar de nuevo\" fortalecen tus emparejamientos",
      "hm.matchmaker.loop_2": "Los votos negativos mutuos retiran silenciosamente a las parejas",
      "hm.matchmaker.loop_3": "Las calificaciones del lugar y la conversación ajustan la mezcla de asientos",
      "hm.matchmaker.loop_4": "El puntaje de confiabilidad protege a los miembros de ausencias repetidas",
      "hm.matchmaker.loop_5": "El sesgo de aprendizaje refuerza las buenas combinaciones mes tras mes",
      "hm.matchmaker.loop_6": "Tú mantienes el control: opta por salir o reinicia tu perfil en cualquier momento",
      "hm.matchmaker.vs_kicker": "¿Cuál es el adecuado para mí?",
      "hm.matchmaker.vs_title": "¿Matchmaker o Circles?",
      "hm.matchmaker.vs_mm_ttl": "Elígeme si quiero variedad.",
      "hm.matchmaker.vs_mm_1": "Una mesa, ciudad y cocina diferente cada vez",
      "hm.matchmaker.vs_mm_2": "Grupo curado por IA de 4-8 desconocidos compatibles",
      "hm.matchmaker.vs_mm_3": "Ritmo semanal, sin compromiso de anfitrión",
      "hm.matchmaker.vs_mm_4": "Ideal si eres nuevo en una ciudad o estás viajando",
      "hm.matchmaker.vs_mm_cta": "Hacer el cuestionario de compatibilidad",
      "hm.matchmaker.vs_circles_ttl": "Elígeme si quiero algo regular.",
      "hm.matchmaker.vs_circles_1": "Mismo anfitrión · mismo lugar · misma noche de la semana",
      "hm.matchmaker.vs_circles_2": "Un grupo que se convierte en tu tradición de los miércoles",
      "hm.matchmaker.vs_circles_3": "Curado por humanos por un anfitrión experimentado",
      "hm.matchmaker.vs_circles_4": "Ideal si acabas de mudarte y quieres echar raíces",
      "hm.matchmaker.vs_circles_cta": "Explorar Circles",
      "hm.matchmaker.bottom_title": "Un cuestionario. Cada cena posterior ya encaja.",
      "hm.matchmaker.bottom_sub": "Recibirás un emparejamiento en menos de quince días. Es el \"sí\" más fácil que darás este mes.",
      "hm.matchmaker.bottom_cta": "Hacer el cuestionario de compatibilidad",
      "hm.matchmaker.bottom_note": "Sin tarjeta de crédito · 10 minutos · Cancela en cualquier momento",
      /* AUTO-TRANSLATED hm.* END */
    },
  },
  fr: {
    translation: {
      /* AUTO-TRANSLATED fr START */
      plaza_nav_features: "Fonctionnalités",
      plaza_nav_rewards: "Récompenses",
      plaza_nav_how: "Comment ça marche",
      plaza_nav_roadmap: "Feuille de route",
      plaza_nav_faq: "FAQ",
      plaza_hero_badge: "Propriété australienne",
      plaza_hero_kicker: "Un panier. Toutes les récompenses.",
      plaza_hero_title_1: "Achetez australien.",
      plaza_hero_title_2: "Gagnez partout.",
      plaza_hero_sub: "Des milliers de vendeurs australiens dans un seul panier. Chaque dollar dépensé vous rapporte des HustlePoints — utilisables dans les restaurants, cafés, hôtels, salons de coiffure, toiletteurs pour animaux et toute entreprise du réseau HustleSuite.",
      plaza_cta_start: "Rejoignez gratuitement — 30 secondes",
      plaza_cta_signin: "Se connecter",
      plaza_cta_shop: "Parcourir la boutique",
      plaza_hero_stat_1_v: "12 847",
      plaza_hero_stat_1_l: "produits en ligne",
      plaza_hero_stat_2_v: "340+",
      plaza_hero_stat_2_l: "vendeurs australiens",
      plaza_hero_stat_3_v: "4,2 M$",
      plaza_hero_stat_3_l: "récompenses versées",
      plaza_hero_stat_4_v: "19",
      plaza_hero_stat_4_l: "catégories",
      plaza_problem_kicker: "Le problème du shopping aujourd'hui",
      plaza_problem_title: "Dix onglets. Dix mots de passe. Zéro fidélité.",
      plaza_problem_sub: "Vous jonglez déjà entre Amazon, eBay, Kogan, Kmart, Woolies, Coles, Bunnings, JB, Officeworks et une demi-douzaine de boutiques spécialisées. Vous passez des heures à comparer les prix — puis ne recevez rien d'autre qu'un reçu.",
      plaza_problem_1: "Des prix impossibles à comparer d'un site à l'autre",
      plaza_problem_2: "Des vendeurs étrangers se faisant passer pour des locaux",
      plaza_problem_3: "Des points de fidélité enfermés dans une seule boutique",
      plaza_problem_4: "De faux avis de robots et de revendeurs",
      plaza_solution_kicker: "Ce que HustlePlaza fait différemment",
      plaza_solution_title: "Le dernier panier que vous ouvrirez de la journée.",
      plaza_solution_sub: "Une recherche. Un panier. Un paiement. Chaque achat vous rapporte des HustlePoints utilisables dans tout notre écosystème — pas seulement cette boutique.",
      plaza_solution_1_t: "Un panier, tous les vendeurs",
      plaza_solution_1_b: "Achetez chez tous les vendeurs australiens de la plateforme et payez une seule fois. Livraison groupée quand c'est possible.",
      plaza_solution_2_t: "Uniquement des locaux vérifiés",
      plaza_solution_2_b: "Chaque vendeur est vérifié avant de pouvoir mettre en ligne. Pas de robots, pas de faux, pas de mystère de dropshipping.",
      plaza_solution_3_t: "Des récompenses qui voyagent vraiment",
      plaza_solution_3_b: "Les points gagnés ici sont utilisables dans toute entreprise HustleSuite — cafés, hôtels, salons de coiffure, restaurants, toiletteurs.",
      plaza_solution_4_t: "Suivi en direct, un seul écran",
      plaza_solution_4_b: "Tous les colis de tous les vendeurs suivis sur une seule page. Notifications par e-mail et SMS à chaque étape.",
      plaza_rewards_kicker: "Le cercle vertueux HustlePoints",
      plaza_rewards_title: "Gagnez en achetant. Dépensez partout dans le réseau.",
      plaza_rewards_sub: "Chaque dollar dépensé sur HustlePlaza rapporte des HustlePoints. Utilisez-les dans n'importe quel restaurant, café, hôtel, salon de coiffure ou entreprise de services fonctionnant sur HustleSuite — partout dans le réseau.",
      plaza_rewards_step_1: "Achetez sur la marketplace",
      plaza_rewards_step_1_b: "Achetez n'importe quoi, d'un t-shirt à un frigo.",
      plaza_rewards_step_2: "Gagnez des HustlePoints instantanément",
      plaza_rewards_step_2_b: "Les points apparaissent dès l'expédition de votre commande — pas de cartes, pas de cartes perforées, pas d'expiration artificielle.",
      plaza_rewards_step_3: "Dépensez dans tout le réseau",
      plaza_rewards_step_3_b: "Utilisez-les dans les cafés, restaurants, hôtels, salons de coiffure, toiletteurs, artisans.",
      plaza_rewards_step_4: "Ou encaissez-les",
      plaza_rewards_step_4_b: "Convertissez en virement bancaire dès que vous atteignez 50$.",
      plaza_cats_kicker: "Acheter par catégorie",
      plaza_cats_title: "19 catégories. Vendeurs australiens. Stock en direct.",
      plaza_cats_sub: "Parcourez un aperçu de ce qui est disponible en ce moment — ou inscrivez-vous et explorez l'intégralité du catalogue de plus de 12 000 produits.",
      plaza_cats_cta: "Voir toutes les catégories",
      plaza_vendors_kicker: "De vrais vendeurs australiens",
      plaza_vendors_title: "Rencontrez les créateurs, producteurs et boutiques familiales.",
      plaza_vendors_sub: "Chaque vendeur sur HustlePlaza est vérifié par ABN, basé en Australie et intégré personnellement. Pas d'intermédiaires étrangers prétendant être locaux.",
      plaza_how_kicker: "Comment ça marche",
      plaza_how_title: "Trois étapes. Un panier. Des récompenses pour toujours.",
      plaza_how_1_title: "Rejoignez en 30 secondes",
      plaza_how_1_body: "Vérifiez votre e-mail et commencez à parcourir instantanément. Ajoutez un numéro de téléphone quand vous êtes prêt à passer commande.",
      plaza_how_2_title: "Remplissez votre panier",
      plaza_how_2_body: "Achetez chez tous les vendeurs. Un panier, une vue de livraison, un paiement. Paiement via Stripe ou PayPal.",
      plaza_how_3_title: "Gagnez et dépensez partout",
      plaza_how_3_body: "Les HustlePoints apparaissent dès l'expédition de votre commande. Dépensez-les dans n'importe quelle entreprise HustleSuite, ou encaissez-les à partir de 50$.",
      plaza_compare_kicker: "Comment nous nous comparons",
      plaza_compare_title: "HustlePlaza vs les géants",
      plaza_compare_col_us: "HustlePlaza",
      plaza_compare_col_amz: "Amazon AU",
      plaza_compare_col_ebay: "eBay AU",
      plaza_compare_col_kmart: "Kmart / Big W",
      plaza_compare_row_1: "Propriété australienne",
      plaza_compare_row_2: "Panier unique pour tous les vendeurs",
      plaza_compare_row_3: "Récompenses utilisables EN DEHORS de cette boutique",
      plaza_compare_row_4: "Encaissement des récompenses sur votre compte",
      plaza_compare_row_5: "Service client hébergé en Australie",
      plaza_trust_kicker: "Pourquoi nous vérifions",
      plaza_trust_title: "Vrais clients. Vrais avis.",
      plaza_trust_body: "Nous vérifions votre e-mail à l'inscription, votre numéro de mobile avant le paiement, et exigeons une photo en direct avant de pouvoir publier des avis, discuter avec les vendeurs ou effectuer des achats de valeur élevée. Cela ralentit les fraudeurs et maintient des prix bas pour vous.",
      plaza_trust_1: "Vérification e-mail pour rejoindre",
      plaza_trust_2: "Vérification SMS pour payer",
      plaza_trust_3: "Vérification photo en direct pour les avis et le chat",
      plaza_trust_4: "Vérifications d'identité des vendeurs à l'intégration",
      plaza_test_kicker: "Ce que disent nos premiers membres",
      plaza_test_title: "Directement de vrais clients.",
      plaza_test_1_body: "J'ai utilisé mes points HustlePlaza pour payer le dîner dans un café près du travail. Je ne croyais vraiment pas que ça marcherait jusqu'à ce que la serveuse sourie et dise 'c'est réglé'.",
      plaza_test_1_name: "Priya S.",
      plaza_test_2_body: "Six vendeurs, une fenêtre de livraison, un e-mail de confirmation. J'aurais normalement six mots de passe et six numéros de suivi. Ça marche tout simplement.",
      plaza_test_2_name: "Marcus T.",
      plaza_test_3_body: "En tant que petit producteur de miel, nous sommes enfin payés le jour même, avec de vrais avis et sans commission de 40%. HustlePlaza m'a fait passer de 'petite activité' à temps plein en huit mois.",
      plaza_test_3_name: "Kate & Dave",
      plaza_faq_kicker: "Réponses",
      plaza_faq_title: "Les questions qu'on nous pose chaque semaine",
      plaza_faq_1_q: "HustlePlaza est-il vraiment de propriété australienne ?",
      plaza_faq_1_a: "Oui. HustlePlaza est 100% de propriété australienne, hébergé sur des serveurs australiens, géré par une équipe australienne. Chaque vendeur passe par une vérification d'identité avant que sa première annonce ne soit mise en ligne.",
      plaza_faq_2_q: "Comment fonctionnent les récompenses ?",
      plaza_faq_2_a: "Chaque dollar dépensé sur HustlePlaza vous rapporte des HustlePoints. Les points apparaissent dès l'expédition de votre commande et n'expirent jamais. Dépensez-les dans toute entreprise fonctionnant sur HustleSuite, ou encaissez-les sur votre compte une fois que vous en avez suffisamment économisé.",
      plaza_faq_3_q: "Pourquoi vérifiez-vous les photos ?",
      plaza_faq_3_a: "Pour que les avis que vous lisez soient réels. La vérification photo est un contrôle par selfie en direct effectué via l'appareil photo de votre téléphone — la même technique utilisée par les banques — et cela empêche les robots, faux comptes et acteurs malveillants de manipuler la plateforme.",
      plaza_faq_4_q: "Que se passe-t-il si ma commande n'arrive pas ?",
      plaza_faq_4_a: "Chaque commande est couverte par notre HustleGuarantee : si le colis n'arrive pas ou ne correspond pas à la description, nous vous remboursons dans les 3 jours ouvrables, puis nous nous occupons du vendeur. Vous n'êtes jamais lésé.",
      plaza_faq_5_q: "Les informations de ma carte sont-elles sécurisées ?",
      plaza_faq_5_a: "Nous ne voyons ni ne stockons votre numéro de carte. Tous les paiements passent par Stripe (PCI-DSS Niveau 1) ou PayPal, tous deux avec authentification 3D Secure qui vous protège contre les fraudes sur cartes volées.",
      plaza_faq_6_q: "Livrez-vous à l'international ?",
      plaza_faq_6_a: "Pas encore. HustlePlaza est réservé à l'Australie au lancement — c'est ainsi que nous pouvons promettre une livraison dans la semaine pour la plupart des articles et un support dans votre fuseau horaire. La Nouvelle-Zélande est prévue pour 2026.",
      plaza_faq_7_q: "Combien coûte l'adhésion ?",
      plaza_faq_7_a: "Rien. L'adhésion est gratuite pour toujours. Les vendeurs paient une petite commission par vente — les acheteurs paient 0$ pour rejoindre, 0$ pour gagner, 0$ pour utiliser.",
      plaza_faq_8_q: "Puis-je vendre sur HustlePlaza ?",
      plaza_faq_8_a: "Absolument. Rendez-vous sur hustlesuite.au pour vous intégrer en tant que vendeur — approbation le jour même pour les entreprises australiennes vérifiées, plus vos 30 premiers jours sans commission.",
      plaza_final_kicker: "Tarif fondateur — places limitées",
      plaza_final_title: "Soyez parmi les premiers — verrouillez le tarif fondateur.",
      plaza_final_sub: "Inscrivez-vous aujourd'hui comme Membre HustleSuite. Vous serez le premier informé du lancement de chaque nouvelle fonctionnalité, et votre compte conservera les récompenses et tarifs de niveau fondateur à vie.",
      plaza_final_bonus: "Les fondateurs ne perdent jamais leur tarif fondateur. Gratuit pour toujours, annulation à tout moment.",
      plaza_final_cta: "S'inscrire comme Membre Fondateur",
      plaza_final_disclosure: "Aucune carte requise. Vérification e-mail + mobile uniquement.",
      plaza_roadmap_kicker: "Ce que nous construisons ensuite",
      plaza_roadmap_title: "Six outils conçus pour vous faciliter la vie.",
      plaza_roadmap_sub: "HustleSuite est plus qu'une boutique — c'est une couche de productivité complète. Voici ce qui est en préparation.",
      plaza_roadmap_soon: "Bientôt disponible",
      plaza_roadmap_next: "Prochainement dans la file",
      plaza_roadmap_1_t: "HustleDictate — reconnaissance vocale sur n'importe quel formulaire",
      plaza_roadmap_1_b: "Extension Chrome. Appuyez sur Alt+M, parlez, et n'importe quel champ sur n'importe quel site se remplit. Demandes immobilières, factures, documents NDIS — fait en deux fois moins de temps.",
      plaza_roadmap_2_t: "HustleConnect Éditer-Partout",
      plaza_roadmap_2_b: "Pour les propriétaires WordPress : modifiez votre site en direct depuis n'importe quelle page avec un seul raccourci. Fini les allers-retours vers wp-admin.",
      plaza_roadmap_3_t: "Bouton universel Enregistrer-dans-HustleSuite",
      plaza_roadmap_3_b: "Clic droit sur n'importe quelle page web — annonce immobilière, article, produit, restaurant — et elle arrive dans le bon module prête à être traitée.",
      plaza_roadmap_4_t: "Comparateur de prix HustlePlaza",
      plaza_roadmap_4_b: "Parcourez Amazon, eBay ou Kmart — une puce flottante affiche le même produit sur HustlePlaza avec aperçu de vos récompenses.",
      plaza_roadmap_5_t: "Collecteur d'avis",
      plaza_roadmap_5_b: "Synchronisation automatique des nouveaux avis Google, Facebook et TripAdvisor vers votre tableau de bord HustleSuite. Économise ~20 minutes par semaine.",
      plaza_roadmap_6_t: "Barre latérale HustleSuite",
      plaza_roadmap_6_b: "Panneau latéral Chrome avec commandes en direct, KPI, publication et facturation rapides. Toute votre entreprise dans une barre à côté de chaque onglet.",
      plaza_footer_owned: "Propriété et hébergement australiens",
      plaza_footer_terms: "Conditions",
      plaza_footer_privacy: "Confidentialité",
      plaza_footer_contact: "Contact",
      plaza_footer_vendors: "Vendre sur HustlePlaza",
      plaza_footer_help: "Centre d'aide",
      plaza_footer_tagline: "Membre de la famille HustleSuite — une connexion, tous les services.",
      verify_gate_title: "Presque terminé — inscription rapide",
      verify_gate_body: "HustlePlaza est un panier d'achat offrant de nombreux avantages, notamment des récompenses échangeables utilisables non seulement sur cette plateforme mais aussi dans toute autre entreprise utilisant la plateforme HustlePlaza ou inscrite avec HustleSuite. Certaines de ces entreprises incluent des restaurants, cafés, hôtels, salons de coiffure et même des toiletteurs pour animaux. Pour continuer à offrir d'excellents prix et services, nous devons nous assurer que vous êtes réel et vous vérifier. Appuyez sur le bouton Rejoindre pour continuer.",
      verify_gate_join: "Rejoindre HustleSuite",
      verify_gate_signin: "J'ai déjà un compte",
      verify_gate_cancel: "Pas maintenant",
      verify_email_title: "Vérifiez votre e-mail",
      verify_email_body: "Nous enverrons un code à 6 chiffres dans votre boîte de réception.",
      verify_email_placeholder: "vous@exemple.com",
      verify_email_send: "Envoyer le code",
      verify_email_code_ph: "Code à 6 chiffres",
      verify_email_check: "Vérifier",
      verify_email_sent: "Code envoyé — consultez votre boîte de réception.",
      verify_sms_title: "Vérifiez votre téléphone",
      verify_sms_body: "Ajoutez un numéro de mobile pour recevoir par SMS les mises à jour de commande et confirmer les achats importants.",
      verify_sms_placeholder: "+61412345678",
      verify_sms_send: "Envoyer SMS",
      verify_sms_check: "Vérifier",
      verify_sms_sent: "Code envoyé — consultez votre téléphone.",
      verify_photo_title: "Vérification photo",
      verify_photo_body: "Scannez le code QR avec votre téléphone pour prendre un selfie en direct. Requis pour le chat, les achats de valeur élevée et la publication dans Meetup / Tackle.",
      verify_photo_waiting: "En attente de votre téléphone…",
      verify_photo_verified: "Photo vérifiée — vous êtes prêt.",
      travel_cta_start: "Rejoindre gratuitement",
      travel_hero_kicker: "HustleTravel",
      travel_hero_title_1: "Une adhésion.",
      travel_hero_title_2: "Partout en Australie.",
      travel_hero_sub: "Hôtels, vols, expériences, planification de road trip et location de voiture — réservés au meilleur tarif disponible et vous rapportant des HustlePoints sur chaque dollar dépensé.",
      travel_cta_plan: "Planifier un road trip",
      travel_cta_hotels: "Rechercher des hôtels",
      travel_svc_hotels_t: "Hôtels et appartements",
      travel_svc_hotels_b: "Compare plus de 200 sites de réservation en une seule recherche — métarecherche propulsée par Trivago.",
      travel_svc_flights_t: "Vols",
      travel_svc_flights_b: "Intérieurs et internationaux, avec plans de sièges en temps réel et règles tarifaires.",
      travel_svc_experiences_t: "Expériences et circuits",
      travel_svc_experiences_b: "Journées récif, dégustations en cave, musique live, excursions familiales.",
      travel_svc_cars_t: "Location de voiture",
      travel_svc_cars_b: "Toutes les grandes marques plus des indépendants locaux — retrait dans n'importe quel aéroport australien.",
      travel_svc_insurance_t: "Assurance voyage",
      travel_svc_insurance_b: "Souscrite par des assureurs australiens régulés par l'APRA. Comparez sur un seul écran.",
      travel_svc_roadtrip_t: "Planificateur de road trip",
      travel_svc_roadtrip_b: "Itinéraires adaptés à l'autonomie des VE avec chargeurs, arrêts nocturnes et estimations de coûts.",
      travel_final_kicker: "Tarif fondateur",
      travel_final_title: "Verrouillez les récompenses de niveau Fondateur à vie.",
      travel_final_sub: "Inscrivez-vous une fois avec HustleSuite. Réservez tout — hôtels, vols, expériences, assurance — sur HustleTravel et gagnez des HustlePoints utilisables dans toutes les entreprises du réseau.",
      travel_final_cta: "S'inscrire comme Membre Fondateur",
      travel_final_disclosure: "Politique du meilleur tarif : résultats classés par meilleure valeur pour le voyageur, pas par commission.",
      dictate_hero_kicker: "HustleDictate",
      dictate_hero_title_1: "Parlez.",
      dictate_hero_title_2: "N'importe quel formulaire. N'importe quel site.",
      dictate_hero_sub: "L'extension Chrome unique qui transforme n'importe quel champ de texte en microphone de dictée vocale professionnel — propulsé par Whisper.",
      dictate_cta_install: "Ajouter à Chrome",
      dictate_uc_re_t: "Notes de location immobilière",
      dictate_uc_re_b: "Dictez les rapports d'inspection et les messages aux locataires directement dans votre système de gestion.",
      dictate_uc_ndis_t: "Notes de progression NDIS",
      dictate_uc_ndis_b: "Énoncez la note de garde pendant que le souvenir est frais — mains libres, sans frappe.",
      dictate_uc_invoice_t: "Descriptions de factures",
      dictate_uc_invoice_b: "Descriptions de lignes dictées directement dans Xero, MYOB ou QuickBooks.",
      dictate_uc_email_t: "Réponses e-mail longues",
      dictate_uc_email_b: "Énoncez les réponses dans Gmail, Outlook ou n'importe quel webmail — trois fois plus rapide que la frappe.",
      dictate_final_kicker: "Gratuit pour les Membres Fondateurs",
      dictate_final_title: "Chaque clavier est maintenant un microphone.",
      dictate_final_sub: "Gratuit pour les Membres Fondateurs HustleSuite. 5$/mois sinon — annulation à tout moment.",
      dictate_final_cta: "Ajouter à Chrome — gratuit",
      "finder.cat.roadtrip": "Road trip",
      "finder.hotels.title": "Trouver un hôtel à proximité",
      "finder.hotels.sub": "Compare plus de 200 sites de réservation en une seule recherche — propulsé par Trivago.",
      "finder.hotels.where": "Où",
      "finder.hotels.where_ph": "Ville ou banlieue (ex. Byron Bay, NSW)",
      "finder.hotels.checkin": "Arrivée",
      "finder.hotels.checkout": "Départ",
      "finder.hotels.adults": "Adultes",
      "finder.hotels.search": "Rechercher des hôtels",
      "finder.hotels.disclosure": "Ouvre Trivago — nous pouvons gagner une commission sur les réservations.",
      /* AUTO-TRANSLATED fr END */
      nav_menu: "Carte",
      nav_book: "Réserver une table",
      nav_reviews: "Avis",
      nav_about: "À propos",
      nav_account: "Mon compte",
      nav_signin: "Connexion",
      cart_open: "Ouvrir le panier",
      cart_title: "Votre commande",
      cart_empty: "Votre panier est vide",
      cart_subtotal: "Sous-total",
      cart_continue: "Continuer",
      cart_pickup_date: "Date de retrait",
      cart_pickup_time: "Heure de retrait",
      cart_pay_on_pickup: "Payer à la livraison",
      cart_pay_now: "Payer maintenant",
      cart_place_order: "Confirmer la commande",
      add_to_order: "Ajouter au panier",
      menu_search_ph: "Rechercher sur la carte…",
      menu_chef_specials: "Spécialités du chef",
      menu_under_kcal: "Moins de",
      menu_kcal_unit: "kcal",
      menu_clear_filters: "Effacer",
      menu_dietary_label: "Régime",
      menu_no_matches: "Aucun résultat",
      menu_relax_filters: "Essayez une autre recherche ou réduisez vos filtres.",
      menu_clear_all: "Effacer tous les filtres",
      book_title: "Réserver une table",
      book_name: "Nom",
      book_email: "E-mail",
      book_phone: "Téléphone",
      book_guests: "Convives",
      book_date: "Date",
      book_time: "Heure",
      book_requests: "Demandes particulières",
      book_submit: "Confirmer la réservation",
      footer_rights: "Tous droits réservés.",
      lang_label: "Langue",
      welcome_back: "Bon retour",
      sign_out: "Se déconnecter",
    
      /* AUTO-TRANSLATED finder.* (added) */
      "finder.title": "HustleFinder",
      "finder.tagline": "Trouvez le lieu public le plus proche — données réelles, dans toute l'Australie.",
      "finder.search_placeholder": "Rechercher des lieux, rues ou banlieues…",
      "finder.hint": "Commencez à taper ci-dessus, touchez une catégorie ou utilisez « Près de moi ».",
      "finder.near_me": "Près de moi",
      "finder.searching": "Recherche en cours…",
      "finder.suburb.placeholder": "Filtrer par banlieue (ex. Darwin, NT)…",
      "finder.suburb.clear": "Effacer",
      "finder.search_in": "Rechercher dans {{cat}}",
      "finder.empty": "Aucun résultat — essayez une autre catégorie ou terme de recherche.",
      "finder.meta.title": "HustleFinder — annuaire gratuit des banlieues australiennes",
      "finder.meta.desc": "Recherchez des entreprises locales, chargeurs, cafés, toilettes et plus encore dans toutes les banlieues australiennes — 100% gratuit.",
      "finder.footer.credits": "Crédits et attribution des données",
      "finder.footer.terms": "Conditions",
      "finder.cat.toilet": "Toilettes",
      "finder.cat.fountain": "Fontaines à eau",
      "finder.cat.transport": "Transport",
      "finder.cat.park": "Parcs",
      "finder.cat.medical": "Médical",
      "finder.cat.school": "Écoles",
      "finder.cat.library": "Bibliothèques",
      "finder.cat.petrol": "Essence",
      "finder.cat.kidzone": "Zone enfants",
      "finder.cat.puzzles": "Jeux de réflexion",
      /* AUTO-TRANSLATED rt.* START */
      "rt.brand_name": "Roadtrip Planner",
      "rt.page_title": "Planificateur de Road-trip EV Australien",
      "rt.from": "De",
      "rt.to": "À",
      "rt.from_placeholder": "Banlieue, ville ou commune (ex : Newcastle NSW)",
      "rt.to_placeholder": "Banlieue, ville ou commune (ex : Hobart TAS)",
      "rt.stop": "STOP",
      "rt.stop_placeholder": "Arrêt intermédiaire",
      "rt.add_stop": "Ajouter un arrêt",
      "rt.upgrade_pro": "Passez à Pro pour jusqu'à 20 arrêts",
      "rt.reverse": "Inverser (calculer le retour)",
      "rt.from_current": "Depuis ma position",
      "rt.range_per_charge": "Autonomie par charge (km)",
      "rt.connector": "Connecteur",
      "rt.connector_any": "Tous",
      "rt.drive_hours_day": "Heures de conduite/jour",
      "rt.accommodation": "Hébergement",
      "rt.accom_any": "Tout (BnB · Hôtel · Caravane)",
      "rt.accom_bnb": "BnB",
      "rt.accom_hotel": "Hôtel",
      "rt.accom_caravan": "Parc de caravanes",
      "rt.min_rating": "Note minimale",
      "rt.any_rating": "Toutes les notes",
      "rt.only_5star": "5★ uniquement",
      "rt.schedule": "Planning :",
      "rt.depart_at": "Départ à",
      "rt.arrive_by": "Arrivée avant",
      "rt.hotel_checkin": "Arrivée hôtel",
      "rt.hotel_checkout": "Départ hôtel",
      "rt.overnight_hint": "Les étapes de nuit ne seront pas planifiées hors de ces horaires.",
      "rt.whos_in_car": "Qui est dans la voiture ?",
      "rt.adults": "Adultes",
      "rt.kids": "Enfants",
      "rt.ages": "Âges",
      "rt.show_attractions": "Afficher sites touristiques",
      "rt.show_kids": "Afficher arrêts adaptés enfants",
      "rt.overnight_stays": "Nuitées",
      "rt.fast_chargers_only": "Chargeurs rapides uniquement",
      "rt.hotel_must_charger": "L'hôtel doit avoir un chargeur",
      "rt.pet_only": "Hébergements acceptant les animaux",
      "rt.safety": "Sécurité",
      "rt.safety_90": "90 % (plat)",
      "rt.safety_85": "85 % (par défaut)",
      "rt.safety_80": "80 % (collines / remorquage)",
      "rt.safety_70": "70 % (charge lourde)",
      "rt.save_trip": "Enregistrer trajet",
      "rt.plan_my_trip": "Planifier mon trajet",
      "rt.refresh_trip": "Actualiser le trajet",
      "rt.planning": "Planification…",
      "rt.resume_title": "Reprendre le trajet ici",
      "rt.resume_body": "Nous avons détecté votre position. Entrez l'autonomie restante affichée au tableau de bord pour planifier la suite selon ce que vous avez — et non une charge complète.",
      "rt.remaining_range": "Autonomie restante (km)",
      "rt.cancel": "Annuler",
      "rt.replan_from_here": "Replanifier d'ici",
      "rt.save_modal_title": "Nommer ce trajet",
      "rt.save_modal_body": "Les trajets enregistrés figurent dans votre profil — rouvrez-les à tout moment et cliquez sur Replanifier.",
      "rt.save_name_placeholder": "ex : Brisbane → Sydney via Byron",
      "rt.your_saved_trips": "Vos trajets enregistrés",
      "rt.custom_location": "Position personnalisée",
      "rt.trip_summary": "Résumé du trajet",
      "rt.itinerary": "Itinéraire",
      "rt.no_stops_needed": "Aucun arrêt nécessaire — tout le trajet tient dans votre autonomie.",
      "rt.chargers_in_corridor": "chargeurs sur le trajet",
      "rt.planned_stops": "arrêts prévus",
      "rt.overnight_stop": "étape de nuit",
      "rt.overnight_stops": "étapes de nuit",
      "rt.suggested_detours": "Détours suggérés — cochez pour ajouter",
      "rt.replan_with_detours": "Replanifier avec détours",
      "rt.attractions_label": "Attraction",
      "rt.kids_label": "Enfants",
      "rt.km_from_start": "km du départ",
      "rt.open_driving": "Mode conduite",
      "rt.big_font_hint": "Grande police, adapté au support voiture. Compatible avec CarPlay/Android Auto via navigateur.",
      "rt.check_price_book": "Prix & réservation sur Trivago ↗",
      "rt.visit_site": "Visiter le site ↗",
      "rt.charger_onsite": "Chargeur sur place",
      "rt.leg_exceeds_range": "Cette étape dépasse votre autonomie — vous seriez à court d'énergie avant d'atteindre ce chargeur. Réduisez l'autonomie estimée ou ajoutez un appoint intermédiaire.",
      "rt.add_waypoint_replan": "Ajouter étape & replanifier",
      /* AUTO-TRANSLATED rt.* END */
                      /* AUTO-TRANSLATED hm.* START */
      "hm.nav.hustlemeet": "HustleMeet",
      "hm.nav.circles": "Circles",
      "hm.nav.matchmaker": "Matchmaker",
      "hm.nav.meet": "Rencontrer",
      "hm.nav.overview": "HustleMeetups",
      "hm.overview.page_title": "HustleMeetups — trois façons chaleureuses de trouver votre monde.",
      "hm.overview.kicker": "Une solution HustleSuite",
      "hm.overview.hero_title": "Trois façons chaleureuses de trouver votre monde.",
      "hm.overview.hero_title_accent": "votre monde",
      "hm.overview.hero_sub": "HustleMeetups est le moyen le plus bienveillant en Australia pour nouer de vraies amitiés — choisissez l'ambiance qui vous convient. Que vous souhaitiez un groupe de course hebdomadaire, un dîner mensuel avec votre bande actuelle ou une table de nouveaux visages sélectionnés par IA, il y a une place pour vous.",
      "hm.overview.tile_meet_kicker": "HustleMeet",
      "hm.overview.tile_meet_title": "Trouvez votre monde. Faites ce que vous aimez.",
      "hm.overview.tile_meet_body": "Le rendez-vous en Australia pour les groupes axés sur les passions. Coureurs, codeurs, investisseurs, marcheurs, danseurs, clubs de petit-déjeuner, cercles de lecture — des centaines de groupes, des événements réels chaque semaine. Venez une fois, ou toutes les semaines.",
      "hm.overview.tile_meet_cta": "Explorer les groupes",
      "hm.overview.tile_circles_kicker": "Circles",
      "hm.overview.tile_circles_title": "Votre bande. Une nouvelle table à chaque fois.",
      "hm.overview.tile_circles_body": "Amenez vos amis habituels. Nous réservons un nouveau restaurant, organisons le quiz, distribuons les cartes. Vous n'avez qu'à venir. La meilleure façon de garder votre bande soudée — sans que personne n'ait à l'organiser.",
      "hm.overview.tile_circles_cta": "Lancer un Circle",
      "hm.overview.tile_matchmaker_kicker": "Matchmaker",
      "hm.overview.tile_matchmaker_title": "L'IA choisit la table. Vous venez.",
      "hm.overview.tile_matchmaker_body": "Répondez à un quiz de 10 minutes. Le lendemain, nous vous installons avec des inconnus qui vous sembleront être de vieux amis dès le dessert. Une table différente à chaque fois.",
      "hm.overview.tile_matchmaker_cta": "Faire le quiz",
      "hm.overview.trust_kicker": "Pourquoi ça marche",
      "hm.overview.trust_title": "La confiance d'abord. La table ensuite.",
      "hm.overview.trust_1_title": "Membres vérifiés",
      "hm.overview.trust_1_body": "Chaque groupe commence par une vérification d'identité réelle + un score de fiabilité. Pas de comptes jetables.",
      "hm.overview.trust_2_title": "Vrais hôtes",
      "hm.overview.trust_2_body": "Chaque groupe est dirigé par un hôte approuvé qui gère l'ambiance, définit le rythme et veille sur la salle.",
      "hm.overview.trust_3_title": "L'amitié avant tout",
      "hm.overview.trust_3_body": "Tout ici est conçu pour de vraies amitiés, pas pour des rencontres amoureuses. Jamais.",
      "hm.overview.trust_4_title": "Modération zéro tolérance",
      "hm.overview.trust_4_body": "Un signalement, un examen, une décision. Les mauvais éléments sont exclus le soir même.",
      "hm.overview.footer_line": "Une solution HustleSuite — détenue et hébergée en Australia.",
      "hm.landing.page_title": "HustleMeet · Trouvez votre monde. Faites ce que vous aimez.",
      "hm.landing.kicker": "Le réseau des passionnés en Australia",
      "hm.landing.hero_title": "Trouvez votre monde. Faites ce que vous aimez.",
      "hm.landing.hero_title_accent": "votre monde",
      "hm.landing.hero_sub": "HustleMeet est le repaire en Australia pour les groupes axés sur les passions. Développeurs débuggeant au déjeuner. Coureurs de fond à 6h du matin. Investisseurs se réunissant chaque mois. Groupes de marche, troupes de danse, clubs de petit-déjeuner, soirées jeux de société. Choisissez un groupe, réservez le prochain événement et venez.",
      "hm.landing.cta_explore_circles": "Parcourir les groupes",
      "hm.landing.cta_try_matchmaker": "Lancer votre propre groupe",
      "hm.landing.trust_verified": "Membres vérifiés",
      "hm.landing.trust_venues": "Hôtes d'événements approuvés",
      "hm.landing.trust_friendship": "Amitié d'abord — jamais de dating",
      "hm.landing.trust_moderation": "Modération zéro tolérance",
      "hm.landing.subbrand_circles_kicker": "Circles",
      "hm.landing.subbrand_circles_title": "Déjà une bande ? Donnez-lui un foyer permanent.",
      "hm.landing.subbrand_circles_body": "Circles s'adresse aux groupes d'amis qui se voient déjà — club de lecture, soirée poker, équipe de course, collègues de travail. Nous gérons le lieu, les rappels et le planning. Vous n'avez qu'à venir.",
      "hm.landing.subbrand_circles_b1": "Lieux tournants, un seul planning",
      "hm.landing.subbrand_circles_b2": "Rappels auto + confirmations faciles",
      "hm.landing.subbrand_circles_b3": "Quiz, cartes et soirées à thème à la demande",
      "hm.landing.subbrand_circles_cta": "Lancer un Circle",
      "hm.landing.subbrand_matchmaker_kicker": "Matchmaker",
      "hm.landing.subbrand_matchmaker_title": "L'IA choisit la table. Vous venez.",
      "hm.landing.subbrand_matchmaker_body": "Un quiz de 10 minutes — personnalité, valeurs, disponibilités. Notre Matchmaker vous installe avec des inconnus qui vous sembleront être de vieux amis dès le dessert. Une table différente à chaque fois.",
      "hm.landing.subbrand_matchmaker_b1": "Score de personnalité Big-5 et valeurs",
      "hm.landing.subbrand_matchmaker_b2": "Invitations immédiates quand votre affinité est de 0.70 ou plus",
      "hm.landing.subbrand_matchmaker_b3": "Apprend de chaque dîner que vous faites",
      "hm.landing.subbrand_matchmaker_cta": "Faire le quiz",
      "hm.landing.how_kicker": "Comment fonctionne HustleMeet",
      "hm.landing.how_title": "Quatre étapes. Trouvez votre groupe. Venez.",
      "hm.landing.how_step1_ttl": "Choisissez vos intérêts",
      "hm.landing.how_step1_sub": "Course, code, investissement, cuisine, musique, marche — dites-nous ce que vous aimez et où vous vivez.",
      "hm.landing.how_step2_ttl": "Parcourez les groupes près de chez vous",
      "hm.landing.how_step2_sub": "Chaque groupe affiche son hôte, son rythme et son prochain événement. Prenez la température avant de vous engager.",
      "hm.landing.how_step3_ttl": "Confirmez pour le prochain événement",
      "hm.landing.how_step3_sub": "Un clic, une invitation calendrier, un rappel clair la veille. Certains événements sont gratuits, d'autres payants — le prix est annoncé d'emblée.",
      "hm.landing.how_step4_ttl": "Venez et connectez",
      "hm.landing.how_step4_sub": "Rencontrez l'hôte, rencontrez le groupe, vivez l'activité. Revenez la semaine suivante si le courant est passé.",
      "hm.landing.stats_kicker": "La nouvelle façon de se rencontrer en Australia",
      "hm.landing.stats_title": "La raison pour laquelle les gens reviennent.",
      "hm.landing.stats_92_sub": "Sentent qu'ils ont eu un déclic avec au moins une personne lors de l'événement",
      "hm.landing.stats_68_sub": "Confirment leur présence au prochain événement dans la semaine",
      "hm.landing.stats_85_sub": "Restent en contact avec quelqu'un du groupe",
      "hm.landing.why_kicker": "Pourquoi ça fonctionne",
      "hm.landing.why_title": "Vrais intérêts. Vrais hôtes. Vrais événements.",
      "hm.landing.why_1_ttl": "Les intérêts d'abord, pas le lieu",
      "hm.landing.why_1_body": "Les membres de votre groupe se soucient réellement de ce qui vous passionne — c'est le raccourci vers une amitié réelle.",
      "hm.landing.why_2_ttl": "De vrais hôtes d'événements",
      "hm.landing.why_2_body": "Chaque groupe a un hôte approuvé qui gère l'ambiance, définit le rythme et intervient en cas de problème. Pas un admin Facebook. Une vraie personne.",
      "hm.landing.why_3_ttl": "La régularité compte",
      "hm.landing.why_3_body": "De nouveaux événements chaque semaine. Quelque chose à attendre avec impatience. Un rythme qui transforme des inconnus en votre bande du mardi soir.",
      "hm.landing.why_4_ttl": "Sûr et sécurisé",
      "hm.landing.why_4_body": "Membres vérifiés, lieux publics, tolérance zéro pour le manque de respect. Notre équipe d'assistance est disponible avant, pendant et après chaque événement.",
      "hm.landing.why_5_ttl": "L'amitié d'abord",
      "hm.landing.why_5_body": "Ce n'est pas du dating. C'est une communauté. De vrais intérêts, de vraies personnes, sans balayage d'écran au milieu.",
      "hm.landing.why_6_ttl": "Assez petit pour compter",
      "hm.landing.why_6_body": "La plupart des groupes limitent les événements à 4-15 places. Assez grand pour susciter de vraies conversations. Assez petit pour que personne ne soit oublié.",
      "hm.landing.cities_kicker": "Où nous sommes présents",
      "hm.landing.cities_title": "Des groupes se réunissent partout en Australia & NZ.",
      "hm.landing.cities_sub_zero": "Premiers groupes en cours d'ouverture dans plus de 15 villes — rejoignez-nous tôt et façonnez l'ambiance.",
      "hm.landing.cities_sub_some": "Groupes actifs dans plus de 15 métropoles. Quelque chose dans votre ville, dès cette semaine.",
      "hm.landing.cities_sub_one": "{{count}} groupe actif · de nouveaux événements chaque semaine · de nouvelles villes ajoutées régulièrement.",
      "hm.landing.cities_sub_many": "{{count}} groupes actifs · de nouveaux événements chaque semaine · de nouvelles villes ajoutées régulièrement.",
      "hm.landing.forme_kicker": "C'est pour vous si…",
      "hm.landing.forme_title": "Vous voulez de vrais intérêts. De vraies personnes. De vrais événements.",
      "hm.landing.forme_1": "Vous en avez assez de la fatigue des applis de rencontre et voulez vraiment rencontrer des gens autour de ce que vous aimez faire.",
      "hm.landing.forme_2": "Vous avez emménagé dans une nouvelle ville et avez besoin d'une bande chaleureuse, pas d'un autre groupe de discussion.",
      "hm.landing.forme_3": "Vous privilégiez la profondeur à la diffusion — un excellent groupe vaut mieux que cinquante abonnements.",
      "hm.landing.forme_4": "Vous croyez que des intérêts partagés + deux heures fixes dans la vraie vie est le vrai secret.",
      "hm.landing.notme_kicker": "Ce n'est pas pour vous si…",
      "hm.landing.notme_title": "HustleMeet ne convient pas à tout le monde.",
      "hm.landing.notme_1": "Vous ne pouvez pas vous engager à venir — les hôtes et les membres comptent sur votre présence.",
      "hm.landing.notme_2": "Vous cherchez un service de rencontre — l'amitié passe avant tout ici.",
      "hm.landing.notme_3": "Vous voulez diffuser sans rencontrer personne — cela ne fonctionne qu'en personne.",
      "hm.landing.notme_4": "Vous attendez des avantages gratuits sans fin — certains événements sont payants. Le prix est toujours indiqué.",
      "hm.landing.safety_kicker": "Notre engagement sécurité",
      "hm.landing.safety_title": "Chaleureux, mais jamais imprudent.",
      "hm.landing.safety_1_ttl": "Lieux publics",
      "hm.landing.safety_1_body": "Chaque événement se déroule dans un lieu public avec du personnel — un café, un restaurant, un parc, une salle de sport ou un studio. Jamais chez quelqu'un, sauf si chaque participant y consent.",
      "hm.landing.safety_2_ttl": "Normes communautaires",
      "hm.landing.safety_2_body": "Tolérance zéro pour le manque de respect, le harcèlement ou la malhonnêteté. Les membres qui enfreignent les règles sont exclus immédiatement.",
      "hm.landing.safety_3_ttl": "Confidentialité protégée",
      "hm.landing.safety_3_body": "Vos coordonnées ne sont jamais partagées publiquement. Vous contrôlez avec qui vous restez connecté après chaque événement.",
      "hm.landing.safety_4_ttl": "Assistance à l'écoute",
      "hm.landing.safety_4_body": "Notre équipe est disponible avant, pendant et après chaque événement si vous avez des questions ou des inquiétudes.",
      "hm.landing.bottom_title": "Votre prochain groupe préféré est à portée de clic.",
      "hm.landing.bottom_sub": "Choisissez votre voie — rejoignez un groupe existant, amenez votre bande dans un Circle, ou laissez le Matchmaker vous installer à une table qui vous correspond déjà.",
      "hm.landing.bottom_cta_circles": "Lancer un Circle",
      "hm.landing.bottom_cta_mm": "Faire le quiz Matchmaker",
      "hm.circles.page_title": "HustleMeet Circles · Votre bande, un nouveau lieu à chaque fois",
      "hm.circles.kicker": "HustleMeet · Circles",
      "hm.circles.hero_title": "Votre bande. Une nouvelle table à chaque fois.",
      "hm.circles.hero_title_accent": "Votre bande",
      "hm.circles.hero_sub": "Déjà vos amis ? Circles est le moyen le plus simple de rester proches. Nous réservons le lieu, organisons le quiz, distribuons les cartes. Vous n'avez qu'à venir — même bande, nouvelle soirée, nouveau lieu.",
      "hm.circles.cta_find": "Lancer un Circle",
      "hm.circles.cta_host_hero": "Rejoindre un Circle ouvert",
      "hm.circles.trust_venue": "Lieux vérifiés par roulement",
      "hm.circles.trust_party": "4-12 amis par Circle",
      "hm.circles.trust_verified": "Membres vérifiés",
      "hm.circles.trust_friendship": "De vraies amitiés, préservées",
      "hm.circles.how_kicker": "Comment ça marche",
      "hm.circles.how_title": "Quatre étapes. Votre bande reste une bande.",
      "hm.circles.how_step1_ttl": "Lancez votre Circle",
      "hm.circles.how_step1_sub": "Nommez-le, invitez vos amis (jusqu'à 12) et choisissez un rythme — hebdomadaire, bimensuel ou mensuel.",
      "hm.circles.how_step2_ttl": "Nous choisissons le lieu",
      "hm.circles.how_step2_sub": "À chaque événement, nous suggérons un nouvel endroit — restaurant, soirée quiz, salle de cartes, bar à vin. Ou verrouillez un favori.",
      "hm.circles.how_step3_ttl": "Chacun confirme en un clic",
      "hm.circles.how_step3_sub": "Rappels auto, partage des frais, places prépayées pour que les désistements ne gâchent pas le plan. L'addition est réglée avant que l'entrée n'arrive.",
      "hm.circles.how_step4_ttl": "Gardez le rythme",
      "hm.circles.how_step4_sub": "Une soirée dans l'agenda qui n'a plus jamais besoin d'être organisée. Le groupe se soude au fil des mois, pas des minutes.",
      "hm.circles.cadence_weekly_ttl": "Hebdomadaire",
      "hm.circles.cadence_weekly_sub": "Chaque semaine",
      "hm.circles.cadence_weekly_body": "Pour les bandes qui veulent un rituel le mardi soir — mêmes visages, nouveau lieu à chaque fois.",
      "hm.circles.cadence_fortnight_ttl": "Bimensuel",
      "hm.circles.cadence_fortnight_sub": "Toutes les 2 semaines",
      "hm.circles.cadence_fortnight_body": "Le juste milieu — régulier sans être trop exigeant pour les agendas chargés.",
      "hm.circles.cadence_monthly_ttl": "Mensuel",
      "hm.circles.cadence_monthly_sub": "Chaque mois",
      "hm.circles.cadence_monthly_body": "Cercles pour occasions spéciales — rôtis du dimanche, grandes soirées quiz, dégustations, poker.",
      "hm.circles.feast_kicker": "Choisissez votre soirée",
      "hm.circles.feast_title": "Trois façons de la passer",
      "hm.circles.feast_sub": "Chaque Circle choisit comment se déroule la soirée. Alternez-les ou gardez le mode préféré de votre bande.",
      "hm.circles.feast_surprise_ttl": "Essayer un nouveau restaurant",
      "hm.circles.feast_surprise_body": "Nous sélectionnons trois lieux près de chez vous, tout le monde vote, la cuisine se prépare pour une table complète. Frais partagés, places prépayées.",
      "hm.circles.feast_preselect_ttl": "Quiz / cartes / jeux",
      "hm.circles.feast_preselect_body": "Réservez une soirée quiz, une salle de poker, un bar à jeux de société ou une piste de bowling. Nous gérons le lieu, l'inscription et les rappels.",
      "hm.circles.feast_per_event_ttl": "Soirée à thème ponctuelle",
      "hm.circles.feast_per_event_body": "Dégustation de vins, table du chef, cours de cocktail, escape room. Quelque chose de spécial de temps en temps — entièrement organisé.",
      "hm.circles.live_kicker": "En ce moment",
      "hm.circles.live_title": "Circles que vous pouvez rejoindre",
      "hm.circles.live_host_link": "Lancer le vôtre",
      "hm.circles.live_loading": "Chargement…",
      "hm.circles.live_empty_title": "Soyez le premier Circle de votre ville.",
      "hm.circles.live_empty_sub": "Lancez-en un avec trois amis et nous vous aiderons à inviter les autres — en plus de vous offrir les trois premiers lieux.",
      "hm.circles.live_empty_cta": "Lancer un Circle",
      "hm.circles.host_kicker": "Pour les leaders de Circle",
      "hm.circles.host_title": "Vous amenez la bande. On gère l'admin.",
      "hm.circles.host_body": "Si vous êtes l'ami qui organise toujours la soirée, Circles vous fait gagner des heures. Rappels auto, inscriptions prépayées, listes de lieux, partage des frais, gestion de la liste d'attente et un portefeuille de cashback sur chaque sortie. Vous venez simplement comme un membre normal.",
      "hm.circles.host_perk_1": "Listes de lieux auto — plus besoin de scroller sur Google",
      "hm.circles.host_perk_2": "Places prépayées — zéro désistement",
      "hm.circles.host_perk_3": "Partage des frais réglé avant le dessert",
      "hm.circles.host_perk_4": "Rappels + invitations calendrier en pilote automatique",
      "hm.circles.host_perk_5": "Liste d'attente avec promotion auto si un ami annule",
      "hm.circles.host_perk_6": "Portefeuille cashback sur chaque soirée",
      "hm.circles.host_cta": "Lancer un Circle maintenant",
      "hm.circles.why_kicker": "Pourquoi Circles fonctionne",
      "hm.circles.why_title": "Les amitiés adultes ont besoin d'infrastructure",
      "hm.circles.why_1_ttl": "Même bande, confiance approfondie",
      "hm.circles.why_1_body": "La vie éloigne les amis. Circles est le rythme fixe qui garde tout le monde proche — les mêmes 4-12 amis, se réunissant le même soir, quoi qu'il arrive.",
      "hm.circles.why_2_ttl": "Zéro fatigue administrative",
      "hm.circles.why_2_body": "Personne n'a besoin d'être le chef du groupe WhatsApp. Nous choisissons les lieux, envoyons les rappels, gérons les paiements, bouclons la boucle.",
      "hm.circles.why_3_ttl": "Nouveau lieu, nouveau souvenir",
      "hm.circles.why_3_body": "Alternez entre les meilleurs petits restaurants, salles de quiz et bars de jeux d'Australia. Visages familiers, nouveaux décors — l'équilibre parfait entre routine et nouveauté.",
      "hm.circles.bottom_title": "La soirée que votre bande attend avec impatience, sans que personne n'ait à la planifier.",
      "hm.circles.bottom_sub": "Un Circle. Les mêmes amis. Un nouveau lieu à chaque fois.",
      "hm.circles.bottom_cta_find": "Lancer un Circle",
      "hm.circles.bottom_cta_mm": "Rencontrer de nouvelles personnes avec Matchmaker →",
      "hm.circles.card_host_pill": "Circle",
      "hm.matchmaker.page_title": "HustleMeet Matchmaker · L'IA choisit la bonne table",
      "hm.matchmaker.kicker": "HustleMeet · Matchmaker",
      "hm.matchmaker.hero_title": "L'IA choisit la table. Vous n'avez qu'à venir.",
      "hm.matchmaker.hero_title_accent": "Vous n'avez qu'à venir.",
      "hm.matchmaker.hero_sub": "Répondez à un court quiz sur votre personnalité et vos valeurs. Notre Matchmaker vous installe avec quatre à sept inconnus qui vous sembleront être de vieux amis dès le dessert. Pas de balayage. Pas de bavardage inutile. Juste une table partagée, prête quand vous l'êtes.",
      "hm.matchmaker.cta_quiz": "Faire le quiz de matching",
      "hm.matchmaker.cta_see_circles": "Voir Circles à la place →",
      "hm.matchmaker.trust_big5": "Score de personnalité Big-5",
      "hm.matchmaker.trust_seats": "4-8 places par table",
      "hm.matchmaker.trust_lock": "Zéro-balayage · zéro-photo",
      "hm.matchmaker.trust_verified": "Membres vérifiés uniquement",
      "hm.matchmaker.how_kicker": "Comment ça marche",
      "hm.matchmaker.how_title": "Quatre étapes vers une table qui vous correspond déjà.",
      "hm.matchmaker.how_step1_ttl": "Faites le quiz",
      "hm.matchmaker.how_step1_sub": "Questionnaire de dix minutes — personnalité, valeurs, régime alimentaire, disponibilités. Vous ne le faites qu'une seule fois.",
      "hm.matchmaker.how_step2_ttl": "Matching en 24h",
      "hm.matchmaker.how_step2_sub": "Notre IA analyse votre profil et les dîners prévus en Australia. Quand votre score d'affinité est de 0.70 ou plus, elle génère une invitation personnelle.",
      "hm.matchmaker.how_step3_ttl": "Accepter ou décliner",
      "hm.matchmaker.how_step3_sub": "Vous recevez un email d'invitation clair avec le lieu, le thème de la table et les trois intérêts que vous partagez avec le groupe.",
      "hm.matchmaker.how_step4_ttl": "Elle apprend de vous",
      "hm.matchmaker.how_step4_sub": "Chaque sondage post-dîner affine le modèle. Votre prochain match devient plus pertinent à mesure que vous dînez.",
      "hm.matchmaker.science_kicker": "Pourquoi ça marche",
      "hm.matchmaker.science_title": "Le Matchmaker ne devine pas — il calcule.",
      "hm.matchmaker.science_sub": "Chaque match est une composition pondérée de quatre facteurs. Quand la moyenne de la table dépasse 0.70, nous envoyons les invitations. Tout ce qui est en dessous reste en attente.",
      "hm.matchmaker.science_1_ttl": "Personnalité (35%)",
      "hm.matchmaker.science_1_body": "Similarité cosinus à travers le vecteur de personnalité Big-5 — ouverture, conscience, extraversion, agréabilité et stabilité émotionnelle.",
      "hm.matchmaker.science_2_ttl": "Valeurs (25%)",
      "hm.matchmaker.science_2_body": "Chevauchement sur les valeurs qui font qu'une conversation de dîner fonctionne vraiment — famille, curiosité, croissance, service, aventure.",
      "hm.matchmaker.science_3_ttl": "Intérêts (25%)",
      "hm.matchmaker.science_3_body": "Chevauchement de Jaccard sur vos intérêts spécifiques — randonnée sur le Six Foot Track, ciné-club à Sydney, scène café de Melbourne, votre troisième livre de l'année.",
      "hm.matchmaker.science_4_ttl": "Disponibilité (15%)",
      "hm.matchmaker.science_4_body": "Le soir où vous pouvez réellement venir. Pas d'invitation le mardi à 19h si votre calendrier indique sport-tous-les-mardis.",
      "hm.matchmaker.safety_kicker": "Sécurité par conception",
      "hm.matchmaker.safety_title": "Le Matchmaker est discrètement prudent.",
      "hm.matchmaker.safety_1_ttl": "Seuil de confiance",
      "hm.matchmaker.safety_1_body": "En dessous d'un score de groupe de 0.70, aucune invitation n'est envoyée. Un match bancal vaut mieux que pas de dîner — jusqu'à ce que ce ne soit plus le cas.",
      "hm.matchmaker.safety_2_ttl": "Liste de blocage mutuelle",
      "hm.matchmaker.safety_2_body": "Si deux membres signalent privément 'non' après un dîner, nous ne les placerons plus jamais ensemble.",
      "hm.matchmaker.safety_3_ttl": "Arrêt d'urgence auto",
      "hm.matchmaker.safety_3_body": "Si 20% ou plus des sondages récents notent une table 2/5 ou moins, les invitations sont suspendues pendant 24h le temps qu'un opérateur vérifie.",
      "hm.matchmaker.safety_4_ttl": "Normes communautaires",
      "hm.matchmaker.safety_4_body": "Chaque membre accepte un code de conduite en langage clair. Nous excluons les personnes qui l'enfreignent — sans appel.",
      "hm.matchmaker.safety_5_ttl": "Lieux publics uniquement",
      "hm.matchmaker.safety_5_body": "Chaque dîner Matchmaker se déroule dans un vrai restaurant avec du personnel. Jamais chez quelqu'un.",
      "hm.matchmaker.safety_6_ttl": "Retour post-événement",
      "hm.matchmaker.safety_6_body": "Votre sondage privé façonne les futurs matches et peut déclencher un examen de modération pour le lieu ou un participant.",
      "hm.matchmaker.loop_kicker": "La boucle d'apprentissage",
      "hm.matchmaker.loop_title": "Chaque dîner rend votre prochain dîner meilleur.",
      "hm.matchmaker.loop_body": "Le Matchmaker n'est pas statique. Après chaque événement, vous soumettez un court sondage privé — comment s'est passée la conversation, avec qui vous aimeriez dîner à nouveau, si le lieu était à la hauteur. Ces signaux alimentent le modèle et remodèlent chaque futur match.",
      "hm.matchmaker.loop_1": "Les votes \"dîner à nouveau\" renforcent vos associations",
      "hm.matchmaker.loop_2": "Les votes négatifs mutuels séparent discrètement les paires",
      "hm.matchmaker.loop_3": "Les notes sur le lieu et la conversation affinent la composition des tables",
      "hm.matchmaker.loop_4": "Le score de fiabilité protège les membres contre les absences répétées",
      "hm.matchmaker.loop_5": "Le biais d'apprentissage renforce les bons matches mois après mois",
      "hm.matchmaker.loop_6": "Vous gardez le contrôle — désinscrivez-vous ou réinitialisez votre profil à tout moment",
      "hm.matchmaker.vs_kicker": "Lequel me convient ?",
      "hm.matchmaker.vs_title": "Matchmaker ou Circles ?",
      "hm.matchmaker.vs_mm_ttl": "Choisissez-moi si je veux de la variété.",
      "hm.matchmaker.vs_mm_1": "Une table, une ville, une cuisine différente à chaque fois",
      "hm.matchmaker.vs_mm_2": "Groupe de 4-8 inconnus compatibles sélectionnés par IA",
      "hm.matchmaker.vs_mm_3": "Rythme hebdomadaire, pas d'engagement d'hôte",
      "hm.matchmaker.vs_mm_4": "Idéal si vous êtes nouveau dans une ville ou en voyage",
      "hm.matchmaker.vs_mm_cta": "Faire le quiz de matching",
      "hm.matchmaker.vs_circles_ttl": "Choisissez-moi si je veux une habitude.",
      "hm.matchmaker.vs_circles_1": "Même hôte · même lieu · même soir de la semaine",
      "hm.matchmaker.vs_circles_2": "Un groupe qui devient votre tradition du mercredi",
      "hm.matchmaker.vs_circles_3": "Sélection humaine par un hôte expérimenté",
      "hm.matchmaker.vs_circles_4": "Idéal si vous venez d'emménager et voulez des racines",
      "hm.matchmaker.vs_circles_cta": "Parcourir les Circles",
      "hm.matchmaker.bottom_title": "Un quiz. Ensuite, chaque dîner vous correspond déjà.",
      "hm.matchmaker.bottom_sub": "Vous recevrez un match d'ici deux semaines. C'est le \"oui\" le plus simple que vous direz ce mois-ci.",
      "hm.matchmaker.bottom_cta": "Faire le quiz de matching",
      "hm.matchmaker.bottom_note": "Pas de carte de crédit · 10 minutes · Quittez quand vous voulez",
      /* AUTO-TRANSLATED hm.* END */
    },
  },
  de: {
    translation: {
      /* AUTO-TRANSLATED de START */
      plaza_nav_features: "Features",
      plaza_nav_rewards: "Prämien",
      plaza_nav_how: "So funktioniert's",
      plaza_nav_roadmap: "Roadmap",
      plaza_nav_faq: "FAQ",
      plaza_hero_badge: "In australischem Besitz",
      plaza_hero_kicker: "Ein Warenkorb. Alle Prämien.",
      plaza_hero_title_1: "Kaufe australisch.",
      plaza_hero_title_2: "Verdiene überall.",
      plaza_hero_sub: "Tausende australische Händler in einem Warenkorb. Jeder ausgegebene Dollar bringt dir HustlePoints — einlösbar in Restaurants, Cafés, Hotels, beim Friseur, bei Haustierfriseuren und in jedem Geschäft im HustleSuite-Netzwerk.",
      plaza_cta_start: "Kostenlos beitreten — dauert 30 Sekunden",
      plaza_cta_signin: "Anmelden",
      plaza_cta_shop: "Shop durchstöbern",
      plaza_hero_stat_1_v: "12.847",
      plaza_hero_stat_1_l: "verfügbare Produkte",
      plaza_hero_stat_2_v: "340+",
      plaza_hero_stat_2_l: "australische Händler",
      plaza_hero_stat_3_v: "$4,2 Mio.",
      plaza_hero_stat_3_l: "ausgezahlte Prämien",
      plaza_hero_stat_4_v: "19",
      plaza_hero_stat_4_l: "Kategorien",
      plaza_problem_kicker: "Das Problem beim heutigen Einkaufen",
      plaza_problem_title: "Zehn Tabs. Zehn Passwörter. Null Treue.",
      plaza_problem_sub: "Du jonglierst bereits mit Amazon, eBay, Kogan, Kmart, Woolies, Coles, Bunnings, JB, Officeworks und einem halben Dutzend Boutique-Shops. Du verbringst Stunden mit Preisvergleichen — und bekommst am Ende nichts zurück außer einem Beleg.",
      plaza_problem_1: "Preise, die sich nicht über Websites vergleichen lassen",
      plaza_problem_2: "Ausländische Verkäufer, die sich als lokal ausgeben",
      plaza_problem_3: "Treuepunkte, die in einem einzigen Shop gefangen sind",
      plaza_problem_4: "Gefälschte Bewertungen von Bots und Dropshippern",
      plaza_solution_kicker: "Was HustlePlaza anders macht",
      plaza_solution_title: "Der letzte Warenkorb, den du heute öffnest.",
      plaza_solution_sub: "Eine Suche. Ein Warenkorb. Ein Checkout. Jeder Einkauf zahlt sich in HustlePoints aus, die du im gesamten Ökosystem ausgeben kannst — nicht nur in diesem Shop.",
      plaza_solution_1_t: "Ein Warenkorb, alle Händler",
      plaza_solution_1_b: "Kaufe bei jedem australischen Verkäufer auf der Plattform ein und bezahle einmal beim Checkout. Lieferung wird nach Möglichkeit zusammengefasst.",
      plaza_solution_2_t: "Nur verifizierte Einheimische",
      plaza_solution_2_b: "Jeder Verkäufer wird identitätsgeprüft, bevor er listen kann. Keine Bots, keine Fälschungen, kein Dropshipping-Mysterium.",
      plaza_solution_3_t: "Prämien, die wirklich reisen",
      plaza_solution_3_b: "Hier verdiente Punkte kannst du in jedem HustleSuite-Geschäft ausgeben — Cafés, Hotels, Friseure, Restaurants, Haustierfriseure.",
      plaza_solution_4_t: "Live-Tracking, ein Bildschirm",
      plaza_solution_4_b: "Jedes Paket von jedem Händler wird auf einer Seite verfolgt. Benachrichtigungen per E-Mail und SMS bei jedem Schritt.",
      plaza_rewards_kicker: "Das HustlePoints-Schwungrad",
      plaza_rewards_title: "Verdiene beim Einkaufen. Gib überall im Netzwerk aus.",
      plaza_rewards_sub: "Jeder auf HustlePlaza ausgegebene $1 bringt dir HustlePoints. Löse sie in jedem Restaurant, Café, Hotel, beim Friseur oder in jedem Dienstleistungsunternehmen ein, das auf HustleSuite läuft — überall im Netzwerk.",
      plaza_rewards_step_1: "Im Marktplatz einkaufen",
      plaza_rewards_step_1_b: "Kaufe alles, vom T-Shirt bis zum Kühlschrank.",
      plaza_rewards_step_2: "Sofort HustlePoints verdienen",
      plaza_rewards_step_2_b: "Punkte erscheinen, sobald deine Bestellung versandt wird — keine Karten, keine Stempelkarten, keine Ablauf-Spielchen.",
      plaza_rewards_step_3: "Im Netzwerk ausgeben",
      plaza_rewards_step_3_b: "Einlösen in Cafés, Restaurants, Hotels, beim Friseur, bei Haustierfriseuren, bei Handwerkern.",
      plaza_rewards_step_4: "Oder auszahlen lassen",
      plaza_rewards_step_4_b: "In Banküberweisung umwandeln, sobald du $50 erreichst.",
      plaza_cats_kicker: "Nach Kategorie einkaufen",
      plaza_cats_title: "19 Kategorien. Australische Händler. Verfügbare Ware.",
      plaza_cats_sub: "Durchstöbere einen Ausschnitt dessen, was gerade verfügbar ist — oder melde dich an und kaufe im vollständigen Katalog mit über 12.000 Produkten ein.",
      plaza_cats_cta: "Alle Kategorien ansehen",
      plaza_vendors_kicker: "Echte australische Verkäufer",
      plaza_vendors_title: "Lerne die Hersteller, Erzeuger und Familiengeschäfte kennen.",
      plaza_vendors_sub: "Jeder Händler auf HustlePlaza ist ABN-verifiziert, in Australien ansässig und persönlich aufgenommen. Keine ausländischen Durchlaufstellen, die sich als lokal ausgeben.",
      plaza_how_kicker: "So funktioniert's",
      plaza_how_title: "Drei Schritte. Ein Warenkorb. Prämien für immer.",
      plaza_how_1_title: "In 30 Sekunden beitreten",
      plaza_how_1_body: "Verifiziere deine E-Mail und stöbere sofort. Füge eine Telefonnummer hinzu, wenn du zur Kasse gehen möchtest.",
      plaza_how_2_title: "Warenkorb füllen",
      plaza_how_2_body: "Kaufe bei jedem Händler ein. Ein Warenkorb, eine Lieferansicht, ein Checkout. Zahlung per Stripe oder PayPal.",
      plaza_how_3_title: "Überall verdienen & ausgeben",
      plaza_how_3_body: "HustlePoints erscheinen, sobald deine Bestellung versandt wird. Gib sie in jedem HustleSuite-Geschäft aus oder lass sie dir ab $50 auszahlen.",
      plaza_compare_kicker: "Wie wir abschneiden",
      plaza_compare_title: "HustlePlaza vs die Giganten",
      plaza_compare_col_us: "HustlePlaza",
      plaza_compare_col_amz: "Amazon AU",
      plaza_compare_col_ebay: "eBay AU",
      plaza_compare_col_kmart: "Kmart / Big W",
      plaza_compare_row_1: "In australischem Besitz",
      plaza_compare_row_2: "Ein Warenkorb für alle Verkäufer",
      plaza_compare_row_3: "Prämien AUSSERHALB dieses Shops ausgeben",
      plaza_compare_row_4: "Prämien-Auszahlung auf dein Bankkonto",
      plaza_compare_row_5: "In Australien gehosteter Kundensupport",
      plaza_trust_kicker: "Warum wir verifizieren",
      plaza_trust_title: "Echte Kunden. Echte Bewertungen.",
      plaza_trust_body: "Wir verifizieren deine E-Mail bei der Anmeldung, deine Handynummer vor dem Checkout und verlangen eine Live-Fotoprüfung, bevor du Bewertungen posten, mit Händlern chatten oder hochwertige Käufe tätigen kannst. Das bremst Betrüger aus und hält die Preise für dich niedrig.",
      plaza_trust_1: "E-Mail-Verifizierung beim Beitritt",
      plaza_trust_2: "SMS-Verifizierung beim Checkout",
      plaza_trust_3: "Live-Fotoverifizierung für Bewertungen und Chat",
      plaza_trust_4: "Identitätsprüfung der Händler beim Onboarding",
      plaza_test_kicker: "Was unsere frühen Mitglieder sagen",
      plaza_test_title: "Direkt von echten Kunden.",
      plaza_test_1_body: "Ich habe meine HustlePlaza-Punkte benutzt, um das Abendessen in einem Café in der Nähe meiner Arbeit zu bezahlen. Ich habe wirklich nicht geglaubt, dass es funktioniert, bis die Kellnerin lächelte und sagte: ‚Alles erledigt'.",
      plaza_test_1_name: "Priya S.",
      plaza_test_2_body: "Sechs Händler, ein Lieferfenster, eine Bestätigungs-E-Mail. Normalerweise hätte ich sechs Passwörter und sechs Tracking-Nummern. Das hier funktioniert einfach.",
      plaza_test_2_name: "Marcus T.",
      plaza_test_3_body: "Als kleiner Honigproduzent bekommen wir endlich am selben Tag bezahlt, echte Bewertungen und keine 40 % Provision. HustlePlaza hat mich in acht Monaten vom ‚Nebenjob' zum Vollzeit-Business gebracht.",
      plaza_test_3_name: "Kate & Dave",
      plaza_faq_kicker: "Antworten",
      plaza_faq_title: "Die Fragen, die wir jede Woche bekommen",
      plaza_faq_1_q: "Ist HustlePlaza wirklich in australischem Besitz?",
      plaza_faq_1_a: "Ja. HustlePlaza ist zu 100 % in australischem Besitz, auf australischen Servern gehostet und wird von einem australischen Team betrieben. Jeder Händler durchläuft eine Identitätsprüfung, bevor sein erstes Angebot live gehen kann.",
      plaza_faq_2_q: "Wie funktionieren die Prämien?",
      plaza_faq_2_a: "Jeder Dollar, den du auf HustlePlaza ausgibst, bringt dir HustlePoints. Die Punkte erscheinen, sobald deine Bestellung versandt wird, und verfallen nie. Gib sie in jedem Geschäft aus, das auf HustleSuite läuft, oder lass sie dir auf dein Bankkonto auszahlen, sobald du genug gesammelt hast.",
      plaza_faq_3_q: "Warum verifiziert ihr Fotos?",
      plaza_faq_3_a: "Damit die Bewertungen, die du liest, echt sind. Die Fotoverifizierung ist eine Live-Selfie-Prüfung über deine Handykamera — die gleiche Technik, die Banken verwenden — und sie stoppt Bots, Fake-Konten und böswillige Akteure daran, die Plattform zu manipulieren.",
      plaza_faq_4_q: "Was ist, wenn meine Bestellung nicht ankommt?",
      plaza_faq_4_a: "Jede Bestellung ist durch unsere HustleGuarantee abgesichert: Wenn das Paket nicht ankommt oder nicht wie beschrieben ist, erstatten wir dir das Geld innerhalb von 3 Werktagen und setzen uns dann mit dem Händler auseinander. Du bleibst nie auf den Kosten sitzen.",
      plaza_faq_5_q: "Sind meine Kartendaten sicher?",
      plaza_faq_5_a: "Wir sehen oder speichern deine Kartennummer nie. Alle Zahlungen laufen über Stripe (PCI-DSS Level 1) oder PayPal, beide mit 3D-Secure-Authentifizierung, die dich vor betrügerischen Abbuchungen mit gestohlenen Karten schützt.",
      plaza_faq_6_q: "Versendet ihr international?",
      plaza_faq_6_a: "Noch nicht. HustlePlaza ist beim Start nur für Australien verfügbar — so können wir bei den meisten Artikeln Lieferung in derselben Woche und Support in deiner Zeitzone versprechen. Neuseeland steht auf der Roadmap für 2026.",
      plaza_faq_7_q: "Was kostet die Mitgliedschaft?",
      plaza_faq_7_a: "Nichts. Die Mitgliedschaft ist für immer kostenlos. Händler zahlen eine kleine Provision pro Verkauf — Käufer zahlen $0 zum Beitritt, $0 zum Verdienen, $0 zum Einlösen.",
      plaza_faq_8_q: "Kann ich auf HustlePlaza verkaufen?",
      plaza_faq_8_a: "Auf jeden Fall. Gehe zu hustlesuite.au, um dich als Händler zu registrieren — Genehmigung am selben Tag für verifizierte australische Unternehmen, plus deine ersten 30 Tage provisionsfrei.",
      plaza_final_kicker: "Gründerpreis — begrenzte Plätze",
      plaza_final_title: "Sei einer der Ersten — sichere dir den Gründerpreis.",
      plaza_final_sub: "Registriere dich heute als HustleSuite-Mitglied. Du erfährst als Erster, wenn jede neue Funktion veröffentlicht wird, und dein Konto behält für immer Gründer-Prämien und -Preise.",
      plaza_final_bonus: "Gründer verlieren nie ihren Gründertarif. Für immer kostenlos, jederzeit kündbar.",
      plaza_final_cta: "Als Gründungsmitglied registrieren",
      plaza_final_disclosure: "Keine Karte erforderlich. Nur E-Mail- und Handyverifizierung.",
      plaza_roadmap_kicker: "Was wir als Nächstes entwickeln",
      plaza_roadmap_title: "Sechs Tools, die dir Arbeit abnehmen.",
      plaza_roadmap_sub: "HustleSuite ist mehr als ein Shop — es ist eine vollständige Produktivitätsebene. Hier ist, was in der Pipeline ist.",
      plaza_roadmap_soon: "Kommt bald",
      plaza_roadmap_next: "Als Nächstes in der Warteschlange",
      plaza_roadmap_1_t: "HustleDictate — Sprache-zu-Text für jedes Formular",
      plaza_roadmap_1_b: "Chrome-Erweiterung. Drücke Alt+M, sprich, und jedes Eingabefeld auf jeder Website füllt sich selbst aus. Immobilienanträge, Rechnungen, NDIS-Papierkram — in der Hälfte der Zeit erledigt.",
      plaza_roadmap_2_t: "HustleConnect Edit-Anywhere",
      plaza_roadmap_2_b: "Für WordPress-Besitzer: Bearbeite deine Live-Website von jeder Seite aus mit einem einzigen Tastendruck. Keine wp-admin-Umwege mehr.",
      plaza_roadmap_3_t: "Save-to-HustleSuite universeller Button",
      plaza_roadmap_3_b: "Rechtsklick auf jede Webseite — Immobilieninserat, Artikel, Produkt, Restaurant — und es landet im richtigen Modul, bereit für deine Aktion.",
      plaza_roadmap_4_t: "HustlePlaza Preisvergleicher",
      plaza_roadmap_4_b: "Durchstöbere Amazon, eBay oder Kmart — ein schwebendes Chip zeigt das gleiche Produkt auf HustlePlaza mit deiner Prämienvorschau.",
      plaza_roadmap_5_t: "Bewertungs-Harvester",
      plaza_roadmap_5_b: "Automatische Synchronisierung neuer Google-, Facebook- und TripAdvisor-Bewertungen mit deinem HustleSuite-Dashboard. Spart ~20 Minuten pro Woche.",
      plaza_roadmap_6_t: "HustleSuite Sidebar",
      plaza_roadmap_6_b: "Chrome-Seitenleiste mit Live-Bestellungen, KPIs, Schnellpost und Schnellrechnung. Dein gesamtes Geschäft in einem Streifen neben jedem Tab.",
      plaza_footer_owned: "In australischem Besitz & gehostet",
      plaza_footer_terms: "AGB",
      plaza_footer_privacy: "Datenschutz",
      plaza_footer_contact: "Kontakt",
      plaza_footer_vendors: "Auf HustlePlaza verkaufen",
      plaza_footer_help: "Hilfecenter",
      plaza_footer_tagline: "Teil der HustleSuite-Familie — ein Login, jeder Service.",
      verify_gate_title: "Fast geschafft — schnelle Anmeldung",
      verify_gate_body: "HustlePlaza ist ein Warenkorb mit vielen Vorteilen, einschließlich einlösbarer Prämien, die nicht nur auf dieser Plattform, sondern auch bei jedem anderen Unternehmen mit der HustlePlaza-Plattform oder jedem anderen bei HustleSuite registrierten Unternehmen verwendet werden können. Einige dieser Unternehmen sind Restaurants, Cafés, Hotels, Friseure und sogar Haustierfriseure. Um weiterhin großartige Preise und Services anbieten zu können, müssen wir sicherstellen, dass du echt bist, und dich verifizieren. Drücke den Beitritt-Button, um fortzufahren.",
      verify_gate_join: "HustleSuite beitreten",
      verify_gate_signin: "Ich habe bereits ein Konto",
      verify_gate_cancel: "Nicht jetzt",
      verify_email_title: "E-Mail verifizieren",
      verify_email_body: "Wir senden einen 6-stelligen Code an dein Postfach.",
      verify_email_placeholder: "du@beispiel.com",
      verify_email_send: "Code senden",
      verify_email_code_ph: "6-stelliger Code",
      verify_email_check: "Verifizieren",
      verify_email_sent: "Code gesendet — prüfe dein Postfach.",
      verify_sms_title: "Telefon verifizieren",
      verify_sms_body: "Füge eine Handynummer hinzu, damit wir dir Bestellaktualisierungen per SMS senden und große Käufe bestätigen können.",
      verify_sms_placeholder: "+61412345678",
      verify_sms_send: "SMS senden",
      verify_sms_check: "Verifizieren",
      verify_sms_sent: "Code gesendet — prüfe dein Handy.",
      verify_photo_title: "Fotoverifizierung",
      verify_photo_body: "Scanne den QR-Code mit deinem Handy, um ein Live-Selfie zu machen. Erforderlich für Chat, hochwertige Käufe und Beiträge in Meetup / Tackle.",
      verify_photo_waiting: "Warte auf dein Handy…",
      verify_photo_verified: "Foto verifiziert — du bist startklar.",
      travel_cta_start: "Kostenlos beitreten",
      travel_hero_kicker: "HustleTravel",
      travel_hero_title_1: "Eine Mitgliedschaft.",
      travel_hero_title_2: "Überall in Australien.",
      travel_hero_sub: "Hotels, Flüge, Erlebnisse, Roadtrip-Planung und Autovermietung — zum besten verfügbaren Preis gebucht und mit HustlePoints für jeden ausgegebenen Dollar belohnt.",
      travel_cta_plan: "Roadtrip planen",
      travel_cta_hotels: "Hotels suchen",
      travel_svc_hotels_t: "Hotels & Apartments",
      travel_svc_hotels_b: "Vergleiche 200+ Buchungsseiten an einem Ort — Trivago-gestützte Metasuche.",
      travel_svc_flights_t: "Flüge",
      travel_svc_flights_b: "National und international, mit Echtzeit-Sitzplänen und Tarifregeln.",
      travel_svc_experiences_t: "Erlebnisse & Touren",
      travel_svc_experiences_b: "Riff-Tage, Weinproben, Live-Musik, kinderfreundliche Ausflüge.",
      travel_svc_cars_t: "Autovermietung",
      travel_svc_cars_b: "Jede große Marke plus lokale unabhängige Anbieter — Abholung an jedem australischen Flughafen.",
      travel_svc_insurance_t: "Reiseversicherung",
      travel_svc_insurance_b: "Versichert durch APRA-regulierte australische Versicherer. Vergleiche auf einem Bildschirm.",
      travel_svc_roadtrip_t: "Roadtrip-Planer",
      travel_svc_roadtrip_b: "E-Auto-reichweitenbewusste Reiserouten mit Ladestationen, Übernachtungsstopps und Kostenschätzungen.",
      travel_final_kicker: "Gründerpreis",
      travel_final_title: "Sichere dir lebenslang Gründer-Prämien.",
      travel_final_sub: "Registriere dich einmal bei HustleSuite. Buche alles — Hotels, Flüge, Erlebnisse, Versicherungen — auf HustleTravel und verdiene HustlePoints, die in jedem Geschäft im Netzwerk ausgegeben werden können.",
      travel_final_cta: "Als Gründungsmitglied registrieren",
      travel_final_disclosure: "Bestpreis-Richtlinie: Ergebnisse nach bestem Wert für den Reisenden sortiert, nicht nach Provision.",
      dictate_hero_kicker: "HustleDictate",
      dictate_hero_title_1: "Sprechen.",
      dictate_hero_title_2: "Jedes Formular. Jede Website.",
      dictate_hero_sub: "Die eine Chrome-Erweiterung, die jedes Textfeld in ein professionelles Sprachdiktier-Mikrofon verwandelt — angetrieben von Whisper.",
      dictate_cta_install: "Zu Chrome hinzufügen",
      dictate_uc_re_t: "Immobilien-Mietnotizen",
      dictate_uc_re_b: "Diktiere Inspektionsberichte und Mieter-Nachrichten direkt in dein PM-System.",
      dictate_uc_ndis_t: "NDIS-Fortschrittsnotizen",
      dictate_uc_ndis_b: "Spreche die Schichtnotiz, solange die Erinnerung frisch ist — freihändig, kein Tippen.",
      dictate_uc_invoice_t: "Rechnungsbeschreibungen",
      dictate_uc_invoice_b: "Positionsbeschreibungen direkt in Xero, MYOB oder QuickBooks diktiert.",
      dictate_uc_email_t: "Lange E-Mail-Antworten",
      dictate_uc_email_b: "Sprich Antworten in Gmail, Outlook oder jedem Webmail aus — dreimal schneller als Tippen.",
      dictate_final_kicker: "Kostenlos für Gründungsmitglieder",
      dictate_final_title: "Jede Tastatur ist jetzt ein Mikrofon.",
      dictate_final_sub: "Kostenlos für HustleSuite-Gründungsmitglieder. Ansonsten $5/Monat — jederzeit kündbar.",
      dictate_final_cta: "Zu Chrome hinzufügen — kostenlos",
      "finder.cat.roadtrip": "Roadtrip",
      "finder.hotels.title": "Hotel in der Nähe finden",
      "finder.hotels.sub": "Vergleicht 200+ Buchungsseiten in einer Suche — angetrieben von Trivago.",
      "finder.hotels.where": "Wo",
      "finder.hotels.where_ph": "Stadt oder Vorort (z. B. Byron Bay, NSW)",
      "finder.hotels.checkin": "Check-in",
      "finder.hotels.checkout": "Check-out",
      "finder.hotels.adults": "Erwachsene",
      "finder.hotels.search": "Hotels suchen",
      "finder.hotels.disclosure": "Öffnet Trivago — wir können Provisionen auf Buchungen verdienen.",
      /* AUTO-TRANSLATED de END */
      welcome_back: "Willkommen zurück",
      sign_out: "Abmelden",
    
      /* AUTO-TRANSLATED finder.* (added) */
      "finder.title": "HustleFinder",
      "finder.tagline": "Finden Sie den nächstgelegenen öffentlichen Ort — echte Daten, australienweit.",
      "finder.search_placeholder": "Orte, Straßen oder Vororte suchen…",
      "finder.hint": "Beginnen Sie oben zu tippen, wählen Sie eine Kategorie oder nutzen Sie „In meiner Nähe\".",
      "finder.near_me": "In meiner Nähe",
      "finder.searching": "Suche läuft…",
      "finder.suburb.placeholder": "Nach Vorort filtern (z.B. Darwin, NT)…",
      "finder.suburb.clear": "Löschen",
      "finder.search_in": "In {{cat}} suchen",
      "finder.empty": "Keine Ergebnisse — versuchen Sie eine andere Kategorie oder einen anderen Suchbegriff.",
      "finder.meta.title": "HustleFinder — kostenloses australisches Vorortverzeichnis",
      "finder.meta.desc": "Suchen Sie lokale Unternehmen, Ladestationen, Cafés, Toiletten und mehr in jedem australischen Vorort — 100% kostenlos.",
      "finder.footer.credits": "Datenquellen & Namensnennung",
      "finder.footer.terms": "Nutzungsbedingungen",
      "finder.cat.toilet": "Toiletten",
      "finder.cat.fountain": "Trinkbrunnen",
      "finder.cat.transport": "Transport",
      "finder.cat.park": "Parks",
      "finder.cat.medical": "Medizinische Einrichtungen",
      "finder.cat.school": "Schulen",
      "finder.cat.library": "Bibliotheken",
      "finder.cat.petrol": "Tankstellen",
      "finder.cat.kidzone": "Kinderbereich",
      "finder.cat.puzzles": "Rätsel",
      /* AUTO-TRANSLATED rt.* START */
      "rt.brand_name": "Roadtrip-Planer",
      "rt.page_title": "Australischer E-Auto-Roadtrip-Planer",
      "rt.from": "Von",
      "rt.to": "Nach",
      "rt.from_placeholder": "Stadtteil oder Ort (z. B. Newcastle NSW)",
      "rt.to_placeholder": "Stadtteil oder Ort (z. B. Hobart TAS)",
      "rt.stop": "STOP",
      "rt.stop_placeholder": "Zwischenstopp",
      "rt.add_stop": "Stopp hinzufügen",
      "rt.upgrade_pro": "Upgrade auf Pro für bis zu 20 Stopps",
      "rt.reverse": "Umkehren (Heimweg berechnen)",
      "rt.from_current": "Vom aktuellen Standort",
      "rt.range_per_charge": "Reichweite pro Ladung (km)",
      "rt.connector": "Anschlusstyp",
      "rt.connector_any": "Beliebig",
      "rt.drive_hours_day": "Fahrstunden/Tag",
      "rt.accommodation": "Unterkunft",
      "rt.accom_any": "Beliebig (BnB · Hotel · Wohnwagen)",
      "rt.accom_bnb": "BnB",
      "rt.accom_hotel": "Hotel",
      "rt.accom_caravan": "Campingplatz",
      "rt.min_rating": "Mindestbewertung",
      "rt.any_rating": "Alle Bewertungen",
      "rt.only_5star": "Nur 5★",
      "rt.schedule": "Zeitplan:",
      "rt.depart_at": "Abfahrt um",
      "rt.arrive_by": "Ankunft bis",
      "rt.hotel_checkin": "Hotel-Check-in",
      "rt.hotel_checkout": "Hotel-Check-out",
      "rt.overnight_hint": "Etappen mit Übernachtung werden nicht außerhalb dieser Zeiten geplant.",
      "rt.whos_in_car": "Wer ist im Auto?",
      "rt.adults": "Erwachsene",
      "rt.kids": "Kinder",
      "rt.ages": "Alter",
      "rt.show_attractions": "Sehenswürdigkeiten anzeigen",
      "rt.show_kids": "Kinderfreundliche Stopps anzeigen",
      "rt.overnight_stays": "Übernachtungen",
      "rt.fast_chargers_only": "Nur Schnellladestationen",
      "rt.hotel_must_charger": "Hotel muss Ladestation haben",
      "rt.pet_only": "Nur haustierfreundliche Unterkünfte",
      "rt.safety": "Sicherheit",
      "rt.safety_90": "90 % (flach)",
      "rt.safety_85": "85 % (Standard)",
      "rt.safety_80": "80 % (Hügel / Anhänger)",
      "rt.safety_70": "70 % (schwere Last)",
      "rt.save_trip": "Trip speichern",
      "rt.plan_my_trip": "Trip planen",
      "rt.refresh_trip": "Trip aktualisieren",
      "rt.planning": "Planung…",
      "rt.resume_title": "Trip ab hier fortsetzen",
      "rt.resume_body": "Wir haben Ihren aktuellen Standort erfasst. Geben Sie die auf Ihrem Display angezeigte Restreichweite ein, damit wir die nächste Etappe basierend auf dem tatsächlichen Ladestand planen – nicht auf einer vollen Ladung.",
      "rt.remaining_range": "Restreichweite (km)",
      "rt.cancel": "Abbrechen",
      "rt.replan_from_here": "Ab hier neu planen",
      "rt.save_modal_title": "Trip benennen",
      "rt.save_modal_body": "Gespeicherte Trips finden Sie in Ihrem Profil – öffnen Sie diese jederzeit und klicken Sie auf 'Neu planen'.",
      "rt.save_name_placeholder": "z. B. Brisbane → Sydney via Byron",
      "rt.your_saved_trips": "Ihre gespeicherten Trips",
      "rt.custom_location": "Eigener Standort",
      "rt.trip_summary": "Trip-Zusammenfassung",
      "rt.itinerary": "Reiseplan",
      "rt.no_stops_needed": "Keine Stopps erforderlich – der gesamte Trip liegt innerhalb Ihrer Reichweite.",
      "rt.chargers_in_corridor": "Ladestationen auf der Strecke",
      "rt.planned_stops": "geplante Stopps",
      "rt.overnight_stop": "Übernachtungsstopp",
      "rt.overnight_stops": "Übernachtungsstopps",
      "rt.suggested_detours": "Vorgeschlagene Umwege – zum Hinzufügen auswählen",
      "rt.replan_with_detours": "Mit Umwegen neu planen",
      "rt.attractions_label": "Sehenswürdigkeit",
      "rt.kids_label": "Kinder",
      "rt.km_from_start": "km ab Start",
      "rt.open_driving": "Fahrmodus öffnen",
      "rt.big_font_hint": "Große Schrift, ideal für Halterungen. Funktioniert mit CarPlay/Android Auto Browser-Spiegelung.",
      "rt.check_price_book": "Preis prüfen & buchen auf Trivago ↗",
      "rt.visit_site": "Website besuchen ↗",
      "rt.charger_onsite": "Ladestation vor Ort",
      "rt.leg_exceeds_range": "Diese Etappe überschreitet Ihre Reichweite – Sie hätten keinen Strom mehr, bevor Sie diese Ladestation erreichen. Verringern Sie die angenommene Reichweite oder fügen Sie einen Zwischenstopp zum Nachladen hinzu.",
      "rt.add_waypoint_replan": "Als Wegpunkt hinzufügen & neu planen",
      /* AUTO-TRANSLATED rt.* END */
                      /* AUTO-TRANSLATED hm.* START */
      "hm.nav.hustlemeet": "HustleMeet",
      "hm.nav.circles": "Circles",
      "hm.nav.matchmaker": "Matchmaker",
      "hm.nav.meet": "Meet",
      "hm.nav.overview": "HustleMeetups",
      "hm.overview.page_title": "HustleMeetups — drei herzliche Wege, um deine Leute zu finden.",
      "hm.overview.kicker": "Teil der HustleSuite",
      "hm.overview.hero_title": "Drei herzliche Wege, um deine Leute zu finden.",
      "hm.overview.hero_title_accent": "deine Leute",
      "hm.overview.hero_sub": "HustleMeetups ist Australiens herzlichste Art, echte Freundschaften zu schließen — wähle den Vibe, der zu dir passt. Ob du eine wöchentliche Laufgruppe suchst, ein monatliches Abendessen mit deiner bestehenden Crew oder einen per KI abgestimmten Tisch mit neuen Gesichtern – es gibt einen Weg für dich.",
      "hm.overview.tile_meet_kicker": "HustleMeet",
      "hm.overview.tile_meet_title": "Finde deine Leute. Mach dein Ding.",
      "hm.overview.tile_meet_body": "Australiens Zuhause für interessenbasierte Gruppen. Läufer, Coder, Investoren, Wanderer, Tänzer, Frühstücksclubs, Buch-Crews — hunderte Gruppen, jede Woche echte Events. Komm einmal vorbei oder jede Woche.",
      "hm.overview.tile_meet_cta": "Gruppen erkunden",
      "hm.overview.tile_circles_kicker": "Circles",
      "hm.overview.tile_circles_title": "Deine Crew. Jedes Mal ein neuer Tisch.",
      "hm.overview.tile_circles_body": "Bring deine feste Freundesgruppe mit. Wir buchen ein neues Restaurant, planen das Quiz, verteilen die Karten. Du erscheinst einfach. Der direkteste Weg, eine bestehende Crew eng zusammenzuhalten — ohne dass jemand organisieren muss.",
      "hm.overview.tile_circles_cta": "Einen Circle starten",
      "hm.overview.tile_matchmaker_kicker": "Matchmaker",
      "hm.overview.tile_matchmaker_title": "Die KI wählt den Tisch. Du erscheinst einfach.",
      "hm.overview.tile_matchmaker_body": "Mach ein 10-Minuten-Quiz. Über Nacht setzen wir dich mit Fremden an einen Tisch, die sich bis zum Dessert wie alte Freunde anfühlen. Jedes Mal ein anderer Tisch.",
      "hm.overview.tile_matchmaker_cta": "Zum Quiz",
      "hm.overview.trust_kicker": "Warum es funktioniert",
      "hm.overview.trust_title": "Zuerst Vertrauen. Dann der Tisch.",
      "hm.overview.trust_1_title": "Verifizierte Mitglieder",
      "hm.overview.trust_1_body": "Jede Gruppe startet mit einem echten ID-Check + Zuverlässigkeits-Score. Keine Wegwerf-Accounts.",
      "hm.overview.trust_2_title": "Echte Hosts",
      "hm.overview.trust_2_body": "Jede Gruppe wird von einem geprüften Host geleitet, der für den Vibe sorgt, den Rhythmus festlegt und sich um die Gäste kümmert.",
      "hm.overview.trust_3_title": "Freundschaft zuerst",
      "hm.overview.trust_3_body": "Alles hier ist auf echte Freundschaften ausgelegt, nicht auf Dating. Niemals.",
      "hm.overview.trust_4_title": "Null-Toleranz-Moderation",
      "hm.overview.trust_4_body": "Eine Meldung, eine Prüfung, eine Entscheidung. Unruhestifter werden noch am selben Abend entfernt.",
      "hm.overview.footer_line": "Teil der HustleSuite — in australischem Besitz & gehostet.",
      "hm.landing.page_title": "HustleMeet · Finde deine Leute. Mach dein Ding.",
      "hm.landing.kicker": "Australiens Interessen-Netzwerk",
      "hm.landing.hero_title": "Finde deine Leute. Mach dein Ding.",
      "hm.landing.hero_title_accent": "deine Leute",
      "hm.landing.hero_sub": "HustleMeet ist Australiens Zuhause für interessenbasierte Gruppen. Software-Entwickler beim Debugging beim Mittagessen. Langstreckenläufer um 6 Uhr morgens. Investoren beim monatlichen Treffen. Wandergruppen, Tanz-Crews, Frühstücks-Clubs, Brettspielabende. Wähle eine Gruppe, buche das nächste Event und erscheine einfach.",
      "hm.landing.cta_explore_circles": "Gruppen durchsuchen",
      "hm.landing.cta_try_matchmaker": "Eigene Gruppe gründen",
      "hm.landing.trust_verified": "Verifizierte Mitglieder",
      "hm.landing.trust_venues": "Geprüfte Event-Hosts",
      "hm.landing.trust_friendship": "Freundschaft zuerst — niemals Dating",
      "hm.landing.trust_moderation": "Null-Toleranz-Moderation",
      "hm.landing.subbrand_circles_kicker": "Circles",
      "hm.landing.subbrand_circles_title": "Hast du bereits deine Crew? Gib ihr ein festes Zuhause.",
      "hm.landing.subbrand_circles_body": "Circles ist für Freundesgruppen, die sich bereits treffen — Buchclub, Pokerrunde, Lauf-Crew, Arbeitskollegen. Wir kümmern uns um den Ort, die Erinnerungen und den Ablauf. Du erscheinst einfach.",
      "hm.landing.subbrand_circles_b1": "Wechselnde Locations, ein fester Ablauf",
      "hm.landing.subbrand_circles_b2": "Auto-Erinnerungen + einfache RSVPs",
      "hm.landing.subbrand_circles_b3": "Quiz, Karten & Themenabende auf Abruf",
      "hm.landing.subbrand_circles_cta": "Einen Circle starten",
      "hm.landing.subbrand_matchmaker_kicker": "Matchmaker",
      "hm.landing.subbrand_matchmaker_title": "Die KI wählt den Tisch. Du erscheinst einfach.",
      "hm.landing.subbrand_matchmaker_body": "Ein 10-Minuten-Quiz — Persönlichkeit, Werte, Verfügbarkeit. Unser Matchmaker setzt dich mit Fremden an einen Tisch, die sich bis zum Dessert wie alte Freunde anfühlen. Jedes Mal ein anderer Tisch.",
      "hm.landing.subbrand_matchmaker_b1": "Big-5 Persönlichkeit & Werte bewertet",
      "hm.landing.subbrand_matchmaker_b2": "Einladungen über Nacht, wenn dein Fit 0,70 oder höher ist",
      "hm.landing.subbrand_matchmaker_b3": "Lernt mit jedem Abendessen dazu",
      "hm.landing.subbrand_matchmaker_cta": "Zum Quiz",
      "hm.landing.how_kicker": "Wie HustleMeet funktioniert",
      "hm.landing.how_title": "Vier Schritte. Finde deine Gruppe. Erscheine einfach.",
      "hm.landing.how_step1_ttl": "Wähle deine Interessen",
      "hm.landing.how_step1_sub": "Laufen, Coden, Investieren, Kochen, Musik, Wandern — sag uns, worauf du Lust hast und wo du wohnst.",
      "hm.landing.how_step2_ttl": "Gruppen in deiner Nähe durchsuchen",
      "hm.landing.how_step2_atp": "Jede Gruppe veröffentlicht ihren Host, ihren Rhythmus und ihr nächstes Event. Schau dir den Vibe an, bevor du zusagst.",
      "hm.landing.how_step3_ttl": "RSVP zum nächsten Event",
      "hm.landing.how_step3_sub": "Ein Tippen, eine Kalendereinladung, eine klare Erinnerung am Tag zuvor. Manche Events sind kostenlos, manche kostenpflichtig — der Preis ist klar deklariert.",
      "hm.landing.how_step4_ttl": "Erscheinen & Connecten",
      "hm.landing.how_step4_sub": "Triff den Host, triff die Crew, mach dein Ding. Komm nächste Woche wieder, wenn es gefunkt hat.",
      "hm.landing.stats_kicker": "Australiens neue Art sich zu treffen",
      "hm.landing.stats_title": "Der Grund, warum die Leute wiederkommen.",
      "hm.landing.stats_92_sub": "Hatten das Gefühl, dass es mit mindestens einer Person beim Event gefunkt hat",
      "hm.landing.stats_68_sub": "Sagen innerhalb einer Woche für ihr nächstes Event zu",
      "hm.landing.stats_85_sub": "Bleiben mit jemandem aus der Gruppe in Kontakt",
      "hm.landing.why_kicker": "Warum wir funktionieren",
      "hm.landing.why_title": "Echte Interessen. Echte Hosts. Echte Events.",
      "hm.landing.why_1_ttl": "Interessen zuerst, nicht der Ort",
      "hm.landing.why_1_body": "Die Leute in deiner Gruppe interessieren sich wirklich für das, was dir wichtig ist — das ist die Abkürzung zu echter Freundschaft.",
      "hm.landing.why_2_ttl": "Echte Event-Hosts",
      "hm.landing.why_2_body": "Jede Gruppe hat einen geprüften Host, der für den Vibe sorgt, den Rhythmus festlegt und eingreift, wenn etwas nicht passt. Kein Facebook-Admin. Eine echte Person.",
      "hm.landing.why_3_ttl": "Beständigkeit zählt",
      "hm.landing.why_3_body": "Jede Woche neue Events. Etwas, auf das man sich freuen kann. Ein Rhythmus, der Fremde in deine Dienstagabend-Crew verwandelt.",
      "hm.landing.why_4_ttl": "Sicher & geschützt",
      "hm.landing.why_4_body": "Verifizierte Mitglieder, öffentliche Orte, Null Toleranz für Respektlosigkeit. Unser Support-Team ist vor, während und nach jedem Event erreichbar.",
      "hm.landing.why_5_ttl": "Freundschaft zuerst",
      "hm.landing.why_5_body": "Das hier ist kein Dating. Es ist Community. Echte Interessen, echte Menschen, kein Swipen zwischendurch.",
      "hm.landing.why_6_ttl": "Klein genug, um zu zählen",
      "hm.landing.why_6_body": "Die meisten Gruppen begrenzen Events auf 4–15 Plätze. Groß genug für echte Gespräche. Klein genug, damit niemand untergeht.",
      "hm.landing.cities_kicker": "Wo wir aktiv sind",
      "hm.landing.cities_title": "Gruppen-Treffen in ganz Australia & NZ.",
      "hm.landing.cities_sub_zero": "Erste Gruppen öffnen in über 15 Städten — sei früh dabei und gestalte den Raum mit.",
      "hm.landing.cities_sub_some": "Gruppen sind in über 15 Metropolen aktiv. Etwas in deiner Stadt, diese Woche.",
      "hm.landing.cities_sub_one": "{{count}} aktive Gruppe · jede Woche neue Events · ständig neue Städte.",
      "hm.landing.cities_sub_many": "{{count}} aktive Gruppen · jede Woche neue Events · ständig neue Städte.",
      "hm.landing.forme_kicker": "Das ist für dich, wenn…",
      "hm.landing.forme_title": "Du willst echte Interessen. Echte Menschen. Echte Events.",
      "hm.landing.forme_1": "Du hast genug von der Swipe-App-Müdigkeit und willst Menschen wirklich bei dem treffen, was du gerne tust.",
      "hm.landing.forme_2": "Du bist in eine neue Stadt gezogen und brauchst eine herzliche Crew, keine weitere Chat-Gruppe.",
      "hm.landing.forme_3": "Du schätzt Tiefe mehr als Reichweite — eine tolle Gruppe schlägt fünfzig Follower.",
      "hm.landing.forme_4": "Du glaubst, dass geteilte Interessen + zwei feste Stunden im echten Leben der wahre Cheat-Code sind.",
      "hm.landing.notme_kicker": "Das ist nichts für dich, wenn…",
      "hm.landing.notme_title": "HustleMeet funktioniert nicht für jeden.",
      "hm.landing.notme_1": "Du kannst dich nicht verpflichten zu erscheinen — Hosts und Mitglieder verlassen sich auf deine Zusage.",
      "hm.landing.notme_2": "Du suchst einen Dating-Service — Freundschaften stehen hier an erster Stelle.",
      "hm.landing.notme_3": "Du willst dich nur mitteilen, ohne jemanden zu treffen — das hier funktioniert nur persönlich.",
      "hm.landing.notme_4": "Du erwartest endlose Gratis-Vorteile — manche Events kosten etwas. Der Preis ist immer transparent.",
      "hm.landing.safety_kicker": "Unser Sicherheitsversprechen",
      "hm.landing.safety_title": "Herzlich, aber niemals nachlässig.",
      "hm.landing.safety_1_ttl": "Öffentliche Orte",
      "hm.landing.safety_1_body": "Jedes Event findet an einem öffentlichen Ort mit Personal statt — ein Café, Restaurant, Park, Fitnessstudio oder Studio. Niemals bei jemandem zu Hause, außer alle Teilnehmer stimmen explizit zu.",
      "hm.landing.safety_2_ttl": "Community-Standards",
      "hm.landing.safety_2_body": "Null Toleranz für Respektlosigkeit, Belästigung oder Unehrlichkeit. Mitglieder, die dagegen verstoßen, werden sofort entfernt.",
      "hm.landing.safety_3_ttl": "Datenschutz",
      "hm.landing.safety_3_body": "Deine Daten werden niemals öffentlich geteilt. Du kontrollierst, mit wem du nach jedem Event verbunden bleibst.",
      "hm.landing.safety_4_ttl": "Support auf Abruf",
      "hm.landing.safety_4_body": "Unser Team ist vor, während und nach jedem Event erreichbar, falls du Fragen oder Bedenken hast.",
      "hm.landing.bottom_title": "Deine nächste Lieblingsgruppe ist nur einen Tipp entfernt.",
      "hm.landing.bottom_sub": "Wähle deinen Pfad — tritt einer bestehenden Gruppe bei, bring deine Crew in einen Circle oder lass dich vom Matchmaker an einen Tisch setzen, der bereits zu dir passt.",
      "hm.landing.bottom_cta_circles": "Einen Circle starten",
      "hm.landing.bottom_cta_mm": "Zum Matchmaker-Quiz",
      "hm.circles.page_title": "HustleMeet Circles · Deine Crew, jedes Mal ein neuer Ort",
      "hm.circles.kicker": "HustleMeet · Circles",
      "hm.circles.hero_title": "Deine Crew. Jedes Mal ein neuer Tisch.",
      "hm.circles.hero_title_accent": "Deine Crew",
      "hm.circles.hero_sub": "Hast du bereits deine Freunde? Circles ist der einfachste Weg, sie eng beisammen zu halten. Wir buchen die Location, planen das Quiz, verteilen die Karten. Du erscheinst einfach — gleiche Crew, neuer Abend, neuer Ort.",
      "hm.circles.cta_find": "Einen Circle starten",
      "hm.circles.cta_host_hero": "Einem offenen Circle beitreten",
      "hm.circles.trust_venue": "Wechselnde, geprüfte Locations",
      "hm.circles.trust_party": "4–12 Freunde pro Circle",
      "hm.circles.trust_verified": "Verifizierte Mitglieder",
      "hm.circles.trust_friendship": "Echte Freundschaften, eng gehalten",
      "hm.circles.how_kicker": "Wie es funktioniert",
      "hm.circles.how_title": "Vier Schritte. Deine Crew bleibt eine Crew.",
      "hm.circles.how_step1_ttl": "Starte deinen Circle",
      "hm.circles.how_step1_sub": "Gib ihm einen Namen, lade deine Freunde ein (bis zu 12) und wähle einen Rhythmus — wöchentlich, zweiwöchentlich oder monatlich.",
      "hm.circles.how_step2_ttl": "Wir wählen die Location",
      "hm.circles.how_step2_sub": "Für jedes Event schlagen wir einen neuen Spot vor — Restaurant, Quizabend, Kartenrunde, Weinbar. Oder leg einen Favoriten fest.",
      "hm.circles.how_step3_ttl": "Alle sagen mit einem Tipp zu",
      "hm.circles.how_step3_sub": "Auto-Erinnerungen, Kostenaufteilung, im Voraus bezahlte Plätze, damit Absagen nicht den Plan ruinieren. Die Rechnung ist erledigt, bevor die Vorspeise serviert wird.",
      "hm.circles.how_step4_ttl": "Behalte den Rhythmus bei",
      "hm.circles.how_step4_sub": "Ein Termin im Kalender, der nie wieder organisiert werden muss. Die Gruppe festigt sich über Monate, nicht Minuten.",
      "hm.circles.cadence_weekly_ttl": "Wöchentlich",
      "hm.circles.cadence_weekly_sub": "Jede Woche",
      "hm.circles.cadence_weekly_body": "Für Crews, die ein Ritual am Dienstagabend wollen — gleiche Gesichter, jedes Mal ein neuer Ort.",
      "hm.circles.cadence_fortnight_ttl": "Zweiwöchentlich",
      "hm.circles.cadence_fortnight_sub": "Alle 2 Wochen",
      "hm.circles.cadence_fortnight_body": "Der ideale Mittelweg — regelmäßig, ohne volle Terminkalender zu überfordern.",
      "hm.circles.cadence_monthly_ttl": "Monatlich",
      "hm.circles.cadence_monthly_sub": "Jeden Monat",
      "hm.circles.cadence_monthly_body": "Circles für besondere Anlässe — Sonntagsbraten, große Quizabende, Degustation, Poker.",
      "hm.circles.feast_kicker": "Wähle deinen Abend",
      "hm.circles.feast_title": "Drei Arten, ihn zu verbringen",
      "hm.circles.feast_sub": "Jeder Circle wählt, wie der Abend abläuft. Wechselt ab oder legt den Favoriten eurer Crew fest.",
      "hm.circles.feast_surprise_ttl": "Ein neues Restaurant probieren",
      "hm.circles.feast_surprise_body": "Wir schlagen drei Locations in eurer Nähe vor, alle stimmen ab, die Küche bereitet sich auf einen vollen Tisch vor. Kostenaufteilung, im Voraus bezahlte Plätze.",
      "hm.circles.feast_preselect_ttl": "Quiz / Karten / Spiele",
      "hm.circles.feast_preselect_body": "Buche einen Quizabend, ein Pokerzimmer, eine Brettspielbar oder eine Bowlingbahn. Wir kümmern uns um die Location, den Buy-in und die Erinnerungen.",
      "hm.circles.feast_per_event_ttl": "Einmaliger Themenabend",
      "hm.circles.feast_per_event_body": "Weinverkostung, Chef's Table, Cocktailkurs, Escape Room. Ab und zu etwas Besonderes — voll organisiert.",
      "hm.circles.live_kicker": "Aktuell",
      "hm.circles.live_title": "Circles, denen du beitreten kannst",
      "hm.circles.live_host_link": "Eigenen starten",
      "hm.circles.live_loading": "Wird geladen…",
      "hm.circles.live_empty_title": "Sei der erste Circle in deiner Stadt.",
      "hm.circles.live_empty_sub": "Starte einen mit drei Freunden und wir helfen dir, den Rest einzuladen — plus die ersten drei Locations gehen auf uns.",
      "hm.circles.live_empty_cta": "Einen Circle starten",
      "hm.circles.host_kicker": "Für Circle-Leader",
      "hm.circles.host_title": "Du bringst die Crew. Wir machen die Admin.",
      "hm.circles.host_body": "Wenn du der Freund bist, der immer den Abend organisiert, spart dir Circles Stunden. Auto-Erinnerungen, im Voraus bezahlte RSVPs, Location-Vorschläge, Kostenaufteilung, Wartelisten-Management und ein Cashback-Guthaben für jeden Abend. Du nimmst einfach als normales Mitglied teil.",
      "hm.circles.host_perk_1": "Auto-Location-Shortlists — nie wieder Google durchforsten",
      "hm.circles.host_perk_2": "Im Voraus bezahlte Plätze — null Absagen",
      "hm.circles.host_perk_3": "Kostenaufteilung vor dem Dessert erledigt",
      "hm.circles.host_perk_4": "Erinnerungen + Kalendereinladungen auf Autopilot",
      "hm.circles.host_perk_5": "Warteliste mit Auto-Nachrücken bei Absagen",
      "hm.circles.host_perk_6": "Cashback-Guthaben bei jedem Abend",
      "hm.circles.host_cta": "Jetzt einen Circle starten",
      "hm.circles.why_kicker": "Warum Circles funktionieren",
      "hm.circles.why_title": "Freundschaften unter Erwachsenen brauchen Struktur",
      "hm.circles.why_1_ttl": "Gleiche Crew, tieferes Vertrauen",
      "hm.circles.why_1_body": "Das Leben zieht Freunde oft auseinander. Circles sind der feste Rhythmus, der alle zusammenhält — die gleichen 4–12 Freunde, am gleichen Abend, egal was passiert.",
      "hm.circles.why_2_ttl": "Keine Admin-Müdigkeit",
      "hm.circles.why_2_body": "Niemand muss der WhatsApp-Gruppen-Chef sein. Wir wählen die Locations, senden die Erinnerungen, wickeln die Zahlungen ab, schließen den Kreis.",
      "hm.circles.why_3_ttl": "Neuer Ort, neue Erinnerung",
      "hm.circles.why_3_body": "Entdecke Australiens beste kleine Restaurants, Quizräume und Spielebars. Vertraute Gesichter, frische Kulissen — die perfekte Mischung aus Routine und Abwechslung.",
      "hm.circles.bottom_title": "Der Abend, auf den sich deine Crew freut, ohne dass jemand planen muss.",
      "hm.circles.bottom_sub": "Ein Circle. Die gleichen Freunde. Jedes Mal eine neue Location.",
      "hm.circles.bottom_cta_find": "Einen Circle starten",
      "hm.circles.bottom_cta_mm": "Triff neue Leute mit Matchmaker →",
      "hm.circles.card_host_pill": "Circle",
      "hm.matchmaker.page_title": "HustleMeet Matchmaker · KI wählt den richtigen Tisch",
      "hm.matchmaker.kicker": "HustleMeet · Matchmaker",
      "hm.matchmaker.hero_title": "KI wählt den Tisch. Du erscheinst einfach.",
      "hm.matchmaker.hero_title_accent": "Du erscheinst einfach.",
      "hm.matchmaker.hero_sub": "Beantworte ein kurzes Quiz zu Persönlichkeit und Werten. Unser Matchmaker setzt dich mit vier bis sieben Fremden zusammen, die sich bis zum Dessert wie alte Freunde anfühlen werden. Kein Swipen. Kein Smalltalk. Nur ein gemeinsamer Tisch, bereit wenn du es bist.",
      "hm.matchmaker.cta_quiz": "Zum Matching-Quiz",
      "hm.matchmaker.cta_see_circles": "Stattdessen Circles ansehen →",
      "hm.matchmaker.trust_big5": "Big-5 Persönlichkeit bewertet",
      "hm.matchmaker.trust_seats": "4–8 Plätze pro Tisch",
      "hm.matchmaker.trust_lock": "Kein Swipen · Keine Fotos",
      "hm.matchmaker.trust_verified": "Nur verifizierte Mitglieder",
      "hm.matchmaker.how_kicker": "Wie es funktioniert",
      "hm.matchmaker.how_title": "Vier Schritte zu einem Tisch, der bereits passt.",
      "hm.matchmaker.how_step1_ttl": "Mach das Quiz",
      "hm.matchmaker.how_step1_sub": "Zehnminütiger Fragebogen — Persönlichkeit, Werte, Ernährung, Verfügbarkeit. Du machst ihn nur einmal.",
      "hm.matchmaker.how_step2_ttl": "Matching über Nacht",
      "hm.matchmaker.how_step2_sub": "Unsere KI analysiert dein Profil und Australiens aktuelle Dinner-Liste. Wenn dein Fit-Score 0,70 oder höher ist, wird eine persönliche Einladung erstellt.",
      "hm.matchmaker.how_step3_ttl": "Zusagen oder Ablehnen",
      "hm.matchmaker.how_step3_sub": "Du erhältst eine klare Einladungs-E-Mail mit der Location, dem Tischmotto und den drei Interessen, die du mit der Gruppe teilst.",
      "hm.matchmaker.how_step4_ttl": "Es lernt von dir",
      "hm.matchmaker.how_step4_sub": "Jede Umfrage nach dem Essen verfeinert das Modell. Dein nächstes Match wird schlauer, je öfter du essen gehst.",
      "hm.matchmaker.science_kicker": "Warum es funktioniert",
      "hm.matchmaker.science_title": "Der Matchmaker rät nicht — er bewertet.",
      "hm.matchmaker.science_sub": "Jedes Match ist eine gewichtete Kombination aus vier Faktoren. Wenn der gesamte Tisch im Durchschnitt über 0,70 liegt, versenden wir die Einladungen. Alles darunter bleibt in der Warteschlange.",
      "hm.matchmaker.science_1_ttl": "Persönlichkeit (35%)",
      "hm.matchmaker.science_1_body": "Kosinus-Ähnlichkeit über den Big-5-Persönlichkeitsvektor — Offenheit, Gewissenhaftigkeit, Extraversion, Verträglichkeit und emotionale Stabilität.",
      "hm.matchmaker.science_2_ttl": "Werte (25%)",
      "hm.matchmaker.science_2_body": "Überschneidung bei den Werten, die ein Gespräch beim Abendessen wirklich beleben — Familie, Neugier, Wachstum, Hilfsbereitschaft, Abenteuer.",
      "hm.matchmaker.science_3_ttl": "Interessen (25%)",
      "hm.matchmaker.science_3_body": "Jaccard-Überschneidung deiner spezifischen Interessen — Wandern auf dem Six Foot Track, Sydney Filmclub, Melbourne Kaffeeszene, dein drittes Buch des Jahres.",
      "hm.matchmaker.science_4_ttl": "Verfügbarkeit (15%)",
      "hm.matchmaker.science_4_body": "Der Abend, an dem du tatsächlich Zeit hast. Keine Einladung für Dienstag 19 Uhr, wenn dein Kalender für immer „Dienstag-Gym“ sagt.",
      "hm.matchmaker.safety_kicker": "Sicherheit durch Design",
      "hm.matchmaker.safety_title": "Der Matchmaker ist diskret vorsichtig.",
      "hm.matchmaker.safety_1_ttl": "Konfidenzschwellenwert",
      "hm.matchmaker.safety_1_body": "Unter einem Gruppen-Score von 0,70 geht keine Einladung raus. Ein unsicheres Match ist schlechter als gar kein Abendessen.",
      "hm.matchmaker.safety_2_ttl": "Beidseitige Sperrliste",
      "hm.matchmaker.safety_2_body": "Wenn zwei Mitglieder nach einem Essen privat mit „Nein“ signalisieren, setzen wir sie nie wieder zusammen.",
      "hm.matchmaker.safety_3_ttl": "Auto-Kill-Switch",
      "hm.matchmaker.safety_3_body": "Wenn 20% oder mehr der jüngsten Umfragen einen Tisch mit 2/5 oder weniger bewerten, pausieren die Einladungen für 24h, während ein Operator dies prüft.",
      "hm.matchmaker.safety_4_ttl": "Community-Standards",
      "hm.matchmaker.safety_4_body": "Jedes Mitglied akzeptiert einen Verhaltenskodex in einfachem Deutsch. Wir entfernen Personen, die dagegen verstoßen — ohne Widerspruch.",
      "hm.matchmaker.safety_5_ttl": "Nur öffentliche Orte",
      "hm.matchmaker.safety_5_body": "Jedes Matchmaker-Dinner findet in einem echten Restaurant mit Personal statt. Niemals bei jemandem zu Hause.",
      "hm.matchmaker.safety_6_ttl": "Feedback nach dem Event",
      "hm.matchmaker.safety_6_body": "Deine private Umfrage formt zukünftige Matches und kann eine Überprüfung der Location oder eines Teilnehmers auslösen.",
      "hm.matchmaker.loop_kicker": "Der Lernkreislauf",
      "hm.matchmaker.loop_title": "Jedes Abendessen macht dein nächstes besser.",
      "hm.matchmaker.loop_body": "Der Matchmaker ist nicht statisch. Nach jedem Event gibst du eine kurze private Umfrage ab — wie sich das Gespräch anfühlte, mit wem du wieder essen gehen würdest, ob die Location überzeugt hat. Diese Signale fließen zurück in das Modell und formen jedes zukünftige Match.",
      "hm.matchmaker.loop_1": "„Nochmal essen“-Stimmen der Peers stärken deine Pairings",
      "hm.matchmaker.loop_2": "Gegenseitige „Nein“-Stimmen lassen Paare lautlos ausscheiden",
      "hm.matchmaker.loop_3": "Location- & Gesprächsbewertungen optimieren den Sitzplatz-Mix",
      "hm.matchmaker.loop_4": "Zuverlässigkeits-Score schützt Mitglieder vor wiederholten No-shows",
      "hm.matchmaker.loop_5": "Lern-Bias verstärkt gute Matches über die Monate hinweg",
      "hm.matchmaker.loop_6": "Du behältst die Kontrolle — Opt-out oder Profil-Reset jederzeit möglich",
      "hm.matchmaker.vs_kicker": "Was ist richtig für mich?",
      "hm.matchmaker.vs_title": "Matchmaker oder Circles?",
      "hm.matchmaker.vs_mm_ttl": "Wähle mich, wenn ich Abwechslung will.",
      "hm.matchmaker.vs_mm_1": "Jedes Mal ein anderer Tisch, Stadt, Küche",
      "hm.matchmaker.vs_mm_2": "KI-kuratierte Gruppe von 4–8 kompatiblen Fremden",
      "hm.matchmaker.vs_mm_3": "Wöchentlicher Rhythmus, keine Host-Verpflichtung",
      "hm.matchmaker.vs_mm_4": "Ideal, wenn du neu in der Stadt oder auf Reisen bist",
      "hm.matchmaker.vs_mm_cta": "Zum Matching-Quiz",
      "hm.matchmaker.vs_circles_ttl": "Wähle mich, wenn ich Stammgäste will.",
      "hm.matchmaker.vs_circles_1": "Gleicher Host · gleiche Location · gleicher Abend",
      "hm.matchmaker.vs_circles_2": "Eine Gruppe, die zu deiner Mittwochs-Tradition wird",
      "hm.matchmaker.vs_circles_3": "Von einem erfahrenen Host persönlich kuratiert",
      "hm.matchmaker.vs_circles_4": "Ideal, wenn du gerade erst umgezogen bist und Wurzeln schlagen willst",
      "hm.matchmaker.vs_circles_cta": "Circles durchsuchen",
      "hm.matchmaker.bottom_title": "Ein Quiz. Jedes Abendessen danach passt bereits.",
      "hm.matchmaker.bottom_sub": "Du wirst innerhalb von zwei Wochen gematcht. Es ist das einfachste „Ja“, das du diesen Monat sagen wirst.",
      "hm.matchmaker.bottom_cta": "Zum Matching-Quiz",
      "hm.matchmaker.bottom_note": "Keine Kreditkarte · 10 Minuten · Abmeldung jederzeit",
      /* AUTO-TRANSLATED hm.* END */
    },
  },
  pt: {
    translation: {
      /* AUTO-TRANSLATED pt START */
      plaza_nav_features: "Funcionalidades",
      plaza_nav_rewards: "Recompensas",
      plaza_nav_how: "Como funciona",
      plaza_nav_roadmap: "Roadmap",
      plaza_nav_faq: "FAQ",
      plaza_hero_badge: "Propriedade australiana",
      plaza_hero_kicker: "Um carrinho. Todas as recompensas.",
      plaza_hero_title_1: "Compre australiano.",
      plaza_hero_title_2: "Ganhe em todo lugar.",
      plaza_hero_sub: "Milhares de vendedores australianos num só lugar. Cada dólar que gastas rende HustlePoints — resgatáveis em restaurantes, cafés, hotéis, cabeleireiros, salões de estética para animais e qualquer negócio na rede HustleSuite.",
      plaza_cta_start: "Regista-te grátis — leva 30 segundos",
      plaza_cta_signin: "Iniciar sessão",
      plaza_cta_shop: "Explorar a loja",
      plaza_hero_stat_1_v: "12.847",
      plaza_hero_stat_1_l: "produtos disponíveis",
      plaza_hero_stat_2_v: "340+",
      plaza_hero_stat_2_l: "vendedores australianos",
      plaza_hero_stat_3_v: "$4,2M",
      plaza_hero_stat_3_l: "recompensas pagas",
      plaza_hero_stat_4_v: "19",
      plaza_hero_stat_4_l: "categorias",
      plaza_problem_kicker: "O problema das compras online hoje",
      plaza_problem_title: "Dez separadores. Dez passwords. Zero fidelização.",
      plaza_problem_sub: "Já fazes malabarismos entre Amazon, eBay, Kogan, Kmart, Woolies, Coles, Bunnings, JB, Officeworks e meia dúzia de lojas especializadas. Passas horas a comparar preços — e no fim só recebes um recibo.",
      plaza_problem_1: "Preços impossíveis de comparar entre sites",
      plaza_problem_2: "Vendedores estrangeiros a fingir ser locais",
      plaza_problem_3: "Pontos de fidelização presos numa única loja",
      plaza_problem_4: "Avaliações falsas de bots e drop-shippers",
      plaza_solution_kicker: "O que a HustlePlaza faz de diferente",
      plaza_solution_title: "O último carrinho que vais abrir hoje.",
      plaza_solution_sub: "Uma pesquisa. Um carrinho. Uma finalização de compra. Cada compra devolve-te HustlePoints que podes gastar em todo o nosso ecossistema — não apenas nesta loja.",
      plaza_solution_1_t: "Um carrinho, todos os vendedores",
      plaza_solution_1_b: "Compra em qualquer vendedor australiano na plataforma e paga apenas uma vez no checkout. Entrega consolidada sempre que possível.",
      plaza_solution_2_t: "Apenas vendedores locais verificados",
      plaza_solution_2_b: "Cada vendedor tem a identidade verificada antes de poder listar produtos. Sem bots, sem falsificações, sem mistérios de drop-shipping.",
      plaza_solution_3_t: "Recompensas que realmente viajam",
      plaza_solution_3_b: "Pontos ganhos aqui gastam-se em qualquer negócio HustleSuite — cafés, hotéis, cabeleireiros, restaurantes, salões para animais.",
      plaza_solution_4_t: "Rastreio ao vivo, num só ecrã",
      plaza_solution_4_b: "Todas as encomendas de todos os vendedores rastreadas numa página. Notificações por email e SMS em cada etapa.",
      plaza_rewards_kicker: "O sistema de HustlePoints",
      plaza_rewards_title: "Ganha enquanto compras. Gasta em qualquer lugar da rede.",
      plaza_rewards_sub: "Cada $1 gasto na HustlePlaza rende HustlePoints. Resgata-os em qualquer restaurante, café, hotel, cabeleireiro ou negócio de serviços que funcione com HustleSuite — em qualquer lugar da rede.",
      plaza_rewards_step_1: "Compra no marketplace",
      plaza_rewards_step_1_b: "Compra qualquer coisa, desde uma t-shirt a um frigorífico.",
      plaza_rewards_step_2: "Ganha HustlePoints instantaneamente",
      plaza_rewards_step_2_b: "Os pontos aparecem no momento em que a tua encomenda é enviada — sem cartões, sem carimbos, sem jogos de expiração.",
      plaza_rewards_step_3: "Gasta em toda a rede",
      plaza_rewards_step_3_b: "Resgata em cafés, restaurantes, hotéis, cabeleireiros, salões para animais, prestadores de serviços.",
      plaza_rewards_step_4: "Ou levanta em dinheiro",
      plaza_rewards_step_4_b: "Converte em transferência bancária assim que atingires $50.",
      plaza_cats_kicker: "Compra por categoria",
      plaza_cats_title: "19 categorias. Vendedores australianos. Stock disponível.",
      plaza_cats_sub: "Explora uma amostra do que está disponível agora — ou regista-te e compra no catálogo completo de mais de 12.000 produtos.",
      plaza_cats_cta: "Ver todas as categorias",
      plaza_vendors_kicker: "Vendedores australianos reais",
      plaza_vendors_title: "Conhece os criadores, produtores e lojas familiares.",
      plaza_vendors_sub: "Cada vendedor na HustlePlaza é verificado com ABN, baseado na Austrália e integrado pessoalmente. Sem intermediários estrangeiros a fingir ser locais.",
      plaza_how_kicker: "Como funciona",
      plaza_how_title: "Três passos. Um carrinho. Recompensas para sempre.",
      plaza_how_1_title: "Regista-te em 30 segundos",
      plaza_how_1_body: "Verifica o teu email e começa a explorar imediatamente. Adiciona um número de telefone quando estiveres pronto para finalizar a compra.",
      plaza_how_2_title: "Enche o teu carrinho",
      plaza_how_2_body: "Compra em todos os vendedores. Um carrinho, uma vista de entrega, uma finalização de compra. Pagamento via Stripe ou PayPal.",
      plaza_how_3_title: "Ganha e gasta em qualquer lugar",
      plaza_how_3_body: "Os HustlePoints aparecem no momento em que a tua encomenda é enviada. Gasta-os em qualquer negócio HustleSuite, ou levanta em dinheiro aos $50.",
      plaza_compare_kicker: "Como nos comparamos",
      plaza_compare_title: "HustlePlaza vs os gigantes",
      plaza_compare_col_us: "HustlePlaza",
      plaza_compare_col_amz: "Amazon AU",
      plaza_compare_col_ebay: "eBay AU",
      plaza_compare_col_kmart: "Kmart / Big W",
      plaza_compare_row_1: "Propriedade australiana",
      plaza_compare_row_2: "Carrinho único para todos os vendedores",
      plaza_compare_row_3: "Recompensas utilizáveis FORA desta loja",
      plaza_compare_row_4: "Levantamento de recompensas para o banco",
      plaza_compare_row_5: "Apoio ao cliente baseado na Austrália",
      plaza_trust_kicker: "Por que verificamos",
      plaza_trust_title: "Clientes reais. Avaliações reais.",
      plaza_trust_body: "Verificamos o teu email no registo, o teu número de telemóvel antes do checkout, e exigimos uma verificação fotográfica ao vivo antes de poderes publicar avaliações, conversar com vendedores ou fazer compras de alto valor. Isso atrasa os burlões e mantém os preços baixos para ti.",
      plaza_trust_1: "Verificação de email para aderir",
      plaza_trust_2: "Verificação por SMS para finalizar compra",
      plaza_trust_3: "Verificação fotográfica ao vivo para avaliações e chat",
      plaza_trust_4: "Verificações de identidade do vendedor na integração",
      plaza_test_kicker: "O que os nossos primeiros membros dizem",
      plaza_test_title: "Direto de clientes reais.",
      plaza_test_1_body: "Usei os meus pontos da HustlePlaza para pagar o jantar num café perto do trabalho. Sinceramente não acreditei que funcionaria até a empregada sorrir e dizer 'está tudo pago'.",
      plaza_test_1_name: "Priya S.",
      plaza_test_2_body: "Seis vendedores, uma janela de entrega, um email de confirmação. Normalmente teria seis passwords e seis números de rastreio. Isto simplesmente funciona.",
      plaza_test_2_name: "Marcus T.",
      plaza_test_3_body: "Como pequenos produtores de mel, finalmente somos pagos no mesmo dia, recebemos avaliações reais e não há comissão de 40%. A HustlePlaza levou-me de 'trabalho extra' a tempo inteiro em oito meses.",
      plaza_test_3_name: "Kate & Dave",
      plaza_faq_kicker: "Respostas",
      plaza_faq_title: "As perguntas que recebemos todas as semanas",
      plaza_faq_1_q: "A HustlePlaza é realmente de propriedade australiana?",
      plaza_faq_1_a: "Sim. A HustlePlaza é 100% de propriedade australiana, alojada em servidores australianos, gerida por uma equipa australiana. Cada vendedor passa por verificação de identidade antes da primeira listagem poder ficar ativa.",
      plaza_faq_2_q: "Como funcionam as recompensas?",
      plaza_faq_2_a: "Cada dólar que gastas na HustlePlaza rende-te HustlePoints. Os pontos aparecem no momento em que a tua encomenda é enviada e nunca expiram. Gasta-os em qualquer negócio que funcione com HustleSuite, ou levanta-os para o teu banco assim que tiveres poupado o suficiente.",
      plaza_faq_3_q: "Por que verificam fotografias?",
      plaza_faq_3_a: "Para que as avaliações que lês sejam reais. A verificação fotográfica é uma selfie ao vivo feita através da câmara do telemóvel — a mesma técnica que os bancos usam — e impede bots, contas falsas e atores hostis de manipular a plataforma.",
      plaza_faq_4_q: "E se a minha encomenda não chegar?",
      plaza_faq_4_a: "Cada encomenda é garantida pela nossa HustleGuarantee: se a encomenda não chegar ou não for conforme descrita, reembolsamos-te em 3 dias úteis e depois contactamos o vendedor. Nunca ficas sem o teu dinheiro.",
      plaza_faq_5_q: "A informação do meu cartão está segura?",
      plaza_faq_5_a: "Nunca vemos ou armazenamos o número do teu cartão. Todos os pagamentos são processados através da Stripe (PCI-DSS Nível 1) ou PayPal, ambos com autenticação 3D Secure que te protege contra cobranças fraudulentas em cartões roubados.",
      plaza_faq_6_q: "Fazem envios internacionais?",
      plaza_faq_6_a: "Ainda não. A HustlePlaza é apenas para a Austrália no lançamento — é assim que podemos prometer entrega na mesma semana para a maioria dos artigos e apoio no teu fuso horário. A Nova Zelândia está no roadmap de 2026.",
      plaza_faq_7_q: "Quanto custa aderir?",
      plaza_faq_7_a: "Nada. A adesão é gratuita para sempre. Os vendedores pagam uma pequena comissão por venda — os compradores pagam $0 para aderir, $0 para ganhar, $0 para resgatar.",
      plaza_faq_8_q: "Posso vender na HustlePlaza?",
      plaza_faq_8_a: "Absolutamente. Vai a hustlesuite.au para te registares como vendedor — aprovação no mesmo dia para negócios australianos verificados, mais os primeiros 30 dias sem comissão.",
      plaza_final_kicker: "Preços de fundador — lugares limitados",
      plaza_final_title: "Sê um dos primeiros — garante preços de fundador.",
      plaza_final_sub: "Regista-te hoje como Membro HustleSuite. Serás o primeiro a saber quando cada nova funcionalidade for lançada, e a tua conta manterá recompensas e preços de nível fundador para toda a vida.",
      plaza_final_bonus: "Os fundadores nunca perdem a tarifa de fundador. Grátis para sempre, cancela a qualquer momento.",
      plaza_final_cta: "Registar como Membro Fundador",
      plaza_final_disclosure: "Sem cartão necessário. Apenas verificação de email + telemóvel.",
      plaza_roadmap_kicker: "O que estamos a construir a seguir",
      plaza_roadmap_title: "Seis ferramentas criadas para facilitar o teu trabalho.",
      plaza_roadmap_sub: "A HustleSuite é mais do que uma loja — é uma camada completa de produtividade. Aqui está o que vem a seguir.",
      plaza_roadmap_soon: "A chegar em breve",
      plaza_roadmap_next: "Próximo na fila",
      plaza_roadmap_1_t: "HustleDictate — voz para texto em qualquer formulário",
      plaza_roadmap_1_b: "Extensão Chrome. Prime Alt+M, fala, e qualquer campo em qualquer website preenche-se sozinho. Candidaturas imobiliárias, faturas, documentação NDIS — feito em metade do tempo.",
      plaza_roadmap_2_t: "HustleConnect Edit-Anywhere",
      plaza_roadmap_2_b: "Para proprietários de WordPress: edita o teu site ao vivo de qualquer página com uma única tecla. Sem mais idas e vindas ao wp-admin.",
      plaza_roadmap_3_t: "Botão universal guardar-em-HustleSuite",
      plaza_roadmap_3_b: "Clica com o botão direito em qualquer página web — anúncio imobiliário, artigo, produto, restaurante — e vai parar ao módulo certo pronto para ação.",
      plaza_roadmap_4_t: "Verificador de preços HustlePlaza",
      plaza_roadmap_4_b: "Explora Amazon, eBay ou Kmart — um chip flutuante mostra o mesmo produto na HustlePlaza com pré-visualização das tuas recompensas.",
      plaza_roadmap_5_t: "Coletor de avaliações",
      plaza_roadmap_5_b: "Sincronização automática de novas avaliações do Google, Facebook e TripAdvisor para o teu painel HustleSuite. Poupa ~20 minutos por semana.",
      plaza_roadmap_6_t: "Barra lateral HustleSuite",
      plaza_roadmap_6_b: "Painel lateral Chrome com encomendas ao vivo, KPIs, publicação rápida e faturação rápida. Todo o teu negócio numa barra ao lado de cada separador.",
      plaza_footer_owned: "Propriedade e alojamento australiano",
      plaza_footer_terms: "Termos",
      plaza_footer_privacy: "Privacidade",
      plaza_footer_contact: "Contacto",
      plaza_footer_vendors: "Vender na HustlePlaza",
      plaza_footer_help: "Centro de ajuda",
      plaza_footer_tagline: "Parte da família HustleSuite — um login, todos os serviços.",
      verify_gate_title: "Quase lá — registo rápido",
      verify_gate_body: "A HustlePlaza é um carrinho de compras que oferece muitos benefícios, incluindo recompensas resgatáveis que podem ser usadas, não apenas nesta plataforma, mas com qualquer outro Negócio que use a plataforma HustlePlaza ou qualquer outro Negócio Registado na HustleSuite. Alguns destes Negócios incluirão Restaurantes, Cafés, Hotéis, Cabeleireiros e até Salões para Animais. Para continuar a oferecer ótimos preços e serviços, precisamos de garantir que és real e verificar-te. Prime o Botão Aderir para continuar.",
      verify_gate_join: "Aderir à HustleSuite",
      verify_gate_signin: "Já tenho uma conta",
      verify_gate_cancel: "Agora não",
      verify_email_title: "Verifica o teu email",
      verify_email_body: "Enviaremos um código de 6 dígitos para a tua caixa de entrada.",
      verify_email_placeholder: "tu@exemplo.com",
      verify_email_send: "Enviar código",
      verify_email_code_ph: "Código de 6 dígitos",
      verify_email_check: "Verificar",
      verify_email_sent: "Código enviado — verifica a tua caixa de entrada.",
      verify_sms_title: "Verifica o teu telefone",
      verify_sms_body: "Adiciona um número de telemóvel para podermos enviar-te atualizações da encomenda e confirmar grandes compras.",
      verify_sms_placeholder: "+351912345678",
      verify_sms_send: "Enviar SMS",
      verify_sms_check: "Verificar",
      verify_sms_sent: "Código enviado — verifica o teu telefone.",
      verify_photo_title: "Verificação fotográfica",
      verify_photo_body: "Digitaliza o código QR com o teu telefone para tirar uma selfie ao vivo. Necessário para chat, compras de alto valor e publicações em Meetup / Tackle.",
      verify_photo_waiting: "À espera do teu telefone…",
      verify_photo_verified: "Fotografia verificada — está tudo pronto.",
      travel_cta_start: "Aderir grátis",
      travel_hero_kicker: "HustleTravel",
      travel_hero_title_1: "Uma adesão.",
      travel_hero_title_2: "Austrália inteira.",
      travel_hero_sub: "Hotéis, voos, experiências, planeamento de viagens de carro e aluguer de automóveis — reservados à melhor tarifa disponível e pagando-te HustlePoints em cada dólar gasto.",
      travel_cta_plan: "Planear uma viagem de carro",
      travel_cta_hotels: "Procurar hotéis",
      travel_svc_hotels_t: "Hotéis e apartamentos",
      travel_svc_hotels_b: "Compara mais de 200 sites de reservas num só lugar — metapesquisa alimentada por Trivago.",
      travel_svc_flights_t: "Voos",
      travel_svc_flights_b: "Domésticos e internacionais, com mapas de assentos em tempo real e regras tarifárias.",
      travel_svc_experiences_t: "Experiências e tours",
      travel_svc_experiences_b: "Dias de recife, provas em adegas, música ao vivo, excursões para crianças.",
      travel_svc_cars_t: "Aluguer de automóveis",
      travel_svc_cars_b: "Todas as principais marcas mais independentes locais — recolha em qualquer aeroporto australiano.",
      travel_svc_insurance_t: "Seguro de viagem",
      travel_svc_insurance_b: "Subscrito por seguradoras australianas reguladas pela APRA. Compara num só ecrã.",
      travel_svc_roadtrip_t: "Planeador de viagens de carro",
      travel_svc_roadtrip_b: "Itinerários conscientes da autonomia de VE com carregadores, paragens noturnas e estimativas de custo.",
      travel_final_kicker: "Preços de fundador",
      travel_final_title: "Garante recompensas de nível fundador para toda a vida.",
      travel_final_sub: "Regista-te uma vez na HustleSuite. Reserva tudo — hotéis, voos, experiências, seguros — na HustleTravel e ganha HustlePoints utilizáveis em todos os negócios da rede.",
      travel_final_cta: "Registar como Membro Fundador",
      travel_final_disclosure: "Política de melhor tarifa: resultados ordenados pelo melhor valor para o viajante, não por comissão.",
      dictate_hero_kicker: "HustleDictate",
      dictate_hero_title_1: "Fala.",
      dictate_hero_title_2: "Qualquer formulário. Qualquer website.",
      dictate_hero_sub: "A única extensão Chrome que transforma qualquer campo de texto num microfone de ditado de nível profissional — alimentado por Whisper.",
      dictate_cta_install: "Adicionar ao Chrome",
      dictate_uc_re_t: "Notas de arrendamento imobiliário",
      dictate_uc_re_b: "Dita relatórios de inspeção e mensagens de inquilinos diretamente no teu sistema de gestão.",
      dictate_uc_ndis_t: "Notas de progresso NDIS",
      dictate_uc_ndis_b: "Fala a nota do turno enquanto a memória está fresca — sem as mãos, sem digitação.",
      dictate_uc_invoice_t: "Descrições de faturas",
      dictate_uc_invoice_b: "Descrições de itens ditadas diretamente no Xero, MYOB ou QuickBooks.",
      dictate_uc_email_t: "Respostas de email longas",
      dictate_uc_email_b: "Fala as respostas no Gmail, Outlook ou qualquer webmail — três vezes mais rápido que digitar.",
      dictate_final_kicker: "Grátis para Membros Fundadores",
      dictate_final_title: "Cada teclado é agora um microfone.",
      dictate_final_sub: "Grátis para Membros Fundadores HustleSuite. $5/mês caso contrário — cancela a qualquer momento.",
      dictate_final_cta: "Adicionar ao Chrome — grátis",
      "finder.cat.roadtrip": "Viagem de carro",
      "finder.hotels.title": "Encontra um hotel próximo",
      "finder.hotels.sub": "Compara mais de 200 sites de reservas numa pesquisa — alimentado por Trivago.",
      "finder.hotels.where": "Onde",
      "finder.hotels.where_ph": "Cidade ou subúrbio (ex: Byron Bay, NSW)",
      "finder.hotels.checkin": "Check-in",
      "finder.hotels.checkout": "Check-out",
      "finder.hotels.adults": "Adultos",
      "finder.hotels.search": "Procurar hotéis",
      "finder.hotels.disclosure": "Abre Trivago — podemos ganhar comissão em reservas.",
      /* AUTO-TRANSLATED pt END */
      welcome_back: "Bem-vindo de volta",
      sign_out: "Sair",
    
      /* AUTO-TRANSLATED finder.* (added) */
      "finder.title": "HustleFinder",
      "finder.tagline": "Encontre o local público mais próximo — dados reais, em toda a Austrália.",
      "finder.search_placeholder": "Pesquisar locais, ruas ou subúrbios…",
      "finder.hint": "Comece a digitar acima, toque numa categoria ou use \"Perto de mim\".",
      "finder.near_me": "Perto de mim",
      "finder.searching": "A pesquisar…",
      "finder.suburb.placeholder": "Filtrar por subúrbio (ex: Darwin, NT)…",
      "finder.suburb.clear": "Limpar",
      "finder.search_in": "Pesquisar em {{cat}}",
      "finder.empty": "Sem resultados — tente outra categoria ou termo de pesquisa.",
      "finder.meta.title": "HustleFinder — diretório gratuito de subúrbios australianos",
      "finder.meta.desc": "Pesquise empresas locais, carregadores, cafés, casas de banho e mais em todos os subúrbios australianos — 100% gratuito.",
      "finder.footer.credits": "Créditos de dados e atribuição",
      "finder.footer.terms": "Termos",
      "finder.cat.toilet": "Casas de Banho",
      "finder.cat.fountain": "Bebedouros",
      "finder.cat.transport": "Transporte",
      "finder.cat.park": "Parques",
      "finder.cat.medical": "Médico",
      "finder.cat.school": "Escolas",
      "finder.cat.library": "Bibliotecas",
      "finder.cat.petrol": "Gasolina",
      "finder.cat.kidzone": "Zona Infantil",
      "finder.cat.puzzles": "Puzzles",
      /* AUTO-TRANSLATED rt.* START */
      "rt.brand_name": "Roadtrip Planner",
      "rt.page_title": "Planejador de Viagens EV na Austrália",
      "rt.from": "De",
      "rt.to": "Para",
      "rt.from_placeholder": "Bairro, vila ou cidade (ex: Newcastle NSW)",
      "rt.to_placeholder": "Bairro, vila ou cidade (ex: Hobart TAS)",
      "rt.stop": "PARADA",
      "rt.stop_placeholder": "Parada intermediária",
      "rt.add_stop": "Adicionar parada",
      "rt.upgrade_pro": "Atualize para o Pro para até 20 paradas",
      "rt.reverse": "Inverter (calcular volta)",
      "rt.from_current": "Da localização atual",
      "rt.range_per_charge": "Autonomia por carga (km)",
      "rt.connector": "Conector",
      "rt.connector_any": "Qualquer",
      "rt.drive_hours_day": "Horas de direção/dia",
      "rt.accommodation": "Acomodação",
      "rt.accom_any": "Qualquer (BnB · Hotel · Caravana)",
      "rt.accom_bnb": "BnB",
      "rt.accom_hotel": "Hotel",
      "rt.accom_caravan": "Parque de Caravana",
      "rt.min_rating": "Avaliação mínima",
      "rt.any_rating": "Qualquer avaliação",
      "rt.only_5star": "Apenas 5★",
      "rt.schedule": "Cronograma:",
      "rt.depart_at": "Partir às",
      "rt.arrive_by": "Chegar até",
      "rt.hotel_checkin": "Check-in no hotel",
      "rt.hotel_checkout": "Check-out no hotel",
      "rt.overnight_hint": "Trechos noturnos não serão agendados fora destes horários.",
      "rt.whos_in_car": "Quem está no carro?",
      "rt.adults": "Adultos",
      "rt.kids": "Crianças",
      "rt.ages": "Idades",
      "rt.show_attractions": "Mostrar atrações turísticas",
      "rt.show_kids": "Mostrar paradas infantis",
      "rt.overnight_stays": "Pernoites",
      "rt.fast_chargers_only": "Apenas carregadores rápidos",
      "rt.hotel_must_charger": "Hotel deve ter carregador",
      "rt.pet_only": "Apenas estadias que aceitam pets",
      "rt.safety": "Segurança",
      "rt.safety_90": "90% (plano)",
      "rt.safety_85": "85% (padrão)",
      "rt.safety_80": "80% (morros / reboque)",
      "rt.safety_70": "70% (carga pesada)",
      "rt.save_trip": "Salvar viagem",
      "rt.plan_my_trip": "Planejar minha viagem",
      "rt.refresh_trip": "Atualizar viagem",
      "rt.planning": "Planejando…",
      "rt.resume_title": "Retomar viagem daqui",
      "rt.resume_body": "Capturamos sua localização atual. Insira a autonomia restante no seu painel para planejarmos o próximo trecho com o que você realmente tem — não uma carga completa.",
      "rt.remaining_range": "Autonomia restante (km)",
      "rt.cancel": "Cancelar",
      "rt.replan_from_here": "Replanejar daqui",
      "rt.save_modal_title": "Nomeie esta viagem",
      "rt.save_modal_body": "Viagens salvas ficam no seu perfil — reabra a qualquer momento e clique em Replanejar.",
      "rt.save_name_placeholder": "ex: Brisbane → Sydney via Byron",
      "rt.your_saved_trips": "Suas viagens salvas",
      "rt.custom_location": "Localização personalizada",
      "rt.trip_summary": "Resumo da viagem",
      "rt.itinerary": "Itinerário",
      "rt.no_stops_needed": "Sem paradas necessárias — a viagem toda cabe na sua autonomia.",
      "rt.chargers_in_corridor": "carregadores no corredor",
      "rt.planned_stops": "paradas planejadas",
      "rt.overnight_stop": "parada para pernoite",
      "rt.overnight_stops": "paradas para pernoite",
      "rt.suggested_detours": "Desvios sugeridos — marque para adicionar",
      "rt.replan_with_detours": "Replanejar com desvios",
      "rt.attractions_label": "Atração",
      "rt.kids_label": "Crianças",
      "rt.km_from_start": "km do início",
      "rt.open_driving": "Abrir modo de condução",
      "rt.big_font_hint": "Fonte grande, ideal para suporte. Funciona com espelhamento de navegador CarPlay/Android Auto.",
      "rt.check_price_book": "Ver preço e reservar no Trivago ↗",
      "rt.visit_site": "Visitar site ↗",
      "rt.charger_onsite": "Carregador no local",
      "rt.leg_exceeds_range": "Este trecho excede a autonomia definida — você ficaria sem energia antes de chegar a este carregador. Reduza a autonomia estimada ou adicione uma recarga intermediária.",
      "rt.add_waypoint_replan": "Adicionar ponto e replanejar",
      /* AUTO-TRANSLATED rt.* END */
                      /* AUTO-TRANSLATED hm.* START */
      "hm.nav.hustlemeet": "HustleMeet",
      "hm.nav.circles": "Circles",
      "hm.nav.matchmaker": "Matchmaker",
      "hm.nav.meet": "Conhecer",
      "hm.nav.overview": "HustleMeetups",
      "hm.overview.page_title": "HustleMeetups — três formas acolhedoras de encontrar sua turma.",
      "hm.overview.kicker": "Parte da HustleSuite",
      "hm.overview.hero_title": "Três formas acolhedoras de encontrar sua turma.",
      "hm.overview.hero_title_accent": "sua turma",
      "hm.overview.hero_sub": "HustleMeetups é a maneira mais gentil da Australia de construir amizades reais — escolha a vibe que combina com você. Quer você queira um grupo de corrida semanal, um jantar mensal com sua turma atual ou uma mesa de rostos novos selecionada por IA, há um lugar para você.",
      "hm.overview.tile_meet_kicker": "HustleMeet",
      "hm.overview.tile_meet_title": "Encontre sua turma. Faça o que gosta.",
      "hm.overview.tile_meet_body": "O lugar na Australia para grupos focados em interesses. Corredores, programadores, investidores, praticantes de caminhada, dançarinos, clubes de café da manhã, grupos de leitura — centenas de grupos, eventos reais toda semana. Apareça uma vez ou toda semana.",
      "hm.overview.tile_meet_cta": "Explorar grupos",
      "hm.overview.tile_circles_kicker": "Circles",
      "hm.overview.tile_circles_title": "Seu grupo. Uma mesa nova a cada encontro.",
      "hm.overview.tile_circles_body": "Traga seu grupo regular de amigos. Nós reservamos um restaurante novo, planejamos o trivia, distribuímos as cartas. Você só precisa aparecer. A maneira mais prática de manter um grupo atual por perto — sem ninguém precisar organizar.",
      "hm.overview.tile_circles_cta": "Começar um Circle",
      "hm.overview.tile_matchmaker_kicker": "Matchmaker",
      "hm.overview.tile_matchmaker_title": "A IA escolhe a mesa. Você só aparece.",
      "hm.overview.tile_matchmaker_body": "Faça um quiz de 10 minutos. De um dia para o outro, sentamos você com estranhos que parecerão velhos amigos na hora da sobremesa. Uma mesa diferente a cada vez.",
      "hm.overview.tile_matchmaker_cta": "Fazer o quiz",
      "hm.overview.trust_kicker": "Por que funciona",
      "hm.overview.trust_title": "Confiança primeiro. Mesa depois.",
      "hm.overview.trust_1_title": "Membros verificados",
      "hm.overview.trust_1_body": "Cada grupo começa com uma verificação de ID real + pontuação de confiabilidade. Sem contas descartáveis.",
      "hm.overview.trust_2_title": "Anfitriões reais",
      "hm.overview.trust_2_body": "Cada grupo é liderado por um anfitrião aprovado que cuida da vibe, define o ritmo e cuida do ambiente.",
      "hm.overview.trust_3_title": "Amizade em primeiro lugar",
      "hm.overview.trust_3_body": "Tudo aqui é projetado para amizades reais, não encontros românticos. Nunca.",
      "hm.overview.trust_4_title": "Moderação de tolerância zero",
      "hm.overview.trust_4_body": "Uma denúncia, uma revisão, uma decisão. Pessoas mal-intencionadas são removidas na mesma noite.",
      "hm.overview.footer_line": "Parte da HustleSuite — de propriedade e hospedagem na Australia.",
      "hm.landing.page_title": "HustleMeet · Encontre sua turma. Faça o que gosta.",
      "hm.landing.kicker": "A rede de interesses da Australia",
      "hm.landing.hero_title": "Encontre sua turma. Faça o que gosta.",
      "hm.landing.hero_title_accent": "sua turma",
      "hm.landing.hero_sub": "HustleMeet é o lugar na Australia para grupos focados em interesses. Desenvolvedores de software depurando no almoço. Corredores de longa distância às 6 da manhã. Investidores se reunindo mensalmente. Grupos de caminhada, turmas de dança, clubes de café da manhã, noites de jogos de tabuleiro. Escolha um grupo, reserve o próximo evento e apareça.",
      "hm.landing.cta_explore_circles": "Navegar pelos grupos",
      "hm.landing.cta_try_matchmaker": "Comece seu próprio grupo",
      "hm.landing.trust_verified": "Membros verificados",
      "hm.landing.trust_venues": "Anfitriões de eventos aprovados",
      "hm.landing.trust_friendship": "Amizade primeiro — nunca encontros",
      "hm.landing.trust_moderation": "Moderação de tolerância zero",
      "hm.landing.subbrand_circles_kicker": "Circles",
      "hm.landing.subbrand_circles_title": "Já tem sua turma? Dê a eles um lar permanente.",
      "hm.landing.subbrand_circles_body": "Circles é para o grupo de amigos que já se reúne — clube do livro, noite de poker, grupo de corrida, amigos do trabalho. Nós cuidamos do local, dos lembretes e da lista. Você só aparece.",
      "hm.landing.subbrand_circles_b1": "Rodízio de locais, uma lista contínua",
      "hm.landing.subbrand_circles_b2": "Lembretes automáticos + RSVPs fáceis",
      "hm.landing.subbrand_circles_b3": "Trivia, cartas & noites temáticas à disposição",
      "hm.landing.subbrand_circles_cta": "Começar um Circle",
      "hm.landing.subbrand_matchmaker_kicker": "Matchmaker",
      "hm.landing.subbrand_matchmaker_title": "A IA escolhe a mesa. Você só aparece.",
      "hm.landing.subbrand_matchmaker_body": "Um quiz de 10 minutos — personalidade, valores, disponibilidade. Nosso Matchmaker senta você com estranhos que parecerão velhos amigos na hora da sobremesa. Uma mesa diferente a cada vez.",
      "hm.landing.subbrand_matchmaker_b1": "Personalidade Big-5 & valores pontuados",
      "hm.landing.subbrand_matchmaker_b2": "Convites da noite para o dia quando a afinidade é 0.70 ou superior",
      "hm.landing.subbrand_matchmaker_b3": "Aprende com cada jantar que você participa",
      "hm.landing.subbrand_matchmaker_cta": "Fazer o quiz",
      "hm.landing.how_kicker": "Como o HustleMeet funciona",
      "hm.landing.how_title": "Quatro passos. Encontre seu grupo. Apareça.",
      "hm.landing.how_step1_ttl": "Escolha seus interesses",
      "hm.landing.how_step1_sub": "Corrida, programação, investimentos, culinária, música, caminhada — diga-nos do que você gosta e onde mora.",
      "hm.landing.how_step2_ttl": "Navegue por grupos perto de você",
      "hm.landing.how_step2_sub": "Cada grupo publica seu anfitrião, seu ritmo e seu próximo evento. Sinta o clima antes de se comprometer.",
      "hm.landing.how_step3_ttl": "Confirme sua presença no próximo evento",
      "hm.landing.how_step3_sub": "Um toque, um convite no calendário, um lembrete claro no dia anterior. Alguns eventos são gratuitos, outros pagos — o preço é transparente.",
      "hm.landing.how_step4_ttl": "Apareça & conecte-se",
      "hm.landing.how_step4_sub": "Conheça o anfitrião, conheça a turma, faça o que gosta. Volte na semana seguinte se der liga.",
      "hm.landing.stats_kicker": "A nova forma de se encontrar na Australia",
      "hm.landing.stats_title": "O motivo pelo qual as pessoas continuam voltando.",
      "hm.landing.stats_92_sub": "Sentiram que deram liga com pelo menos uma pessoa no evento",
      "hm.landing.stats_68_sub": "Confirmam presença no próximo evento dentro de uma semana",
      "hm.landing.stats_85_sub": "Mantêm contato com alguém do grupo",
      "hm.landing.why_kicker": "Por que funcionamos",
      "hm.landing.why_title": "Interesses reais. Anfitriões reais. Eventos reais.",
      "hm.landing.why_1_ttl": "Focado em interesses, não apenas em localização",
      "hm.landing.why_1_body": "As pessoas no seu grupo realmente se importam com as mesmas coisas que você — esse é o atalho para uma amizade real.",
      "hm.landing.why_2_ttl": "Anfitriões de eventos reais",
      "hm.landing.why_2_body": "Cada grupo tem um anfitrião aprovado que cuida da vibe, define o ritmo e intervém se algo estiver errado. Não é um administrador do Facebook. É uma pessoa real.",
      "hm.landing.why_3_ttl": "Consistência importa",
      "hm.landing.why_3_body": "Novos eventos toda semana. Algo para esperar ansiosamente. Um ritmo que transforma estranhos na sua turma de terça à noite.",
      "hm.landing.why_4_ttl": "Seguro & protegido",
      "hm.landing.why_4_body": "Membros verificados, locais públicos, tolerância zero para desrespeito. Nossa equipe de suporte está de prontidão antes, durante e depois de cada evento.",
      "hm.landing.why_5_ttl": "Amizade primeiro",
      "hm.landing.why_5_body": "Isso não é um site de encontros. É comunidade. Interesses reais, pessoas reais, sem ficar deslizando telas no meio.",
      "hm.landing.why_6_ttl": "Pequeno o suficiente para importar",
      "hm.landing.why_6_body": "A maioria dos grupos limita os eventos a 4-15 lugares. Grande o suficiente para gerar conversas reais. Pequeno o suficiente para que ninguém se perca.",
      "hm.landing.cities_kicker": "Onde operamos",
      "hm.landing.cities_title": "Grupos se reunindo em toda a Australia & NZ.",
      "hm.landing.cities_sub_zero": "Primeiros grupos abrindo em mais de 15 cidades — participe cedo e ajude a moldar o ambiente.",
      "hm.landing.cities_sub_some": "Grupos ativos em mais de 15 metrópoles. Algo na sua cidade, esta semana.",
      "hm.landing.cities_sub_one": "{{count}} grupo ativo · novos eventos toda semana · sempre adicionando cidades.",
      "hm.landing.cities_sub_many": "{{count}} grupos ativos · novos eventos toda semana · sempre adicionando cidades.",
      "hm.landing.forme_kicker": "Isto é para você se…",
      "hm.landing.forme_title": "Você quer interesses reais. Pessoas reais. Eventos reais.",
      "hm.landing.forme_1": "Você cansou da fadiga dos apps de swipe e quer realmente conhecer pessoas em torno do que ama fazer.",
      "hm.landing.forme_2": "Você mudou para uma cidade nova e precisa de uma turma acolhedora, não de mais um grupo de chat.",
      "hm.landing.forme_3": "Você valoriza profundidade em vez de transmissão — um ótimo grupo bate cinquenta seguidores.",
      "hm.landing.forme_4": "Você acredita que interesses compartilhados + duas horas marcadas na vida real é o verdadeiro segredo.",
      "hm.landing.notme_kicker": "Isto não é para você se…",
      "hm.landing.notme_title": "HustleMeet não funciona para todos.",
      "hm.landing.notme_1": "Você não pode se comprometer a aparecer — anfitriões e membros contam com sua confirmação.",
      "hm.landing.notme_2": "Você está procurando um serviço de encontros românticos — amizades vêm primeiro aqui.",
      "hm.landing.notme_3": "Você quer se promover sem conhecer ninguém — isso só funciona pessoalmente.",
      "hm.landing.notme_4": "Você espera benefícios gratuitos infinitos — alguns eventos têm custos. O preço é sempre informado antecipadamente.",
      "hm.landing.safety_kicker": "Nosso compromisso com a segurança",
      "hm.landing.safety_title": "Acolhedor, mas nunca descuidado.",
      "hm.landing.safety_1_ttl": "Locais públicos",
      "hm.landing.safety_1_body": "Cada evento acontece em um local público com equipe — um café, restaurante, parque, academia ou estúdio. Nunca na casa de alguém, a menos que todos os participantes aceitem.",
      "hm.landing.safety_2_ttl": "Padrões da comunidade",
      "hm.landing.safety_2_body": "Tolerância zero para desrespeito, assédio ou desonestidade. Membros que violarem as regras são removidos imediatamente.",
      "hm.landing.safety_3_ttl": "Privacidade protegida",
      "hm.landing.safety_3_body": "Seus detalhes nunca são compartilhados publicamente. Você controla com quem mantém conexão após cada evento.",
      "hm.landing.safety_4_ttl": "Suporte de prontidão",
      "hm.landing.safety_4_body": "Nossa equipe está disponível antes, durante e depois de cada evento se você tiver dúvidas ou preocupações.",
      "hm.landing.bottom_title": "Seu próximo grupo favorito está a um toque de distância.",
      "hm.landing.bottom_sub": "Escolha seu caminho — junte-se a um grupo existente, traga sua turma para um Circle ou deixe o Matchmaker sentar você em uma mesa que já combina com você.",
      "hm.landing.bottom_cta_circles": "Começar um Circle",
      "hm.landing.bottom_cta_mm": "Fazer o quiz do Matchmaker",
      "hm.circles.page_title": "HustleMeet Circles · Sua turma, novo local a cada vez",
      "hm.circles.kicker": "HustleMeet · Circles",
      "hm.circles.hero_title": "Sua turma. Uma mesa nova a cada encontro.",
      "hm.circles.hero_title_accent": "Sua turma",
      "hm.circles.hero_sub": "Já tem seus amigos? Circles é a maneira mais fácil de mantê-los por perto. Nós reservamos o local, planejamos o trivia, distribuímos as cartas. Você só aparece — a mesma turma, uma nova noite, um lugar novo.",
      "hm.circles.cta_find": "Começar um Circle",
      "hm.circles.cta_host_hero": "Participar de um Circle aberto",
      "hm.circles.trust_venue": "Rodízio de locais verificados",
      "hm.circles.trust_party": "4-12 amigos por Circle",
      "hm.circles.trust_verified": "Membros verificados",
      "hm.circles.trust_friendship": "Amizades reais, mantidas por perto",
      "hm.circles.how_kicker": "Como funciona",
      "hm.circles.how_title": "Quatro passos. Sua turma continua sendo uma turma.",
      "hm.circles.how_step1_ttl": "Comece seu Circle",
      "hm.circles.how_step1_sub": "Dê um nome, convide seus amigos (até 12) e escolha um ritmo — semanal, quinzenal ou mensal.",
      "hm.circles.how_step2_ttl": "Nós escolhemos o local",
      "hm.circles.how_step2_sub": "A cada evento sugerimos um ponto novo — restaurante, noite de trivia, sala de cartas, wine bar. Ou escolha um favorito.",
      "hm.circles.how_step3_ttl": "Todos confirmam com um toque",
      "hm.circles.how_step3_sub": "Lembretes automáticos, divisão de custos, assentos pré-pagos para que faltas não estraguem o plano. A conta está resolvida antes da entrada chegar.",
      "hm.circles.how_step4_ttl": "Mantenha o ritmo",
      "hm.circles.how_step4_sub": "Uma noite na agenda que nunca mais precisará ser organizada. O grupo se fortalece ao longo dos meses, não apenas minutos.",
      "hm.circles.cadence_weekly_ttl": "Semanal",
      "hm.circles.cadence_weekly_sub": "Toda semana",
      "hm.circles.cadence_weekly_body": "Para turmas que querem um ritual de terça à noite — mesmos rostos, lugar novo a cada vez.",
      "hm.circles.cadence_fortnight_ttl": "Quinzenal",
      "hm.circles.cadence_fortnight_sub": "A cada 2 semanas",
      "hm.circles.cadence_fortnight_body": "O ponto ideal — regular sem exigir demais de agendas lotadas.",
      "hm.circles.cadence_monthly_ttl": "Mensal",
      "hm.circles.cadence_monthly_sub": "Todo mês",
      "hm.circles.cadence_monthly_body": "Circles para ocasiões especiais — assados de domingo, grandes noites de trivia, degustação, poker.",
      "hm.circles.feast_kicker": "Escolha sua noite",
      "hm.circles.feast_title": "Três formas de aproveitar",
      "hm.circles.feast_sub": "Cada Circle escolhe como a noite funciona. Alterne entre elas ou fixe a favorita da sua turma.",
      "hm.circles.feast_surprise_ttl": "Tente um restaurante novo",
      "hm.circles.feast_surprise_body": "Selecionamos três locais perto de você, todos votam, a cozinha se prepara para uma mesa cheia. Custo dividido, assentos pré-pagos.",
      "hm.circles.feast_preselect_ttl": "Trivia / cartas / jogos",
      "hm.circles.feast_preselect_body": "Reserve uma noite de trivia, uma sala de poker, bar de jogos de tabuleiro ou pista de boliche. Nós cuidamos do local, da entrada e dos lembretes.",
      "hm.circles.feast_per_event_ttl": "Noite temática única",
      "hm.circles.feast_per_event_body": "Degustação de vinhos, mesa do chef, aula de coquetéis, escape room. Algo especial de vez em quando — totalmente organizado.",
      "hm.circles.live_kicker": "Agora mesmo",
      "hm.circles.live_title": "Circles que você pode participar",
      "hm.circles.live_host_link": "Começar o seu próprio",
      "hm.circles.live_loading": "Carregando…",
      "hm.circles.live_empty_title": "Seja o primeiro Circle na sua cidade.",
      "hm.circles.live_empty_sub": "Comece um com três amigos e ajudaremos você a convidar o resto — além de oferecer os três primeiros locais por nossa conta.",
      "hm.circles.live_empty_cta": "Começar um Circle",
      "hm.circles.host_kicker": "Para líderes de Circle",
      "hm.circles.host_title": "Você traz a turma. Nós cuidamos do admin.",
      "hm.circles.host_body": "Se você é o amigo que sempre organiza a noite, Circles economiza horas do seu tempo. Lembretes automáticos, RSVPs pré-pagos, seleções de locais, divisões de custos, gestão de lista de espera e uma carteira de cashback em cada saída. Você só aparece como um membro normal.",
      "hm.circles.host_perk_1": "Seleções automáticas de locais — nunca mais perca tempo no Google",
      "hm.circles.host_perk_2": "Assentos pré-pagos — zero faltas",
      "hm.circles.host_perk_3": "Divisão de custos feita antes da sobremesa",
      "hm.circles.host_perk_4": "Lembretes + convites de calendário no piloto automático",
      "hm.circles.host_perk_5": "Lista de espera com promoção automática se um amigo cancelar",
      "hm.circles.host_perk_6": "Carteira de cashback em cada saída à noite",
      "hm.circles.host_cta": "Começar um Circle agora",
      "hm.circles.why_kicker": "Por que o Circles funciona",
      "hm.circles.why_title": "Amizades adultas precisam de infraestrutura",
      "hm.circles.why_1_ttl": "Mesma turma, confiança mais profunda",
      "hm.circles.why_1_body": "A vida afasta os amigos. Circles são o ritmo fixo que mantém todos por perto — os mesmos 4 a 12 amigos, reunindo-se na mesma noite da semana, não importa o quê.",
      "hm.circles.why_2_ttl": "Zero fadiga administrativa",
      "hm.circles.why_2_body": "Ninguém precisa ser o chefe do grupo de WhatsApp. Nós escolhemos os locais, enviamos os lembretes, processamos os pagamentos, fechamos o ciclo.",
      "hm.circles.why_3_ttl": "Novo local, nova memória",
      "hm.circles.why_3_body": "Faça um rodízio pelos melhores pequenos restaurantes, salas de trivia e bares de jogos da Australia. Rostos familiares, cenários novos — o ponto ideal entre rotina e novidade.",
      "hm.circles.bottom_title": "A noite que sua turma espera ansiosamente, sem ninguém precisar planejar.",
      "hm.circles.bottom_sub": "Um Circle. Mesmos amigos. Novo local a cada vez.",
      "hm.circles.bottom_cta_find": "Começar um Circle",
      "hm.circles.bottom_cta_mm": "Conhecer pessoas novas com o Matchmaker →",
      "hm.circles.card_host_pill": "Circle",
      "hm.matchmaker.page_title": "HustleMeet Matchmaker · A IA escolhe a mesa certa",
      "hm.matchmaker.kicker": "HustleMeet · Matchmaker",
      "hm.matchmaker.hero_title": "A IA escolhe a mesa. Você só aparece.",
      "hm.matchmaker.hero_title_accent": "Você só aparece.",
      "hm.matchmaker.hero_sub": "Responda a um curto quiz de personalidade & valores. Nosso Matchmaker senta você com quatro a sete estranhos que parecerão velhos amigos na hora da sobremesa. Sem swiping. Sem conversa fiada. Apenas uma mesa compartilhada, pronta quando você estiver.",
      "hm.matchmaker.cta_quiz": "Fazer o quiz de compatibilidade",
      "hm.matchmaker.cta_see_circles": "Ver Circles em vez disso →",
      "hm.matchmaker.trust_big5": "Personalidade Big-5 pontuada",
      "hm.matchmaker.trust_seats": "4-8 lugares por mesa",
      "hm.matchmaker.trust_lock": "Zero-swipe · zero-foto",
      "hm.matchmaker.trust_verified": "Apenas membros verificados",
      "hm.matchmaker.how_kicker": "Como funciona",
      "hm.matchmaker.how_title": "Quatro passos para uma mesa que já combina.",
      "hm.matchmaker.how_step1_ttl": "Faça o quiz",
      "hm.matchmaker.how_step1_sub": "Questionário de dez minutos — personalidade, valores, restrições alimentares, disponibilidade. Você faz apenas uma vez.",
      "hm.matchmaker.how_step2_ttl": "Combinação da noite para o dia",
      "hm.matchmaker.how_step2_sub": "Nossa IA lê seu perfil e a agenda de jantares ativos da Australia. Quando sua pontuação de afinidade é 0.70 ou superior, ela elabora um convite pessoal.",
      "hm.matchmaker.how_step3_ttl": "Aceite ou recuse",
      "hm.matchmaker.how_step3_sub": "Você recebe um e-mail de convite claro com o local, o tema da mesa e os três interesses que você compartilha com o grupo.",
      "hm.matchmaker.how_step4_ttl": "O sistema aprende com você",
      "hm.matchmaker.how_step4_sub": "Cada pesquisa pós-jantar ajusta o modelo. Seu próximo match fica mais inteligente quanto mais você janta.",
      "hm.matchmaker.science_kicker": "Por que funciona",
      "hm.matchmaker.science_title": "O Matchmaker não adivinha — ele pontua.",
      "hm.matchmaker.science_sub": "Cada combinação é uma composição ponderada de quatro fatores. Quando a média de toda a mesa está acima de 0.70, enviamos os convites. Qualquer valor abaixo fica na fila.",
      "hm.matchmaker.science_1_ttl": "Personalidade (35%)",
      "hm.matchmaker.science_1_body": "Similaridade de cosseno no vetor de personalidade Big-5 — abertura, conscienciosidade, extroversão, amabilidade e estabilidade emocional.",
      "hm.matchmaker.science_2_ttl": "Valores (25%)",
      "hm.matchmaker.science_2_body": "Sobreposição nos valores que fazem a conversa no jantar realmente fluir — família, curiosidade, crescimento, serviço, aventura.",
      "hm.matchmaker.science_3_ttl": "Interesses (25%)",
      "hm.matchmaker.science_3_body": "Sobreposição de Jaccard em seus interesses específicos — trilha Six Foot Track, clube de cinema de Sydney, cena de café de Melbourne, seu terceiro livro do ano.",
      "hm.matchmaker.science_4_ttl": "Disponibilidade (15%)",
      "hm.matchmaker.science_4_body": "A noite em que você realmente pode comparecer. Sem convites para terça às 19h se sua agenda diz academia-terça-para-sempre.",
      "hm.matchmaker.safety_kicker": "Segurança por design",
      "hm.matchmaker.safety_title": "O Matchmaker é silenciosamente cauteloso.",
      "hm.matchmaker.safety_1_ttl": "Limite de confiança",
      "hm.matchmaker.safety_1_body": "Abaixo de uma pontuação de grupo de 0.70, nenhum convite é enviado. Um match incerto é pior do que nenhum jantar.",
      "hm.matchmaker.safety_2_ttl": "Lista de bloqueio por 'não' mútuo",
      "hm.matchmaker.safety_2_body": "Se dois membros sinalizarem privadamente 'não' após um jantar, nunca os sentaremos juntos novamente.",
      "hm.matchmaker.safety_3_ttl": "Botão de interrupção automático",
      "hm.matchmaker.safety_3_body": "Se 20% ou mais das pesquisas recentes avaliarem uma mesa com 2/5 ou menos, os convites param por 24h enquanto um operador revisa.",
      "hm.matchmaker.safety_4_ttl": "Padrões da comunidade",
      "hm.matchmaker.safety_4_body": "Cada membro aceita um código de conduta claro. Removemos pessoas que o violarem — sem apelações.",
      "hm.matchmaker.safety_5_ttl": "Apenas locais públicos",
      "hm.matchmaker.safety_5_body": "Cada jantar do Matchmaker acontece em um restaurante real com equipe. Nunca na casa de alguém.",
      "hm.matchmaker.safety_6_ttl": "Feedback pós-evento",
      "hm.matchmaker.safety_6_body": "Sua pesquisa privada molda futuros matches e pode acionar uma revisão de moderação do local ou de um colega.",
      "hm.matchmaker.loop_kicker": "O ciclo de aprendizado",
      "hm.matchmaker.loop_title": "Cada jantar torna seu próximo jantar melhor.",
      "hm.matchmaker.loop_body": "O Matchmaker não é estático. Após cada evento, você envia uma breve pesquisa privada — como sentiu a conversa, com quem jantaria novamente, se o local atendeu às expectativas. Esses sinais alimentam o modelo e moldam cada combinação futura.",
      "hm.matchmaker.loop_1": "Votos de \"jantar novamente\" de colegas fortalecem seus pareamentos",
      "hm.matchmaker.loop_2": "Votos de \"não\" mútuo retiram silenciosamente os pares",
      "hm.matchmaker.loop_3": "Avaliações de local & conversa ajustam o mix de assentos",
      "hm.matchmaker.loop_4": "Pontuação de confiabilidade protege membros de faltas repetidas",
      "hm.matchmaker.loop_5": "O viés de aprendizado reforça boas combinações mês após mês",
      "hm.matchmaker.loop_6": "Você mantém o controle — opte por sair ou resete seu perfil a qualquer momento",
      "hm.matchmaker.vs_kicker": "Qual é o ideal para mim?",
      "hm.matchmaker.vs_title": "Matchmaker ou Circles?",
      "hm.matchmaker.vs_mm_ttl": "Escolha-me se eu quiser variedade.",
      "hm.matchmaker.vs_mm_1": "Uma mesa, cidade e culinária diferente a cada vez",
      "hm.matchmaker.vs_mm_2": "Grupo de 4-8 estranhos compatíveis com curadoria de IA",
      "hm.matchmaker.vs_mm_3": "Ritmo semanal, sem compromisso de anfitrião",
      "hm.matchmaker.vs_mm_4": "Ótimo se você for novo em uma cidade ou estiver viajando",
      "hm.matchmaker.vs_mm_cta": "Fazer o quiz de compatibilidade",
      "hm.matchmaker.vs_circles_ttl": "Escolha-me se eu quiser um lugar fixo.",
      "hm.matchmaker.vs_circles_1": "Mesmo anfitrião · mesmo local · mesma noite da semana",
      "hm.matchmaker.vs_circles_2": "Um grupo que se transforma na sua tradição de quarta-feira",
      "hm.matchmaker.vs_circles_3": "Curadoria humana por um anfitrião experiente",
      "hm.matchmaker.vs_circles_4": "Ótimo se você acabou de se mudar e quer criar raízes",
      "hm.matchmaker.vs_circles_cta": "Navegar pelos Circles",
      "hm.matchmaker.bottom_title": "Um quiz. Cada jantar depois disso já combina com você.",
      "hm.matchmaker.bottom_sub": "Você será combinado em menos de duas semanas. É o \"sim\" mais fácil que você dará este mês.",
      "hm.matchmaker.bottom_cta": "Fazer o quiz de compatibilidade",
      "hm.matchmaker.bottom_note": "Sem cartão de crédito · 10 minutos · Cancele a qualquer momento",
      /* AUTO-TRANSLATED hm.* END */
    },
  },
  id: {
    translation: {
      /* AUTO-TRANSLATED id START */
      plaza_nav_features: "Fitur",
      plaza_nav_rewards: "Rewards",
      plaza_nav_how: "Cara Kerja",
      plaza_nav_roadmap: "Roadmap",
      plaza_nav_faq: "FAQ",
      plaza_hero_badge: "Dimiliki Australia",
      plaza_hero_kicker: "Satu keranjang. Semua reward.",
      plaza_hero_title_1: "Belanja Aussie.",
      plaza_hero_title_2: "Dapat rewards di mana saja.",
      plaza_hero_sub: "Ribuan vendor Aussie dalam satu keranjang. Setiap dolar yang Anda belanjakan menghasilkan HustlePoints — dapat ditukar di restoran, kafe, hotel, salon, pet groomer, dan bisnis apa pun dalam jaringan HustleSuite.",
      plaza_cta_start: "Gabung gratis — hanya 30 detik",
      plaza_cta_signin: "Masuk",
      plaza_cta_shop: "Jelajahi toko",
      plaza_hero_stat_1_v: "12.847",
      plaza_hero_stat_1_l: "produk tersedia",
      plaza_hero_stat_2_v: "340+",
      plaza_hero_stat_2_l: "vendor Aussie",
      plaza_hero_stat_3_v: "$4,2 juta",
      plaza_hero_stat_3_l: "rewards dibayarkan",
      plaza_hero_stat_4_v: "19",
      plaza_hero_stat_4_l: "kategori",
      plaza_problem_kicker: "Masalah belanja saat ini",
      plaza_problem_title: "Sepuluh tab. Sepuluh password. Nol loyalitas.",
      plaza_problem_sub: "Anda sudah harus mengelola Amazon, eBay, Kogan, Kmart, Woolies, Coles, Bunnings, JB, Officeworks dan setengah lusin toko boutique lainnya. Anda menghabiskan berjam-jam membandingkan harga — lalu tidak mendapat apa-apa selain struk.",
      plaza_problem_1: "Harga yang tidak bisa dibandingkan antar situs",
      plaza_problem_2: "Penjual luar negeri yang berpura-pura lokal",
      plaza_problem_3: "Poin loyalitas terkunci di satu toko",
      plaza_problem_4: "Review palsu dari bot dan drop-shipper",
      plaza_solution_kicker: "Yang HustlePlaza lakukan berbeda",
      plaza_solution_title: "Keranjang terakhir yang Anda buka sepanjang hari.",
      plaza_solution_sub: "Satu pencarian. Satu keranjang. Satu checkout. Setiap pembelian memberi Anda HustlePoints yang dapat dibelanjakan di seluruh ekosistem kami — bukan hanya di toko ini.",
      plaza_solution_1_t: "Satu keranjang, semua vendor",
      plaza_solution_1_b: "Belanja dari setiap penjual Aussie di platform dan bayar sekali saat checkout. Pengiriman digabungkan bila memungkinkan.",
      plaza_solution_2_t: "Hanya lokal terverifikasi",
      plaza_solution_2_b: "Setiap penjual diverifikasi identitasnya sebelum dapat listing. Tanpa bot, tanpa palsu, tanpa misteri drop-shipping.",
      plaza_solution_3_t: "Rewards yang benar-benar bisa dipakai di mana saja",
      plaza_solution_3_b: "Poin yang diperoleh di sini dapat dibelanjakan di bisnis HustleSuite mana pun — kafe, hotel, salon, restoran, pet groomer.",
      plaza_solution_4_t: "Pelacakan langsung, satu layar",
      plaza_solution_4_b: "Setiap paket dari setiap vendor dilacak di satu halaman. Notifikasi melalui email dan SMS di setiap langkah.",
      plaza_rewards_kicker: "Siklus HustlePoints",
      plaza_rewards_title: "Dapat sambil belanja. Belanjakan di mana saja dalam jaringan.",
      plaza_rewards_sub: "Setiap $1 yang dibelanjakan di HustlePlaza menghasilkan HustlePoints. Tukarkan di restoran, kafe, hotel, salon, atau bisnis jasa mana pun yang menggunakan HustleSuite — di mana saja dalam jaringan.",
      plaza_rewards_step_1: "Belanja di marketplace",
      plaza_rewards_step_1_b: "Beli apa saja dari kaos hingga kulkas.",
      plaza_rewards_step_2: "Dapatkan HustlePoints langsung",
      plaza_rewards_step_2_b: "Poin muncul saat pesanan Anda dikirim — tanpa kartu, tanpa punch card, tanpa permainan masa berlaku.",
      plaza_rewards_step_3: "Belanjakan di seluruh jaringan",
      plaza_rewards_step_3_b: "Tukar di kafe, restoran, hotel, salon, pet groomer, tukang.",
      plaza_rewards_step_4: "Atau cairkan",
      plaza_rewards_step_4_b: "Konversi ke transfer bank setelah Anda mencapai $50.",
      plaza_cats_kicker: "Belanja berdasarkan kategori",
      plaza_cats_title: "19 kategori. Vendor Australia. Stok langsung.",
      plaza_cats_sub: "Jelajahi sebagian dari yang tersedia sekarang — atau daftar dan belanja katalog lengkap 12.000+ produk.",
      plaza_cats_cta: "Lihat semua kategori",
      plaza_vendors_kicker: "Penjual Aussie asli",
      plaza_vendors_title: "Temui pembuat, petani, dan toko keluarga.",
      plaza_vendors_sub: "Setiap vendor di HustlePlaza terverifikasi ABN, berbasis di Australia, dan di-onboard secara personal. Tanpa perantara luar negeri yang berpura-pura lokal.",
      plaza_how_kicker: "Cara kerja",
      plaza_how_title: "Tiga langkah. Satu keranjang. Rewards selamanya.",
      plaza_how_1_title: "Gabung dalam 30 detik",
      plaza_how_1_body: "Verifikasi email Anda dan langsung jelajahi. Tambahkan nomor telepon saat Anda siap checkout.",
      plaza_how_2_title: "Isi keranjang Anda",
      plaza_how_2_body: "Belanja dari setiap vendor. Satu keranjang, satu tampilan pengiriman, satu checkout. Pembayaran melalui Stripe atau PayPal.",
      plaza_how_3_title: "Dapat & belanjakan di mana saja",
      plaza_how_3_body: "HustlePoints muncul saat pesanan Anda dikirim. Belanjakan di bisnis HustleSuite mana pun, atau cairkan di $50.",
      plaza_compare_kicker: "Bagaimana kami dibandingkan",
      plaza_compare_title: "HustlePlaza vs para raksasa",
      plaza_compare_col_us: "HustlePlaza",
      plaza_compare_col_amz: "Amazon AU",
      plaza_compare_col_ebay: "eBay AU",
      plaza_compare_col_kmart: "Kmart / Big W",
      plaza_compare_row_1: "Dimiliki Australia",
      plaza_compare_row_2: "Satu keranjang untuk semua penjual",
      plaza_compare_row_3: "Rewards dapat dibelanjakan DI LUAR toko ini",
      plaza_compare_row_4: "Rewards dapat dicairkan ke bank Anda",
      plaza_compare_row_5: "Dukungan pelanggan di-host di Aussie",
      plaza_trust_kicker: "Mengapa kami verifikasi",
      plaza_trust_title: "Pelanggan asli. Review asli.",
      plaza_trust_body: "Kami memverifikasi email Anda saat pendaftaran, nomor ponsel Anda sebelum checkout, dan memerlukan pemeriksaan foto langsung sebelum Anda dapat memposting review, chat dengan vendor, atau melakukan pembelian bernilai tinggi. Ini memperlambat penipu dan menjaga harga tetap rendah untuk Anda.",
      plaza_trust_1: "Verifikasi email untuk bergabung",
      plaza_trust_2: "Verifikasi SMS untuk checkout",
      plaza_trust_3: "Verifikasi foto langsung untuk review dan chat",
      plaza_trust_4: "Pemeriksaan identitas vendor saat onboarding",
      plaza_test_kicker: "Apa kata anggota awal kami",
      plaza_test_title: "Langsung dari pelanggan asli.",
      plaza_test_1_body: "Saya menggunakan poin HustlePlaza saya untuk membayar makan malam di kafe dekat kantor. Sungguh tidak percaya akan berhasil sampai pelayannya tersenyum dan berkata 'Anda sudah selesai'.",
      plaza_test_1_name: "Priya S.",
      plaza_test_2_body: "Enam vendor, satu waktu pengiriman, satu email konfirmasi. Biasanya saya punya enam password dan enam nomor pelacakan. Ini benar-benar berhasil.",
      plaza_test_2_name: "Marcus T.",
      plaza_test_3_body: "Sebagai produsen madu kecil, kami akhirnya dibayar di hari yang sama, mendapat review asli, dan tanpa potongan 40%. HustlePlaza membawa saya dari 'sampingan' menjadi full-time dalam delapan bulan.",
      plaza_test_3_name: "Kate & Dave",
      plaza_faq_kicker: "Jawaban",
      plaza_faq_title: "Pertanyaan yang kami terima setiap minggu",
      plaza_faq_1_q: "Apakah HustlePlaza benar-benar dimiliki Australia?",
      plaza_faq_1_a: "Ya. HustlePlaza 100% dimiliki Australia, di-host di server Australia, dijalankan oleh tim Australia. Setiap vendor melalui verifikasi identitas sebelum listing pertama mereka dapat ditayangkan.",
      plaza_faq_2_q: "Bagaimana cara kerja rewards?",
      plaza_faq_2_a: "Setiap dolar yang Anda belanjakan di HustlePlaza memberi Anda HustlePoints. Poin muncul saat pesanan Anda dikirim dan tidak pernah kedaluwarsa. Belanjakan di bisnis mana pun yang menggunakan HustleSuite, atau cairkan ke bank Anda setelah Anda mengumpulkan cukup.",
      plaza_faq_3_q: "Mengapa Anda memverifikasi foto?",
      plaza_faq_3_a: "Agar review yang Anda baca asli. Verifikasi foto adalah pemeriksaan selfie langsung yang dilakukan melalui kamera ponsel Anda — teknik yang sama dengan yang digunakan bank — dan ini menghentikan bot, akun palsu, dan aktor jahat dari memanipulasi platform.",
      plaza_faq_4_q: "Bagaimana jika pesanan saya tidak tiba?",
      plaza_faq_4_a: "Setiap pesanan didukung oleh HustleGuarantee kami: jika paket tidak tiba atau tidak sesuai deskripsi, kami mengembalikan uang Anda dalam 3 hari kerja, lalu mengejar vendor. Anda tidak akan pernah rugi.",
      plaza_faq_5_q: "Apakah informasi kartu saya aman?",
      plaza_faq_5_a: "Kami tidak pernah melihat atau menyimpan nomor kartu Anda. Semua pembayaran melalui Stripe (PCI-DSS Level 1) atau PayPal, keduanya dengan autentikasi 3D Secure yang melindungi Anda dari tagihan palsu pada kartu curian.",
      plaza_faq_6_q: "Apakah Anda kirim internasional?",
      plaza_faq_6_a: "Belum. HustlePlaza khusus Australia saat peluncuran — itulah cara kami bisa menjanjikan pengiriman seminggu untuk sebagian besar item dan dukungan di zona waktu Anda. NZ ada di roadmap 2026.",
      plaza_faq_7_q: "Berapa biaya untuk bergabung?",
      plaza_faq_7_a: "Tidak ada. Keanggotaan gratis selamanya. Vendor membayar komisi kecil per penjualan — pembeli membayar $0 untuk bergabung, $0 untuk mendapatkan, $0 untuk menukar.",
      plaza_faq_8_q: "Bisakah saya berjualan di HustlePlaza?",
      plaza_faq_8_a: "Tentu saja. Kunjungi hustlesuite.au untuk onboard sebagai vendor — persetujuan hari-sama untuk bisnis Aussie terverifikasi, plus 30 hari pertama Anda bebas komisi.",
      plaza_final_kicker: "Harga founder — kursi terbatas",
      plaza_final_title: "Jadilah salah satu yang pertama — kunci harga founder.",
      plaza_final_sub: "Daftar hari ini sebagai Anggota HustleSuite. Anda akan menjadi yang pertama tahu saat setiap fitur baru diluncurkan, dan akun Anda akan mempertahankan rewards dan harga tingkat founder seumur hidup.",
      plaza_final_bonus: "Founder tidak pernah kehilangan tarif founder mereka. Gratis selamanya, batalkan kapan saja.",
      plaza_final_cta: "Daftar sebagai Anggota Founder",
      plaza_final_disclosure: "Tidak perlu kartu. Hanya verifikasi email + ponsel.",
      plaza_roadmap_kicker: "Yang kami bangun selanjutnya",
      plaza_roadmap_title: "Enam alat yang dibuat untuk meringankan pekerjaan Anda.",
      plaza_roadmap_sub: "HustleSuite lebih dari sekadar toko — ini adalah lapisan produktivitas penuh. Inilah yang ada di jalur pengembangan.",
      plaza_roadmap_soon: "Segera hadir",
      plaza_roadmap_next: "Selanjutnya dalam antrian",
      plaza_roadmap_1_t: "HustleDictate — speech-to-text di semua form",
      plaza_roadmap_1_b: "Ekstensi Chrome. Tekan Alt+M, bicara, dan input apa pun di website mana pun terisi sendiri. Aplikasi real estat, invoice, dokumen NDIS — selesai dalam setengah waktu.",
      plaza_roadmap_2_t: "HustleConnect Edit-Anywhere",
      plaza_roadmap_2_b: "Untuk pemilik WordPress: edit situs live Anda dari halaman mana pun dengan satu hotkey. Tidak perlu bolak-balik wp-admin lagi.",
      plaza_roadmap_3_t: "Tombol universal Save-to-HustleSuite",
      plaza_roadmap_3_b: "Klik kanan di halaman web mana pun — listing properti, artikel, produk, restoran — dan masuk ke modul yang tepat siap untuk Anda tindaklanjuti.",
      plaza_roadmap_4_t: "Price-checker HustlePlaza",
      plaza_roadmap_4_b: "Jelajahi Amazon, eBay atau Kmart — chip melayang menunjukkan produk yang sama di HustlePlaza dengan preview rewards Anda.",
      plaza_roadmap_5_t: "Review harvester",
      plaza_roadmap_5_b: "Sinkronkan otomatis review baru dari Google, Facebook dan TripAdvisor ke dashboard HustleSuite Anda. Menghemat ~20 menit per minggu.",
      plaza_roadmap_6_t: "HustleSuite Sidebar",
      plaza_roadmap_6_b: "Panel samping Chrome dengan pesanan langsung, KPI, quick-post dan quick-invoice. Seluruh bisnis Anda dalam strip di samping setiap tab.",
      plaza_footer_owned: "Dimiliki & di-host di Australia",
      plaza_footer_terms: "Syarat",
      plaza_footer_privacy: "Privasi",
      plaza_footer_contact: "Kontak",
      plaza_footer_vendors: "Jual di HustlePlaza",
      plaza_footer_help: "Pusat bantuan",
      plaza_footer_tagline: "Bagian dari keluarga HustleSuite — satu login, semua layanan.",
      verify_gate_title: "Hampir selesai — pendaftaran cepat",
      verify_gate_body: "HustlePlaza adalah keranjang belanja yang menawarkan banyak manfaat termasuk rewards yang dapat ditukar yang dapat digunakan, tidak hanya dalam platform ini tetapi dengan Bisnis lain mana pun yang menggunakan platform HustlePlaza atau Bisnis lain yang Terdaftar dengan HustleSuite. Beberapa Bisnis ini akan mencakup Restoran, Kafe, Hotel, Salon, dan bahkan Pet Grooming. Untuk terus menawarkan harga dan layanan yang bagus, kami perlu memastikan Anda asli dan memverifikasi Anda. Tekan tombol Gabung untuk melanjutkan.",
      verify_gate_join: "Gabung HustleSuite",
      verify_gate_signin: "Saya sudah punya akun",
      verify_gate_cancel: "Nanti saja",
      verify_email_title: "Verifikasi email Anda",
      verify_email_body: "Kami akan mengirim kode 6 digit ke kotak masuk Anda.",
      verify_email_placeholder: "anda@contoh.com",
      verify_email_send: "Kirim kode",
      verify_email_code_ph: "Kode 6 digit",
      verify_email_check: "Verifikasi",
      verify_email_sent: "Kode terkirim — cek kotak masuk Anda.",
      verify_sms_title: "Verifikasi ponsel Anda",
      verify_sms_body: "Tambahkan nomor ponsel agar kami dapat mengirim SMS pembaruan pesanan dan mengonfirmasi pembelian besar.",
      verify_sms_placeholder: "+62812345678",
      verify_sms_send: "Kirim SMS",
      verify_sms_check: "Verifikasi",
      verify_sms_sent: "Kode terkirim — cek ponsel Anda.",
      verify_photo_title: "Verifikasi foto",
      verify_photo_body: "Pindai kode QR dengan ponsel Anda untuk mengambil selfie langsung. Diperlukan untuk chat, pembelian bernilai tinggi, dan posting di Meetup / Tackle.",
      verify_photo_waiting: "Menunggu ponsel Anda…",
      verify_photo_verified: "Foto terverifikasi — Anda siap.",
      travel_cta_start: "Gabung gratis",
      travel_hero_kicker: "HustleTravel",
      travel_hero_title_1: "Satu keanggotaan.",
      travel_hero_title_2: "Ke seluruh Australia.",
      travel_hero_sub: "Hotel, penerbangan, pengalaman, perencanaan road trip dan sewa mobil — dipesan dengan tarif terbaik yang tersedia dan memberi Anda HustlePoints untuk setiap dolar yang dibelanjakan.",
      travel_cta_plan: "Rencanakan road trip",
      travel_cta_hotels: "Cari hotel",
      travel_svc_hotels_t: "Hotel & apartemen",
      travel_svc_hotels_b: "Bandingkan 200+ situs pemesanan dalam satu tempat — metasearch bertenaga Trivago.",
      travel_svc_flights_t: "Penerbangan",
      travel_svc_flights_b: "Domestik dan internasional, dengan peta kursi real-time dan aturan tarif.",
      travel_svc_experiences_t: "Pengalaman & tur",
      travel_svc_experiences_b: "Hari di karang, kunjungan cellar-door, musik live, wisata ramah anak.",
      travel_svc_cars_t: "Sewa mobil",
      travel_svc_cars_b: "Setiap merek besar plus independen lokal — pickup di bandara Australia mana pun.",
      travel_svc_insurance_t: "Asuransi perjalanan",
      travel_svc_insurance_b: "Dijamin oleh perusahaan asuransi Australia yang diatur APRA. Bandingkan dalam satu layar.",
      travel_svc_roadtrip_t: "Perencana road trip",
      travel_svc_roadtrip_b: "Itinerary sadar jarak EV dengan charger, pemberhentian semalam, dan estimasi biaya.",
      travel_final_kicker: "Harga founder",
      travel_final_title: "Kunci rewards tingkat Founder seumur hidup.",
      travel_final_sub: "Daftar sekali dengan HustleSuite. Pesan semuanya — hotel, penerbangan, pengalaman, asuransi — di HustleTravel dan dapatkan HustlePoints yang dapat dibelanjakan di setiap bisnis dalam jaringan.",
      travel_final_cta: "Daftar sebagai Anggota Founder",
      travel_final_disclosure: "Kebijakan tarif terbaik: hasil diurutkan berdasarkan nilai terbaik bagi traveler, bukan komisi.",
      dictate_hero_kicker: "HustleDictate",
      dictate_hero_title_1: "Bicara.",
      dictate_hero_title_2: "Semua form. Semua website.",
      dictate_hero_sub: "Satu ekstensi Chrome yang mengubah semua kolom teks menjadi mikrofon dikte suara tingkat profesional — bertenaga Whisper.",
      dictate_cta_install: "Tambahkan ke Chrome",
      dictate_uc_re_t: "Catatan sewa real estat",
      dictate_uc_re_b: "Dikte laporan inspeksi dan pesan penyewa langsung ke sistem PM Anda.",
      dictate_uc_ndis_t: "Catatan kemajuan NDIS",
      dictate_uc_ndis_b: "Ucapkan catatan shift saat ingatan masih segar — hands-free, tanpa mengetik.",
      dictate_uc_invoice_t: "Deskripsi invoice",
      dictate_uc_invoice_b: "Deskripsi item baris didiktekan langsung ke Xero, MYOB atau QuickBooks.",
      dictate_uc_email_t: "Balasan email panjang",
      dictate_uc_email_b: "Ucapkan balasan di Gmail, Outlook atau webmail mana pun — tiga kali lebih cepat dari mengetik.",
      dictate_final_kicker: "Gratis untuk Anggota Founder",
      dictate_final_title: "Setiap keyboard sekarang adalah mikrofon.",
      dictate_final_sub: "Gratis untuk Anggota Founder HustleSuite. $5/bulan jika tidak — batalkan kapan saja.",
      dictate_final_cta: "Tambahkan ke Chrome — gratis",
      "finder.cat.roadtrip": "Road trip",
      "finder.hotels.title": "Temukan hotel terdekat",
      "finder.hotels.sub": "Membandingkan 200+ situs pemesanan dalam satu pencarian — bertenaga Trivago.",
      "finder.hotels.where": "Di mana",
      "finder.hotels.where_ph": "Kota atau pinggiran kota (mis. Byron Bay, NSW)",
      "finder.hotels.checkin": "Check-in",
      "finder.hotels.checkout": "Check-out",
      "finder.hotels.adults": "Dewasa",
      "finder.hotels.search": "Cari hotel",
      "finder.hotels.disclosure": "Membuka Trivago — kami mungkin mendapat komisi dari pemesanan.",
      /* AUTO-TRANSLATED id END */
      welcome_back: "Selamat datang kembali",
      sign_out: "Keluar",
    
      /* AUTO-TRANSLATED finder.* (added) */
      "finder.title": "HustleFinder",
      "finder.tagline": "Temukan tempat umum terdekat — data nyata, seluruh Australia.",
      "finder.search_placeholder": "Cari tempat, jalan, atau pinggiran kota…",
      "finder.hint": "Mulai mengetik di atas, ketuk kategori, atau gunakan \"Dekat saya\".",
      "finder.near_me": "Dekat saya",
      "finder.searching": "Mencari…",
      "finder.suburb.placeholder": "Filter berdasarkan pinggiran kota (mis. Darwin, NT)…",
      "finder.suburb.clear": "Hapus",
      "finder.search_in": "Cari di {{cat}}",
      "finder.empty": "Tidak ada hasil — coba kategori atau kata kunci lain.",
      "finder.meta.title": "HustleFinder — direktori pinggiran kota Australia gratis",
      "finder.meta.desc": "Cari bisnis lokal, pengisian daya, kafe, toilet, dan lainnya di setiap pinggiran kota Australia — 100% gratis.",
      "finder.footer.credits": "Kredit data & atribusi",
      "finder.footer.terms": "Ketentuan",
      "finder.cat.toilet": "Toilet",
      "finder.cat.fountain": "Air Minum",
      "finder.cat.transport": "Transportasi",
      "finder.cat.park": "Taman",
      "finder.cat.medical": "Medis",
      "finder.cat.school": "Sekolah",
      "finder.cat.library": "Perpustakaan",
      "finder.cat.petrol": "Bensin",
      "finder.cat.kidzone": "Zona Anak",
      "finder.cat.puzzles": "Teka-teki",
      /* AUTO-TRANSLATED rt.* START */
      "rt.brand_name": "Roadtrip Planner",
      "rt.page_title": "Perencana Road-trip EV Aussie",
      "rt.from": "Dari",
      "rt.to": "Ke",
      "rt.from_placeholder": "Pinggiran kota, kota (mis. Newcastle NSW)",
      "rt.to_placeholder": "Pinggiran kota, kota (mis. Hobart TAS)",
      "rt.stop": "STOP",
      "rt.stop_placeholder": "Pemberhentian antara",
      "rt.add_stop": "Tambah pemberhentian",
      "rt.upgrade_pro": "Upgrade ke Pro untuk hingga 20 pemberhentian",
      "rt.reverse": "Balikkan (hitung jalan pulang)",
      "rt.from_current": "Dari lokasi saat ini",
      "rt.range_per_charge": "Jarak per pengisian (km)",
      "rt.connector": "Konektor",
      "rt.connector_any": "Semua",
      "rt.drive_hours_day": "Jam berkendara/hari",
      "rt.accommodation": "Akomodasi",
      "rt.accom_any": "Apa saja (BnB · Hotel · Karavan)",
      "rt.accom_bnb": "BnB",
      "rt.accom_hotel": "Hotel",
      "rt.accom_caravan": "Taman Karavan",
      "rt.min_rating": "Rating minimum",
      "rt.any_rating": "Rating apa saja",
      "rt.only_5star": "Hanya 5★",
      "rt.schedule": "Jadwal:",
      "rt.depart_at": "Berangkat jam",
      "rt.arrive_by": "Tiba sebelum",
      "rt.hotel_checkin": "Check-in hotel",
      "rt.hotel_checkout": "Check-out hotel",
      "rt.overnight_hint": "Tahap menginap tidak akan dijadwalkan di luar jam ini.",
      "rt.whos_in_car": "Siapa saja di mobil?",
      "rt.adults": "Dewasa",
      "rt.kids": "Anak-anak",
      "rt.ages": "Usia",
      "rt.show_attractions": "Tampilkan objek wisata",
      "rt.show_kids": "Tampilkan perhentian ramah anak",
      "rt.overnight_stays": "Menginap semalam",
      "rt.fast_chargers_only": "Hanya pengisi daya cepat",
      "rt.hotel_must_charger": "Hotel harus punya pengisi daya",
      "rt.pet_only": "Hanya penginapan ramah hewan",
      "rt.safety": "Keamanan",
      "rt.safety_90": "90% (datar)",
      "rt.safety_85": "85% (default)",
      "rt.safety_80": "80% (bukit / menderek)",
      "rt.safety_70": "70% (beban berat)",
      "rt.save_trip": "Simpan perjalanan",
      "rt.plan_my_trip": "Rencanakan perjalanan",
      "rt.refresh_trip": "Perbarui perjalanan",
      "rt.planning": "Merencanakan…",
      "rt.resume_title": "Lanjutkan perjalanan dari sini",
      "rt.resume_body": "Kami mendeteksi lokasi Anda saat ini. Masukkan sisa jarak pada dasbor agar kami dapat merencanakan tahap berikutnya sesuai sisa daya Anda — bukan pengisian penuh.",
      "rt.remaining_range": "Sisa jarak (km)",
      "rt.cancel": "Batal",
      "rt.replan_from_here": "Rencanakan ulang dari sini",
      "rt.save_modal_title": "Beri nama perjalanan ini",
      "rt.save_modal_body": "Perjalanan tersimpan ada di profil Anda — buka kapan saja dan tekan Rencanakan Ulang.",
      "rt.save_name_placeholder": "mis. Brisbane → Sydney via Byron",
      "rt.your_saved_trips": "Perjalanan tersimpan Anda",
      "rt.custom_location": "Lokasi khusus",
      "rt.trip_summary": "Ringkasan perjalanan",
      "rt.itinerary": "Itinerary",
      "rt.no_stops_needed": "Tidak butuh perhentian — seluruh perjalanan sesuai dengan jarak tempuh Anda.",
      "rt.chargers_in_corridor": "pengisi daya di jalur ini",
      "rt.planned_stops": "pemberhentian direncanakan",
      "rt.overnight_stop": "perhentian menginap",
      "rt.overnight_stops": "perhentian menginap",
      "rt.suggested_detours": "Saran pengalihan — centang untuk menambah",
      "rt.replan_with_detours": "Rencanakan ulang dengan pengalihan",
      "rt.attractions_label": "Wisata",
      "rt.kids_label": "Anak-anak",
      "rt.km_from_start": "km dari awal",
      "rt.open_driving": "Buka mode berkendara",
      "rt.big_font_hint": "Font besar, ramah dudukan ponsel. Berfungsi dengan mirror browser CarPlay/Android Auto.",
      "rt.check_price_book": "Cek harga & pesan di Trivago ↗",
      "rt.visit_site": "Kunjungi situs ↗",
      "rt.charger_onsite": "Pengisi daya di lokasi",
      "rt.leg_exceeds_range": "Tahap ini melebihi jarak tempuh Anda — daya akan habis sebelum mencapai pengisi daya ini. Kurangi asumsi jarak atau tambahkan pengisian antara.",
      "rt.add_waypoint_replan": "Tambah sebagai titik jalan & rencanakan ulang",
      /* AUTO-TRANSLATED rt.* END */
                      /* AUTO-TRANSLATED hm.* START */
      "hm.nav.hustlemeet": "HustleMeet",
      "hm.nav.circles": "Circles",
      "hm.nav.matchmaker": "Matchmaker",
      "hm.nav.meet": "Meet",
      "hm.nav.overview": "HustleMeetups",
      "hm.overview.page_title": "HustleMeetups — tiga cara hangat untuk menemukan orang-orangmu.",
      "hm.overview.kicker": "Bagian dari HustleSuite",
      "hm.overview.hero_title": "Tiga cara hangat untuk menemukan orang-orangmu.",
      "hm.overview.hero_title_accent": "orang-orangmu",
      "hm.overview.hero_sub": "HustleMeetups adalah cara paling ramah di Australia untuk membangun pertemanan nyata — pilih vibe yang sesuai untukmu. Apakah kamu menginginkan grup lari mingguan, makan malam bulanan dengan teman lama, atau meja yang dicocokkan oleh AI dengan wajah-wajah baru, selalu ada jalan untukmu.",
      "hm.overview.tile_meet_kicker": "HustleMeet",
      "hm.overview.tile_meet_title": "Temukan orang-orangmu. Lakukan hobimu.",
      "hm.overview.tile_meet_body": "Rumah bagi grup berbasis minat di Australia. Pelari, coder, investor, pejalan kaki, penari, klub sarapan, kru buku — ratusan grup, acara nyata setiap minggu. Datang sekali, atau setiap minggu.",
      "hm.overview.tile_meet_cta": "Jelajahi grup",
      "hm.overview.tile_circles_kicker": "Circles",
      "hm.overview.tile_circles_title": "Krumu. Meja baru setiap saat.",
      "hm.overview.tile_circles_body": "Bawa grup teman tetapmu. Kami yang memesan restoran baru, merencanakan trivia, menyiapkan kartu. Kamu tinggal datang. Cara paling erat untuk menjaga hubungan pertemanan yang sudah ada — tanpa perlu ada yang repot mengatur.",
      "hm.overview.tile_circles_cta": "Mulai Circle",
      "hm.overview.tile_matchmaker_kicker": "Matchmaker",
      "hm.overview.tile_matchmaker_title": "AI yang memilih meja. Kamu tinggal datang.",
      "hm.overview.tile_matchmaker_body": "Ikuti kuis 10 menit. Dalam semalam, kami akan mendudukkanmu dengan orang-orang asing yang akan terasa seperti teman lama saat hidangan penutup tiba. Meja yang berbeda setiap kalinya.",
      "hm.overview.tile_matchmaker_cta": "Ikuti kuis",
      "hm.overview.trust_kicker": "Mengapa ini berhasil",
      "hm.overview.trust_title": "Kepercayaan dulu. Meja kemudian.",
      "hm.overview.trust_1_title": "Anggota terverifikasi",
      "hm.overview.trust_1_body": "Setiap grup dimulai dengan pemeriksaan ID asli + skor keandalan. Tidak ada akun sekali pakai.",
      "hm.overview.trust_2_title": "Host asli",
      "hm.overview.trust_2_body": "Setiap grup dipimpin oleh host yang telah diperiksa yang menjaga vibe, mengatur ritme, dan memperhatikan suasana ruangan.",
      "hm.overview.trust_3_title": "Pertemanan utama",
      "hm.overview.trust_3_body": "Semua di sini dirancang untuk pertemanan nyata, bukan kencan. Selamanya.",
      "hm.overview.trust_4_title": "Moderasi tanpa toleransi",
      "hm.overview.trust_4_body": "Satu laporan, satu ulasan, satu keputusan. Pelaku buruk dihapus pada malam yang sama.",
      "hm.overview.footer_line": "Bagian dari HustleSuite — dimiliki & di-hosting di Australia.",
      "hm.landing.page_title": "HustleMeet · Temukan orang-orangmu. Lakukan hobimu.",
      "hm.landing.kicker": "Jaringan minat Australia",
      "hm.landing.hero_title": "Temukan orang-orangmu. Lakukan hobimu.",
      "hm.landing.hero_title_accent": "orang-orangmu",
      "hm.landing.hero_sub": "HustleMeet adalah rumah bagi grup berbasis minat di Australia. Software dev yang debugging saat makan siang. Pelari jarak jauh pada jam 6 pagi. Investor yang bertemu setiap bulan. Grup jalan kaki, kru tari, klub sarapan, malam board-game. Pilih grup, pesan acara berikutnya, dan datanglah.",
      "hm.landing.cta_explore_circles": "Telusuri grup",
      "hm.landing.cta_try_matchmaker": "Mulai grupmu sendiri",
      "hm.landing.trust_verified": "Anggota terverifikasi",
      "hm.landing.trust_venues": "Host acara yang terverifikasi",
      "hm.landing.trust_friendship": "Pertemanan utama — bukan kencan",
      "hm.landing.trust_moderation": "Moderasi tanpa toleransi",
      "hm.landing.subbrand_circles_kicker": "Circles",
      "hm.landing.subbrand_circles_title": "Sudah punya kru sendiri? Beri mereka rumah permanen.",
      "hm.landing.subbrand_circles_body": "Circles adalah untuk grup teman yang sudah sering bertemu — klub buku, malam poker, kru lari, teman kantor. Kami menangani tempat, pengingat, dan jadwal. Kamu tinggal datang.",
      "hm.landing.subbrand_circles_b1": "Tempat berganti, satu jadwal berjalan",
      "hm.landing.subbrand_circles_b2": "Pengingat otomatis + RSVP mudah",
      "hm.landing.subbrand_circles_b3": "Trivia, kartu & malam bertema tersedia",
      "hm.landing.subbrand_circles_cta": "Mulai Circle",
      "hm.landing.subbrand_matchmaker_kicker": "Matchmaker",
      "hm.landing.subbrand_matchmaker_title": "AI yang memilih meja. Kamu tinggal datang.",
      "hm.landing.subbrand_matchmaker_body": "Satu kuis 10 menit — kepribadian, nilai-nilai, ketersediaan waktu. Matchmaker kami akan mendudukkanmu dengan orang asing yang akan terasa seperti teman lama saat hidangan penutup tiba. Meja yang berbeda setiap kalinya.",
      "hm.landing.subbrand_matchmaker_b1": "Skor kepribadian & nilai-nilai Big-5",
      "hm.landing.subbrand_matchmaker_b2": "Undangan dalam semalam saat kecocokanmu 0.70 atau lebih tinggi",
      "hm.landing.subbrand_matchmaker_b3": "Belajar dari setiap makan malam yang kamu lakukan",
      "hm.landing.subbrand_matchmaker_cta": "Ikuti kuis",
      "hm.landing.how_kicker": "Cara kerja HustleMeet",
      "hm.landing.how_title": "Empat langkah. Temukan grupmu. Datanglah.",
      "hm.landing.how_step1_ttl": "Pilih minatmu",
      "hm.landing.how_step1_sub": "Lari, coding, investasi, memasak, musik, jalan santai — beri tahu kami apa yang kamu sukai dan di mana kamu tinggal.",
      "hm.landing.how_step2_ttl": "Telusuri grup di dekatmu",
      "hm.landing.how_step2_sub": "Setiap grup menampilkan host-nya, ritmenya, dan acara berikutnya. Kenali suasananya sebelum kamu bergabung.",
      "hm.landing.how_step3_ttl": "RSVP ke acara berikutnya",
      "hm.landing.how_step3_sub": "Satu ketukan, satu undangan kalender, satu pengingat jelas sehari sebelumnya. Beberapa acara gratis, beberapa berbayar — harga tertera dengan jelas.",
      "hm.landing.how_step4_ttl": "Datang & terhubung",
      "hm.landing.how_step4_sub": "Temui host, temui kru, lakukan hobimu. Kembali lagi minggu depan jika kamu merasa cocok.",
      "hm.landing.stats_kicker": "Cara baru bertemu di Australia",
      "hm.landing.stats_title": "Alasan mengapa orang terus datang kembali.",
      "hm.landing.stats_92_sub": "Merasa nyambung dengan setidaknya satu orang di acara tersebut",
      "hm.landing.stats_68_sub": "Melakukan RSVP ke acara berikutnya dalam waktu seminggu",
      "hm.landing.stats_85_sub": "Tetap berhubungan dengan seseorang dari grup",
      "hm.landing.why_kicker": "Mengapa kami berhasil",
      "hm.landing.why_title": "Minat nyata. Host asli. Acara nyata.",
      "hm.landing.why_1_ttl": "Utamakan minat, bukan lokasi",
      "hm.landing.why_1_body": "Orang-orang di grupmu benar-benar peduli pada hal yang kamu sukai — itulah jalan pintas menuju pertemanan nyata.",
      "hm.landing.why_2_ttl": "Host acara asli",
      "hm.landing.why_2_body": "Setiap grup memiliki host terverifikasi yang menjaga vibe, mengatur ritme, dan turun tangan jika ada yang tidak beres. Bukan admin Facebook. Tapi orang asli.",
      "hm.landing.why_3_ttl": "Konsistensi itu penting",
      "hm.landing.why_3_body": "Acara baru setiap minggu. Sesuatu yang dinantikan. Ritme yang mengubah orang asing menjadi krumu di Selasa malam.",
      "hm.landing.why_4_ttl": "Aman & terlindungi",
      "hm.landing.why_4_body": "Anggota terverifikasi, tempat umum, tanpa toleransi untuk rasa tidak hormat. Tim dukungan kami siap dipanggil sebelum, selama, dan sesudah setiap acara.",
      "hm.landing.why_5_ttl": "Pertemanan utama",
      "hm.landing.why_5_body": "Ini bukan kencan. Ini adalah komunitas. Minat nyata, orang nyata, tanpa swipe di tengah-tengahnya.",
      "hm.landing.why_6_ttl": "Cukup kecil untuk berarti",
      "hm.landing.why_6_body": "Sebagian besar grup membatasi acara pada 4-15 kursi. Cukup besar untuk memicu percakapan nyata. Cukup kecil agar tidak ada yang terabaikan.",
      "hm.landing.cities_kicker": "Di mana kami beroperasi",
      "hm.landing.cities_title": "Grup bertemu di seluruh Australia & NZ.",
      "hm.landing.cities_sub_zero": "Grup pertama dibuka di 15+ kota — bergabunglah lebih awal dan bentuk suasananya.",
      "hm.landing.cities_sub_some": "Grup aktif di 15+ wilayah metro. Sesuatu di kotamu, minggu ini.",
      "hm.landing.cities_sub_one": "{{count}} grup aktif · acara baru setiap minggu · selalu menambah kota.",
      "hm.landing.cities_sub_many": "{{count}} grup aktif · acara baru setiap minggu · selalu menambah kota.",
      "hm.landing.forme_kicker": "Ini untukmu jika…",
      "hm.landing.forme_title": "Kamu menginginkan minat nyata. Orang nyata. Acara nyata.",
      "hm.landing.forme_1": "Kamu lelah dengan aplikasi swipe dan ingin benar-benar bertemu orang-orang di sekitar hal yang kamu sukai.",
      "hm.landing.forme_2": "Kamu baru pindah ke kota baru dan butuh kru yang ramah, bukan grup chat lainnya.",
      "hm.landing.forme_3": "Kamu menghargai kedalaman daripada sekadar siaran — satu grup hebat lebih baik daripada lima puluh pengikut.",
      "hm.landing.forme_4": "Kamu percaya minat yang sama + waktu dua jam yang ditetapkan di dunia nyata adalah rahasia pertemanan yang sesungguhnya.",
      "hm.landing.notme_kicker": "Ini bukan untukmu jika…",
      "hm.landing.notme_title": "HustleMeet tidak cocok untuk semua orang.",
      "hm.landing.notme_1": "Kamu tidak bisa berkomitmen untuk datang — host dan anggota mengandalkan RSVP-mu.",
      "hm.landing.notme_2": "Kamu mencari layanan kencan — pertemanan adalah prioritas utama di sini.",
      "hm.landing.notme_3": "Kamu ingin menyiarkan sesuatu tanpa bertemu siapa pun — ini hanya berhasil secara tatap muka.",
      "hm.landing.notme_4": "Kamu mengharapkan fasilitas gratis tanpa henti — beberapa acara membutuhkan biaya. Harga selalu transparan di awal.",
      "hm.landing.safety_kicker": "Komitmen keamanan kami",
      "hm.landing.safety_title": "Hangat, namun tidak pernah ceroboh.",
      "hm.landing.safety_1_ttl": "Tempat umum",
      "hm.landing.safety_1_body": "Setiap acara berlangsung di tempat umum yang memiliki staf — kafe, restoran, taman, gym, atau studio. Tidak pernah di rumah seseorang kecuali setiap peserta setuju.",
      "hm.landing.safety_2_ttl": "Standar komunitas",
      "hm.landing.safety_2_body": "Tanpa toleransi untuk ketidakhormatan, pelecehan, atau ketidakjujuran. Anggota yang melanggar akan segera dihapus.",
      "hm.landing.safety_3_ttl": "Privasi terlindungi",
      "hm.landing.safety_3_body": "Detail pribadimu tidak pernah dibagikan secara publik. Kamu mengontrol dengan siapa kamu tetap terhubung setelah setiap acara.",
      "hm.landing.safety_4_ttl": "Dukungan siap panggil",
      "hm.landing.safety_4_body": "Tim kami tersedia sebelum, selama, dan sesudah setiap acara jika kamu memiliki pertanyaan atau kekhawatiran.",
      "hm.landing.bottom_title": "Grup favoritmu berikutnya hanya berjarak satu ketukan.",
      "hm.landing.bottom_sub": "Pilih jalanmu — bergabung dengan grup yang ada, bawa krumu ke dalam Circle, atau biarkan Matchmaker mendudukkanmu di meja yang sudah cocok.",
      "hm.landing.bottom_cta_circles": "Mulai Circle",
      "hm.landing.bottom_cta_mm": "Ikuti kuis Matchmaker",
      "hm.circles.page_title": "HustleMeet Circles · Krumu, tempat baru setiap saat",
      "hm.circles.kicker": "HustleMeet · Circles",
      "hm.circles.hero_title": "Krumu. Meja baru setiap saat.",
      "hm.circles.hero_title_accent": "Krumu",
      "hm.circles.hero_sub": "Sudah punya teman akrab? Circles adalah cara termudah untuk menjaga mereka tetap dekat. Kami memesan tempat, merencanakan trivia, menyiapkan kartu. Kamu tinggal datang — kru yang sama, malam baru, tempat baru.",
      "hm.circles.cta_find": "Mulai Circle",
      "hm.circles.cta_host_hero": "Bergabung dengan Circle terbuka",
      "hm.circles.trust_venue": "Tempat terverifikasi yang bergantian",
      "hm.circles.trust_party": "4-12 teman per Circle",
      "hm.circles.trust_verified": "Anggota terverifikasi",
      "hm.circles.trust_friendship": "Pertemanan nyata, tetap erat",
      "hm.circles.how_kicker": "Cara kerja",
      "hm.circles.how_title": "Empat langkah. Krumu tetap menjadi kru.",
      "hm.circles.how_step1_ttl": "Mulai Circle-mu",
      "hm.circles.how_step1_sub": "Beri nama, undang teman-temanmu (hingga 12 orang), dan pilih ritmenya — mingguan, dua mingguan, atau bulanan.",
      "hm.circles.how_step2_ttl": "Kami memilih tempatnya",
      "hm.circles.how_step2_sub": "Setiap acara kami menyarankan tempat baru — restoran, malam trivia, ruang kartu, bar anggur. Atau tetapkan tempat favorit.",
      "hm.circles.how_step3_ttl": "Semua orang RSVP dalam satu ketukan",
      "hm.circles.how_step3_sub": "Pengingat otomatis, bagi biaya, kursi prabayar sehingga ketidakhadiran tidak merusak rencana. Tagihan selesai sebelum makanan pembuka tiba.",
      "hm.circles.how_step4_ttl": "Jaga ritmenya",
      "hm.circles.how_step4_sub": "Satu malam di agenda yang tidak perlu diatur lagi. Grup menjadi semakin erat seiring berjalannya bulan, bukan menit.",
      "hm.circles.cadence_weekly_ttl": "Mingguan",
      "hm.circles.cadence_weekly_sub": "Setiap minggu",
      "hm.circles.cadence_weekly_body": "Untuk kru yang menginginkan ritual Selasa malam — wajah yang sama, tempat baru setiap kali.",
      "hm.circles.cadence_fortnight_ttl": "Dua mingguan",
      "hm.circles.cadence_fortnight_sub": "Setiap 2 minggu",
      "hm.circles.cadence_fortnight_body": "Titik keseimbangan — rutin tanpa terlalu membebani agenda yang sibuk.",
      "hm.circles.cadence_monthly_ttl": "Bulanan",
      "hm.circles.cadence_monthly_sub": "Setiap bulan",
      "hm.circles.cadence_monthly_body": "Circles untuk acara khusus — Sunday roast, malam trivia besar, degustasi, poker.",
      "hm.circles.feast_kicker": "Pilih malammu",
      "hm.circles.feast_title": "Tiga cara untuk menghabiskannya",
      "hm.circles.feast_sub": "Setiap Circle memilih bagaimana malam itu berjalan. Ganti-ganti caranya, atau tetapkan favorit krumu.",
      "hm.circles.feast_surprise_ttl": "Coba restoran baru",
      "hm.circles.feast_surprise_body": "Kami membuat daftar pendek tiga tempat di dekatmu, semua orang memilih, dapur bersiap untuk meja penuh. Bagi biaya, kursi prabayar.",
      "hm.circles.feast_preselect_ttl": "Trivia / kartu / game",
      "hm.circles.feast_preselect_body": "Pesan malam trivia, ruang poker, bar board-game, atau lintasan bowling. Kami menangani tempat, biaya masuk, dan pengingat.",
      "hm.circles.feast_per_event_ttl": "Malam bertema sekali-sekali",
      "hm.circles.feast_per_event_body": "Mencicipi anggur, chef's table, kelas koktail, escape room. Sesuatu yang istimewa sesekali — diatur sepenuhnya.",
      "hm.circles.live_kicker": "Sekarang juga",
      "hm.circles.live_title": "Circles yang bisa kamu ikuti",
      "hm.circles.live_host_link": "Mulai punyamu sendiri",
      "hm.circles.live_loading": "Memuat…",
      "hm.circles.live_empty_title": "Jadilah Circle pertama di kotamu.",
      "hm.circles.live_empty_sub": "Mulai satu dengan tiga teman dan kami akan membantumu mengundang sisanya — plus kami traktir tiga tempat pertama.",
      "hm.circles.live_empty_cta": "Mulai Circle",
      "hm.circles.host_kicker": "Untuk pemimpin Circle",
      "hm.circles.host_title": "Kamu bawa krunya. Kami urus adminnya.",
      "hm.circles.host_body": "Jika kamu adalah teman yang selalu mengatur malam itu, Circles menghemat waktumu berjam-jam. Pengingat otomatis, RSVP prabayar, daftar pendek tempat, bagi biaya, manajemen daftar tunggu, dan dompet cashback pada setiap malam keluar. Kamu tinggal datang sebagai anggota biasa.",
      "hm.circles.host_perk_1": "Daftar pendek tempat otomatis — tidak perlu scroll Google lagi",
      "hm.circles.host_perk_2": "Kursi prabayar — nol ketidakhadiran",
      "hm.circles.host_perk_3": "Bagi biaya selesai sebelum hidangan penutup",
      "hm.circles.host_perk_4": "Pengingat + undangan kalender secara autopilot",
      "hm.circles.host_perk_5": "Daftar tunggu dengan promosi otomatis jika ada teman yang batal",
      "hm.circles.host_perk_6": "Dompet cashback pada setiap malam keluar",
      "hm.circles.host_cta": "Mulai Circle sekarang",
      "hm.circles.why_kicker": "Mengapa Circles berhasil",
      "hm.circles.why_title": "Pertemanan dewasa butuh infrastruktur",
      "hm.circles.why_1_ttl": "Kru yang sama, kepercayaan yang lebih dalam",
      "hm.circles.why_1_body": "Hidup sering memisahkan teman-teman. Circles adalah ritme tetap yang menjaga semua orang tetap dekat — 4-12 teman yang sama, bertemu di malam kerja yang sama, apa pun yang terjadi.",
      "hm.circles.why_2_ttl": "Nol kelelahan admin",
      "hm.circles.why_2_body": "Tidak ada yang perlu menjadi ketua WhatsApp grup. Kami memilih tempat, mengirim pengingat, menerima pembayaran, menyelesaikan prosesnya.",
      "hm.circles.why_3_ttl": "Tempat baru, kenangan baru",
      "hm.circles.why_3_body": "Berganti-ganti melalui restoran kecil terbaik di Australia, ruang trivia, dan bar game. Wajah-wajah familiar, latar belakang segar — keseimbangan sempurna antara rutinitas dan kebaruan.",
      "hm.circles.bottom_title": "Malam yang dinantikan krumu, tanpa ada yang perlu repot merencanakannya.",
      "hm.circles.bottom_sub": "Satu Circle. Teman yang sama. Tempat baru setiap saat.",
      "hm.circles.bottom_cta_find": "Mulai Circle",
      "hm.circles.bottom_cta_mm": "Temui orang baru dengan Matchmaker →",
      "hm.circles.card_host_pill": "Circle",
      "hm.matchmaker.page_title": "HustleMeet Matchmaker · AI memilih meja yang tepat",
      "hm.matchmaker.kicker": "HustleMeet · Matchmaker",
      "hm.matchmaker.hero_title": "AI yang memilih meja. Kamu tinggal datang.",
      "hm.matchmaker.hero_title_accent": "Kamu tinggal datang.",
      "hm.matchmaker.hero_sub": "Jawab kuis singkat tentang kepribadian & nilai-nilai. Matchmaker kami akan mendudukkanmu dengan empat hingga tujuh orang asing yang akan terasa seperti teman lama saat hidangan penutup tiba. Tanpa swipe. Tanpa basa-basi. Hanya meja bersama, siap saat kamu siap.",
      "hm.matchmaker.cta_quiz": "Ikuti kuis pencocokan",
      "hm.matchmaker.cta_see_circles": "Lihat Circles sebagai gantinya →",
      "hm.matchmaker.trust_big5": "Skor kepribadian Big-5",
      "hm.matchmaker.trust_seats": "4-8 kursi per meja",
      "hm.matchmaker.trust_lock": "Tanpa swipe · tanpa foto",
      "hm.matchmaker.trust_verified": "Hanya anggota terverifikasi",
      "hm.matchmaker.how_kicker": "Cara kerja",
      "hm.matchmaker.how_title": "Empat langkah menuju meja yang sudah cocok.",
      "hm.matchmaker.how_step1_ttl": "Ikuti kuis",
      "hm.matchmaker.how_step1_sub": "Kuesioner sepuluh menit — kepribadian, nilai-nilai, diet, ketersediaan. Kamu hanya melakukannya sekali.",
      "hm.matchmaker.how_step2_ttl": "Pencocokan dalam semalam",
      "hm.matchmaker.how_step2_sub": "AI kami membaca profilmu dan daftar makan malam aktif di Australia. Saat skor kecocokanmu 0.70 atau lebih tinggi, AI akan menyusun undangan pribadi.",
      "hm.matchmaker.how_step3_ttl": "Terima atau tolak",
      "hm.matchmaker.how_step3_sub": "Kamu akan menerima satu email undangan yang jelas dengan detail tempat, tema meja, dan tiga minat yang kamu miliki bersama grup tersebut.",
      "hm.matchmaker.how_step4_ttl": "Ia belajar darimu",
      "hm.matchmaker.how_step4_sub": "Setiap survei pasca-makan malam akan menyempurnakan model kami. Kecocokanmu berikutnya akan lebih pintar seiring seringnya kamu makan malam.",
      "hm.matchmaker.science_kicker": "Mengapa ini berhasil",
      "hm.matchmaker.science_title": "Matchmaker tidak menebak — ia memberi skor.",
      "hm.matchmaker.science_sub": "Setiap kecocokan adalah gabungan berbobot dari empat faktor. Ketika rata-rata seluruh meja di atas 0.70, kami mengirim undangan. Apa pun di bawah itu tetap berada di antrean.",
      "hm.matchmaker.science_1_ttl": "Kepribadian (35%)",
      "hm.matchmaker.science_1_body": "Kemiripan kosinus di seluruh vektor kepribadian Big-5 — keterbukaan, kesadaran, ekstraversi, keramahan, dan stabilitas emosional.",
      "hm.matchmaker.science_2_ttl": "Nilai-nilai (25%)",
      "hm.matchmaker.science_2_body": "Kesesuaian pada nilai-nilai yang membuat percakapan makan malam benar-benar nyambung — keluarga, rasa ingin tahu, pertumbuhan, pengabdian, petualangan.",
      "hm.matchmaker.science_3_ttl": "Minat (25%)",
      "hm.matchmaker.science_3_body": "Overlap Jaccard pada minat spesifikmu — mendaki Six Foot Track, klub film Sydney, skena kopi Melbourne, buku ketiga kamu tahun ini.",
      "hm.matchmaker.science_4_ttl": "Ketersediaan (15%)",
      "hm.matchmaker.science_4_body": "Malam yang benar-benar bisa kamu hadiri. Tidak ada undangan Selasa jam 7 malam jika kalendermu tertulis gym-setiap-Selasa.",
      "hm.matchmaker.safety_kicker": "Keamanan melalui desain",
      "hm.matchmaker.safety_title": "Matchmaker bekerja dengan penuh kehati-hatian.",
      "hm.matchmaker.safety_1_ttl": "Ambang batas kepercayaan",
      "hm.matchmaker.safety_1_body": "Di bawah skor grup 0.70, tidak ada undangan yang dikirim. Kecocokan yang meragukan lebih buruk daripada tidak ada makan malam sama sekali.",
      "hm.matchmaker.safety_2_ttl": "Daftar blokir mutual-no",
      "hm.matchmaker.safety_2_body": "Jika dua anggota secara pribadi memberi tanda 'tidak' setelah makan malam, kami tidak akan pernah mendudukkan mereka bersama lagi.",
      "hm.matchmaker.safety_3_ttl": "Tombol pemutus otomatis",
      "hm.matchmaker.safety_3_body": "Jika 20% atau lebih dari survei terbaru memberi peringkat meja 2/5 atau lebih rendah, undangan akan dijeda selama 24 jam sementara operator meninjau.",
      "hm.matchmaker.safety_4_ttl": "Standar komunitas",
      "hm.matchmaker.safety_4_body": "Setiap anggota menerima kode etik bahasa yang sederhana. Kami mengeluarkan orang yang melanggarnya — tidak ada banding.",
      "hm.matchmaker.safety_5_ttl": "Hanya tempat umum",
      "hm.matchmaker.safety_5_body": "Setiap makan malam Matchmaker berlangsung di restoran asli dengan staf. Tidak pernah di rumah seseorang.",
      "hm.matchmaker.safety_6_ttl": "Umpan balik pasca-acara",
      "hm.matchmaker.safety_6_body": "Survei pribadimu membentuk kecocokan di masa depan, dan dapat memicu peninjauan moderator terhadap tempat atau rekan makan malam.",
      "hm.matchmaker.loop_kicker": "Loop pembelajaran",
      "hm.matchmaker.loop_title": "Setiap makan malam membuat makan malammu berikutnya lebih baik.",
      "hm.matchmaker.loop_body": "Matchmaker tidak bersifat statis. Setelah setiap acara, kamu mengirimkan survei pribadi singkat — bagaimana suasana percakapannya, dengan siapa kamu ingin makan malam lagi, apakah tempatnya memuaskan. Sinyal-sinyal tersebut masuk kembali ke model dan membentuk setiap kecocokan di masa depan.",
      "hm.matchmaker.loop_1": "Pilihan rekan \"makan lagi\" memperkuat pasanganmu",
      "hm.matchmaker.loop_2": "Pilihan mutual-no secara diam-diam memisahkan pasangan",
      "hm.matchmaker.loop_3": "Peringkat tempat & percakapan menyempurnakan bauran kursi",
      "hm.matchmaker.loop_4": "Skor keandalan melindungi anggota dari orang yang sering tidak datang",
      "hm.matchmaker.loop_5": "Bias pembelajaran memperkuat kecocokan yang baik dari bulan ke bulan",
      "hm.matchmaker.loop_6": "Kamu tetap memegang kendali — pilih keluar atau atur ulang profilmu kapan saja",
      "hm.matchmaker.vs_kicker": "Mana yang tepat untukku?",
      "hm.matchmaker.vs_title": "Matchmaker atau Circles?",
      "hm.matchmaker.vs_mm_ttl": "Pilih aku jika ingin variasi.",
      "hm.matchmaker.vs_mm_1": "Meja, kota, dan masakan yang berbeda setiap saat",
      "hm.matchmaker.vs_mm_2": "Grup 4-8 orang asing yang dikurasi AI untuk kompatibilitas",
      "hm.matchmaker.vs_mm_3": "Ritme mingguan, tanpa komitmen sebagai host",
      "hm.matchmaker.vs_mm_4": "Bagus jika kamu baru di sebuah kota atau sedang bepergian",
      "hm.matchmaker.vs_mm_cta": "Ikuti kuis pencocokan",
      "hm.matchmaker.vs_circles_ttl": "Pilih aku jika ingin yang rutin.",
      "hm.matchmaker.vs_circles_1": "Host yang sama · tempat yang sama · malam kerja yang sama",
      "hm.matchmaker.vs_circles_2": "Grup yang berubah menjadi tradisi Rabu malammu",
      "hm.matchmaker.vs_circles_3": "Dikurasi oleh manusia oleh host yang berpengalaman",
      "hm.matchmaker.vs_circles_4": "Bagus jika kamu baru saja pindah dan ingin menetap",
      "hm.matchmaker.vs_circles_cta": "Telusuri Circles",
      "hm.matchmaker.bottom_title": "Satu kuis. Setiap makan malam setelah itu sudah pasti cocok.",
      "hm.matchmaker.bottom_sub": "Kamu akan mendapatkan pasangan dalam waktu dua minggu. Ini adalah jawaban \"ya\" termudah yang akan kamu buat bulan ini.",
      "hm.matchmaker.bottom_cta": "Ikuti kuis pencocokan",
      "hm.matchmaker.bottom_note": "Tanpa kartu kredit · 10 menit · Berhenti kapan saja",
      /* AUTO-TRANSLATED hm.* END */
    },
  },
  ja: {
    translation: {
      /* AUTO-TRANSLATED ja START */
      plaza_nav_features: "機能",
      plaza_nav_rewards: "リワード",
      plaza_nav_how: "仕組み",
      plaza_nav_roadmap: "ロードマップ",
      plaza_nav_faq: "よくある質問",
      plaza_hero_badge: "オーストラリア所有",
      plaza_hero_kicker: "1つのカート。すべてのリワード。",
      plaza_hero_title_1: "オーストラリアで買い物。",
      plaza_hero_title_2: "どこでも獲得。",
      plaza_hero_sub: "何千ものオーストラリアのベンダーが1つのバスケットに。使った1ドルごとにHustlePointsを獲得 — レストラン、カフェ、ホテル、美容院、ペットグルーマー、そしてHustleSuiteネットワークのあらゆるビジネスで利用可能です。",
      plaza_cta_start: "無料参加 — 30秒で完了",
      plaza_cta_signin: "サインイン",
      plaza_cta_shop: "ストアを見る",
      plaza_hero_stat_1_v: "12,847",
      plaza_hero_stat_1_l: "商品公開中",
      plaza_hero_stat_2_v: "340+",
      plaza_hero_stat_2_l: "オーストラリアのベンダー",
      plaza_hero_stat_3_v: "$4.2M",
      plaza_hero_stat_3_l: "リワード支払額",
      plaza_hero_stat_4_v: "19",
      plaza_hero_stat_4_l: "カテゴリー",
      plaza_problem_kicker: "今日の買い物の問題点",
      plaza_problem_title: "10個のタブ。10個のパスワード。ロイヤルティはゼロ。",
      plaza_problem_sub: "Amazon、eBay、Kogan、Kmart、Woolies、Coles、Bunnings、JB、Officeworks、そして多数のブティックストアをすでに使い分けています。価格を比較するのに何時間も費やし、手に入るのはレシートだけ。",
      plaza_problem_1: "サイト間で比較できない価格",
      plaza_problem_2: "地元を装う海外の販売者",
      plaza_problem_3: "1つの店舗に閉じ込められたロイヤルティポイント",
      plaza_problem_4: "ボットやドロップシッパーによる偽レビュー",
      plaza_solution_kicker: "HustlePlazaの違い",
      plaza_solution_title: "一日中開く最後のカート。",
      plaza_solution_sub: "1つの検索。1つのカート。1つのチェックアウト。すべての購入でHustlePointsが還元され、このショップだけでなく、エコシステム全体で使用できます。",
      plaza_solution_1_t: "1つのカート、すべてのベンダー",
      plaza_solution_1_b: "プラットフォーム上のすべてのオーストラリアの販売者から買い物し、チェックアウト時に一度に支払い。可能な場合は配送を統合。",
      plaza_solution_2_t: "認証済み地元のみ",
      plaza_solution_2_b: "すべての販売者は出品前に本人確認を実施。ボット、偽物、ドロップシッピングの謎はありません。",
      plaza_solution_3_t: "実際に使えるリワード",
      plaza_solution_3_b: "ここで獲得したポイントは、HustleSuiteのあらゆるビジネス — カフェ、ホテル、美容院、レストラン、ペットグルーマー — で使用可能。",
      plaza_solution_4_t: "すべてのベンダーからのすべての荷物を1ページで追跡。各ステップでメールとSMSで通知。",
      plaza_rewards_kicker: "HustlePointsフライホイール",
      plaza_rewards_title: "買い物しながら獲得。ネットワーク内のどこでも使える。",
      plaza_rewards_sub: "HustlePlazaで使った1ドルごとにHustlePointsを獲得。HustleSuiteで運営されているレストラン、カフェ、ホテル、美容院、サービスビジネスなど、ネットワーク全体のどこでも利用可能。",
      plaza_rewards_step_1: "マーケットプレイスで買い物",
      plaza_rewards_step_1_b: "Tシャツから冷蔵庫まで何でも購入。",
      plaza_rewards_step_2: "即座にHustlePointsを獲得",
      plaza_rewards_step_2_b: "注文が発送された瞬間にポイントが付与 — カード不要、スタンプカード不要、有効期限のゲームなし。",
      plaza_rewards_step_3: "ネットワーク全体で使用",
      plaza_rewards_step_3_b: "カフェ、レストラン、ホテル、美容院、ペットグルーマー、職人で利用可能。",
      plaza_rewards_step_4: "または現金化",
      plaza_rewards_step_4_b: "$50に達したら銀行振込に換金。",
      plaza_cats_kicker: "カテゴリーで買い物",
      plaza_cats_title: "19カテゴリー。オーストラリアのベンダー。在庫あり。",
      plaza_cats_sub: "現在公開中のものの一部をご覧ください — または登録して12,000以上の商品カタログ全体を買い物しましょう。",
      plaza_cats_cta: "すべてのカテゴリーを見る",
      plaza_vendors_kicker: "本物のオーストラリアの販売者",
      plaza_vendors_title: "メーカー、生産者、ファミリーショップに会いましょう。",
      plaza_vendors_sub: "HustlePlaza上のすべてのベンダーはABN認証済み、オーストラリアベース、個人的にオンボーディング済み。地元を装う海外の中継業者はいません。",
      plaza_how_kicker: "仕組み",
      plaza_how_title: "3つのステップ。1つのカート。永遠のリワード。",
      plaza_how_1_title: "30秒で参加",
      plaza_how_1_body: "メールアドレスを確認してすぐに閲覧開始。チェックアウトの準備ができたら電話番号を追加。",
      plaza_how_2_title: "カートに入れる",
      plaza_how_2_body: "すべてのベンダーから買い物。1つのカート、1つの配送ビュー、1つのチェックアウト。StripeまたはPayPalで支払い。",
      plaza_how_3_title: "どこでも獲得・使用",
      plaza_how_3_body: "注文が発送された瞬間にHustlePointsが付与。HustleSuiteのあらゆるビジネスで使用するか、$50で現金化。",
      plaza_compare_kicker: "私たちの立ち位置",
      plaza_compare_title: "HustlePlaza vs 大手企業",
      plaza_compare_col_us: "HustlePlaza",
      plaza_compare_col_amz: "Amazon AU",
      plaza_compare_col_ebay: "eBay AU",
      plaza_compare_col_kmart: "Kmart / Big W",
      plaza_compare_row_1: "オーストラリア所有",
      plaza_compare_row_2: "すべての販売者で1つのカート",
      plaza_compare_row_3: "このストア外で使えるリワード",
      plaza_compare_row_4: "リワードを銀行に現金化",
      plaza_compare_row_5: "オーストラリアホスト型カスタマーサポート",
      plaza_trust_kicker: "なぜ認証するのか",
      plaza_trust_title: "本物の顧客。本物のレビュー。",
      plaza_trust_body: "サインアップ時にメールアドレス、チェックアウト前に携帯電話番号を確認し、レビューの投稿、ベンダーとのチャット、高額購入の前にライブ写真チェックが必要です。これにより詐欺師の動きを遅くし、お客様の価格を低く保ちます。",
      plaza_trust_1: "参加時のメール認証",
      plaza_trust_2: "チェックアウト時のSMS認証",
      plaza_trust_3: "レビューとチャット用のライブ写真認証",
      plaza_trust_4: "オンボーディング時のベンダー本人確認",
      plaza_test_kicker: "初期メンバーの声",
      plaza_test_title: "本物の顧客から直接。",
      plaza_test_1_body: "HustlePlazaのポイントを使って職場近くのカフェで夕食を支払いました。ウェイトレスが笑顔で「お会計済みです」と言うまで、本当に使えるとは信じられませんでした。",
      plaza_test_1_name: "Priya S.",
      plaza_test_2_body: "6つのベンダー、1つの配送期間、1つの確認メール。通常なら6つのパスワードと6つの追跡番号が必要です。これは本当に機能します。",
      plaza_test_2_name: "Marcus T.",
      plaza_test_3_body: "小規模な蜂蜜生産者として、ついに即日支払い、本物のレビュー、40%のカットなしを実現できました。HustlePlazaのおかげで8ヶ月で「副業」からフルタイムになりました。",
      plaza_test_3_name: "Kate & Dave",
      plaza_faq_kicker: "回答",
      plaza_faq_title: "毎週受ける質問",
      plaza_faq_1_q: "HustlePlazaは本当にオーストラリア所有ですか?",
      plaza_faq_1_a: "はい。HustlePlazaは100%オーストラリア所有、オーストラリアのサーバーでホスト、オーストラリアのチームが運営しています。すべてのベンダーは最初の出品が公開される前に本人確認を通過します。",
      plaza_faq_2_q: "リワードの仕組みは?",
      plaza_faq_2_a: "HustlePlazaで使った1ドルごとにHustlePointsを獲得します。ポイントは注文が発送された瞬間に付与され、有効期限はありません。HustleSuiteで運営されているあらゆるビジネスで使用するか、十分に貯まったら銀行に現金化できます。",
      plaza_faq_3_q: "なぜ写真認証を行うのですか?",
      plaza_faq_3_a: "読むレビューが本物であることを保証するためです。写真認証はスマートフォンのカメラで行うライブ自撮りチェックで、銀行が使用するのと同じ技術です。これによりボット、偽アカウント、悪意のある行為者がプラットフォームを悪用するのを防ぎます。",
      plaza_faq_4_q: "注文が届かない場合は?",
      plaza_faq_4_a: "すべての注文はHustleGuaranteeで保証されています。荷物が届かないか説明と異なる場合、3営業日以内に返金し、その後ベンダーに追跡します。お客様が損をすることはありません。",
      plaza_faq_5_q: "カード情報は安全ですか?",
      plaza_faq_5_a: "カード番号を見たり保存したりすることはありません。すべての支払いはStripe(PCI-DSS Level 1)またはPayPalを通じて行われ、両方とも盗まれたカードでの不正請求からお客様を保護する3D Secure認証を備えています。",
      plaza_faq_6_q: "国際配送はしていますか?",
      plaza_faq_6_a: "まだです。HustlePlazaはローンチ時はオーストラリアのみです — だからこそほとんどのアイテムで同週配送とお客様のタイムゾーンでのサポートを約束できます。ニュージーランドは2026年のロードマップにあります。",
      plaza_faq_7_q: "参加費用は?",
      plaza_faq_7_a: "無料です。メンバーシップは永久に無料です。ベンダーが販売ごとに少額の手数料を支払います — 購入者は参加$0、獲得$0、利用$0です。",
      plaza_faq_8_q: "HustlePlazaで販売できますか?",
      plaza_faq_8_a: "もちろんです。hustlesuite.auにアクセスしてベンダーとしてオンボード — 認証済みオーストラリアビジネスは即日承認、さらに最初の30日間は手数料無料です。",
      plaza_final_kicker: "創設者価格 — 限定席",
      plaza_final_title: "最初の1人になり、創設者価格を確保しましょう。",
      plaza_final_sub: "今日HustleSuite Memberとして登録。新機能が公開されるたびに最初に通知を受け取り、アカウントは生涯にわたって創設者ティアのリワードと価格を維持します。",
      plaza_final_bonus: "創設者は創設者レートを失うことはありません。永久無料、いつでもキャンセル可能。",
      plaza_final_cta: "Founder Memberとして登録",
      plaza_final_disclosure: "カード不要。メールと携帯電話の確認のみ。",
      plaza_roadmap_kicker: "次に構築するもの",
      plaza_roadmap_title: "作業を軽減するために構築された6つのツール。",
      plaza_roadmap_sub: "HustleSuiteはショップ以上のものです — 完全な生産性レイヤーです。以下は予定されているものです。",
      plaza_roadmap_soon: "まもなく公開",
      plaza_roadmap_next: "次の予定",
      plaza_roadmap_1_t: "HustleDictate — あらゆるフォームで音声テキスト変換",
      plaza_roadmap_1_b: "Chrome拡張機能。Alt+Mを押して話すと、あらゆるウェブサイトの入力欄が自動入力されます。不動産申請、請求書、NDIS書類 — 半分の時間で完了。",
      plaza_roadmap_2_t: "HustleConnect Edit-Anywhere",
      plaza_roadmap_2_b: "WordPress所有者向け:1つのホットキーで任意のページからライブサイトを編集。wp-adminへの往復が不要に。",
      plaza_roadmap_3_t: "Save-to-HustleSuiteユニバーサルボタン",
      plaza_roadmap_3_b: "任意のウェブページを右クリック — 物件リスト、記事、商品、レストラン — そして適切なモジュールにアクション準備完了で保存。",
      plaza_roadmap_4_t: "HustlePlaza価格チェッカー",
      plaza_roadmap_4_b: "Amazon、eBay、Kmartを閲覧 — フローティングチップがHustlePlazaの同じ商品とリワードプレビューを表示。",
      plaza_roadmap_5_t: "レビューハーベスター",
      plaza_roadmap_5_b: "Google、Facebook、TripAdvisorの新しいレビューをHustleSuiteダッシュボードに自動同期。週に約20分節約。",
      plaza_roadmap_6_t: "HustleSuite Sidebar",
      plaza_roadmap_6_b: "ライブ注文、KPI、クイック投稿、クイック請求書を備えたChromeサイドパネル。すべてのタブの横にあなたのビジネス全体。",
      plaza_footer_owned: "オーストラリア所有・ホスト",
      plaza_footer_terms: "利用規約",
      plaza_footer_privacy: "プライバシー",
      plaza_footer_contact: "お問い合わせ",
      plaza_footer_vendors: "HustlePlazaで販売",
      plaza_footer_help: "ヘルプセンター",
      plaza_footer_tagline: "HustleSuiteファミリーの一員 — 1つのログイン、すべてのサービス。",
      verify_gate_title: "もう少しです — 簡単なサインアップ",
      verify_gate_body: "HustlePlazaは、このプラットフォーム内だけでなく、HustlePlazaプラットフォームを使用する他のビジネスやHustleSuiteに登録されている他のビジネスでも利用できる交換可能なリワードを含む多くの特典を提供するショッピングカートです。これらのビジネスには、レストラン、カフェ、ホテル、美容院、さらにはペットグルーミングなどが含まれます。優れた価格とサービスを提供し続けるために、お客様が本物であることを確認し、認証する必要があります。続行するには参加ボタンを押してください。",
      verify_gate_join: "HustleSuiteに参加",
      verify_gate_signin: "すでにアカウントを持っています",
      verify_gate_cancel: "今はしない",
      verify_email_title: "メールアドレスを確認",
      verify_email_body: "受信箱に6桁のコードを送信します。",
      verify_email_placeholder: "you@example.com",
      verify_email_send: "コードを送信",
      verify_email_code_ph: "6桁のコード",
      verify_email_check: "確認",
      verify_email_sent: "コード送信済み — 受信箱を確認してください。",
      verify_sms_title: "電話番号を確認",
      verify_sms_body: "注文の更新情報をテキストで送信し、大口購入を確認できるように携帯電話番号を追加してください。",
      verify_sms_placeholder: "+61412345678",
      verify_sms_send: "SMSを送信",
      verify_sms_check: "確認",
      verify_sms_sent: "コード送信済み — 携帯電話を確認してください。",
      verify_photo_title: "写真認証",
      verify_photo_body: "スマートフォンでQRコードをスキャンしてライブ自撮りを撮影してください。チャット、高額購入、MeetupやTackleへの投稿に必要です。",
      verify_photo_waiting: "スマートフォンを待っています…",
      verify_photo_verified: "写真認証完了 — すべて準備完了です。",
      travel_cta_start: "無料参加",
      travel_hero_kicker: "HustleTravel",
      travel_hero_title_1: "1つのメンバーシップ。",
      travel_hero_title_2: "オーストラリア全土。",
      travel_hero_sub: "ホテル、フライト、体験、ロードトリップ計画、レンタカー — 最安値で予約し、使った1ドルごとにHustlePointsを獲得。",
      travel_cta_plan: "ロードトリップを計画",
      travel_cta_hotels: "ホテルを検索",
      travel_svc_hotels_t: "ホテル・アパートメント",
      travel_svc_hotels_b: "200以上の予約サイトを1か所で比較 — Trivago提供のメタサーチ。",
      travel_svc_flights_t: "フライト",
      travel_svc_flights_b: "国内線と国際線、リアルタイムの座席マップと運賃規則付き。",
      travel_svc_experiences_t: "体験・ツアー",
      travel_svc_experiences_b: "リーフデイ、ワイナリーテイスティング、ライブミュージック、子供向け遠足。",
      travel_svc_cars_t: "レンタカー",
      travel_svc_cars_b: "すべての主要ブランドと地元の独立系業者 — オーストラリアのすべての空港で受取可能。",
      travel_svc_insurance_t: "旅行保険",
      travel_svc_insurance_b: "APRA規制下のオーストラリアの保険会社による引受。1画面で比較。",
      travel_svc_roadtrip_t: "ロードトリッププランナー",
      travel_svc_roadtrip_b: "EV航続距離対応の旅程で充電器、宿泊地、費用見積もり付き。",
      travel_final_kicker: "創設者価格",
      travel_final_title: "生涯にわたる創設者ティアリワードを確保しましょう。",
      travel_final_sub: "HustleSuiteで一度登録。ホテル、フライト、体験、保険など、HustleTravelですべてを予約し、ネットワーク内のすべてのビジネスで使えるHustlePointsを獲得。",
      travel_final_cta: "Founder Memberとして登録",
      travel_final_disclosure: "最安値ポリシー:結果は手数料ではなく旅行者にとっての最良の価値順に表示。",
      dictate_hero_kicker: "HustleDictate",
      dictate_hero_title_1: "話す。",
      dictate_hero_title_2: "あらゆるフォーム。あらゆるウェブサイト。",
      dictate_hero_sub: "あらゆるテキストフィールドをプロフェッショナルグレードの音声ディクテーションマイクに変える唯一のChrome拡張機能 — Whisper搭載。",
      dictate_cta_install: "Chromeに追加",
      dictate_uc_re_t: "不動産賃貸メモ",
      dictate_uc_re_b: "検査報告書とテナントメッセージをPMシステムに直接ディクテーション。",
      dictate_uc_ndis_t: "NDIS進捗メモ",
      dictate_uc_ndis_b: "記憶が新しいうちにシフトメモを話す — ハンズフリー、タイピング不要。",
      dictate_uc_invoice_t: "請求書の説明",
      dictate_uc_invoice_b: "行項目の説明をXero、MYOB、QuickBooksに直接ディクテーション。",
      dictate_uc_email_t: "長文メール返信",
      dictate_uc_email_b: "Gmail、Outlook、あらゆるウェブメールで返信を話す — タイピングより3倍速い。",
      dictate_final_kicker: "Founder Member無料",
      dictate_final_title: "すべてのキーボードが今やマイクに。",
      dictate_final_sub: "HustleSuite Founder Member無料。それ以外は月$5 — いつでもキャンセル可能。",
      dictate_final_cta: "Chromeに追加 — 無料",
      "finder.cat.roadtrip": "ロードトリップ",
      "finder.hotels.title": "近くのホテルを探す",
      "finder.hotels.sub": "1回の検索で200以上の予約サイトを比較 — Trivago提供。",
      "finder.hotels.where": "場所",
      "finder.hotels.where_ph": "都市または郊外(例:Byron Bay, NSW)",
      "finder.hotels.checkin": "チェックイン",
      "finder.hotels.checkout": "チェックアウト",
      "finder.hotels.adults": "大人",
      "finder.hotels.search": "ホテルを検索",
      "finder.hotels.disclosure": "Trivagoで開きます — 予約時に手数料を得る場合があります。",
      /* AUTO-TRANSLATED ja END */
      welcome_back: "おかえりなさい",
      sign_out: "サインアウト",
    
      /* AUTO-TRANSLATED finder.* (added) */
      "finder.title": "HustleFinder",
      "finder.tagline": "最寄りの公共施設を検索 — オーストラリア全土の実際のデータ。",
      "finder.search_placeholder": "場所、通り、または郊外を検索…",
      "finder.hint": "上に入力するか、カテゴリーをタップ、または「現在地付近」を使用してください。",
      "finder.near_me": "現在地付近",
      "finder.searching": "検索中…",
      "finder.suburb.placeholder": "郊外で絞り込む(例: Darwin, NT)…",
      "finder.suburb.clear": "クリア",
      "finder.search_in": "{{cat}}内を検索",
      "finder.empty": "結果なし — 別のカテゴリーまたは検索語句をお試しください。",
      "finder.meta.title": "HustleFinder — 無料オーストラリア郊外ディレクトリ",
      "finder.meta.desc": "オーストラリア全郊外の地元ビジネス、充電器、カフェ、トイレなどを検索 — 100%無料。",
      "finder.footer.credits": "データクレジット&帰属表示",
      "finder.footer.terms": "利用規約",
      "finder.cat.toilet": "トイレ",
      "finder.cat.fountain": "飲料水噴水",
      "finder.cat.transport": "交通機関",
      "finder.cat.park": "公園",
      "finder.cat.medical": "医療",
      "finder.cat.school": "学校",
      "finder.cat.library": "図書館",
      "finder.cat.petrol": "ガソリンスタンド",
      "finder.cat.kidzone": "キッズゾーン",
      "finder.cat.puzzles": "パズル",
      /* AUTO-TRANSLATED rt.* START */
      "rt.brand_name": "Roadtrip Planner",
      "rt.page_title": "オーストラリアEVロードトリップ・プランナー",
      "rt.from": "出発地",
      "rt.to": "目的地",
      "rt.from_placeholder": "地区、町、都市（例：Newcastle NSW）",
      "rt.to_placeholder": "地区、町、都市（例：Hobart TAS）",
      "rt.stop": "STOP",
      "rt.stop_placeholder": "経由地",
      "rt.add_stop": "経由地を追加",
      "rt.upgrade_pro": "Proにアップグレードして最大20か所の経由地を追加",
      "rt.reverse": "反転（帰路を計算）",
      "rt.from_current": "現在地から出発",
      "rt.range_per_charge": "1充電あたりの航続距離 (km)",
      "rt.connector": "コネクタ",
      "rt.connector_any": "すべて",
      "rt.drive_hours_day": "1日の運転時間",
      "rt.accommodation": "宿泊施設",
      "rt.accom_any": "指定なし (BnB · ホテル · キャラバン)",
      "rt.accom_bnb": "BnB",
      "rt.accom_hotel": "ホテル",
      "rt.accom_caravan": "キャラバンパーク",
      "rt.min_rating": "最低評価",
      "rt.any_rating": "すべての評価",
      "rt.only_5star": "5★のみ",
      "rt.schedule": "スケジュール:",
      "rt.depart_at": "出発時刻",
      "rt.arrive_by": "到着期限",
      "rt.hotel_checkin": "ホテル・チェックイン",
      "rt.hotel_checkout": "ホテル・チェックアウト",
      "rt.overnight_hint": "宿泊を伴う区間では、この時間外の走行はスケジュールされません。",
      "rt.whos_in_car": "同乗者",
      "rt.adults": "大人",
      "rt.kids": "子供",
      "rt.ages": "年齢",
      "rt.show_attractions": "観光スポットを表示",
      "rt.show_kids": "子供向けの経由地を表示",
      "rt.overnight_stays": "宿泊数",
      "rt.fast_chargers_only": "急速充電器のみ",
      "rt.hotel_must_charger": "充電器のあるホテルを優先",
      "rt.pet_only": "ペット可の宿泊施設のみ",
      "rt.safety": "安全マージン",
      "rt.safety_90": "90% (平坦な道)",
      "rt.safety_85": "85% (デフォルト)",
      "rt.safety_80": "80% (坂道 / 牽引)",
      "rt.safety_70": "70% (重い荷物)",
      "rt.save_trip": "旅行を保存",
      "rt.plan_my_trip": "ルートを作成",
      "rt.refresh_trip": "ルートを更新",
      "rt.planning": "計画中…",
      "rt.resume_title": "ここから旅行を再開",
      "rt.resume_body": "現在地を取得しました。実際の残量に合わせて次の区間を計画するため、ダッシュボードに表示されている残りの航続距離を入力してください（フル充電の状態ではなく）。",
      "rt.remaining_range": "残りの航続距離 (km)",
      "rt.cancel": "キャンセル",
      "rt.replan_from_here": "ここから再計画",
      "rt.save_modal_title": "旅行に名前を付ける",
      "rt.save_modal_body": "保存された旅行はプロフィールに表示されます。いつでも開いて「再計画」を実行できます。",
      "rt.save_name_placeholder": "例：ブリスベン → シドニー（バイロン経由）",
      "rt.your_saved_trips": "保存済みの旅行",
      "rt.custom_location": "カスタム場所",
      "rt.trip_summary": "旅行の概要",
      "rt.itinerary": "旅程",
      "rt.no_stops_needed": "経由地は不要です。全行程を1回の充電で走行可能です。",
      "rt.chargers_in_corridor": "ルート上の充電器",
      "rt.planned_stops": "計画された経由地",
      "rt.overnight_stop": "宿泊（1泊）",
      "rt.overnight_stops": "宿泊数",
      "rt.suggested_detours": "おすすめの寄り道 — チェックして追加",
      "rt.replan_with_detours": "寄り道を含めて再計画",
      "rt.attractions_label": "観光スポット",
      "rt.kids_label": "子供",
      "rt.km_from_start": "出発地から(km)",
      "rt.open_driving": "ドライブモードを開く",
      "rt.big_font_hint": "大きなフォントで車載ホルダーに最適。CarPlayやAndroid Autoのブラウザミラーリングに対応しています。",
      "rt.check_price_book": "Trivago で価格確認・予約 ↗",
      "rt.visit_site": "サイトを見る ↗",
      "rt.charger_onsite": "敷地内に充電器あり",
      "rt.leg_exceeds_range": "この区間は設定された航続距離を超えています。この充電器に到着する前に電欠になる可能性があります。航続距離の設定を下げるか、途中で充電を追加してください。",
      "rt.add_waypoint_replan": "経由地に追加して再計画",
      /* AUTO-TRANSLATED rt.* END */
                      /* AUTO-TRANSLATED hm.* START */
      "hm.nav.hustlemeet": "HustleMeet",
      "hm.nav.circles": "Circles",
      "hm.nav.matchmaker": "Matchmaker",
      "hm.nav.meet": "Meet",
      "hm.nav.overview": "HustleMeetups",
      "hm.overview.page_title": "HustleMeetups — 心の通う仲間を見つける、3つの温かい方法。",
      "hm.overview.kicker": "HustleSuite サービス",
      "hm.overview.hero_title": "心の通う仲間を見つける、3つの温かい方法。",
      "hm.overview.hero_title_accent": "心の通う仲間",
      "hm.overview.hero_sub": "HustleMeetups は、オーストラリアで最も心地よく本当の友情を築ける場所です。自分に合ったスタイルを選んでください。毎週のランニンググループ、気心の知れた仲間との月1回のディナー、あるいはAIがマッチングした初対面のメンバーとの食事会など、あなたにぴったりの入り口が用意されています。",
      "hm.overview.tile_meet_kicker": "HustleMeet",
      "hm.overview.tile_meet_title": "仲間を見つけて、好きなことをしよう。",
      "hm.overview.tile_meet_body": "オーストラリア発の、興味・関心でつながるコミュニティ。ランナー、プログラマー、投資家、ウォーキング、ダンサー、朝食会、読書会など、何百ものグループが毎週イベントを開催しています。一度だけの参加も、毎週の参加も自由です。",
      "hm.overview.tile_meet_cta": "グループを探索する",
      "hm.overview.tile_circles_kicker": "Circles",
      "hm.overview.tile_circles_title": "いつもの仲間と、毎回新しいテーブルで。",
      "hm.overview.tile_circles_body": "いつもの友人グループを誘ってみませんか？新しいレストランの予約から、クイズの企画、カードゲームの準備まで、すべてお任せください。あなたはただ参加するだけ。幹事の負担なしに、既存の仲間との絆を深める最高の形です。",
      "hm.overview.tile_circles_cta": "Circle を始める",
      "hm.overview.tile_matchmaker_kicker": "Matchmaker",
      "hm.overview.tile_matchmaker_title": "AIがテーブルを選び、あなたは席に着くだけ。",
      "hm.overview.tile_matchmaker_body": "10分ほどの診断を受けるだけで、翌日には、デザートを食べる頃には旧知の仲のように感じられる初対面のメンバーとの席をご案内します。毎回異なるメンバーとの出会いが待っています。",
      "hm.overview.tile_matchmaker_cta": "診断を受ける",
      "hm.overview.trust_kicker": "選ばれる理由",
      "hm.overview.trust_title": "信頼を第一に、最高のテーブルを。",
      "hm.overview.trust_1_title": "認証済みメンバー",
      "hm.overview.trust_1_body": "すべてのグループは、本人確認と信頼スコアのチェックから始まります。使い捨てのアカウントは存在しません。",
      "hm.overview.trust_2_title": "信頼できるホスト",
      "hm.overview.trust_2_body": "すべてのグループは、場の雰囲気を大切にし、リズムを整え、参加者に目配りをする審査済みのホストによって運営されています。",
      "hm.overview.trust_3_title": "友情を第一に",
      "hm.overview.trust_3_body": "ここではすべてが「真の友情」を築くために設計されています。出会い目的（デーティング）ではありません。",
      "hm.overview.trust_4_title": "厳格なモデレーション",
      "hm.overview.trust_4_body": "報告があれば即座にレビューし、判断を下します。不適切なユーザーはその日のうちに除名されます。",
      "hm.overview.footer_line": "HustleSuite サービス — Australia で運営・ホストされています。",
      "hm.landing.page_title": "HustleMeet · 仲間を見つけて、好きなことをしよう。",
      "hm.landing.kicker": "オーストラリアの関心・興味ネットワーク",
      "hm.landing.hero_title": "仲間を見つけて、好きなことをしよう。",
      "hm.landing.hero_title_accent": "仲間",
      "hm.landing.hero_sub": "HustleMeet は、オーストラリアの「好きなこと」でつながるコミュニティの拠点です。ランチ中にデバッグする開発者、朝6時の長距離ランナー、毎月集まる投資家。ウォーキンググループ、ダンスチーム、朝食会、ボードゲームの夜。グループを選び、イベントを予約して、あとは参加するだけです。",
      "hm.landing.cta_explore_circles": "グループを見る",
      "hm.landing.cta_try_matchmaker": "自分のグループを作る",
      "hm.landing.trust_verified": "認証済みメンバー",
      "hm.landing.trust_venues": "審査済みのイベントホスト",
      "hm.landing.trust_friendship": "友情第一 — デーティングではありません",
      "hm.landing.trust_moderation": "不適切な行為へのゼロ寛容",
      "hm.landing.subbrand_circles_kicker": "Circles",
      "hm.landing.subbrand_circles_title": "いつもの仲間がいますか？彼らのための居場所を作りましょう。",
      "hm.landing.subbrand_circles_body": "Circles は、読書会、ポーカー仲間、ランニング仲間、同僚など、すでに集まっている友人同士のためのサービスです。会場選び、リマインド、参加管理は私たちが担当します。あなたはただ楽しむだけです。",
      "hm.landing.subbrand_circles_b1": "毎回変わる会場、一括管理のリスト",
      "hm.landing.subbrand_circles_b2": "自動リマインド ＋ 簡単な出欠確認",
      "hm.landing.subbrand_circles_b3": "クイズ、カード、テーマ別ナイトをご用意",
      "hm.landing.subbrand_circles_cta": "Circle を始める",
      "hm.landing.subbrand_matchmaker_kicker": "Matchmaker",
      "hm.landing.subbrand_matchmaker_title": "AIがテーブルを選び、あなたは席に着くだけ。",
      "hm.landing.subbrand_matchmaker_body": "性格、価値観、予定についての10分間の診断。Matchmaker が、デザートの頃には親友のように感じられる初対面のメンバーをマッチングします。毎回違うメンバーとの出会いを楽しめます。",
      "hm.landing.subbrand_matchmaker_b1": "Big-5 性格診断 ＆ 価値観スコアリング",
      "hm.landing.subbrand_matchmaker_b2": "適合率0.70以上の場合のみ、翌日に招待を送付",
      "hm.landing.subbrand_matchmaker_b3": "食事を重ねるごとにAIがあなたの好みを学習",
      "hm.landing.subbrand_matchmaker_cta": "診断を受ける",
      "hm.landing.how_kicker": "HustleMeet の仕組み",
      "hm.landing.how_title": "4つのステップ。グループを見つけて、参加するだけ。",
      "hm.landing.how_step1_ttl": "興味のあることを選ぶ",
      "hm.landing.how_step1_sub": "ランニング、コーディング、投資、料理、音楽、散歩。あなたの好きなことと、お住まいの地域を教えてください。",
      "hm.landing.how_step2_ttl": "近くのグループを探す",
      "hm.landing.how_step2_sub": "各グループは、ホスト、活動頻度、次回のイベントを公開しています。雰囲気を知ってから参加を決められます。",
      "hm.landing.how_step3_ttl": "イベントに申し込む",
      "hm.landing.how_step3_sub": "タップ一つで予約完了。カレンダー登録と前日のリマインドも届きます。無料のものから有料のものまで、料金は明確に表示されています。",
      "hm.landing.how_step4_ttl": "参加してつながる",
      "hm.landing.how_step4_sub": "ホストやメンバーに会い、活動を楽しみましょう。気が合えば、また来週も参加してください。",
      "hm.landing.stats_kicker": "オーストラリアの新しい出会い方",
      "hm.landing.stats_title": "多くの人がリピートする理由。",
      "hm.landing.stats_92_sub": "イベントで少なくとも一人のメンバーと意気投合できたと感じている",
      "hm.landing.stats_68_sub": "1週間以内に次のイベントへの参加を申し込んでいる",
      "hm.landing.stats_85_sub": "グループの誰かと連絡を取り続けている",
      "hm.landing.why_kicker": "成功の秘訣",
      "hm.landing.why_title": "本物の興味。本物のホスト。本物のイベント。",
      "hm.landing.why_1_ttl": "場所よりも「興味」を優先",
      "hm.landing.why_1_body": "グループのメンバーがあなたと同じことに興味を持っていること。それが本当の友情を築くための近道です。",
      "hm.landing.why_2_ttl": "実在するイベントホスト",
      "hm.landing.why_2_body": "各グループには審査済みのホストがいます。場の雰囲気を守り、リズムを整え、トラブルがあれば対応します。Facebookの管理人のような存在ではなく、実在する頼れる人物です。",
      "hm.landing.why_3_ttl": "継続することの大切さ",
      "hm.landing.why_3_body": "毎週新しいイベントがあります。楽しみにできる予定があること。そのリズムが、初対面の人を「火曜夜の仲間」へと変えていきます。",
      "hm.landing.why_4_ttl": "安全で安心",
      "hm.landing.why_4_body": "認証済みメンバー、公共の会場、不適切な言動へのゼロ寛容。サポートチームがイベントの前後・開催中も待機しています。",
      "hm.landing.why_5_ttl": "友情を第一に",
      "hm.landing.why_5_body": "これはデーティングではありません。コミュニティです。共通の興味を持つ本物の人々と出会い、スワイプに明け暮れる日々から卒業しましょう。",
      "hm.landing.why_6_ttl": "顔の見えるサイズ",
      "hm.landing.why_6_body": "ほとんどのイベントは4〜15名に制限されています。深い会話ができる十分な人数でありながら、誰一人取り残されないサイズです。",
      "hm.landing.cities_kicker": "展開エリア",
      "hm.landing.cities_title": "Australia と NZ の各地でグループが活動中。",
      "hm.landing.cities_sub_zero": "15以上の都市で最初のグループがスタートしています。早期に参加して、コミュニティを作っていきましょう。",
      "hm.landing.cities_sub_some": "15以上の主要都市でグループが活動しています。今週、あなたの街でも何かが開催されています。",
      "hm.landing.cities_sub_one": "{{count}} 件のグループが活動中 · 毎週新しいイベント開催 · 都市を拡大中。",
      "hm.landing.cities_sub_many": "{{count}} 件のグループが活動中 · 毎週新しいイベント開催 · 都市を拡大中。",
      "hm.landing.forme_kicker": "こんな方におすすめ…",
      "hm.landing.forme_title": "本物の興味、本物の人々、本物のイベントを求めている方へ。",
      "hm.landing.forme_1": "スワイプアプリには疲れ果て、好きなことを通じて実際に人と会いたい。",
      "hm.landing.forme_2": "新しい街に引っ越したばかりで、チャットグループではなく、温かい仲間が必要だ。",
      "hm.landing.forme_3": "広く浅い関係よりも深さを重視する。50人のフォロワーより、一つの素晴らしいグループが大切だ。",
      "hm.landing.forme_4": "「共通の興味 ＋ 決まった2時間」がリアルな友情の近道だと信じている。",
      "hm.landing.notme_kicker": "こんな方には不向きです…",
      "hm.landing.notme_title": "HustleMeet がすべての人に合うわけではありません。",
      "hm.landing.notme_1": "「参加する」と約束できない。ホストや他のメンバーはあなたの出席を待っています。",
      "hm.landing.notme_2": "デーティングサービスを探している。ここでは友情が常に最優先です。",
      "hm.landing.notme_3": "実際に会わずに発信だけしたい。このサービスは対面でこそ価値があります。",
      "hm.landing.notme_4": "すべてが無料で提供されることを期待している。一部のイベントは有料ですが、料金は常に事前公開されます。",
      "hm.landing.safety_kicker": "安全への取り組み",
      "hm.landing.safety_title": "温かく、それでいて細心の注意を。",
      "hm.landing.safety_1_ttl": "公共の会場",
      "hm.landing.safety_1_body": "すべてのイベントは、カフェ、レストラン、公園、ジム、スタジオなどの公共の場所で開催されます。参加者全員の合意がない限り、個人の自宅で開催することはありません。",
      "hm.landing.safety_2_ttl": "コミュニティ基準",
      "hm.landing.safety_2_body": "無礼な振る舞い、ハラスメント、不正直な行為は一切容認しません。違反したメンバーは即座に除名されます。",
      "hm.landing.safety_3_ttl": "プライバシーの保護",
      "hm.landing.safety_3_body": "あなたの詳細情報が公開されることはありません。イベント後に誰とつながり続けるかは、すべてあなたがコントロールできます。",
      "hm.landing.safety_4_ttl": "サポート体制",
      "hm.landing.safety_4_body": "質問や懸念がある場合は、イベントの前後・開催中を問わず、私たちのチームが対応します。",
      "hm.landing.bottom_title": "お気に入りのグループは、すぐそこに見つかります。",
      "hm.landing.bottom_sub": "既存のグループに参加するか、いつもの仲間と Circle を作るか、Matchmaker でぴったりのテーブルを見つけてもらうか。あなたの道を選んでください。",
      "hm.landing.bottom_cta_circles": "Circle を始める",
      "hm.landing.bottom_cta_mm": "Matchmaker 診断を受ける",
      "hm.circles.page_title": "HustleMeet Circles · いつもの仲間と、毎回違う会場を",
      "hm.circles.kicker": "HustleMeet · Circles",
      "hm.circles.hero_title": "いつもの仲間と、毎回新しいテーブルで。",
      "hm.circles.hero_title_accent": "いつもの仲間",
      "hm.circles.hero_sub": "すでに気心の知れた仲間がいますか？Circles は、その絆を保つための最も簡単な方法です。会場の予約、企画、準備はすべてお任せください。あなたは同じ仲間と、新しい場所へ、その日に行くだけです。",
      "hm.circles.cta_find": "Circle を始める",
      "hm.circles.cta_host_hero": "公開されている Circle に参加する",
      "hm.circles.trust_venue": "厳選された会場をローテーション",
      "hm.circles.trust_party": "1つの Circle につき4〜12名の仲間",
      "hm.circles.trust_verified": "認証済みメンバー",
      "hm.circles.trust_friendship": "深い友情を、より身近に",
      "hm.circles.how_kicker": "仕組み",
      "hm.circles.how_title": "4つのステップ。いつもの仲間が、ずっと仲間でいられるように。",
      "hm.circles.how_step1_ttl": "Circle を立ち上げる",
      "hm.circles.how_step1_sub": "名前を決め、友人（最大12名）を招待し、頻度（毎週、隔週、または毎月）を選びます。",
      "hm.circles.how_step2_ttl": "会場を選定",
      "hm.circles.how_step2_sub": "毎回、新しいスポット（レストラン、クイズナイト、ワインバーなど）を提案します。お気に入りのお店に固定することも可能です。",
      "hm.circles.how_step3_ttl": "ワンタップで出欠確認",
      "hm.circles.how_step3_sub": "自動リマインド、費用の分割、事前決済により、ドタキャンで予定が台無しになるのを防ぎます。メイン料理が来る前に会計は完了しています。",
      "hm.circles.how_step4_ttl": "リズムを刻む",
      "hm.circles.how_step4_sub": "幹事がいなくても、自然とカレンダーに予定が刻まれます。数分ではなく、数ヶ月かけてグループの絆が深まっていきます。",
      "hm.circles.cadence_weekly_ttl": "毎週",
      "hm.circles.cadence_weekly_sub": "週に1回",
      "hm.circles.cadence_weekly_body": "火曜の夜を習慣にしたいグループに。同じ顔ぶれで、毎回違う場所へ。",
      "hm.circles.cadence_fortnight_ttl": "隔週",
      "hm.circles.cadence_fortnight_sub": "2週に1回",
      "hm.circles.cadence_fortnight_body": "忙しい日常の中でも負担にならず、定期的に集まれるベストな頻度です。",
      "hm.circles.cadence_monthly_ttl": "毎月",
      "hm.circles.cadence_monthly_sub": "月に1回",
      "hm.circles.cadence_monthly_body": "特別なイベント向け。日曜のローストランチ、本格クイズ大会、デギュスタシオン、ポーカーなど。",
      "hm.circles.feast_kicker": "夜の過ごし方を選ぶ",
      "hm.circles.feast_title": "3つの楽しみ方",
      "hm.circles.feast_sub": "その夜の過ごし方は Circle ごとに選べます。ローテーションするのも、お気に入りに固定するのも自由です。",
      "hm.circles.feast_surprise_ttl": "新しいレストランを開拓",
      "hm.circles.feast_surprise_body": "近くの3つの候補から全員で投票。キッチンは予約に合わせて準備。費用は分割・事前決済済みです。",
      "hm.circles.feast_preselect_ttl": "クイズ / カード / ゲーム",
      "hm.circles.feast_preselect_body": "クイズナイト、ポーカー、ボドゲバー、ボウリングなどを予約。会場手配、参加費、リマインドもすべて管理します。",
      "hm.circles.feast_per_event_ttl": "単発のテーマナイト",
      "hm.circles.feast_per_event_body": "ワインの試飲、シェフズテーブル、カクテル教室、脱出ゲーム。たまには趣向を凝らした特別な夜を、完璧なセッティングで。",
      "hm.circles.live_kicker": "現在募集中の",
      "hm.circles.live_title": "参加可能な Circles",
      "hm.circles.live_host_link": "自分で新しく作る",
      "hm.circles.live_loading": "読み込み中…",
      "hm.circles.live_empty_title": "あなたの街で最初の Circle を作りましょう。",
      "hm.circles.live_empty_sub": "友人3人と始めれば、残りのメンバーの招待をお手伝いします。さらに、最初の3回の会場予約をプレゼントします。",
      "hm.circles.live_empty_cta": "Circle を始める",
      "hm.circles.host_kicker": "Circle リーダーの方へ",
      "hm.circles.host_title": "あなたは仲間を呼ぶだけ。面倒な事務はすべてお任せ。",
      "hm.circles.host_body": "いつも幹事を務めてくれるあなたのために、Circles は何時間もの手間を省きます。自動リマインド、事前決済、会場選び、割り勘、キャンセル待ち管理。さらに、開催ごとにキャッシュバック・ウォレットも。あなたは一人の参加者として楽しむだけです。",
      "hm.circles.host_perk_1": "会場候補の自動リストアップ — Google検索はもう不要",
      "hm.circles.host_perk_2": "事前決済システム — ドタキャン・ノーショーをゼロに",
      "hm.circles.host_perk_3": "デザートの前に割り勘完了",
      "hm.circles.host_perk_4": "リマインドとカレンダー登録の自動化",
      "hm.circles.host_perk_5": "友人のキャンセル時には、キャンセル待ちを自動繰り上げ",
      "hm.circles.host_perk_6": "開催のたびにキャッシュバックが貯まる",
      "hm.circles.host_cta": "今すぐ Circle を始める",
      "hm.circles.why_kicker": "Circles が続く理由",
      "hm.circles.why_title": "大人の友情には「インフラ」が必要です",
      "hm.circles.why_1_ttl": "同じ仲間だから、深まる信頼",
      "hm.circles.why_1_body": "生活環境が変わると友達とは疎遠になりがちです。Circles は、たとえ何があっても同じ4〜12名の仲間が同じ平日の夜に集まる「固定されたリズム」を作ります。",
      "hm.circles.why_2_ttl": "管理の疲れがゼロ",
      "hm.circles.why_2_body": "誰も WhatsApp グループの「幹事」になる必要はありません。会場選び、リマインド、集金、すべて私たちが完結させます。",
      "hm.circles.why_3_ttl": "新しい会場、新しい思い出",
      "hm.circles.why_3_body": "オーストラリア屈指のレストランやクイズルーム、ゲームバーを巡りましょう。おなじみの顔ぶれと、新鮮な舞台。日常と刺激の絶妙なバランスです。",
      "hm.circles.bottom_title": "誰も計画を立てることなく、みんなが待ち遠しくなる夜を。",
      "hm.circles.bottom_sub": "一つの Circle。いつもの仲間。毎回新しい会場。",
      "hm.circles.bottom_cta_find": "Circle を始める",
      "hm.circles.bottom_cta_mm": "Matchmaker で新しい出会いを探す →",
      "hm.circles.card_host_pill": "Circle",
      "hm.matchmaker.page_title": "HustleMeet Matchmaker · AIが最適なテーブルをチョイス",
      "hm.matchmaker.kicker": "HustleMeet · Matchmaker",
      "hm.matchmaker.hero_title": "AIがテーブルを選び、あなたは席に着くだけ。",
      "hm.matchmaker.hero_title_accent": "あなたは席に着くだけ。",
      "hm.matchmaker.hero_sub": "性格や価値観に関する簡単な診断に答えてください。Matchmaker が、デザートの頃には旧知の仲のように感じられる4〜7名のメンバーをマッチングします。スワイプも世間話も不要。準備の整ったシェアテーブルがあなたを待っています。",
      "hm.matchmaker.cta_quiz": "マッチング診断を受ける",
      "hm.matchmaker.cta_see_circles": "Circles を見る →",
      "hm.matchmaker.trust_big5": "Big-5 性格スコアリング",
      "hm.matchmaker.trust_seats": "1テーブル4〜8席",
      "hm.matchmaker.trust_lock": "スワイプ・写真選別なし",
      "hm.matchmaker.trust_verified": "認証済みメンバー限定",
      "hm.matchmaker.how_kicker": "仕組み",
      "hm.matchmaker.how_title": "4つのステップで、あなたにぴったりのテーブルへ。",
      "hm.matchmaker.how_step1_ttl": "診断を受ける",
      "hm.matchmaker.how_step1_sub": "性格、価値観、食事制限、予定についての10分間の質問。回答は一度だけでOKです。",
      "hm.matchmaker.how_step2_ttl": "AIによるマッチング",
      "hm.matchmaker.how_step2_sub": "AIがあなたのプロフィールと、開催中のディナー予定を照合。適合率が0.70を超えたら、招待状を作成します。",
      "hm.matchmaker.how_step3_ttl": "招待を受ける・断る",
      "hm.matchmaker.how_step3_sub": "会場、テーブルのテーマ、そしてグループと共有する3つの興味関心が記載された招待メールが届きます。",
      "hm.matchmaker.how_step4_ttl": "使えば使うほど進化",
      "hm.matchmaker.how_step4_sub": "ディナー後のアンケートがモデルをチューニングします。参加を重ねるほど、マッチングの精度は上がります。",
      "hm.matchmaker.science_kicker": "科学的なアプローチ",
      "hm.matchmaker.science_title": "Matchmaker は直感ではなく、スコアで選びます。",
      "hm.matchmaker.science_sub": "すべてのマッチングは、4つの要素を組み合わせた重み付けスコアに基づいています。テーブル全体の平均が0.70を超えた場合のみ招待を送り、それ以下はキューに留まります。",
      "hm.matchmaker.science_1_ttl": "性格 (35%)",
      "hm.matchmaker.science_1_body": "Big-5性格特性（開放性、誠実性、外向性、協調性、情緒安定性）のベクトルにおけるコサイン類似度。",
      "hm.matchmaker.science_2_ttl": "価値観 (25%)",
      "hm.matchmaker.science_2_body": "ディナーの会話を盛り上げる鍵となる価値観（家族、好奇心、成長、奉仕、冒険など）の重なり。",
      "hm.matchmaker.science_3_ttl": "興味関心 (25%)",
      "hm.matchmaker.science_3_body": "具体的な関心事（Six Foot Track のハイキング、映画鑑賞会、コーヒー巡りなど）のジャカード類似度。",
      "hm.matchmaker.science_4_ttl": "予定 (15%)",
      "hm.matchmaker.science_4_body": "実際に参加可能な夜。カレンダーに「火曜夜はジム」とあれば、火曜19時の招待は届きません。",
      "hm.matchmaker.safety_kicker": "設計された安全性",
      "hm.matchmaker.safety_title": "Matchmaker は慎重に機能します。",
      "hm.matchmaker.safety_1_ttl": "信頼性のしきい値",
      "hm.matchmaker.safety_1_body": "スコアが0.70を下回る場合、招待は送られません。不確実なマッチングより、開催しないことを選びます。",
      "hm.matchmaker.safety_2_ttl": "相互ブロックリスト",
      "hm.matchmaker.safety_2_body": "食事後に二人のメンバーが非公開で「No」のサインを出した場合、二度と同じテーブルになることはありません。",
      "hm.matchmaker.safety_3_ttl": "自動停止スイッチ",
      "hm.matchmaker.safety_3_body": "直近のアンケートで20%以上が「2/5以下」の評価をつけた場合、運営による確認が終わるまで招待を一時停止します。",
      "hm.matchmaker.safety_4_ttl": "コミュニティ基準",
      "hm.matchmaker.safety_4_body": "すべてのメンバーは行動規範に同意しています。違反者は異議申し立てなしで除名されます。",
      "hm.matchmaker.safety_5_ttl": "公共の会場限定",
      "hm.matchmaker.safety_5_body": "すべてのディナーは、スタッフのいる実在のレストランで開催されます。個人の自宅ではありません。",
      "hm.matchmaker.safety_6_ttl": "フィードバックの活用",
      "hm.matchmaker.safety_6_body": "非公開のアンケートは次回のマッチングに活用されるほか、会場や他メンバーのモデレーター・レビューのトリガーとなります。",
      "hm.matchmaker.loop_kicker": "学習サイクル",
      "hm.matchmaker.loop_title": "回を重ねるごとに、ディナーはもっと楽しくなる。",
      "hm.matchmaker.loop_body": "Matchmaker は静止したシステムではありません。イベント後、会話の雰囲気や「また一緒に食事をしたいか」、会場の質などについての簡単なアンケートに答えてください。それらの信号がモデルにフィードバックされ、将来のマッチングを形作ります。",
      "hm.matchmaker.loop_1": "「また食事をしたい」という評価がペアリングを強化",
      "hm.matchmaker.loop_2": "お互いの「No」の評価は、静かにペアを解消",
      "hm.matchmaker.loop_3": "会場や会話の評価が座席構成をチューニング",
      "hm.matchmaker.loop_4": "信頼スコアにより、ドタキャンの多いメンバーから保護",
      "hm.matchmaker.loop_5": "学習バイアスが、月を追うごとに良いマッチングを強化",
      "hm.matchmaker.loop_6": "オプトアウトやプロフィールのリセットはいつでも可能",
      "hm.matchmaker.vs_kicker": "どちらが自分に合っていますか？",
      "hm.matchmaker.vs_title": "Matchmaker それとも Circles？",
      "hm.matchmaker.vs_mm_ttl": "変化を楽しみたいならこちら",
      "hm.matchmaker.vs_mm_1": "毎回異なるテーブル、都市、料理",
      "hm.matchmaker.vs_mm_2": "AIが選んだ、相性の良い4〜8名の初対面グループ",
      "hm.matchmaker.vs_mm_3": "毎週のリズム、ホストとしての負担なし",
      "hm.matchmaker.vs_mm_4": "新しい街に来たばかりの人や旅行中の方に最適",
      "hm.matchmaker.vs_mm_cta": "マッチング診断を受ける",
      "hm.matchmaker.vs_circles_ttl": "定番が欲しいならこちら",
      "hm.matchmaker.vs_circles_1": "同じホスト · 同じ会場 · 同じ曜日の夜",
      "hm.matchmaker.vs_circles_2": "水曜の伝統になるような、決まったグループ",
      "hm.matchmaker.vs_circles_3": "経験豊富なホストによる、人間中心のキュレーション",
      "hm.matchmaker.vs_circles_4": "引っ越したばかりで、その地に根を張りたい方に最適",
      "hm.matchmaker.vs_circles_cta": "Circles を探す",
      "hm.matchmaker.bottom_title": "一度の診断。それからのディナーは、すべて自分にぴったりのものに。",
      "hm.matchmaker.bottom_sub": "通常、2週間以内にマッチングされます。今月できる、最も簡単な「自分へのご褒美」です。",
      "hm.matchmaker.bottom_cta": "マッチング診断を受ける",
      "hm.matchmaker.bottom_note": "クレジットカード不要 · 所要時間10分 · いつでも解除可能",
      /* AUTO-TRANSLATED hm.* END */
    },
  },
  ko: {
    translation: {
      /* AUTO-TRANSLATED ko START */
      plaza_nav_features: "기능",
      plaza_nav_rewards: "리워드",
      plaza_nav_how: "이용 방법",
      plaza_nav_roadmap: "로드맵",
      plaza_nav_faq: "자주 묻는 질문",
      plaza_hero_badge: "호주 기업",
      plaza_hero_kicker: "하나의 장바구니. 모든 리워드.",
      plaza_hero_title_1: "호주 제품을 쇼핑하세요.",
      plaza_hero_title_2: "모든 곳에서 적립하세요.",
      plaza_hero_sub: "수천 개의 호주 판매자를 한곳에서. 소비하는 모든 달러마다 HustlePoints를 적립하세요 — 레스토랑, 카페, 호텔, 미용실, 애완동물 미용실, 그리고 HustleSuite 네트워크의 모든 비즈니스에서 사용할 수 있습니다.",
      plaza_cta_start: "무료 가입 — 30초 소요",
      plaza_cta_signin: "로그인",
      plaza_cta_shop: "스토어 둘러보기",
      plaza_hero_stat_1_v: "12,847",
      plaza_hero_stat_1_l: "판매 중인 상품",
      plaza_hero_stat_2_v: "340+",
      plaza_hero_stat_2_l: "호주 판매자",
      plaza_hero_stat_3_v: "$4.2M",
      plaza_hero_stat_3_l: "지급된 리워드",
      plaza_hero_stat_4_v: "19",
      plaza_hero_stat_4_l: "카테고리",
      plaza_problem_kicker: "오늘날 쇼핑의 문제점",
      plaza_problem_title: "10개의 탭. 10개의 비밀번호. 로열티는 제로.",
      plaza_problem_sub: "이미 Amazon, eBay, Kogan, Kmart, Woolies, Coles, Bunnings, JB, Officeworks와 수십 개의 부티크 스토어를 오가고 계십니다. 가격 비교에 몇 시간을 쓰지만 — 영수증 외에는 아무것도 돌려받지 못합니다.",
      plaza_problem_1: "사이트 간 비교할 수 없는 가격",
      plaza_problem_2: "현지인인 척하는 해외 판매자",
      plaza_problem_3: "한 매장에만 갇힌 로열티 포인트",
      plaza_problem_4: "봇과 직송업자의 가짜 리뷰",
      plaza_solution_kicker: "HustlePlaza가 다른 점",
      plaza_solution_title: "하루 종일 열어둘 마지막 장바구니.",
      plaza_solution_sub: "한 번의 검색. 한 개의 장바구니. 한 번의 결제. 모든 구매는 HustlePoints로 돌려받으세요. 이 쇼핑몰뿐만 아니라 전체 생태계에서 사용 가능합니다.",
      plaza_solution_1_t: "한 장바구니, 모든 판매자",
      plaza_solution_1_b: "플랫폼의 모든 호주 판매자에게서 쇼핑하고 결제 시 한 번에 지불하세요. 가능한 경우 배송이 통합됩니다.",
      plaza_solution_2_t: "검증된 로컬 판매자만",
      plaza_solution_2_b: "모든 판매자는 제품을 등록하기 전에 신원 확인을 거칩니다. 봇, 가짜, 불분명한 직송 없음.",
      plaza_solution_3_t: "실제로 이동하는 리워드",
      plaza_solution_3_b: "여기서 적립한 포인트는 모든 HustleSuite 비즈니스 — 카페, 호텔, 미용실, 레스토랑, 애완동물 미용실에서 사용할 수 있습니다.",
      plaza_solution_4_t: "실시간 추적, 한 화면에서",
      plaza_solution_4_b: "모든 판매자의 모든 소포를 한 페이지에서 추적. 매 단계마다 이메일과 SMS로 알림.",
      plaza_rewards_kicker: "HustlePoints 선순환",
      plaza_rewards_title: "쇼핑하며 적립하세요. 네트워크 어디서나 사용하세요.",
      plaza_rewards_sub: "HustlePlaza에서 소비하는 모든 $1마다 HustlePoints를 적립합니다. HustleSuite를 사용하는 모든 레스토랑, 카페, 호텔, 미용실 또는 서비스 비즈니스 — 네트워크 전체 어디서나 사용하세요.",
      plaza_rewards_step_1: "마켓플레이스에서 쇼핑",
      plaza_rewards_step_1_b: "티셔츠부터 냉장고까지 무엇이든 구매하세요.",
      plaza_rewards_step_2: "HustlePoints 즉시 적립",
      plaza_rewards_step_2_b: "주문이 발송되는 순간 포인트가 적립됩니다 — 카드도, 스탬프 카드도, 만료 게임도 없습니다.",
      plaza_rewards_step_3: "네트워크 전체에서 사용",
      plaza_rewards_step_3_b: "카페, 레스토랑, 호텔, 미용실, 애완동물 미용실, 기술자에게서 사용하세요.",
      plaza_rewards_step_4: "또는 현금 인출",
      plaza_rewards_step_4_b: "$50에 도달하면 은행 계좌로 전환하세요.",
      plaza_cats_kicker: "카테고리별로 쇼핑",
      plaza_cats_title: "19개 카테고리. 호주 판매자. 실시간 재고.",
      plaza_cats_sub: "지금 판매 중인 일부를 둘러보세요 — 또는 가입하여 전체 12,000+ 제품 카탈로그를 쇼핑하세요.",
      plaza_cats_cta: "모든 카테고리 보기",
      plaza_vendors_kicker: "진짜 호주 판매자",
      plaza_vendors_title: "제조자, 재배자, 가족 상점을 만나보세요.",
      plaza_vendors_sub: "HustlePlaza의 모든 판매자는 ABN 확인을 거쳤으며, 호주에 기반을 두고 있고, 개인적으로 등록되었습니다. 현지인인 척하는 해외 중개인은 없습니다.",
      plaza_how_kicker: "이용 방법",
      plaza_how_title: "3단계. 한 장바구니. 영원한 리워드.",
      plaza_how_1_title: "30초 만에 가입",
      plaza_how_1_body: "이메일을 인증하고 즉시 둘러보세요. 결제 준비가 되면 전화번호를 추가하세요.",
      plaza_how_2_title: "장바구니 채우기",
      plaza_how_2_body: "모든 판매자에게서 쇼핑하세요. 한 장바구니, 한 배송 보기, 한 번의 결제. Stripe 또는 PayPal을 통한 결제.",
      plaza_how_3_title: "어디서나 적립하고 사용",
      plaza_how_3_body: "주문이 발송되는 순간 HustlePoints가 적립됩니다. 모든 HustleSuite 비즈니스에서 사용하거나 $50에 현금 인출하세요.",
      plaza_compare_kicker: "우리의 비교 우위",
      plaza_compare_title: "HustlePlaza vs 거대 기업들",
      plaza_compare_col_us: "HustlePlaza",
      plaza_compare_col_amz: "Amazon AU",
      plaza_compare_col_ebay: "eBay AU",
      plaza_compare_col_kmart: "Kmart / Big W",
      plaza_compare_row_1: "호주 소유",
      plaza_compare_row_2: "모든 판매자 통합 장바구니",
      plaza_compare_row_3: "이 스토어 밖에서 사용 가능한 리워드",
      plaza_compare_row_4: "리워드 은행 계좌로 인출",
      plaza_compare_row_5: "호주 기반 고객 지원",
      plaza_trust_kicker: "우리가 검증하는 이유",
      plaza_trust_title: "진짜 고객. 진짜 리뷰.",
      plaza_trust_body: "가입 시 이메일을 확인하고, 결제 전 휴대전화 번호를 확인하며, 리뷰 작성, 판매자와의 채팅 또는 고액 구매 전에 실시간 사진 확인이 필요합니다. 사기꾼의 속도를 늦추고 고객에게 낮은 가격을 유지합니다.",
      plaza_trust_1: "가입 시 이메일 인증",
      plaza_trust_2: "결제 시 SMS 인증",
      plaza_trust_3: "리뷰 및 채팅을 위한 실시간 사진 인증",
      plaza_trust_4: "등록 시 판매자 신원 확인",
      plaza_test_kicker: "초기 회원들의 평가",
      plaza_test_title: "실제 고객의 직접적인 평가.",
      plaza_test_1_body: "직장 근처 카페에서 저녁 식사 비용을 HustlePlaza 포인트로 지불했어요. 웨이트리스가 웃으며 '결제 완료됐어요'라고 말할 때까지 정말 작동할 거라고 믿지 못했어요.",
      plaza_test_1_name: "Priya S.",
      plaza_test_2_body: "6개 판매자, 한 번의 배송 시간, 한 통의 확인 이메일. 보통이라면 6개의 비밀번호와 6개의 추적 번호가 필요했을 거예요. 이건 그냥 작동해요.",
      plaza_test_2_name: "Marcus T.",
      plaza_test_3_body: "소규모 꿀 생산자로서 드디어 당일 지급, 진짜 리뷰, 그리고 40% 수수료 없음을 얻었어요. HustlePlaza 덕분에 8개월 만에 '부업'에서 풀타임으로 전환했어요.",
      plaza_test_3_name: "Kate & Dave",
      plaza_faq_kicker: "답변",
      plaza_faq_title: "매주 받는 질문들",
      plaza_faq_1_q: "HustlePlaza는 정말 호주 소유인가요?",
      plaza_faq_1_a: "네. HustlePlaza는 100% 호주 소유이며, 호주 서버에서 호스팅되고, 호주 팀이 운영합니다. 모든 판매자는 첫 제품 등록 전에 신원 확인을 거칩니다.",
      plaza_faq_2_q: "리워드는 어떻게 작동하나요?",
      plaza_faq_2_a: "HustlePlaza에서 소비하는 모든 달러마다 HustlePoints를 적립합니다. 주문이 발송되는 순간 포인트가 적립되며 만료되지 않습니다. HustleSuite를 사용하는 모든 비즈니스에서 사용하거나, 충분히 모으면 은행 계좌로 인출하세요.",
      plaza_faq_3_q: "왜 사진을 인증하나요?",
      plaza_faq_3_a: "읽는 리뷰가 진짜임을 보장하기 위해서입니다. 사진 인증은 휴대전화 카메라를 통한 실시간 셀카 확인입니다 — 은행에서 사용하는 것과 동일한 기술 — 봇, 가짜 계정 및 악의적인 행위자가 플랫폼을 조작하는 것을 방지합니다.",
      plaza_faq_4_q: "주문이 도착하지 않으면 어떻게 되나요?",
      plaza_faq_4_a: "모든 주문은 HustleGuarantee로 보호됩니다: 소포가 도착하지 않거나 설명과 다른 경우, 영업일 기준 3일 이내에 환불해 드리고 판매자를 추적합니다. 손해를 보는 일은 없습니다.",
      plaza_faq_5_q: "카드 정보가 안전한가요?",
      plaza_faq_5_a: "우리는 카드 번호를 보거나 저장하지 않습니다. 모든 결제는 Stripe(PCI-DSS Level 1) 또는 PayPal을 통해 처리되며, 둘 다 도난 카드의 사기 청구로부터 보호하는 3D Secure 인증을 사용합니다.",
      plaza_faq_6_q: "해외 배송이 가능한가요?",
      plaza_faq_6_a: "아직은 아닙니다. HustlePlaza는 출시 시 호주 전용입니다 — 이것이 대부분의 품목에 대해 같은 주 배송과 귀하의 시간대 지원을 약속할 수 있는 방법입니다. 뉴질랜드는 2026년 로드맵에 있습니다.",
      plaza_faq_7_q: "가입 비용은 얼마인가요?",
      plaza_faq_7_a: "무료입니다. 회원 가입은 영구 무료입니다. 판매자는 판매당 소액 수수료를 지불합니다 — 구매자는 가입 $0, 적립 $0, 사용 $0입니다.",
      plaza_faq_8_q: "HustlePlaza에서 판매할 수 있나요?",
      plaza_faq_8_a: "물론입니다. hustlesuite.au로 가서 판매자로 등록하세요 — 검증된 호주 비즈니스는 당일 승인, 그리고 첫 30일은 수수료 면제입니다.",
      plaza_final_kicker: "창립 멤버 가격 — 제한된 좌석",
      plaza_final_title: "최초 멤버가 되어 창립 멤버 가격을 확정하세요.",
      plaza_final_sub: "오늘 HustleSuite 회원으로 등록하세요. 새로운 기능이 출시될 때마다 가장 먼저 알게 되며, 귀하의 계정은 평생 동안 창립 멤버 등급 리워드와 가격을 유지합니다.",
      plaza_final_bonus: "창립 멤버는 창립 멤버 요금을 절대 잃지 않습니다. 영구 무료, 언제든지 취소 가능.",
      plaza_final_cta: "창립 멤버로 등록",
      plaza_final_disclosure: "카드 불필요. 이메일 + 휴대전화 인증만.",
      plaza_roadmap_kicker: "다음에 구축할 것",
      plaza_roadmap_title: "일을 덜어주도록 만들어진 6가지 도구.",
      plaza_roadmap_sub: "HustleSuite는 단순한 쇼핑몰 그 이상입니다 — 완전한 생산성 레이어입니다. 다음 계획입니다.",
      plaza_roadmap_soon: "곧 출시",
      plaza_roadmap_next: "대기 중",
      plaza_roadmap_1_t: "HustleDictate — 모든 양식의 음성-텍스트 변환",
      plaza_roadmap_1_b: "Chrome 확장 프로그램. Alt+M을 누르고 말하면 모든 웹사이트의 모든 입력란이 자동으로 채워집니다. 부동산 신청서, 송장, NDIS 서류 — 절반의 시간에 완료.",
      plaza_roadmap_2_t: "HustleConnect 어디서나 편집",
      plaza_roadmap_2_b: "WordPress 소유자용: 단일 단축키로 모든 페이지에서 라이브 사이트를 편집하세요. wp-admin 왕복 불필요.",
      plaza_roadmap_3_t: "HustleSuite에 저장 범용 버튼",
      plaza_roadmap_3_b: "모든 웹페이지를 마우스 오른쪽 클릭 — 부동산 목록, 기사, 제품, 레스토랑 — 작업할 준비가 된 올바른 모듈에 저장됩니다.",
      plaza_roadmap_4_t: "HustlePlaza 가격 체커",
      plaza_roadmap_4_b: "Amazon, eBay 또는 Kmart를 탐색 — 플로팅 칩이 HustlePlaza의 동일한 제품과 리워드 미리보기를 표시합니다.",
      plaza_roadmap_5_t: "리뷰 수집기",
      plaza_roadmap_5_b: "새로운 Google, Facebook 및 TripAdvisor 리뷰를 HustleSuite 대시보드에 자동 동기화. 주당 약 20분 절약.",
      plaza_roadmap_6_t: "HustleSuite 사이드바",
      plaza_roadmap_6_b: "실시간 주문, KPI, 빠른 게시 및 빠른 송장이 있는 Chrome 사이드 패널. 모든 탭 옆에 있는 스트립에서 전체 비즈니스.",
      plaza_footer_owned: "호주 소유 및 호스팅",
      plaza_footer_terms: "약관",
      plaza_footer_privacy: "개인정보 보호",
      plaza_footer_contact: "문의",
      plaza_footer_vendors: "HustlePlaza에서 판매",
      plaza_footer_help: "고객센터",
      plaza_footer_tagline: "HustleSuite 패밀리의 일부 — 하나의 로그인, 모든 서비스.",
      verify_gate_title: "거의 다 되었습니다 — 간단한 가입",
      verify_gate_body: "HustlePlaza는 이 플랫폼뿐만 아니라 HustlePlaza 플랫폼을 사용하는 다른 비즈니스 또는 HustleSuite에 등록된 다른 비즈니스에서 사용할 수 있는 사용 가능한 리워드를 포함한 많은 혜택을 제공하는 쇼핑 카트입니다. 이러한 비즈니스 중 일부는 레스토랑, 카페, 호텔, 미용실, 심지어 애완동물 미용도 포함됩니다. 훌륭한 가격과 서비스를 계속 제공하기 위해 귀하가 실제 사용자임을 확인하고 인증해야 합니다. 계속하려면 가입 버튼을 누르세요.",
      verify_gate_join: "HustleSuite 가입",
      verify_gate_signin: "이미 계정이 있습니다",
      verify_gate_cancel: "나중에",
      verify_email_title: "이메일 인증",
      verify_email_body: "받은 편지함으로 6자리 코드를 보내드립니다.",
      verify_email_placeholder: "you@example.com",
      verify_email_send: "코드 발송",
      verify_email_code_ph: "6자리 코드",
      verify_email_check: "인증",
      verify_email_sent: "코드 발송됨 — 받은 편지함을 확인하세요.",
      verify_sms_title: "전화번호 인증",
      verify_sms_body: "주문 업데이트와 고액 구매 확인을 위해 문자를 보낼 수 있도록 휴대전화 번호를 추가하세요.",
      verify_sms_placeholder: "+61412345678",
      verify_sms_send: "SMS 발송",
      verify_sms_check: "인증",
      verify_sms_sent: "코드 발송됨 — 휴대전화를 확인하세요.",
      verify_photo_title: "사진 인증",
      verify_photo_body: "휴대전화로 QR 코드를 스캔하여 실시간 셀카를 찍으세요. 채팅, 고액 구매, Meetup / Tackle 게시에 필요합니다.",
      verify_photo_waiting: "휴대전화를 기다리는 중…",
      verify_photo_verified: "사진 인증 완료 — 모든 준비 완료.",
      travel_cta_start: "무료 가입",
      travel_hero_kicker: "HustleTravel",
      travel_hero_title_1: "하나의 멤버십.",
      travel_hero_title_2: "호주 전역 어디서나.",
      travel_hero_sub: "호텔, 항공편, 체험, 로드 트립 계획 및 렌터카 — 최고의 가능 요금으로 예약하고 소비한 모든 달러에 대해 HustlePoints를 받으세요.",
      travel_cta_plan: "로드 트립 계획",
      travel_cta_hotels: "호텔 검색",
      travel_svc_hotels_t: "호텔 & 아파트",
      travel_svc_hotels_b: "한곳에서 200+ 예약 사이트 비교 — Trivago 제공 메타 검색.",
      travel_svc_flights_t: "항공편",
      travel_svc_flights_b: "국내 및 국제, 실시간 좌석 배치도 및 요금 규정 포함.",
      travel_svc_experiences_t: "체험 & 투어",
      travel_svc_experiences_b: "리프 데이, 와인 시음, 라이브 음악, 어린이 친화적 관광.",
      travel_svc_cars_t: "렌터카",
      travel_svc_cars_b: "모든 주요 브랜드와 지역 독립 업체 — 호주 모든 공항에서 픽업.",
      travel_svc_insurance_t: "여행자 보험",
      travel_svc_insurance_b: "APRA 규제 호주 보험사가 인수. 한 화면에서 비교.",
      travel_svc_roadtrip_t: "로드 트립 플래너",
      travel_svc_roadtrip_b: "EV 주행거리 인식 여정과 충전소, 숙박 장소 및 비용 예측.",
      travel_final_kicker: "창립 멤버 가격",
      travel_final_title: "평생 창립 멤버 등급 리워드를 확정하세요.",
      travel_final_sub: "HustleSuite에 한 번 등록하세요. HustleTravel에서 모든 것 — 호텔, 항공편, 체험, 보험 — 을 예약하고 네트워크의 모든 비즈니스에서 사용 가능한 HustlePoints를 적립하세요.",
      travel_final_cta: "창립 멤버로 등록",
      travel_final_disclosure: "최고 요금 정책: 결과는 수수료가 아닌 여행자에게 최고의 가치 순으로 정렬됩니다.",
      dictate_hero_kicker: "HustleDictate",
      dictate_hero_title_1: "말하세요.",
      dictate_hero_title_2: "모든 양식. 모든 웹사이트.",
      dictate_hero_sub: "모든 텍스트 필드를 전문가급 음성 받아쓰기 마이크로 바꾸는 하나의 Chrome 확장 프로그램 — Whisper 제공.",
      dictate_cta_install: "Chrome에 추가",
      dictate_uc_re_t: "부동산 임대 메모",
      dictate_uc_re_b: "검사 보고서와 임차인 메시지를 PM 시스템에 바로 받아쓰세요.",
      dictate_uc_ndis_t: "NDIS 진행 노트",
      dictate_uc_ndis_b: "기억이 생생할 때 교대 노트를 말하세요 — 핸즈프리, 타이핑 불필요.",
      dictate_uc_invoice_t: "송장 설명",
      dictate_uc_invoice_b: "Xero, MYOB 또는 QuickBooks에 바로 항목별 설명을 받아쓰세요.",
      dictate_uc_email_t: "긴 이메일 답변",
      dictate_uc_email_b: "Gmail, Outlook 또는 모든 웹메일에서 답변을 말하세요 — 타이핑보다 3배 빠릅니다.",
      dictate_final_kicker: "창립 멤버 무료",
      dictate_final_title: "이제 모든 키보드가 마이크입니다.",
      dictate_final_sub: "HustleSuite 창립 멤버에게 무료. 그 외 월 $5 — 언제든지 취소 가능.",
      dictate_final_cta: "Chrome에 추가 — 무료",
      "finder.cat.roadtrip": "로드 트립",
      "finder.hotels.title": "근처 호텔 찾기",
      "finder.hotels.sub": "한 번의 검색으로 200+ 예약 사이트 비교 — Trivago 제공.",
      "finder.hotels.where": "장소",
      "finder.hotels.where_ph": "도시 또는 지역 (예: Byron Bay, NSW)",
      "finder.hotels.checkin": "체크인",
      "finder.hotels.checkout": "체크아웃",
      "finder.hotels.adults": "성인",
      "finder.hotels.search": "호텔 검색",
      "finder.hotels.disclosure": "Trivago 열림 — 예약 시 수수료를 받을 수 있습니다.",
      /* AUTO-TRANSLATED ko END */
      welcome_back: "환영합니다",
      sign_out: "로그아웃",
    
      /* AUTO-TRANSLATED finder.* (added) */
      "finder.title": "HustleFinder",
      "finder.tagline": "가장 가까운 공공장소 찾기 — 실제 데이터, 호주 전역.",
      "finder.search_placeholder": "장소, 거리 또는 교외 지역 검색…",
      "finder.hint": "위에 입력하거나, 카테고리를 탭하거나, \"내 근처\"를 사용하세요.",
      "finder.near_me": "내 근처",
      "finder.searching": "검색 중…",
      "finder.suburb.placeholder": "교외 지역으로 필터링 (예: Darwin, NT)…",
      "finder.suburb.clear": "지우기",
      "finder.search_in": "{{cat}}에서 검색",
      "finder.empty": "결과 없음 — 다른 카테고리나 검색어를 시도해보세요.",
      "finder.meta.title": "HustleFinder — 무료 호주 교외 지역 디렉토리",
      "finder.meta.desc": "호주 전역의 모든 교외 지역에서 지역 비즈니스, 충전소, 카페, 화장실 등을 검색하세요 — 100% 무료.",
      "finder.footer.credits": "데이터 크레딧 및 출처",
      "finder.footer.terms": "약관",
      "finder.cat.toilet": "화장실",
      "finder.cat.fountain": "식수대",
      "finder.cat.transport": "교통",
      "finder.cat.park": "공원",
      "finder.cat.medical": "의료",
      "finder.cat.school": "학교",
      "finder.cat.library": "도서관",
      "finder.cat.petrol": "주유소",
      "finder.cat.kidzone": "키즈존",
      "finder.cat.puzzles": "퍼즐",
      /* AUTO-TRANSLATED rt.* START */
      "rt.brand_name": "Roadtrip Planner",
      "rt.page_title": "호주 전기차 로드트립 플래너",
      "rt.from": "출발지",
      "rt.to": "목적지",
      "rt.from_placeholder": "지역명, 마을 또는 도시 (예: Newcastle NSW)",
      "rt.to_placeholder": "지역명, 마을 또는 도시 (예: Hobart TAS)",
      "rt.stop": "STOP",
      "rt.stop_placeholder": "경유지",
      "rt.add_stop": "경유지 추가",
      "rt.upgrade_pro": "Pro로 업그레이드하고 최대 20개 경유지 추가",
      "rt.reverse": "반대로 (집으로 돌아오는 길 계산)",
      "rt.from_current": "현 위치에서 출발",
      "rt.range_per_charge": "1회 충전 주행 거리 (km)",
      "rt.connector": "커넥터",
      "rt.connector_any": "전체",
      "rt.drive_hours_day": "일일 주행 시간",
      "rt.accommodation": "숙소",
      "rt.accom_any": "전체 (BnB · 호텔 · 카라반)",
      "rt.accom_bnb": "BnB",
      "rt.accom_hotel": "호텔",
      "rt.accom_caravan": "카라반 파크",
      "rt.min_rating": "최소 평점",
      "rt.any_rating": "모든 평점",
      "rt.only_5star": "5★만",
      "rt.schedule": "일정:",
      "rt.depart_at": "출발 시간",
      "rt.arrive_by": "도착 예정 시간",
      "rt.hotel_checkin": "호텔 체크인",
      "rt.hotel_checkout": "호텔 체크아웃",
      "rt.overnight_hint": "숙박 구간은 이 시간 외에는 예약되지 않습니다.",
      "rt.whos_in_car": "탑승 인원",
      "rt.adults": "성인",
      "rt.kids": "어린이",
      "rt.ages": "연령",
      "rt.show_attractions": "관광 명소 표시",
      "rt.show_kids": "아이와 가기 좋은 곳 표시",
      "rt.overnight_stays": "숙박",
      "rt.fast_chargers_only": "급속 충전기만",
      "rt.hotel_must_charger": "충전기 있는 호텔만",
      "rt.pet_only": "반려동물 동반 가능 숙소만",
      "rt.safety": "안전 여유분",
      "rt.safety_90": "90% (평지)",
      "rt.safety_85": "85% (기본)",
      "rt.safety_80": "80% (언덕 / 견인)",
      "rt.safety_70": "70% (과적)",
      "rt.save_trip": "여행 저장",
      "rt.plan_my_trip": "여행 계획하기",
      "rt.refresh_trip": "경로 새로고침",
      "rt.planning": "계획 중…",
      "rt.resume_title": "여기서 여행 재개",
      "rt.resume_body": "현재 위치를 파악했습니다. 계기판에 표시된 남은 주행 거리를 입력해 주세요. 완충 상태가 아닌 실제 잔량을 기준으로 다음 구간을 계획합니다.",
      "rt.remaining_range": "남은 주행 거리 (km)",
      "rt.cancel": "취소",
      "rt.replan_from_here": "여기서부터 다시 계획",
      "rt.save_modal_title": "여행 이름 지정",
      "rt.save_modal_body": "저장된 여행은 프로필에서 확인할 수 있습니다. 언제든 다시 열어서 '다시 계획'을 눌러보세요.",
      "rt.save_name_placeholder": "예: 브리즈번 → 시드니 (바이런 경유)",
      "rt.your_saved_trips": "저장된 여행",
      "rt.custom_location": "사용자 지정 위치",
      "rt.trip_summary": "여행 요약",
      "rt.itinerary": "여정",
      "rt.no_stops_needed": "경유 불필요 — 충전 없이 목적지까지 주행 가능합니다.",
      "rt.chargers_in_corridor": "경로상 충전소",
      "rt.planned_stops": "계획된 경유지",
      "rt.overnight_stop": "숙박지",
      "rt.overnight_stops": "숙박지",
      "rt.suggested_detours": "추천 우회 경로 — 선택하여 추가",
      "rt.replan_with_detours": "우회 경로를 포함해 다시 계획",
      "rt.attractions_label": "명소",
      "rt.kids_label": "어린이",
      "rt.km_from_start": "출발지로부터의 거리 (km)",
      "rt.open_driving": "주행 모드 열기",
      "rt.big_font_hint": "큰 글씨, 거치대 최적화. CarPlay/Android Auto 브라우저 미러링을 지원합니다.",
      "rt.check_price_book": "Trivago에서 가격 확인 및 예약 ↗",
      "rt.visit_site": "사이트 방문 ↗",
      "rt.charger_onsite": "숙소 내 충전기 있음",
      "rt.leg_exceeds_range": "이 구간은 설정된 주행 거리를 초과합니다. 충전소에 도착하기 전에 배터리가 방전될 수 있습니다. 주행 거리 설정을 줄이거나 중간 충전 지점을 추가하세요.",
      "rt.add_waypoint_replan": "경유지로 추가하고 다시 계획",
      /* AUTO-TRANSLATED rt.* END */
                      /* AUTO-TRANSLATED hm.* START */
      "hm.nav.hustlemeet": "HustleMeet",
      "hm.nav.circles": "Circles",
      "hm.nav.matchmaker": "Matchmaker",
      "hm.nav.meet": "Meet",
      "hm.nav.overview": "HustleMeetups",
      "hm.overview.page_title": "HustleMeetups — 나만의 사람들을 찾는 따뜻한 세 가지 방법.",
      "hm.overview.kicker": "HustleSuite의 서비스",
      "hm.overview.hero_title": "나만의 사람들을 찾는 따뜻한 세 가지 방법.",
      "hm.overview.hero_title_accent": "나만의 사람들",
      "hm.overview.hero_sub": "HustleMeetups는 Australia에서 진정한 우정을 쌓을 수 있는 가장 다정한 방법입니다. 당신에게 맞는 분위기를 선택하세요. 매주 진행되는 러닝 모임, 기존 멤버들과의 월간 저녁 식사, 또는 AI가 매칭해 주는 새로운 얼굴들과의 식사 자리까지, 당신을 위한 자리가 준비되어 있습니다.",
      "hm.overview.tile_meet_kicker": "HustleMeet",
      "hm.overview.tile_meet_title": "나만의 사람들을 찾고, 함께 활동하세요.",
      "hm.overview.tile_meet_body": "Australia의 관심사 중심 그룹을 위한 커뮤니티입니다. 러너, 개발자, 투자자, 산책가, 댄서, 조찬 클럽, 독서 모임 등 수백 개의 그룹이 매주 실제 이벤트를 엽니다. 한 번만 참여해도 좋고, 매주 참여해도 좋습니다.",
      "hm.overview.tile_meet_cta": "그룹 둘러보기",
      "hm.overview.tile_circles_kicker": "Circles",
      "hm.overview.tile_circles_title": "우리만의 크루. 매번 새로운 테이블.",
      "hm.overview.tile_circles_body": "평소 함께하는 친구들을 데려오세요. 저희가 새로운 식당을 예약하고, 트라비아를 계획하고, 즐길 거리를 준비합니다. 당신은 그저 몸만 오세요. 누군가 따로 고생해서 예약할 필요 없이 기존 크루와 돈독한 관계를 유지하는 가장 스마트한 방법입니다.",
      "hm.overview.tile_circles_cta": "Circle 시작하기",
      "hm.overview.tile_matchmaker_kicker": "Matchmaker",
      "hm.overview.tile_matchmaker_title": "AI가 엄선한 테이블. 당신은 참석만 하세요.",
      "hm.overview.tile_matchmaker_body": "10분 분량의 퀴즈에 참여하세요. 저희가 하룻밤 사이에 디저트를 먹을 때쯤이면 오랜 친구처럼 느껴질 낯선 이들과의 식사 자리를 마련해 드립니다. 매번 새로운 만남이 기다립니다.",
      "hm.overview.tile_matchmaker_cta": "퀴즈 참여하기",
      "hm.overview.trust_kicker": "신뢰할 수 있는 이유",
      "hm.overview.trust_title": "신뢰가 먼저, 테이블은 그다음.",
      "hm.overview.trust_1_title": "인증된 회원",
      "hm.overview.trust_1_body": "모든 그룹은 실제 신원 확인과 신뢰 점수를 바탕으로 시작됩니다. 일회용 계정은 허용되지 않습니다.",
      "hm.overview.trust_2_title": "진정성 있는 호스트",
      "hm.overview.trust_2_body": "모든 그룹은 분위기를 책임지고 리듬을 조율하며 현장을 세심하게 살피는 검증된 호스트가 이끕니다.",
      "hm.overview.trust_3_title": "우정 우선",
      "hm.overview.trust_3_body": "이곳의 모든 서비스는 데이트가 아닌 진정한 우정을 위해 설계되었습니다. 언제나 그렇습니다.",
      "hm.overview.trust_4_title": "무관용 원칙의 운영",
      "hm.overview.trust_4_body": "한 번의 신고, 한 번의 검토, 한 번의 결정. 부적절한 사용자는 당일 밤 즉시 퇴출됩니다.",
      "hm.overview.footer_line": "HustleSuite의 서비스 — Australia 자본으로 운영 및 호스팅됩니다.",
      "hm.landing.page_title": "HustleMeet · 나만의 사람들을 찾고, 함께 활동하세요.",
      "hm.landing.kicker": "Australia의 관심사 네트워크",
      "hm.landing.hero_title": "나만의 사람들을 찾고, 함께 활동하세요.",
      "hm.landing.hero_title_accent": "나만의 사람들",
      "hm.landing.hero_sub": "HustleMeet는 Australia의 관심사 중심 그룹을 위한 공간입니다. 점심을 먹으며 디버깅하는 소프트웨어 개발자들, 새벽 6시의 장거리 러너들, 매달 만나는 투자자들. 산책 그룹, 댄스 크루, 조찬 클럽, 보드게임 밤까지. 그룹을 선택하고 다음 이벤트에 예약한 뒤, 그저 참석하세요.",
      "hm.landing.cta_explore_circles": "그룹 둘러보기",
      "hm.landing.cta_try_matchmaker": "직접 그룹 만들기",
      "hm.landing.trust_verified": "인증된 회원",
      "hm.landing.trust_venues": "검증된 이벤트 호스트",
      "hm.landing.trust_friendship": "우정 우선 — 데이트 목적 금지",
      "hm.landing.trust_moderation": "무관용 원칙의 운영",
      "hm.landing.subbrand_circles_kicker": "Circles",
      "hm.landing.subbrand_circles_title": "이미 마음 맞는 크루가 있나요? 그들에게 아지트를 선물하세요.",
      "hm.landing.subbrand_circles_body": "Circles는 독서 모임, 포커 밤, 러닝 크루, 직장 동료 등 이미 정기적으로 만나는 친구들을 위한 서비스입니다. 장소 섭외, 리마인더, 명단 관리는 저희가 맡겠습니다. 당신은 즐기기만 하세요.",
      "hm.landing.subbrand_circles_b1": "매번 바뀌는 장소, 변함없는 멤버 구성",
      "hm.landing.subbrand_circles_b2": "자동 리마인더 + 간편한 RSVP",
      "hm.landing.subbrand_circles_b3": "트라비아, 카드 게임 및 테마 밤 준비 완료",
      "hm.landing.subbrand_circles_cta": "Circle 시작하기",
      "hm.landing.subbrand_matchmaker_kicker": "Matchmaker",
      "hm.landing.subbrand_matchmaker_title": "AI가 엄선한 테이블. 당신은 참석만 하세요.",
      "hm.landing.subbrand_matchmaker_body": "성격, 가치관, 일정에 관한 10분 퀴즈 한 번이면 충분합니다. Matchmaker가 디저트를 먹을 때쯤이면 오랜 친구처럼 느껴질 낯선 이들과의 자리를 마련합니다. 매번 새로운 사람들을 만나보세요.",
      "hm.landing.subbrand_matchmaker_b1": "Big-5 성격 분석 및 가치관 점수화",
      "hm.landing.subbrand_matchmaker_b2": "매칭 점수 0.70 이상일 때 전송되는 초대장",
      "hm.landing.subbrand_matchmaker_b3": "식사를 거듭할수록 정교해지는 매칭 시스템",
      "hm.landing.subbrand_matchmaker_cta": "퀴즈 참여하기",
      "hm.landing.how_kicker": "HustleMeet 이용 방법",
      "hm.landing.how_title": "네 단계면 충분합니다. 그룹을 찾고, 참석하세요.",
      "hm.landing.how_step1_ttl": "관심사 선택",
      "hm.landing.how_step1_sub": "러닝, 코딩, 투자, 요리, 음악, 산책 등 당신의 관심사와 거주 지역을 알려주세요.",
      "hm.landing.how_step2_ttl": "근처 그룹 둘러보기",
      "hm.landing.how_step2_sub": "모든 그룹은 호스트, 운영 방식, 다음 이벤트 일정을 공개합니다. 참여하기 전에 분위기를 미리 확인하세요.",
      "hm.landing.how_step3_ttl": "다음 이벤트 RSVP",
      "hm.landing.how_step3_sub": "클릭 한 번으로 캘린더 등록과 전날 리마인더까지 완료됩니다. 일부는 유료이며, 가격은 투명하게 공개됩니다.",
      "hm.landing.how_step4_ttl": "참석 및 연결",
      "hm.landing.how_step4_sub": "호스트와 크루를 만나 관심 활동을 즐기세요. 마음이 맞는다면 다음 주에 또 만나요.",
      "hm.landing.stats_kicker": "Australia의 새로운 만남 방식",
      "hm.landing.stats_title": "사람들이 계속 다시 찾는 이유.",
      "hm.landing.stats_92_sub": "이벤트에서 최소 한 명과는 마음이 통했다고 느낍니다",
      "hm.landing.stats_68_sub": "일주일 이내에 다음 이벤트에 다시 RSVP를 보냅니다",
      "hm.landing.stats_85_sub": "그룹에서 만난 사람과 계속 연락을 이어갑니다",
      "hm.landing.why_kicker": "작동 원리",
      "hm.landing.why_title": "진짜 관심사. 진짜 호스트. 진짜 이벤트.",
      "hm.landing.why_1_ttl": "지역보다 관심사 우선",
      "hm.landing.why_1_body": "그룹원들이 내가 정말 아끼는 주제를 공유한다는 것, 그것이 진정한 우정으로 가는 지름길입니다.",
      "hm.landing.why_2_ttl": "진정성 있는 호스트",
      "hm.landing.why_2_body": "모든 그룹에는 분위기를 관리하고 리듬을 조율하며 문제가 생기면 중재하는 검증된 호스트가 있습니다. 단순한 온라인 관리자가 아닌 실제 사람입니다.",
      "hm.landing.why_3_ttl": "꾸준함의 가치",
      "hm.landing.why_3_body": "매주 새로운 이벤트가 열립니다. 기다려지는 무언가, 낯선 이를 화요일 밤의 소중한 크루로 만들어주는 리듬이 있습니다.",
      "hm.landing.why_4_ttl": "안전과 보안",
      "hm.landing.why_4_body": "인증된 회원, 공개된 장소, 무례함에 대한 무관용 원칙. 지원 팀은 모든 이벤트 전후와 진행 중에 대기하고 있습니다.",
      "hm.landing.why_5_ttl": "우정 우선",
      "hm.landing.why_5_body": "이것은 데이트 서비스가 아닙니다. 커뮤니티입니다. 진짜 관심사와 진짜 사람들 사이에서 스와이프는 필요 없습니다.",
      "hm.landing.why_6_ttl": "의미 있는 소규모 모임",
      "hm.landing.why_6_body": "대부분의 그룹은 이벤트를 4~15석으로 제한합니다. 깊은 대화를 나누기에 충분히 크면서도, 아무도 소외되지 않을 만큼 작은 규모입니다.",
      "hm.landing.cities_kicker": "운영 지역",
      "hm.landing.cities_title": "Australia 및 NZ 전역에서 열리는 모임.",
      "hm.landing.cities_sub_zero": "15개 이상의 도시에서 첫 그룹들이 열리고 있습니다 — 일찍 합류하여 분위기를 직접 만들어보세요.",
      "hm.landing.cities_sub_some": "15개 이상의 주요 도시에서 그룹이 운영 중입니다. 이번 주, 당신의 도시에서도 무언가 일어나고 있습니다.",
      "hm.landing.cities_sub_one": "{{count}}개의 활성 그룹 · 매주 열리는 새로운 이벤트 · 지속적으로 도시 확장 중.",
      "hm.landing.cities_sub_many": "{{count}}개의 활성 그룹 · 매주 열리는 새로운 이벤트 · 지속적으로 도시 확장 중.",
      "hm.landing.forme_kicker": "이런 분들을 위한 서비스입니다…",
      "hm.landing.forme_title": "진짜 관심사, 진짜 사람들, 진짜 이벤트를 원한다면.",
      "hm.landing.forme_1": "스와이프 앱에 지쳐, 내가 좋아하는 일을 함께할 사람들을 실제로 만나고 싶은 분.",
      "hm.landing.forme_2": "새로운 도시로 이사 와서 단순한 채팅방이 아닌 따뜻한 크루가 필요한 분.",
      "hm.landing.forme_3": "넓고 얕은 관계보다 깊이 있는 연결을 가치 있게 생각하는 분.",
      "hm.landing.forme_4": "공유된 관심사와 오프라인에서의 2시간이 진정한 관계의 치트키라고 믿는 분.",
      "hm.landing.notme_kicker": "이런 분들에게는 맞지 않을 수 있습니다…",
      "hm.landing.notme_title": "HustleMeet가 모든 사람에게 맞는 것은 아닙니다.",
      "hm.landing.notme_1": "참석 약속을 지키기 어려운 분 — 호스트와 멤버들은 당신의 RSVP를 신뢰합니다.",
      "hm.landing.notme_2": "데이트 서비스를 찾는 분 — 이곳에서는 우정이 최우선입니다.",
      "hm.landing.notme_3": "직접 만나지 않고 온라인 활동만 하고 싶은 분 — 오프라인 만남이 핵심입니다.",
      "hm.landing.notme_4": "모든 것이 무료이길 바라는 분 — 일부 이벤트는 비용이 발생하며, 가격은 항상 사전에 공개됩니다.",
      "hm.landing.safety_kicker": "우리의 안전 약속",
      "hm.landing.safety_title": "따뜻하지만, 결코 소홀하지 않게.",
      "hm.landing.safety_1_ttl": "공공 장소",
      "hm.landing.safety_1_body": "모든 이벤트는 카페, 레스토랑, 공원, 체육관 또는 스튜디오와 같이 직원이 있는 공개된 장소에서 열립니다. 모든 참석자가 동의하지 않는 한 개인의 집에서는 열리지 않습니다.",
      "hm.landing.safety_2_ttl": "커뮤니티 표준",
      "hm.landing.safety_2_body": "무례함, 괴롭힘, 부정직함은 절대 용납되지 않습니다. 규정을 위반하는 회원은 즉시 퇴출됩니다.",
      "hm.landing.safety_3_ttl": "개인정보 보호",
      "hm.landing.safety_3_body": "귀하의 상세 정보는 절대 공개적으로 공유되지 않습니다. 이벤트 종료 후 연락을 유지할 대상을 직접 제어할 수 있습니다.",
      "hm.landing.safety_4_ttl": "상시 지원 체계",
      "hm.landing.safety_4_body": "질문이나 우려 사항이 있는 경우, 이벤트 전후와 진행 중에 저희 팀의 도움을 받을 수 있습니다.",
      "hm.landing.bottom_title": "클릭 한 번으로 다음번 가장 좋아하는 그룹을 만나보세요.",
      "hm.landing.bottom_sub": "원하는 방식을 선택하세요 — 기존 그룹에 합류하거나, 친구들과 Circle을 만들거나, Matchmaker를 통해 나에게 꼭 맞는 테이블에 앉아보세요.",
      "hm.landing.bottom_cta_circles": "Circle 시작하기",
      "hm.landing.bottom_cta_mm": "Matchmaker 퀴즈 참여하기",
      "hm.circles.page_title": "HustleMeet Circles · 우리만의 크루, 매번 새로운 장소",
      "hm.circles.kicker": "HustleMeet · Circles",
      "hm.circles.hero_title": "우리만의 크루. 매번 새로운 테이블.",
      "hm.circles.hero_title_accent": "우리만의 크루",
      "hm.circles.hero_sub": "이미 친구들이 있나요? Circles는 그들과 돈독한 관계를 유지하는 가장 쉬운 방법입니다. 저희가 장소를 예약하고, 트라비아를 계획하고, 즐길 거리를 준비합니다. 당신은 그저 오기만 하세요 — 같은 멤버, 새로운 밤, 새로운 장소가 기다립니다.",
      "hm.circles.cta_find": "Circle 시작하기",
      "hm.circles.cta_host_hero": "열린 Circle 참여하기",
      "hm.circles.trust_venue": "검증된 장소 순환 예약",
      "hm.circles.trust_party": "Circle당 4~12명의 친구",
      "hm.circles.trust_verified": "인증된 회원",
      "hm.circles.trust_friendship": "돈독하게 유지되는 실제 우정",
      "hm.circles.how_kicker": "이용 방법",
      "hm.circles.how_title": "네 단계면 충분합니다. 우리 크루는 영원하니까요.",
      "hm.circles.how_step1_ttl": "Circle 시작하기",
      "hm.circles.how_step1_sub": "이름을 짓고, 친구들(최대 12명)을 초대하고, 주기(매주, 격주 또는 매달)를 선택하세요.",
      "hm.circles.how_step2_ttl": "장소는 저희가 정합니다",
      "hm.circles.how_step2_sub": "매번 새로운 장소를 제안합니다 — 레스토랑, 트라비아 밤, 카드 룸, 와인 바 등. 물론 좋아하는 곳을 고정할 수도 있습니다.",
      "hm.circles.how_step3_ttl": "클릭 한 번으로 RSVP 완료",
      "hm.circles.how_step3_sub": "자동 리마인더, 비용 정산, 예약 결제 시스템으로 불참자로 인해 계획이 틀어질 걱정이 없습니다. 전채 요리가 나오기 전에 정산이 완료됩니다.",
      "hm.circles.how_step4_ttl": "리듬 유지하기",
      "hm.circles.how_step4_sub": "더 이상 누군가 번거롭게 계획할 필요 없는 정기적인 모임이 생깁니다. 시간이 흐를수록 그룹의 유대는 더욱 깊어집니다.",
      "hm.circles.cadence_weekly_ttl": "매주",
      "hm.circles.cadence_weekly_sub": "매주 한 번",
      "hm.circles.cadence_weekly_body": "화요일 밤의 의식을 원하는 크루들을 위해 — 같은 얼굴들, 매번 새로운 장소.",
      "hm.circles.cadence_fortnight_ttl": "격주",
      "hm.circles.cadence_fortnight_sub": "2주마다",
      "hm.circles.cadence_fortnight_body": "바쁜 일상 속에서도 부담 없이 정기적으로 만날 수 있는 가장 이상적인 주기입니다.",
      "hm.circles.cadence_monthly_ttl": "매달",
      "hm.circles.cadence_monthly_sub": "한 달에 한 번",
      "hm.circles.cadence_monthly_body": "특별한 모임을 위한 Circles — 일요일 로스트 치킨, 대규모 트라비아, 코스 요리, 포커 밤 등.",
      "hm.circles.feast_kicker": "당신의 밤을 선택하세요",
      "hm.circles.feast_title": "세 가지 즐거운 방식",
      "hm.circles.feast_sub": "모든 Circle은 밤을 어떻게 보낼지 결정합니다. 돌아가며 시도하거나, 크루가 가장 좋아하는 방식을 고정하세요.",
      "hm.circles.feast_surprise_ttl": "새로운 식당 탐방",
      "hm.circles.feast_surprise_body": "근처의 식당 세 곳을 추천하고 투표로 결정합니다. 주방은 미리 준비하고, 비용은 예약 시 자동 정산됩니다.",
      "hm.circles.feast_preselect_ttl": "트라비아 / 카드 / 게임",
      "hm.circles.feast_preselect_body": "트라비아 밤, 포커 룸, 보드게임 바 또는 볼링 레인을 예약하세요. 장소 섭외와 참가비, 리마인더까지 모두 처리합니다.",
      "hm.circles.feast_per_event_ttl": "특별한 테마 밤",
      "hm.circles.feast_per_event_body": "와인 테이스팅, 셰프의 테이블, 칵테일 클래스, 방탈출 등 가끔씩 즐기는 특별한 이벤트가 완벽하게 준비됩니다.",
      "hm.circles.live_kicker": "지금 바로",
      "hm.circles.live_title": "참여 가능한 Circles",
      "hm.circles.live_host_link": "직접 만들기",
      "hm.circles.live_loading": "불러오는 중…",
      "hm.circles.live_empty_title": "당신의 도시에서 첫 번째 Circle을 시작해보세요.",
      "hm.circles.live_empty_sub": "친구 세 명과 함께 시작하면 나머지 멤버 초대를 도와드립니다. 첫 세 곳의 장소 혜택도 드립니다.",
      "hm.circles.live_empty_cta": "Circle 시작하기",
      "hm.circles.host_kicker": "Circle 리더를 위한 기능",
      "hm.circles.host_title": "친구들만 데려오세요. 행정 업무는 저희가 합니다.",
      "hm.circles.host_body": "항상 모임을 주도하는 친구라면, Circles가 시간을 획기적으로 아껴 드립니다. 자동 리마인더, 예약 결제, 장소 추천, 정산, 대기자 관리까지. 모든 모임에서 캐시백 혜택도 누릴 수 있습니다. 당신은 그저 멤버로서 즐기기만 하면 됩니다.",
      "hm.circles.host_perk_1": "자동 장소 추천 — 더 이상 Google을 뒤질 필요가 없습니다",
      "hm.circles.host_perk_2": "예약 결제 좌석 — 노쇼 걱정 제로",
      "hm.circles.host_perk_3": "디저트 먹기 전 완료되는 비용 정산",
      "hm.circles.host_perk_4": "자동 리마인더 및 캘린더 초대",
      "hm.circles.host_perk_5": "취소 발생 시 자동 승계되는 대기 명단",
      "hm.circles.host_perk_6": "모임마다 쌓이는 캐시백 지갑",
      "hm.circles.host_cta": "지금 Circle 시작하기",
      "hm.circles.why_kicker": "Circles가 유지되는 이유",
      "hm.circles.why_title": "성인의 우정에는 인프라가 필요합니다",
      "hm.circles.why_1_ttl": "같은 크루, 깊어지는 신뢰",
      "hm.circles.why_1_body": "삶은 때로 친구들을 멀어지게 합니다. Circles는 어떤 상황에서도 같은 멤버 4~12명이 같은 요일에 만나는 고정된 리듬을 만들어줍니다.",
      "hm.circles.why_2_ttl": "행정 피로도 제로",
      "hm.circles.why_2_body": "누구도 단톡방에서 총대를 멜 필요가 없습니다. 저희가 장소를 정하고, 알림을 보내고, 결제를 관리하며 모든 과정을 마무리합니다.",
      "hm.circles.why_3_ttl": "새로운 장소, 새로운 추억",
      "hm.circles.why_3_body": "Australia 최고의 레스토랑, 트라비아 룸, 게임 바를 순환합니다. 익숙한 얼굴들과 새로운 배경 — 일상과 새로움이 공존하는 완벽한 조합입니다.",
      "hm.circles.bottom_title": "누구도 계획할 필요 없는, 우리 크루가 손꼽아 기다리는 밤.",
      "hm.circles.bottom_sub": "하나의 Circle. 같은 친구들. 매번 새로운 장소.",
      "hm.circles.bottom_cta_find": "Circle 시작하기",
      "hm.circles.bottom_cta_mm": "Matchmaker로 새로운 사람 만나기 →",
      "hm.circles.card_host_pill": "Circle",
      "hm.matchmaker.page_title": "HustleMeet Matchmaker · AI가 엄선한 최적의 테이블",
      "hm.matchmaker.kicker": "HustleMeet · Matchmaker",
      "hm.matchmaker.hero_title": "AI가 엄선한 테이블. 당신은 참석만 하세요.",
      "hm.matchmaker.hero_title_accent": "당신은 참석만 하세요.",
      "hm.matchmaker.hero_sub": "성격과 가치관에 관한 간단한 퀴즈에 응답하세요. Matchmaker가 디저트를 먹을 때쯤이면 오랜 친구처럼 느껴질 4~7명의 낯선 이들과 자리를 만들어 드립니다. 스와이프도, 어색한 스몰 토크도 없습니다. 그저 준비된 테이블에 앉아 즐기세요.",
      "hm.matchmaker.cta_quiz": "매칭 퀴즈 시작하기",
      "hm.matchmaker.cta_see_circles": "Circles 둘러보기 →",
      "hm.matchmaker.trust_big5": "Big-5 성격 점수화",
      "hm.matchmaker.trust_seats": "테이블당 4~8석",
      "hm.matchmaker.trust_lock": "스와이프 없음 · 사진 중심 아님",
      "hm.matchmaker.trust_verified": "인증된 회원 전용",
      "hm.matchmaker.how_kicker": "이용 방법",
      "hm.matchmaker.how_title": "네 단계면 나에게 꼭 맞는 테이블이 완성됩니다.",
      "hm.matchmaker.how_step1_ttl": "퀴즈 참여",
      "hm.matchmaker.how_step1_sub": "성격, 가치관, 식단, 시간대 등 10분 정도 소요되는 설문에 참여하세요. 한 번만 하면 됩니다.",
      "hm.matchmaker.how_step2_ttl": "밤사이 진행되는 매칭",
      "hm.matchmaker.how_step2_sub": "AI가 당신의 프로필과 Australia의 실시간 저녁 식사 리스트를 분석합니다. 적합도 점수가 0.70 이상일 때 개인 초대장이 생성됩니다.",
      "hm.matchmaker.how_step3_ttl": "수락 또는 거절",
      "hm.matchmaker.how_step3_sub": "장소, 테이블 테마, 그리고 그룹원과 공유하는 세 가지 공통 관심사가 담긴 초대 메일을 받게 됩니다.",
      "hm.matchmaker.how_step4_ttl": "지속적인 학습",
      "hm.matchmaker.how_step4_sub": "식사 후 설문조사 결과가 모델을 미세 조정합니다. 식사를 거듭할수록 다음 매칭은 더욱 정교해집니다.",
      "hm.matchmaker.science_kicker": "과학적 원리",
      "hm.matchmaker.science_title": "Matchmaker는 추측하지 않고 점수화합니다.",
      "hm.matchmaker.science_sub": "모든 매칭은 네 가지 요소의 가중 합산으로 결정됩니다. 테이블 전체 평균이 0.70을 넘을 때만 초대장을 발송합니다. 기준 미달인 경우 대기열에 머뭅니다.",
      "hm.matchmaker.science_1_ttl": "성격 (35%)",
      "hm.matchmaker.science_1_body": "Big-5 성격 벡터(개방성, 성실성, 외향성, 우호성, 정서적 안정성) 전반의 코사인 유사도를 분석합니다.",
      "hm.matchmaker.science_2_ttl": "가치관 (25%)",
      "hm.matchmaker.science_2_body": "식사 자리의 대화를 풍성하게 만드는 핵심 가치관(가족, 호기심, 성장, 헌신, 모험 등)의 중첩도를 확인합니다.",
      "hm.matchmaker.science_3_ttl": "관심사 (25%)",
      "hm.matchmaker.science_3_body": "Six Foot Track 하이킹, Sydney 영화 클럽, Melbourne 커피 문화 등 구체적인 관심사의 자카드 지수(Jaccard overlap)를 계산합니다.",
      "hm.matchmaker.science_4_ttl": "일정 (15%)",
      "hm.matchmaker.science_4_body": "실제로 참석 가능한 밤을 고려합니다. 화요일 저녁이 체육관 가는 시간이라면 화요일 7시 초대장은 보내지 않습니다.",
      "hm.matchmaker.safety_kicker": "설계된 안전",
      "hm.matchmaker.safety_title": "Matchmaker는 신중하고 세심합니다.",
      "hm.matchmaker.safety_1_ttl": "신뢰도 임계값",
      "hm.matchmaker.safety_1_body": "그룹 점수 0.70 미만인 경우 초대장을 발송하지 않습니다. 어설픈 만남보다는 식사가 없는 편이 낫습니다.",
      "hm.matchmaker.safety_2_ttl": "상호 거부 차단 시스템",
      "hm.matchmaker.safety_2_body": "식사 후 두 멤버가 비공개로 '다시 만나지 않음'을 선택하면, 다시는 같은 자리에 매칭되지 않습니다.",
      "hm.matchmaker.safety_3_ttl": "자동 중단 스위치",
      "hm.matchmaker.safety_3_body": "최근 설문 중 20% 이상이 테이블 평점을 2/5 이하로 매길 경우, 운영자가 검토하는 24시간 동안 초대가 일시 중단됩니다.",
      "hm.matchmaker.safety_4_ttl": "커뮤니티 표준",
      "hm.matchmaker.safety_4_body": "모든 회원은 명확한 행동 강령에 동의해야 합니다. 이를 위반할 경우 재고 없이 즉시 퇴출됩니다.",
      "hm.matchmaker.safety_5_ttl": "공개된 장소 전용",
      "hm.matchmaker.safety_5_body": "모든 Matchmaker 저녁 식사는 직원이 있는 실제 레스토랑에서 진행됩니다. 개인의 집은 절대 사용하지 않습니다.",
      "hm.matchmaker.safety_6_ttl": "이벤트 후 피드백",
      "hm.matchmaker.safety_6_body": "비공개 설문은 미래의 매칭을 결정하며, 필요한 경우 장소나 타인에 대한 운영자 검토를 요청할 수 있습니다.",
      "hm.matchmaker.loop_kicker": "학습 루프",
      "hm.matchmaker.loop_title": "한 번의 식사가 다음 식사를 더 즐겁게 만듭니다.",
      "hm.matchmaker.loop_body": "Matchmaker는 고정되어 있지 않습니다. 이벤트 후 대화의 느낌, 다시 만나고 싶은 멤버, 장소의 만족도 등에 대한 짧은 설문을 제출하면, 이러한 신호들이 모델에 반영되어 다음 매칭을 더욱 정교하게 만듭니다.",
      "hm.matchmaker.loop_1": "동료의 \"다시 식사하고 싶음\" 투표가 매칭 유대를 강화합니다",
      "hm.matchmaker.loop_2": "상호 거부 투표는 해당 페어 매칭을 조용히 중단시킵니다",
      "hm.matchmaker.loop_3": "장소 및 대화 평점이 좌석 배합을 튜닝합니다",
      "hm.matchmaker.loop_4": "신뢰 점수가 반복적인 노쇼로부터 멤버들을 보호합니다",
      "hm.matchmaker.loop_5": "학습 편향이 시간이 지남에 따라 좋은 매칭을 더욱 강화합니다",
      "hm.matchmaker.loop_6": "언제든지 프로필을 재설정하거나 참여를 중단할 수 있습니다",
      "hm.matchmaker.vs_kicker": "나에게 맞는 서비스는?",
      "hm.matchmaker.vs_title": "Matchmaker 또는 Circles?",
      "hm.matchmaker.vs_mm_ttl": "다양한 만남을 원한다면 선택하세요.",
      "hm.matchmaker.vs_mm_1": "매번 다른 테이블, 도시, 요리를 경험",
      "hm.matchmaker.vs_mm_2": "AI가 큐레이션한 4~8명의 잘 맞는 낯선 이들과의 만남",
      "hm.matchmaker.vs_mm_3": "호스트에 대한 부담 없는 매주 정기적인 리듬",
      "hm.matchmaker.vs_mm_4": "새로운 도시에 왔거나 여행 중일 때 최적",
      "hm.matchmaker.vs_mm_cta": "매칭 퀴즈 시작하기",
      "hm.matchmaker.vs_circles_ttl": "정기적인 모임을 원한다면 선택하세요.",
      "hm.matchmaker.vs_circles_1": "같은 호스트 · 같은 장소 · 같은 요일",
      "hm.matchmaker.vs_circles_2": "수요일의 전통이 되어줄 든든한 그룹",
      "hm.matchmaker.vs_circles_3": "숙련된 호스트에 의한 세심한 운영",
      "hm.matchmaker.vs_circles_4": "새로운 곳에 정착하여 깊은 유대를 쌓고 싶을 때 최적",
      "hm.matchmaker.vs_circles_cta": "Circles 둘러보기",
      "hm.matchmaker.bottom_title": "퀴즈 한 번이면, 그 뒤의 모든 식사는 완벽해집니다.",
      "hm.matchmaker.bottom_sub": "2주 이내에 첫 매칭이 이루어집니다. 이번 달 당신이 할 수 있는 가장 쉬운 선택이 될 것입니다.",
      "hm.matchmaker.bottom_cta": "매칭 퀴즈 시작하기",
      "hm.matchmaker.bottom_note": "신용카드 필요 없음 · 10분 소요 · 언제든 매칭 중단 가능",
      /* AUTO-TRANSLATED hm.* END */
    },
  },
  "zh-CN": {
    translation: {
      /* AUTO-TRANSLATED zh START */
      plaza_nav_features: "功能特色",
      plaza_nav_rewards: "奖励计划",
      plaza_nav_how: "运作方式",
      plaza_nav_roadmap: "路线图",
      plaza_nav_faq: "常见问题",
      plaza_hero_badge: "澳大利亚本土企业",
      plaza_hero_kicker: "一个购物车。所有奖励。",
      plaza_hero_title_1: "购澳洲货。",
      plaza_hero_title_2: "到处赚积分。",
      plaza_hero_sub: "数千家澳洲供应商集于一篮。您花的每一澳元都能赚取HustlePoints积分——可在餐厅、咖啡馆、酒店、理发店、宠物美容店及HustleSuite网络中的任何商家兑换。",
      plaza_cta_start: "免费注册——仅需30秒",
      plaza_cta_signin: "登录",
      plaza_cta_shop: "浏览商店",
      plaza_hero_stat_1_v: "12,847",
      plaza_hero_stat_1_l: "在售商品",
      plaza_hero_stat_2_v: "340+",
      plaza_hero_stat_2_l: "澳洲供应商",
      plaza_hero_stat_3_v: "$4.2M",
      plaza_hero_stat_3_l: "已发奖励",
      plaza_hero_stat_4_v: "19",
      plaza_hero_stat_4_l: "类别",
      plaza_problem_kicker: "当今购物的痛点",
      plaza_problem_title: "十个标签页。十个密码。零忠诚度。",
      plaza_problem_sub: "您已经在Amazon、eBay、Kogan、Kmart、Woolies、Coles、Bunnings、JB、Officeworks和几十家精品店之间疲于奔命。您花费数小时比价——最后只得到一张收据。",
      plaza_problem_1: "跨网站价格无法比较",
      plaza_problem_2: "海外卖家假冒本地商家",
      plaza_problem_3: "忠诚积分被困在单一商店",
      plaza_problem_4: "机器人和代发货商的虚假评论",
      plaza_solution_kicker: "HustlePlaza的不同之处",
      plaza_solution_title: "您全天要打开的最后一个购物车。",
      plaza_solution_sub: "一次搜索。一个购物车。一次结账。每次购物都能获得HustlePoints积分回馈,可在我们整个生态系统中消费——不仅限于这家商店。",
      plaza_solution_1_t: "一个购物车,所有供应商",
      plaza_solution_1_b: "在平台上所有澳洲卖家处购物,结账时一次付清。尽可能合并配送。",
      plaza_solution_2_t: "仅限经核实的本地商家",
      plaza_solution_2_b: "每个卖家都需通过身份验证才能上架商品。杜绝机器人、假冒和神秘代发货。",
      plaza_solution_3_t: "真正通用的奖励",
      plaza_solution_3_b: "这里赚取的积分可在任何HustleSuite商家消费——咖啡馆、酒店、理发店、餐厅、宠物美容店。",
      plaza_solution_4_t: "实时追踪,一屏显示",
      plaza_solution_4_b: "每个供应商的每个包裹都在一个页面上追踪。每个步骤都通过电子邮件和短信通知。",
      plaza_rewards_kicker: "HustlePoints飞轮效应",
      plaza_rewards_title: "购物赚积分。网络内随处消费。",
      plaza_rewards_sub: "在HustlePlaza每消费$1即赚取HustlePoints积分。在任何使用HustleSuite的餐厅、咖啡馆、酒店、理发店或服务商家兑换——整个网络随处可用。",
      plaza_rewards_step_1: "在市场购物",
      plaza_rewards_step_1_b: "购买从T恤到冰箱的任何商品。",
      plaza_rewards_step_2: "即时赚取HustlePoints积分",
      plaza_rewards_step_2_b: "订单发货即刻获得积分——无需卡片、无需打卡、无过期套路。",
      plaza_rewards_step_3: "全网络消费",
      plaza_rewards_step_3_b: "在咖啡馆、餐厅、酒店、理发店、宠物美容店、技工处兑换。",
      plaza_rewards_step_4: "或提现",
      plaza_rewards_step_4_b: "达到$50后可转账至银行账户。",
      plaza_cats_kicker: "按类别购物",
      plaza_cats_title: "19个类别。澳洲供应商。实时库存。",
      plaza_cats_sub: "浏览当前在售的部分商品——或注册后浏览完整的12,000+商品目录。",
      plaza_cats_cta: "查看所有类别",
      plaza_vendors_kicker: "真正的澳洲卖家",
      plaza_vendors_title: "认识制造商、种植者和家庭店铺。",
      plaza_vendors_sub: "HustlePlaza上的每个供应商都经过ABN验证,位于澳大利亚境内,并由我们亲自引导入驻。绝无海外转手假冒本地商家。",
      plaza_how_kicker: "运作方式",
      plaza_how_title: "三个步骤。一个购物车。终身奖励。",
      plaza_how_1_title: "30秒注册",
      plaza_how_1_body: "验证您的电子邮箱即可立即浏览。准备结账时添加手机号码。",
      plaza_how_2_title: "填满购物车",
      plaza_how_2_body: "跨所有供应商购物。一个购物车,一个配送视图,一次结账。通过Stripe或PayPal支付。",
      plaza_how_3_title: "随处赚取和消费",
      plaza_how_3_body: "订单发货即刻获得HustlePoints积分。在任何HustleSuite商家消费,或在$50时提现。",
      plaza_compare_kicker: "我们的优势",
      plaza_compare_title: "HustlePlaza对比巨头",
      plaza_compare_col_us: "HustlePlaza",
      plaza_compare_col_amz: "Amazon AU",
      plaza_compare_col_ebay: "eBay AU",
      plaza_compare_col_kmart: "Kmart / Big W",
      plaza_compare_row_1: "澳洲本土所有",
      plaza_compare_row_2: "跨所有卖家的单一购物车",
      plaza_compare_row_3: "奖励可在本店外消费",
      plaza_compare_row_4: "奖励可提现至银行",
      plaza_compare_row_5: "澳洲本土客户支持",
      plaza_trust_kicker: "我们为何验证",
      plaza_trust_title: "真实顾客。真实评论。",
      plaza_trust_body: "我们在注册时验证您的电子邮箱,结账前验证您的手机号码,并要求在发布评论、与供应商聊天或进行高价值购买前通过实时照片验证。这减缓了欺诈者的速度,为您保持低价。",
      plaza_trust_1: "注册需验证电子邮箱",
      plaza_trust_2: "结账需验证短信",
      plaza_trust_3: "评论和聊天需实时照片验证",
      plaza_trust_4: "入驻时对供应商进行身份检查",
      plaza_test_kicker: "早期会员评价",
      plaza_test_title: "来自真实顾客的声音。",
      plaza_test_1_body: "我用HustlePlaza积分支付了公司附近咖啡馆的晚餐。直到服务员微笑着说'您已结账完成'时,我才真正相信这真的有效。",
      plaza_test_1_name: "Priya S.",
      plaza_test_2_body: "六个供应商,一个配送时间窗口,一封确认邮件。平常我需要六个密码和六个追踪号码。这个就是好用。",
      plaza_test_2_name: "Marcus T.",
      plaza_test_3_body: "作为小型蜂蜜生产商,我们终于能当天收款、获得真实评论,而且没有40%的抽成。HustlePlaza让我从'副业'变成全职,仅用了八个月。",
      plaza_test_3_name: "Kate & Dave",
      plaza_faq_kicker: "解答",
      plaza_faq_title: "我们每周都会被问到的问题",
      plaza_faq_1_q: "HustlePlaza真的是澳洲本土企业吗?",
      plaza_faq_1_a: "是的。HustlePlaza是100%澳洲本土所有,托管在澳洲服务器上,由澳洲团队运营。每个供应商在第一个商品上架前都需通过身份验证。",
      plaza_faq_2_q: "奖励如何运作?",
      plaza_faq_2_a: "您在HustlePlaza每消费一澳元即可赚取HustlePoints积分。订单发货即刻获得积分,且永不过期。在任何使用HustleSuite的商家消费,或在攒够后提现至您的银行账户。",
      plaza_faq_3_q: "为什么要验证照片?",
      plaza_faq_3_a: "为了确保您读到的评论是真实的。照片验证是通过手机摄像头进行的实时自拍检查——与银行使用的技术相同——可防止机器人、虚假账户和恶意行为者操纵平台。",
      plaza_faq_4_q: "如果我的订单没有送达怎么办?",
      plaza_faq_4_a: "每个订单都有我们的HustleGuarantee保障:如果包裹未送达或与描述不符,我们会在3个工作日内退款,然后追究供应商责任。您绝不会蒙受损失。",
      plaza_faq_5_q: "我的银行卡信息安全吗?",
      plaza_faq_5_a: "我们永远不会看到或存储您的卡号。所有支付都通过Stripe(PCI-DSS一级)或PayPal处理,两者都有3D安全认证,保护您免受被盗卡的欺诈性收费。",
      plaza_faq_6_q: "你们支持国际配送吗?",
      plaza_faq_6_a: "暂时不支持。HustlePlaza上线时仅限澳大利亚——这样我们才能承诺大部分商品在一周内送达,并在您的时区内提供支持。新西兰在2026年路线图上。",
      plaza_faq_7_q: "注册需要多少费用?",
      plaza_faq_7_a: "完全免费。会员资格永久免费。供应商为每笔销售支付少量佣金——买家注册$0,赚取$0,兑换$0。",
      plaza_faq_8_q: "我可以在HustlePlaza上销售吗?",
      plaza_faq_8_a: "当然可以。访问hustlesuite.au作为供应商入驻——经验证的澳洲商家可当天获批,且前30天免佣金。",
      plaza_final_kicker: "创始会员定价——名额有限",
      plaza_final_title: "成为首批会员——锁定创始会员定价。",
      plaza_final_sub: "今天注册成为HustleSuite会员。您将第一时间了解每个新功能的发布,您的账户将终身保留创始会员级别的奖励和定价。",
      plaza_final_bonus: "创始会员永不失去创始会员费率。永久免费,随时取消。",
      plaza_final_cta: "注册为创始会员",
      plaza_final_disclosure: "无需银行卡。仅需电子邮箱+手机验证。",
      plaza_roadmap_kicker: "我们接下来要构建的",
      plaza_roadmap_title: "六个工具,旨在减轻您的工作负担。",
      plaza_roadmap_sub: "HustleSuite不仅是一家商店——它是一个完整的生产力层。以下是即将推出的功能。",
      plaza_roadmap_soon: "即将推出",
      plaza_roadmap_next: "下一个排队项",
      plaza_roadmap_1_t: "HustleDictate——任何表单的语音转文字",
      plaza_roadmap_1_b: "Chrome扩展程序。按Alt+M,说话,任何网站上的任何输入框都会自动填充。房地产申请、发票、NDIS文书——时间减半。",
      plaza_roadmap_2_t: "HustleConnect随处编辑",
      plaza_roadmap_2_b: "针对WordPress所有者:用一个快捷键从任何页面编辑您的实时网站。无需再往返wp-admin。",
      plaza_roadmap_3_t: "保存到HustleSuite通用按钮",
      plaza_roadmap_3_b: "右键单击任何网页——房产列表、文章、商品、餐厅——它会落入正确的模块中等待您处理。",
      plaza_roadmap_4_t: "HustlePlaza价格检查器",
      plaza_roadmap_4_b: "浏览Amazon、eBay或Kmart——浮动标签显示HustlePlaza上的相同商品及您的奖励预览。",
      plaza_roadmap_5_t: "评论采集器",
      plaza_roadmap_5_b: "自动同步新的Google、Facebook和TripAdvisor评论到您的HustleSuite仪表板。每周节省约20分钟。",
      plaza_roadmap_6_t: "HustleSuite侧边栏",
      plaza_roadmap_6_b: "Chrome侧面板,包含实时订单、KPI、快速发布和快速开票。您的整个业务在每个标签页旁的一条栏中。",
      plaza_footer_owned: "澳洲本土所有和托管",
      plaza_footer_terms: "条款",
      plaza_footer_privacy: "隐私",
      plaza_footer_contact: "联系我们",
      plaza_footer_vendors: "在HustlePlaza上销售",
      plaza_footer_help: "帮助中心",
      plaza_footer_tagline: "HustleSuite家族的一部分——一次登录,所有服务。",
      verify_gate_title: "快完成了——快速注册",
      verify_gate_body: "HustlePlaza是一个购物车,提供多种福利,包括可兑换的奖励,这些奖励不仅可以在本平台内使用,还可以在使用HustlePlaza平台的任何其他商家或在HustleSuite注册的任何其他商家使用。这些商家包括餐厅、咖啡馆、酒店、理发店,甚至宠物美容店。为继续提供优惠价格和服务,我们需要确认您是真人并验证您的身份。按加入按钮继续。",
      verify_gate_join: "加入HustleSuite",
      verify_gate_signin: "我已有账户",
      verify_gate_cancel: "暂不",
      verify_email_title: "验证您的电子邮箱",
      verify_email_body: "我们将向您的收件箱发送一个6位数验证码。",
      verify_email_placeholder: "you@example.com",
      verify_email_send: "发送验证码",
      verify_email_code_ph: "6位数验证码",
      verify_email_check: "验证",
      verify_email_sent: "验证码已发送——请查看您的收件箱。",
      verify_sms_title: "验证您的手机",
      verify_sms_body: "添加手机号码,以便我们可以向您发送订单更新短信并确认大额购买。",
      verify_sms_placeholder: "+61412345678",
      verify_sms_send: "发送短信",
      verify_sms_check: "验证",
      verify_sms_sent: "验证码已发送——请查看您的手机。",
      verify_photo_title: "照片验证",
      verify_photo_body: "用手机扫描二维码进行实时自拍。聊天、高价值购买以及在Meetup / Tackle中发帖时需要。",
      verify_photo_waiting: "等待您的手机…",
      verify_photo_verified: "照片已验证——一切就绪。",
      travel_cta_start: "免费加入",
      travel_hero_kicker: "HustleTravel",
      travel_hero_title_1: "一个会员资格。",
      travel_hero_title_2: "游遍澳大利亚。",
      travel_hero_sub: "酒店、航班、体验、自驾游规划和租车——以最优惠的价格预订,每消费一澳元即赚取HustlePoints积分。",
      travel_cta_plan: "规划自驾游",
      travel_cta_hotels: "搜索酒店",
      travel_svc_hotels_t: "酒店和公寓",
      travel_svc_hotels_b: "在一处比较200+预订网站——由Trivago提供支持的元搜索。",
      travel_svc_flights_t: "航班",
      travel_svc_flights_b: "国内和国际航班,带实时座位图和票价规则。",
      travel_svc_experiences_t: "体验和旅游",
      travel_svc_experiences_b: "大堡礁一日游、酒窖品酒、现场音乐、适合儿童的游览项目。",
      travel_svc_cars_t: "租车",
      travel_svc_cars_b: "所有主要品牌加本地独立商家——可在任何澳洲机场取车。",
      travel_svc_insurance_t: "旅游保险",
      travel_svc_insurance_b: "由APRA监管的澳洲保险公司承保。在一个屏幕上比较。",
      travel_svc_roadtrip_t: "自驾游规划器",
      travel_svc_roadtrip_b: "电动车续航感知行程,包含充电站、过夜停留点和成本估算。",
      travel_final_kicker: "创始会员定价",
      travel_final_title: "锁定终身创始会员奖励。",
      travel_final_sub: "在HustleSuite注册一次。在HustleTravel上预订所有项目——酒店、航班、体验、保险——赚取可在网络内所有商家消费的HustlePoints积分。",
      travel_final_cta: "注册为创始会员",
      travel_final_disclosure: "最优价格政策:结果按对旅行者的最佳价值排序,而非佣金。",
      dictate_hero_kicker: "HustleDictate",
      dictate_hero_title_1: "说话。",
      dictate_hero_title_2: "任何表单。任何网站。",
      dictate_hero_sub: "唯一一个将任何文本框变成专业级语音听写麦克风的Chrome扩展程序——由Whisper提供支持。",
      dictate_cta_install: "添加到Chrome",
      dictate_uc_re_t: "房地产租赁备注",
      dictate_uc_re_b: "直接将检查报告和租户消息听写到您的物业管理系统中。",
      dictate_uc_ndis_t: "NDIS进度备注",
      dictate_uc_ndis_b: "在记忆犹新时口述轮班备注——无需手动打字。",
      dictate_uc_invoice_t: "发票描述",
      dictate_uc_invoice_b: "将行项目描述直接听写到Xero、MYOB或QuickBooks中。",
      dictate_uc_email_t: "长篇电子邮件回复",
      dictate_uc_email_b: "在Gmail、Outlook或任何网页邮件中口述回复——速度是打字的三倍。",
      dictate_final_kicker: "创始会员免费",
      dictate_final_title: "每个键盘现在都是麦克风。",
      dictate_final_sub: "HustleSuite创始会员免费。其他用户$5/月——随时取消。",
      dictate_final_cta: "添加到Chrome——免费",
      "finder.cat.roadtrip": "自驾游",
      "finder.hotels.title": "查找附近酒店",
      "finder.hotels.sub": "在一次搜索中比较200+预订网站——由Trivago提供支持。",
      "finder.hotels.where": "地点",
      "finder.hotels.where_ph": "城市或郊区(例如:Byron Bay, NSW)",
      "finder.hotels.checkin": "入住",
      "finder.hotels.checkout": "退房",
      "finder.hotels.adults": "成人",
      "finder.hotels.search": "搜索酒店",
      "finder.hotels.disclosure": "打开Trivago——我们可能从预订中赚取佣金。",
      /* AUTO-TRANSLATED zh END */
      welcome_back: "欢迎回来",
      sign_out: "退出登录",
      /* Login / Auth flow ─ manual zh-CN */
      login_heading: "登录",
      login_email_label: "电子邮箱",
      login_password_label: "密码",
      login_forgot_password: "忘记密码？",
      login_submit: "登录",
      login_signing_in: "登录中…",
      login_or_no_password: "或者，无需密码",
      login_magic_button: "给我发送登录链接",
      login_magic_sending: "正在发送链接…",
      login_magic_sent_body: "我们已将登录链接发送至 {{email}}，15分钟内有效。请检查您的收件箱（以及垃圾邮件）——点击链接即可登录。",
      login_new_here: "初次使用？",
      login_create_account: "创建账户",
      login_welcome_prefix: "登录成功——欢迎，",
      login_welcome_back_prefix: "欢迎回来，",
      login_google_failed: "Google 登录失败，请重试。",
      login_enter_email_first: "请先输入您的邮箱地址——我们将把链接发送到该邮箱。",
      login_magic_toast_sent: "请查收邮件——我们已发送一次性登录链接。",
      login_magic_toast_failed: "无法发送链接，请稍后再试。",
      login_redirecting: "正在跳转…",
      login_continue_google: "使用 Google 继续",
      login_continue_apple: "使用 Apple 继续",
      login_apple_failed: "无法启动 Apple 登录流程。",
      login_passkey_button: "使用通行密钥 / Face ID / Touch ID 登录",
      login_passkey_waiting: "等待设备响应…",
      login_passkey_cancelled: "已取消通行密钥提示。",
      login_passkey_failed: "通行密钥登录失败。",
      login_passkey_saved: "通行密钥已保存。下次可使用 Face ID / Touch ID 登录。",
      login_passkey_registration_cancelled: "已取消注册。",
      login_passkey_register_failed: "无法保存通行密钥。",
      login_passkey_enable_biometric: "为此账户启用 Face ID / Touch ID",
      login_passkey_add_security_key: "添加通行密钥（安全密钥）",
      /* Legacy restaurant/booking keys (merged from orphan zh-Hans block) */
      nav_menu: "菜单",
      nav_book: "预订座位",
      nav_reviews: "评价",
      nav_about: "关于我们",
      nav_account: "我的账户",
      nav_signin: "登录",
      cart_open: "打开购物车",
      cart_title: "您的订单",
      cart_empty: "购物车是空的",
      cart_subtotal: "小计",
      cart_continue: "继续",
      cart_pickup_date: "取餐日期",
      cart_pickup_time: "取餐时间",
      cart_pay_on_pickup: "取餐时付款",
      cart_pay_now: "立即付款",
      cart_place_order: "提交订单",
      add_to_order: "加入订单",
      menu_search_ph: "搜索菜单…",
      menu_chef_specials: "主厨推荐",
      menu_under_kcal: "低于",
      menu_kcal_unit: "千卡",
      menu_clear_filters: "清除",
      menu_dietary_label: "饮食",
      menu_no_matches: "没有匹配项",
      menu_relax_filters: "请尝试其他搜索或放宽筛选条件。",
      menu_clear_all: "清除所有筛选",
      book_title: "预订座位",
      book_name: "姓名",
      book_email: "电子邮箱",
      book_phone: "电话",
      book_guests: "用餐人数",
      book_date: "日期",
      book_time: "时间",
      book_requests: "特殊要求",
      book_submit: "确认预订",
      footer_rights: "版权所有。",
      lang_label: "语言",

      /* AUTO-TRANSLATED finder.* (added) */
      "finder.title": "HustleFinder",
      "finder.tagline": "查找最近的公共场所——真实数据,覆盖全澳。",
      "finder.search_placeholder": "搜索地点、街道或郊区…",
      "finder.hint": "在上方开始输入、点击类别或使用\"附近\"。",
      "finder.near_me": "附近",
      "finder.searching": "搜索中…",
      "finder.suburb.placeholder": "按郊区筛选(例如 Darwin, NT)…",
      "finder.suburb.clear": "清除",
      "finder.search_in": "在{{cat}}中搜索",
      "finder.empty": "无结果——请尝试其他类别或搜索词。",
      "finder.meta.title": "HustleFinder——免费澳洲郊区目录",
      "finder.meta.desc": "搜索澳大利亚每个郊区的本地商家、充电站、咖啡馆、公厕等——100%免费。",
      "finder.footer.credits": "数据来源与署名",
      "finder.footer.terms": "条款",
      "finder.cat.toilet": "公厕",
      "finder.cat.fountain": "饮水处",
      "finder.cat.transport": "交通",
      "finder.cat.park": "公园",
      "finder.cat.medical": "医疗",
      "finder.cat.school": "学校",
      "finder.cat.library": "图书馆",
      "finder.cat.petrol": "加油站",
      "finder.cat.kidzone": "儿童区",
      "finder.cat.puzzles": "谜题",
      /* AUTO-TRANSLATED rt.* START */
      "rt.brand_name": "Roadtrip Planner",
      "rt.page_title": "Aussie EV Road-trip Planner",
      "rt.from": "出发地",
      "rt.to": "目的地",
      "rt.from_placeholder": "郊区、城镇或城市（例如 Newcastle NSW）",
      "rt.to_placeholder": "郊区、城镇或城市（例如 Hobart TAS）",
      "rt.stop": "STOP",
      "rt.stop_placeholder": "途径点",
      "rt.add_stop": "添加途径点",
      "rt.upgrade_pro": "升级至 Pro 获取多达 20 个途径点",
      "rt.reverse": "反向（计算回程）",
      "rt.from_current": "从当前位置出发",
      "rt.range_per_charge": "单次充电续航里程 (km)",
      "rt.connector": "充电接口",
      "rt.connector_any": "不限",
      "rt.drive_hours_day": "每日驾驶时长",
      "rt.accommodation": "住宿",
      "rt.accom_any": "不限 (BnB · 酒店 · 房车营地)",
      "rt.accom_bnb": "BnB",
      "rt.accom_hotel": "酒店",
      "rt.accom_caravan": "房车营地",
      "rt.min_rating": "最低评分",
      "rt.any_rating": "不限评分",
      "rt.only_5star": "仅限 5★",
      "rt.schedule": "时间安排：",
      "rt.depart_at": "出发时间",
      "rt.arrive_by": "到达时间",
      "rt.hotel_checkin": "酒店入住",
      "rt.hotel_checkout": "酒店退房",
      "rt.overnight_hint": "跨夜行程将不会安排在这些时间段之外。",
      "rt.whos_in_car": "车上有谁？",
      "rt.adults": "成人",
      "rt.kids": "儿童",
      "rt.ages": "年龄",
      "rt.show_attractions": "显示旅游景点",
      "rt.show_kids": "显示适合儿童的停靠点",
      "rt.overnight_stays": "住宿停留",
      "rt.fast_chargers_only": "仅限快速充电桩",
      "rt.hotel_must_charger": "酒店必须备有充电桩",
      "rt.pet_only": "仅限宠物友好住宿",
      "rt.safety": "电量安全阈值",
      "rt.safety_90": "90% (平路)",
      "rt.safety_85": "85% (默认)",
      "rt.safety_80": "80% (山路 / 牵引)",
      "rt.safety_70": "70% (重载)",
      "rt.save_trip": "保存行程",
      "rt.plan_my_trip": "规划我的行程",
      "rt.refresh_trip": "刷新行程",
      "rt.planning": "规划中…",
      "rt.resume_title": "从此处继续行程",
      "rt.resume_body": "我们已获取您的当前位置。请输入仪表盘显示的剩余续航，以便我们根据实际电量（而非满电状态）规划下一段行程。",
      "rt.remaining_range": "剩余续航里程 (km)",
      "rt.cancel": "取消",
      "rt.replan_from_here": "从此处重新规划",
      "rt.save_modal_title": "命名此行程",
      "rt.save_modal_body": "已保存的行程将显示在您的个人资料下——可随时重新打开并点击“重新规划”。",
      "rt.save_name_placeholder": "例如 Brisbane → Sydney 途经 Byron",
      "rt.your_saved_trips": "您保存的行程",
      "rt.custom_location": "自定义地点",
      "rt.trip_summary": "行程摘要",
      "rt.itinerary": "路线详情",
      "rt.no_stops_needed": "无需中途停留——整段行程在您的续航范围内。",
      "rt.chargers_in_corridor": "沿途充电桩",
      "rt.planned_stops": "计划停靠点",
      "rt.overnight_stop": "住宿点",
      "rt.overnight_stops": "住宿点",
      "rt.suggested_detours": "建议绕道景点——勾选以添加",
      "rt.replan_with_detours": "包含绕道点重新规划",
      "rt.attractions_label": "景点",
      "rt.kids_label": "儿童",
      "rt.km_from_start": "距起点 km",
      "rt.open_driving": "开启驾驶模式",
      "rt.big_font_hint": "大字体，适合车载支架。支持 CarPlay/Android Auto 浏览器镜像。",
      "rt.check_price_book": "在 Trivago 查看价格并预订 ↗",
      "rt.visit_site": "访问网站 ↗",
      "rt.charger_onsite": "院内设有充电桩",
      "rt.leg_exceeds_range": "该段行程超过了您设置的续航里程——在到达此充电桩前电量将耗尽。请降低续航预期或添加中途补能点。",
      "rt.add_waypoint_replan": "添加为途径点并重新规划",
      /* AUTO-TRANSLATED rt.* END */
                      /* AUTO-TRANSLATED hm.* START */
      "hm.nav.hustlemeet": "HustleMeet",
      "hm.nav.circles": "Circles",
      "hm.nav.matchmaker": "Matchmaker",
      "hm.nav.meet": "遇见",
      "hm.nav.overview": "HustleMeetups",
      "hm.overview.page_title": "HustleMeetups — 三种温馨的方式，助你找到志趣相投的人。",
      "hm.overview.kicker": "HustleSuite 旗下成员",
      "hm.overview.hero_title": "三种温馨的方式，助你找到志趣相投的人。",
      "hm.overview.hero_title_accent": "志趣相投的人",
      "hm.overview.hero_sub": "HustleMeetups 是 Australia 最温情的真实友谊建立方式 —— 选择适合你的氛围。无论你想要每周一次的跑步小组，还是每月一次的老友聚餐，抑或是由 AI 匹配的陌生人饭局，这里都有你的位置。",
      "hm.overview.tile_meet_kicker": "HustleMeet",
      "hm.overview.tile_meet_title": "找到对的人，做热爱的事。",
      "hm.overview.tile_meet_body": "Australia 的兴趣社交大本营。跑者、程序员、投资者、漫步者、舞者、早餐俱乐部、书友会 —— 这里有数百个小组，每周都有真实活动。无论是偶尔参加还是每周报到，随你心意。",
      "hm.overview.tile_meet_cta": "探索小组",
      "hm.overview.tile_circles_kicker": "Circles",
      "hm.overview.tile_circles_title": "固定班底，全新地点。",
      "hm.overview.tile_circles_body": "带上你的老友。我们负责预订餐厅、策划活动、分发卡牌。你们只需准时到场。这是让老友团保持紧密联系的最简单方式 —— 无需任何人费心组织。",
      "hm.overview.tile_circles_cta": "开启一个 Circle",
      "hm.overview.tile_matchmaker_kicker": "Matchmaker",
      "hm.overview.tile_matchmaker_title": "AI 组局，你只管准时到场。",
      "hm.overview.tile_matchmaker_body": "花 10 分钟做个测试。一夜之间，我们会为你匹配志同道合的陌生人。你会发现，等到上甜点时，大家已如故交。每次都是不同的餐桌。",
      "hm.overview.tile_matchmaker_cta": "参与测试",
      "hm.overview.trust_kicker": "运作原理",
      "hm.overview.trust_title": "信任第一，餐桌第二。",
      "hm.overview.trust_1_title": "经过验证的成员",
      "hm.overview.trust_1_body": "每个小组都从真实的身份核验 + 可靠性评分开始。拒绝僵尸号。",
      "hm.overview.trust_2_title": "真实的主理人",
      "hm.overview.trust_2_body": "每个小组都由经过审核的主理人领导，他们把控氛围，设定节奏，并照顾全场。",
      "hm.overview.trust_3_title": "友谊至上",
      "hm.overview.trust_3_body": "这里的每一处设计都是为了建立真实的友谊，而非约会。永远如此。",
      "hm.overview.trust_4_title": "零容忍审核机制",
      "hm.overview.trust_4_body": "一次举报，一次审核，即刻裁定。不良行为者会在当晚被移出平台。",
      "hm.overview.footer_line": "HustleSuite 旗下成员 — Australia 本地所有及托管。",
      "hm.landing.page_title": "HustleMeet · 找到对的人，做热爱的事。",
      "hm.landing.kicker": "Australia 的兴趣网络",
      "hm.landing.hero_title": "找到对的人，做热爱的事。",
      "hm.landing.hero_title_accent": "对的人",
      "hm.landing.hero_sub": "HustleMeet 是 Australia 兴趣优先小组的聚集地。午间交流技术的软件开发人员。清晨 6 点的长跑者。每月聚会的投资者。漫步小组、舞蹈队、早餐俱乐部、桌游之夜。加入小组，预订活动，然后准时出现。",
      "hm.landing.cta_explore_circles": "浏览小组",
      "hm.landing.cta_try_matchmaker": "创建你自己的小组",
      "hm.landing.trust_verified": "经过验证的成员",
      "hm.landing.trust_venues": "经过审核的活动主理人",
      "hm.landing.trust_friendship": "友谊至上 — 绝非约会",
      "hm.landing.trust_moderation": "零容忍审核机制",
      "hm.landing.subbrand_circles_kicker": "Circles",
      "hm.landing.subbrand_circles_title": "已经有自己的小圈子了？给老友一个永久的家。",
      "hm.landing.subbrand_circles_body": "Circles 专为已经定期聚会的好友设计 —— 读书会、扑克之夜、跑步小组、工作伙伴。我们负责场地、提醒和排班。你只需准时到场。",
      "hm.landing.subbrand_circles_b1": "轮换场地，固定排班",
      "hm.landing.subbrand_circles_b2": "自动提醒 + 轻松 RSVP",
      "hm.landing.subbrand_circles_b3": "知识竞答、桌游和主题之夜随心选",
      "hm.landing.subbrand_circles_cta": "开启一个 Circle",
      "hm.landing.subbrand_matchmaker_kicker": "Matchmaker",
      "hm.landing.subbrand_matchmaker_title": "AI 组局，你只管准时到场。",
      "hm.landing.subbrand_matchmaker_body": "一个 10 分钟的测试 — 性格、价值观、空闲时间。我们的 Matchmaker 会为你匹配志趣相投的陌生人，等到上甜点时，大家已如故交。每次都是不同的餐桌。",
      "hm.landing.subbrand_matchmaker_b1": "Big-5 性格与价值观评分",
      "hm.landing.subbrand_matchmaker_b2": "当契合度达到 0.70 或更高时，即刻发送邀请",
      "hm.landing.subbrand_matchmaker_b3": "从你的每一次晚餐中不断学习优化",
      "hm.landing.subbrand_matchmaker_cta": "参与测试",
      "hm.landing.how_kicker": "HustleMeet 运作方式",
      "hm.landing.how_title": "四个步骤。找到你的组织。准时出现。",
      "hm.landing.how_step1_ttl": "选择你的兴趣",
      "hm.landing.how_step1_sub": "跑步、编程、投资、烹饪、音乐、散步 —— 告诉我们你的爱好以及你的坐标。",
      "hm.landing.how_step2_ttl": "浏览附近的活动小组",
      "hm.landing.how_step2_sub": "每个小组都会公开其主理人、聚会节奏和下一场活动。参与之前先了解氛围。",
      "hm.landing.how_step3_ttl": "RSVP 报名下一次活动",
      "hm.landing.how_step3_sub": "一键点击，日历邀请，活动前一天贴心提醒。有些活动免费，有些收费 —— 价格透明明了。",
      "hm.landing.how_step4_ttl": "到场并建立连接",
      "hm.landing.how_step4_sub": "与主理人碰头，结识伙伴，开启活动。如果聊得来，下周再见。",
      "hm.landing.stats_kicker": "Australia 社交新方式",
      "hm.landing.stats_title": "人们流连忘返的原因。",
      "hm.landing.stats_92_sub": "觉得在活动中至少和一个人“合拍”",
      "hm.landing.stats_68_sub": "在一周内 RSVP 报名下一次活动",
      "hm.landing.stats_85_sub": "与小组中的某人保持联系",
      "hm.landing.why_kicker": "为什么我们能成功",
      "hm.landing.why_title": "真实的兴趣。真实的主理人。真实的活动。",
      "hm.landing.why_1_ttl": "兴趣优先，而非地点优先",
      "hm.landing.why_1_body": "小组里的人和你一样，真心关注那些事情 —— 这是建立真实友谊的捷径。",
      "hm.landing.why_2_ttl": "真实活动主理人",
      "hm.landing.why_2_body": "每个小组都有一位经过审核的主理人，他们把控氛围，设定节奏，并在出现异常时介入。不是 Facebook 管理员，而是真实存在的人。",
      "hm.landing.why_3_ttl": "稳定性至关重要",
      "hm.landing.why_3_body": "每周都有新活动。一些值得期待的事。一种能让陌生人变成你“周二晚班底”的节奏。",
      "hm.landing.why_4_ttl": "安全且可靠",
      "hm.landing.why_4_body": "经过验证的成员、公共场所、对不尊重行为零容忍。我们的支持团队在活动前、期间和之后随时待命。",
      "hm.landing.why_5_ttl": "友谊至上",
      "hm.landing.why_5_body": "这不是约会，而是社区。真实的兴趣，真实的人，中间没有“划一划”的干扰。",
      "hm.landing.why_6_ttl": "规模恰到好处",
      "hm.landing.why_6_body": "大多数小组将活动名额限制在 4-15 人。规模大到足以激发真实对话，小到不会让任何一个人被忽视。",
      "hm.landing.cities_kicker": "服务范围",
      "hm.landing.cities_title": "活动遍布 Australia & NZ。",
      "hm.landing.cities_sub_zero": "首批小组在 15+ 个城市开放 —— 尽早加入并塑造你的圈子。",
      "hm.landing.cities_sub_some": "活动已覆盖 15+ 个城市。这周，你的城市就有精彩。",
      "hm.landing.cities_sub_one": "{{count}} 个活跃小组 · 每周都有新活动 · 更多城市持续加入中。",
      "hm.landing.cities_sub_many": "{{count}} 个活跃小组 · 每周都有新活动 · 更多城市持续加入中。",
      "hm.landing.forme_kicker": "如果你……这里很适合你",
      "hm.landing.forme_title": "你想要真实的兴趣。真实的人。真实的活动。",
      "hm.landing.forme_1": "你已经厌倦了交友软件带来的疲劳，想真正地围绕自己的爱好去认识人。",
      "hm.landing.forme_2": "你刚搬到一座新城市，需要一个温馨的小圈子，而不是又一个聊天群。",
      "hm.landing.forme_3": "你重视深度而非广度 —— 一个优秀的小组胜过五十个关注。",
      "hm.landing.forme_4": "你相信共同兴趣 + 现实生活中固定的两小时才是社交的终极秘籍。",
      "hm.landing.notme_kicker": "如果你……这里不适合你",
      "hm.landing.notme_title": "HustleMeet 并不适合所有人。",
      "hm.landing.notme_1": "你无法承诺到场 —— 主理人和成员都指望你的 RSVP 承诺。",
      "hm.landing.notme_2": "你在寻找约会服务 —— 在这里，友谊第一。",
      "hm.landing.notme_3": "你只想在线围观而不想线下见面 —— 这只有在面对面时才起作用。",
      "hm.landing.notme_4": "你期待无尽的免费福利 —— 有些活动是有成本的。价格始终公开透明。",
      "hm.landing.safety_kicker": "我们的安全承诺",
      "hm.landing.safety_title": "温馨，但绝不马虎。",
      "hm.landing.safety_1_ttl": "公共场所",
      "hm.landing.safety_1_body": "每场活动都在公共、有员工服务的场所进行 —— 咖啡馆、餐厅、公园、健身房或工作室。除非所有参与者都同意，否则绝不去私人住宅。",
      "hm.landing.safety_2_ttl": "社区准则",
      "hm.landing.safety_2_body": "对不尊重、骚扰或欺诈行为零容忍。违规成员将被立即移出。",
      "hm.landing.safety_3_ttl": "隐私保护",
      "hm.landing.safety_3_body": "你的详细信息绝不会公开。你可以自行控制在每次活动后与谁保持联系。",
      "hm.landing.safety_4_ttl": "随时待命的支持",
      "hm.landing.safety_4_body": "如果你你有任何疑问或顾虑，我们的团队在每场活动前、期间和之后都会提供支持。",
      "hm.landing.bottom_title": "点击一下，开启你的下一个心头好小组。",
      "hm.landing.bottom_sub": "选择你的路径 —— 加入现有小组，带上你的老友加入 Circle，或者让 Matchmaker 为你安排一桌志同道合的饭局。",
      "hm.landing.bottom_cta_circles": "开启一个 Circle",
      "hm.landing.bottom_cta_mm": "参与 Matchmaker 测试",
      "hm.circles.page_title": "HustleMeet Circles · 你的老友团，每次都有新场地",
      "hm.circles.kicker": "HustleMeet · Circles",
      "hm.circles.hero_title": "固定班底，全新地点。",
      "hm.circles.hero_title_accent": "固定班底",
      "hm.circles.hero_sub": "已经有自己的伙伴了？Circles 是保持紧密联系的最简单方式。我们预订场地、策划活动、分发卡牌。你只需准时出现 —— 同样的伙伴，不同的夜晚，全新的地点。",
      "hm.circles.cta_find": "开启一个 Circle",
      "hm.circles.cta_host_hero": "加入开放的 Circle",
      "hm.circles.trust_venue": "轮换经过审核的场地",
      "hm.circles.trust_party": "每个 Circle 包含 4-12 位伙伴",
      "hm.circles.trust_verified": "经过验证的成员",
      "hm.circles.trust_friendship": "守护真实的友谊",
      "hm.circles.how_kicker": "How it works",
      "hm.circles.how_title": "四个步骤。让你们的圈子长久如初。",
      "hm.circles.how_step1_ttl": "开启你的 Circle",
      "hm.circles.how_step1_sub": "为其命名，邀请你的伙伴（最多 12 人），并选择聚会频率 —— 每周、每两周或每月。",
      "hm.circles.how_step2_ttl": "我们来挑选场地",
      "hm.circles.how_step2_sub": "每次活动我们都会推荐一个新地点 —— 餐厅、知识竞答之夜、棋牌室、酒吧。或者固定一个心头好。",
      "hm.circles.how_step3_ttl": "全员一键 RSVP",
      "hm.circles.how_step3_sub": "自动提醒、费用分摊、预付座位，避免爽约破坏计划。在开胃菜上桌前，账单就已经搞定了。",
      "hm.circles.how_step4_ttl": "保持节奏",
      "hm.circles.how_step4_sub": "在日程表上定下一个永远无需再费心组织的夜晚。随着月深日久，团队会变得更加紧密。",
      "hm.circles.cadence_weekly_ttl": "每周",
      "hm.circles.cadence_weekly_sub": "每星期",
      "hm.circles.cadence_weekly_body": "适合想要建立周二晚仪式感的伙伴团 —— 同样的面孔，每次都有新体验。",
      "hm.circles.cadence_fortnight_ttl": "每两周",
      "hm.circles.cadence_fortnight_sub": "每 2 星期",
      "hm.circles.cadence_fortnight_body": "完美的黄金频率 —— 既有规律，又不会让繁忙的日程感到吃力。",
      "hm.circles.cadence_monthly_ttl": "每月",
      "hm.circles.cadence_monthly_sub": "每个月",
      "hm.circles.cadence_monthly_body": "适合特殊场合的 Circle —— 周日烤肉、大型知识竞答、精品品鉴会、扑克局。",
      "hm.circles.feast_kicker": "选择你的夜晚",
      "hm.circles.feast_title": "三种消费方式",
      "hm.circles.feast_sub": "每个 Circle 都可以选择晚上的活动流程。可以轮换进行，也可以固定在老友们的最爱。",
      "hm.circles.feast_surprise_ttl": "尝试新餐厅",
      "hm.circles.feast_surprise_body": "我们为你附近的三个场地制作候选名单，大家投票，餐厅准备全桌服务。费用分摊，预付座位。",
      "hm.circles.feast_preselect_ttl": "知识竞答 / 棋牌 / 游戏",
      "hm.circles.feast_preselect_body": "预订知识竞答之夜、扑克室、桌游吧或保龄球馆。我们负责场地、报名费和提醒。",
      "hm.circles.feast_per_event_ttl": "一次性主题之夜",
      "hm.circles.feast_per_event_body": "品酒会、厨师桌、调酒课、密室逃脱。偶尔来点特别的 —— 全程无忧组织。",
      "hm.circles.live_kicker": "正在进行中",
      "hm.circles.live_title": "你可以加入的 Circles",
      "hm.circles.live_host_link": "开启你自己的",
      "hm.circles.live_loading": "加载中…",
      "hm.circles.live_empty_title": "成为你所在城市的第一个 Circle。",
      "hm.circles.live_empty_sub": "与三位好友开启一个 Circle，我们会帮你邀请其他成员 —— 并为你提供前三个场地的费用减免。",
      "hm.circles.live_empty_cta": "开启一个 Circle",
      "hm.circles.host_kicker": "致 Circle 领袖",
      "hm.circles.host_title": "你负责带人，我们负责行政。",
      "hm.circles.host_body": "If如果你是那个总是负责组织聚会的人，Circles 能为你节省数小时。自动提醒、预付 RSVP、场地清单、费用分摊、候补名单管理，以及每晚聚会的返现钱包。你只需作为一个普通成员准时出现。",
      "hm.circles.host_perk_1": "自动生成场地清单 —— 再也不用刷 Google 了",
      "hm.circles.host_perk_2": "预付座位 —— 拒绝爽约",
      "hm.circles.host_perk_3": "甜点还没上，费用分摊就已搞定",
      "hm.circles.host_perk_4": "自动完成提醒 + 日历邀请",
      "hm.circles.host_perk_5": "候补名单管理，好友取消时自动补位",
      "hm.circles.host_perk_6": "每晚聚会均有返现钱包",
      "hm.circles.host_cta": "立即开启一个 Circle now",
      "hm.circles.why_kicker": "为什么 Circles 行得通",
      "hm.circles.why_title": "成年人的友谊需要基础设施",
      "hm.circles.why_1_ttl": "原班人马，更深层的信任",
      "hm.circles.why_1_body": "生活总会让朋友渐行渐远。Circles 是固定的节奏，让大家保持亲近 —— 同样的 4-12 位伙伴，在固定的周中夜晚见面，风雨无阻。",
      "hm.circles.why_2_ttl": "零行政负担",
      "hm.circles.why_2_body": "没人需要当 WhatsApp 群组里的组织大臣。我们选址、发提醒、扣款、完成闭环。",
      "hm.circles.why_3_ttl": "新场地，新记忆",
      "hm.circles.why_3_body": "轮换体验 Australia 最棒的小餐馆、知识竞答室和游戏酒吧。熟悉的面孔，新鲜的背景 —— 兼具常规与新鲜感的绝佳体验。",
      "hm.circles.bottom_title": "这是一个让你的伙伴们充满期待、又无需任何人费心筹划的夜晚。",
      "hm.circles.bottom_sub": "一个 Circle。老班底。每次都有新场地。",
      "hm.circles.bottom_cta_find": "开启一个 Circle",
      "hm.circles.bottom_cta_mm": "通过 Matchmaker 结识新朋友 →",
      "hm.circles.card_host_pill": "Circle",
      "hm.matchmaker.page_title": "HustleMeet Matchmaker · AI 为你挑选合适的餐桌",
      "hm.matchmaker.kicker": "HustleMeet · Matchmaker",
      "hm.matchmaker.hero_title": "AI 组局，你只管准时到场。",
      "hm.matchmaker.hero_title_accent": "你只管准时到场。",
      "hm.matchmaker.hero_sub": "回答简短的性格与价值观测试。我们的 Matchmaker 会为你匹配四到七位陌生人，等到上甜点时，大家已如故交。没有划来划去，没有尬聊。只有一张已经为你备好的餐桌，随时待命。",
      "hm.matchmaker.cta_quiz": "参与匹配测试",
      "hm.matchmaker.cta_see_circles": "改看 Circles 方案 →",
      "hm.matchmaker.trust_big5": "Big-5 性格评分",
      "hm.matchmaker.trust_seats": "每桌 4-8 个席位",
      "hm.matchmaker.trust_lock": "拒绝刷脸 · 无需照片",
      "hm.matchmaker.trust_verified": "仅限经验证的成员",
      "hm.matchmaker.how_kicker": "运作方式",
      "hm.matchmaker.how_title": "四个步骤，坐上一张本就契合的餐桌。",
      "hm.matchmaker.how_step1_ttl": "参与测试",
      "hm.matchmaker.how_step1_sub": "十分钟问卷 —— 性格、价值观、饮食要求、空闲时间。只需填写一次。",
      "hm.matchmaker.how_step2_ttl": "隔夜匹配",
      "hm.matchmaker.how_step2_sub": "我们的 AI 会读取你的资料和 Australia 的实时晚餐档期。当契合度达到 0.70 或更高时，它会草拟一份个人邀请。",
      "hm.matchmaker.how_step3_ttl": "接受或拒绝",
      "hm.matchmaker.how_step3_sub": "你会收到一封简洁的邀请邮件，告知场地、桌餐主题，以及你与小组共享的三项共同兴趣。",
      "hm.matchmaker.how_step4_ttl": "智能学习",
      "hm.matchmaker.how_step4_sub": "每份晚餐后的反馈调查都会优化模型。你用餐越多，下一次匹配就越精准。",
      "hm.matchmaker.science_kicker": "为什么它有效",
      "hm.matchmaker.science_title": "Matchmaker 从不靠猜 —— 它靠评分。",
      "hm.matchmaker.science_sub": "每项匹配都是四个因素的加权综合。当整桌平均分超过 0.70 时，我们才发送邀请。低于此分的将留在队列中。",
      "hm.matchmaker.science_1_ttl": "性格 (35%)",
      "hm.matchmaker.science_1_body": "Big-5 性格向量的余弦相似度 —— 开放性、尽责性、外向性、宜人性和情绪稳定性。",
      "hm.matchmaker.science_2_ttl": "价值观 (25%)",
      "hm.matchmaker.science_2_body": "价值观的重合能让餐桌对话真正产生共鸣 —— 家庭、好奇心、成长、奉献、冒险。",
      "hm.matchmaker.science_3_ttl": "兴趣 (25%)",
      "hm.matchmaker.science_3_body": "针对具体兴趣的杰卡德重合度 (Jaccard overlap) —— 徒步 Six Foot Track、Sydney 电影俱乐部、Melbourne 咖啡文化、你今年的第三本书。",
      "hm.matchmaker.science_4_ttl": "空闲时间 (15%)",
      "hm.matchmaker.science_4_body": "你真正能抽出的夜晚。如果你的日历写着“周二永远健身”，我们就不会给你发周二晚 7 点的邀请。",
      "hm.matchmaker.safety_kicker": "安全设计",
      "hm.matchmaker.safety_title": "Matchmaker 保持谨慎。",
      "hm.matchmaker.safety_1_ttl": "置信度阈值",
      "hm.matchmaker.safety_1_body": "如果小组评分低于 0.70，则不发送邀请。虽然“不完美的匹配”总比“没有晚餐”强，但在我们的标准下不行。",
      "hm.matchmaker.safety_2_ttl": "互选黑名单",
      "hm.matchmaker.safety_2_body": "如果两位成员在晚餐后私下都选择了“不建议再次匹配”，我们永远不会再把他们排在一起。",
      "hm.matchmaker.safety_3_ttl": "自动熔断机制",
      "hm.matchmaker.safety_3_body": "如果近期 20% 或更多的反馈调查对某桌评分低于 2/5，邀请将暂停 24 小时，由运营人员介入审核。",
      "hm.matchmaker.safety_4_ttl": "社区准则",
      "hm.matchmaker.safety_4_body": "每位成员都要签署一份通俗易懂的行为准则。我们会移除违规者 —— 且不接受申诉。",
      "hm.matchmaker.safety_5_ttl": "仅限公共场所",
      "hm.matchmaker.safety_5_body": "每场 Matchmaker 晚餐都在有员工服务的正规餐厅进行。绝不去私人住宅。",
      "hm.matchmaker.safety_6_ttl": "活动后反馈",
      "hm.matchmaker.safety_6_body": "你的私下调研将决定未来的匹配，并可能触发人工对场地或同伴的审核。",
      "hm.matchmaker.loop_kicker": "学习闭环",
      "hm.matchmaker.loop_title": "每一餐，都让下一餐更精彩。",
      "hm.matchmaker.loop_body": "Matchmaker 不是静态的。活动结束后，你会提交一份简短的私密调查 —— 聊得怎么样，你会想和谁再次进餐，场地是否满意。这些信号会回馈给模型，重塑未来的每一次匹配。",
      "hm.matchmaker.loop_1": "伙伴间的“再次进餐”投票会强化你们的配对",
      "hm.matchmaker.loop_2": "互选“不建议再次匹配”将默默终止该配对",
      "hm.matchmaker.loop_3": "场地和对话评分会微调席位组合",
      "hm.matchmaker.loop_4": "可靠性评分保护成员免受重复爽约的困扰",
      "hm.matchmaker.loop_5": "学习偏差会逐月强化优质匹配",
      "hm.matchmaker.loop_6": "你始终拥有控制权 —— 随时可以退出或重置资料",
      "hm.matchmaker.vs_kicker": "哪个适合我？",
      "hm.matchmaker.vs_title": "Matchmaker 还是 Circles?",
      "hm.matchmaker.vs_mm_ttl": "如果我想要多样性，选我。",
      "hm.matchmaker.vs_mm_1": "每次都是不同的餐桌、城市、菜系",
      "hm.matchmaker.vs_mm_2": "AI 精选的 4-8 位志同道合的陌生人",
      "hm.matchmaker.vs_mm_3": "每周节奏，无主理承诺负担",
      "hm.matchmaker.vs_mm_4": "适合刚到新城市或正在旅行的你",
      "hm.matchmaker.vs_mm_cta": "参与匹配测试",
      "hm.matchmaker.vs_circles_ttl": "如果我想要固定节奏，选我。",
      "hm.matchmaker.vs_circles_1": "同样的主理人 · 同样的场地 · 同样的周中夜晚",
      "hm.matchmaker.vs_circles_2": "一个能变成你周三传统的小组",
      "hm.matchmaker.vs_circles_3": "由经验丰富的主理人亲自人工筛选",
      "hm.matchmaker.vs_circles_4": "适合刚搬家并想稳定下来的你",
      "hm.matchmaker.vs_circles_cta": "浏览 Circles",
      "hm.matchmaker.bottom_title": "一个测试。此后的每一餐都恰到好处。",
      "hm.matchmaker.bottom_sub": "你将在两周内获得匹配。这是你本月最容易做出的决定。",
      "hm.matchmaker.bottom_cta": "参与匹配测试",
      "hm.matchmaker.bottom_note": "无需信用卡 · 10 分钟 · 随时取消匹配",
      /* AUTO-TRANSLATED hm.* END */
    },
  },
  nl: {
    translation: {
      /* AUTO-TRANSLATED finder.* (added) */
      "finder.title": "HustleFinder",
      "finder.tagline": "Vind de dichtstbijzijnde openbare plek — echte data, door heel Australië.",
      "finder.search_placeholder": "Zoek plaatsen, straten of voorsteden…",
      "finder.hint": "Begin hierboven te typen, tik op een categorie, of gebruik \"In de buurt\".",
      "finder.near_me": "In de buurt",
      "finder.searching": "Zoeken…",
      "finder.suburb.placeholder": "Filter op voorstad (bijv. Darwin, NT)…",
      "finder.suburb.clear": "Wissen",
      "finder.search_in": "Zoeken in {{cat}}",
      "finder.empty": "Geen resultaten — probeer een andere categorie of zoekterm.",
      "finder.meta.title": "HustleFinder — gratis Australische voorstadengids",
      "finder.meta.desc": "Zoek lokale bedrijven, laders, cafés, toiletten en meer in elke Australische voorstad — 100% gratis.",
      "finder.footer.credits": "Data credits & naamsvermelding",
      "finder.footer.terms": "Voorwaarden",
      "finder.cat.toilet": "Toiletten",
      "finder.cat.fountain": "Drinkfonteinen",
      "finder.cat.transport": "Vervoer",
      "finder.cat.park": "Parken",
      "finder.cat.medical": "Medisch",
      "finder.cat.school": "Scholen",
      "finder.cat.library": "Bibliotheken",
      "finder.cat.petrol": "Benzine",
      "finder.cat.kidzone": "Kinderzone",
      "finder.cat.puzzles": "Puzzels",
      "finder.cat.roadtrip": "Roadtrip",
      "finder.hotels.title": "Vind een hotel in de buurt",
      "finder.hotels.sub": "Vergelijkt 200+ boekingssites in één zoekopdracht — mogelijk gemaakt door Trivago.",
      "finder.hotels.where": "Waar",
      "finder.hotels.where_ph": "Stad of voorstad (bijv. Byron Bay, NSW)",
      "finder.hotels.checkin": "Check-in",
      "finder.hotels.checkout": "Check-out",
      "finder.hotels.adults": "Volwassenen",
      "finder.hotels.search": "Zoek hotels",
      "finder.hotels.disclosure": "Opent Trivago — wij kunnen commissie verdienen op boekingen.",
      /* AUTO-TRANSLATED rt.* START */
      "rt.brand_name": "Roadtrip Planner",
      "rt.page_title": "Australische EV Roadtrip Planner",
      "rt.from": "Van",
      "rt.to": "Naar",
      "rt.from_placeholder": "Wijk, dorp of stad (bijv. Newcastle NSW)",
      "rt.to_placeholder": "Wijk, dorp of stad (bijv. Hobart TAS)",
      "rt.stop": "STOP",
      "rt.stop_placeholder": "Tussenstop",
      "rt.add_stop": "Stop toevoegen",
      "rt.upgrade_pro": "Upgrade naar Pro voor max. 20 stops",
      "rt.reverse": "Omdraaien (bereken terugreis)",
      "rt.from_current": "Vanaf huidige locatie",
      "rt.range_per_charge": "Actieradius per lading (km)",
      "rt.connector": "Connector",
      "rt.connector_any": "Alle",
      "rt.drive_hours_day": "Rijuren per dag",
      "rt.accommodation": "Accommodatie",
      "rt.accom_any": "Alle (BnB · Hotel · Caravan)",
      "rt.accom_bnb": "BnB",
      "rt.accom_hotel": "Hotel",
      "rt.accom_caravan": "Caravanpark",
      "rt.min_rating": "Minimale score",
      "rt.any_rating": "Alle scores",
      "rt.only_5star": "Alleen 5★",
      "rt.schedule": "Planning:",
      "rt.depart_at": "Vertrek om",
      "rt.arrive_by": "Aankomst om",
      "rt.hotel_checkin": "Hotel inchecken",
      "rt.hotel_checkout": "Hotel uitchecken",
      "rt.overnight_hint": "Overnachtingen worden niet buiten deze tijden gepland.",
      "rt.whos_in_car": "Wie reizen er mee?",
      "rt.adults": "Volwassenen",
      "rt.kids": "Kinderen",
      "rt.ages": "Leeftijden",
      "rt.show_attractions": "Toon toeristische attracties",
      "rt.show_kids": "Toon kindvriendelijke stops",
      "rt.overnight_stays": "Overnachtingen",
      "rt.fast_chargers_only": "Alleen snelladers",
      "rt.hotel_must_charger": "Hotel moet lader hebben",
      "rt.pet_only": "Alleen huisdiervriendelijke verblijven",
      "rt.safety": "Veiligheidsmarge",
      "rt.safety_90": "90% (vlak)",
      "rt.safety_85": "85% (standaard)",
      "rt.safety_80": "80% (heuvels / slepen)",
      "rt.safety_70": "70% (zware belading)",
      "rt.save_trip": "Reis opslaan",
      "rt.plan_my_trip": "Plan mijn reis",
      "rt.refresh_trip": "Reis vernieuwen",
      "rt.planning": "Plannen…",
      "rt.resume_title": "Reis vanaf hier hervatten",
      "rt.resume_body": "We hebben je huidige locatie vastgesteld. Voer de resterende actieradius van je dashboard in, zodat we de volgende etappe plannen op basis van wat je echt hebt — niet een volle lading.",
      "rt.remaining_range": "Resterende actieradius (km)",
      "rt.cancel": "Annuleren",
      "rt.replan_from_here": "Vanaf hier herplannen",
      "rt.save_modal_title": "Geef deze reis een naam",
      "rt.save_modal_body": "Opgeslagen reizen staan in je profiel — open ze wanneer je wilt en klik op Herplannen.",
      "rt.save_name_placeholder": "bijv. Brisbane → Sydney via Byron",
      "rt.your_saved_trips": "Jouw opgeslagen reizen",
      "rt.custom_location": "Aangepaste locatie",
      "rt.trip_summary": "Reisoverzicht",
      "rt.itinerary": "Reisschema",
      "rt.no_stops_needed": "Geen stops nodig — de hele reis past binnen je actieradius.",
      "rt.chargers_in_corridor": "laders op de route",
      "rt.planned_stops": "geplande stops",
      "rt.overnight_stop": "overnachting",
      "rt.overnight_stops": "overnachtingen",
      "rt.suggested_detours": "Voorgestelde omwegen — vink aan om toe te voegen",
      "rt.replan_with_detours": "Herplannen met omwegen",
      "rt.attractions_label": "Attractie",
      "rt.kids_label": "Kinderen",
      "rt.km_from_start": "km vanaf start",
      "rt.open_driving": "Open rijmodus",
      "rt.big_font_hint": "Groot lettertype, geschikt voor houder. Werkt met CarPlay/Android Auto browser-mirror.",
      "rt.check_price_book": "Check prijs & boek op Trivago ↗",
      "rt.visit_site": "Bezoek website ↗",
      "rt.charger_onsite": "Lader op locatie",
      "rt.leg_exceeds_range": "Deze etappe overschrijdt je ingestelde actieradius — je zou zonder stroom komen te zitten voordat je deze lader bereikt. Verlaag de geschatte actieradius of voeg een tussenstop toe.",
      "rt.add_waypoint_replan": "Toevoegen als tussenstop & herplannen",
      /* AUTO-TRANSLATED rt.* END */
                      /* AUTO-TRANSLATED hm.* START */
      "hm.nav.hustlemeet": "HustleMeet",
      "hm.nav.circles": "Circles",
      "hm.nav.matchmaker": "Matchmaker",
      "hm.nav.meet": "Meet",
      "hm.nav.overview": "HustleMeetups",
      "hm.overview.page_title": "HustleMeetups — drie warme manieren om je mensen te vinden.",
      "hm.overview.kicker": "Onderdeel van HustleSuite",
      "hm.overview.hero_title": "Drie warme manieren om je mensen te vinden.",
      "hm.overview.hero_title_accent": "je mensen",
      "hm.overview.hero_sub": "HustleMeetups is Australia’s vriendelijkste manier om echte vriendschappen op te bouwen — kies de vibe die bij je past. Of je nu een wekelijkse hardloopgroep zoekt, een maandelijks diner met je eigen vrienden, of een door AI samengestelde tafel vol nieuwe gezichten, er is altijd een plek voor jou.",
      "hm.overview.tile_meet_kicker": "HustleMeet",
      "hm.overview.tile_meet_title": "Vind je mensen. Doe je ding.",
      "hm.overview.tile_meet_body": "Australia’s thuisbasis voor groepen gebaseerd op interesses. Hardlopers, coders, investeerders, wandelaars, dansers, ontbijtclubs, boekenwurmen — honderden groepen, elke week echte evenementen. Kom één keer langs, of elke week.",
      "hm.overview.tile_meet_cta": "Ontdek groepen",
      "hm.overview.tile_circles_kicker": "Circles",
      "hm.overview.tile_circles_title": "Jouw crew. Elke keer een nieuwe tafel.",
      "hm.overview.tile_circles_body": "Breng je vaste vriendengroep mee. Wij boeken een nieuw restaurant, regelen de trivia en delen de kaarten. Jij hoeft alleen maar te verschijnen. De beste manier om een bestaande crew dichtbij te houden — zonder dat iemand het hoeft te organiseren.",
      "hm.overview.tile_circles_cta": "Start een Circle",
      "hm.overview.tile_matchmaker_kicker": "Matchmaker",
      "hm.overview.tile_matchmaker_title": "AI kiest de tafel. Jij schuift aan.",
      "hm.overview.tile_matchmaker_body": "Doe een quiz van 10 minuten. Binnen een dag zetten we je aan tafel met vreemden die tegen het dessert als oude vrienden voelen. Elke keer een andere tafel.",
      "hm.overview.tile_matchmaker_cta": "Doe de quiz",
      "hm.overview.trust_kicker": "Waarom het werkt",
      "hm.overview.trust_title": "Eerst vertrouwen. Dan de tafel.",
      "hm.overview.trust_1_title": "Geverifieerde leden",
      "hm.overview.trust_1_body": "Elke groep begint met een echte ID-check + betrouwbaarheidsscore. Geen wegwerpaccounts.",
      "hm.overview.trust_2_title": "Echte hosts",
      "hm.overview.trust_2_body": "Elke groep wordt geleid door een gescreende host die de vibe bewaakt, het ritme bepaalt en voor de groep zorgt.",
      "hm.overview.trust_3_title": "Vriendschap eerst",
      "hm.overview.trust_3_body": "Alles hier is ontworpen voor echte vriendschappen, niet voor dating. Nooit.",
      "hm.overview.trust_4_title": "Zero-tolerance moderatie",
      "hm.overview.trust_4_body": "Eén melding, één beoordeling, één besluit. Mensen met slechte bedoelingen worden dezelfde avond nog verwijderd.",
      "hm.overview.footer_line": "Onderdeel van HustleSuite — eigendom van en gehost in Australia.",
      "hm.landing.page_title": "HustleMeet · Vind je mensen. Doe je ding.",
      "hm.landing.kicker": "Australia’s interessenetwerk",
      "hm.landing.hero_title": "Vind je mensen. Doe je ding.",
      "hm.landing.hero_title_accent": "je mensen",
      "hm.landing.hero_sub": "HustleMeet is Australia’s plek voor groepen op basis van interesses. Software devs die debuggen tijdens de lunch. Hardlopers om 6 uur 's ochtends. Investeerders die maandelijks samenkomen. Wandelgroepen, danscrews, ontbijtclubs, bordspelavonden. Kies een groep, boek het volgende evenement en kom langs.",
      "hm.landing.cta_explore_circles": "Groepen bekijken",
      "hm.landing.cta_try_matchmaker": "Start je eigen groep",
      "hm.landing.trust_verified": "Geverifieerde leden",
      "hm.landing.trust_venues": "Gescreende event hosts",
      "hm.landing.trust_friendship": "Vriendschap eerst — nooit dating",
      "hm.landing.trust_moderation": "Zero-tolerance moderatie",
      "hm.landing.subbrand_circles_kicker": "Circles",
      "hm.landing.subbrand_circles_title": "Heb je al een crew? Geef ze een vaste plek.",
      "hm.landing.subbrand_circles_body": "Circles is voor vriendengroepen die elkaar al ontmoeten — boekenclub, pokeravond, hardloopmaatjes, collega's. Wij regelen de locatie, de herinneringen en de planning. Jij hoeft alleen maar te verschijnen.",
      "hm.landing.subbrand_circles_b1": "Wisselende locaties, één vaste planning",
      "hm.landing.subbrand_circles_b2": "Auto-reminders + makkelijke RSVP's",
      "hm.landing.subbrand_circles_b3": "Trivia, kaarten & thema-avonden binnen handbereik",
      "hm.landing.subbrand_circles_cta": "Start een Circle",
      "hm.landing.subbrand_matchmaker_kicker": "Matchmaker",
      "hm.landing.subbrand_matchmaker_title": "AI kiest de tafel. Jij schuift aan.",
      "hm.landing.subbrand_matchmaker_body": "Eén quiz van 10 minuten — persoonlijkheid, waarden, beschikbaarheid. Onze Matchmaker zet je aan tafel met vreemden die tegen het dessert als oude vrienden voelen. Elke keer een andere tafel.",
      "hm.landing.subbrand_matchmaker_b1": "Big-5 persoonlijkheid & waarden gescoord",
      "hm.landing.subbrand_matchmaker_b2": "Direct uitnodigingen zodra je match 0.70 of hoger is",
      "hm.landing.subbrand_matchmaker_b3": "Leert van elk diner dat je hebt",
      "hm.landing.subbrand_matchmaker_cta": "Doe de quiz",
      "hm.landing.how_kicker": "Hoe HustleMeet werkt",
      "hm.landing.how_title": "Vier stappen. Vind je groep. Kom langs.",
      "hm.landing.how_step1_ttl": "Kies je interesses",
      "hm.landing.how_step1_sub": "Hardlopen, coderen, beleggen, koken, muziek, wandelen — vertel ons wat je leuk vindt en waar je woont.",
      "hm.landing.how_step2_ttl": "Bekijk groepen bij jou in de buurt",
      "hm.landing.how_step2_sub": "Elke groep toont zijn host, het ritme en het volgende evenement. Bekijk de vibe voordat je meedoet.",
      "hm.landing.how_step3_ttl": "Meld je aan voor het volgende event",
      "hm.landing.how_step3_sub": "Eén klik, één agenda-uitnodiging, één duidelijke herinnering de dag van tevoren. Sommige events zijn gratis, sommige betaald — de prijs is altijd duidelijk.",
      "hm.landing.how_step4_ttl": "Kom langs & verbind",
      "hm.landing.how_step4_sub": "Ontmoet de host, ontmoet de groep, doe je ding. Kom volgende week terug als het klikte.",
      "hm.landing.stats_kicker": "Australia’s nieuwe manier van ontmoeten",
      "hm.landing.stats_title": "De reden dat mensen blijven terugkomen.",
      "hm.landing.stats_92_sub": "Vindt dat ze met minstens één persoon op het evenement een klik hadden",
      "hm.landing.stats_68_sub": "Meldt zich binnen een week aan voor hun volgende evenement",
      "hm.landing.stats_85_sub": "Blijft in contact met iemand uit de groep",
      "hm.landing.why_kicker": "Waarom we werken",
      "hm.landing.why_title": "Echte interesses. Echte hosts. Echte events.",
      "hm.landing.why_1_ttl": "Interesse eerst, niet locatie eerst",
      "hm.landing.why_1_body": "De mensen in je groep geven echt om de dingen waar jij om geeft — dat is de shortcut naar echte vriendschap.",
      "hm.landing.why_2_ttl": "Echte event hosts",
      "hm.landing.why_2_body": "Elke groep heeft een gescreende host die de sfeer bewaakt, het ritme bepaalt en ingrijpt als er iets niet klopt. Geen Facebook-beheerder, maar een echt mens.",
      "hm.landing.why_3_ttl": "Consistentie is belangrijk",
      "hm.landing.why_3_body": "Elke week nieuwe evenementen. Iets om naar uit te kijken. Een ritme dat vreemden verandert in je vaste dinsdagavond-crew.",
      "hm.landing.why_4_ttl": "Veilig & vertrouwd",
      "hm.landing.why_4_body": "Geverifieerde leden, openbare locaties, zero tolerance voor respectloos gedrag. Ons supportteam is bereikbaar voor, tijdens en na elk evenement.",
      "hm.landing.why_5_ttl": "Vriendschap eerst",
      "hm.landing.why_5_body": "Dit is geen dating. Het is community. Echte interesses, echte mensen, geen swipe-gedoe tussendoor.",
      "hm.landing.why_6_ttl": "Klein genoeg om ertoe te doen",
      "hm.landing.why_6_body": "De meeste groepen hebben een limiet van 4-15 plekken. Groot genoeg voor echte gesprekken, klein genoeg zodat niemand buiten de boot valt.",
      "hm.landing.cities_kicker": "Waar we actief zijn",
      "hm.landing.cities_title": "Groepen komen samen door heel Australia & NZ.",
      "hm.landing.cities_sub_zero": "Eerste groepen openen in meer dan 15 steden — sluit je vroeg aan en geef de groep vorm.",
      "hm.landing.cities_sub_some": "Groepen zijn actief in meer dan 15 regio's. Deze week nog iets in jouw stad.",
      "hm.landing.cities_sub_one": "{{count}} actieve groep · elke week nieuwe events · we voegen constant steden toe.",
      "hm.landing.cities_sub_many": "{{count}} actieve groepen · elke week nieuwe events · we voegen constant steden toe.",
      "hm.landing.forme_kicker": "Dit is voor jou als…",
      "hm.landing.forme_title": "Je wilt echte interesses. Echte mensen. Echte events.",
      "hm.landing.forme_1": "Je klaar bent met de swipe-app vermoeidheid en mensen wilt ontmoeten rondom wat je echt graag doet.",
      "hm.landing.forme_2": "Je naar een nieuwe stad bent verhuisd en een fijne crew nodig hebt, geen zoveelste chatgroep.",
      "hm.landing.forme_3": "Je diepgang belangrijker vindt dan bereik — één goede groep is beter dan vijftig volgers.",
      "hm.landing.forme_4": "Je gelooft dat gedeelde interesses + twee uur in het echt de echte cheatcode zijn.",
      "hm.landing.notme_kicker": "Dit is niet voor jou als…",
      "hm.landing.notme_title": "HustleMeet werkt niet voor iedereen.",
      "hm.landing.notme_1": "Je niet kunt beloven dat je komt opdagen — hosts en leden rekenen op je RSVP.",
      "hm.landing.notme_2": "Je op zoek bent naar een datingservice — vriendschappen staan hier op de eerste plaats.",
      "hm.landing.notme_3": "Je wilt zenden zonder iemand te ontmoeten — dit werkt alleen in persoon.",
      "hm.landing.notme_4": "Je eindeloze gratis extraatjes verwacht — sommige events kosten geld. De prijs is altijd vooraf bekend.",
      "hm.landing.safety_kicker": "Onze belofte voor veiligheid",
      "hm.landing.safety_title": "Warm, maar nooit onvoorzichtig.",
      "hm.landing.safety_1_ttl": "Openbare locaties",
      "hm.landing.safety_1_body": "Elk evenement vindt plaats op een openbare locatie met personeel — een café, restaurant, park, sportschool of studio. Nooit bij iemand thuis, tenzij elke deelnemer hiermee instemt.",
      "hm.landing.safety_2_ttl": "Community-normen",
      "hm.landing.safety_2_body": "Zero tolerance voor respectloos gedrag, intimidatie of oneerlijkheid. Leden die de regels overtreden worden onmiddellijk verwijderd.",
      "hm.landing.safety_3_ttl": "Privacy beschermd",
      "hm.landing.safety_3_body": "Je gegevens worden nooit openbaar gedeeld. Je bepaalt zelf met wie je na elk evenement in contact blijft.",
      "hm.landing.safety_4_ttl": "Support binnen handbereik",
      "hm.landing.safety_4_body": "Ons team is beschikbaar voor, tijdens en na elk evenement als je vragen of zorgen hebt.",
      "hm.landing.bottom_title": "Je volgende favoriete groep is één klik verwijderd.",
      "hm.landing.bottom_sub": "Kies je pad — sluit je aan bij een bestaande groep, breng je crew onder in een Circle, of laat de Matchmaker je aan een tafel zetten die al bij je past.",
      "hm.landing.bottom_cta_circles": "Start een Circle",
      "hm.landing.bottom_cta_mm": "Doe de Matchmaker quiz",
      "hm.circles.page_title": "HustleMeet Circles · Jouw crew, elke keer een nieuwe locatie",
      "hm.circles.kicker": "HustleMeet · Circles",
      "hm.circles.hero_title": "Jouw crew. Elke keer een nieuwe tafel.",
      "hm.circles.hero_title_accent": "Jouw crew",
      "hm.circles.hero_sub": "Heb je je vrienden al? Circles is de makkelijkste manier om ze dichtbij te houden. Wij boeken de locatie, plannen de trivia, delen de kaarten. Jij komt gewoon opdagen — dezelfde crew, nieuwe avond, nieuwe plek.",
      "hm.circles.cta_find": "Start een Circle",
      "hm.circles.cta_host_hero": "Word lid van een open Circle",
      "hm.circles.trust_venue": "Wisselende gescreende locaties",
      "hm.circles.trust_party": "4-12 vrienden per Circle",
      "hm.circles.trust_verified": "Geverifieerde leden",
      "hm.circles.trust_friendship": "Echte vriendschappen, dichtbij gehouden",
      "hm.circles.how_kicker": "Hoe het werkt",
      "hm.circles.how_title": "Vier stappen. Jouw crew blijft een crew.",
      "hm.circles.how_step1_ttl": "Start je Circle",
      "hm.circles.how_step1_sub": "Geef het een naam, nodig je vrienden uit (tot 12), en kies een ritme — wekelijks, om de week of maandelijks.",
      "hm.circles.how_step2_ttl": "Wij kiezen de locatie",
      "hm.circles.how_step2_sub": "Voor elk event stellen we een nieuwe plek voor — restaurant, trivia-avond, kaartkamer, wijnbar. Of kies een vaste favoriet.",
      "hm.circles.how_step3_ttl": "Iedereen RSVP't met één klik",
      "hm.circles.how_step3_sub": "Auto-reminders, kostenverdeling, vooraf betaalde plekken zodat no-shows de planning niet verpesten. De rekening is geregeld voordat het voorgerecht op tafel staat.",
      "hm.circles.how_step4_ttl": "Houd het ritme vast",
      "hm.circles.how_step4_sub": "Eén avond in de agenda die nooit meer georganiseerd hoeft te worden. De groep wordt hechter over de maanden, niet minuten.",
      "hm.circles.cadence_weekly_ttl": "Wekelijks",
      "hm.circles.cadence_weekly_sub": "Elke week",
      "hm.circles.cadence_weekly_body": "Voor crews die een dinsdagavondritueel willen — dezelfde gezichten, elke keer een nieuwe plek.",
      "hm.circles.cadence_fortnight_ttl": "Om de week",
      "hm.circles.cadence_fortnight_sub": "Elke 2 weken",
      "hm.circles.cadence_fortnight_body": "De ideale balans — regelmatig zonder te veel druk op drukke agenda's.",
      "hm.circles.cadence_monthly_ttl": "Maandelijks",
      "hm.circles.cadence_monthly_sub": "Elke maand",
      "hm.circles.cadence_monthly_body": "Circles voor speciale gelegenheden — Sunday roasts, grote trivia-avonden, degustatie, poker.",
      "hm.circles.feast_kicker": "Kies je avond",
      "hm.circles.feast_title": "Drie manieren om het te besteden",
      "hm.circles.feast_sub": "Elke Circle kiest hoe de avond verloopt. Wissel ze af, of kies de favoriet van je crew.",
      "hm.circles.feast_surprise_ttl": "Probeer een nieuw restaurant",
      "hm.circles.feast_surprise_body": "Wij selecteren drie locaties bij jou in de buurt, iedereen stemt, de keuken bereidt zich voor op een volle tafel. Kosten gedeeld, plekken vooraf betaald.",
      "hm.circles.feast_preselect_ttl": "Trivia / kaarten / spellen",
      "hm.circles.feast_preselect_body": "Boek een trivia-avond, een pokerroom, bordspel-bar of bowlingbaan. Wij regelen de locatie, de inleg en de herinneringen.",
      "hm.circles.feast_per_event_ttl": "Eenmalige thema-avond",
      "hm.circles.feast_per_event_body": "Wijnproeverij, chef's table, cocktailworkshop, escape room. Af en toe iets speciaals — volledig georganiseerd.",
      "hm.circles.live_kicker": "Nu actief",
      "hm.circles.live_title": "Circles waar je bij kunt",
      "hm.circles.live_host_link": "Start je eigen Circle",
      "hm.circles.live_loading": "Laden…",
      "hm.circles.live_empty_title": "Wees de eerste Circle in jouw stad.",
      "hm.circles.live_empty_sub": "Start er een met drie vrienden en wij helpen je de rest uit te nodigen — plus de eerste drie locaties krijg je van ons.",
      "hm.circles.live_empty_cta": "Start een Circle",
      "hm.circles.host_kicker": "Voor Circle-leiders",
      "hm.circles.host_title": "Jij brengt de crew. Wij doen de admin.",
      "hm.circles.host_body": "Als jij de vriend bent die altijd de avond organiseert, bespaart Circles je uren werk. Auto-reminders, vooraf betaalde RSVP's, locatie-shortlists, kostenverdeling, wachtlijstbeheer en een cashback-wallet voor elke avond uit. Je schuift gewoon aan als een normaal lid.",
      "hm.circles.host_perk_1": "Auto-locatie shortlists — nooit meer scrollen op Google",
      "hm.circles.host_perk_2": "Vooraf betaalde plekken — nul no-shows",
      "hm.circles.host_perk_3": "Kostenverdeling geregeld voor het dessert",
      "hm.circles.host_perk_4": "Herinneringen + agenda-uitnodigingen op de automatische piloot",
      "hm.circles.host_perk_5": "Wachtlijst met auto-promotie als een vriend afzegt",
      "hm.circles.host_perk_6": "Cashback-wallet op elke avond uit",
      "hm.circles.host_cta": "Start nu een Circle",
      "hm.circles.why_kicker": "Waarom Circles werken",
      "hm.circles.why_title": "Vriendschappen tussen volwassenen hebben infrastructuur nodig",
      "hm.circles.why_1_ttl": "Zelfde crew, dieper vertrouwen",
      "hm.circles.why_1_body": "Het leven drijft vrienden uit elkaar. Circles zijn het vaste ritme dat iedereen dichtbij houdt — dezelfde 4-12 vrienden, op dezelfde doordeweekse avond, wat er ook gebeurt.",
      "hm.circles.why_2_ttl": "Geen admin-moeheid",
      "hm.circles.why_2_body": "Niemand hoeft de WhatsApp-beheerder van de groep te zijn. Wij kiezen de locaties, sturen de herinneringen, regelen de betalingen en sluiten de cirkel.",
      "hm.circles.why_3_ttl": "Nieuwe locatie, nieuwe herinnering",
      "hm.circles.why_3_body": "Wissel af tussen Australia’s beste kleine restaurants, trivia-kamers en spelletjesbars. Vertrouwde gezichten, nieuwe decors — de ideale mix van routine en vernieuwing.",
      "hm.circles.bottom_title": "De avond waar je crew naar uitkijkt, zonder dat iemand het hoeft te plannen.",
      "hm.circles.bottom_sub": "Eén Circle. Dezelfde vrienden. Elke keer een nieuwe locatie.",
      "hm.circles.bottom_cta_find": "Start een Circle",
      "hm.circles.bottom_cta_mm": "Ontmoet nieuwe mensen met Matchmaker →",
      "hm.circles.card_host_pill": "Circle",
      "hm.matchmaker.page_title": "HustleMeet Matchmaker · AI kiest de juiste tafel",
      "hm.matchmaker.kicker": "HustleMeet · Matchmaker",
      "hm.matchmaker.hero_title": "AI kiest de tafel. Jij schuift gewoon aan.",
      "hm.matchmaker.hero_title_accent": "Jij schuift gewoon aan.",
      "hm.matchmaker.hero_sub": "Beantwoord een korte quiz over persoonlijkheid en waarden. Onze Matchmaker zet je aan tafel met vier tot zeven vreemden die tegen het dessert als oude vrienden voelen. Geen geswipe. Geen smalltalk. Gewoon een gedeelde tafel, klaar wanneer jij dat bent.",
      "hm.matchmaker.cta_quiz": "Doe de matching quiz",
      "hm.matchmaker.cta_see_circles": "Bekijk Circles in plaats daarvan →",
      "hm.matchmaker.trust_big5": "Big-5 persoonlijkheid gescoord",
      "hm.matchmaker.trust_seats": "4-8 plekken per tafel",
      "hm.matchmaker.trust_lock": "Zero-swipe · zero-foto",
      "hm.matchmaker.trust_verified": "Alleen geverifieerde leden",
      "hm.matchmaker.how_kicker": "Hoe het werkt",
      "hm.matchmaker.how_title": "Vier stappen naar een tafel die al past.",
      "hm.matchmaker.how_step1_ttl": "Doe de quiz",
      "hm.matchmaker.how_step1_sub": "Vragenlijst van tien minuten — persoonlijkheid, waarden, dieetwensen, beschikbaarheid. Je doet het één keer.",
      "hm.matchmaker.how_step2_ttl": "Matching binnen een dag",
      "hm.matchmaker.how_step2_sub": "Onze AI analyseert je profiel en de actieve diners in Australia. Zodra je match-score 0.70 of hoger is, wordt er een persoonlijke uitnodiging opgesteld.",
      "hm.matchmaker.how_step3_ttl": "Accepteren of weigeren",
      "hm.matchmaker.how_step3_sub": "Je ontvangt één duidelijke uitnodiging per e-mail met de locatie, het thema van de tafel en de drie interesses die je deelt met de groep.",
      "hm.matchmaker.how_step4_ttl": "Het leert van je",
      "hm.matchmaker.how_step4_sub": "Elke enquête na het diner verbetert het model. Je volgende match wordt slimmer naarmate je vaker dineert.",
      "hm.matchmaker.science_kicker": "Waarom het werkt",
      "hm.matchmaker.science_title": "De Matchmaker gokt niet — hij scoort.",
      "hm.matchmaker.science_sub": "Elke match is een gewogen combinatie van vier factoren. Wanneer het gemiddelde van de hele tafel boven de 0.70 ligt, sturen we de uitnodigingen. Alles daaronder blijft in de wachtrij.",
      "hm.matchmaker.science_1_ttl": "Persoonlijkheid (35%)",
      "hm.matchmaker.science_1_body": "Cosine similarity over de Big-5 persoonlijkheidsvector — openheid, consciëntieusheid, extraversie, vriendelijkheid en emotionele stabiliteit.",
      "hm.matchmaker.science_2_ttl": "Waarden (25%)",
      "hm.matchmaker.science_2_body": "Overeenkomst in waarden die een tafelgesprek echt laten slagen — familie, nieuwsgierigheid, groei, dienstbaarheid, avontuur.",
      "hm.matchmaker.science_3_ttl": "Interesses (25%)",
      "hm.matchmaker.science_3_body": "Jaccard overlap op je specifieke interesses — de Six Foot Track wandelen, Sydney filmclub, Melbourne koffiescene, je derde boek van het jaar.",
      "hm.matchmaker.science_4_ttl": "Beschikbaarheid (15%)",
      "hm.matchmaker.science_4_body": "De avond dat je ook echt kunt. Geen uitnodiging voor dinsdag 19:00 uur als je agenda zegt: sporten-op-dinsdag-voor-altijd.",
      "hm.matchmaker.safety_kicker": "Veiligheid door ontwerp",
      "hm.matchmaker.safety_title": "De Matchmaker is stilletjes voorzichtig.",
      "hm.matchmaker.safety_1_ttl": "Vertrouwensdrempel",
      "hm.matchmaker.safety_1_body": "Onder een groepsscore van 0.70 gaat er geen uitnodiging uit. Een matige match is beter dan geen diner — totdat het dat niet is.",
      "hm.matchmaker.safety_2_ttl": "Wederzijdse blokkeerlijst",
      "hm.matchmaker.safety_2_body": "Als twee leden na een diner privé 'nee' aangeven, zetten we ze nooit meer bij elkaar aan tafel.",
      "hm.matchmaker.safety_3_ttl": "Automatische stopknop",
      "hm.matchmaker.safety_3_body": "Als 20% of meer van de recente enquêtes een tafel 2/5 of lager beoordeelt, pauzeren de uitnodigingen voor 24 uur terwijl een operator de boel beoordeelt.",
      "hm.matchmaker.safety_4_ttl": "Community-normen",
      "hm.matchmaker.safety_4_body": "Elk lid accepteert een gedragscode in begrijpelijk Nederlands. We verwijderen mensen die deze overtreden — zonder beroep.",
      "hm.matchmaker.safety_5_ttl": "Alleen openbare locaties",
      "hm.matchmaker.safety_5_body": "Elk Matchmaker diner vindt plaats in een echt restaurant met personeel. Nooit bij iemand thuis.",
      "hm.matchmaker.safety_6_ttl": "Feedback na het evenement",
      "hm.matchmaker.safety_6_body": "Je privé-enquête vormt toekomstige matches en kan een beoordeling van de locatie of een mede-lid door een moderator triggeren.",
      "hm.matchmaker.loop_kicker": "De leer-loop",
      "hm.matchmaker.loop_title": "Elk diner maakt je volgende diner beter.",
      "hm.matchmaker.loop_body": "De Matchmaker is niet statisch. Na elk evenement vul je een korte privé-enquête in — hoe het gesprek voelde, met wie je nog eens zou willen dineren en of de locatie beviel. Die signalen gaan terug naar het model en beïnvloeden elke toekomstige match.",
      "hm.matchmaker.loop_1": "Stemmen van anderen om \"nog eens te dineren\" versterken je koppelingen",
      "hm.matchmaker.loop_2": "Wederzijdse 'nee'-stemmen halen paren stilletjes uit elkaar",
      "hm.matchmaker.loop_3": "Beoordelingen van locaties en gesprekken verfijnen de tafelmix",
      "hm.matchmaker.loop_4": "De betrouwbaarheidsscore beschermt leden tegen herhaaldelijke no-shows",
      "hm.matchmaker.loop_5": "Leervoorkeur versterkt goede matches maand na maand",
      "hm.matchmaker.loop_6": "Jij houdt de controle — meld je af of reset je profiel op elk moment",
      "hm.matchmaker.vs_kicker": "Wat is geschikt voor mij?",
      "hm.matchmaker.vs_title": "Matchmaker of Circles?",
      "hm.matchmaker.vs_mm_ttl": "Kies mij als ik variatie wil.",
      "hm.matchmaker.vs_mm_1": "Elke keer een andere tafel, stad en keuken",
      "hm.matchmaker.vs_mm_2": "Door AI samengestelde groep van 4-8 compatibele vreemden",
      "hm.matchmaker.vs_mm_3": "Wekelijks ritme, geen verplichting als host",
      "hm.matchmaker.vs_mm_4": "Ideaal als je nieuw bent in een stad of op reis bent",
      "hm.matchmaker.vs_mm_cta": "Doe de matching quiz",
      "hm.matchmaker.vs_circles_ttl": "Kies mij als ik vastigheid wil.",
      "hm.matchmaker.vs_circles_1": "Zelfde host · zelfde locatie · zelfde doordeweekse avond",
      "hm.matchmaker.vs_circles_2": "Een groep die uitgroeit tot je vaste woensdagtraditie",
      "hm.matchmaker.vs_circles_3": "Door mensen samengesteld door een ervaren host",
      "hm.matchmaker.vs_circles_4": "Ideaal als je net verhuisd bent en ergens wilt aarden",
      "hm.matchmaker.vs_circles_cta": "Bekijk Circles",
      "hm.matchmaker.bottom_title": "Eén quiz. Elk diner daarna past al bij je.",
      "hm.matchmaker.bottom_sub": "Je wordt binnen twee weken gekoppeld. Het is de makkelijkste 'ja' die je deze maand zult zeggen.",
      "hm.matchmaker.bottom_cta": "Doe de matching quiz",
      "hm.matchmaker.bottom_note": "Geen creditcard nodig · 10 minuten · Op elk moment opzeggen",
      /* AUTO-TRANSLATED hm.* END */
    },
  },
  pl: {
    translation: {
      /* AUTO-TRANSLATED finder.* (added) */
      "finder.title": "HustleFinder",
      "finder.tagline": "Znajdź najbliższe miejsce publiczne — prawdziwe dane z całej Australii.",
      "finder.search_placeholder": "Szukaj miejsc, ulic lub przedmieść…",
      "finder.hint": "Zacznij pisać powyżej, dotknij kategorii lub użyj „W pobliżu\".",
      "finder.near_me": "W pobliżu",
      "finder.searching": "Wyszukiwanie…",
      "finder.suburb.placeholder": "Filtruj według przedmieścia (np. Darwin, NT)…",
      "finder.suburb.clear": "Wyczyść",
      "finder.search_in": "Szukaj w {{cat}}",
      "finder.empty": "Brak wyników — spróbuj innej kategorii lub hasła wyszukiwania.",
      "finder.meta.title": "HustleFinder — darmowy katalog przedmieść Australii",
      "finder.meta.desc": "Szukaj lokalnych firm, ładowarek, kawiarni, toalet i więcej w każdym australijskim przedmieściu — w 100% za darmo.",
      "finder.footer.credits": "Źródła danych i atrybucja",
      "finder.footer.terms": "Regulamin",
      "finder.cat.toilet": "Toalety",
      "finder.cat.fountain": "Poidełka",
      "finder.cat.transport": "Transport",
      "finder.cat.park": "Parki",
      "finder.cat.medical": "Medyczne",
      "finder.cat.school": "Szkoły",
      "finder.cat.library": "Biblioteki",
      "finder.cat.petrol": "Stacje benzynowe",
      "finder.cat.kidzone": "Strefa dla dzieci",
      "finder.cat.puzzles": "Łamigłówki",
      "finder.cat.roadtrip": "Wycieczka",
      "finder.hotels.title": "Znajdź hotel w pobliżu",
      "finder.hotels.sub": "Porównuje ponad 200 serwisów rezerwacyjnych w jednym wyszukiwaniu — obsługiwane przez Trivago.",
      "finder.hotels.where": "Gdzie",
      "finder.hotels.where_ph": "Miasto lub przedmieście (np. Byron Bay, NSW)",
      "finder.hotels.checkin": "Zameldowanie",
      "finder.hotels.checkout": "Wymeldowanie",
      "finder.hotels.adults": "Dorośli",
      "finder.hotels.search": "Szukaj hoteli",
      "finder.hotels.disclosure": "Otwiera Trivago — możemy otrzymać prowizję od rezerwacji.",
      /* AUTO-TRANSLATED rt.* START */
      "rt.brand_name": "Planer podróży",
      "rt.page_title": "Australijski planer podróży elektrykiem",
      "rt.from": "Z",
      "rt.to": "Do",
      "rt.from_placeholder": "Dzielnica, miasteczko lub miasto (np. Newcastle NSW)",
      "rt.to_placeholder": "Dzielnica, miasteczko lub miasto (np. Hobart TAS)",
      "rt.stop": "STOP",
      "rt.stop_placeholder": "Przystanek pośredni",
      "rt.add_stop": "Dodaj przystanek",
      "rt.upgrade_pro": "Przejdź na Pro, aby dodać do 20 przystanków",
      "rt.reverse": "Odwróć (oblicz drogę powrotną)",
      "rt.from_current": "Z obecnej lokalizacji",
      "rt.range_per_charge": "Zasięg na ładowaniu (km)",
      "rt.connector": "Złącze",
      "rt.connector_any": "Dowolne",
      "rt.drive_hours_day": "Godziny jazdy/dzień",
      "rt.accommodation": "Zakwaterowanie",
      "rt.accom_any": "Dowolne (BnB · Hotel · Kemping)",
      "rt.accom_bnb": "BnB",
      "rt.accom_hotel": "Hotel",
      "rt.accom_caravan": "Kemping",
      "rt.min_rating": "Minimalna ocena",
      "rt.any_rating": "Dowolna ocena",
      "rt.only_5star": "Tylko 5★",
      "rt.schedule": "Harmonogram:",
      "rt.depart_at": "Wyjazd o",
      "rt.arrive_by": "Przyjazd do",
      "rt.hotel_checkin": "Zameldowanie",
      "rt.hotel_checkout": "Wymeldowanie",
      "rt.overnight_hint": "Odcinki nocne nie zostaną zaplanowane poza tymi godzinami.",
      "rt.whos_in_car": "Kto jedzie w aucie?",
      "rt.adults": "Dorośli",
      "rt.kids": "Dzieci",
      "rt.ages": "Wiek",
      "rt.show_attractions": "Pokaż atrakcje turystyczne",
      "rt.show_kids": "Pokaż miejsca przyjazne dzieciom",
      "rt.overnight_stays": "Noclegi",
      "rt.fast_chargers_only": "Tylko szybkie ładowarki",
      "rt.hotel_must_charger": "Hotel musi mieć ładowarkę",
      "rt.pet_only": "Tylko noclegi przyjazne zwierzętom",
      "rt.safety": "Bezpieczeństwo",
      "rt.safety_90": "90% (płasko)",
      "rt.safety_85": "85% (domyślnie)",
      "rt.safety_80": "80% (góry / holowanie)",
      "rt.safety_70": "70% (duże obciążenie)",
      "rt.save_trip": "Zapisz trasę",
      "rt.plan_my_trip": "Zaplanuj trasę",
      "rt.refresh_trip": "Odśwież trasę",
      "rt.planning": "Planowanie…",
      "rt.resume_title": "Wznów trasę stąd",
      "rt.resume_body": "Pobraliśmy Twoją obecną lokalizację. Wpisz zasięg widoczny na desce rozdzielczej, abyśmy mogli zaplanować kolejny odcinek na podstawie faktycznego stanu, a nie pełnego naładowania.",
      "rt.remaining_range": "Pozostały zasięg (km)",
      "rt.cancel": "Anuluj",
      "rt.replan_from_here": "Zaplanuj ponownie stąd",
      "rt.save_modal_title": "Nazwij tę trasę",
      "rt.save_modal_body": "Zapisane trasy znajdziesz w profilu — otwórz je w dowolnym momencie i kliknij Zaplanuj ponownie.",
      "rt.save_name_placeholder": "np. Brisbane → Sydney przez Byron",
      "rt.your_saved_trips": "Twoje zapisane trasy",
      "rt.custom_location": "Własna lokalizacja",
      "rt.trip_summary": "Podsumowanie trasy",
      "rt.itinerary": "Plan podróży",
      "rt.no_stops_needed": "Brak konieczności postojów — cała trasa mieści się w zasięgu.",
      "rt.chargers_in_corridor": "ładowarek na trasie",
      "rt.planned_stops": "zaplanowane postoje",
      "rt.overnight_stop": "postój nocny",
      "rt.overnight_stops": "postoje nocne",
      "rt.suggested_detours": "Sugerowane objazdy — zaznacz, aby dodać",
      "rt.replan_with_detours": "Zaplanuj z objazdami",
      "rt.attractions_label": "Atrakcja",
      "rt.kids_label": "Dzieci",
      "rt.km_from_start": "km od startu",
      "rt.open_driving": "Otwórz tryb jazdy",
      "rt.big_font_hint": "Duża czcionka, wygodna w uchwycie. Działa z CarPlay/Android Auto przez przeglądarkę.",
      "rt.check_price_book": "Sprawdź cenę i zarezerwuj na Trivago ↗",
      "rt.visit_site": "Odwiedź stronę ↗",
      "rt.charger_onsite": "Ładowarka na miejscu",
      "rt.leg_exceeds_range": "Ten odcinek przekracza ustawiony zasięg — zabraknie energii przed dotarciem do tej ładowarki. Zmniejsz założony zasięg lub dodaj przystanek na doładowanie.",
      "rt.add_waypoint_replan": "Dodaj punkt trasy i zaplanuj ponownie",
      /* AUTO-TRANSLATED rt.* END */
                      /* AUTO-TRANSLATED hm.* START */
      "hm.nav.hustlemeet": "HustleMeet",
      "hm.nav.circles": "Circles",
      "hm.nav.matchmaker": "Matchmaker",
      "hm.nav.meet": "Poznaj",
      "hm.nav.overview": "HustleMeetups",
      "hm.overview.page_title": "HustleMeetups — trzy serdeczne sposoby na znalezienie swoich ludzi.",
      "hm.overview.kicker": "Część HustleSuite",
      "hm.overview.hero_title": "Trzy serdeczne sposoby na znalezienie swoich ludzi.",
      "hm.overview.hero_title_accent": "swoich ludzi",
      "hm.overview.hero_sub": "HustleMeetups to najbardziej życzliwy sposób w Australia na budowanie prawdziwych przyjaźni — wybierz klimat, który Ci odpowiada. Niezależnie od tego, czy szukasz cotygodniowej grupy biegowej, comiesięcznej kolacji ze swoją ekipą, czy stolika nowych twarzy dopasowanych przez AI, znajdziesz tu coś dla siebie.",
      "hm.overview.tile_meet_kicker": "HustleMeet",
      "hm.overview.tile_meet_title": "Znajdź swoich ludzi. Róbcie to, co kochacie.",
      "hm.overview.tile_meet_body": "Australijskie miejsce dla grup opartych na zainteresowaniach. Biegacze, programiści, inwestorzy, spacerowicze, tancerze, kluby śniadaniowe, ekipy książkowe — setki grup, realne wydarzenia w każdym tygodniu. Pojaw się raz lub co tydzień.",
      "hm.overview.tile_meet_cta": "Przeglądaj grupy",
      "hm.overview.tile_circles_kicker": "Circles",
      "hm.overview.tile_circles_title": "Twoja ekipa. Za każdym razem inny stolik.",
      "hm.overview.tile_circles_body": "Zabierz swoją stałą grupę znajomych. My rezerwujemy nową restaurację, planujemy quiz, rozdajemy karty. Ty po prostu się pojawiasz. Najlepszy sposób, by trzymać się blisko z obecną ekipą — bez konieczności organizowania czegokolwiek przez kogokolwiek.",
      "hm.overview.tile_circles_cta": "Załóż Circle",
      "hm.overview.tile_matchmaker_kicker": "Matchmaker",
      "hm.overview.tile_matchmaker_title": "AI wybiera stolik. Ty się pojawiasz.",
      "hm.overview.tile_matchmaker_body": "Wypełnij 10-minutowy quiz. W ciągu nocy posadzimy Cię przy stole z nieznajomymi, którzy przy deserze będą się czuć jak starzy znajomi. Za każdym razem inny stolik.",
      "hm.overview.tile_matchmaker_cta": "Wypełnij quiz",
      "hm.overview.trust_kicker": "Dlaczego to działa",
      "hm.overview.trust_title": "Najpierw zaufanie. Potem stolik.",
      "hm.overview.trust_1_title": "Zweryfikowani członkowie",
      "hm.overview.trust_1_body": "Każda grupa zaczyna się od prawdziwej weryfikacji tożsamości + oceny rzetelności. Żadnych anonimowych kont.",
      "hm.overview.trust_2_title": "Prawdziwi gospodarze",
      "hm.overview.trust_2_body": "Każdą grupą kieruje zweryfikowany gospodarz, który dba o klimat, nadaje rytm i opiekuje się gośćmi.",
      "hm.overview.trust_3_title": "Przyjaźń na pierwszym miejscu",
      "hm.overview.trust_3_body": "Wszystko tutaj jest zaprojektowane z myślą o prawdziwych przyjaźniach, a nie o randkowaniu. Nigdy.",
      "hm.overview.trust_4_title": "Moderacja zero tolerancji",
      "hm.overview.trust_4_body": "Jedno zgłoszenie, jedna weryfikacja, jedna decyzja. Osoby naruszające zasady są usuwane jeszcze tej samej nocy.",
      "hm.overview.footer_line": "Część HustleSuite — australijska własność i hosting.",
      "hm.landing.page_title": "HustleMeet · Znajdź swoich ludzi. Róbcie to, co kochacie.",
      "hm.landing.kicker": "Australijska sieć zainteresowań",
      "hm.landing.hero_title": "Znajdź swoich ludzi. Róbcie to, co kochacie.",
      "hm.landing.hero_title_accent": "swoich ludzi",
      "hm.landing.hero_sub": "HustleMeet to australijskie miejsce dla grup opartych na zainteresowaniach. Programiści rozmawiający o kodzie przy lunchu. Biegacze długodystansowi o 6 rano. Inwestorzy spotykający się co miesiąc. Grupy spacerowe, ekipy taneczne, kluby śniadaniowe, wieczory z planszówkami. Wybierz grupę, zarezerwuj wydarzenie i przyjdź.",
      "hm.landing.cta_explore_circles": "Przeglądaj grupy",
      "hm.landing.cta_try_matchmaker": "Załóż własną grupę",
      "hm.landing.trust_verified": "Zweryfikowani członkowie",
      "hm.landing.trust_venues": "Zweryfikowani gospodarze",
      "hm.landing.trust_friendship": "Najpierw przyjaźń — nigdy randki",
      "hm.landing.trust_moderation": "Moderacja zero tolerancji",
      "hm.landing.subbrand_circles_kicker": "Circles",
      "hm.landing.subbrand_circles_title": "Masz już swoją ekipę? Stwórz dla niej stałe miejsce.",
      "hm.landing.subbrand_circles_body": "Circles to rozwiązanie dla grup znajomych, którzy już się spotykają — klubów książki, graczy pokera, ekip biegowych czy znajomych z pracy. My zajmujemy się lokalem, przypomnieniami i listą obecności. Ty po prostu przychodzisz.",
      "hm.landing.subbrand_circles_b1": "Rotacyjne lokale, jedna stała lista",
      "hm.landing.subbrand_circles_b2": "Automatyczne przypomnienia + łatwe RSVP",
      "hm.landing.subbrand_circles_b3": "Quizy, karty i wieczory tematyczne na życzenie",
      "hm.landing.subbrand_circles_cta": "Załóż Circle",
      "hm.landing.subbrand_matchmaker_kicker": "Matchmaker",
      "hm.landing.subbrand_matchmaker_title": "AI wybiera stolik. Ty się pojawiasz.",
      "hm.landing.subbrand_matchmaker_body": "Jeden 10-minutowy quiz — osobowość, wartości, dostępność. Nasz Matchmaker posadzi Cię z nieznajomymi, którzy przy deserze będą się czuć jak starzy znajomi. Za każdym razem inny stolik.",
      "hm.landing.subbrand_matchmaker_b1": "Ocena osobowości Big-5 i wartości",
      "hm.landing.subbrand_matchmaker_b2": "Zaproszenia w ciągu nocy, gdy dopasowanie wynosi 0,70 lub więcej",
      "hm.landing.subbrand_matchmaker_b3": "Uczy się na podstawie każdej kolacji, w której uczestniczysz",
      "hm.landing.subbrand_matchmaker_cta": "Wypełnij quiz",
      "hm.landing.how_kicker": "Jak działa HustleMeet",
      "hm.landing.how_title": "Cztery kroki. Znajdź grupę. Przyjdź.",
      "hm.landing.how_step1_ttl": "Wybierz swoje zainteresowania",
      "hm.landing.how_step1_sub": "Bieganie, kodowanie, inwestowanie, gotowanie, muzyka, spacery — powiedz nam, co Cię kręci i gdzie mieszkasz.",
      "hm.landing.how_step2_ttl": "Przeglądaj grupy w Twojej okolicy",
      "hm.landing.how_step2_sub": "Każda grupa pokazuje swojego gospodarza, rytm spotkań i najbliższe wydarzenie. Poznaj klimat, zanim dołączysz.",
      "hm.landing.how_step3_ttl": "Potwierdź udział w wydarzeniu",
      "hm.landing.how_step3_sub": "Jedno dotknięcie, jedno zaproszenie do kalendarza, jedno wyraźne przypomnienie dzień wcześniej. Niektóre wydarzenia są darmowe, inne płatne — cena jest podana czarno na białym.",
      "hm.landing.how_step4_ttl": "Przyjdź i nawiąż kontakt",
      "hm.landing.how_step4_sub": "Poznaj gospodarza, poznaj ekipę, róbcie to, co kochacie. Wróć za tydzień, jeśli zaiskrzyło.",
      "hm.landing.stats_kicker": "Nowy sposób na spotkania w Australia",
      "hm.landing.stats_title": "Powód, dla którego ludzie do nas wracają.",
      "hm.landing.stats_92_sub": "Czuje, że złapało wspólny język z przynajmniej jedną osobą na wydarzeniu",
      "hm.landing.stats_68_sub": "Potwierdza udział w kolejnym wydarzeniu w ciągu tygodnia",
      "hm.landing.stats_85_sub": "Pozostaje w kontakcie z kimś z grupy",
      "hm.landing.why_kicker": "Dlaczego to działa",
      "hm.landing.why_title": "Prawdziwe pasje. Prawdziwi gospodarze. Prawdziwe wydarzenia.",
      "hm.landing.why_1_ttl": "Najpierw pasje, potem lokalizacja",
      "hm.landing.why_1_body": "Ludziom w Twojej grupie naprawdę zależy na tym samym, co Tobie — to najkrótsza droga do prawdziwej przyjaźni.",
      "hm.landing.why_2_ttl": "Prawdziwi gospodarze wydarzeń",
      "hm.landing.why_2_body": "Każda grupa ma sprawdzonego gospodarza, który dba o klimat, ustala rytm i reaguje, gdy coś jest nie tak. To nie administrator na Facebooku. To prawdziwa osoba.",
      "hm.landing.why_3_ttl": "Konsekwencja ma znaczenie",
      "hm.landing.why_3_body": "Nowe wydarzenia co tydzień. Coś, na co warto czekać. Rytm, który zmienia nieznajomych w Twoją ekipę na wtorkowe wieczory.",
      "hm.landing.why_4_ttl": "Bezpieczeństwo i pewność",
      "hm.landing.why_4_body": "Zweryfikowani członkowie, miejsca publiczne, zero tolerancji dla braku szacunku. Nasz zespół wsparcia jest do dyspozycji przed, w trakcie i po każdym wydarzeniu.",
      "hm.landing.why_5_ttl": "Najpierw przyjaźń",
      "hm.landing.why_5_body": "To nie są randki. To społeczność. Prawdziwe zainteresowania, prawdziwi ludzie, żadnego przesuwania zdjęć w międzyczasie.",
      "hm.landing.why_6_ttl": "Wystarczająco małe, by mieć znaczenie",
      "hm.landing.why_6_body": "Większość grup ogranicza liczbę miejsc do 4-15. To wystarczająco dużo, by wywołać ciekawe rozmowy i wystarczająco mało, by nikt nie czuł się pominięty.",
      "hm.landing.cities_kicker": "Gdzie działamy",
      "hm.landing.cities_title": "Grupy spotykają się w całej Australia i NZ.",
      "hm.landing.cities_sub_zero": "Pierwsze grupy otwierają się w ponad 15 miastach — dołącz wcześnie i kształtuj społeczność.",
      "hm.landing.cities_sub_some": "Grupy działają w ponad 15 metropoliach. Coś w Twoim mieście jeszcze w tym tygodniu.",
      "hm.landing.cities_sub_one": "{{count}} aktywna grupa · nowe wydarzenia co tydzień · stale dodajemy nowe miasta.",
      "hm.landing.cities_sub_many": "{{count}} aktywnych grup · nowe wydarzenia co tydzień · stale dodajemy nowe miasta.",
      "hm.landing.forme_kicker": "To jest dla Ciebie, jeśli…",
      "hm.landing.forme_title": "Chcesz prawdziwych zainteresowań. Prawdziwych ludzi. Prawdziwych wydarzeń.",
      "hm.landing.forme_1": "Masz dość zmęczenia aplikacjami randkowymi i chcesz po prostu poznać ludzi wokół tego, co uwielbiasz robić.",
      "hm.landing.forme_2": "Przeprowadziłeś się do nowego miasta i potrzebujesz serdecznej ekipy, a nie kolejnej grupy na czacie.",
      "hm.landing.forme_3": "Cenisz głębię relacji bardziej niż zasięgi — jedna świetna grupa jest lepsza niż pięćdziesiąt obserwowanych kont.",
      "hm.landing.forme_4": "Wierzysz, że wspólne pasje + zaplanowane dwie godziny w realu to najlepszy sposób na życie.",
      "hm.landing.notme_kicker": "To nie jest dla Ciebie, jeśli…",
      "hm.landing.notme_title": "HustleMeet nie jest dla każdego.",
      "hm.landing.notme_1": "Nie możesz zobowiązać się do przyjścia — gospodarze i członkowie liczą na Twoje potwierdzenie obecności.",
      "hm.landing.notme_2": "Szukasz serwisu randkowego — tutaj przyjaźnie są na pierwszym miejscu.",
      "hm.landing.notme_3": "Chcesz tylko nadawać bez spotykania się z kimkolwiek — to działa tylko osobiście.",
      "hm.landing.notme_4": "Oczekujesz ciągłych darmowych korzyści — niektóre wydarzenia są płatne. Cena jest zawsze znana z góry.",
      "hm.landing.safety_kicker": "Nasze zaangażowanie w bezpieczeństwo",
      "hm.landing.safety_title": "Serdecznie, ale nigdy nieostrożnie.",
      "hm.landing.safety_1_ttl": "Miejsca publiczne",
      "hm.landing.safety_1_body": "Każde wydarzenie odbywa się w publicznym, obsługiwanym miejscu — kawiarni, restauracji, parku, siłowni lub studiu. Nigdy w niczyim domu, chyba że każdy uczestnik wyrazi na to zgodę.",
      "hm.landing.safety_2_ttl": "Standardy społeczności",
      "hm.landing.safety_2_body": "Zero tolerancji dla braku szacunku, nękania lub nieuczciwości. Członkowie naruszający zasady są natychmiast usuwani.",
      "hm.landing.safety_3_ttl": "Ochrona prywatności",
      "hm.landing.safety_3_body": "Twoje dane nigdy nie są udostępniane publicznie. Ty decydujesz, z kim pozostaniesz w kontakcie po każdym wydarzeniu.",
      "hm.landing.safety_4_ttl": "Wsparcie pod telefonem",
      "hm.landing.safety_4_body": "Nasz zespół jest dostępny przed, w trakcie i po każdym wydarzeniu, jeśli masz pytania lub obawy.",
      "hm.landing.bottom_title": "Twoja nowa ulubiona grupa jest o jedno kliknięcie stąd.",
      "hm.landing.bottom_sub": "Wybierz swoją ścieżkę — dołącz do istniejącej grupy, wprowadź swoją ekipę do Circle lub pozwól, by Matchmaker posadził Cię przy stole, który już do Ciebie pasuje.",
      "hm.landing.bottom_cta_circles": "Załóż Circle",
      "hm.landing.bottom_cta_mm": "Wypełnij quiz Matchmaker",
      "hm.circles.page_title": "HustleMeet Circles · Twoja ekipa, za każdym razem nowy lokal",
      "hm.circles.kicker": "HustleMeet · Circles",
      "hm.circles.hero_title": "Twoja ekipa. Za każdym razem inny stolik.",
      "hm.circles.hero_title_accent": "Twoja ekipa",
      "hm.circles.hero_sub": "Masz już paczkę znajomych? Circles to najprostszy sposób, by trzymać się blisko. My rezerwujemy lokal, planujemy quiz, rozdajemy karty. Ty po prostu przychodzisz — ta sama ekipa, nowy wieczór, nowe miejsce.",
      "hm.circles.cta_find": "Załóż Circle",
      "hm.circles.cta_host_hero": "Dołącz do otwartego Circle",
      "hm.circles.trust_venue": "Rotacyjne, sprawdzone lokale",
      "hm.circles.trust_party": "4-12 znajomych w Circle",
      "hm.circles.trust_verified": "Zweryfikowani członkowie",
      "hm.circles.trust_friendship": "Prawdziwe przyjaźnie, bliskie relacje",
      "hm.circles.how_kicker": "Jak to działa",
      "hm.circles.how_title": "Cztery kroki. Twoja ekipa pozostaje ekipą.",
      "hm.circles.how_step1_ttl": "Załóż swoje Circle",
      "hm.circles.how_step1_sub": "Nazwij je, zaproś znajomych (do 12 osób) i wybierz rytm — co tydzień, co dwa tygodnie lub co miesiąc.",
      "hm.circles.how_step2_ttl": "My wybieramy lokal",
      "hm.circles.how_step2_sub": "Na każde wydarzenie sugerujemy nowe miejsce — restaurację, wieczór z quizem, pokój karciany, winiarnię. Możecie też zostać przy ulubionym miejscu.",
      "hm.circles.how_step3_ttl": "Każdy potwierdza jednym kliknięciem",
      "hm.circles.how_step3_sub": "Automatyczne przypomnienia, podział kosztów, przedpłacone miejsca, aby rezygnacje nie psuły planów. Rachunek jest gotowy przed przystawką.",
      "hm.circles.how_step4_ttl": "Utrzymaj rytm",
      "hm.circles.how_step4_sub": "Jeden wieczór w kalendarzu, którego nigdy więcej nie trzeba organizować. Grupa zżywa się przez miesiące, nie minuty.",
      "hm.circles.cadence_weekly_ttl": "Co tydzień",
      "hm.circles.cadence_weekly_sub": "W każdym tygodniu",
      "hm.circles.cadence_weekly_body": "Dla ekip, które chcą wtorkowego rytuału — te same twarze, za każdym razem nowe miejsce.",
      "hm.circles.cadence_fortnight_ttl": "Co dwa tygodnie",
      "hm.circles.cadence_fortnight_sub": "Co 2 tygodnie",
      "hm.circles.cadence_fortnight_body": "Idealny balans — regularnie, ale bez zbytniego obciążania zajętych kalendarzy.",
      "hm.circles.cadence_monthly_ttl": "Co miesiąc",
      "hm.circles.cadence_monthly_sub": "W każdym miesiącu",
      "hm.circles.cadence_monthly_body": "Circles na specjalne okazje — niedzielne pieczenie, duże quizy, degustacje, poker.",
      "hm.circles.feast_kicker": "Wybierz swój wieczór",
      "hm.circles.feast_title": "Trzy sposoby na spędzenie czasu",
      "hm.circles.feast_sub": "Każde Circle wybiera, jak przebiega wieczór. Możecie je zmieniać lub zostać przy ulubionym formacie.",
      "hm.circles.feast_surprise_ttl": "Wypróbuj nową restaurację",
      "hm.circles.feast_surprise_body": "Przygotowujemy listę trzech lokali w pobliżu, wszyscy głosują, kuchnia szykuje stolik. Podział kosztów, miejsca przedpłacone.",
      "hm.circles.feast_preselect_ttl": "Quizy / karty / gry",
      "hm.circles.feast_preselect_body": "Zarezerwuj wieczór z quizem, pokój do pokera, bar z planszówkami lub tor do kręgli. My zajmujemy się lokalem, wpisowym i przypomnieniami.",
      "hm.circles.feast_per_event_ttl": "Jednorazowy wieczór tematyczny",
      "hm.circles.feast_per_event_body": "Degustacja wina, stolik u szefa kuchni, warsztaty koktajlowe, escape room. Coś wyjątkowego raz na jakiś czas — w pełni zorganizowane.",
      "hm.circles.live_kicker": "W tej chwili",
      "hm.circles.live_title": "Circles, do których możesz dołączyć",
      "hm.circles.live_host_link": "Załóż własne",
      "hm.circles.live_loading": "Ładowanie…",
      "hm.circles.live_empty_title": "Bądź pierwszym Circle w swoim mieście.",
      "hm.circles.live_empty_sub": "Zacznij z trojgiem znajomych, a pomożemy Ci zaprosić resztę — plus fundujemy pierwsze trzy lokale.",
      "hm.circles.live_empty_cta": "Załóż Circle",
      "hm.circles.host_kicker": "Dla liderów Circles",
      "hm.circles.host_title": "Ty przyprowadzasz ekipę. My zajmujemy się resztą.",
      "hm.circles.host_body": "Jeśli to Ty zawsze organizujesz wyjścia, Circles oszczędzi Ci godziny pracy. Automatyczne przypomnienia, przedpłacone RSVP, listy lokali, podział kosztów, zarządzanie listą oczekujących i portfel cashback na każde wyjście. Ty po prostu przychodzisz jako zwykły członek.",
      "hm.circles.host_perk_1": "Automatyczne listy lokali — koniec z Google Maps",
      "hm.circles.host_perk_2": "Przedpłacone miejsca — zero nieobecności",
      "hm.circles.host_perk_3": "Podział kosztów zrobiony przed deserem",
      "hm.circles.host_perk_4": "Przypomnienia + zaproszenia do kalendarza na autopilocie",
      "hm.circles.host_perk_5": "Lista oczekujących z automatem, jeśli ktoś odwoła",
      "hm.circles.host_perk_6": "Portfel cashback przy każdym wyjściu",
      "hm.circles.host_cta": "Załóż Circle teraz",
      "hm.circles.why_kicker": "Dlaczego Circles działają",
      "hm.circles.why_title": "Dorosłe przyjaźnie potrzebują infrastruktury",
      "hm.circles.why_1_ttl": "Ta sama ekipa, głębsze zaufanie",
      "hm.circles.why_1_body": "Życie oddala przyjaciół od siebie. Circles to stały rytm, który trzyma wszystkich blisko — tych samych 4-12 znajomych, spotykających się w ten sam wieczór, bez względu na wszystko.",
      "hm.circles.why_2_ttl": "Zero zmęczenia organizacją",
      "hm.circles.why_2_body": "Nikt nie musi być szefem grupy na WhatsAppie. My wybieramy lokale, wysyłamy przypomnienia, przyjmujemy płatności, domykamy sprawy.",
      "hm.circles.why_3_ttl": "Nowy lokal, nowe wspomnienie",
      "hm.circles.why_3_body": "Rotacja przez najlepsze małe restauracje, pokoje quizowe i bary z grami w Australia. Znajome twarze, świeże tła — idealne połączenie rutyny i nowości.",
      "hm.circles.bottom_title": "Wieczór, na który Twoja ekipa czeka, a nikt nie musi go planować.",
      "hm.circles.bottom_sub": "Jedno Circle. Ci sami znajomi. Za każdym razem nowy lokal.",
      "hm.circles.bottom_cta_find": "Załóż Circle",
      "hm.circles.bottom_cta_mm": "Poznaj nowych ludzi z Matchmaker →",
      "hm.circles.card_host_pill": "Circle",
      "hm.matchmaker.page_title": "HustleMeet Matchmaker · AI dobiera odpowiedni stolik",
      "hm.matchmaker.kicker": "HustleMeet · Matchmaker",
      "hm.matchmaker.hero_title": "AI wybiera stolik. Ty po prostu przychodzisz.",
      "hm.matchmaker.hero_title_accent": "Ty po prostu przychodzisz.",
      "hm.matchmaker.hero_sub": "Odpowiedz na krótki quiz o osobowości i wartościach. Nasz Matchmaker posadzi Cię z grupą od czterech do siedmiu nieznajomych, którzy przy deserze będą się czuć jak starzy znajomi. Bez przesuwania. Bez small talku. Tylko wspólny stół, gotowy wtedy, kiedy Ty.",
      "hm.matchmaker.cta_quiz": "Wypełnij quiz dopasowania",
      "hm.matchmaker.cta_see_circles": "Zobacz Circles →",
      "hm.matchmaker.trust_big5": "Osobowość oceniana wg Big-5",
      "hm.matchmaker.trust_seats": "4-8 miejsc przy stole",
      "hm.matchmaker.trust_lock": "Zero przesuwania · zero zdjęć",
      "hm.matchmaker.trust_verified": "Tylko zweryfikowani członkowie",
      "hm.matchmaker.how_kicker": "Jak to działa",
      "hm.matchmaker.how_title": "Cztery kroki do stolika, który już do Ciebie pasuje.",
      "hm.matchmaker.how_step1_ttl": "Wypełnij quiz",
      "hm.matchmaker.how_step1_sub": "Dziesięciominutowy kwestionariusz — osobowość, wartości, dieta, dostępność. Robisz to tylko raz.",
      "hm.matchmaker.how_step2_ttl": "Dopasowanie w ciągu nocy",
      "hm.matchmaker.how_step2_sub": "Nasze AI analizuje Twój profil i aktualne kolacje w Australia. Gdy wynik dopasowania wynosi 0,70 lub więcej, przygotowuje osobiste zaproszenie.",
      "hm.matchmaker.how_step3_ttl": "Zaakceptuj lub odrzuć",
      "hm.matchmaker.how_step3_sub": "Otrzymasz jasny e-mail z zaproszeniem, w którym znajdziesz lokal, temat stolika oraz trzy wspólne zainteresowania, które dzielisz z grupą.",
      "hm.matchmaker.how_step4_ttl": "System uczy się od Ciebie",
      "hm.matchmaker.how_step4_sub": "Każda ankieta po kolacji ulepsza model. Twoje kolejne dopasowanie staje się trafniejsze z każdym wspólnym posiłkiem.",
      "hm.matchmaker.science_kicker": "Dlaczego to działa",
      "hm.matchmaker.science_title": "Matchmaker nie zgaduje — on wylicza.",
      "hm.matchmaker.science_sub": "Każde dopasowanie to ważona suma czterech czynników. Gdy średnia całego stołu przekracza 0,70, wysyłamy zaproszenia. Wszystko poniżej czeka w kolejce.",
      "hm.matchmaker.science_1_ttl": "Osobowość (35%)",
      "hm.matchmaker.science_1_body": "Podobieństwo cosinusowe w wektorze osobowości Big-5 — otwartość, sumienność, ekstrawersja, ugodowość i stabilność emocjonalna.",
      "hm.matchmaker.science_2_ttl": "Wartości (25%)",
      "hm.matchmaker.science_2_body": "Zbieżność wartości, dzięki którym rozmowa przy kolacji naprawdę się klei — rodzina, ciekawość, rozwój, służba, przygoda.",
      "hm.matchmaker.science_3_ttl": "Zainteresowania (25%)",
      "hm.matchmaker.science_3_body": "Nakładanie się Jaccarda w Twoich specyficznych zainteresowaniach — wędrówki Six Foot Track, klub filmowy w Sydney, scena kawowa w Melbourne, Twoja trzecia książka w tym roku.",
      "hm.matchmaker.science_4_ttl": "Dostępność (15%)",
      "hm.matchmaker.science_4_body": "Wieczór, w którym naprawdę możesz się pojawić. Żadnych zaproszeń na 19:00 we wtorek, jeśli w kalendarzu masz wpisane 'siłownia-we-wtorki-na-zawsze'.",
      "hm.matchmaker.safety_kicker": "Bezpieczeństwo z założenia",
      "hm.matchmaker.safety_title": "Matchmaker jest cichy i ostrożny.",
      "hm.matchmaker.safety_1_ttl": "Próg pewności",
      "hm.matchmaker.safety_1_body": "Poniżej wyniku 0,70 dla grupy zaproszenie nie zostanie wysłane. Niepewne dopasowanie przegrywa z brakiem kolacji — dopóki nie stanie się pewne.",
      "hm.matchmaker.safety_2_ttl": "Wspólna lista blokad",
      "hm.matchmaker.safety_2_body": "Jeśli dwoje członków prywatnie zasygnalizuje 'nie' po kolacji, nigdy więcej nie posadzimy ich razem przy stole.",
      "hm.matchmaker.safety_3_ttl": "Automatyczny wyłącznik",
      "hm.matchmaker.safety_3_body": "Jeśli 20% lub więcej ostatnich ankiet oceni stolik na 2/5 lub niżej, zaproszenia są wstrzymywane na 24h do czasu weryfikacji przez operatora.",
      "hm.matchmaker.safety_4_ttl": "Standardy społeczności",
      "hm.matchmaker.safety_4_body": "Każdy członek akceptuje jasny kodeks postępowania. Usuwamy osoby, które go naruszają — bez możliwości odwołania.",
      "hm.matchmaker.safety_5_ttl": "Tylko miejsca publiczne",
      "hm.matchmaker.safety_5_body": "Każda kolacja Matchmaker odbywa się w prawdziwej restauracji z obsługą. Nigdy w niczyim domu.",
      "hm.matchmaker.safety_6_ttl": "Opinia po wydarzeniu",
      "hm.matchmaker.safety_6_body": "Twoja prywatna ankieta kształtuje przyszłe dopasowania i może wywołać przegląd lokalu lub zachowania innego uczestnika.",
      "hm.matchmaker.loop_kicker": "Pętla uczenia się",
      "hm.matchmaker.loop_title": "Każda kolacja sprawia, że następna jest lepsza.",
      "hm.matchmaker.loop_body": "Matchmaker nie jest statyczny. Po każdym wydarzeniu przesyłasz krótką, prywatną ankietę — o tym, jak przebiegała rozmowa, z kim chciałbyś zjeść ponownie i czy lokal spełnił oczekiwania. Te sygnały wracają do modelu i kształtują każde przyszłe dopasowanie.",
      "hm.matchmaker.loop_1": "Głosy 'zjedzmy razem ponownie' wzmacniają Twoje parowanie",
      "hm.matchmaker.loop_2": "Wspólne głosy na 'nie' po cichu wycofują pary",
      "hm.matchmaker.loop_3": "Oceny lokalu i rozmowy dostrajają skład miejsc",
      "hm.matchmaker.loop_4": "Ocena rzetelności chroni członków przed osobami, które się nie pojawiają",
      "hm.matchmaker.loop_5": "Mechanizm uczenia się wzmacnia dobre dopasowania z miesiąca na miesiąc",
      "hm.matchmaker.loop_6": "Masz pełną kontrolę — możesz zrezygnować lub zresetować profil w dowolnym momencie",
      "hm.matchmaker.vs_kicker": "Co jest dla mnie odpowiednie?",
      "hm.matchmaker.vs_title": "Matchmaker czy Circles?",
      "hm.matchmaker.vs_mm_ttl": "Wybierz mnie, jeśli chcę różnorodności.",
      "hm.matchmaker.vs_mm_1": "Za każdym razem inny stolik, miasto i kuchnia",
      "hm.matchmaker.vs_mm_2": "Dobierana przez AI grupa 4-8 dopasowanych nieznajomych",
      "hm.matchmaker.vs_mm_3": "Tygodniowy rytm, brak zobowiązań gospodarza",
      "hm.matchmaker.vs_mm_4": "Świetne, jeśli jesteś nowy w mieście lub podróżujesz",
      "hm.matchmaker.vs_mm_cta": "Wypełnij quiz dopasowania",
      "hm.matchmaker.vs_circles_ttl": "Wybierz mnie, jeśli chcę stałego miejsca.",
      "hm.matchmaker.vs_circles_1": "Ten sam gospodarz · ten sam lokal · ten sam wieczór",
      "hm.matchmaker.vs_circles_2": "Grupa, która zmienia się w Twoją środową tradycję",
      "hm.matchmaker.vs_circles_3": "Dobierane przez człowieka, doświadczonego gospodarza",
      "hm.matchmaker.vs_circles_4": "Świetne, jeśli właśnie się przeprowadziłeś i chcesz zapuścić korzenie",
      "hm.matchmaker.vs_circles_cta": "Przeglądaj Circles",
      "hm.matchmaker.bottom_title": "Jeden quiz. Każda kolejna kolacja już do Ciebie pasuje.",
      "hm.matchmaker.bottom_sub": "Zostaniesz dopasowany w ciągu dwóch tygodni. To najłatwiejsze 'tak', jakie powiesz w tym miesiącu.",
      "hm.matchmaker.bottom_cta": "Wypełnij quiz dopasowania",
      "hm.matchmaker.bottom_note": "Bez karty kredytowej · 10 minut · Zrezygnuj w dowolnym momencie",
      /* AUTO-TRANSLATED hm.* END */
    },
  },
  ru: {
    translation: {
      /* AUTO-TRANSLATED finder.* (added) */
      "finder.title": "HustleFinder",
      "finder.tagline": "Найдите ближайшее общественное место — реальные данные по всей Австралии.",
      "finder.search_placeholder": "Поиск мест, улиц или пригородов…",
      "finder.hint": "Начните вводить выше, нажмите на категорию или используйте «Рядом со мной».",
      "finder.near_me": "Рядом со мной",
      "finder.searching": "Поиск…",
      "finder.suburb.placeholder": "Фильтр по пригороду (например, Darwin, NT)…",
      "finder.suburb.clear": "Очистить",
      "finder.search_in": "Искать в {{cat}}",
      "finder.empty": "Нет результатов — попробуйте другую категорию или поисковый запрос.",
      "finder.meta.title": "HustleFinder — бесплатный справочник пригородов Австралии",
      "finder.meta.desc": "Поиск местных предприятий, зарядных станций, кафе, туалетов и многого другого в каждом австралийском пригороде — 100% бесплатно.",
      "finder.footer.credits": "Авторство данных",
      "finder.footer.terms": "Условия",
      "finder.cat.toilet": "Туалеты",
      "finder.cat.fountain": "Питьевые фонтанчики",
      "finder.cat.transport": "Транспорт",
      "finder.cat.park": "Парки",
      "finder.cat.medical": "Медицина",
      "finder.cat.school": "Школы",
      "finder.cat.library": "Библиотеки",
      "finder.cat.petrol": "Бензин",
      "finder.cat.kidzone": "Детская зона",
      "finder.cat.puzzles": "Головоломки",
      "finder.cat.roadtrip": "Путешествие",
      "finder.hotels.title": "Найдите отель рядом",
      "finder.hotels.sub": "Сравнивает более 200 сайтов бронирования в одном поиске — от Trivago.",
      "finder.hotels.where": "Где",
      "finder.hotels.where_ph": "Город или пригород (например, Byron Bay, NSW)",
      "finder.hotels.checkin": "Заезд",
      "finder.hotels.checkout": "Выезд",
      "finder.hotels.adults": "Взрослые",
      "finder.hotels.search": "Найти отели",
      "finder.hotels.disclosure": "Открывает Trivago — мы можем получать комиссию с бронирований.",
      /* AUTO-TRANSLATED rt.* START */
      "rt.brand_name": "Roadtrip Planner",
      "rt.page_title": "Aussie EV Road-trip Planner",
      "rt.from": "Откуда",
      "rt.to": "Куда",
      "rt.from_placeholder": "Район, поселок или город (напр., Newcastle NSW)",
      "rt.to_placeholder": "Район, поселок или город (напр., Hobart TAS)",
      "rt.stop": "СТОП",
      "rt.stop_placeholder": "Промежуточная остановка",
      "rt.add_stop": "Добавить остановку",
      "rt.upgrade_pro": "Перейдите на Pro, чтобы добавить до 20 остановок",
      "rt.reverse": "Обратно (рассчитать путь домой)",
      "rt.from_current": "От текущего местоположения",
      "rt.range_per_charge": "Запас хода на одном заряде (км)",
      "rt.connector": "Разъем",
      "rt.connector_any": "Любой",
      "rt.drive_hours_day": "Часов за рулем в день",
      "rt.accommodation": "Жилье",
      "rt.accom_any": "Любое (BnB · Отель · Кемпинг)",
      "rt.accom_bnb": "BnB",
      "rt.accom_hotel": "Отель",
      "rt.accom_caravan": "Кемпинг",
      "rt.min_rating": "Минимальный рейтинг",
      "rt.any_rating": "Любой рейтинг",
      "rt.only_5star": "Только 5★",
      "rt.schedule": "Расписание:",
      "rt.depart_at": "Отправление в",
      "rt.arrive_by": "Прибытие к",
      "rt.hotel_checkin": "Заезд в отель",
      "rt.hotel_checkout": "Выезд из отеля",
      "rt.overnight_hint": "Ночные переезды не будут планироваться вне этого времени.",
      "rt.whos_in_car": "Кто в машине?",
      "rt.adults": "Взрослые",
      "rt.kids": "Дети",
      "rt.ages": "Возраст",
      "rt.show_attractions": "Показать достопримечательности",
      "rt.show_kids": "Остановки для детей",
      "rt.overnight_stays": "Ночевки",
      "rt.fast_chargers_only": "Только быстрые зарядки",
      "rt.hotel_must_charger": "В отеле должна быть зарядка",
      "rt.pet_only": "Разрешено с животными",
      "rt.safety": "Безопасность",
      "rt.safety_90": "90% (равнина)",
      "rt.safety_85": "85% (стандарт)",
      "rt.safety_80": "80% (холмы / буксировка)",
      "rt.safety_70": "70% (большой груз)",
      "rt.save_trip": "Сохранить поездку",
      "rt.plan_my_trip": "Спланировать поездку",
      "rt.refresh_trip": "Обновить поездку",
      "rt.planning": "Планирование…",
      "rt.resume_title": "Продолжить поездку отсюда",
      "rt.resume_body": "Мы определили ваше текущее местоположение. Введите оставшийся запас хода, чтобы мы спланировали следующий этап на основе текущего заряда, а не полного.",
      "rt.remaining_range": "Оставшийся запас хода (км)",
      "rt.cancel": "Отмена",
      "rt.replan_from_here": "Перепланировать отсюда",
      "rt.save_modal_title": "Назовите поездку",
      "rt.save_modal_body": "Сохраненные поездки находятся в профиле — откройте их в любое время и нажмите «Перепланировать».",
      "rt.save_name_placeholder": "напр., Брисбен → Сидней через Байрон",
      "rt.your_saved_trips": "Ваши сохраненные поездки",
      "rt.custom_location": "Свое местоположение",
      "rt.trip_summary": "Сводка поездки",
      "rt.itinerary": "Маршрут",
      "rt.no_stops_needed": "Остановки не нужны — поездка в рамках вашего запаса хода.",
      "rt.chargers_in_corridor": "зарядок по пути",
      "rt.planned_stops": "плановых остановок",
      "rt.overnight_stop": "ночевка",
      "rt.overnight_stops": "ночевки",
      "rt.suggested_detours": "Предлагаемые заезды — отметьте, чтобы добавить",
      "rt.replan_with_detours": "Перепланировать с заездами",
      "rt.attractions_label": "Интересное место",
      "rt.kids_label": "Дети",
      "rt.km_from_start": "км от старта",
      "rt.open_driving": "Режим вождения",
      "rt.big_font_hint": "Крупный шрифт, удобно в держателе. Работает через CarPlay/Android Auto в браузере.",
      "rt.check_price_book": "Узнать цену и забронировать на Trivago ↗",
      "rt.visit_site": "Перейти на сайт ↗",
      "rt.charger_onsite": "Зарядка на месте",
      "rt.leg_exceeds_range": "Этот участок превышает ваш запас хода — заряд кончится до этой станции. Снизьте ожидаемый запас хода или добавьте промежуточную зарядку.",
      "rt.add_waypoint_replan": "Добавить точку и перепланировать",
      /* AUTO-TRANSLATED rt.* END */
                      /* AUTO-TRANSLATED hm.* START */
      "hm.nav.hustlemeet": "HustleMeet",
      "hm.nav.circles": "Circles",
      "hm.nav.matchmaker": "Matchmaker",
      "hm.nav.meet": "Встречи",
      "hm.nav.overview": "HustleMeetups",
      "hm.overview.page_title": "HustleMeetups — три теплых способа найти своих людей.",
      "hm.overview.kicker": "Часть HustleSuite",
      "hm.overview.hero_title": "Три теплых способа найти своих людей.",
      "hm.overview.hero_title_accent": "своих людей",
      "hm.overview.hero_sub": "HustleMeetups — это самый добрый в Australia способ завязать настоящую дружбу. Выберите подходящую вам атмосферу. Будь то еженедельная группа по бегу, ежемесячный ужин со старой компанией или столик с новыми лицами, подобранными ИИ — здесь найдется место для каждого.",
      "hm.overview.tile_meet_kicker": "HustleMeet",
      "hm.overview.tile_meet_title": "Найдите своих людей. Делайте то, что любите.",
      "hm.overview.tile_meet_body": "Главная площадка Australia для групп по интересам. Бегуны, программисты, инвесторы, любители прогулок, танцоры, завтрак-клубы и книжные сообщества — сотни групп и реальные события каждую неделю. Приходите разово или заглядывайте каждую неделю.",
      "hm.overview.tile_meet_cta": "Исследовать группы",
      "hm.overview.tile_circles_kicker": "Circles",
      "hm.overview.tile_circles_title": "Ваша компания. Новый столик каждый раз.",
      "hm.overview.tile_circles_body": "Собирайте своих старых друзей. Мы забронируем новый ресторан, продумаем квиз и подготовим карты. Вам нужно только прийти. Самый надежный способ поддерживать связь с близкими, не тратя время на организацию.",
      "hm.overview.tile_circles_cta": "Создать Circle",
      "hm.overview.tile_matchmaker_kicker": "Matchmaker",
      "hm.overview.tile_matchmaker_title": "ИИ выбирает столик. Вы просто приходите.",
      "hm.overview.tile_matchmaker_body": "Пройдите 10-минутный квиз. Мы подберем вам компанию из незнакомых людей, которые к десерту станут вам как старые друзья. Каждый раз — новый состав участников.",
      "hm.overview.tile_matchmaker_cta": "Пройти квиз",
      "hm.overview.trust_kicker": "Почему это работает",
      "hm.overview.trust_title": "Сначала доверие. Потом общий стол.",
      "hm.overview.trust_1_title": "Проверенные участники",
      "hm.overview.trust_1_body": "Каждая группа начинается с реальной проверки личности (ID) и рейтинга надежности. Никаких одноразовых аккаунтов.",
      "hm.overview.trust_2_title": "Настоящие организаторы",
      "hm.overview.trust_2_body": "Каждую группу ведет проверенный хост, который задает атмосферу, ритм и заботится о комфорте каждого гостя.",
      "hm.overview.trust_3_title": "Дружба прежде всего",
      "hm.overview.trust_3_body": "Здесь все создано для поиска настоящих друзей, а не для свиданий. Без исключений.",
      "hm.overview.trust_4_title": "Нулевая терпимость к нарушениям",
      "hm.overview.trust_4_body": "Одна жалоба, одна проверка, одно решение. Нарушители правил исключаются в тот же вечер.",
      "hm.overview.footer_line": "Часть HustleSuite — австралийский проект с локальным хостингом.",
      "hm.landing.page_title": "HustleMeet · Найдите своих людей. Делайте то, что любите.",
      "hm.landing.kicker": "Сеть сообществ по интересам в Australia",
      "hm.landing.hero_title": "Найдите своих людей. Делайте то, что любите.",
      "hm.landing.hero_title_accent": "своих людей",
      "hm.landing.hero_sub": "HustleMeet — это дом для сообществ по интересам в Australia. Разработчики ПО, общающиеся за обедом. Бегуны на длинные дистанции в 6 утра. Ежемесячные встречи инвесторов. Группы для прогулок, танцевальные студии, завтрак-клубы и вечера настольных игр. Выберите группу, забронируйте участие и приходите.",
      "hm.landing.cta_explore_circles": "Просмотреть группы",
      "hm.landing.cta_try_matchmaker": "Создать свою группу",
      "hm.landing.trust_verified": "Проверенные участники",
      "hm.landing.trust_venues": "Надежные организаторы событий",
      "hm.landing.trust_friendship": "Только дружба — никакого дейтинга",
      "hm.landing.trust_moderation": "Нулевая терпимость к нарушениям",
      "hm.landing.subbrand_circles_kicker": "Circles",
      "hm.landing.subbrand_circles_title": "Уже есть своя компания? Создайте для нее постоянный дом.",
      "hm.landing.subbrand_circles_body": "Circles — это сервис для групп друзей, которые уже встречаются: книжные клубы, покер по вечерам, беговые команды или коллеги. Мы берем на себя выбор места, напоминания и список гостей. Вам остается только прийти.",
      "hm.landing.subbrand_circles_b1": "Смена локаций, единый график",
      "hm.landing.subbrand_circles_b2": "Авто-напоминания и удобные RSVP",
      "hm.landing.subbrand_circles_b3": "Квизы, карты и тематические вечера по запросу",
      "hm.landing.subbrand_circles_cta": "Создать Circle",
      "hm.landing.subbrand_matchmaker_kicker": "Matchmaker",
      "hm.landing.subbrand_matchmaker_title": "ИИ выбирает столик. Вы просто приходите.",
      "hm.landing.subbrand_matchmaker_body": "Один 10-минутный квиз: характер, ценности, свободное время. Наш Matchmaker подберет вам компанию незнакомых людей, которые к десерту станут вам как старые друзья. Новый столик каждый раз.",
      "hm.landing.subbrand_matchmaker_b1": "Оценка по системе Big-5 и ценностям",
      "hm.landing.subbrand_matchmaker_b2": "Приглашения при совпадении от 0.70 и выше",
      "hm.landing.subbrand_matchmaker_b3": "Самообучение на основе каждого ужина",
      "hm.landing.subbrand_matchmaker_cta": "Пройти квиз",
      "hm.landing.how_kicker": "Как работает HustleMeet",
      "hm.landing.how_title": "Четыре шага. Найдите свою группу. Приходите.",
      "hm.landing.how_step1_ttl": "Выберите ваши интересы",
      "hm.landing.how_step1_sub": "Бег, код, инвестиции, кулинария, музыка, прогулки — расскажите нам, что вы любите и где живете.",
      "hm.landing.how_step2_ttl": "Найдите группы рядом",
      "hm.landing.how_step2_sub": "У каждой группы указан организатор, частота встреч и следующее событие. Оцените атмосферу заранее.",
      "hm.landing.how_step3_ttl": "Подтвердите участие (RSVP)",
      "hm.landing.how_step3_sub": "Одно нажатие, приглашение в календаре и четкое напоминание за день до встречи. События бывают платными или бесплатными — цена указана сразу.",
      "hm.landing.how_step4_ttl": "Приходите и общайтесь",
      "hm.landing.how_step4_sub": "Познакомьтесь с хостом и компанией, делайте то, что любите. Если все понравилось — возвращайтесь на следующей неделе.",
      "hm.landing.stats_kicker": "Новый способ знакомства в Australia",
      "hm.landing.stats_title": "Почему люди возвращаются снова и снова.",
      "hm.landing.stats_92_sub": "Чувствуют, что нашли общий язык хотя бы с одним человеком на встрече",
      "hm.landing.stats_68_sub": "Подтверждают участие в следующем событии в течение недели",
      "hm.landing.stats_85_sub": "Продолжают общаться с кем-то из группы после встречи",
      "hm.landing.why_kicker": "Почему мы работаем",
      "hm.landing.why_title": "Общие интересы. Реальные хосты. Живые встречи.",
      "hm.landing.why_1_ttl": "Интересы важнее геолокации",
      "hm.landing.why_1_body": "Людям в вашей группе действительно важно то же, что и вам — это кратчайший путь к настоящей дружбе.",
      "hm.landing.why_2_ttl": "Реальные организаторы событий",
      "hm.landing.why_2_body": "Каждую группу ведет опытный хост, который создает атмосферу и следит за порядком. Это не админ в Facebook, а живой человек.",
      "hm.landing.why_3_ttl": "Постоянство имеет значение",
      "hm.landing.why_3_body": "Новые события каждую неделю. То, чего стоит ждать. Ритм, который превращает незнакомцев в вашу привычную компанию по вторникам.",
      "hm.landing.why_4_ttl": "Безопасно и надежно",
      "hm.landing.why_4_body": "Проверенные участники, общественные места, никакой терпимости к неуважению. Наша поддержка на связи до, во время и после каждого события.",
      "hm.landing.why_5_ttl": "Дружба превыше всего",
      "hm.landing.why_5_body": "Это не сайт знакомств. Это сообщество. Реальные интересы, реальные люди, никакого бесконечного свайпанья.",
      "hm.landing.why_6_ttl": "Камерность, которая важна",
      "hm.landing.why_6_body": "В большинстве групп количество мест ограничено (от 4 до 15). Достаточно, чтобы завязать разговор, и достаточно мало, чтобы никто не чувствовал себя лишним.",
      "hm.landing.cities_kicker": "Где мы работаем",
      "hm.landing.cities_title": "Встречи групп по всей Australia и NZ.",
      "hm.landing.cities_sub_zero": "Открываем первые группы в 15+ городах — присоединяйтесь раньше всех и задавайте тон.",
      "hm.landing.cities_sub_some": "Группы активны в 15+ мегаполисах. Что-то интересное в вашем городе уже на этой неделе.",
      "hm.landing.cities_sub_one": "{{count}} активная группа · новые события каждую неделю · список городов постоянно растет.",
      "hm.landing.cities_sub_many": "{{count}} активных групп · новые события каждую неделю · список городов постоянно растет.",
      "hm.landing.forme_kicker": "Это для вас, если…",
      "hm.landing.forme_title": "Вы ищете реальные интересы. Реальных людей. Реальные события.",
      "hm.landing.forme_1": "Вы устали от дейтинг-приложений и хотите общаться с людьми на почве общих увлечений.",
      "hm.landing.forme_2": "Вы переехали в новый город и вам нужна теплая компания, а не очередной чат.",
      "hm.landing.forme_3": "Вы цените глубину общения — одна отличная группа лучше пятидесяти подписок.",
      "hm.landing.forme_4": "Вы верите, что общие интересы + пара часов в реальной жизни — это лучший способ найти своих.",
      "hm.landing.notme_kicker": "Это не для вас, если…",
      "hm.landing.notme_title": "HustleMeet подходит не всем.",
      "hm.landing.notme_1": "Вы не можете гарантировать свое присутствие — хосты и участники рассчитывают на вас.",
      "hm.landing.notme_2": "Вы ищете сервис для свиданий — здесь дружба на первом месте.",
      "hm.landing.notme_3": "Вы хотите вещать на аудиторию без личных встреч — это работает только вживую.",
      "hm.landing.notme_4": "Вы ждете бесконечных бесплатных бонусов — некоторые события платные, и цена всегда известна заранее.",
      "hm.landing.safety_kicker": "Наши обязательства по безопасности",
      "hm.landing.safety_title": "Душевно, но ответственно.",
      "hm.landing.safety_1_ttl": "Общественные места",
      "hm.landing.safety_1_body": "Все мероприятия проходят в общественных местах с персоналом — кафе, рестораны, парки, залы или студии. Никогда на дому, если только все участники не подтвердили согласие.",
      "hm.landing.safety_2_ttl": "Стандарты сообщества",
      "hm.landing.safety_2_body": "Нулевая терпимость к неуважению, домогательствам или обману. Участники, нарушающие правила, исключаются немедленно.",
      "hm.landing.safety_3_ttl": "Защита приватности",
      "hm.landing.safety_3_body": "Ваши данные никогда не публикуются. Вы сами решаете, с кем поддерживать связь после мероприятия.",
      "hm.landing.safety_4_ttl": "Поддержка на связи",
      "hm.landing.safety_4_body": "Наша команда доступна до, во время и после каждого события, если у вас возникнут вопросы или опасения.",
      "hm.landing.bottom_title": "Ваша новая любимая группа на расстоянии одного клика.",
      "hm.landing.bottom_sub": "Выберите свой путь — вступите в группу, соберите друзей в Circle или позвольте Matchmaker подобрать вам идеальный столик.",
      "hm.landing.bottom_cta_circles": "Создать Circle",
      "hm.landing.bottom_cta_mm": "Пройти квиз Matchmaker",
      "hm.circles.page_title": "HustleMeet Circles · Ваша компания, новое место каждый раз",
      "hm.circles.kicker": "HustleMeet · Circles",
      "hm.circles.hero_title": "Ваша компания. Новый столик каждый раз.",
      "hm.circles.hero_title_accent": "Ваша компания",
      "hm.circles.hero_sub": "Уже есть друзья? Circles — самый простой способ оставаться на связи. Мы бронируем столик, планируем квиз и готовим карты. Вам нужно только прийти — та же компания, новый вечер, новое место.",
      "hm.circles.cta_find": "Создать Circle",
      "hm.circles.cta_host_hero": "Вступить в открытый Circle",
      "hm.circles.trust_venue": "Смена проверенных локаций",
      "hm.circles.trust_party": "4–12 друзей в Circle",
      "hm.circles.trust_verified": "Проверенные участники",
      "hm.circles.trust_friendship": "Настоящая дружба рядом",
      "hm.circles.how_kicker": "Как это работает",
      "hm.circles.how_title": "Четыре шага. Ваша компания остается вместе.",
      "hm.circles.how_step1_ttl": "Создайте свой Circle",
      "hm.circles.how_step1_sub": "Дайте ему название, пригласите друзей (до 12 человек) и выберите ритм: раз в неделю, раз в две недели или раз в месяц.",
      "hm.circles.how_step2_ttl": "Мы выбираем место",
      "hm.circles.how_step2_sub": "Для каждой встречи мы предлагаем новое место: ресторан, квиз, карточный клуб или винный бар. Или закрепите любимое заведение.",
      "hm.circles.how_step3_ttl": "Подтверждение в один клик",
      "hm.circles.how_step3_sub": "Авто-напоминания, разделение счета, предоплаченные места, чтобы отмены не портили планы. Все счета оплачены еще до подачи закусок.",
      "hm.circles.how_step4_ttl": "Поддерживайте ритм",
      "hm.circles.how_step4_sub": "Вечер в календаре, который больше не нужно организовывать. Связи в группе крепнут с каждым месяцем.",
      "hm.circles.cadence_weekly_ttl": "Еженедельно",
      "hm.circles.cadence_weekly_sub": "Каждую неделю",
      "hm.circles.cadence_weekly_body": "Для тех, кто хочет ритуал по вторникам: те же лица, новое место каждый раз.",
      "hm.circles.cadence_fortnight_ttl": "Раз в две недели",
      "hm.circles.cadence_fortnight_sub": "Каждые 2 недели",
      "hm.circles.cadence_fortnight_body": "Золотая середина: регулярные встречи, которые легко вписать в плотный график.",
      "hm.circles.cadence_monthly_ttl": "Ежемесячно",
      "hm.circles.cadence_monthly_sub": "Раз в месяц",
      "hm.circles.cadence_monthly_body": "Circles для особых случаев: воскресное жаркое, большие квизы, дегустации или покер.",
      "hm.circles.feast_kicker": "Выберите формат вечера",
      "hm.circles.feast_title": "Три способа провести время",
      "hm.circles.feast_sub": "Каждый Circle сам выбирает формат. Чередуйте их или выберите любимый.",
      "hm.circles.feast_surprise_ttl": "Попробуйте новый ресторан",
      "hm.circles.feast_surprise_body": "Мы подберем три заведения рядом, вы проголосуете, а кухня подготовится к вашему приходу. Счет разделен, места оплачены.",
      "hm.circles.feast_preselect_ttl": "Квизы / карты / игры",
      "hm.circles.feast_preselect_body": "Забронируйте квиз, покерную комнату, бар с настолками или боулинг. Мы берем на себя бронь, взносы и напоминания.",
      "hm.circles.feast_per_event_ttl": "Разовый тематический вечер",
      "hm.circles.feast_per_event_body": "Дегустация вин, ужин от шеф-повара, коктейльный мастер-класс или квест. Что-то особенное время от времени — полностью организованное нами.",
      "hm.circles.live_kicker": "Прямо сейчас",
      "hm.circles.live_title": "Circles, к которым можно присоединиться",
      "hm.circles.live_host_link": "Создать свой собственный",
      "hm.circles.live_loading": "Загрузка…",
      "hm.circles.live_empty_title": "Станьте первым Circle в вашем городе.",
      "hm.circles.live_empty_sub": "Начните с тремя друзьями, и мы поможем пригласить остальных, а также организуем посещение первых трех заведений за наш счет.",
      "hm.circles.live_empty_cta": "Создать Circle",
      "hm.circles.host_kicker": "Для лидеров Circle",
      "hm.circles.host_title": "С вас компания. С нас — вся рутина.",
      "hm.circles.host_body": "Если вы тот самый друг, который всегда всех организует, Circles сэкономит вам часы времени. Авто-напоминания, предоплата RSVP, подборки мест, разделение счетов, управление очередью и кэшбэк-кошелек за каждый вечер. Вы просто отдыхаете вместе со всеми.",
      "hm.circles.host_perk_1": "Авто-подбор заведений — забудьте про поиск в Google",
      "hm.circles.host_perk_2": "Предоплаченные места — никаких неявок",
      "hm.circles.host_perk_3": "Счет разделен еще до десерта",
      "hm.circles.host_perk_4": "Напоминания и приглашения в календаре на автопилоте",
      "hm.circles.host_perk_5": "Список ожидания с авто-добавлением, если кто-то отменил встречу",
      "hm.circles.host_perk_6": "Кэшбэк-кошелек за каждый проведенный вечер",
      "hm.circles.host_cta": "Создать Circle сейчас",
      "hm.circles.why_kicker": "Почему Circles работают",
      "hm.circles.why_title": "Взрослой дружбе нужна инфраструктура",
      "hm.circles.why_1_ttl": "Та же компания, больше доверия",
      "hm.circles.why_1_body": "Жизнь часто разводит друзей. Circles — это фиксированный ритм, который держит всех вместе: одни и те же 4–12 друзей, встречающиеся в один и тот же вечер, несмотря ни на что.",
      "hm.circles.why_2_ttl": "Никакой усталости от организации",
      "hm.circles.why_2_body": "Никому не нужно быть «главным по чату» в WhatsApp. Мы выбираем места, шлем напоминания, принимаем платежи и закрываем все вопросы.",
      "hm.circles.why_3_ttl": "Новое место, новые воспоминания",
      "hm.circles.why_3_body": "Посещайте лучшие небольшие рестораны, квиз-румы и игровые бары Australia. Знакомые лица в новых декорациях — идеальный баланс привычки и новизны.",
      "hm.circles.bottom_title": "Вечер, которого ждет вся компания, и который никому не нужно планировать.",
      "hm.circles.bottom_sub": "Один Circle. Те же друзья. Новое место каждый раз.",
      "hm.circles.bottom_cta_find": "Создать Circle",
      "hm.circles.bottom_cta_mm": "Знакомьтесь с новыми людьми через Matchmaker →",
      "hm.circles.card_host_pill": "Circle",
      "hm.matchmaker.page_title": "HustleMeet Matchmaker · ИИ подберет идеальный столик",
      "hm.matchmaker.kicker": "HustleMeet · Matchmaker",
      "hm.matchmaker.hero_title": "ИИ выбирает столик. Вы просто приходите.",
      "hm.matchmaker.hero_title_accent": "Вы просто приходите.",
      "hm.matchmaker.hero_sub": "Ответьте на вопросы квиза о характере и ценностях. Наш Matchmaker подберет вам компанию из 4–7 незнакомцев, которые к десерту станут вам как старые друзья. Никаких свайпов и неловких пауз. Только общий стол и приятная беседа.",
      "hm.matchmaker.cta_quiz": "Пройти квиз для подбора",
      "hm.matchmaker.cta_see_circles": "Посмотреть Circles вместо этого →",
      "hm.matchmaker.trust_big5": "Оценка по системе Big-5",
      "hm.matchmaker.trust_seats": "4–8 мест за столиком",
      "hm.matchmaker.trust_lock": "Без свайпов и фото",
      "hm.matchmaker.trust_verified": "Только проверенные участники",
      "hm.matchmaker.how_kicker": "Как это работает",
      "hm.matchmaker.how_title": "Четыре шага к компании, которая вам подходит.",
      "hm.matchmaker.how_step1_ttl": "Пройдите квиз",
      "hm.matchmaker.how_step1_sub": "Десятиминутный опрос: личность, ценности, диета, свободное время. Проходится один раз.",
      "hm.matchmaker.how_step2_ttl": "Ночной подбор",
      "hm.matchmaker.how_step2_sub": "Наш ИИ анализирует ваш профиль и текущие планы ужинов в Australia. Когда индекс совместимости достигает 0.70 или выше, формируется приглашение.",
      "hm.matchmaker.how_step3_ttl": "Принять или отклонить",
      "hm.matchmaker.how_step3_sub": "Вы получаете четкое письмо-приглашение с указанием места, темы стола и трех интересов, которые объединяют вас с этой группой.",
      "hm.matchmaker.how_step4_ttl": "Система учится у вас",
      "hm.matchmaker.how_step4_sub": "Каждый опрос после ужина настраивает модель. Ваш следующий подбор становится точнее с каждым посещенным ужином.",
      "hm.matchmaker.science_kicker": "Почему это работает",
      "hm.matchmaker.science_title": "Matchmaker не гадает — он считает.",
      "hm.matchmaker.science_sub": "Каждое совпадение — это взвешенная комбинация четырех факторов. Когда средний балл стола выше 0.70, мы рассылаем приглашения. Все, что ниже, остается в очереди.",
      "hm.matchmaker.science_1_ttl": "Личность (35%)",
      "hm.matchmaker.science_1_body": "Косинусное сходство по вектору личности Big-5 — открытость опыту, добросовестность, экстраверсия, доброжелательность и эмоциональная стабильность.",
      "hm.matchmaker.science_2_ttl": "Ценности (25%)",
      "hm.matchmaker.science_2_body": "Совпадение по ценностям, которые делают беседу за ужином живой: семья, любопытство, рост, служение обществу, приключения.",
      "hm.matchmaker.science_3_ttl": "Интересы (25%)",
      "hm.matchmaker.science_3_body": "Пересечение по индексу Жаккара ваших конкретных интересов: трекинг в Six Foot Track, киноклуб в Sydney, кофейная сцена Melbourne, книги.",
      "hm.matchmaker.science_4_ttl": "Свободное время (15%)",
      "hm.matchmaker.science_4_body": "Вечер, когда вы действительно свободны. Никаких приглашений на 19:00 во вторник, если по вторникам у вас вечный спортзал.",
      "hm.matchmaker.safety_kicker": "Безопасность в основе",
      "hm.matchmaker.safety_title": "Matchmaker действует осторожно.",
      "hm.matchmaker.safety_1_ttl": "Порог уверенности",
      "hm.matchmaker.safety_1_body": "Если индекс группы ниже 0.70, приглашение не отправляется. Сомнительный подбор хуже, чем отсутствие ужина.",
      "hm.matchmaker.safety_2_ttl": "Блокировка взаимных «нет»",
      "hm.matchmaker.safety_2_body": "Если два участника приватно отметили «нет» после ужина, мы больше никогда не посадим их за один стол.",
      "hm.matchmaker.safety_3_ttl": "Автоматическая остановка",
      "hm.matchmaker.safety_3_body": "Если 20% или более недавних опросов оценивают стол на 2/5 или ниже, рассылка приглашений приостанавливается на 24 часа для проверки оператором.",
      "hm.matchmaker.safety_4_ttl": "Стандарты сообщества",
      "hm.matchmaker.safety_4_body": "Каждый участник принимает кодекс поведения. Мы исключаем нарушителей без права на апелляцию.",
      "hm.matchmaker.safety_5_ttl": "Только общественные заведения",
      "hm.matchmaker.safety_5_body": "Каждый ужин Matchmaker проходит в реальном ресторане с персоналом. Никогда не дома.",
      "hm.matchmaker.safety_6_ttl": "Обратная связь после события",
      "hm.matchmaker.safety_6_body": "Ваш приватный опрос влияет на будущие подборы и может стать поводом для проверки заведения или участника модератором.",
      "hm.matchmaker.loop_kicker": "Цикл обучения",
      "hm.matchmaker.loop_title": "Каждый ужин делает следующий еще лучше.",
      "hm.matchmaker.loop_body": "Matchmaker не статичен. После каждого события вы проходите короткий опрос: как прошла беседа, с кем бы вы поужинали снова, соответствовало ли место ожиданиям. Эти сигналы возвращаются в модель и меняют каждый будущий подбор.",
      "hm.matchmaker.loop_1": "Голоса за повторный ужин укрепляют ваши связи",
      "hm.matchmaker.loop_2": "Взаимные отказы незаметно разделяют пары",
      "hm.matchmaker.loop_3": "Рейтинги заведений и бесед настраивают состав стола",
      "hm.matchmaker.loop_4": "Рейтинг надежности защищает от частых отмен",
      "hm.matchmaker.loop_5": "Предвзятость обучения усиливает удачные совпадения из месяца в месяц",
      "hm.matchmaker.loop_6": "Вы сохраняете контроль — можно выйти или сбросить профиль в любое время",
      "hm.matchmaker.vs_kicker": "Что подходит мне?",
      "hm.matchmaker.vs_title": "Matchmaker или Circles?",
      "hm.matchmaker.vs_mm_ttl": "Выбирайте меня, если хотите разнообразия.",
      "hm.matchmaker.vs_mm_1": "Разные столы, города и кухни каждый раз",
      "hm.matchmaker.vs_mm_2": "Подобранная ИИ группа из 4–8 совместимых незнакомцев",
      "hm.matchmaker.vs_mm_3": "Еженедельный ритм, никаких обязательств организатора",
      "hm.matchmaker.vs_mm_4": "Идеально, если вы новичок в городе или путешествуете",
      "hm.matchmaker.vs_mm_cta": "Пройти квиз для подбора",
      "hm.matchmaker.vs_circles_ttl": "Выбирайте меня, если хотите постоянства.",
      "hm.matchmaker.vs_circles_1": "Тот же хост · то же место · тот же вечер",
      "hm.matchmaker.vs_circles_2": "Группа, которая станет вашей традицией по средам",
      "hm.matchmaker.vs_circles_3": "Кураторство опытным хостом-человеком",
      "hm.matchmaker.vs_circles_4": "Идеально, если вы переехали и хотите пустить корни",
      "hm.matchmaker.vs_circles_cta": "Просмотреть Circles",
      "hm.matchmaker.bottom_title": "Один квиз. Все последующие ужины уже вам подходят.",
      "hm.matchmaker.bottom_sub": "Вы получите первое совпадение в течение двух недель. Это самое простое «да», которое вы скажете в этом месяце.",
      "hm.matchmaker.bottom_cta": "Пройти квиз для подбора",
      "hm.matchmaker.bottom_note": "Без кредитной карты · 10 минут · Отмена в любой момент",
      /* AUTO-TRANSLATED hm.* END */
    },
  },
  tr: {
    translation: {
      /* AUTO-TRANSLATED finder.* (added) */
      "finder.title": "HustleFinder",
      "finder.tagline": "En yakın halka açık yeri bulun — gerçek veri, Avustralya genelinde.",
      "finder.search_placeholder": "Yer, sokak veya banliyö ara…",
      "finder.hint": "Yukarıya yazmaya başlayın, bir kategori seçin veya \"Yakınımda\" kullanın.",
      "finder.near_me": "Yakınımda",
      "finder.searching": "Aranıyor…",
      "finder.suburb.placeholder": "Banliyöye göre filtrele (örn. Darwin, NT)…",
      "finder.suburb.clear": "Temizle",
      "finder.search_in": "{{cat}} içinde ara",
      "finder.empty": "Sonuç yok — başka bir kategori veya arama terimi deneyin.",
      "finder.meta.title": "HustleFinder — ücretsiz Avustralya banliyö rehberi",
      "finder.meta.desc": "Her Avustralya banliyösünde yerel işletmeleri, şarj istasyonlarını, kafeleri, tuvaletleri ve daha fazlasını arayın — %100 ücretsiz.",
      "finder.footer.credits": "Veri kredileri ve atıf",
      "finder.footer.terms": "Şartlar",
      "finder.cat.toilet": "Tuvaletler",
      "finder.cat.fountain": "İçme Çeşmeleri",
      "finder.cat.transport": "Ulaşım",
      "finder.cat.park": "Parklar",
      "finder.cat.medical": "Sağlık",
      "finder.cat.school": "Okullar",
      "finder.cat.library": "Kütüphaneler",
      "finder.cat.petrol": "Benzin",
      "finder.cat.kidzone": "Çocuk Bölgesi",
      "finder.cat.puzzles": "Bulmacalar",
      "finder.cat.roadtrip": "Yol gezisi",
      "finder.hotels.title": "Yakında otel bul",
      "finder.hotels.sub": "Tek aramada 200+ rezervasyon sitesini karşılaştırır — Trivago tarafından desteklenmektedir.",
      "finder.hotels.where": "Nerede",
      "finder.hotels.where_ph": "Şehir veya banliyö (örn. Byron Bay, NSW)",
      "finder.hotels.checkin": "Giriş",
      "finder.hotels.checkout": "Çıkış",
      "finder.hotels.adults": "Yetişkinler",
      "finder.hotels.search": "Otel ara",
      "finder.hotels.disclosure": "Trivago'yu açar — rezervasyonlardan komisyon kazanabiliriz.",
      /* AUTO-TRANSLATED rt.* START */
      "rt.brand_name": "Roadtrip Planner",
      "rt.page_title": "Avustralya EV Yolculuğu Planlayıcı",
      "rt.from": "Nereden",
      "rt.to": "Nereye",
      "rt.from_placeholder": "Semt, kasaba veya şehir (örn. Newcastle NSW)",
      "rt.to_placeholder": "Semt, kasaba veya şehir (örn. Hobart TAS)",
      "rt.stop": "DURAK",
      "rt.stop_placeholder": "Ara durak",
      "rt.add_stop": "Durak ekle",
      "rt.upgrade_pro": "20 durağa kadar Pro'ya yükseltin",
      "rt.reverse": "Tersine çevir (dönüş yolunu hesapla)",
      "rt.from_current": "Mevcut konumdan",
      "rt.range_per_charge": "Şarj başına menzil (km)",
      "rt.connector": "Konektör",
      "rt.connector_any": "Herhangi",
      "rt.drive_hours_day": "Sürüş saati/gün",
      "rt.accommodation": "Konaklama",
      "rt.accom_any": "Herhangi (BnB · Otel · Karavan)",
      "rt.accom_bnb": "BnB",
      "rt.accom_hotel": "Otel",
      "rt.accom_caravan": "Karavan Parkı",
      "rt.min_rating": "Minimum puan",
      "rt.any_rating": "Tüm puanlar",
      "rt.only_5star": "Sadece 5★",
      "rt.schedule": "Program:",
      "rt.depart_at": "Kalkış",
      "rt.arrive_by": "Varış",
      "rt.hotel_checkin": "Otel girişi",
      "rt.hotel_checkout": "Otel çıkışı",
      "rt.overnight_hint": "Geceleme etapları sizi bu saatlerin dışında planlamayacaktır.",
      "rt.whos_in_car": "Araçta kimler var?",
      "rt.adults": "Yetişkinler",
      "rt.kids": "Çocuklar",
      "rt.ages": "Yaşlar",
      "rt.show_attractions": "Turistik yerleri göster",
      "rt.show_kids": "Çocuk dostu durakları göster",
      "rt.overnight_stays": "Konaklamalar",
      "rt.fast_chargers_only": "Sadece hızlı şarj cihazları",
      "rt.hotel_must_charger": "Otelde şarj ünitesi olmalı",
      "rt.pet_only": "Sadece evcil hayvan dostu konaklamalar",
      "rt.safety": "Güvenlik",
      "rt.safety_90": "%90 (düz)",
      "rt.safety_85": "%85 (varsayılan)",
      "rt.safety_80": "%80 (tepeler / çekme)",
      "rt.safety_70": "%70 (ağır yük)",
      "rt.save_trip": "Yolculuğu kaydet",
      "rt.plan_my_trip": "Yolculuğumu planla",
      "rt.refresh_trip": "Yolculuğu yenile",
      "rt.planning": "Planlanıyor…",
      "rt.resume_title": "Yolculuğa buradan devam et",
      "rt.resume_body": "Mevcut konumunuzu belirledik. Bir sonraki etabı tam şarj üzerinden değil, gerçekte sahip olduğunuz şarja göre planlamamız için göstergenizde görünen kalan menzili girin.",
      "rt.remaining_range": "Kalan menzil (km)",
      "rt.cancel": "İptal",
      "rt.replan_from_here": "Buradan yeniden planla",
      "rt.save_modal_title": "Bu yolculuğa isim ver",
      "rt.save_modal_body": "Kaydedilen yolculuklar profilinizde yer alır; istediğiniz zaman yeniden açıp Yeniden Planla'ya basın.",
      "rt.save_name_placeholder": "örn. Brisbane → Sydney (Byron üzerinden)",
      "rt.your_saved_trips": "Kaydedilen yolculuklarınız",
      "rt.custom_location": "Özel konum",
      "rt.trip_summary": "Yolculuk özeti",
      "rt.itinerary": "Güzergah",
      "rt.no_stops_needed": "Durağa gerek yok; tüm yolculuk menziliniz dahilinde.",
      "rt.chargers_in_corridor": "güzergah üzerindeki şarj üniteleri",
      "rt.planned_stops": "planlanmış duraklar",
      "rt.overnight_stop": "konaklama durağı",
      "rt.overnight_stops": "konaklama durakları",
      "rt.suggested_detours": "Önerilen yan yollar — eklemek için işaretleyin",
      "rt.replan_with_detours": "Yan yollarla yeniden planla",
      "rt.attractions_label": "Turistik Yer",
      "rt.kids_label": "Çocuklar",
      "rt.km_from_start": "km başlangıçtan itibaren",
      "rt.open_driving": "Sürüş modunu aç",
      "rt.big_font_hint": "Büyük yazı tipi, araç tutucu dostu. CarPlay/Android Auto tarayıcı yansıtma ile çalışır.",
      "rt.check_price_book": "Fiyat kontrolü ve Trivago üzerinden rezervasyon ↗",
      "rt.visit_site": "Siteyi ziyaret et ↗",
      "rt.charger_onsite": "Tesis bünyesinde şarj",
      "rt.leg_exceeds_range": "Bu etap belirlediğiniz menzili aşıyor; bu şarj ünitesine ulaşmadan enerjiniz bitebilir. Menzil varsayımını azaltın veya bir ara dolum ekleyin.",
      "rt.add_waypoint_replan": "Ara nokta olarak ekle ve yeniden planla",
      /* AUTO-TRANSLATED rt.* END */
                      /* AUTO-TRANSLATED hm.* START */
      "hm.nav.hustlemeet": "HustleMeet",
      "hm.nav.circles": "Circles",
      "hm.nav.matchmaker": "Matchmaker",
      "hm.nav.meet": "Buluş",
      "hm.nav.overview": "HustleMeetups",
      "hm.overview.page_title": "HustleMeetups — insanlarınızı bulmanın üç samimi yolu.",
      "hm.overview.kicker": "HustleSuite'in bir parçası",
      "hm.overview.hero_title": "İnsanlarınızı bulmanın üç samimi yolu.",
      "hm.overview.hero_title_accent": "insanlarınızı",
      "hm.overview.hero_sub": "HustleMeetups, Australia'da gerçek dostluklar kurmanın en nazik yoludur — size uyan havayı seçin. İster haftalık bir koşu grubu, ister mevcut ekibinizle aylık bir yemek, isterseniz de AI tarafından eşleştirilmiş yeni yüzlerden oluşan bir masa isteyin; burada size uygun bir yol var.",
      "hm.overview.tile_meet_kicker": "HustleMeet",
      "hm.overview.tile_meet_title": "İnsanlarınızı bulun. Sevdiğiniz şeyi yapın.",
      "hm.overview.tile_meet_body": "Australia'nın ilgi alanı odaklı gruplarının merkezi. Koşucular, yazılımcılar, yatırımcılar, yürüyüşçüler, dansçılar, kahvaltı kulüpleri, kitap ekipleri — yüzlerce grup, her hafta gerçek etkinlikler. Bir kez katılın veya her hafta gelin.",
      "hm.overview.tile_meet_cta": "Grupları keşfet",
      "hm.overview.tile_circles_kicker": "Circles",
      "hm.overview.tile_circles_title": "Sizin ekibiniz. Her seferinde yeni bir masa.",
      "hm.overview.tile_circles_body": "Düzenli arkadaş grubunuzu getirin. Biz yeni bir restoran ayarlarız, bilgi yarışmasını planlarız, kartları dağıtırız. Siz sadece gelirsiniz. Mevcut bir ekibi, kimsenin organizasyonla uğraşmasına gerek kalmadan yakın tutmanın en sıkı yolu.",
      "hm.overview.tile_circles_cta": "Bir Circle başlat",
      "hm.overview.tile_matchmaker_kicker": "Matchmaker",
      "hm.overview.tile_matchmaker_title": "Masayı AI seçer. Siz sadece gelirsiniz.",
      "hm.overview.tile_matchmaker_body": "10 dakikalık bir test yapın. Bir gecede sizi, tatlıya gelindiğinde eski dostlarmış gibi hissettiren yabancılarla aynı masaya oturturuz. Her seferinde farklı bir masa.",
      "hm.overview.tile_matchmaker_cta": "Teste başla",
      "hm.overview.trust_kicker": "Neden işe yarıyor?",
      "hm.overview.trust_title": "Önce güven. Sonra masa.",
      "hm.overview.trust_1_title": "Doğrulanmış üyeler",
      "hm.overview.trust_1_body": "Her grup gerçek bir kimlik kontrolü + güvenilirlik puanı ile başlar. Tek kullanımlık hesaplara yer yok.",
      "hm.overview.trust_2_title": "Gerçek ev sahipleri",
      "hm.overview.trust_2_body": "Her grup; havayı belirleyen, ritmi ayarlayan ve ortamla ilgilenen denetlenmiş bir ev sahibi tarafından yönetilir.",
      "hm.overview.trust_3_title": "Önce arkadaşlık",
      "hm.overview.trust_3_body": "Buradaki her şey flört için değil, gerçek arkadaşlıklar için tasarlandı. Her zaman.",
      "hm.overview.trust_4_title": "Sıfır toleranslı moderasyon",
      "hm.overview.trust_4_body": "Bir rapor, bir inceleme, bir karar. Olumsuz davranış sergileyenler aynı gece uzaklaştırılır.",
      "hm.overview.footer_line": "HustleSuite'in bir parçası — Australia sermayeli ve işletmelidir.",
      "hm.landing.page_title": "HustleMeet · İnsanlarınızı bulun. Sevdiğiniz şeyi yapın.",
      "hm.landing.kicker": "Australia'nın ilgi alanı ağı",
      "hm.landing.hero_title": "İnsanlarınızı bulun. Sevdiğiniz şeyi yapın.",
      "hm.landing.hero_title_accent": "insanlarınızı",
      "hm.landing.hero_sub": "HustleMeet, Australia'nın ilgi alanı odaklı gruplarının merkezidir. Öğle yemeğinde hata ayıklayan yazılımcılar. Sabah 6'da uzun mesafe koşucuları. Aylık buluşan yatırımcılar. Yürüyüş grupları, dans ekipleri, kahvaltı kulüpleri, kutu oyunu geceleri. Bir grup seçin, bir sonraki etkinliğe kaydolun ve katılın.",
      "hm.landing.cta_explore_circles": "Gruplara göz at",
      "hm.landing.cta_try_matchmaker": "Kendi grubunu başlat",
      "hm.landing.trust_verified": "Doğrulanmış üyeler",
      "hm.landing.trust_venues": "Denetlenmiş etkinlik sahipleri",
      "hm.landing.trust_friendship": "Önce arkadaşlık — asla flört değil",
      "hm.landing.trust_moderation": "Sıfır toleranslı moderasyon",
      "hm.landing.subbrand_circles_kicker": "Circles",
      "hm.landing.subbrand_circles_title": "Zaten bir ekibiniz mi var? Onlara kalıcı bir yuva verin.",
      "hm.landing.subbrand_circles_body": "Circles, zaten buluşan arkadaş grupları içindir — kitap kulübü, poker gecesi, koşu ekibi, iş arkadaşları. Mekanı, hatırlatıcıları ve listeyi biz hallederiz. Siz sadece gelirsiniz.",
      "hm.landing.subbrand_circles_b1": "Değişen mekanlar, sabit bir liste",
      "hm.landing.subbrand_circles_b2": "Otomatik hatırlatıcılar + kolay LCV",
      "hm.landing.subbrand_circles_b3": "Bilgi yarışmaları, kartlar ve temalı geceler her an hazır",
      "hm.landing.subbrand_circles_cta": "Bir Circle başlat",
      "hm.landing.subbrand_matchmaker_kicker": "Matchmaker",
      "hm.landing.subbrand_matchmaker_title": "Masayı AI seçer. Siz sadece gelirsiniz.",
      "hm.landing.subbrand_matchmaker_body": "10 dakikalık tek bir test — kişilik, değerler, uygunluk. Matchmaker'ımız sizi, tatlıya gelindiğinde eski dostlarmış gibi hissettiren yabancılarla aynı masaya oturtur. Her seferinde farklı bir masa.",
      "hm.landing.subbrand_matchmaker_b1": "Big-5 kişilik ve değer puanlaması",
      "hm.landing.subbrand_matchmaker_b2": "Uyum puanınız 0.70 veya üzerindeyse anında davet",
      "hm.landing.subbrand_matchmaker_b3": "Katıldığınız her yemekten yeni bir şeyler öğrenir",
      "hm.landing.subbrand_matchmaker_cta": "Teste başla",
      "hm.landing.how_kicker": "HustleMeet nasıl çalışır?",
      "hm.landing.how_title": "Dört adım. Grubunuzu bulun. Katılın.",
      "hm.landing.how_step1_ttl": "İlgi alanlarınızı seçin",
      "hm.landing.how_step1_sub": "Koşu, kodlama, yatırım, yemek pişirme, müzik, yürüyüş — nelerden hoşlandığınızı ve nerede yaşadığınızı bize söyleyin.",
      "hm.landing.how_step2_ttl": "Yakınınızdaki gruplara göz atın",
      "hm.landing.how_step2_sub": "Her grup; ev sahibini, ritmini ve bir sonraki etkinliğini yayınlar. Katılmadan önce ortamı inceleyin.",
      "hm.landing.how_step3_ttl": "Bir sonraki etkinliğe LCV yapın",
      "hm.landing.how_step3_sub": "Tek bir dokunuş, bir takvim daveti, bir gün öncesinden net bir hatırlatıcı. Bazı etkinlikler ücretsiz, bazıları ücretlidir — fiyat her zaman bellidir.",
      "hm.landing.how_step4_ttl": "Katılın ve bağ kurun",
      "hm.landing.how_step4_sub": "Ev sahibiyle tanışın, ekiple tanışın, sevdiğiniz şeyi yapın. Eğer kafa dengi bulursanız haftaya yine gelin.",
      "hm.landing.stats_kicker": "Australia'nın yeni tanışma yolu",
      "hm.landing.stats_title": "İnsanların tekrar gelmesinin bir sebebi var.",
      "hm.landing.stats_92_sub": "Etkinlikte en az bir kişiyle kafa dengi olduğunu hissediyor",
      "hm.landing.stats_68_sub": "Bir hafta içinde bir sonraki etkinliklerine LCV yapıyor",
      "hm.landing.stats_85_sub": "Gruptan biriyle iletişimde kalmaya devam ediyor",
      "hm.landing.why_kicker": "Neden işe yarıyoruz?",
      "hm.landing.why_title": "Gerçek ilgi alanları. Gerçek ev sahipleri. Gerçek etkinlikler.",
      "hm.landing.why_1_ttl": "Konum değil, ilgi alanı öncelikli",
      "hm.landing.why_1_body": "Grubunuzdaki insanlar aslında sizin önemsediğiniz şeyi önemsiyor — bu, gerçek arkadaşlığa giden kestirme yoldur.",
      "hm.landing.why_2_ttl": "Gerçek etkinlik sahipleri",
      "hm.landing.why_2_body": "Her grubun; havayı belirleyen, ritmi ayarlayan ve bir sorun olduğunda müdahale eden denetlenmiş bir ev sahibi vardır. Bir Facebook yöneticisi değil, gerçek bir kişi.",
      "hm.landing.why_3_ttl": "Süreklilik önemlidir",
      "hm.landing.why_3_body": "Her hafta yeni etkinlikler. Sabırsızlıkla bekleyeceğiniz bir şey. Yabancıları Salı gecesi ekibinize dönüştüren bir ritim.",
      "hm.landing.why_4_ttl": "Güvenli ve emniyetli",
      "hm.landing.why_4_body": "Doğrulanmış üyeler, halka açık mekanlar, saygısızlığa karşı sıfır tolerans. Destek ekibimiz her etkinlik öncesinde, sırasında ve sonrasında görev başındadır.",
      "hm.landing.why_5_ttl": "Önce arkadaşlık",
      "hm.landing.why_5_body": "Bu bir flört uygulaması değil. Bu bir topluluktur. Gerçek ilgi alanları, gerçek insanlar, arada kaydırma (swiping) yok.",
      "hm.landing.why_6_ttl": "Önemseyecek kadar küçük",
      "hm.landing.why_6_body": "Çoğu grup, etkinlikleri 4-15 kişiyle sınırlar. Gerçek sohbetleri başlatacak kadar büyük, kimsenin arada kaybolmayacağı kadar küçük.",
      "hm.landing.cities_kicker": "Neredeyiz?",
      "hm.landing.cities_title": "Australia ve NZ genelinde buluşan gruplar.",
      "hm.landing.cities_sub_zero": "15'ten fazla şehirde ilk gruplar açılıyor — erkenden katılın ve ortamı siz şekillendirin.",
      "hm.landing.cities_sub_some": "15'ten fazla metropolde gruplar aktif. Bu hafta şehrinizde mutlaka bir şeyler var.",
      "hm.landing.cities_sub_one": "{{count}} aktif grup · her hafta yeni etkinlikler · sürekli yeni şehirler ekleniyor.",
      "hm.landing.cities_sub_many": "{{count}} aktif grup · her hafta yeni etkinlikler · sürekli yeni şehirler ekleniyor.",
      "hm.landing.forme_kicker": "Bu tam size göre, eğer…",
      "hm.landing.forme_title": "Gerçek ilgi alanları. Gerçek insanlar. Gerçek etkinlikler istiyorsanız.",
      "hm.landing.forme_1": "Uygulamalarda kaydırma yapmaktan yorulduysanız ve sevdiğiniz şeyler etrafında insanlarla gerçekten tanışmak istiyorsanız.",
      "hm.landing.forme_2": "Yeni bir şehre taşındıysanız ve başka bir sohbet grubu değil, sıcak bir ekip arıyorsanız.",
      "hm.landing.forme_3": "Yüzeysellik yerine derinliğe önem veriyorsanız — bir harika grup, elli takipçiden iyidir.",
      "hm.landing.forme_4": "Ortak ilgi alanları + gerçek hayatta ayrılmış iki saatin asıl çözüm olduğuna inanıyorsanız.",
      "hm.landing.notme_kicker": "Bu size göre değil, eğer…",
      "hm.landing.notme_title": "HustleMeet herkes için uygun olmayabilir.",
      "hm.landing.notme_1": "Katılma sözü veremiyorsanız — ev sahipleri ve üyeler sizin LCV yanıtınıza güveniyor.",
      "hm.landing.notme_2": "Bir flört servisi arıyorsanız — burada arkadaşlıklar her zaman önce gelir.",
      "hm.landing.notme_3": "Kimseyle tanışmadan sadece yayın yapmak istiyorsanız — bu sadece yüz yüze işe yarar.",
      "hm.landing.notme_4": "Sürekli ücretsiz ayrıcalıklar bekliyorsanız — bazı etkinliklerin bir maliyeti vardır ve fiyat her zaman peşin söylenir.",
      "hm.landing.safety_kicker": "Güvenlik taahhüdümüz",
      "hm.landing.safety_title": "Sıcak ama asla dikkatsiz değil.",
      "hm.landing.safety_1_ttl": "Halka açık mekanlar",
      "hm.landing.safety_1_body": "Her etkinlik halka açık, personeli olan bir mekanda gerçekleşir — kafe, restoran, park, spor salonu veya stüdyo. Her katılımcı onaylamadığı sürece asla birinin evi değildir.",
      "hm.landing.safety_2_ttl": "Topluluk standartları",
      "hm.landing.safety_2_body": "Saygısızlığa, tacize veya dürüst olmayan davranışlara sıfır tolerans. Kuralları ihlal eden üyeler derhal uzaklaştırılır.",
      "hm.landing.safety_3_ttl": "Gizlilik koruması",
      "hm.landing.safety_3_body": "Bilgileriniz asla halka açık paylaşılmaz. Her etkinlikten sonra kiminle iletişimde kalacağınızı siz kontrol edersiniz.",
      "hm.landing.safety_4_ttl": "Destek hattı",
      "hm.landing.safety_4_body": "Sorularınız veya endişeleriniz olursa ekibimiz her etkinlik öncesinde, sırasında ve sonrasında yanınızdadır.",
      "hm.landing.bottom_title": "Bir sonraki favori grubunuz bir dokunuş uzağınızda.",
      "hm.landing.bottom_sub": "Yolunuzu seçin — mevcut bir gruba katılın, ekibinizi bir Circle'a getirin veya Matchmaker'ın sizi size uygun bir masaya oturtmasına izin verin.",
      "hm.landing.bottom_cta_circles": "Bir Circle başlat",
      "hm.landing.bottom_cta_mm": "Matchmaker testini çöz",
      "hm.circles.page_title": "HustleMeet Circles · Sizin ekibiniz, her seferinde yeni mekan",
      "hm.circles.kicker": "HustleMeet · Circles",
      "hm.circles.hero_title": "Sizin ekibiniz. Her seferinde yeni bir masa.",
      "hm.circles.hero_title_accent": "Sizin ekibiniz",
      "hm.circles.hero_sub": "Zaten arkadaşlarınız var mı? Circles onları yakın tutmanın en kolay yoludur. Mekanı ayarlarız, bilgi yarışmasını planlarız, kartları dağıtırız. Siz sadece gelirsiniz — aynı ekip, yeni bir gece, yeni bir yer.",
      "hm.circles.cta_find": "Bir Circle başlat",
      "hm.circles.cta_host_hero": "Açık bir Circle'a katıl",
      "hm.circles.trust_venue": "Değişen denetlenmiş mekanlar",
      "hm.circles.trust_party": "Circle başına 4-12 arkadaş",
      "hm.circles.trust_verified": "Doğrulanmış üyeler",
      "hm.circles.trust_friendship": "Gerçek dostluklar, her zaman yakın",
      "hm.circles.how_kicker": "Nasıl çalışır?",
      "hm.circles.how_title": "Dört adım. Ekibiniz ekip kalmaya devam etsin.",
      "hm.circles.how_step1_ttl": "Circle'ınızı başlatın",
      "hm.circles.how_step1_sub": "İsim verin, arkadaşlarınızı davet edin (12 kişiye kadar) ve bir ritim seçin — haftalık, iki haftada bir veya aylık.",
      "hm.circles.how_step2_ttl": "Mekanı biz seçelim",
      "hm.circles.how_step2_sub": "Her etkinlik için yeni bir yer öneririz — restoran, bilgi yarışması gecesi, kart odası, şarap barı. Veya favori bir yerinizi sabitleyin.",
      "hm.circles.how_step3_ttl": "Herkes tek dokunuşla LCV yapsın",
      "hm.circles.how_step3_sub": "Otomatik hatırlatıcılar, maliyet paylaşımı, ön ödemeli koltuklar; böylece gelmeyenler planı bozmaz. Hesap, başlangıç yemeği gelmeden halledilir.",
      "hm.circles.how_step4_ttl": "Ritmi koruyun",
      "hm.circles.how_step4_sub": "Ajandada artık bir daha asla organize edilmesi gerekmeyen bir gece. Grup dakikalar içinde değil, aylar içinde kenetlenir.",
      "hm.circles.cadence_weekly_ttl": "Haftalık",
      "hm.circles.cadence_weekly_sub": "Her hafta",
      "hm.circles.cadence_weekly_body": "Salı gecesi ritüeli isteyen ekipler için — aynı yüzler, her seferinde yeni bir yer.",
      "hm.circles.cadence_fortnight_ttl": "İki haftada bir",
      "hm.circles.cadence_fortnight_sub": "2 haftada bir",
      "hm.circles.cadence_fortnight_body": "İdeal denge — yoğun takvimleri yormadan düzenli görüşme.",
      "hm.circles.cadence_monthly_ttl": "Aylık",
      "hm.circles.cadence_monthly_sub": "Her ay",
      "hm.circles.cadence_monthly_body": "Özel gün Circles — Pazar rostosu, büyük bilgi yarışması geceleri, tadım menüleri, poker.",
      "hm.circles.feast_kicker": "Gecenizi seçin",
      "hm.circles.feast_title": "Geçirmek için üç yol",
      "hm.circles.feast_sub": "Her Circle gecenin nasıl geçeceğine karar verir. Bunları dönüştürün veya ekibinizin favorisini sabitleyin.",
      "hm.circles.feast_surprise_ttl": "Yeni bir restoran deneyin",
      "hm.circles.feast_surprise_body": "Yakınınızdaki üç mekanı listeleriz, herkes oy verir, mutfak tam bir masa için hazırlanır. Maliyet paylaşımı, ön ödemeli koltuklar.",
      "hm.circles.feast_preselect_ttl": "Bilgi yarışması / kartlar / oyunlar",
      "hm.circles.feast_preselect_body": "Bir bilgi yarışması gecesi, poker odası, kutu oyunu barı veya bowling alanı ayırtın. Mekanı, katılım ücretini ve hatırlatıcıları biz hallederiz.",
      "hm.circles.feast_per_event_ttl": "Tek seferlik temalı gece",
      "hm.circles.feast_per_event_body": "Şarap tadımı, şefin masası, kokteyl kursu, kaçış odası. Arada bir özel bir şey — tamamen organize edilmiş.",
      "hm.circles.live_kicker": "Şu anda",
      "hm.circles.live_title": "Katılabileceğiniz Circles",
      "hm.circles.live_host_link": "Kendi Circle'ını başlat",
      "hm.circles.live_loading": "Yükleniyor…",
      "hm.circles.live_empty_title": "Şehrinizdeki ilk Circle olun.",
      "hm.circles.live_empty_sub": "Üç arkadaşınızla bir tane başlatın, gerisini davet etmenize yardımcı olalım — ayrıca ilk üç mekan bizden.",
      "hm.circles.live_empty_cta": "Bir Circle başlat",
      "hm.circles.host_kicker": "Circle liderleri için",
      "hm.circles.host_title": "Siz ekibi getirin. Organizasyonu biz yapalım.",
      "hm.circles.host_body": "Eğer her zaman geceyi organize eden o arkadaşsanız, Circles size saatler kazandırır. Otomatik hatırlatıcılar, ön ödemeli LCV'ler, mekan listeleri, maliyet paylaşımları, bekleme listesi yönetimi ve her dışarı çıkışınızda bir nakit iade cüzdanı. Siz sadece normal bir üye gibi katılırsınız.",
      "hm.circles.host_perk_1": "Otomatik mekan listeleri — bir daha asla Google'da kaybolmayın",
      "hm.circles.host_perk_2": "Ön ödemeli koltuklar — sıfır gelmeme oranı",
      "hm.circles.host_perk_3": "Tatlı gelmeden maliyet paylaşımı hazır",
      "hm.circles.host_perk_4": "Otomatik hatırlatıcılar + takvim davetleri",
      "hm.circles.host_perk_5": "Bir arkadaş iptal ederse otomatik onaylı bekleme listesi",
      "hm.circles.host_perk_6": "Her gece dışarı çıktığınızda nakit iade cüzdanı",
      "hm.circles.host_cta": "Hemen bir Circle başlat",
      "hm.circles.why_kicker": "Circles neden işe yarıyor?",
      "hm.circles.why_title": "Yetişkin arkadaşlıklarının bir altyapıya ihtiyacı var",
      "hm.circles.why_1_ttl": "Aynı ekip, daha derin güven",
      "hm.circles.why_1_body": "Hayat arkadaşları birbirinden koparabilir. Circles, ne olursa olsun aynı 4-12 arkadaşı aynı hafta içi gecesinde bir araya getiren sabit bir ritimdir.",
      "hm.circles.why_2_ttl": "Sıfır organizasyon yorgunluğu",
      "hm.circles.why_2_body": "Kimsenin WhatsApp grubunun başkanı olmasına gerek yok. Mekanları seçiyoruz, hatırlatıcıları gönderiyoruz, ödemeleri alıyoruz ve süreci tamamlıyoruz.",
      "hm.circles.why_3_ttl": "Yeni mekan, yeni anı",
      "hm.circles.why_3_body": "Australia'nın en iyi küçük restoranlarını, bilgi yarışması odalarını ve oyun barlarını keşfedin. Tanıdık yüzler, taze ortamlar — rutin ve yeniliğin mükemmel dengesi.",
      "hm.circles.bottom_title": "Ekibinizin, kimsenin planlamasına gerek kalmadan sabırsızlıkla beklediği gece.",
      "hm.circles.bottom_sub": "Bir Circle. Aynı arkadaşlar. Her seferinde yeni mekan.",
      "hm.circles.bottom_cta_find": "Bir Circle başlat",
      "hm.circles.bottom_cta_mm": "Matchmaker ile yeni insanlarla tanışın →",
      "hm.circles.card_host_pill": "Circle",
      "hm.matchmaker.page_title": "HustleMeet Matchmaker · AI doğru masayı seçer",
      "hm.matchmaker.kicker": "HustleMeet · Matchmaker",
      "hm.matchmaker.hero_title": "Masayı AI seçer. Siz sadece gelirsiniz.",
      "hm.matchmaker.hero_title_accent": "Siz sadece gelirsiniz.",
      "hm.matchmaker.hero_sub": "Kısa bir kişilik ve değerler testini yanıtlayın. Matchmaker'ımız sizi, tatlıya gelindiğinde eski dostlarmış gibi hissettiren dört ila yedi yabancıyla aynı masaya oturtur. Kaydırma yok. Boş laf yok. Sadece hazır bir masa.",
      "hm.matchmaker.cta_quiz": "Eşleşme testini çöz",
      "hm.matchmaker.cta_see_circles": "Bunun yerine Circles'a göz at →",
      "hm.matchmaker.trust_big5": "Big-5 kişilik puanlaması",
      "hm.matchmaker.trust_seats": "Masa başına 4-8 koltuk",
      "hm.matchmaker.trust_lock": "Sıfır kaydırma · sıfır fotoğraf",
      "hm.matchmaker.trust_verified": "Sadece doğrulanmış üyeler",
      "hm.matchmaker.how_kicker": "Nasıl çalışır?",
      "hm.matchmaker.how_title": "Size zaten uyan bir masaya giden dört adım.",
      "hm.matchmaker.how_step1_ttl": "Teste katılın",
      "hm.matchmaker.how_step1_sub": "On dakikalık anket — kişilik, değerler, diyet, uygunluk. Bunu bir kez yaparsınız.",
      "hm.matchmaker.how_step2_ttl": "Bir gecede eşleşme",
      "hm.matchmaker.how_step2_sub": "AI'mız profilinizi ve Australia'nın canlı akşam yemeği listesini okur. Uyum puanınız 0.70 veya üzerindeyse kişisel bir davet hazırlar.",
      "hm.matchmaker.how_step3_ttl": "Kabul et veya reddet",
      "hm.matchmaker.how_step3_sub": "Mekanı, masa temasını ve grupla paylaştığınız üç ilgi alanını içeren net bir davet e-postası alırsınız.",
      "hm.matchmaker.how_step4_ttl": "Sizden öğrenir",
      "hm.matchmaker.how_step4_sub": "Yemek sonrası her anket modeli optimize eder. Ne kadar çok yemeğe katılırsanız, bir sonraki eşleşmeniz o kadar akıllıca olur.",
      "hm.matchmaker.science_kicker": "Neden işe yarıyor?",
      "hm.matchmaker.science_title": "Matchmaker tahmin etmez — puanlar.",
      "hm.matchmaker.science_sub": "Her eşleşme dört faktörün ağırlıklı bileşimidir. Tüm masanın ortalaması 0.70'in üzerine çıktığında davetleri göndeririz. Bunun altındakiler sırada bekler.",
      "hm.matchmaker.science_1_ttl": "Kişilik (%35)",
      "hm.matchmaker.science_1_body": "Big-5 kişilik vektörü boyunca kosinüs benzerliği — açıklık, sorumluluk, dışadönüklük, uyumluluk ve duygusal denge.",
      "hm.matchmaker.science_2_ttl": "Değerler (%25)",
      "hm.matchmaker.science_2_body": "Bir akşam yemeği sohbetinin gerçekten akıcı olmasını sağlayan değerlerdeki örtüşme — aile, merak, gelişim, hizmet, macera.",
      "hm.matchmaker.science_3_ttl": "İlgi Alanları (%25)",
      "hm.matchmaker.science_3_body": "Belirli ilgi alanlarınızdaki Jaccard örtüşmesi — Six Foot Track yürüyüşü, Sydney film kulübü, Melbourne kahve dünyası, yılın üçüncü kitabı.",
      "hm.matchmaker.science_4_ttl": "Uygunluk (%15)",
      "hm.matchmaker.science_4_body": "Gerçekten katılabileceğiniz gece. Eğer takviminizde her Salı spor salonu varsa, Salı saat 19:00 daveti almazsınız.",
      "hm.matchmaker.safety_kicker": "Tasarım gereği güvenli",
      "hm.matchmaker.safety_title": "Matchmaker sessizce temkinlidir.",
      "hm.matchmaker.safety_1_ttl": "Güven eşiği",
      "hm.matchmaker.safety_1_body": "0.70 grup puanının altında davet gönderilmez. Zayıf bir eşleşme, hiç yemek yiyememekten iyidir — ta ki bir sorun çıkana kadar.",
      "hm.matchmaker.safety_2_ttl": "Karşılıklı-hayır engelleme listesi",
      "hm.matchmaker.safety_2_body": "İki üye bir yemekten sonra özel olarak 'hayır' sinyali verirse, onları bir daha asla aynı masaya oturtmayız.",
      "hm.matchmaker.safety_3_ttl": "Otomatik durdurma anahtarı",
      "hm.matchmaker.safety_3_body": "Son anketlerin %20'si veya daha fazlası bir masayı 2/5 veya daha düşük puanlarsa, bir operatör inceleme yapana kadar davetler 24 saat duraklatılır.",
      "hm.matchmaker.safety_4_ttl": "Topluluk standartları",
      "hm.matchmaker.safety_4_body": "Her üye yalın dilli bir davranış kurallarını kabul eder. İhlal edenleri uzaklaştırırız — itiraz hakkı yoktur.",
      "hm.matchmaker.safety_5_ttl": "Sadece halka açık mekanlar",
      "hm.matchmaker.safety_5_body": "Her Matchmaker yemeği personeli olan gerçek bir restoranda gerçekleşir. Asla birinin evinde değil.",
      "hm.matchmaker.safety_6_ttl": "Etkinlik sonrası geri bildirim",
      "hm.matchmaker.safety_6_body": "Özel anketiniz gelecekteki eşleşmeleri şekillendirir ve bir mekanın veya bir üyenin moderatör incelemesini tetikleyebilir.",
      "hm.matchmaker.loop_kicker": "Öğrenme döngüsü",
      "hm.matchmaker.loop_title": "Her yemek bir sonrakini daha iyi kılar.",
      "hm.matchmaker.loop_body": "Matchmaker statik değildir. Her etkinlikten sonra kısa bir özel anket gönderirsiniz — sohbetin nasıl hissettirdiği, kiminle tekrar yemek yiyeceğiniz, mekanın beklentiyi karşılayıp karşılamadığı. Bu sinyaller modele geri beslenir ve gelecekteki her eşleşmeyi yeniden şekillendirir.",
      "hm.matchmaker.loop_1": "Üyelerin \"tekrar yemek ye\" oyları eşleşmelerinizi güçlendirir",
      "hm.matchmaker.loop_2": "Karşılıklı-hayır oyları eşleşmeleri sessizce bitirir",
      "hm.matchmaker.loop_3": "Mekan ve sohbet puanları koltuk karmasını optimize eder",
      "hm.matchmaker.loop_4": "Güvenilirlik puanı, üyeleri sürekli gelmeyen kişilerden korur",
      "hm.matchmaker.loop_5": "Öğrenme eğilimi, aylar geçtikçe iyi eşleşmeleri pekiştirir",
      "hm.matchmaker.loop_6": "Kontrol sizde kalır — istediğiniz zaman ayrılabilir veya profilinizi sıfırlayabilirsiniz",
      "hm.matchmaker.vs_kicker": "Hangisi benim için uygun?",
      "hm.matchmaker.vs_title": "Matchmaker mı Circles mı?",
      "hm.matchmaker.vs_mm_ttl": "Çeşitlilik istiyorsam beni seç.",
      "hm.matchmaker.vs_mm_1": "Her seferinde farklı bir masa, şehir, mutfak",
      "hm.matchmaker.vs_mm_2": "AI tarafından seçilmiş 4-8 uyumlu yabancıdan oluşan grup",
      "hm.matchmaker.vs_mm_3": "Haftalık ritim, ev sahibi sorumluluğu yok",
      "hm.matchmaker.vs_mm_4": "Yeni bir şehirdeyseniz veya seyahat ediyorsanız harikadır",
      "hm.matchmaker.vs_mm_cta": "Eşleşme testini çöz",
      "hm.matchmaker.vs_circles_ttl": "Düzenli bir grup istiyorsam beni seç.",
      "hm.matchmaker.vs_circles_1": "Aynı ev sahibi · aynı mekan · aynı hafta içi gecesi",
      "hm.matchmaker.vs_circles_2": "Çarşamba geleneğinize dönüşen bir grup",
      "hm.matchmaker.vs_circles_3": "Deneyimli bir ev sahibi tarafından manuel olarak seçilmiş",
      "hm.matchmaker.vs_circles_4": "Yeni taşındıysanız ve kök salmak istiyorsanız harikadır",
      "hm.matchmaker.vs_circles_cta": "Circles'a göz at",
      "hm.matchmaker.bottom_title": "Tek test. Sonrasındaki her yemek zaten size uyar.",
      "hm.matchmaker.bottom_sub": "İki hafta içinde eşleşeceksiniz. Bu ay yapacağınız en kolay \"evet\" olacak.",
      "hm.matchmaker.bottom_cta": "Eşleşme testini çöz",
      "hm.matchmaker.bottom_note": "Kredi kartı gerekmez · 10 dakika · İstediğiniz zaman ayrılın",
      /* AUTO-TRANSLATED hm.* END */
    },
  },
  he: {
    translation: {
      /* AUTO-TRANSLATED finder.* (added) */
      "finder.title": "HustleFinder",
      "finder.tagline": "מצא את המקום הציבורי הקרוב ביותר — נתונים אמיתיים, ברחבי אוסטרליה.",
      "finder.search_placeholder": "חפש מקומות, רחובות או פרברים…",
      "finder.hint": "התחל להקליד למעלה, הקש על קטגוריה, או השתמש ב\"קרוב אליי\".",
      "finder.near_me": "קרוב אליי",
      "finder.searching": "מחפש…",
      "finder.suburb.placeholder": "סנן לפי פרבר (למשל Darwin, NT)…",
      "finder.suburb.clear": "נקה",
      "finder.search_in": "חפש ב{{cat}}",
      "finder.empty": "אין תוצאות — נסה קטגוריה אחרת או מונח חיפוש אחר.",
      "finder.meta.title": "HustleFinder — מדריך פרברים אוסטרלי חינמי",
      "finder.meta.desc": "חפש עסקים מקומיים, מטענים, בתי קפה, שירותים ועוד בכל פרבר אוסטרלי — 100% חינם.",
      "finder.footer.credits": "קרדיטים וייחוס למקורות נתונים",
      "finder.footer.terms": "תנאים",
      "finder.cat.toilet": "שירותים",
      "finder.cat.fountain": "מזרקות שתייה",
      "finder.cat.transport": "תחבורה",
      "finder.cat.park": "פארקים",
      "finder.cat.medical": "רפואי",
      "finder.cat.school": "בתי ספר",
      "finder.cat.library": "ספריות",
      "finder.cat.petrol": "דלק",
      "finder.cat.kidzone": "אזור ילדים",
      "finder.cat.puzzles": "חידות",
      "finder.cat.roadtrip": "טיול בדרכים",
      "finder.hotels.title": "מצא מלון בקרבת מקום",
      "finder.hotels.sub": "משווה 200+ אתרי הזמנות בחיפוש אחד — מופעל על ידי Trivago.",
      "finder.hotels.where": "איפה",
      "finder.hotels.where_ph": "עיר או פרבר (למשל Byron Bay, NSW)",
      "finder.hotels.checkin": "צ'ק-אין",
      "finder.hotels.checkout": "צ'ק-אאוט",
      "finder.hotels.adults": "מבוגרים",
      "finder.hotels.search": "חפש מלונות",
      "finder.hotels.disclosure": "פותח את Trivago — אנו עשויים להרוויח עמלה מהזמנות.",
      /* AUTO-TRANSLATED rt.* START */
      "rt.brand_name": "Roadtrip Planner",
      "rt.page_title": "מתכנן טיולי רכב חשמלי באוסטרליה",
      "rt.from": "מ-",
      "rt.to": "אל",
      "rt.from_placeholder": "פרוור, עיירה או עיר (לדוגמה: Newcastle NSW)",
      "rt.to_placeholder": "פרוור, עיירה או עיר (לדוגמה: Hobart TAS)",
      "rt.stop": "עצירה",
      "rt.stop_placeholder": "עצירת ביניים",
      "rt.add_stop": "הוספת עצירה",
      "rt.upgrade_pro": "שדרוג ל-Pro עבור עד 20 עצירות",
      "rt.reverse": "היפוך (חישוב הדרך הביתה)",
      "rt.from_current": "מהמיקום הנוכחי",
      "rt.range_per_charge": "טווח לטעינה (ק\"מ)",
      "rt.connector": "מחבר",
      "rt.connector_any": "הכל",
      "rt.drive_hours_day": "שעות נהיגה ליום",
      "rt.accommodation": "לינה",
      "rt.accom_any": "הכל (BnB · מלון · קרוואן)",
      "rt.accom_bnb": "BnB",
      "rt.accom_hotel": "מלון",
      "rt.accom_caravan": "חניון קרוואנים",
      "rt.min_rating": "דירוג מינימלי",
      "rt.any_rating": "כל דירוג",
      "rt.only_5star": "5★ בלבד",
      "rt.schedule": "לוח זמנים:",
      "rt.depart_at": "יציאה ב-",
      "rt.arrive_by": "הגעה עד",
      "rt.hotel_checkin": "צ'ק-אין במלון",
      "rt.hotel_checkout": "צ'ק-אאוט מהמלון",
      "rt.overnight_hint": "קטעי נסיעה ליליים לא יתוזמנו מחוץ לשעות אלו.",
      "rt.whos_in_car": "מי ברכב?",
      "rt.adults": "מבוגרים",
      "rt.kids": "ילדים",
      "rt.ages": "גילאים",
      "rt.show_attractions": "הצגת אטרקציות תיירותיות",
      "rt.show_kids": "הצגת עצירות ידידותיות לילדים",
      "rt.overnight_stays": "לינות",
      "rt.fast_chargers_only": "מטענים מהירים בלבד",
      "rt.hotel_must_charger": "חובה מטען במלון",
      "rt.pet_only": "לינות ידידותיות לחיות מחמד בלבד",
      "rt.safety": "בטיחות (יתרת סוללה)",
      "rt.safety_90": "90% (מישור)",
      "rt.safety_85": "85% (ברירת מחדל)",
      "rt.safety_80": "80% (עליות / גרירה)",
      "rt.safety_70": "70% (מטען כבד)",
      "rt.save_trip": "שמירת טיול",
      "rt.plan_my_trip": "תכנון הטיול שלי",
      "rt.refresh_trip": "רענון טיול",
      "rt.planning": "מתכנן…",
      "rt.resume_title": "המשך טיול מכאן",
      "rt.resume_body": "זיהינו את המיקום הנוכחי שלך. הזן את הטווח הנותר שמוצג בלוח המחוונים כדי שנתכנן את הקטע הבא לפי מה שיש לך בפועל — ולא לפי טעינה מלאה.",
      "rt.remaining_range": "טווח נותר (ק\"מ)",
      "rt.cancel": "ביטול",
      "rt.replan_from_here": "תכנון מחדש מכאן",
      "rt.save_modal_title": "מתן שם לטיול",
      "rt.save_modal_body": "טיולים שמורים נמצאים בפרופיל שלך — ניתן לפתוח אותם בכל עת וללחוץ על 'תכנון מחדש'.",
      "rt.save_name_placeholder": "לדוגמה: Brisbane ← Sydney דרך Byron",
      "rt.your_saved_trips": "הטיולים השמורים שלך",
      "rt.custom_location": "מיקום מותאם אישית",
      "rt.trip_summary": "סיכום טיול",
      "rt.itinerary": "מסלול טיול",
      "rt.no_stops_needed": "אין צורך בעצירות — כל המסלול נכנס בטווח הטעינה.",
      "rt.chargers_in_corridor": "מטענים לאורך המסלול",
      "rt.planned_stops": "עצירות מתוכננות",
      "rt.overnight_stop": "עצירת לילה",
      "rt.overnight_stops": "עצירות לילה",
      "rt.suggested_detours": "סטיות מומלצות מהמסלול — סמן כדי להוסיף",
      "rt.replan_with_detours": "תכנון מחדש עם סטיות",
      "rt.attractions_label": "אטרקציה",
      "rt.kids_label": "ילדים",
      "rt.km_from_start": "ק\"מ מנקודת ההתחלה",
      "rt.open_driving": "פתיחת מצב נהיגה",
      "rt.big_font_hint": "גופן גדול, מותאם למעמד לרכב. עובד עם שיקוף דפדפן ב-CarPlay/Android Auto.",
      "rt.check_price_book": "בדיקת מחיר והזמנה ב-Trivago ↗",
      "rt.visit_site": "ביקור באתר ↗",
      "rt.charger_onsite": "מטען במקום",
      "rt.leg_exceeds_range": "קטע זה חורג מהטווח שהוגדר — הסוללה תיגמר לפני ההגעה למטען זה. הקטן את טווח הנסיעה המשוער או הוסף עצירת טעינה נוספת.",
      "rt.add_waypoint_replan": "הוספה כנקודת ציון ותכנון מחדש",
      /* AUTO-TRANSLATED rt.* END */
                      /* AUTO-TRANSLATED hm.* START */
      "hm.nav.hustlemeet": "HustleMeet",
      "hm.nav.circles": "Circles",
      "hm.nav.matchmaker": "Matchmaker",
      "hm.nav.meet": "נפגשים",
      "hm.nav.overview": "HustleMeetups",
      "hm.overview.page_title": "HustleMeetups — שלוש דרכים חמות למצוא את האנשים שלך.",
      "hm.overview.kicker": "חלק מ-HustleSuite",
      "hm.overview.hero_title": "שלוש דרכים חמות למצוא את האנשים שלך.",
      "hm.overview.hero_title_accent": "האנשים שלך",
      "hm.overview.hero_sub": "HustleMeetups היא הדרך הכי לבבית ב-Australia לבנות חברויות אמיתיות — פשוט בחרו את האווירה שמתאימה לכם. בין אם אתם מחפשים קבוצת ריצה שבועית, ארוחת ערב חודשית עם החברים הקבועים, או שולחן מותאם AI של פנים חדשות, יש מקום לכולם.",
      "hm.overview.tile_meet_kicker": "HustleMeet",
      "hm.overview.tile_meet_title": "למצוא את האנשים שלך. לעשות את מה שאוהבים.",
      "hm.overview.tile_meet_body": "הבית של Australia לקבוצות מבוססות תחומי עניין. רצים, מתכנתים, משקיעים, הולכים, רקדנים, מועדוני ארוחת בוקר, חבורות ספרים — מאות קבוצות, אירועים אמיתיים מדי שבוע. הגיעו פעם אחת, או בכל שבוע.",
      "hm.overview.tile_meet_cta": "לצפייה בקבוצות",
      "hm.overview.tile_circles_kicker": "Circles",
      "hm.overview.tile_circles_title": "החבורה שלכם. שולחן חדש בכל פעם.",
      "hm.overview.tile_circles_body": "תביאו את החברים הקבועים שלכם. אנחנו נזמין מסעדה חדשה, נתכנן את הטריוויה, ונחלק את הקלפים. אתם רק צריכים להגיע. הדרך הכי מגבשת לשמור על הקשר עם החבורה הקיימת — בלי שאף אחד יצטרך להתעסק בארגונים.",
      "hm.overview.tile_circles_cta": "התחילו Circle",
      "hm.overview.tile_matchmaker_kicker": "Matchmaker",
      "hm.overview.tile_matchmaker_title": "ה-AI בוחר את השולחן. אתם רק מגיעים.",
      "hm.overview.tile_matchmaker_body": "ענו על שאלון של 10 דקות. תוך לילה, נושיב אתכם עם זרים שירגישו כמו חברים ותיקים כבר בשלב הקינוח. שולחן שונה בכל פעם.",
      "hm.overview.tile_matchmaker_cta": "התחילו את השאלון",
      "hm.overview.trust_kicker": "למה זה עובד",
      "hm.overview.trust_title": "קודם אמון. אחר כך שולחן.",
      "hm.overview.trust_1_title": "חברים מאומתים",
      "hm.overview.trust_1_body": "כל קבוצה מתחילה עם אימות זהות אמיתי + מדד אמינות. אין חשבונות פיקטיביים.",
      "hm.overview.trust_2_title": "מארחים אמיתיים",
      "hm.overview.trust_2_body": "כל קבוצה מובלת על ידי מארח שנבדק בקפידה, האחראי על האווירה, קובע את הקצב ודואג לכל מי שבחדר.",
      "hm.overview.trust_3_title": "חברות לפני הכל",
      "hm.overview.trust_3_body": "כל דבר כאן נועד לבניית חברויות אמיתיות, לא לדייטינג. אף פעם.",
      "hm.overview.trust_4_title": "מדיניות אפס סובלנות",
      "hm.overview.trust_4_body": "דיווח אחד, בדיקה אחת, החלטה אחת. גורמים שליליים מורחקים עוד באותו הלילה.",
      "hm.overview.footer_line": "חלק מ-HustleSuite — בבעלות ובאירוח ב-Australia.",
      "hm.landing.page_title": "HustleMeet · למצוא את האנשים שלך. לעשות את מה שאוהבים.",
      "hm.landing.kicker": "רשת תחומי העניין של Australia",
      "hm.landing.hero_title": "למצוא את האנשים שלך. לעשות את מה שאוהבים.",
      "hm.landing.hero_title_accent": "האנשים שלך",
      "hm.landing.hero_sub": "HustleMeet היא הבית של Australia לקבוצות מבוססות תחומי עניין. מפתחי תוכנה שעושים דיבאגינג בארוחת צהריים. רצים למרחקים ארוכים ב-6 בבוקר. משקיעים שנפגשים מדי חודש. קבוצות הליכה, חבורות ריקוד, מועדוני ארוחת בוקר, ערבי משחקי קופסה. בחרו קבוצה, שריינו את האירוע הבא, ופשוט תגיעו.",
      "hm.landing.cta_explore_circles": "דפדוף בקבוצות",
      "hm.landing.cta_try_matchmaker": "התחילו קבוצה משלכם",
      "hm.landing.trust_verified": "חברים מאומתים",
      "hm.landing.trust_venues": "מארחי אירועים שנבדקו",
      "hm.landing.trust_friendship": "חברות לפני הכל — אף פעם לא דייטינג",
      "hm.landing.trust_moderation": "מדיניות אפס סובלנות",
      "hm.landing.subbrand_circles_kicker": "Circles",
      "hm.landing.subbrand_circles_title": "כבר יש לכם חבורה? תנו להם בית קבוע.",
      "hm.landing.subbrand_circles_body": "Circles מיועד לקבוצת חברים שכבר נפגשת — מועדון ספרים, ערב פוקר, קבוצת ריצה או חברים מהעבודה. אנחנו דואגים למקום, לתזכורות ולרשימת המשתתפים. אתם רק צריכים להגיע.",
      "hm.landing.subbrand_circles_b1": "מקומות מתחלפים, רשימת משתתפים אחת",
      "hm.landing.subbrand_circles_b2": "תזכורות אוטומטיות + אישור הגעה קל",
      "hm.landing.subbrand_circles_b3": "ערבי טריוויה, קלפים וערבים נושאיים זמינים תמיד",
      "hm.landing.subbrand_circles_cta": "התחילו Circle",
      "hm.landing.subbrand_matchmaker_kicker": "Matchmaker",
      "hm.landing.subbrand_matchmaker_title": "ה-AI בוחר את השולחן. אתם רק מגיעים.",
      "hm.landing.subbrand_matchmaker_body": "שאלון אחד של 10 דקות — אישיות, ערכים, זמינות. ה-Matchmaker שלנו מושיב אתכם עם זרים שירגישו כמו חברים ותיקים עד הקינוח. שולחן שונה בכל פעם.",
      "hm.landing.subbrand_matchmaker_b1": "דירוג אישיות וערכים לפי Big-5",
      "hm.landing.subbrand_matchmaker_b2": "הזמנות תוך לילה כשההתאמה היא 0.70 ומעלה",
      "hm.landing.subbrand_matchmaker_b3": "לומד מכל ארוחה שאתם מקיימים",
      "hm.landing.subbrand_matchmaker_cta": "התחילו את השאלון",
      "hm.landing.how_kicker": "איך HustleMeet עובד",
      "hm.landing.how_title": "ארבעה שלבים. מצאו את הקבוצה שלכם. תגיעו.",
      "hm.landing.how_step1_ttl": "בחרו את תחומי העניין שלכם",
      "hm.landing.how_step1_sub": "ריצה, תכנות, השקעות, בישול, מוזיקה, הליכה — ספרו לנו מה אתם אוהבים ואיפה אתם גרים.",
      "hm.landing.how_step2_ttl": "דפדפו בקבוצות קרובות אליכם",
      "hm.landing.how_step2_sub": "כל קבוצה מפרסמת את המארח שלה, את הקצב שלה ואת האירוע הבא שלה. תרגישו את האווירה לפני שאתם מתחייבים.",
      "hm.landing.how_step3_ttl": "אשרו הגעה לאירוע הבא",
      "hm.landing.how_step3_sub": "לחיצה אחת, הזמנה אחת ליומן, ותזכורת ברורה יום לפני. חלק מהאירועים בחינם, חלק בתשלום — המחיר תמיד גלוי.",
      "hm.landing.how_step4_ttl": "תגיעו ותתחברו",
      "hm.landing.how_step4_sub": "תפגשו את המארח, את החבורה, ותעשו את מה שאוהבים. תחזרו בשבוע הבא אם זה הקליק.",
      "hm.landing.stats_kicker": "הדרך החדשה של Australia להיפגש",
      "hm.landing.stats_title": "הסיבה שאנשים ממשיכים לחזור.",
      "hm.landing.stats_92_sub": "מרגישים שהיה להם קליק עם לפחות אדם אחד באירוע",
      "hm.landing.stats_68_sub": "מאשרים הגעה לאירוע הבא שלהם תוך שבוע",
      "hm.landing.stats_85_sub": "נשארים בקשר עם מישהו מהקבוצה",
      "hm.landing.why_kicker": "למה זה עובד לנו",
      "hm.landing.why_title": "עניין אמיתי. מארחים אמיתיים. אירועים אמיתיים.",
      "hm.landing.why_1_ttl": "קודם עניין, אחר כך מיקום",
      "hm.landing.why_1_body": "לאנשים בקבוצה שלכם באמת אכפת מהדברים שמעניינים אתכם — זה הקיצור לבניית חברות אמיתית.",
      "hm.landing.why_2_ttl": "מארחי אירועים אמיתיים",
      "hm.landing.why_2_body": "לכל קבוצה יש מארח שנבדק, שאחראי על האווירה, קובע את הקצב ומתערב אם משהו לא תקין. לא מנהל קבוצה ב-Facebook. אדם אמיתי.",
      "hm.landing.why_3_ttl": "עקביות היא המפתח",
      "hm.landing.why_3_body": "אירועים חדשים מדי שבוע. משהו לצפות לו. קצב שהופך זרים לחבורה הקבועה שלכם בימי שלישי בערב.",
      "hm.landing.why_4_ttl": "בטוח ומוגן",
      "hm.landing.why_4_body": "חברים מאומתים, מקומות ציבוריים, אפס סובלנות לחוסר כבוד. צוות התמיכה שלנו זמין לפני, במהלך ואחרי כל אירוע.",
      "hm.landing.why_5_ttl": "חברות לפני הכל",
      "hm.landing.why_5_body": "זה לא דייטינג. זו קהילה. תחומי עניין אמיתיים, אנשים אמיתיים, בלי סוואיפים באמצע.",
      "hm.landing.why_6_ttl": "קטן מספיק כדי להיות משמעותי",
      "hm.landing.why_6_body": "רוב הקבוצות מגבילות את האירועים ל-4 עד 15 משתתפים. גדול מספיק לשיחות מעניינות. קטן מספיק כדי שאף אחד לא ילך לאיבוד.",
      "hm.landing.cities_kicker": "איפה אנחנו פועלים",
      "hm.landing.cities_title": "קבוצות נפגשות ברחבי Australia ו-NZ.",
      "hm.landing.cities_sub_zero": "קבוצות ראשונות נפתחות ביותר מ-15 ערים — הצטרפו מוקדם ועצבו את האווירה.",
      "hm.landing.cities_sub_some": "קבוצות פעילות ביותר מ-15 מטרופולינים. משהו קורה בעיר שלכם כבר השבוע.",
      "hm.landing.cities_sub_one": "קבוצה פעילה {{count}} · אירועים חדשים מדי שבוע · ערים נוספות מתווספות כל הזמן.",
      "hm.landing.cities_sub_many": "{{count}} קבוצות פעילות · אירועים חדשים מדי שבוע · ערים נוספות מתווספות כל הזמן.",
      "hm.landing.forme_kicker": "זה בשבילך אם...",
      "hm.landing.forme_title": "אתם רוצים עניין אמיתי. אנשים אמיתיים. אירועים אמיתיים.",
      "hm.landing.forme_1": "נמאס לכם מהעייפות של אפליקציות ה\"סוואיפ\" ואתם רוצים באמת לפגוש אנשים סביב מה שאתם אוהבים לעשות.",
      "hm.landing.forme_2": "עברתם לעיר חדשה ואתם זקוקים לחבורה חמה, לא לעוד קבוצת צ'אט.",
      "hm.landing.forme_3": "אתם מעריכים עומק על פני שידור המוני — קבוצה אחת טובה שווה יותר מ-50 עוקבים.",
      "hm.landing.forme_4": "אתם מאמינים שתחומי עניין משותפים + שעתיים קבועות בחיים האמיתיים הם הקיצור האמיתי להצלחה.",
      "hm.landing.notme_kicker": "זה לא בשבילך אם...",
      "hm.landing.notme_title": "HustleMeet לא מתאים לכולם.",
      "hm.landing.notme_1": "אתם לא יכולים להתחייב להגיע — המארחים והחברים בונים על אישור ההגעה שלכם.",
      "hm.landing.notme_2": "אתם מחפשים שירות דייטינג — חברויות באות כאן במקום הראשון.",
      "hm.landing.notme_3": "אתם רוצים לשדר מבלי לפגוש אף אחד — זה עובד רק פנים אל פנים.",
      "hm.landing.notme_4": "אתם מצפים להטבות חינם אינסופיות — חלק מהאירועים כרוכים בתשלום. המחיר תמיד ידוע מראש.",
      "hm.landing.safety_kicker": "המחויבות שלנו לבטיחות",
      "hm.landing.safety_title": "לבבי, אבל לעולם לא רשלני.",
      "hm.landing.safety_1_ttl": "מקומות ציבוריים",
      "hm.landing.safety_1_body": "כל אירוע מתקיים במקום ציבורי עם צוות עובדים — בית קפה, מסעדה, פארק, חדר כושר או סטודיו. לעולם לא בבית פרטי אלא אם כל המשתתפים אישרו זאת במפורש.",
      "hm.landing.safety_2_ttl": "סטנדרטים קהילתיים",
      "hm.landing.safety_2_body": "אפס סובלנות לחוסר כבוד, הטרדה או חוסר יושר. חברים שמפרים את הכללים מורחקים מיידית.",
      "hm.landing.safety_3_ttl": "הגנה על פרטיות",
      "hm.landing.safety_3_body": "הפרטים שלכם לעולם לא משותפים בפומבי. אתם שולטים במי שאתם נשארים איתו בקשר אחרי כל אירוע.",
      "hm.landing.safety_4_ttl": "תמיכה זמינה",
      "hm.landing.safety_4_body": "הצוות שלנו זמין לפני, במהלך ואחרי כל אירוע אם יש לכם שאלות או חששות.",
      "hm.landing.bottom_title": "הקבוצה האהובה הבאה שלכם נמצאת במרחק נגיעה.",
      "hm.landing.bottom_sub": "בחרו את הדרך שלכם — הצטרפו לקבוצה קיימת, הביאו את החבורה שלכם ל-Circle, או תנו ל-Matchmaker להושיב אתכם בשולחן שכבר מתאים לכם.",
      "hm.landing.bottom_cta_circles": "התחילו Circle",
      "hm.landing.bottom_cta_mm": "התחילו את שאלון ה-Matchmaker",
      "hm.circles.page_title": "HustleMeet Circles · החבורה שלכם, מקום חדש בכל פעם",
      "hm.circles.kicker": "HustleMeet · Circles",
      "hm.circles.hero_title": "החבורה שלכם. שולחן חדש בכל פעם.",
      "hm.circles.hero_title_accent": "החבורה שלכם",
      "hm.circles.hero_sub": "כבר יש לכם חברים קבועים? Circles היא הדרך הקלה ביותר לשמור עליהם קרובים. אנחנו נזמין את המקום, נתכנן את הטריוויה, ונחלק את הקלפים. אתם רק מגיעים — אותה חבורה, ערב חדש, מקום חדש.",
      "hm.circles.cta_find": "התחילו Circle",
      "hm.circles.cta_host_hero": "הצטרפו ל-Circle פתוח",
      "hm.circles.trust_venue": "מקומות נבחרים ומתחלפים",
      "hm.circles.trust_party": "4-12 חברים לכל Circle",
      "hm.circles.trust_verified": "חברים מאומתים",
      "hm.circles.trust_friendship": "חברויות אמיתיות, נשארות קרובות",
      "hm.circles.how_kicker": "איך זה עובד",
      "hm.circles.how_title": "ארבעה שלבים. החבורה שלכם נשארת חבורה.",
      "hm.circles.how_step1_ttl": "התחילו את ה-Circle שלכם",
      "hm.circles.how_step1_sub": "תנו לו שם, הזמינו את החברים (עד 12), ובחרו קצב — שבועי, דו-שבועי או חודשי.",
      "hm.circles.how_step2_ttl": "אנחנו בוחרים את המקום",
      "hm.circles.how_step2_sub": "בכל אירוע אנחנו מציעים מקום חדש — מסעדה, ערב טריוויה, חדר קלפים, בר יין. או שתנעלו מקום אהוב.",
      "hm.circles.how_step3_ttl": "כולם מאשרים הגעה בלחיצה אחת",
      "hm.circles.how_step3_sub": "תזכורות אוטומטיות, פיצול עלויות, מקומות בתשלום מראש כדי שביטולים לא יהרסו את התוכנית. החשבון סגור עוד לפני שהמנה הראשונה מגיעה.",
      "hm.circles.how_step4_ttl": "שומרים על הקצב",
      "hm.circles.how_step4_sub": "ערב אחד ביומן שאף פעם לא צריך לארגן מחדש. הקבוצה מתגבשת לאורך חודשים, לא דקות.",
      "hm.circles.cadence_weekly_ttl": "שבועי",
      "hm.circles.cadence_weekly_sub": "בכל שבוע",
      "hm.circles.cadence_weekly_body": "לחבורות שרוצות ריטואל בימי שלישי בערב — אותם פנים, מקום חדש בכל פעם.",
      "hm.circles.cadence_fortnight_ttl": "דו-שבועי",
      "hm.circles.cadence_fortnight_sub": "כל שבועיים",
      "hm.circles.cadence_fortnight_body": "האיזון המושלם — קבוע מבלי להכביד על לוחות זמנים עמוסים.",
      "hm.circles.cadence_monthly_ttl": "חודשי",
      "hm.circles.cadence_monthly_sub": "בכל חודש",
      "hm.circles.cadence_monthly_body": "Circles לאירועים מיוחדים — ארוחת צהריים של יום ראשון, ערבי טריוויה גדולים, ארוחת טעימות, פוקר.",
      "hm.circles.feast_kicker": "בחרו את הערב שלכם",
      "hm.circles.feast_title": "שלוש דרכים לבלות",
      "hm.circles.feast_sub": "כל Circle בוחר איך הערב יתנהל. החליפו ביניהם, או היצמדו למועדף על החבורה שלכם.",
      "hm.circles.feast_surprise_ttl": "נסו מסעדה חדשה",
      "hm.circles.feast_surprise_body": "אנחנו מכינים רשימה של שלושה מקומות קרובים אליכם, כולם מצביעים, והמטבח נערך לשולחן מלא. פיצול עלויות, מקומות בתשלום מראש.",
      "hm.circles.feast_preselect_ttl": "טריוויה / קלפים / משחקים",
      "hm.circles.feast_preselect_body": "הזמינו ערב טריוויה, חדר פוקר, בר משחקי קופסה או באולינג. אנחנו דואגים למקום, לדמי הכניסה ולתזכורות.",
      "hm.circles.feast_per_event_ttl": "ערב נושאי חד-פעמי",
      "hm.circles.feast_per_event_body": "טעימות יין, שולחן שף, סדנת קוקטיילים, חדר בריחה. משהו מיוחד מדי פעם — מאורגן לחלוטין.",
      "hm.circles.live_kicker": "עכשיו",
      "hm.circles.live_title": "Circles שאפשר להצטרף אליהם",
      "hm.circles.live_host_link": "התחילו אחד משלכם",
      "hm.circles.live_loading": "טוען…",
      "hm.circles.live_empty_title": "היו ה-Circle הראשון בעיר שלכם.",
      "hm.circles.live_empty_sub": "התחילו אחד עם שלושה חברים ואנחנו נעזור לכם להזמין את השאר — ובנוסף ניתן לכם את שלושת המקומות הראשונים עלינו.",
      "hm.circles.live_empty_cta": "התחילו Circle",
      "hm.circles.host_kicker": "למובילי Circle",
      "hm.circles.host_title": "אתם מביאים את החבורה. אנחנו עושים את הניהול.",
      "hm.circles.host_body": "אם אתם החברים שתמיד מארגנים את הערב, Circles יחסוך לכם שעות. תזכורות אוטומטיות, אישורי הגעה בתשלום מראש, רשימות מקומות נבחרים, פיצול עלויות, ניהול רשימת המתנה וארנק קאשבק על כל יציאה. אתם פשוט מגיעים כחברים רגילים.",
      "hm.circles.host_perk_1": "רשימות מקומות אוטומטיות — לעולם לא תצטרכו לחפש ב-Google שוב",
      "hm.circles.host_perk_2": "מקומות בתשלום מראש — אפס הברזות",
      "hm.circles.host_perk_3": "פיצול העלויות נעשה עוד לפני הקינוח",
      "hm.circles.host_perk_4": "תזכורות + הזמנות ליומן על אוטומט",
      "hm.circles.host_perk_5": "רשימת המתנה עם קידום אוטומטי אם חבר מבטל",
      "hm.circles.host_perk_6": "ארנק קאשבק בכל יציאה בערב",
      "hm.circles.host_cta": "התחילו Circle עכשיו",
      "hm.circles.why_kicker": "למה Circles עובד",
      "hm.circles.why_title": "חברויות של מבוגרים צריכות תשתית",
      "hm.circles.why_1_ttl": "אותה חבורה, אמון עמוק יותר",
      "hm.circles.why_1_body": "החיים מרחיקים חברים. Circles הם הקצב הקבוע ששומר על כולם קרובים — אותם 4-12 חברים, נפגשים באותו ערב בשבוע, לא משנה מה.",
      "hm.circles.why_2_ttl": "אפס עייפות ניהולית",
      "hm.circles.why_2_body": "אף אחד לא צריך להיות \"ראש קבוצת ה-WhatsApp\". אנחנו בוחרים את המקומות, שולחים את התזכורות, גובים את התשלומים וסוגרים את כל הקצוות.",
      "hm.circles.why_3_ttl": "מקום חדש, זיכרון חדש",
      "hm.circles.why_3_body": "תתחלפו בין המסעדות הקטנות והטובות ביותר ב-Australia, חדרי טריוויה ובארים של משחקים. פנים מוכרות, רקע רענן — השילוב המושלם בין שגרה לחידוש.",
      "hm.circles.bottom_title": "הערב שהחבורה שלכם מחכה לו, בלי שאף אחד יצטרך לתכנן אותו.",
      "hm.circles.bottom_sub": "Circle אחד. אותם חברים. מקום חדש בכל פעם.",
      "hm.circles.bottom_cta_find": "התחילו Circle",
      "hm.circles.bottom_cta_mm": "הכירו אנשים חדשים עם Matchmaker →",
      "hm.circles.card_host_pill": "Circle",
      "hm.matchmaker.page_title": "HustleMeet Matchmaker · ה-AI בוחר את השולחן המתאים",
      "hm.matchmaker.kicker": "HustleMeet · Matchmaker",
      "hm.matchmaker.hero_title": "ה-AI בוחר את השולחן. אתם רק מגיעים.",
      "hm.matchmaker.hero_title_accent": "אתם רק מגיעים.",
      "hm.matchmaker.hero_sub": "ענו על שאלון קצר על אישיות וערכים. ה-Matchmaker שלנו יושיב אתכם עם ארבעה עד שבעה זרים שירגישו כמו חברים ותיקים כבר בשלב הקינוח. בלי סוואיפים. בלי סמול טוק. רק שולחן משותף, מוכן כשאתם מוכנים.",
      "hm.matchmaker.cta_quiz": "התחילו את שאלון ההתאמה",
      "hm.matchmaker.cta_see_circles": "ראו Circles במקום ←",
      "hm.matchmaker.trust_big5": "דירוג אישיות לפי Big-5",
      "hm.matchmaker.trust_seats": "4-8 מושבים לכל שולחן",
      "hm.matchmaker.trust_lock": "בלי סוואיפים · בלי תמונות",
      "hm.matchmaker.trust_verified": "חברים מאומתים בלבד",
      "hm.matchmaker.how_kicker": "איך זה עובד",
      "hm.matchmaker.how_title": "ארבעה שלבים לשולחן שכבר מתאים לכם.",
      "hm.matchmaker.how_step1_ttl": "התחילו את השאלון",
      "hm.matchmaker.how_step1_sub": "שאלון של עשר דקות — אישיות, ערכים, העדפות קולינריות, זמינות. עושים את זה פעם אחת.",
      "hm.matchmaker.how_step2_ttl": "התאמה תוך לילה",
      "hm.matchmaker.how_step2_sub": "ה-AI שלנו קורא את הפרופיל שלכם ואת רשימת ארוחות הערב הפעילות ב-Australia. כשציון ההתאמה שלכם הוא 0.70 ומעלה, הוא מנסח הזמנה אישית.",
      "hm.matchmaker.how_step3_ttl": "אשרו או דחו",
      "hm.matchmaker.how_step3_sub": "תקבלו אימייל הזמנה ברור עם המקום, נושא השולחן ושלושת תחומי העניין שאתם חולקים עם הקבוצה.",
      "hm.matchmaker.how_step4_ttl": "זה לומד מכם",
      "hm.matchmaker.how_step4_sub": "כל סקר שאחרי הארוחה מכייל את המודל. ההתאמה הבאה שלכם תהיה חכמה יותר ככל שתסעדו יותר.",
      "hm.matchmaker.science_kicker": "למה זה עובד",
      "hm.matchmaker.science_title": "ה-Matchmaker לא מנחש — הוא מדרג.",
      "hm.matchmaker.science_sub": "כל התאמה היא שקלול של ארבעה גורמים. כשהממוצע של כל השולחן הוא מעל 0.70, אנחנו שולחים את ההזמנות. כל מה שמתחת נשאר בתור.",
      "hm.matchmaker.science_1_ttl": "אישיות (35%)",
      "hm.matchmaker.science_1_body": "דמיון קוסינוס לאורך וקטור האישיות של ה-Big-5 — פתיחות, מצפוניות, מוחצנות, נעימות ויציבות רגשית.",
      "hm.matchmaker.science_2_ttl": "ערכים (25%)",
      "hm.matchmaker.science_2_body": "חפיפה בערכים שגורמים לשיחת ארוחת ערב באמת לעבוד — משפחה, סקרנות, צמיחה, נתינה, הרפתקנות.",
      "hm.matchmaker.science_3_ttl": "תחומי עניין (25%)",
      "hm.matchmaker.science_3_body": "חפיפת Jaccard בתחומי העניין הספציפיים שלכם — טיולים ב-Six Foot Track, מועדון קולנוע ב-Sydney, סצנת הקפה של Melbourne, הספר השלישי שלכם השנה.",
      "hm.matchmaker.science_4_ttl": "זמינות (15%)",
      "hm.matchmaker.science_4_body": "הלילה שבו אתם באמת יכולים להגיע. לא תקבלו הזמנה ליום שלישי ב-19:00 אם היומן שלכם אומר \"חדר כושר-בכל-שלישי\".",
      "hm.matchmaker.safety_kicker": "בטיחות מובנית",
      "hm.matchmaker.safety_title": "ה-Matchmaker זהיר מטבעו.",
      "hm.matchmaker.safety_1_ttl": "סף ביטחון",
      "hm.matchmaker.safety_1_body": "מתחת לציון קבוצתי של 0.70, לא נשלחת הזמנה. התאמה רעועה עדיפה על חוסר ארוחה — עד שהיא כבר לא.",
      "hm.matchmaker.safety_2_ttl": "חסימת \"לא\" הדדית",
      "hm.matchmaker.safety_2_body": "אם שני חברים מסמנים בפרטי \"לא\" אחרי ארוחה, לעולם לא נושיב אותם יחד שוב.",
      "hm.matchmaker.safety_3_ttl": "מנגנון עצירה אוטומטי",
      "hm.matchmaker.safety_3_body": "אם 20% או יותר מהסקרים האחרונים מדרגים שולחן ב-2/5 ומטה, ההזמנות מושהות ל-24 שעות בזמן שמפעיל אנושי בודק את המקרה.",
      "hm.matchmaker.safety_4_ttl": "סטנדרטים קהילתיים",
      "hm.matchmaker.safety_4_body": "כל חבר מאשר קוד אתי בשפה פשוטה. אנחנו מרחיקים אנשים שמפרים אותו — ללא ערעור.",
      "hm.matchmaker.safety_5_ttl": "מקומות ציבוריים בלבד",
      "hm.matchmaker.safety_5_body": "כל ארוחת Matchmaker מתקיימת במסעדה אמיתית עם צוות עובדים. לעולם לא בבית פרטי.",
      "hm.matchmaker.safety_6_ttl": "משוב לאחר אירוע",
      "hm.matchmaker.safety_6_body": "הסקר הפרטי שלכם מעצב התאמות עתידיות, ויכול להוביל לבדיקת מנהל של המקום או של משתתף אחר.",
      "hm.matchmaker.loop_kicker": "לולאת הלמידה",
      "hm.matchmaker.loop_title": "כל ארוחה הופכת את הארוחה הבאה שלכם לטובה יותר.",
      "hm.matchmaker.loop_body": "ה-Matchmaker אינו סטטי. אחרי כל אירוע אתם מגישים סקר פרטי קצר — איך הרגישה השיחה, עם מי הייתם סועדים שוב, והאם המקום סיפק את הסחורה. האותות האלה חוזרים למודל ומעצבים מחדש כל התאמה עתידית.",
      "hm.matchmaker.loop_1": "הצבעות עמיתים ל\"סעודה חוזרת\" מחזקות את הצמדים שלכם",
      "hm.matchmaker.loop_2": "הצבעות \"לא\" הדדיות מפרישות צמדים בשקט",
      "hm.matchmaker.loop_3": "דירוגי מקום ושיחה מכיילים את תמהיל המושבים",
      "hm.matchmaker.loop_4": "מדד אמינות מגן על חברים מפני מבריזנים סדרתיים",
      "hm.matchmaker.loop_5": "הטיית למידה מחזקת התאמות טובות מחודש לחודש",
      "hm.matchmaker.loop_6": "אתם נשארים בשליטה — אפשר לבטל או לאפס את הפרופיל בכל עת",
      "hm.matchmaker.vs_kicker": "מה נכון לי?",
      "hm.matchmaker.vs_title": "Matchmaker או Circles?",
      "hm.matchmaker.vs_mm_ttl": "בחרו בי אם אתם רוצים גיוון.",
      "hm.matchmaker.vs_mm_1": "שולחן, עיר ומטבח שונים בכל פעם",
      "hm.matchmaker.vs_mm_2": "קבוצה של 4-8 זרים תואמים שנבחרה על ידי AI",
      "hm.matchmaker.vs_mm_3": "קצב שבועי, ללא התחייבות של מארח",
      "hm.matchmaker.vs_mm_4": "מעולה אם אתם חדשים בעיר או מטיילים",
      "hm.matchmaker.vs_mm_cta": "התחילו את שאלון ההתאמה",
      "hm.matchmaker.vs_circles_ttl": "בחרו בי אם אתם רוצים משהו קבוע.",
      "hm.matchmaker.vs_circles_1": "אותו מארח · אותו מקום · אותו ערב בשבוע",
      "hm.matchmaker.vs_circles_2": "קבוצה שהופכת למסורת של ימי רביעי שלכם",
      "hm.matchmaker.vs_circles_3": "נבחרת על ידי מארח אנושי ומנוסה",
      "hm.matchmaker.vs_circles_4": "מעולה אם הרגע עברתם ואתם רוצים להכות שורשים",
      "hm.matchmaker.vs_circles_cta": "דפדפו ב-Circles",
      "hm.matchmaker.bottom_title": "שאלון אחד. כל ארוחה אחר כך כבר מתאימה.",
      "hm.matchmaker.bottom_sub": "תקבלו התאמה בתוך שבועיים. זה ה-\"כן\" הכי קל שתגידו החודש.",
      "hm.matchmaker.bottom_cta": "התחילו את שאלון ההתאמה",
      "hm.matchmaker.bottom_note": "ללא כרטיס אשראי · 10 דקות · ביטול התאמה בכל עת",
      /* AUTO-TRANSLATED hm.* END */
    },
  },
  fa: {
    translation: {
      /* AUTO-TRANSLATED finder.* (added) */
      "finder.title": "HustleFinder",
      "finder.tagline": "نزدیک‌ترین مکان عمومی را پیدا کنید — داده‌های واقعی، در سراسر استرالیا.",
      "finder.search_placeholder": "جستجوی مکان‌ها، خیابان‌ها یا حومه‌ها…",
      "finder.hint": "شروع به تایپ کنید، یک دسته را لمس کنید، یا از «نزدیک من» استفاده کنید.",
      "finder.near_me": "نزدیک من",
      "finder.searching": "در حال جستجو…",
      "finder.suburb.placeholder": "فیلتر بر اساس حومه (مثلاً Darwin, NT)…",
      "finder.suburb.clear": "پاک کردن",
      "finder.search_in": "جستجو در {{cat}}",
      "finder.empty": "نتیجه‌ای یافت نشد — دسته یا عبارت جستجوی دیگری امتحان کنید.",
      "finder.meta.title": "HustleFinder — راهنمای رایگان حومه‌های استرالیا",
      "finder.meta.desc": "جستجوی کسب‌وکارهای محلی، شارژرها، کافه‌ها، توالت‌ها و بیشتر در تمام حومه‌های استرالیا — ۱۰۰٪ رایگان.",
      "finder.footer.credits": "اعتبار داده‌ها و منابع",
      "finder.footer.terms": "شرایط",
      "finder.cat.toilet": "توالت‌ها",
      "finder.cat.fountain": "آب‌خوری‌ها",
      "finder.cat.transport": "حمل‌ونقل",
      "finder.cat.park": "پارک‌ها",
      "finder.cat.medical": "پزشکی",
      "finder.cat.school": "مدارس",
      "finder.cat.library": "کتابخانه‌ها",
      "finder.cat.petrol": "پمپ بنزین",
      "finder.cat.kidzone": "منطقه کودکان",
      "finder.cat.puzzles": "پازل‌ها",
      "finder.cat.roadtrip": "سفر جاده‌ای",
      "finder.hotels.title": "هتل نزدیک را پیدا کنید",
      "finder.hotels.sub": "بیش از ۲۰۰ سایت رزرو را در یک جستجو مقایسه می‌کند — با قدرت Trivago.",
      "finder.hotels.where": "کجا",
      "finder.hotels.where_ph": "شهر یا حومه (مثلاً Byron Bay, NSW)",
      "finder.hotels.checkin": "ورود",
      "finder.hotels.checkout": "خروج",
      "finder.hotels.adults": "بزرگسالان",
      "finder.hotels.search": "جستجوی هتل‌ها",
      "finder.hotels.disclosure": "Trivago باز می‌شود — ممکن است از رزروها کمیسیون دریافت کنیم.",
      /* AUTO-TRANSLATED rt.* START */
      "rt.brand_name": "Roadtrip Planner",
      "rt.page_title": "برنامه‌ریز سفر جاده‌ای استرالیا برای خودروهای برقی",
      "rt.from": "مبدأ",
      "rt.to": "مقصد",
      "rt.from_placeholder": "محله، شهرک یا شهر (مثلاً Newcastle NSW)",
      "rt.to_placeholder": "محله، شهرک یا شهر (مثلاً Hobart TAS)",
      "rt.stop": "STOP",
      "rt.stop_placeholder": "توقف میانی",
      "rt.add_stop": "افزودن توقف",
      "rt.upgrade_pro": "برای داشتن حداکثر ۲۰ توقف به نسخه Pro ارتقا دهید",
      "rt.reverse": "برعکس کردن (محاسبه مسیر بازگشت)",
      "rt.from_current": "از موقعیت فعلی",
      "rt.range_per_charge": "مسافت با هر شارژ (کیلومتر)",
      "rt.connector": "رابط شارژ",
      "rt.connector_any": "هر کدام",
      "rt.drive_hours_day": "ساعات رانندگی در روز",
      "rt.accommodation": "اقامتگاه",
      "rt.accom_any": "هر کدام (BnB · هتل · کاروان)",
      "rt.accom_bnb": "BnB",
      "rt.accom_hotel": "هتل",
      "rt.accom_caravan": "پارک کاروان",
      "rt.min_rating": "حداقل امتیاز",
      "rt.any_rating": "هر امتیازی",
      "rt.only_5star": "فقط ۵★",
      "rt.schedule": "زمان‌بندی:",
      "rt.depart_at": "حرکت در ساعت",
      "rt.arrive_by": "رسیدن تا ساعت",
      "rt.hotel_checkin": "پذیرش هتل",
      "rt.hotel_checkout": "تخلیه هتل",
      "rt.overnight_hint": "بخش‌های شبانه خارج از این ساعات برنامه‌ریزی نمی‌شوند.",
      "rt.whos_in_car": "چه کسانی در خودرو هستند؟",
      "rt.adults": "بزرگسالان",
      "rt.kids": "کودکان",
      "rt.ages": "سنین",
      "rt.show_attractions": "نمایش جاذبه‌های گردشگری",
      "rt.show_kids": "نمایش توقف‌های مناسب کودکان",
      "rt.overnight_stays": "اقامت‌های شبانه",
      "rt.fast_chargers_only": "فقط شارژرهای سریع",
      "rt.hotel_must_charger": "هتل باید دارای شارژر باشد",
      "rt.pet_only": "فقط اقامتگاه‌های مجاز برای حیوانات",
      "rt.safety": "ضریب اطمینان",
      "rt.safety_90": "۹۰٪ (مسیر کفی)",
      "rt.safety_85": "۸۵٪ (پیش‌فرض)",
      "rt.safety_80": "۸۰٪ (تپه / یدک‌کشی)",
      "rt.safety_70": "۷۰٪ (بار سنگین)",
      "rt.save_trip": "ذخیره سفر",
      "rt.plan_my_trip": "برنامه‌ریزی سفر من",
      "rt.refresh_trip": "به‌روزرسانی سفر",
      "rt.planning": "در حال برنامه‌ریزی…",
      "rt.resume_title": "ادامه سفر از اینجا",
      "rt.resume_body": "موقعیت فعلی شما ثبت شد. میزان مسافت باقی‌مانده روی داشبورد را وارد کنید تا بخش بعدی سفر بر اساس شارژ واقعی شما برنامه‌ریزی شود — نه شارژ کامل.",
      "rt.remaining_range": "مسافت باقی‌مانده (کیلومتر)",
      "rt.cancel": "لغو",
      "rt.replan_from_here": "برنامه‌ریزی مجدد از اینجا",
      "rt.save_modal_title": "نام‌گذاری این سفر",
      "rt.save_modal_body": "سفرهای ذخیره‌شده در پروفایل شما قرار می‌گیرند — می‌توانید هر زمان آن‌ها را باز کرده و «برنامه‌ریزی مجدد» را بزنید.",
      "rt.save_name_placeholder": "مثلاً Brisbane ← Sydney از طریق Byron",
      "rt.your_saved_trips": "سفرهای ذخیره‌شده شما",
      "rt.custom_location": "موقعیت دلخواه",
      "rt.trip_summary": "خلاصه سفر",
      "rt.itinerary": "برنامه سفر",
      "rt.no_stops_needed": "نیازی به توقف نیست — کل سفر با میزان شارژ شما قابل انجام است.",
      "rt.chargers_in_corridor": "شارژر در مسیر",
      "rt.planned_stops": "توقف برنامه‌ریزی‌شده",
      "rt.overnight_stop": "توقف شبانه",
      "rt.overnight_stops": "توقف شبانه",
      "rt.suggested_detours": "مسیرهای انحرافی پیشنهادی — برای افزودن علامت بزنید",
      "rt.replan_with_detours": "برنامه‌ریزی مجدد با مسیرهای انحرافی",
      "rt.attractions_label": "جاذبه",
      "rt.kids_label": "کودکان",
      "rt.km_from_start": "کیلومتر از مبدأ",
      "rt.open_driving": "باز کردن حالت رانندگی",
      "rt.big_font_hint": "فونت بزرگ، مناسب پایه نگهدارنده. سازگار با نمایشگر آینه‌ای مرورگر CarPlay/Android Auto.",
      "rt.check_price_book": "مشاهده قیمت و رزرو در Trivago ↗",
      "rt.visit_site": "مشاهده سایت ↗",
      "rt.charger_onsite": "دارای شارژر در محل",
      "rt.leg_exceeds_range": "این بخش از سفر از محدوده مسافت تعیین‌شده شما بیشتر است — قبل از رسیدن به این شارژر شارژ تمام خواهید کرد. مسافت فرضی را کاهش دهید یا یک توقف میانی برای شارژ اضافه کنید.",
      "rt.add_waypoint_replan": "افزودن به عنوان توقف بین‌راهی و برنامه‌ریزی مجدد",
      /* AUTO-TRANSLATED rt.* END */
                      /* AUTO-TRANSLATED hm.* START */
      "hm.nav.hustlemeet": "HustleMeet",
      "hm.nav.circles": "Circles",
      "hm.nav.matchmaker": "Matchmaker",
      "hm.nav.meet": "ملاقات",
      "hm.nav.overview": "HustleMeetups",
      "hm.overview.page_title": "HustleMeetups — سه راه صمیمی برای پیدا کردن جمع خودتان.",
      "hm.overview.kicker": "بخشی از HustleSuite",
      "hm.overview.hero_title": "سه راه صمیمی برای پیدا کردن جمع خودتان.",
      "hm.overview.hero_title_accent": "جمع خودتان",
      "hm.overview.hero_sub": "HustleMeetups مهربان‌ترین راه در Australia برای ساختن دوستی‌های واقعی است — فضایی را که به شما می‌خورد انتخاب کنید. چه یک گروه دویدن هفتگی بخواهید، چه شام ماهانه با جمع دوستان فعلی‌تان، یا میزی از چهره‌های جدید که توسط هوش مصنوعی هماهنگ شده‌اند، راهی برای ورود وجود دارد.",
      "hm.overview.tile_meet_kicker": "HustleMeet",
      "hm.overview.tile_meet_title": "جمع خود را پیدا کنید. کارهای موردعلاقه‌تان را انجام دهید.",
      "hm.overview.tile_meet_body": "خانه‌ی گروه‌های علاقه‌محور در Australia. دونده‌ها، برنامه‌نویس‌ها، سرمایه‌گذارها، پیاده‌روی، رقص، کلوب‌های صبحانه، جمع‌های کتاب‌خوانی — صدها گروه و رویداد واقعی در هر هفته. یک بار شرکت کنید، یا هر هفته بیایید.",
      "hm.overview.tile_meet_cta": "کاوش گروه‌ها",
      "hm.overview.tile_circles_kicker": "Circles",
      "hm.overview.tile_circles_title": "جمع شما. هر بار یک میز جدید.",
      "hm.overview.tile_circles_body": "گروه دوستان همیشگی خود را بیاورید. ما رستوران جدید رزرو می‌کنیم، مسابقات را برنامه‌ریزی می‌کنیم و بازی‌ها را می‌چینیم. شما فقط تشریف می‌آورید. صمیمی‌ترین راه برای نزدیک نگه داشتن دوستان فعلی — بدون اینکه کسی دغدغه‌ی هماهنگی داشته باشد.",
      "hm.overview.tile_circles_cta": "شروع یک Circle",
      "hm.overview.tile_matchmaker_kicker": "Matchmaker",
      "hm.overview.tile_matchmaker_title": "هوش مصنوعی میز را انتخاب می‌کند. شما تشریف می‌آورید.",
      "hm.overview.tile_matchmaker_body": "یک آزمون ۱۰ دقیقه‌ای بدهید. ما یک‌شبه شما را در کنار غریبه‌هایی می‌نشانیم که تا زمان دسر، مثل دوستان قدیمی به نظر می‌رسند. هر بار میزی متفاوت.",
      "hm.overview.tile_matchmaker_cta": "شرکت در آزمون",
      "hm.overview.trust_kicker": "چرا کار می‌کند",
      "hm.overview.trust_title": "اول اعتماد. بعداً میز.",
      "hm.overview.trust_1_title": "اعضای تایید شده",
      "hm.overview.trust_1_body": "هر گروه با بررسی هویت واقعی + امتیاز قابلیت اطمینان شروع می‌شود. حساب‌های کاربری یک‌بارمصرف نداریم.",
      "hm.overview.trust_2_title": "میزبان‌های واقعی",
      "hm.overview.trust_2_body": "هر گروه توسط یک میزبان تایید شده هدایت می‌شود که مسئولیت فضا را بر عهده دارد، ریتم را تعیین می‌کند و مراقب جمع است.",
      "hm.overview.trust_3_title": "اول دوستی",
      "hm.overview.trust_3_body": "همه چیز در اینجا برای دوستی‌های واقعی طراحی شده است، نه قرار ملاقات (dating). هیچ‌وقت.",
      "hm.overview.trust_4_title": "نظارت با تسامح صفر",
      "hm.overview.trust_4_body": "یک گزارش، یک بررسی، یک تصمیم. افراد نامناسب همان شب حذف می‌شوند.",
      "hm.overview.footer_line": "بخشی از HustleSuite — مالکیت و میزبانی در Australia.",
      "hm.landing.page_title": "HustleMeet · جمع خود را پیدا کنید. کارهای موردعلاقه‌تان را انجام دهید.",
      "hm.landing.kicker": "شبکه علاقه‌مندی‌های Australia",
      "hm.landing.hero_title": "جمع خود را پیدا کنید. کارهای موردعلاقه‌تان را انجام دهید.",
      "hm.landing.hero_title_accent": "جمع خودتان",
      "hm.landing.hero_sub": "HustleMeet خانه‌ی گروه‌های علاقه‌محور در Australia است. توسعه‌دهندگان نرم‌افزار که هنگام ناهار با هم رفع اشکال می‌کنند. دونده‌های مسیر طولانی در ساعت ۶ صبح. سرمایه‌گذارانی که ماهانه ملاقات می‌کنند. گروه‌های پیاده‌روی، اکیپ‌های رقص، کلوب‌های صبحانه، شب‌های بازی‌های رومیزی. یک گروه را انتخاب کنید، رویداد بعدی را رزرو کنید و حضور پیدا کنید.",
      "hm.landing.cta_explore_circles": "مشاهده گروه‌ها",
      "hm.landing.cta_try_matchmaker": "گروه خودتان را شروع کنید",
      "hm.landing.trust_verified": "اعضای تایید شده",
      "hm.landing.trust_venues": "میزبان‌های تایید شده‌ی رویداد",
      "hm.landing.trust_friendship": "اول دوستی — هرگز قرار ملاقات نیست",
      "hm.landing.trust_moderation": "نظارت با تسامح صفر",
      "hm.landing.subbrand_circles_kicker": "Circles",
      "hm.landing.subbrand_circles_title": "همین حالا اکیپ خودتان را دارید؟ یک خانه‌ی دائمی به آن‌ها بدهید.",
      "hm.landing.subbrand_circles_body": "Circles برای گروه‌های دوستی است که از قبل با هم ملاقات می‌کنند — کلوب کتاب، شب پوکر، اکیپ دویدن، دوستان کاری. ما هماهنگی محل، یادآوری‌ها و لیست افراد را مدیریت می‌کنیم. شما فقط تشریف می‌آورید.",
      "hm.landing.subbrand_circles_b1": "محل‌های گردشی، یک لیست ثابت",
      "hm.landing.subbrand_circles_b2": "یادآوری خودکار + اعلام حضور آسان",
      "hm.landing.subbrand_circles_b3": "مسابقات اطلاعات عمومی، بازی‌های ورقی و شب‌های تم‌دار آماده اجرا",
      "hm.landing.subbrand_circles_cta": "شروع یک Circle",
      "hm.landing.subbrand_matchmaker_kicker": "Matchmaker",
      "hm.landing.subbrand_matchmaker_title": "هوش مصنوعی میز را انتخاب می‌کند. شما تشریف می‌آورید.",
      "hm.landing.subbrand_matchmaker_body": "یک آزمون ۱۰ دقیقه‌ای — شخصیت، ارزش‌ها، زمان‌های آزاد. Matchmaker ما شما را در کنار غریبه‌هایی می‌نشاند که تا زمان دسر مثل دوستان قدیمی به نظر می‌رسند. هر بار میزی متفاوت.",
      "hm.landing.subbrand_matchmaker_b1": "سنجش شخصیت Big-5 و ارزش‌ها",
      "hm.landing.subbrand_matchmaker_b2": "دعوت‌نامه‌های یک‌شبه وقتی تناسب شما ۰.۷۰ یا بالاتر باشد",
      "hm.landing.subbrand_matchmaker_b3": "یادگیری از هر شامی که میل می‌کنید",
      "hm.landing.subbrand_matchmaker_cta": "شرکت در آزمون",
      "hm.landing.how_kicker": "HustleMeet چگونه کار می‌کند",
      "hm.landing.how_title": "چهار مرحله. گروه خود را پیدا کنید. حضور پیدا کنید.",
      "hm.landing.how_step1_ttl": "علاقه‌مندی‌های خود را انتخاب کنید",
      "hm.landing.how_step1_sub": "دویدن، برنامه‌نویسی، سرمایه‌گذاری، آشپزی، موسیقی، پیاده‌روی — به ما بگویید به چه چیزی علاقه دارید و کجا زندگی می‌کنید.",
      "hm.landing.how_step2_ttl": "گروه‌های نزدیک خود را بررسی کنید",
      "hm.landing.how_step2_sub": "هر گروه میزبان، ریتم و رویداد بعدی خود را منتشر می‌کند. قبل از تعهد، فضا را بسنجید.",
      "hm.landing.how_step3_ttl": "برای رویداد بعدی اعلام حضور کنید",
      "hm.landing.how_step3_sub": "یک ضربه، یک دعوت‌نامه تقویم، یک یادآوری دقیق روز قبل. برخی رویدادها رایگان و برخی پولی هستند — قیمت مشخص و شفاف است.",
      "hm.landing.how_step4_ttl": "حضور یابید و متصل شوید",
      "hm.landing.how_step4_sub": "با میزبان ملاقات کنید، با بقیه آشنا شوید، کار موردعلاقه را انجام دهید. اگر خوشتان آمد، هفته بعد دوباره بیایید.",
      "hm.landing.stats_kicker": "راه جدید Australia برای ملاقات",
      "hm.landing.stats_title": "دلیلی که مردم دوباره برمی‌گردند.",
      "hm.landing.stats_92_sub": "احساس کردند با حداقل یک نفر در رویداد صمیمی شده‌اند",
      "hm.landing.stats_68_sub": "ظرف یک هفته برای رویداد بعدی‌شان اعلام حضور می‌کنند",
      "hm.landing.stats_85_sub": "با کسی از گروه در ارتباط می‌مانند",
      "hm.landing.why_kicker": "چرا ما موفق هستیم",
      "hm.landing.why_title": "علاقه‌های واقعی. میزبان‌های واقعی. رویدادهای واقعی.",
      "hm.landing.why_1_ttl": "اول علاقه، نه مکان",
      "hm.landing.why_1_body": "افراد گروه شما واقعاً به همان چیزی اهمیت می‌دهند که شما می‌دهید — این راه میان‌بر برای یک دوستی واقعی است.",
      "hm.landing.why_2_ttl": "میزبان‌های واقعی رویداد",
      "hm.landing.why_2_body": "هر گروه یک میزبان تایید شده دارد که مسئول فضا است، ریتم را تعیین می‌کند و اگر چیزی درست نباشد مداخله می‌کند. نه یک ادمین Facebook، بلکه یک فرد واقعی.",
      "hm.landing.why_3_ttl": "تداوم مهم است",
      "hm.landing.why_3_body": "رویدادهای جدید در هر هفته. چیزی برای انتظار کشیدن. ریتمی که غریبه‌ها را به اکیپ شب‌های سه‌شنبه شما تبدیل می‌کند.",
      "hm.landing.why_4_ttl": "امن و مطمئن",
      "hm.landing.why_4_body": "اعضای تایید شده، مکان‌های عمومی، تسامح صفر برای بی‌احترامی. تیم پشتیبانی ما قبل، حین و بعد از هر رویداد آماده پاسخگویی است.",
      "hm.landing.why_5_ttl": "اول دوستی",
      "hm.landing.why_5_body": "اینجا قرار ملاقات (dating) نیست. این یک جامعه است. علاقه‌های واقعی، آدم‌های واقعی، بدون ورق زدن عکس‌ها در این بین.",
      "hm.landing.why_6_ttl": "به اندازه کافی کوچک برای اهمیت داشتن",
      "hm.landing.why_6_body": "بیشتر گروه‌ها ظرفیت رویداد را بین ۴ تا ۱۵ صندلی محدود می‌کنند. به اندازه کافی بزرگ برای جرقه زدن گفتگوهای واقعی و به اندازه کافی کوچک تا کسی نادیده گرفته نشود.",
      "hm.landing.cities_kicker": "کجا فعال هستیم",
      "hm.landing.cities_title": "ملاقات گروه‌ها در سراسر Australia و NZ.",
      "hm.landing.cities_sub_zero": "اولین گروه‌ها در بیش از ۱۵ شهر در حال افتتاح هستند — زودتر بپیوندید و فضا را شکل دهید.",
      "hm.landing.cities_sub_some": "گروه‌ها در بیش از ۱۵ کلان‌شهر فعال هستند. همین هفته چیزی در شهر شما هست.",
      "hm.landing.cities_sub_one": "{{count}} گروه فعال · رویدادهای جدید در هر هفته · همواره در حال اضافه کردن شهرهای جدید.",
      "hm.landing.cities_sub_many": "{{count}} گروه فعال · رویدادهای جدید در هر هفته · همواره در حال اضافه کردن شهرهای جدید.",
      "hm.landing.forme_kicker": "این برای شماست اگر…",
      "hm.landing.forme_title": "شما به دنبال علاقه‌های واقعی، آدم‌های واقعی و رویدادهای واقعی هستید.",
      "hm.landing.forme_1": "از خستگی اپلیکیشن‌های ورق‌زدنی (swipe) خسته شده‌اید و می‌خواهید واقعاً آدم‌ها را حول کارهایی که دوست دارید ملاقات کنید.",
      "hm.landing.forme_2": "به شهر جدیدی نقل مکان کرده‌اید و به یک اکیپ صمیمی نیاز دارید، نه یک گروه چت دیگر.",
      "hm.landing.forme_3": "ارزش عمق را بیشتر از انتشار عمومی می‌دانید — یک گروه عالی بهتر از پنجاه فالوور است.",
      "hm.landing.forme_4": "معتقدید علاقه‌مندی‌های مشترک + دو ساعت زمان مشخص در دنیای واقعی، رمز اصلی موفقیت است.",
      "hm.landing.notme_kicker": "این برای شما نیست اگر…",
      "hm.landing.notme_title": "HustleMeet برای همه مناسب نیست.",
      "hm.landing.notme_1": "نمی‌توانید به حضور پیدا کردن متعهد شوید — میزبان‌ها و اعضا روی اعلام حضور شما حساب می‌کنند.",
      "hm.landing.notme_2": "به دنبال خدمات همسریابی یا قرار ملاقات هستید — اینجا دوستی در اولویت اول است.",
      "hm.landing.notme_3": "می‌خواهید بدون ملاقات با کسی، فقط پیام منتشر کنید — این سیستم فقط به صورت حضوری کار می‌کند.",
      "hm.landing.notme_4": "انتظار مزایای رایگان بی‌پایان دارید — برخی رویدادها هزینه دارند. قیمت همیشه از قبل اعلام می‌شود.",
      "hm.landing.safety_kicker": "تعهد ایمنی ما",
      "hm.landing.safety_title": "صمیمی، اما هرگز بی‌دقت.",
      "hm.landing.safety_1_ttl": "مکان‌های عمومی",
      "hm.landing.safety_1_body": "هر رویداد در یک مکان عمومی و دارای پرسنل برگزار می‌شود — کافه، رستوران، پارک، باشگاه یا استودیو. هرگز در خانه کسی برگزار نمی‌شود مگر اینکه تمام شرکت‌کنندگان موافقت کنند.",
      "hm.landing.safety_2_ttl": "استانداردهای جامعه",
      "hm.landing.safety_2_body": "تسامح صفر برای بی‌احترامی، مزاحمت یا عدم صداقت. اعضایی که تخطی کنند بلافاصله حذف می‌شوند.",
      "hm.landing.safety_3_ttl": "محافظت از حریم خصوصی",
      "hm.landing.safety_3_body": "جزئیات شما هرگز به صورت عمومی به اشتراک گذاشته نمی‌شود. شما کنترل می‌کنید که بعد از هر رویداد با چه کسی در ارتباط بمانید.",
      "hm.landing.safety_4_ttl": "پشتیبانی آماده به خدمت",
      "hm.landing.safety_4_body": "تیم ما قبل، حین و بعد از هر رویداد در دسترس است تا به سوالات یا نگرانی‌های شما رسیدگی کند.",
      "hm.landing.bottom_title": "گروه محبوب بعدی شما فقط یک ضربه فاصله دارد.",
      "hm.landing.bottom_sub": "مسیر خود را انتخاب کنید — به یک گروه موجود بپیوندید، اکیپ خود را به یک Circle بیاورید، یا اجازه دهید Matchmaker شما را بر سر میزی بنشاند که از قبل با شما هماهنگ است.",
      "hm.landing.bottom_cta_circles": "شروع یک Circle",
      "hm.landing.bottom_cta_mm": "شرکت در آزمون Matchmaker",
      "hm.circles.page_title": "HustleMeet Circles · اکیپ شما، هر بار مکان جدید",
      "hm.circles.kicker": "HustleMeet · Circles",
      "hm.circles.hero_title": "جمع شما. هر بار یک میز جدید.",
      "hm.circles.hero_title_accent": "جمع شما",
      "hm.circles.hero_sub": "همین حالا دوستان خود را دارید؟ Circles راحت‌ترین راه برای نزدیک نگه داشتن آن‌هاست. ما محل را رزرو می‌کنیم، مسابقات را برنامه‌ریزی می‌کنیم و بازی‌ها را می‌چینیم. شما فقط می‌آیید — همان اکیپ، شب جدید، جای جدید.",
      "hm.circles.cta_find": "شروع یک Circle",
      "hm.circles.cta_host_hero": "پیوستن به یک Circle باز",
      "hm.circles.trust_venue": "مکان‌های تایید شده‌ی گردشی",
      "hm.circles.trust_party": "۴ تا ۱۲ دوست در هر Circle",
      "hm.circles.trust_verified": "اعضای تایید شده",
      "hm.circles.trust_friendship": "دوستی‌های واقعی، در کنار هم",
      "hm.circles.how_kicker": "چگونه کار می‌کند",
      "hm.circles.how_title": "چهار مرحله. اکیپ شما یک اکیپ باقی می‌ماند.",
      "hm.circles.how_step1_ttl": "Circle خود را شروع کنید",
      "hm.circles.how_step1_sub": "نامی برایش انتخاب کنید، دوستانتان را دعوت کنید (تا ۱۲ نفر) و یک ریتم انتخاب کنید — هفتگی، دوهفته‌یک‌بار یا ماهانه.",
      "hm.circles.how_step2_ttl": "ما محل را انتخاب می‌کنیم",
      "hm.circles.how_step2_sub": "برای هر رویداد یک نقطه جدید پیشنهاد می‌دهیم — رستوران، شب مسابقه، اتاق بازی ورقی، کافه. یا یک جای محبوب را ثابت کنید.",
      "hm.circles.how_step3_ttl": "همه با یک ضربه اعلام حضور می‌کنند",
      "hm.circles.how_step3_sub": "یادآوری خودکار، تقسیم هزینه، صندلی‌های پیش‌پرداخت شده تا غیبت‌های ناگهانی برنامه را خراب نکند. صورت‌حساب قبل از رسیدن پیش‌غذا تمام شده است.",
      "hm.circles.how_step4_ttl": "ریتم را حفظ کنید",
      "hm.circles.how_step4_sub": "یک شب در تقویم که دیگر هرگز نیاز به هماهنگی دستی ندارد. صمیمیت گروه در طول ماه‌ها بیشتر می‌شود، نه فقط دقایق.",
      "hm.circles.cadence_weekly_ttl": "هفتگی",
      "hm.circles.cadence_weekly_sub": "هر هفته",
      "hm.circles.cadence_weekly_body": "برای اکیپ‌هایی که یک آیین شب‌های سه‌شنبه می‌خواهند — چهره‌های تکراری، هر بار جای جدید.",
      "hm.circles.cadence_fortnight_ttl": "دوهفته‌یک‌بار",
      "hm.circles.cadence_fortnight_sub": "هر ۲ هفته",
      "hm.circles.cadence_fortnight_body": "حالت ایده‌آل — منظم بدون اینکه به برنامه‌های شلوغ فشار بیاورد.",
      "hm.circles.cadence_monthly_ttl": "ماهانه",
      "hm.circles.cadence_monthly_sub": "هر ماه",
      "hm.circles.cadence_monthly_body": "Circles برای مناسبت‌های خاص — کباب یکشنبه‌ها، شب‌های مسابقه بزرگ، تست غذاهای خاص، پوکر.",
      "hm.circles.feast_kicker": "شب خود را انتخاب کنید",
      "hm.circles.feast_title": "سه راه برای گذراندن آن",
      "hm.circles.feast_sub": "هر Circle انتخاب می‌کند که شب چگونه بگذرد. آن‌ها را بچرخانید یا روی گزینه محبوب اکیپ‌تان ثابت بمانید.",
      "hm.circles.feast_surprise_ttl": "امتحان یک رستوران جدید",
      "hm.circles.feast_surprise_body": "ما سه مکان نزدیک شما را لیست می‌کنیم، همه رای می‌دهند، آشپزخانه برای یک میز کامل آماده می‌شود. تقسیم هزینه، صندلی‌های پیش‌پرداخت شده.",
      "hm.circles.feast_preselect_ttl": "مسابقات / ورق‌بازی / بازی‌ها",
      "hm.circles.feast_preselect_body": "یک شب مسابقه اطلاعات عمومی، اتاق پوکر، کافه بازی‌های رومیزی یا سالن بولینگ رزرو کنید. ما هماهنگی محل، ورودی و یادآوری‌ها را انجام می‌دهیم.",
      "hm.circles.feast_per_event_ttl": "شب تم‌دار یک‌باره",
      "hm.circles.feast_per_event_body": "تست نوشیدنی، میز مخصوص سرآشپز، کلاس کوکتل، اتاق فرار. چیزی خاص برای هر از گاهی — کاملاً سازماندهی شده.",
      "hm.circles.live_kicker": "همین حالا",
      "hm.circles.live_title": "Circles که می‌توانید به آن‌ها بپیوندید",
      "hm.circles.live_host_link": "شروع Circle خودتان",
      "hm.circles.live_loading": "در حال بارگذاری…",
      "hm.circles.live_empty_title": "اولین Circle در شهر خود باشید.",
      "hm.circles.live_empty_sub": "یکی را با سه دوست شروع کنید و ما به شما کمک می‌کنیم بقیه را دعوت کنید — به علاوه هزینه سه محل اول با ما.",
      "hm.circles.live_empty_cta": "شروع یک Circle",
      "hm.circles.host_kicker": "برای رهبران Circle",
      "hm.circles.host_title": "شما اکیپ را بیاورید. کارهای اداری با ما.",
      "hm.circles.host_body": "اگر شما همان دوستی هستید که همیشه شب‌ها را هماهنگ می‌کند، Circles ساعت‌ها در وقت شما صرفه‌جویی می‌کند. یادآوری خودکار، پیش‌پرداخت‌ها، لیست کوتاه مکان‌ها، تقسیم هزینه، مدیریت لیست انتظار و یک کیف پول بازگشت وجه برای هر شب بیرون رفتن. شما فقط به عنوان یک عضو عادی حضور پیدا می‌کنید.",
      "hm.circles.host_perk_1": "لیست کوتاه خودکار مکان‌ها — دیگر هرگز در Google نگردید",
      "hm.circles.host_perk_2": "صندلی‌های پیش‌پرداخت شده — غیبت صفر",
      "hm.circles.host_perk_3": "تقسیم هزینه قبل از دسر انجام می‌شود",
      "hm.circles.host_perk_4": "یادآوری‌ها + دعوت‌نامه‌های تقویم روی حالت خودکار",
      "hm.circles.host_perk_5": "لیست انتظار با جایگزینی خودکار در صورت لغو دوست",
      "hm.circles.host_perk_6": "کیف پول بازگشت وجه در هر شب بیرون رفتن",
      "hm.circles.host_cta": "همین حالا یک Circle شروع کنید",
      "hm.circles.why_kicker": "چرا Circles کار می‌کند",
      "hm.circles.why_title": "دوستی‌های بزرگسالی نیاز به زیرساخت دارند",
      "hm.circles.why_1_ttl": "همان اکیپ، اعتماد عمیق‌تر",
      "hm.circles.why_1_body": "زندگی دوستان را از هم دور می‌کند. Circles ریتم ثابتی است که همه را نزدیک نگه می‌دارد — همان ۴ تا ۱۲ دوست، ملاقات در همان شب هفته، بدون توجه به هر اتفاقی.",
      "hm.circles.why_2_ttl": "خستگی اداری صفر",
      "hm.circles.why_2_body": "هیچ‌کس مجبور نیست رئیس گروه WhatsApp باشد. ما مکان‌ها را انتخاب می‌کنیم، یادآوری‌ها را می‌فرستیم، پرداخت‌ها را می‌گیریم و چرخه را کامل می‌کنیم.",
      "hm.circles.why_3_ttl": "مکان جدید، خاطره جدید",
      "hm.circles.why_3_body": "در بهترین رستوران‌های کوچک، اتاق‌های مسابقه و کافه بازی‌های Australia بگردید. چهره‌های آشنا، پس‌زمینه‌های تازه — تعادل کامل بین روتین و تازگی.",
      "hm.circles.bottom_title": "شبی که اکیپ شما برایش لحظه‌شماری می‌کند، بدون اینکه کسی مجبور به برنامه‌ریزی‌اش باشد.",
      "hm.circles.bottom_sub": "یک Circle. همان دوستان. هر بار مکان جدید.",
      "hm.circles.bottom_cta_find": "شروع یک Circle",
      "hm.circles.bottom_cta_mm": "ملاقات با افراد جدید با Matchmaker →",
      "hm.circles.card_host_pill": "Circle",
      "hm.matchmaker.page_title": "HustleMeet Matchmaker · هوش مصنوعی میز مناسب را انتخاب می‌کند",
      "hm.matchmaker.kicker": "HustleMeet · Matchmaker",
      "hm.matchmaker.hero_title": "هوش مصنوعی میز را انتخاب می‌کند. شما فقط تشریف می‌آورید.",
      "hm.matchmaker.hero_title_accent": "شما فقط تشریف می‌آورید.",
      "hm.matchmaker.hero_sub": "به یک آزمون کوتاه شخصیت و ارزش‌ها پاسخ دهید. Matchmaker ما شما را در کنار چهار تا هفت غریبه می‌نشاند که تا زمان دسر مثل دوستان قدیمی به نظر می‌رسند. بدون ورق زدن عکس‌ها. بدون صحبت‌های سطحی. فقط یک میز مشترک، آماده برای حضور شما.",
      "hm.matchmaker.cta_quiz": "شرکت در آزمون هماهنگی",
      "hm.matchmaker.cta_see_circles": "مشاهده Circles به جای آن →",
      "hm.matchmaker.trust_big5": "سنجش شخصیت Big-5",
      "hm.matchmaker.trust_seats": "۴ تا ۸ صندلی در هر میز",
      "hm.matchmaker.trust_lock": "بدون ورق زدن · بدون عکس",
      "hm.matchmaker.trust_verified": "فقط اعضای تایید شده",
      "hm.matchmaker.how_kicker": "چگونه کار می‌کند",
      "hm.matchmaker.how_title": "چهار مرحله تا میزی که از قبل با شما جور است.",
      "hm.matchmaker.how_step1_ttl": "شرکت در آزمون",
      "hm.matchmaker.how_step1_sub": "پرسشنامه ۱۰ دقیقه‌ای — شخصیت، ارزش‌ها، رژیم غذایی، زمان‌های آزاد. فقط یک بار آن را انجام می‌دهید.",
      "hm.matchmaker.how_step2_ttl": "هماهنگی یک‌شبه",
      "hm.matchmaker.how_step2_sub": "هوش مصنوعی ما پروفایل شما و لیست شام‌های فعال Australia را بررسی می‌کند. وقتی امتیاز تناسب شما ۰.۷۰ یا بالاتر باشد، یک دعوت‌نامه شخصی تهیه می‌کند.",
      "hm.matchmaker.how_step3_ttl": "پذیرش یا رد",
      "hm.matchmaker.how_step3_sub": "یک ایمیل دعوت شفاف با جزئیات مکان، تم میز و سه مورد از علاقه‌مندی‌های مشترک شما با گروه دریافت می‌کنید.",
      "hm.matchmaker.how_step4_ttl": "از شما یاد می‌گیرد",
      "hm.matchmaker.how_step4_sub": "هر نظرسنجی بعد از شام، مدل را تنظیم می‌کند. هرچه بیشتر شام بخورید، هماهنگی بعدی شما هوشمندانه‌تر می‌شود.",
      "hm.matchmaker.science_kicker": "چرا کار می‌کند",
      "hm.matchmaker.science_title": "Matchmaker حدس نمی‌زند — امتیازدهی می‌کند.",
      "hm.matchmaker.science_sub": "هر هماهنگی ترکیبی وزنی از چهار فاکتور است. وقتی میانگین کل میز بالای ۰.۷۰ باشد، دعوت‌نامه‌ها را می‌فرستیم. هر چیزی کمتر از آن در صف انتظار می‌ماند.",
      "hm.matchmaker.science_1_ttl": "شخصیت (۳۵٪)",
      "hm.matchmaker.science_1_body": "شباهت کسینوسی در بردار شخصیتی Big-5 — گشودگی، وظیفه‌شناسی، برون‌گرایی، موافق بودن و ثبات عاطفی.",
      "hm.matchmaker.science_2_ttl": "ارزش‌ها (۲۵٪)",
      "hm.matchmaker.science_2_body": "هم‌پوشانی در ارزش‌هایی که باعث می‌شود یک گفتگوی شام واقعاً به دل بنشیند — خانواده، کنجکاوی، رشد، خدمت، ماجراجویی.",
      "hm.matchmaker.science_3_ttl": "علاقه‌مندی‌ها (۲۵٪)",
      "hm.matchmaker.science_3_body": "هم‌پوشانی ژاکارد در علاقه‌مندی‌های خاص شما — پیاده‌روی در مسیر Six Foot Track، کلوب فیلم Sydney، صحنه قهوه Melbourne، سومین کتاب سالتان.",
      "hm.matchmaker.science_4_ttl": "زمان‌های آزاد (۱۵٪)",
      "hm.matchmaker.science_4_body": "شبی که واقعاً می‌توانید در آن شرکت کنید. اگر تقویم شما می‌گوید سه‌شنبه‌ها همیشه باشگاه هستید، دعوت‌نامه ۷ عصر سه‌شنبه نخواهید داشت.",
      "hm.matchmaker.safety_kicker": "طراحی شده برای ایمنی",
      "hm.matchmaker.safety_title": "Matchmaker در سکوت محتاط است.",
      "hm.matchmaker.safety_1_ttl": "آستانه اطمینان",
      "hm.matchmaker.safety_1_body": "پایین‌تر از امتیاز گروهی ۰.۷۰، هیچ دعوت‌نامه‌ای ارسال نمی‌شود. یک هماهنگی ضعیف از شام نخوردن بدتر است.",
      "hm.matchmaker.safety_2_ttl": "لیست مسدودسازی متقابل",
      "hm.matchmaker.safety_2_body": "اگر دو عضو بعد از شام به طور خصوصی سیگنال «نه» بدهند، دیگر هرگز آن‌ها را کنار هم نمی‌نشانیم.",
      "hm.matchmaker.safety_3_ttl": "قطع‌کن خودکار",
      "hm.matchmaker.safety_3_body": "اگر ۲۰٪ یا بیشتر از نظرسنجی‌های اخیر به یک میز امتیاز ۲/۵ یا کمتر بدهند، ارسال دعوت‌نامه‌ها ۲۴ ساعت متوقف می‌شود تا اپراتور بررسی کند.",
      "hm.matchmaker.safety_4_ttl": "استانداردهای جامعه",
      "hm.matchmaker.safety_4_body": "هر عضو یک منشور اخلاقی به زبان ساده را می‌پذیرد. افرادی را که از آن تخطی کنند بدون امکان اعتراض حذف می‌کنیم.",
      "hm.matchmaker.safety_5_ttl": "فقط مکان‌های عمومی",
      "hm.matchmaker.safety_5_body": "هر شام Matchmaker در یک رستوران واقعی با پرسنل برگزار می‌شود. هرگز در خانه کسی نیست.",
      "hm.matchmaker.safety_6_ttl": "بازخورد پس از رویداد",
      "hm.matchmaker.safety_6_body": "نظرسنجی خصوصی شما هماهنگی‌های آینده را شکل می‌دهد و می‌تواند باعث بررسی نظارتی یک مکان یا یک همتا شود.",
      "hm.matchmaker.loop_kicker": "چرخه یادگیری",
      "hm.matchmaker.loop_title": "هر شام، شام بعدی شما را بهتر می‌کند.",
      "hm.matchmaker.loop_body": "Matchmaker ایستا نیست. بعد از هر رویداد یک نظرسنجی خصوصی کوتاه می‌فرستید — اینکه گفتگو چطور بود، با چه کسی دوست دارید دوباره شام بخورید و آیا مکان مناسب بود یا خیر. این سیگنال‌ها به مدل بازمی‌گردند و هر هماهنگی آینده را بازسازی می‌کنند.",
      "hm.matchmaker.loop_1": "رای‌های «دوباره شام بخوریم» از طرف همتایان، جفت‌شدن‌های شما را تقویت می‌کند",
      "hm.matchmaker.loop_2": "رای‌های منفی متقابل در سکوت جفت‌ها را بازنشسته می‌کند",
      "hm.matchmaker.loop_3": "رتبه‌بندی مکان و گفتگو، ترکیب صندلی‌ها را تنظیم می‌کند",
      "hm.matchmaker.loop_4": "امتیاز قابلیت اطمینان، اعضا را در برابر غیبت‌های تکراری محافظت می‌کند",
      "hm.matchmaker.loop_5": "سوگیری یادگیری، هماهنگی‌های خوب را ماه به ماه تقویت می‌کند",
      "hm.matchmaker.loop_6": "شما در کنترل می‌مانید — هر زمان که بخواهید انصراف دهید یا پروفایل خود را بازنشانی کنید",
      "hm.matchmaker.vs_kicker": "کدام برای من مناسب است؟",
      "hm.matchmaker.vs_title": "Matchmaker یا Circles؟",
      "hm.matchmaker.vs_mm_ttl": "مرا انتخاب کن اگر تنوع می‌خواهی.",
      "hm.matchmaker.vs_mm_1": "هر بار یک میز، شهر و غذای متفاوت",
      "hm.matchmaker.vs_mm_2": "گروه منتخب هوش مصنوعی از ۴ تا ۸ غریبه‌ی سازگار",
      "hm.matchmaker.vs_mm_3": "ریتم هفتگی، بدون تعهد میزبانی",
      "hm.matchmaker.vs_mm_4": "عالی اگر در شهر جدید هستید یا در حال سفر",
      "hm.matchmaker.vs_mm_cta": "شرکت در آزمون هماهنگی",
      "hm.matchmaker.vs_circles_ttl": "مرا انتخاب کن اگر یک جمع ثابت می‌خواهی.",
      "hm.matchmaker.vs_circles_1": "همان میزبان · همان مکان · همان شب هفته",
      "hm.matchmaker.vs_circles_2": "گروهی که به سنت شب‌های چهارشنبه شما تبدیل می‌شود",
      "hm.matchmaker.vs_circles_3": "منتخب توسط انسان و یک میزبان با تجربه",
      "hm.matchmaker.vs_circles_4": "عالی اگر تازه نقل مکان کرده‌اید و می‌خواهید ریشه بدوانید",
      "hm.matchmaker.vs_circles_cta": "مشاهده Circles",
      "hm.matchmaker.bottom_title": "یک آزمون. تمام شام‌های بعد از آن از قبل با شما جور هستند.",
      "hm.matchmaker.bottom_sub": "ظرف دو هفته هماهنگ می‌شوید. این راحت‌ترین «بله»ای است که این ماه خواهید گفت.",
      "hm.matchmaker.bottom_cta": "شرکت در آزمون هماهنگی",
      "hm.matchmaker.bottom_note": "بدون نیاز به کارت اعتباری · ۱۰ دقیقه · لغو در هر زمان",
      /* AUTO-TRANSLATED hm.* END */
    },
  },
  ur: {
    translation: {
      /* AUTO-TRANSLATED finder.* (added) */
      "finder.title": "HustleFinder",
      "finder.tagline": "قریب ترین عوامی جگہ تلاش کریں — حقیقی ڈیٹا، پورے آسٹریلیا میں۔",
      "finder.search_placeholder": "جگہیں، سڑکیں یا مضافات تلاش کریں…",
      "finder.hint": "اوپر ٹائپ کرنا شروع کریں، کسی زمرے پر ٹیپ کریں، یا \"میرے قریب\" استعمال کریں۔",
      "finder.near_me": "میرے قریب",
      "finder.searching": "تلاش جاری ہے…",
      "finder.suburb.placeholder": "مضافات کے لحاظ سے فلٹر کریں (مثلاً Darwin, NT)…",
      "finder.suburb.clear": "صاف کریں",
      "finder.search_in": "{{cat}} میں تلاش کریں",
      "finder.empty": "کوئی نتیجہ نہیں — کوئی اور زمرہ یا تلاش کی اصطلاح آزمائیں۔",
      "finder.meta.title": "HustleFinder — مفت آسٹریلوی مضافاتی ڈائریکٹری",
      "finder.meta.desc": "تمام آسٹریلوی مضافات میں مقامی کاروبار، چارجرز، کیفے، بیت الخلا اور مزید تلاش کریں — 100% مفت۔",
      "finder.footer.credits": "ڈیٹا کریڈٹس اور انتساب",
      "finder.footer.terms": "شرائط",
      "finder.cat.toilet": "بیت الخلا",
      "finder.cat.fountain": "پینے کے فوارے",
      "finder.cat.transport": "نقل و حمل",
      "finder.cat.park": "پارکس",
      "finder.cat.medical": "طبی",
      "finder.cat.school": "اسکول",
      "finder.cat.library": "لائبریریاں",
      "finder.cat.petrol": "پیٹرول",
      "finder.cat.kidzone": "بچوں کا زون",
      "finder.cat.puzzles": "پہیلیاں",
      "finder.cat.roadtrip": "سڑک کا سفر",
      "finder.hotels.title": "قریب میں ہوٹل تلاش کریں",
      "finder.hotels.sub": "ایک تلاش میں 200+ بکنگ سائٹس کا موازنہ کرتا ہے — Trivago کی طاقت سے۔",
      "finder.hotels.where": "کہاں",
      "finder.hotels.where_ph": "شہر یا مضافات (مثلاً Byron Bay, NSW)",
      "finder.hotels.checkin": "چیک ان",
      "finder.hotels.checkout": "چیک آؤٹ",
      "finder.hotels.adults": "بالغ",
      "finder.hotels.search": "ہوٹل تلاش کریں",
      "finder.hotels.disclosure": "Trivago کھولتا ہے — ہم بکنگ پر کمیشن حاصل کر سکتے ہیں۔",
      /* AUTO-TRANSLATED rt.* START */
      "rt.brand_name": "Roadtrip Planner",
      "rt.page_title": "آسٹریلوی ای وی روڈ ٹرپ پلانر",
      "rt.from": "سے",
      "rt.to": "تک",
      "rt.from_placeholder": "مضافات، قصبہ یا شہر (مثلاً Newcastle NSW)",
      "rt.to_placeholder": "مضافات، قصبہ یا شہر (مثلاً Hobart TAS)",
      "rt.stop": "STOP",
      "rt.stop_placeholder": "درمیانی اسٹاپ",
      "rt.add_stop": "اسٹاپ شامل کریں",
      "rt.upgrade_pro": "20 اسٹاپس تک کے لیے پرو (Pro) پر اپ گریڈ کریں",
      "rt.reverse": "برعکس (گھر واپسی کا راستہ تلاش کریں)",
      "rt.from_current": "موجودہ مقام سے",
      "rt.range_per_charge": "رینج فی چارج (کلو میٹر)",
      "rt.connector": "کنیکٹر",
      "rt.connector_any": "کوئی بھی",
      "rt.drive_hours_day": "ڈرائیو کے گھنٹے/دن",
      "rt.accommodation": "رہائش",
      "rt.accom_any": "کوئی بھی (BnB · ہوٹل · کیروان)",
      "rt.accom_bnb": "BnB",
      "rt.accom_hotel": "ہوٹل",
      "rt.accom_caravan": "کیروان پارک",
      "rt.min_rating": "کم از کم ریٹنگ",
      "rt.any_rating": "کوئی بھی ریٹنگ",
      "rt.only_5star": "صرف 5★",
      "rt.schedule": "شیڈول:",
      "rt.depart_at": "روانگی کا وقت",
      "rt.arrive_by": "پہنچنے کا وقت",
      "rt.hotel_checkin": "ہوٹل چیک ان",
      "rt.hotel_checkout": "ہوٹل چیک آؤٹ",
      "rt.overnight_hint": "رات کے سفر ان اوقات سے باہر شیڈول نہیں ہوں گے۔",
      "rt.whos_in_car": "گاڑی میں کون ہے؟",
      "rt.adults": "بالغ",
      "rt.kids": "بچے",
      "rt.ages": "عمریں",
      "rt.show_attractions": "سیاحتی مقامات دکھائیں",
      "rt.show_kids": "بچوں کے لیے موزوں اسٹاپس دکھائیں",
      "rt.overnight_stays": "رات کا قیام",
      "rt.fast_chargers_only": "صرف فاسٹ چارجرز",
      "rt.hotel_must_charger": "ہوٹل میں چارجر لازمی ہو",
      "rt.pet_only": "صرف پالتو جانوروں کے لیے موزوں قیام",
      "rt.safety": "حفاظت",
      "rt.safety_90": "90% (ہموار)",
      "rt.safety_85": "85% (ڈیفالٹ)",
      "rt.safety_80": "80% (پہاڑیاں / ٹونگ)",
      "rt.safety_70": "70% (بھاری سامان)",
      "rt.save_trip": "ٹرپ محفوظ کریں",
      "rt.plan_my_trip": "میرا ٹرپ پلان کریں",
      "rt.refresh_trip": "ٹرپ ریفریش کریں",
      "rt.planning": "پلاننگ ہو رہی ہے…",
      "rt.resume_title": "یہاں سے ٹرپ دوبارہ شروع کریں",
      "rt.resume_body": "ہم نے آپ کی موجودہ لوکیشن حاصل کر لی ہے۔ اپنے ڈیش بورڈ پر دکھائی دینے والی باقی رینج درج کریں تاکہ ہم اگلے مرحلے کی منصوبہ بندی آپ کی موجودہ بیٹری کے مطابق کریں — نہ کہ فل چارج کے مطابق۔",
      "rt.remaining_range": "باقی رینج (کلو میٹر)",
      "rt.cancel": "منسوخ کریں",
      "rt.replan_from_here": "یہاں سے دوبارہ پلان کریں",
      "rt.save_modal_title": "اس ٹرپ کا نام رکھیں",
      "rt.save_modal_body": "محفوظ کردہ ٹرپس آپ کی پروفائل میں ہوتے ہیں — کسی بھی وقت دوبارہ کھولیں اور Replan پر کلک کریں۔",
      "rt.save_name_placeholder": "مثلاً Brisbane → Sydney بذریعہ Byron",
      "rt.your_saved_trips": "آپ کے محفوظ کردہ ٹرپس",
      "rt.custom_location": "اپنی مرضی کی جگہ",
      "rt.trip_summary": "ٹرپ کا خلاصہ",
      "rt.itinerary": "سفر نامہ",
      "rt.no_stops_needed": "کسی اسٹاپ کی ضرورت نہیں — پورا ٹرپ آپ کی رینج کے اندر ہے۔",
      "rt.chargers_in_corridor": "کوریڈور میں چارجرز",
      "rt.planned_stops": "پلان کیے گئے اسٹاپس",
      "rt.overnight_stop": "رات کا اسٹاپ",
      "rt.overnight_stops": "رات کے اسٹاپس",
      "rt.suggested_detours": "تجویز کردہ راستے — شامل کرنے کے لیے نشان لگائیں",
      "rt.replan_with_detours": "نئے راستوں کے ساتھ دوبارہ پلان کریں",
      "rt.attractions_label": "مقامِ دلچسپی",
      "rt.kids_label": "بچے",
      "rt.km_from_start": "آغاز سے کلو میٹر",
      "rt.open_driving": "ڈرائیونگ موڈ کھولیں",
      "rt.big_font_hint": "بڑے فونٹ، ہولڈر کے لیے موزوں۔ CarPlay/Android Auto براؤزر مررنگ کے ساتھ کام کرتا ہے۔",
      "rt.check_price_book": "Trivago پر قیمت چیک کریں اور بک کریں ↗",
      "rt.visit_site": "سائٹ دیکھیں ↗",
      "rt.charger_onsite": "سائٹ پر چارجر موجود ہے",
      "rt.leg_exceeds_range": "یہ مرحلہ آپ کی مقررہ رینج سے زیادہ ہے — آپ اس چارجر تک پہنچنے سے پہلے پاور ختم کر دیں گے۔ رینج کا تخمینہ کم کریں یا درمیان میں ایک چارجنگ اسٹاپ شامل کریں۔",
      "rt.add_waypoint_replan": "بطور وے پوائنٹ شامل کریں اور دوبارہ پلان کریں",
      /* AUTO-TRANSLATED rt.* END */
                      /* AUTO-TRANSLATED hm.* START */
      "hm.nav.hustlemeet": "HustleMeet",
      "hm.nav.circles": "Circles",
      "hm.nav.matchmaker": "Matchmaker",
      "hm.nav.meet": "Meet",
      "hm.nav.overview": "HustleMeetups",
      "hm.overview.page_title": "HustleMeetups — اپنے لوگوں کو تلاش کرنے کے تین پُرتپاک طریقے۔",
      "hm.overview.kicker": "HustleSuite کا حصہ",
      "hm.overview.hero_title": "اپنے لوگوں کو تلاش کرنے کے تین پُرتپاک طریقے۔",
      "hm.overview.hero_title_accent": "اپنے لوگ",
      "hm.overview.hero_sub": "HustleMeetups حقیقی دوستیاں بنانے کا Australia کا سب سے شائستہ طریقہ ہے — وہ انداز منتخب کریں جو آپ کو پسند ہو۔ چاہے آپ ہفتہ وار رننگ گروپ چاہیں، اپنے موجودہ دوستوں کے ساتھ ماہانہ ڈنر، یا AI کے ذریعے منتخب کردہ نئے چہروں کے ساتھ ایک میز، یہاں ہر ایک کے لیے جگہ ہے۔",
      "hm.overview.tile_meet_kicker": "HustleMeet",
      "hm.overview.tile_meet_title": "اپنے لوگوں کو تلاش کریں۔ وہ کام کریں جو آپ کو پسند ہے۔",
      "hm.overview.tile_meet_body": "دلچسپی کو فوقیت دینے والے گروپس کے لیے Australia کا گھر۔ رنرز، کوڈرز، سرمایہ کار، واکرز، ڈانسرز، بریک فاسٹ کلبز، بک کروز — سینکڑوں گروپس، ہر ہفتے حقیقی ایونٹس۔ ایک بار آئیں، یا ہر ہفتے تشریف لائیں۔",
      "hm.overview.tile_meet_cta": "گروپس تلاش کریں",
      "hm.overview.tile_circles_kicker": "Circles",
      "hm.overview.tile_circles_title": "آپ کے ساتھی۔ ہر بار ایک نئی میز۔",
      "hm.overview.tile_circles_body": "اپنے پرانے دوستوں کو ساتھ لائیں۔ ہم ایک نیا ریسٹورنٹ بک کرتے ہیں، ٹریویا پلان کرتے ہیں، اور کارڈز کا انتظام کرتے ہیں۔ آپ بس وہاں پہنچ جائیں۔ اپنے موجودہ گروپ کو قریب رکھنے کا بہترین طریقہ — کسی کو بھی انتظام کرنے کی زحمت نہیں کرنی پڑتی۔",
      "hm.overview.tile_circles_cta": "ایک Circle شروع کریں",
      "hm.overview.tile_matchmaker_kicker": "Matchmaker",
      "hm.overview.tile_matchmaker_title": "AI میز کا انتخاب کرتا ہے۔ آپ بس پہنچ جائیں۔",
      "hm.overview.tile_matchmaker_body": "ایک 10 منٹ کا کوئز لیں۔ راتوں رات، ہم آپ کو ایسے اجنبیوں کے ساتھ بٹھا دیتے ہیں جو میٹھے تک پرانے دوستوں کی طرح لگنے لگتے ہیں۔ ہر بار ایک مختلف میز۔",
      "hm.overview.tile_matchmaker_cta": "کوئز شروع کریں",
      "hm.overview.trust_kicker": "یہ کیوں کامیاب ہے",
      "hm.overview.trust_title": "پہلے بھروسہ۔ پھر میز۔",
      "hm.overview.trust_1_title": "تصدیق شدہ اراکین",
      "hm.overview.trust_1_body": "ہر گروپ کا آغاز شناختی کارڈ کی تصدیق + قابل اعتماد اسکور سے ہوتا ہے۔ یہاں کوئی فرضی اکاؤنٹس نہیں ہیں۔",
      "hm.overview.trust_2_title": "حقیقی میزبان",
      "hm.overview.trust_2_body": "ہر گروپ کی قیادت ایک تصدیق شدہ میزبان کرتا ہے جو ماحول کو خوشگوار بناتا ہے، رفتار طے کرتا ہے، اور سب کا خیال رکھتا ہے۔",
      "hm.overview.trust_3_title": "پہلے دوستی",
      "hm.overview.trust_3_body": "یہاں ہر چیز حقیقی دوستی کے لیے بنائی گئی ہے، ڈیٹنگ کے لیے نہیں۔ کبھی بھی نہیں۔",
      "hm.overview.trust_4_title": "سخت اعتدال پسندی",
      "hm.overview.trust_4_body": "ایک رپورٹ، ایک جائزہ، ایک فیصلہ۔ غلط رویہ رکھنے والوں کو اسی رات ہٹا دیا جاتا ہے۔",
      "hm.overview.footer_line": "HustleSuite کا حصہ — Australia کی ملکیت اور میزبان۔",
      "hm.landing.page_title": "HustleMeet · اپنے لوگوں کو تلاش کریں۔ وہ کام کریں جو آپ کو پسند ہے۔",
      "hm.landing.kicker": "Australia کا دلچسپیوں پر مبنی نیٹ ورک",
      "hm.landing.hero_title": "اپنے لوگوں کو تلاش کریں۔ وہ کام کریں جو آپ کو پسند ہے۔",
      "hm.landing.hero_title_accent": "اپنے لوگ",
      "hm.landing.hero_sub": "HustleMeet ایسی دلچسپیوں والے گروپس کا گھر ہے جنہیں فوقیت حاصل ہے۔ سافٹ ویئر ڈویلپرز لنچ پر ڈیبگنگ کر رہے ہیں۔ صبح 6 بجے لمبی دوری کے رنرز۔ ماہانہ ملاقات کرنے والے سرمایہ کار۔ واکنگ گروپس، ڈانس کروز، بریک فاسٹ کلب، اور بورڈ گیم نائٹس۔ ایک گروپ منتخب کریں، اگلے ایونٹ کو بک کریں، اور پہنچ جائیں۔",
      "hm.landing.cta_explore_circles": "گروپس دیکھیں",
      "hm.landing.cta_try_matchmaker": "اپنا گروپ شروع کریں",
      "hm.landing.trust_verified": "تصدیق شدہ اراکین",
      "hm.landing.trust_venues": "تصدیق شدہ ایونٹ میزبان",
      "hm.landing.trust_friendship": "پہلے دوستی — ڈیٹنگ کبھی نہیں",
      "hm.landing.trust_moderation": "سخت اعتدال پسندی",
      "hm.landing.subbrand_circles_kicker": "Circles",
      "hm.landing.subbrand_circles_title": "پہلے سے دوست موجود ہیں؟ انہیں ایک مستقل جگہ دیں۔",
      "hm.landing.subbrand_circles_body": "Circles ان دوستوں کے لیے ہے جو پہلے ہی ملتے ہیں — بک کلب، پوکر نائٹ، رننگ کرو، یا کام کے ساتھی۔ ہم جگہ، ریمائنڈرز اور روسٹر کا خیال رکھتے ہیں۔ آپ بس وہاں پہنچ جائیں۔",
      "hm.landing.subbrand_circles_b1": "بدلتے ہوئے مقامات، ایک مستقل لسٹ",
      "hm.landing.subbrand_circles_b2": "خودکار ریمائنڈرز + آسان RSVPs",
      "hm.landing.subbrand_circles_b3": "ٹریویا، کارڈز اور تھیم والی راتیں دستیاب ہیں",
      "hm.landing.subbrand_circles_cta": "ایک Circle شروع کریں",
      "hm.landing.subbrand_matchmaker_kicker": "Matchmaker",
      "hm.landing.subbrand_matchmaker_title": "AI میز کا انتخاب کرتا ہے۔ آپ بس پہنچ جائیں۔",
      "hm.landing.subbrand_matchmaker_body": "ایک 10 منٹ کا کوئز — شخصیت، اقدار، اور دستیابی۔ ہمارا Matchmaker آپ کو ایسے اجنبیوں کے ساتھ بٹھاتا ہے جو میٹھے تک پرانے دوستوں کی طرح محسوس ہوں گے۔ ہر بار ایک نئی میز۔",
      "hm.landing.subbrand_matchmaker_b1": "Big-5 شخصیت اور اقدار کا اسکور",
      "hm.landing.subbrand_matchmaker_b2": "جب آپ کی مطابقت 0.70 یا اس سے زیادہ ہو تو فوری دعوت نامے",
      "hm.landing.subbrand_matchmaker_b3": "آپ کے ہر ڈنر سے سسٹم سیکھتا ہے",
      "hm.landing.subbrand_matchmaker_cta": "کوئز شروع کریں",
      "hm.landing.how_kicker": "HustleMeet کیسے کام کرتا ہے",
      "hm.landing.how_title": "چار مراحل۔ اپنا گروپ تلاش کریں۔ پہنچ جائیں۔",
      "hm.landing.how_step1_ttl": "اپنی دلچسپیاں منتخب کریں",
      "hm.landing.how_step1_sub": "رننگ، کوڈنگ، سرمایہ کاری، کھانا پکانا، موسیقی، پیدل چلنا — ہمیں بتائیں کہ آپ کو کیا پسند ہے اور آپ کہاں رہتے ہیں۔",
      "hm.landing.how_step2_ttl": "اپنے قریبی گروپس دیکھیں",
      "hm.landing.how_step2_sub": "ہر گروپ اپنے میزبان، اپنی رفتار اور اپنے اگلے ایونٹ کی تفصیلات شائع کرتا ہے۔ شامل ہونے سے پہلے گروپ کے ماحول کو سمجھ لیں۔",
      "hm.landing.how_step3_ttl": "اگلے ایونٹ کے لیے RSVP کریں",
      "hm.landing.how_step3_sub": "ایک ٹیپ، ایک کیلنڈر دعوت نامہ، اور ایک دن پہلے ریمائنڈر۔ کچھ ایونٹس مفت ہیں، کچھ پیڈ — قیمت بالکل واضح درج ہوتی ہے۔",
      "hm.landing.how_step4_ttl": "پہنچیں اور تعلقات بنائیں",
      "hm.landing.how_step4_sub": "میزبان سے ملیں، گروپ سے ملیں، اور کام شروع کریں۔ اگر اچھا لگا تو اگلے ہفتے پھر آئیں۔",
      "hm.landing.stats_kicker": "Australia میں ملاقات کا نیا طریقہ",
      "hm.landing.stats_title": "لوگوں کے بار بار آنے کی وجہ۔",
      "hm.landing.stats_92_sub": "محسوس کرتے ہیں کہ ان کی ایونٹ میں کم از کم ایک شخص سے اچھی بات چیت ہوئی",
      "hm.landing.stats_68_sub": "ایک ہفتے کے اندر اپنے اگلے ایونٹ کے لیے RSVP کرتے ہیں",
      "hm.landing.stats_85_sub": "گروپ کے کسی شخص کے ساتھ رابطے میں رہتے ہیں",
      "hm.landing.why_kicker": "ہم کیوں کامیاب ہیں",
      "hm.landing.why_title": "حقیقی دلچسپیاں۔ حقیقی میزبان۔ حقیقی ایونٹس۔",
      "hm.landing.why_1_ttl": "پہلے دلچسپی، پھر مقام",
      "hm.landing.why_1_body": "آپ کے گروپ کے لوگ واقعی ان چیزوں کی پرواہ کرتے ہیں جن کی آپ کرتے ہیں — یہ حقیقی دوستی کا شارٹ کٹ ہے۔",
      "hm.landing.why_2_ttl": "حقیقی ایونٹ میزبان",
      "hm.landing.why_2_body": "ہر گروپ کا ایک تصدیق شدہ میزبان ہوتا ہے جو ماحول کو سنبھالتا ہے، ترتیب طے کرتا ہے، اور کسی بھی مسئلے کی صورت میں مداخلت کرتا ہے۔ یہ کوئی Facebook ایڈمن نہیں، بلکہ ایک حقیقی انسان ہے۔",
      "hm.landing.why_3_ttl": "مستقل مزاجی اہم ہے",
      "hm.landing.why_3_body": "ہر ہفتے نئے ایونٹس۔ ایسی چیز جس کا آپ کو انتظار رہے۔ ایک ایسی روٹین جو اجنبیوں کو آپ کا منگل کی رات کا ساتھی بنا دیتی ہے۔",
      "hm.landing.why_4_ttl": "محفوظ اور قابلِ اعتماد",
      "hm.landing.why_4_body": "تصدیق شدہ اراکین، عوامی مقامات، اور بدتمیزی کے خلاف زیرو ٹالرینس۔ ہماری سپورٹ ٹیم ہر ایونٹ سے پہلے، دوران اور بعد میں دستیاب ہوتی ہے۔",
      "hm.landing.why_5_ttl": "پہلے دوستی",
      "hm.landing.why_5_body": "یہ ڈیٹنگ نہیں ہے۔ یہ کمیونٹی ہے۔ حقیقی دلچسپیاں، حقیقی لوگ، کوئی سوائپنگ نہیں۔",
      "hm.landing.why_6_ttl": "اتنا چھوٹا کہ اہمیت رکھے",
      "hm.landing.why_6_body": "زیادہ تر گروپس ایونٹس کو 4-15 نشستوں تک محدود رکھتے ہیں۔ حقیقی گفتگو کے لیے کافی بڑا، لیکن اتنا چھوٹا کہ کوئی نظر انداز نہ ہو۔",
      "hm.landing.cities_kicker": "ہم کہاں فعال ہیں",
      "hm.landing.cities_title": "گروپس جو پورے Australia اور NZ میں مل رہے ہیں۔",
      "hm.landing.cities_sub_zero": "15+ شہروں میں پہلے گروپس کھل رہے ہیں — جلدی شامل ہوں اور ماحول ترتیب دینے میں مدد کریں۔",
      "hm.landing.cities_sub_some": "15+ میٹرو علاقوں میں گروپس لائیو ہیں۔ اسی ہفتے آپ کے شہر میں کچھ نہ کچھ ہو رہا ہے۔",
      "hm.landing.cities_sub_one": "{{count}} لائیو گروپ · ہر ہفتے نئے ایونٹس · مزید شہر شامل کیے جا رہے ہیں۔",
      "hm.landing.cities_sub_many": "{{count}} لائیو گروپس · ہر ہفتے نئے ایونٹس · مزید شہر شامل کیے جا رہے ہیں۔",
      "hm.landing.forme_kicker": "یہ آپ کے لیے ہے اگر…",
      "hm.landing.forme_title": "آپ حقیقی دلچسپیاں، حقیقی لوگ، اور حقیقی ایونٹس چاہتے ہیں۔",
      "hm.landing.forme_1": "آپ سوائپ ایپس سے تھک چکے ہیں اور ان چیزوں کے گرد لوگوں سے ملنا چاہتے ہیں جو آپ کو پسند ہیں۔",
      "hm.landing.forme_2": "آپ ایک نئے شہر میں منتقل ہوئے ہیں اور آپ کو صرف چیٹ گروپ نہیں، بلکہ ایک مخلص گروپ کی ضرورت ہے۔",
      "hm.landing.forme_3": "آپ دکھاوے کے بجائے گہرائی کو اہمیت دیتے ہیں — پچاس فالوز سے ایک اچھا گروپ بہتر ہے۔",
      "hm.landing.forme_4": "آپ کا ماننا ہے کہ مشترکہ دلچسپیاں + حقیقی زندگی میں دو گھنٹے کا وقت ہی اصل کامیابی ہے۔",
      "hm.landing.notme_kicker": "یہ آپ کے لیے نہیں ہے اگر…",
      "hm.landing.notme_title": "HustleMeet ہر کسی کے لیے نہیں ہے۔",
      "hm.landing.notme_1": "آپ آنے کا وعدہ نہیں نبھا سکتے — میزبان اور اراکین آپ کے RSVP پر بھروسہ کرتے ہیں۔",
      "hm.landing.notme_2": "آپ ڈیٹنگ سروس تلاش کر رہے ہیں — یہاں دوستی سب سے پہلے ہے۔",
      "hm.landing.notme_3": "آپ کسی سے ملے بغیر صرف اپنی بات پھیلانا چاہتے ہیں — یہ صرف روبرو کام کرتا ہے۔",
      "hm.landing.notme_4": "آپ صرف مفت مراعات کی توقع رکھتے ہیں — کچھ ایونٹس کی قیمت ہوتی ہے جو پہلے بتا دی جاتی ہے۔",
      "hm.landing.safety_kicker": "ہمارا حفاظتی عہد",
      "hm.landing.safety_title": "مخلص، مگر کبھی لاپرواہ نہیں۔",
      "hm.landing.safety_1_ttl": "عوامی مقامات",
      "hm.landing.safety_1_body": "ہر ایونٹ عوامی اور عملے والے مقامات پر ہوتا ہے — کیفے، ریسٹورنٹ، پارک، جم یا اسٹوڈیو۔ کبھی کسی کے گھر نہیں جب تک کہ تمام شرکاء راضی نہ ہوں۔",
      "hm.landing.safety_2_ttl": "کمیونٹی کے معیارات",
      "hm.landing.safety_2_body": "بدتمیزی، ہراساں کرنے یا بددیانتی کے لیے کوئی جگہ نہیں ہے۔ خلاف ورزی کرنے والے اراکین کو فوری طور پر ہٹا دیا جاتا ہے۔",
      "hm.landing.safety_3_ttl": "رازداری کا تحفظ",
      "hm.landing.safety_3_body": "آپ کی تفصیلات کبھی بھی عوامی طور پر شیئر نہیں کی جاتیں۔ آپ خود کنٹرول کرتے ہیں کہ ایونٹ کے بعد کس کے ساتھ رابطے میں رہنا ہے۔",
      "hm.landing.safety_4_ttl": "سپورٹ دستیاب ہے",
      "hm.landing.safety_4_body": "اگر آپ کے پاس سوالات یا تحفظات ہوں تو ہماری ٹیم ہر ایونٹ سے پہلے، دوران اور بعد میں دستیاب ہے۔",
      "hm.landing.bottom_title": "آپ کا اگلا پسندیدہ گروپ صرف ایک کلک دور ہے۔",
      "hm.landing.bottom_sub": "اپنا راستہ منتخب کریں — کسی موجودہ گروپ میں شامل ہوں، اپنے ساتھیوں کو Circle میں لائیں، یا Matchmaker کو آپ کے لیے ایک موزوں میز منتخب کرنے دیں۔",
      "hm.landing.bottom_cta_circles": "ایک Circle شروع کریں",
      "hm.landing.bottom_cta_mm": "Matchmaker کوئز شروع کریں",
      "hm.circles.page_title": "HustleMeet Circles · آپ کے ساتھی، ہر بار نئی جگہ",
      "hm.circles.kicker": "HustleMeet · Circles",
      "hm.circles.hero_title": "آپ کے ساتھی۔ ہر بار ایک نئی میز۔",
      "hm.circles.hero_title_accent": "آپ کے ساتھی",
      "hm.circles.hero_sub": "پہلے سے دوست موجود ہیں؟ Circles انہیں قریب رکھنے کا آسان ترین طریقہ ہے۔ ہم جگہ بک کرتے ہیں، ٹریویا پلان کرتے ہیں، اور کارڈز کا انتظام کرتے ہیں۔ آپ بس پہنچ جائیں — وہی ساتھی، نئی رات، نئی جگہ۔",
      "hm.circles.cta_find": "ایک Circle شروع کریں",
      "hm.circles.cta_host_hero": "ایک اوپن Circle میں شامل ہوں",
      "hm.circles.trust_venue": "بدلتے ہوئے تصدیق شدہ مقامات",
      "hm.circles.trust_party": "4-12 دوست فی Circle",
      "hm.circles.trust_verified": "تصدیق شدہ اراکین",
      "hm.circles.trust_friendship": "حقیقی دوستیاں، ہمیشہ قریب",
      "hm.circles.how_kicker": "یہ کیسے کام کرتا ہے",
      "hm.circles.how_title": "چار مراحل۔ آپ کے ساتھی ہمیشہ ساتھ رہیں۔",
      "hm.circles.how_step1_ttl": "اپنا Circle شروع کریں",
      "hm.circles.how_step1_sub": "اسے ایک نام دیں، اپنے دوستوں کو مدعو کریں (12 تک)، اور ایک ترتیب منتخب کریں — ہفتہ وار، پندرہ روزہ یا ماہانہ۔",
      "hm.circles.how_step2_ttl": "ہم مقام کا انتخاب کرتے ہیں",
      "hm.circles.how_step2_sub": "ہر ایونٹ کے لیے ہم ایک نئی جگہ تجویز کرتے ہیں — ریسٹورنٹ، ٹریویا نائٹ، کارڈ روم، یا وائن بار۔ یا اپنی پسندیدہ جگہ پکی کر لیں۔",
      "hm.circles.how_step3_ttl": "ہر کوئی ایک ٹیپ میں RSVP کرتا ہے",
      "hm.circles.how_step3_sub": "خودکار ریمائنڈرز، لاگت کی تقسیم، اور پری پیڈ نشستیں تاکہ کسی کے نہ آنے سے پلان خراب نہ ہو۔ بل کا معاملہ کھانے سے پہلے ہی حل ہو جاتا ہے۔",
      "hm.circles.how_step4_ttl": "سلسلہ جاری رکھیں",
      "hm.circles.how_step4_sub": "ڈائری میں ایک ایسی رات جسے دوبارہ کبھی ترتیب دینے کی ضرورت نہیں۔ گروپ مہینوں میں نہیں، منٹوں میں گہرا ہوتا جاتا ہے۔",
      "hm.circles.cadence_weekly_ttl": "ہفتہ وار",
      "hm.circles.cadence_weekly_sub": "ہر ہفتے",
      "hm.circles.cadence_weekly_body": "ان دوستوں کے لیے جو منگل کی رات کا ایک مستقل معمول چاہتے ہیں — وہی چہرے، ہر بار نئی جگہ۔",
      "hm.circles.cadence_fortnight_ttl": "پندرہ روزہ",
      "hm.circles.cadence_fortnight_sub": "ہر 2 ہفتے بعد",
      "hm.circles.cadence_fortnight_body": "بہترین توازن — مصروف زندگی میں بھی مستقل مزاجی۔",
      "hm.circles.cadence_monthly_ttl": "ماہانہ",
      "hm.circles.cadence_monthly_sub": "ہر مہینے",
      "hm.circles.cadence_monthly_body": "خاص مواقع والے Circles — سنڈے روسٹ، بڑی ٹریویا نائٹس، ڈگسٹیشن، یا پوکر۔",
      "hm.circles.feast_kicker": "اپنی رات منتخب کریں",
      "hm.circles.feast_title": "وقت گزارنے کے تین طریقے",
      "hm.circles.feast_sub": "ہر Circle منتخب کرتا ہے کہ رات کیسے گزرے گی۔ انہیں بدلتے رہیں، یا اپنے گروپ کی پسندیدہ چیز پکی کر لیں۔",
      "hm.circles.feast_surprise_ttl": "نیا ریسٹورنٹ آزمائیں",
      "hm.circles.feast_surprise_body": "ہم آپ کے قریب تین مقامات کی فہرست دیتے ہیں، ہر کوئی ووٹ دیتا ہے، اور کچن میز تیار کرتا ہے۔ لاگت کی تقسیم اور پری پیڈ نشستیں۔",
      "hm.circles.feast_preselect_ttl": "ٹریویا / کارڈز / گیمز",
      "hm.circles.feast_preselect_body": "ٹریویا نائٹ، پوکر روم، بورڈ گیم بار یا بولنگ لین بک کریں۔ ہم جگہ، انٹری فیس اور ریمائنڈرز کا خیال رکھتے ہیں۔",
      "hm.circles.feast_per_event_ttl": "ایک بار کی تھیم نائٹ",
      "hm.circles.feast_per_event_body": "وائن ٹیسٹنگ، شیف ٹیبل، کاک ٹیل کلاس، یا اسکیپ روم۔ کبھی کبھار کچھ خاص — مکمل طور پر منظم۔",
      "hm.circles.live_kicker": "ابھی",
      "hm.circles.live_title": "Circles جن میں آپ شامل ہو سکتے ہیں",
      "hm.circles.live_host_link": "اپنا شروع کریں",
      "hm.circles.live_loading": "لوڈنگ…",
      "hm.circles.live_empty_title": "اپنے شہر کا پہلا Circle شروع کریں۔",
      "hm.circles.live_empty_sub": "تین دوستوں کے ساتھ شروع کریں اور ہم باقی لوگوں کو مدعو کرنے میں آپ کی مدد کریں گے — اور پہلے تین مقامات ہماری طرف سے ہوں گے۔",
      "hm.circles.live_empty_cta": "ایک Circle شروع کریں",
      "hm.circles.host_kicker": "Circle لیڈروں کے لیے",
      "hm.circles.host_title": "آپ ساتھی لائیں، ہم انتظام کریں گے۔",
      "hm.circles.host_body": "اگر آپ وہ دوست ہیں جو ہمیشہ پروگرام بناتے ہیں، تو Circles آپ کے گھنٹوں بچا سکتا ہے۔ خودکار ریمائنڈرز، پری پیڈ RSVPs، مقامات کی فہرستیں، بل کی تقسیم، ویٹ لسٹ مینجمنٹ اور ہر باہر جانے پر کیش بیک والیٹ۔ آپ بس ایک عام ممبر کی طرح تشریف لائیں۔",
      "hm.circles.host_perk_1": "خودکار مقامات کی فہرستیں — پھر کبھی Google پر تلاش نہیں کرنا پڑے گا",
      "hm.circles.host_perk_2": "پری پیڈ نشستیں — کوئی غیر حاضری نہیں",
      "hm.circles.host_perk_3": "کھانے سے پہلے ہی بل کی تقسیم مکمل",
      "hm.circles.host_perk_4": "خودکار ریمائنڈرز + کیلنڈر دعوت نامے",
      "hm.circles.host_perk_5": "اگر کوئی دوست کینسل کرے تو ویٹ لسٹ سے خودکار شمولیت",
      "hm.circles.host_perk_6": "ہر بار باہر جانے پر کیش بیک والیٹ",
      "hm.circles.host_cta": "ابھی ایک Circle شروع کریں",
      "hm.circles.why_kicker": "Circles کیوں کام کرتے ہیں",
      "hm.circles.why_title": "دوستی برقرار رکھنے کے لیے ایک نظام کی ضرورت ہے",
      "hm.circles.why_1_ttl": "وہی ساتھی، گہرا بھروسہ",
      "hm.circles.why_1_body": "زندگی دوستوں کو دور کر دیتی ہے۔ Circles وہ مستقل رفتار ہے جو سب کو قریب رکھتی ہے — وہی 4-12 ساتھی، ایک ہی رات کو ملتے ہوئے، چاہے کچھ بھی ہو۔",
      "hm.circles.why_2_ttl": "انتظامی تھکن سے نجات",
      "hm.circles.why_2_body": "کسی کو بھی WhatsApp گروپ کا چیف بننے کی ضرورت نہیں۔ ہم جگہیں منتخب کرتے ہیں، ریمائنڈرز بھیجتے ہیں، ادائیگیاں لیتے ہیں اور سب کچھ مکمل کرتے ہیں۔",
      "hm.circles.why_3_ttl": "نیا مقام، نئی یاد",
      "hm.circles.why_3_body": "Australia کے بہترین چھوٹے ریسٹورنٹس، ٹریویا رومز اور گیم بارز کا رخ کریں۔ جانے پہچانے چہرے، نئے ماحول — روٹین اور تجدید کا بہترین امتزاج۔",
      "hm.circles.bottom_title": "ایسی رات جس کا آپ کے ساتھیوں کو انتظار رہے، کسی کی محنت کے بغیر۔",
      "hm.circles.bottom_sub": "ایک Circle۔ وہی دوست۔ ہر بار ایک نئی جگہ۔",
      "hm.circles.bottom_cta_find": "ایک Circle شروع کریں",
      "hm.circles.bottom_cta_mm": "Matchmaker کے ساتھ نئے لوگوں سے ملیں →",
      "hm.circles.card_host_pill": "Circle",
      "hm.matchmaker.page_title": "HustleMeet Matchmaker · AI صحیح میز کا انتخاب کرتا ہے",
      "hm.matchmaker.kicker": "HustleMeet · Matchmaker",
      "hm.matchmaker.hero_title": "AI میز کا انتخاب کرتا ہے۔ آپ بس پہنچ جائیں۔",
      "hm.matchmaker.hero_title_accent": "آپ بس پہنچ جائیں۔",
      "hm.matchmaker.hero_sub": "شخصیت اور اقدار کے ایک مختصر کوئز کا جواب دیں۔ ہمارا Matchmaker آپ کو چار سے سات ایسے اجنبیوں کے ساتھ بٹھاتا ہے جو میٹھے تک پرانے دوستوں کی طرح محسوس ہوں گے۔ نہ کوئی سوائپنگ، نہ ہی فضول باتیں، بس ایک مشترکہ میز، جب آپ چاہیں۔",
      "hm.matchmaker.cta_quiz": "میچنگ کوئز شروع کریں",
      "hm.matchmaker.cta_see_circles": "اس کے بجائے Circles دیکھیں →",
      "hm.matchmaker.trust_big5": "Big-5 شخصیت کا اسکور",
      "hm.matchmaker.trust_seats": "4-8 نشستیں فی میز",
      "hm.matchmaker.trust_lock": "کوئی سوائپنگ نہیں · کوئی تصویر نہیں",
      "hm.matchmaker.trust_verified": "صرف تصدیق شدہ اراکین",
      "hm.matchmaker.how_kicker": "یہ کیسے کام کرتا ہے",
      "hm.matchmaker.how_title": "ایسی میز تک پہنچنے کے چار مراحل جو آپ کے لیے موزوں ہو۔",
      "hm.matchmaker.how_step1_ttl": "کوئز لیں",
      "hm.matchmaker.how_step1_sub": "دس منٹ کا سوالنامہ — شخصیت، اقدار، غذائی ضروریات، دستیابی۔ آپ اسے صرف ایک بار کرتے ہیں۔",
      "hm.matchmaker.how_step2_ttl": "راتوں رات میچنگ",
      "hm.matchmaker.how_step2_sub": "ہمارا AI آپ کے پروفائل اور Australia کے ڈنر شیڈول کا جائزہ لیتا ہے۔ جب آپ کا مطابقت اسکور 0.70 یا اس سے زیادہ ہوتا ہے، تو یہ ایک ذاتی دعوت نامہ تیار کرتا ہے۔",
      "hm.matchmaker.how_step3_ttl": "قبول یا مسترد کریں",
      "hm.matchmaker.how_step3_sub": "آپ کو ایک واضح دعوت نامہ موصول ہوتا ہے جس میں جگہ، میز کی تھیم، اور تین ایسی دلچسپیاں درج ہوتی ہیں جو آپ گروپ کے ساتھ شیئر کرتے ہیں۔",
      "hm.matchmaker.how_step4_ttl": "یہ آپ سے سیکھتا ہے",
      "hm.matchmaker.how_step4_sub": "ہر ڈنر کے بعد کا سروے ماڈل کو بہتر بناتا ہے۔ آپ جتنا زیادہ کھانا کھائیں گے، آپ کا اگلا میچ اتنا ہی بہتر ہوگا۔",
      "hm.matchmaker.science_kicker": "یہ کیوں کام کرتا ہے",
      "hm.matchmaker.science_title": "Matchmaker اندازہ نہیں لگاتا — یہ اسکور کرتا ہے۔",
      "hm.matchmaker.science_sub": "ہر میچ چار عوامل کا مرکب ہوتا ہے۔ جب پوری میز کا اوسط 0.70 سے اوپر ہوتا ہے، تو ہم دعوت نامے بھیجتے ہیں۔ اس سے کم پر وہ قطار میں رہتے ہیں۔",
      "hm.matchmaker.science_1_ttl": "شخصیت (35%)",
      "hm.matchmaker.science_1_body": "Big-5 شخصیت کے ویکٹرز میں مماثلت — کشادگی، دیانتداری، خارج پسندی، خوش اخلاقی، اور جذباتی استحکام۔",
      "hm.matchmaker.science_2_ttl": "اقدار (25%)",
      "hm.matchmaker.science_2_body": "ان اقدار میں مماثلت جو ڈنر کی گفتگو کو کامیاب بناتی ہیں — خاندان، تجسس، ترقی، خدمت، اور مہم جوئی۔",
      "hm.matchmaker.science_3_ttl": "دلچسپیاں (25%)",
      "hm.matchmaker.science_3_body": "آپ کی مخصوص دلچسپیوں میں اشتراک — جیسے Six Foot Track پر ہائیکنگ، Sydney فلم کلب، Melbourne کی کافی، یا آپ کی پسندیدہ کتابیں۔",
      "hm.matchmaker.science_4_ttl": "دستیابی (15%)",
      "hm.matchmaker.science_4_body": "وہ رات جب آپ واقعی آ سکتے ہیں۔ منگل کی رات کا دعوت نامہ نہیں ملے گا اگر آپ نے جم کے لیے وقت رکھا ہوا ہے۔",
      "hm.matchmaker.safety_kicker": "حفاظت سب سے پہلے",
      "hm.matchmaker.safety_title": "Matchmaker نہایت محتاط ہے۔",
      "hm.matchmaker.safety_1_ttl": "اعتماد کی حد",
      "hm.matchmaker.safety_1_body": "0.70 گروپ اسکور سے نیچے کوئی دعوت نامہ نہیں بھیجا جاتا۔ ایک کمزور میچ سے بہتر ہے کہ ڈنر نہ ہو۔",
      "hm.matchmaker.safety_2_ttl": "باہمی بلاک لسٹ",
      "hm.matchmaker.safety_2_body": "اگر دو اراکین ڈنر کے بعد 'نہ' کا اشارہ دیتے ہیں، تو ہم انہیں دوبارہ کبھی ایک ساتھ نہیں بٹھاتے۔",
      "hm.matchmaker.safety_3_ttl": "خودکار روک تھام",
      "hm.matchmaker.safety_3_body": "اگر 20% یا اس سے زیادہ سروے کسی میز کو 2/5 یا اس سے کم ریٹنگ دیں، تو دعوت نامے 24 گھنٹوں کے لیے روک دیے جاتے ہیں جب تک کہ ریویو نہ ہو جائے۔",
      "hm.matchmaker.safety_4_ttl": "کمیونٹی کے معیارات",
      "hm.matchmaker.safety_4_body": "ہر ممبر ایک ضابطہ اخلاق تسلیم کرتا ہے۔ خلاف ورزی کرنے والوں کو ہٹا دیا جاتا ہے — کوئی اپیل نہیں سنی جاتی۔",
      "hm.matchmaker.safety_5_ttl": "صرف عوامی مقامات",
      "hm.matchmaker.safety_5_body": "ہر Matchmaker ڈنر ایک حقیقی ریسٹورنٹ میں ہوتا ہے جہاں عملہ موجود ہو۔ کبھی کسی کے گھر نہیں۔",
      "hm.matchmaker.safety_6_ttl": "ایونٹ کے بعد فیڈ بیک",
      "hm.matchmaker.safety_6_body": "آپ کا نجی سروے مستقبل کے میچز کو بہتر بناتا ہے اور کسی جگہ یا رکن کے ریویو کا سبب بن سکتا ہے۔",
      "hm.matchmaker.loop_kicker": "سیکھنے کا عمل",
      "hm.matchmaker.loop_title": "ہر ڈنر آپ کے اگلے ڈنر کو بہتر بناتا ہے۔",
      "hm.matchmaker.loop_body": "Matchmaker ساکن نہیں ہے۔ ہر ایونٹ کے بعد آپ ایک مختصر نجی سروے جمع کراتے ہیں — گفتگو کیسی رہی، آپ کس کے ساتھ دوبارہ ملنا چاہیں گے، اور کیا جگہ اچھی تھی۔ یہ اشارے ماڈل کو واپس ملتے ہیں اور مستقبل کے ہر میچ کو بہتر بناتے ہیں۔",
      "hm.matchmaker.loop_1": "ساتھیوں کے \"دوبارہ کھانے\" کے ووٹ آپ کے جوڑوں کو مضبوط بناتے ہیں",
      "hm.matchmaker.loop_2": "باہمی 'نہ' والے ووٹ خاموشی سے جوڑوں کو الگ کر دیتے ہیں",
      "hm.matchmaker.loop_3": "جگہ اور گفتگو کی درجہ بندی سیٹوں کی ترتیب کو درست کرتی ہے",
      "hm.matchmaker.loop_4": "اعتبار کا اسکور ممبران کو بار بار نہ آنے والوں سے بچاتا ہے",
      "hm.matchmaker.loop_5": "سیکھنے کا عمل ماہ بہ ماہ اچھے میچز کو مزید مضبوط بناتا ہے",
      "hm.matchmaker.loop_6": "آپ کا کنٹرول برقرار رہتا ہے — کسی بھی وقت آپشن ختم کریں یا پروفائل ری سیٹ کریں",
      "hm.matchmaker.vs_kicker": "میرے لیے کون سا صحیح ہے؟",
      "hm.matchmaker.vs_title": "Matchmaker یا Circles؟",
      "hm.matchmaker.vs_mm_ttl": "مجھے منتخب کریں اگر آپ تنوع چاہتے ہیں۔",
      "hm.matchmaker.vs_mm_1": "ہر بار ایک مختلف میز، شہر، اور کھانا",
      "hm.matchmaker.vs_mm_2": "AI کے ذریعے منتخب کردہ 4-8 ہم خیال اجنبیوں کا گروپ",
      "hm.matchmaker.vs_mm_3": "ہفتہ وار ترتیب، میزبان کی کوئی ذمہ داری نہیں",
      "hm.matchmaker.vs_mm_4": "بہترین ہے اگر آپ شہر میں نئے ہیں یا سفر کر رہے ہیں",
      "hm.matchmaker.vs_mm_cta": "میچنگ کوئز شروع کریں",
      "hm.matchmaker.vs_circles_ttl": "مجھے منتخب کریں اگر آپ مستقل مزاجی چاہتے ہیں۔",
      "hm.matchmaker.vs_circles_1": "وہی میزبان · وہی مقام · وہی رات",
      "hm.matchmaker.vs_circles_2": "ایک ایسا گروپ جو آپ کی بدھ کی رات کی روایت بن جائے",
      "hm.matchmaker.vs_circles_3": "ایک تجربہ کار میزبان کے ذریعے ترتیب دیا گیا",
      "hm.matchmaker.vs_circles_4": "بہترین ہے اگر آپ ابھی منتقل ہوئے ہیں اور تعلقات بنانا چاہتے ہیں",
      "hm.matchmaker.vs_circles_cta": "Circles دیکھیں",
      "hm.matchmaker.bottom_title": "ایک کوئز۔ اس کے بعد ہر ڈنر آپ کے لیے موزوں ہوگا۔",
      "hm.matchmaker.bottom_sub": "آپ کو دو ہفتوں کے اندر میچ مل جائے گا۔ یہ اس مہینے کا آپ کا سب سے آسان فیصلہ ہوگا۔",
      "hm.matchmaker.bottom_cta": "میچنگ کوئز شروع کریں",
      "hm.matchmaker.bottom_note": "کوئی کریڈٹ کارڈ درکار نہیں · 10 منٹ · کسی بھی وقت ختم کریں",
      /* AUTO-TRANSLATED hm.* END */
    },
  },
  bn: {
    translation: {
      /* AUTO-TRANSLATED finder.* (added) */
      "finder.title": "HustleFinder",
      "finder.tagline": "নিকটতম পাবলিক স্থান খুঁজুন — প্রকৃত তথ্য, অস্ট্রেলিয়াজুড়ে।",
      "finder.search_placeholder": "স্থান, রাস্তা বা শহরতলি খুঁজুন…",
      "finder.hint": "উপরে টাইপ করা শুরু করুন, একটি ক্যাটাগরি ট্যাপ করুন, অথবা \"আমার কাছাকাছি\" ব্যবহার করুন।",
      "finder.near_me": "আমার কাছাকাছি",
      "finder.searching": "খুঁজছি…",
      "finder.suburb.placeholder": "শহরতলি অনুসারে ফিল্টার করুন (যেমন Darwin, NT)…",
      "finder.suburb.clear": "মুছুন",
      "finder.search_in": "{{cat}}-এ খুঁজুন",
      "finder.empty": "কোনো ফলাফল নেই — অন্য ক্যাটাগরি বা অনুসন্ধান শব্দ চেষ্টা করুন।",
      "finder.meta.title": "HustleFinder — বিনামূল্যে অস্ট্রেলিয়ান শহরতলি ডিরেক্টরি",
      "finder.meta.desc": "প্রতিটি অস্ট্রেলিয়ান শহরতলিতে স্থানীয় ব্যবসা, চার্জার, ক্যাফে, টয়লেট এবং আরও অনেক কিছু খুঁজুন — ১০০% বিনামূল্যে।",
      "finder.footer.credits": "তথ্য ক্রেডিট ও অ্যাট্রিবিউশন",
      "finder.footer.terms": "শর্তাবলী",
      "finder.cat.toilet": "টয়লেট",
      "finder.cat.fountain": "পানীয় জলের ফোয়ারা",
      "finder.cat.transport": "পরিবহন",
      "finder.cat.park": "পার্ক",
      "finder.cat.medical": "চিকিৎসা",
      "finder.cat.school": "স্কুল",
      "finder.cat.library": "লাইব্রেরি",
      "finder.cat.petrol": "পেট্রোল",
      "finder.cat.kidzone": "শিশু অঞ্চল",
      "finder.cat.puzzles": "ধাঁধা",
      "finder.cat.roadtrip": "সড়ক ভ্রমণ",
      "finder.hotels.title": "কাছাকাছি একটি হোটেল খুঁজুন",
      "finder.hotels.sub": "একটি অনুসন্ধানে ২০০+ বুকিং সাইট তুলনা করে — Trivago দ্বারা চালিত।",
      "finder.hotels.where": "কোথায়",
      "finder.hotels.where_ph": "শহর বা শহরতলি (যেমন Byron Bay, NSW)",
      "finder.hotels.checkin": "চেক-ইন",
      "finder.hotels.checkout": "চেক-আউট",
      "finder.hotels.adults": "প্রাপ্তবয়স্ক",
      "finder.hotels.search": "হোটেল খুঁজুন",
      "finder.hotels.disclosure": "Trivago খোলে — বুকিংয়ে আমরা কমিশন পেতে পারি।",
      /* AUTO-TRANSLATED rt.* START */
      "rt.brand_name": "রোডট্রিপ প্ল্যানার",
      "rt.page_title": "অসি EV রোড-ট্রিপ প্ল্যানার",
      "rt.from": "থেকে",
      "rt.to": "পর্যন্ত",
      "rt.from_placeholder": "শহরতলী, শহর বা নগর (যেমন: Newcastle NSW)",
      "rt.to_placeholder": "শহরতলী, শহর বা নগর (যেমন: Hobart TAS)",
      "rt.stop": "STOP",
      "rt.stop_placeholder": "মধ্যবর্তী বিরতি",
      "rt.add_stop": "বিরতি যোগ করুন",
      "rt.upgrade_pro": "২০টি পর্যন্ত বিরতির জন্য প্রো-তে আপগ্রেড করুন",
      "rt.reverse": "রিভার্স (বাড়ি ফেরার পথ হিসাব করুন)",
      "rt.from_current": "বর্তমান অবস্থান থেকে",
      "rt.range_per_charge": "চার্জ প্রতি রেঞ্জ (কিমি)",
      "rt.connector": "কানেক্টর",
      "rt.connector_any": "যেকোনো",
      "rt.drive_hours_day": "দৈনিক ড্রাইভ (ঘণ্টা)",
      "rt.accommodation": "আবাসন",
      "rt.accom_any": "যেকোনো (BnB · হোটেল · ক্যারাভান)",
      "rt.accom_bnb": "BnB",
      "rt.accom_hotel": "হোটেল",
      "rt.accom_caravan": "ক্যারাভান পার্ক",
      "rt.min_rating": "ন্যূনতম রেটিং",
      "rt.any_rating": "যেকোনো রেটিং",
      "rt.only_5star": "শুধু ৫★",
      "rt.schedule": "সময়সূচী:",
      "rt.depart_at": "যাত্রা শুরু",
      "rt.arrive_by": "পৌঁছানোর সময়",
      "rt.hotel_checkin": "হোটেল চেক-ইন",
      "rt.hotel_checkout": "হোটেল চেক-আউট",
      "rt.overnight_hint": "রাতারাতি ভ্রমণের অংশগুলো এই সময়ের বাইরে নির্ধারিত হবে না।",
      "rt.whos_in_car": "গাড়িতে কারা আছেন?",
      "rt.adults": "প্রাপ্তবয়স্ক",
      "rt.kids": "শিশু",
      "rt.ages": "বয়স",
      "rt.show_attractions": "পর্যটন আকর্ষণগুলো দেখান",
      "rt.show_kids": "শিশুদের উপযোগী বিরতি দেখান",
      "rt.overnight_stays": "রাত কাটানোর ব্যবস্থা",
      "rt.fast_chargers_only": "শুধু ফাস্ট চার্জার",
      "rt.hotel_must_charger": "হোটেল চার্জার থাকা আবশ্যক",
      "rt.pet_only": "শুধু পোষা প্রাণীর উপযোগী আবাসন",
      "rt.safety": "নিরাপত্তা",
      "rt.safety_90": "৯০% (সমতল)",
      "rt.safety_85": "৮৫% (ডিফল্ট)",
      "rt.safety_80": "৮০% (পাহাড় / টোইং)",
      "rt.safety_70": "৭০% (ভারী লোড)",
      "rt.save_trip": "ট্রিপ সেভ করুন",
      "rt.plan_my_trip": "আমার ট্রিপ পরিকল্পনা করুন",
      "rt.refresh_trip": "ট্রিপ রিফ্রেশ করুন",
      "rt.planning": "পরিকল্পনা করা হচ্ছে…",
      "rt.resume_title": "এখান থেকে ট্রিপ পুনরায় শুরু করুন",
      "rt.resume_body": "আমরা আপনার বর্তমান অবস্থান চিহ্নিত করেছি। আপনার ড্যাশবোর্ডে দেখানো অবশিষ্ট রেঞ্জটি লিখুন যাতে আমরা আপনার কাছে প্রকৃতপক্ষে থাকা চার্জ দিয়ে পরবর্তী অংশের পরিকল্পনা করতে পারি — সম্পূর্ণ চার্জ নয়।",
      "rt.remaining_range": "অবশিষ্টাংশ রেঞ্জ (কিমি)",
      "rt.cancel": "বাতিল",
      "rt.replan_from_here": "এখান থেকে পুনরায় পরিকল্পনা করুন",
      "rt.save_modal_title": "এই ট্রিপের নাম দিন",
      "rt.save_modal_body": "সেভ করা ট্রিপগুলো আপনার প্রোফাইলে থাকবে — যেকোনো সময় খুলুন এবং Replan চাপুন।",
      "rt.save_name_placeholder": "যেমন: Brisbane → Sydney via Byron",
      "rt.your_saved_trips": "আপনার সেভ করা ট্রিপ",
      "rt.custom_location": "কাস্টম অবস্থান",
      "rt.trip_summary": "ট্রিপের সারাংশ",
      "rt.itinerary": "ভ্রমণসূচী",
      "rt.no_stops_needed": "বিরতির প্রয়োজন নেই — পুরো ট্রিপটি আপনার রেঞ্জের মধ্যেই হবে।",
      "rt.chargers_in_corridor": "করিডোরে থাকা চার্জার",
      "rt.planned_stops": "পরিকল্পিত বিরতি",
      "rt.overnight_stop": "রাত কাটানোর বিরতি",
      "rt.overnight_stops": "রাত কাটানোর বিরতিগুলো",
      "rt.suggested_detours": "প্রস্তাবিত বিকল্প পথ — যোগ করতে টিক দিন",
      "rt.replan_with_detours": "বিকল্প পথসহ পুনরায় পরিকল্পনা করুন",
      "rt.attractions_label": "আকর্ষণ",
      "rt.kids_label": "শিশু",
      "rt.km_from_start": "শুরু থেকে কিমি",
      "rt.open_driving": "ড্রাইভিং মোড খুলুন",
      "rt.big_font_hint": "বড় ফন্ট, হোল্ডার-উপযোগী। CarPlay/Android Auto ব্রাউজার মিররের সাথে কাজ করে।",
      "rt.check_price_book": "Trivago-তে দাম দেখুন এবং বুক করুন ↗",
      "rt.visit_site": "সাইট দেখুন ↗",
      "rt.charger_onsite": "চার্জার অন-সাইট",
      "rt.leg_exceeds_range": "ভ্রমণের এই অংশটি আপনার নির্ধারিত রেঞ্জ ছাড়িয়ে যাচ্ছে — এই চার্জারে পৌঁছানোর আগেই আপনার চার্জ শেষ হয়ে যাবে। রেঞ্জ কমিয়ে ধরুন অথবা মাঝে কোনো টপ-আপ যোগ করুন।",
      "rt.add_waypoint_replan": "ওয়েপয়েন্ট হিসেবে যোগ করুন এবং পুনরায় পরিকল্পনা করুন",
      /* AUTO-TRANSLATED rt.* END */
                      /* AUTO-TRANSLATED hm.* START */
      "hm.nav.hustlemeet": "HustleMeet",
      "hm.nav.circles": "Circles",
      "hm.nav.matchmaker": "Matchmaker",
      "hm.nav.meet": "দেখা করুন",
      "hm.nav.overview": "HustleMeetups",
      "hm.overview.page_title": "HustleMeetups — আপনার আপন মানুষদের খুঁজে পাওয়ার তিনটি আন্তরিক উপায়।",
      "hm.overview.kicker": "HustleSuite-এর একটি অংশ",
      "hm.overview.hero_title": "আপনার আপন মানুষদের খুঁজে পাওয়ার তিনটি আন্তরিক উপায়।",
      "hm.overview.hero_title_accent": "আপনার আপন মানুষ",
      "hm.overview.hero_sub": "প্রকৃত বন্ধুত্ব গড়ে তোলার জন্য HustleMeetups হলো Australia-র সবচেয়ে সহজ ও আন্তরিক উপায় — আপনার সাথে মানানসই পরিবেশটি বেছে নিন। আপনি সাপ্তাহিক রানিং গ্রুপ চান, আপনার বর্তমান বন্ধুদের নিয়ে মাসিক ডিনার চান, নাকি AI-এর মাধ্যমে বাছাই করা নতুন বন্ধুদের সাথে ডিনার করতে চান, এখানে সবার জন্যই ব্যবস্থা আছে।",
      "hm.overview.tile_meet_kicker": "HustleMeet",
      "hm.overview.tile_meet_title": "আপনার আপন মানুষদের খুঁজে নিন। প্রিয় কাজটি করুন।",
      "hm.overview.tile_meet_body": "আগ্রহকে গুরুত্ব দেওয়া গ্রুপগুলোর জন্য Australia-র সেরা ঠিকানা। রানার, কোডার, ইনভেস্টর, ওয়াকার, ড্যান্সার, ব্রেকফাস্ট ক্লাব, বুক ক্রু — শত শত গ্রুপ, প্রতি সপ্তাহে বাস্তব ইভেন্ট। একবার যোগ দিন, অথবা প্রতি সপ্তাহে।",
      "hm.overview.tile_meet_cta": "গ্রুপগুলো এক্সপ্লোর করুন",
      "hm.overview.tile_circles_kicker": "Circles",
      "hm.overview.tile_circles_title": "আপনার পরিচিত দল। প্রতিবার নতুন টেবিল।",
      "hm.overview.tile_circles_body": "আপনার নিয়মিত বন্ধুদের নিয়ে আসুন। আমরা নতুন রেস্তোরাঁ বুক করব, ট্রিভিয়া প্ল্যান করব, কার্ডের ব্যবস্থা করব। আপনি শুধু উপস্থিত হবেন। কোনো আয়োজন করার ঝামেলা ছাড়াই বন্ধুদের সাথে ঘনিষ্ঠ থাকার সবচেয়ে সহজ উপায়।",
      "hm.overview.tile_circles_cta": "একটি Circle শুরু করুন",
      "hm.overview.tile_matchmaker_kicker": "Matchmaker",
      "hm.overview.tile_matchmaker_title": "AI টেবিল বেছে নেবে। আপনি শুধু যোগ দেবেন।",
      "hm.overview.tile_matchmaker_body": "১০ মিনিটের একটি কুইজ দিন। রাতারাতি আমরা আপনাকে এমন কিছু অপরিচিত মানুষের সাথে বসিয়ে দেব যারা ডেজার্ট আসার আগেই পুরনো বন্ধু হয়ে উঠবে। প্রতিবার ভিন্ন টেবিল।",
      "hm.overview.tile_matchmaker_cta": "কুইজটি দিন",
      "hm.overview.trust_kicker": "কেন এটি কাজ করে",
      "hm.overview.trust_title": "ভরসা আগে। টেবিল পরে।",
      "hm.overview.trust_1_title": "যাচাইকৃত সদস্য",
      "hm.overview.trust_1_body": "প্রতিটি গ্রুপ শুরু হয় আসল ID চেক + নির্ভরযোগ্যতা স্কোরের মাধ্যমে। কোনো ভুয়া অ্যাকাউন্ট নেই।",
      "hm.overview.trust_2_title": "আসল হোস্ট",
      "hm.overview.trust_2_body": "প্রতিটি গ্রুপ একজন অভিজ্ঞ হোস্ট দ্বারা পরিচালিত হয় যিনি পরিবেশ বজায় রাখেন, ছন্দ নির্ধারণ করেন এবং সবার খেয়াল রাখেন।",
      "hm.overview.trust_3_title": "আগে বন্ধুত্ব",
      "hm.overview.trust_3_body": "এখানকার সবকিছু প্রকৃত বন্ধুত্বের জন্য তৈরি, ডেটিংয়ের জন্য নয়। কখনোই নয়।",
      "hm.overview.trust_4_title": "জিরো-টলারেন্স মডারেশন",
      "hm.overview.trust_4_body": "একটি রিপোর্ট, একটি রিভিউ, একটি সিদ্ধান্ত। বিশৃঙ্খলা সৃষ্টিকারীদের সেই রাতেই সরিয়ে দেওয়া হয়।",
      "hm.overview.footer_line": "HustleSuite-এর একটি অংশ — Australia-র মালিকানাধীন এবং পরিচালিত।",
      "hm.landing.page_title": "HustleMeet · আপনার আপন মানুষদের খুঁজে নিন। প্রিয় কাজটি করুন।",
      "hm.landing.kicker": "Australia-র ইন্টারেস্ট নেটওয়ার্ক",
      "hm.landing.hero_title": "আপনার আপন মানুষদের খুঁজে নিন। প্রিয় কাজটি করুন।",
      "hm.landing.hero_title_accent": "আপনার আপন মানুষ",
      "hm.landing.hero_sub": "আগ্রহকে গুরুত্ব দেওয়া গ্রুপগুলোর জন্য HustleMeet হলো Australia-র সেরা ঠিকানা। লাঞ্চে সফটওয়্যার ডেভেলপারদের আড্ডা। সকাল ৬টায় লং-কোর্স রানার। ইনভেস্টরদের মাসিক মিটআপ। ওয়াকিং গ্রুপ, ড্যান্স ক্রু, ব্রেকফাস্ট ক্লাব, বোর্ড-গেম নাইট। একটি গ্রুপ বেছে নিন, পরবর্তী ইভেন্ট বুক করুন এবং যোগ দিন।",
      "hm.landing.cta_explore_circles": "গ্রুপগুলো দেখুন",
      "hm.landing.cta_try_matchmaker": "আপনার নিজস্ব গ্রুপ শুরু করুন",
      "hm.landing.trust_verified": "যাচাইকৃত সদস্য",
      "hm.landing.trust_venues": "অভিজ্ঞ ইভেন্ট হোস্ট",
      "hm.landing.trust_friendship": "আগে বন্ধুত্ব — কখনোই ডেটিং নয়",
      "hm.landing.trust_moderation": "জিরো-টলারেন্স মডারেশন",
      "hm.landing.subbrand_circles_kicker": "Circles",
      "hm.landing.subbrand_circles_title": "আপনার বন্ধুদের দল কি আগে থেকেই আছে? তাদের জন্য একটি স্থায়ী ঠিকানা তৈরি করুন।",
      "hm.landing.subbrand_circles_body": "Circles হলো সেই বন্ধুদের দলের জন্য যারা আগে থেকেই দেখা করে — বুক ক্লাব, পোকার নাইট, রানিং ক্রু বা অফিসের বন্ধু। আমরা ভেন্যু, রিমাইন্ডার এবং রোস্টার সামলাই। আপনি শুধু উপস্থিত হবেন।",
      "hm.landing.subbrand_circles_b1": "পরিবর্তনশীল ভেন্যু, একটি রানিং রোস্টার",
      "hm.landing.subbrand_circles_b2": "অটো-রিমাইন্ডার + সহজ RSVPs",
      "hm.landing.subbrand_circles_b3": "ট্রিভিয়া, কার্ড এবং থিমযুক্ত রাত সব এক জায়গায়",
      "hm.landing.subbrand_circles_cta": "একটি Circle শুরু করুন",
      "hm.landing.subbrand_matchmaker_kicker": "Matchmaker",
      "hm.landing.subbrand_matchmaker_title": "AI টেবিল বেছে নেবে। আপনি শুধু যোগ দেবেন।",
      "hm.landing.subbrand_matchmaker_body": "একটি ১০ মিনিটের কুইজ — ব্যক্তিত্ব, মূল্যবোধ, প্রাপ্যতা। আমাদের Matchmaker আপনাকে এমন কিছু অপরিচিত মানুষের সাথে বসিয়ে দেবে যারা ডেজার্ট শেষ হওয়ার আগেই পুরনো বন্ধু হয়ে উঠবে। প্রতিবার ভিন্ন টেবিল।",
      "hm.landing.subbrand_matchmaker_b1": "Big-5 ব্যক্তিত্ব এবং মূল্যবোধের স্কোর",
      "hm.landing.subbrand_matchmaker_b2": "আপনার ফিট স্কোর ০.৭০ বা তার বেশি হলে রাতারাতি আমন্ত্রণ",
      "hm.landing.subbrand_matchmaker_b3": "প্রতিটি ডিনার থেকে নতুন কিছু শেখে",
      "hm.landing.subbrand_matchmaker_cta": "কুইজটি দিন",
      "hm.landing.how_kicker": "HustleMeet যেভাবে কাজ করে",
      "hm.landing.how_title": "চারটি ধাপ। আপনার গ্রুপ খুঁজে নিন। যোগ দিন।",
      "hm.landing.how_step1_ttl": "আপনার আগ্রহের বিষয় বেছে নিন",
      "hm.landing.how_step1_sub": "রানিং, কোডিং, ইনভেস্টিং, কুকিং, মিউজিক, ওয়াকিং — আপনার পছন্দের বিষয় এবং আপনি কোথায় থাকেন তা আমাদের জানান।",
      "hm.landing.how_step2_ttl": "আপনার কাছের গ্রুপগুলো দেখুন",
      "hm.landing.how_step2_sub": "প্রতিটি গ্রুপ তাদের হোস্ট, কাজের ধরন এবং পরবর্তী ইভেন্ট প্রকাশ করে। যোগ দেওয়ার আগে গ্রুপের পরিবেশ সম্পর্কে জেনে নিন।",
      "hm.landing.how_step3_ttl": "পরবর্তী ইভেন্টে RSVP করুন",
      "hm.landing.how_step3_sub": "একটি ট্যাপ, একটি ক্যালেন্ডার ইনভাইট এবং আগের দিন একটি স্পষ্ট রিমাইন্ডার। কিছু ইভেন্ট ফ্রি, কিছু পেইড — দাম আগে থেকেই জানানো থাকে।",
      "hm.landing.how_step4_ttl": "উপস্থিত হোন এবং পরিচিত হন",
      "hm.landing.how_step4_sub": "হোস্টের সাথে দেখা করুন, দলের সাথে পরিচিত হোন এবং প্রিয় কাজটি করুন। ভালো লাগলে পরের সপ্তাহে আবার আসুন।",
      "hm.landing.stats_kicker": "Australia-র নতুন ভাবে পরিচিত হওয়ার মাধ্যম",
      "hm.landing.stats_title": "যে কারণে মানুষ বারবার ফিরে আসে।",
      "hm.landing.stats_92_sub": "মনে করেন যে তারা ইভেন্টে অন্তত একজনের সাথে খুব ভালো সংযোগ অনুভব করেছেন",
      "hm.landing.stats_68_sub": "এক সপ্তাহের মধ্যে তাদের পরবর্তী ইভেন্টে RSVP করেন",
      "hm.landing.stats_85_sub": "গ্রুপের কারো সাথে যোগাযোগ বজায় রাখেন",
      "hm.landing.why_kicker": "কেন আমরা সফল",
      "hm.landing.why_title": "আসল আগ্রহ। আসল হোস্ট। আসল ইভেন্ট।",
      "hm.landing.why_1_ttl": "আগে আগ্রহ, পরে অবস্থান",
      "hm.landing.why_1_body": "আপনার গ্রুপের মানুষগুলো সত্যিই সেই বিষয় নিয়ে আগ্রহী যা আপনি পছন্দ করেন — এটিই প্রকৃত বন্ধুত্বের শর্টকাট।",
      "hm.landing.why_2_ttl": "আসল ইভেন্ট হোস্ট",
      "hm.landing.why_2_body": "প্রতিটি গ্রুপের একজন যাচাইকৃত হোস্ট থাকে যিনি পরিবেশ বজায় রাখেন এবং কোনো সমস্যা হলে পাশে দাঁড়ান। কোনো Facebook অ্যাডমিন নয়, বরং একজন বাস্তব মানুষ।",
      "hm.landing.why_3_ttl": "ধারাবাহিকতা গুরুত্বপূর্ণ",
      "hm.landing.why_3_body": "প্রতি সপ্তাহে নতুন ইভেন্ট। প্রতীক্ষার জন্য নতুন কিছু। এমন একটি ছন্দ যা অপরিচিতদের আপনার প্রিয় বন্ধুদের দলে পরিণত করে।",
      "hm.landing.why_4_ttl": "নিরাপদ এবং সুরক্ষিত",
      "hm.landing.why_4_body": "যাচাইকৃত সদস্য, পাবলিক ভেন্যু, অভদ্রতার প্রতি জিরো টলারেন্স। প্রতিটি ইভেন্টের আগে, চলাকালীন এবং পরে আমাদের সাপোর্ট টিম পাশে থাকে।",
      "hm.landing.why_5_ttl": "আগে বন্ধুত্ব",
      "hm.landing.why_5_body": "এটি ডেটিং নয়। এটি একটি কমিউনিটি। আসল আগ্রহ, আসল মানুষ, মাঝখানে কোনো সোয়াইপ করার বিষয় নেই।",
      "hm.landing.why_6_ttl": "ছোট এবং অর্থপূর্ণ",
      "hm.landing.why_6_body": "বেশিরভাগ গ্রুপে ৪-১৫টি সিট থাকে। প্রকৃত আলোচনার জন্য যথেষ্ট বড়, কিন্তু কেউ হারিয়ে যাওয়ার মতো বড় নয়।",
      "hm.landing.cities_kicker": "আমরা যেখানে আছি",
      "hm.landing.cities_title": "Australia ও NZ জুড়ে গ্রুপগুলো মিলিত হচ্ছে।",
      "hm.landing.cities_sub_zero": "১৫টিরও বেশি শহরে প্রথম গ্রুপগুলো শুরু হচ্ছে — আগেভাগে যোগ দিন এবং পরিবেশ তৈরি করতে সাহায্য করুন।",
      "hm.landing.cities_sub_some": "১৫টিরও বেশি মেট্রো এলাকায় গ্রুপগুলো লাইভ আছে। এই সপ্তাহে আপনার শহরেই কিছু একটা ঘটছে।",
      "hm.landing.cities_sub_one": "{{count}}টি লাইভ গ্রুপ · প্রতি সপ্তাহে নতুন ইভেন্ট · প্রতিনিয়ত নতুন শহর যোগ হচ্ছে।",
      "hm.landing.cities_sub_many": "{{count}}টি লাইভ গ্রুপ · প্রতি সপ্তাহে নতুন ইভেন্ট · প্রতিনিয়ত নতুন শহর যোগ হচ্ছে।",
      "hm.landing.forme_kicker": "এটি আপনার জন্য যদি…",
      "hm.landing.forme_title": "আপনি আসল আগ্রহ, আসল মানুষ এবং আসল ইভেন্ট চান।",
      "hm.landing.forme_1": "আপনি সোয়াইপ-অ্যাপের ক্লান্তি থেকে মুক্তি চান এবং নিজের পছন্দের কাজগুলো নিয়ে মানুষের সাথে দেখা করতে চান।",
      "hm.landing.forme_2": "আপনি নতুন শহরে এসেছেন এবং আপনার শুধু একটি চ্যাট গ্রুপ নয়, বরং একটি আন্তরিক দল প্রয়োজন।",
      "hm.landing.forme_3": "আপনি লোকদেখানো প্রচারের চেয়ে গভীরতাকে গুরুত্ব দেন — পঞ্চাশটি ফলোয়ারের চেয়ে একটি ভালো গ্রুপ অনেক শ্রেয়।",
      "hm.landing.forme_4": "আপনি বিশ্বাস করেন যে ভাগ করে নেওয়া আগ্রহ + বাস্তব জীবনে কাটানো দুই ঘণ্টা হলো বন্ধুত্বের আসল চাবিকাঠি।",
      "hm.landing.notme_kicker": "এটি আপনার জন্য নয় যদি…",
      "hm.landing.notme_title": "HustleMeet সবার জন্য কাজ করে না।",
      "hm.landing.notme_1": "আপনি উপস্থিত থাকার প্রতিশ্রুতি দিতে না পারেন — হোস্ট এবং সদস্যরা আপনার RSVP-র ওপর নির্ভর করে।",
      "hm.landing.notme_2": "আপনি একটি ডেটিং সার্ভিস খুঁজছেন — এখানে বন্ধুত্ব সবার আগে।",
      "hm.landing.notme_3": "আপনি কারো সাথে দেখা না করে শুধু প্রচার করতে চান — এটি কেবল সরাসরি সাক্ষাতের মাধ্যমেই কাজ করে।",
      "hm.landing.notme_4": "আপনি অবিরাম ফ্রি সুবিধা আশা করেন — কিছু ইভেন্টের খরচ আছে। দাম সবসময় আগেই জানিয়ে দেওয়া হয়।",
      "hm.landing.safety_kicker": "আমাদের নিরাপত্তার প্রতিশ্রুতি",
      "hm.landing.safety_title": "আন্তরিক, কিন্তু কখনোই অসতর্ক নয়।",
      "hm.landing.safety_1_ttl": "পাবলিক ভেন্যু",
      "hm.landing.safety_1_body": "প্রতিটি ইভেন্ট একটি পাবলিক ভেন্যুতে পরিচালিত হয় — যেমন ক্যাফে, রেস্তোরাঁ, পার্ক, জিম বা স্টুডিও। সকল অংশগ্রহণকারী রাজি না হলে কখনোই কারো বাড়িতে নয়।",
      "hm.landing.safety_2_ttl": "কমিউনিটি স্ট্যান্ডার্ডস",
      "hm.landing.safety_2_body": "অসম্মান, হয়রানি বা অসততার প্রতি জিরো টলারেন্স। নিয়ম ভঙ্গকারী সদস্যদের অবিলম্বে সরিয়ে দেওয়া হয়।",
      "hm.landing.safety_3_ttl": "গোপনীয়তা সুরক্ষিত",
      "hm.landing.safety_3_body": "আপনার তথ্য কখনোই জনসমক্ষে প্রকাশ করা হয় না। প্রতিটি ইভেন্টের পর আপনি কার সাথে যুক্ত থাকতে চান তা আপনি নিয়ন্ত্রণ করেন।",
      "hm.landing.safety_4_ttl": "সাপোর্ট অন কল",
      "hm.landing.safety_4_body": "আপনার কোনো প্রশ্ন বা উদ্বেগ থাকলে প্রতিটি ইভেন্টের আগে, চলাকালীন এবং পরে আমাদের টিম প্রস্তুত থাকে।",
      "hm.landing.bottom_title": "আপনার পরবর্তী প্রিয় গ্রুপ মাত্র একটি ট্যাপ দূরে।",
      "hm.landing.bottom_sub": "আপনার পথ বেছে নিন — বিদ্যমান কোনো গ্রুপে যোগ দিন, আপনার বন্ধুদের একটি Circle-এ নিয়ে আসুন, অথবা Matchmaker-এর মাধ্যমে আপনার সাথে মানানসই একটি টেবিলে আসন নিন।",
      "hm.landing.bottom_cta_circles": "একটি Circle শুরু করুন",
      "hm.landing.bottom_cta_mm": "Matchmaker কুইজটি দিন",
      "hm.circles.page_title": "HustleMeet Circles · আপনার দল, প্রতিবার নতুন ভেন্যু",
      "hm.circles.kicker": "HustleMeet · Circles",
      "hm.circles.hero_title": "আপনার পরিচিত দল। প্রতিবার নতুন টেবিল।",
      "hm.circles.hero_title_accent": "আপনার পরিচিত দল",
      "hm.circles.hero_sub": "আপনার বন্ধুদের দল কি আগে থেকেই আছে? তাদের সাথে ঘনিষ্ঠ থাকার সবচেয়ে সহজ উপায় হলো Circles। আমরা ভেন্যু বুক করি, ট্রিভিয়া প্ল্যান করি, কার্ডের ব্যবস্থা করি। আপনি শুধু উপস্থিত হবেন — একই দল, নতুন রাত, নতুন জায়গা।",
      "hm.circles.cta_find": "একটি Circle শুরু করুন",
      "hm.circles.cta_host_hero": "একটি ওপেন Circle-এ যোগ দিন",
      "hm.circles.trust_venue": "পরিবর্তনশীল যাচাইকৃত ভেন্যু",
      "hm.circles.trust_party": "প্রতি Circle-এ ৪-১২ জন বন্ধু",
      "hm.circles.trust_verified": "যাচাইকৃত সদস্য",
      "hm.circles.trust_friendship": "প্রকৃত বন্ধুত্ব, চিরস্থায়ী বন্ধন",
      "hm.circles.how_kicker": "এটি যেভাবে কাজ করে",
      "hm.circles.how_title": "চারটি ধাপ। আপনার দল সবসময় একসাথেই থাকবে।",
      "hm.circles.how_step1_ttl": "আপনার Circle শুরু করুন",
      "hm.circles.how_step1_sub": "একটি নাম দিন, আপনার বন্ধুদের আমন্ত্রণ জানান (১২ জন পর্যন্ত), এবং একটি ছন্দ বেছে নিন — সাপ্তাহিক, পাক্ষিক বা মাসিক।",
      "hm.circles.how_step2_ttl": "আমরা ভেন্যু বেছে নেব",
      "hm.circles.how_step2_sub": "প্রতিটি ইভেন্টে আমরা একটি নতুন জায়গার পরামর্শ দেব — রেস্তোরাঁ, ট্রিভিয়া নাইট, কার্ড রুম, ওয়াইন বার। অথবা আপনার পছন্দের কোনো জায়গা স্থির করুন।",
      "hm.circles.how_step3_ttl": "সবাই এক ট্যাপে RSVP করবে",
      "hm.circles.how_step3_sub": "অটো-রিমাইন্ডার, খরচ ভাগ করে নেওয়া, প্রিপেইড সিট যাতে শেষ মুহূর্তে কেউ না এলে প্ল্যান নষ্ট না হয়। স্টার্টার আসার আগেই বিলের ঝামেলা শেষ।",
      "hm.circles.how_step4_ttl": "ছন্দ বজায় রাখুন",
      "hm.circles.how_step4_sub": "ডায়েরিতে এমন একটি রাত যা আর কখনোই আলাদা করে আয়োজন করতে হবে না। দলটির ঘনিষ্ঠতা সময়ের সাথে আরও বাড়ে।",
      "hm.circles.cadence_weekly_ttl": "সাপ্তাহিক",
      "hm.circles.cadence_weekly_sub": "প্রতি সপ্তাহে",
      "hm.circles.cadence_weekly_body": "সেইসব দলের জন্য যারা প্রতি মঙ্গলবার রাতে আড্ডা দিতে চায় — একই পরিচিত মুখ, প্রতিবার নতুন জায়গা।",
      "hm.circles.cadence_fortnight_ttl": "পাক্ষিক",
      "hm.circles.cadence_fortnight_sub": "প্রতি ২ সপ্তাহে একবার",
      "hm.circles.cadence_fortnight_body": "একটি আদর্শ সময় — ব্যস্ততার মাঝেও নিয়মিত যোগাযোগ রাখার উপায়।",
      "hm.circles.cadence_monthly_ttl": "মাসিক",
      "hm.circles.cadence_monthly_sub": "প্রতি মাসে",
      "hm.circles.cadence_monthly_body": "বিশেষ কোনো আয়োজনের Circle — সানডে রোস্ট, বড় ট্রিভিয়া নাইট, ডেগাস্টেশন বা পোকার নাইট।",
      "hm.circles.feast_kicker": "আপনার রাত বেছে নিন",
      "hm.circles.feast_title": "সময় কাটানোর তিনটি উপায়",
      "hm.circles.feast_sub": "প্রতিটি Circle ঠিক করে রাতটি কীভাবে কাটবে। পরিবর্তন করে করে দেখুন, অথবা আপনার দলের পছন্দের বিষয়টি স্থির করুন।",
      "hm.circles.feast_surprise_ttl": "নতুন রেস্তোরাঁ ট্রাই করুন",
      "hm.circles.feast_surprise_body": "আমরা আপনার কাছের তিনটি ভেন্যুর তালিকা দেব, সবাই ভোট দেবে, কিচেন পুরো টেবিল তৈরি রাখবে। খরচ ভাগ করা এবং প্রিপেইড সিট।",
      "hm.circles.feast_preselect_ttl": "ট্রিভিয়া / কার্ড / গেমস",
      "hm.circles.feast_preselect_body": "ট্রিভিয়া নাইট, পোকার রুম, বোর্ড-গেম বার বা বোলিং লেন বুক করুন। আমরা ভেন্যু, এন্ট্রি ফি এবং রিমাইন্ডার সামলাব।",
      "hm.circles.feast_per_event_ttl": "এককালীন থিমযুক্ত রাত",
      "hm.circles.feast_per_event_body": "ওয়াইন টেস্টিং, শেফস টেবিল, ককটেল ক্লাস বা এস্কেপ রুম। মাঝেমধ্যে বিশেষ কিছু — যা সম্পূর্ণভাবে আয়োজিত।",
      "hm.circles.live_kicker": "এই মুহূর্তে",
      "hm.circles.live_title": "যেসব Circles-এ আপনি যোগ দিতে পারেন",
      "hm.circles.live_host_link": "আপনার নিজস্ব শুরু করুন",
      "hm.circles.live_loading": "লোড হচ্ছে…",
      "hm.circles.live_empty_title": "আপনার শহরের প্রথম Circle হয়ে উঠুন।",
      "hm.circles.live_empty_sub": "তিনজন বন্ধু নিয়ে একটি শুরু করুন এবং আমরা বাকিদের আমন্ত্রণ জানাতে সাহায্য করব — সাথে আপনার প্রথম তিনটি ভেন্যুর দায়িত্ব আমাদের।",
      "hm.circles.live_empty_cta": "একটি Circle শুরু করুন",
      "hm.circles.host_kicker": "Circle লিডারদের জন্য",
      "hm.circles.host_title": "আপনি বন্ধুদের নিয়ে আসুন। আয়োজনের দায়িত্ব আমাদের।",
      "hm.circles.host_body": "আপনি যদি সেই বন্ধু হন যে সবসময় আড্ডার আয়োজন করে, তবে Circles আপনার অনেক সময় বাঁচাবে। অটো-রিমাইন্ডার, প্রিপেইড RSVPs, ভেন্যুর তালিকা, খরচ ভাগ করা, ওয়েটলিস্ট ম্যানেজমেন্ট এবং প্রতিটি ইভেন্টে একটি ক্যাশব্যাক ওয়ালেট। আপনি শুধু একজন সাধারণ মেম্বার হিসেবে যোগ দেবেন।",
      "hm.circles.host_perk_1": "স্বয়ংক্রিয় ভেন্যু তালিকা — আর Google স্ক্রল করতে হবে না",
      "hm.circles.host_perk_2": "প্রিপেইড সিট — কেউ অনুপস্থিত থাকার ভয় নেই",
      "hm.circles.host_perk_3": "ডেজার্টের আগেই খরচ ভাগ করার কাজ শেষ",
      "hm.circles.host_perk_4": "অটোপাইলটে রিমাইন্ডার + ক্যালেন্ডার ইনভাইট",
      "hm.circles.host_perk_5": "কেউ বাতিল করলে অটো-প্রমোট সহ ওয়েটলিস্ট",
      "hm.circles.host_perk_6": "প্রতিটি ইভেন্টে ক্যাশব্যাক ওয়ালেট",
      "hm.circles.host_cta": "এখনই একটি Circle শুরু করুন",
      "hm.circles.why_kicker": "কেন Circles সফল",
      "hm.circles.why_title": "প্রাপ্তবয়স্কদের বন্ধুত্বের জন্য একটি কাঠামোর প্রয়োজন",
      "hm.circles.why_1_ttl": "একই দল, গভীর বিশ্বাস",
      "hm.circles.why_1_body": "জীবন বন্ধুদের দূরে সরিয়ে দেয়। Circles হলো একটি নির্দিষ্ট ছন্দ যা সবাইকে কাছাকাছি রাখে — একই ৪-১২ জন বন্ধু, একই দিনে দেখা করা, পরিস্থিতি যাই হোক না কেন।",
      "hm.circles.why_2_ttl": "আয়োজনের কোনো ক্লান্তি নেই",
      "hm.circles.why_2_body": "কাউকেই গ্রুপের WhatsApp চিফ হতে হবে না। আমরা ভেন্যু বেছে নিই, রিমাইন্ডার পাঠাই, পেমেন্ট নিই এবং সবকিছু গুছিয়ে রাখি।",
      "hm.circles.why_3_ttl": "নতুন ভেন্যু, নতুন স্মৃতি",
      "hm.circles.why_3_body": "Australia-র সেরা ছোট রেস্তোরাঁ, ট্রিভিয়া রুম এবং গেম বারগুলোতে ঘুরুন। পরিচিত মুখ, নতুন পরিবেশ — রুটিন এবং নতুনত্বের এক দারুণ সমন্বয়।",
      "hm.circles.bottom_title": "বন্ধুদের সাথে কাটানো একটি রাত যার প্রতীক্ষায় সবাই থাকে, কোনো পরিকল্পনা ছাড়াই।",
      "hm.circles.bottom_sub": "একটি Circle। একই বন্ধু। প্রতিবার নতুন ভেন্যু।",
      "hm.circles.bottom_cta_find": "একটি Circle শুরু করুন",
      "hm.circles.bottom_cta_mm": "Matchmaker-এর মাধ্যমে নতুন মানুষদের সাথে দেখা করুন →",
      "hm.circles.card_host_pill": "Circle",
      "hm.matchmaker.page_title": "HustleMeet Matchmaker · AI সঠিক টেবিল বেছে নেবে",
      "hm.matchmaker.kicker": "HustleMeet · Matchmaker",
      "hm.matchmaker.hero_title": "AI টেবিল বেছে নেবে। আপনি শুধু যোগ দেবেন।",
      "hm.matchmaker.hero_title_accent": "আপনি শুধু যোগ দেবেন।",
      "hm.matchmaker.hero_sub": "ব্যক্তিত্ব এবং মূল্যবোধের একটি ছোট কুইজের উত্তর দিন। আমাদের Matchmaker আপনাকে চার থেকে সাতজন অপরিচিত মানুষের সাথে বসিয়ে দেবে যারা ডেজার্ট শেষ হওয়ার আগেই পুরনো বন্ধু হয়ে উঠবে। কোনো সোয়াইপ নেই। কোনো অপ্রয়োজনীয় কথা নেই। শুধু একটি সুন্দর আড্ডা, যখন আপনি প্রস্তুত।",
      "hm.matchmaker.cta_quiz": "ম্যাচিং কুইজটি দিন",
      "hm.matchmaker.cta_see_circles": "পরিবর্তে Circles দেখুন →",
      "hm.matchmaker.trust_big5": "Big-5 ব্যক্তিত্ব স্কোর",
      "hm.matchmaker.trust_seats": "প্রতি টেবিলে ৪-৮টি সিট",
      "hm.matchmaker.trust_lock": "কোনো সোয়াইপ নয় · কোনো ফটো নয়",
      "hm.matchmaker.trust_verified": "শুধুমাত্র যাচাইকৃত সদস্য",
      "hm.matchmaker.how_kicker": "এটি যেভাবে কাজ করে",
      "hm.matchmaker.how_title": "সঠিক টেবিল খুঁজে পাওয়ার চারটি ধাপ।",
      "hm.matchmaker.how_step1_ttl": "কুইজটি দিন",
      "hm.matchmaker.how_step1_sub": "দশ মিনিটের প্রশ্নাবলী — ব্যক্তিত্ব, মূল্যবোধ, ডায়েটরি পছন্দ, প্রাপ্যতা। আপনি এটি একবারই করবেন।",
      "hm.matchmaker.how_step2_ttl": "রাতারাতি ম্যাচিং",
      "hm.matchmaker.how_step2_sub": "আমাদের AI আপনার প্রোফাইল এবং Australia-র লাইভ ডিনার তালিকা পর্যালোচনা করে। আপনার ফিট স্কোর ০.৭০ বা তার বেশি হলে এটি একটি ব্যক্তিগত আমন্ত্রণ তৈরি করে।",
      "hm.matchmaker.how_step3_ttl": "গ্রহণ করুন বা প্রত্যাখ্যান করুন",
      "hm.matchmaker.how_step3_sub": "আপনি ভেন্যু, টেবিলের থিম এবং গ্রুপের সাথে শেয়ার করা তিনটি সাধারণ আগ্রহ সহ একটি স্পষ্ট আমন্ত্রণ ইমেল পাবেন।",
      "hm.matchmaker.how_step4_ttl": "এটি আপনার থেকে শেখে",
      "hm.matchmaker.how_step4_sub": "প্রতিটি ডিনার-পরবর্তী সার্ভে মডেলটিকে আরও উন্নত করে। আপনি যত বেশি ডিনারে অংশ নেবেন, আপনার পরবর্তী ম্যাচ তত স্মার্ট হবে।",
      "hm.matchmaker.science_kicker": "কেন এটি কাজ করে",
      "hm.matchmaker.science_title": "Matchmaker অনুমান করে না — এটি স্কোর করে।",
      "hm.matchmaker.science_sub": "প্রতিটি ম্যাচ চারটি বিষয়ের একটি সম্মিলিত স্কোর। যখন পুরো টেবিলের গড় ০.৭০-এর উপরে থাকে, আমরা আমন্ত্রণ পাঠাই। এর নিচের স্কোরগুলো কিউতে থাকে।",
      "hm.matchmaker.science_1_ttl": "ব্যক্তিত্ব (৩৫%)",
      "hm.matchmaker.science_1_body": "Big-5 ব্যক্তিত্ব ভেক্টরের কোসাইন সাদৃশ্য — খোলামেলা ভাব, কর্তব্যনিষ্ঠা, বহির্মুখিতা, অমায়িকতা এবং আবেগীয় স্থিতিশীলতা।",
      "hm.matchmaker.science_2_ttl": "মূল্যবোধ (২৫%)",
      "hm.matchmaker.science_2_body": "যেসব মূল্যবোধ ডিনারের আড্ডাকে প্রাণবন্ত করে — পরিবার, কৌতূহল, উন্নতি, সেবা, অ্যাডভেঞ্চার।",
      "hm.matchmaker.science_3_ttl": "আগ্রহ (২৫%)",
      "hm.matchmaker.science_3_body": "আপনার নির্দিষ্ট আগ্রহের জ্যাকার্ড ওভারল্যাপ — Six Foot Track-এ হাইকিং, Sydney ফিল্ম ক্লাব, Melbourne কফি সিন, বা আপনার বছরের তৃতীয় বইটি।",
      "hm.matchmaker.science_4_ttl": "প্রাপ্যতা (১৫%)",
      "hm.matchmaker.science_4_body": "যে রাতে আপনি সত্যি উপস্থিত থাকতে পারবেন। আপনার ক্যালেন্ডারে যদি মঙ্গলবার রাতে জিম থাকে, তবে মঙ্গলবার সন্ধ্যা ৭টার আমন্ত্রণ পাঠানো হবে না।",
      "hm.matchmaker.safety_kicker": "পরিকল্পিত নিরাপত্তা",
      "hm.matchmaker.safety_title": "Matchmaker খুবই সতর্ক।",
      "hm.matchmaker.safety_1_ttl": "কনফিডেন্স থ্রেশহোল্ড",
      "hm.matchmaker.safety_1_body": "গ্রুপ স্কোর ০.৭০-এর নিচে হলে কোনো আমন্ত্রণ পাঠানো হয় না। একটি নড়বড়ে ম্যাচের চেয়ে ডিনার না হওয়াই ভালো।",
      "hm.matchmaker.safety_2_ttl": "মিউচুয়াল-নো ব্লক লিস্ট",
      "hm.matchmaker.safety_2_body": "যদি দুইজন সদস্য ব্যক্তিগতভাবে ডিনারের পর 'না' সংকেত দেন, আমরা তাদের আর কখনোই একসাথে বসাই না।",
      "hm.matchmaker.safety_3_ttl": "অটো-কিল সুইচ",
      "hm.matchmaker.safety_3_body": "যদি সাম্প্রতিক সার্ভেগুলোর ২০% বা তার বেশি কোনো টেবিলকে ২/৫ বা তার কম রেটিং দেয়, তবে অপারেটর পর্যালোচনা করার সময় পর্যন্ত ২৪ ঘণ্টার জন্য আমন্ত্রণ স্থগিত থাকে।",
      "hm.matchmaker.safety_4_ttl": "কমিউনিটি স্ট্যান্ডার্ডস",
      "hm.matchmaker.safety_4_body": "প্রতিটি সদস্য একটি সহজ কোড অফ কন্ডাক্ট মেনে চলতে বাধ্য থাকেন। যারা এটি ভঙ্গ করেন তাদের আমরা সরিয়ে দিই — কোনো আপিল ছাড়াই।",
      "hm.matchmaker.safety_5_ttl": "শুধুমাত্র পাবলিক ভেন্যু",
      "hm.matchmaker.safety_5_body": "প্রতিটি Matchmaker ডিনার স্টাফ সম্বলিত একটি আসল রেস্তোরাঁয় হয়। কখনোই কারো বাড়িতে নয়।",
      "hm.matchmaker.safety_6_ttl": "ইভেন্ট-পরবর্তী ফিডব্যাক",
      "hm.matchmaker.safety_6_body": "আপনার ব্যক্তিগত সার্ভে ভবিষ্যৎ ম্যাচগুলো নির্ধারণ করে এবং কোনো ভেন্যু বা সহকর্মীর মডারেটর রিভিউ শুরু করতে পারে।",
      "hm.matchmaker.loop_kicker": "লার্নিং লুপ",
      "hm.matchmaker.loop_title": "প্রতিটি ডিনার আপনার পরবর্তী ডিনারকে আরও উন্নত করে।",
      "hm.matchmaker.loop_body": "Matchmaker স্থির নয়। প্রতিটি ইভেন্টের পর আপনি একটি ছোট ব্যক্তিগত সার্ভে জমা দেন — কথোপকথন কেমন ছিল, কার সাথে আপনি আবার ডিনার করতে চান, ভেন্যু কেমন ছিল। এই সংকেতগুলো মডেলে ফিরে যায় এবং ভবিষ্যতের প্রতিটি ম্যাচকে নতুন রূপ দেয়।",
      "hm.matchmaker.loop_1": "অন্যান্য সদস্যদের \"আবার ডিনার করার\" ভোট আপনার পেয়ারিংকে শক্তিশালী করে",
      "hm.matchmaker.loop_2": "মিউচুয়াল-নো ভোট নীরবে পেয়ারগুলো সরিয়ে দেয়",
      "hm.matchmaker.loop_3": "ভেন্যু এবং কথোপকথন রেটিং সিট মিক্সকে উন্নত করে",
      "hm.matchmaker.loop_4": "নির্ভরযোগ্যতা স্কোর মেম্বারদের বারবার অনুপস্থিত হওয়া থেকে সুরক্ষা দেয়",
      "hm.matchmaker.loop_5": "লার্নিং বায়াস মাসের পর মাস ভালো ম্যাচগুলোকে আরও দৃঢ় করে",
      "hm.matchmaker.loop_6": "নিয়ন্ত্রণ আপনার হাতে — যেকোনো সময় আপনার প্রোফাইল অপ্ট-আউট বা রিসেট করুন",
      "hm.matchmaker.vs_kicker": "আমার জন্য কোনটি সঠিক?",
      "hm.matchmaker.vs_title": "Matchmaker নাকি Circles?",
      "hm.matchmaker.vs_mm_ttl": "আমাকে বেছে নিন যদি আপনি বৈচিত্র্য চান।",
      "hm.matchmaker.vs_mm_1": "প্রতিবার ভিন্ন টেবিল, শহর এবং খাবার",
      "hm.matchmaker.vs_mm_2": "AI-দ্বারা কিউরেট করা ৪-৮ জন সামঞ্জস্যপূর্ণ অপরিচিতের গ্রুপ",
      "hm.matchmaker.vs_mm_3": "সাপ্তাহিক ছন্দ, কোনো হোস্টের দায়বদ্ধতা নেই",
      "hm.matchmaker.vs_mm_4": "আপনি যদি শহরে নতুন হন বা ভ্রমণে থাকেন তবে এটি দারুণ",
      "hm.matchmaker.vs_mm_cta": "ম্যাচিং কুইজটি দিন",
      "hm.matchmaker.vs_circles_ttl": "আমাকে বেছে নিন যদি আপনি নিয়মিত হতে চান।",
      "hm.matchmaker.vs_circles_1": "একই হোস্ট · একই ভেন্যু · একই রাত",
      "hm.matchmaker.vs_circles_2": "এমন একটি গ্রুপ যা আপনার বুধবারের ঐতিহ্যে পরিণত হয়",
      "hm.matchmaker.vs_circles_3": "অভিজ্ঞ হোস্ট দ্বারা মানুষের মাধ্যমে কিউরেট করা",
      "hm.matchmaker.vs_circles_4": "আপনি যদি নতুন এসে থাকেন এবং স্থায়ী সম্পর্ক চান তবে এটি দারুণ",
      "hm.matchmaker.vs_circles_cta": "Circles ব্রাউজ করুন",
      "hm.matchmaker.bottom_title": "একটি কুইজ। এরপরের প্রতিটি ডিনার আপনার জন্য উপযুক্ত হবে।",
      "hm.matchmaker.bottom_sub": "আপনি পাক্ষিকের মধ্যেই ম্যাচ পেয়ে যাবেন। এটি আপনার এই মাসের সবচেয়ে সহজ \"হ্যাঁ\" হবে।",
      "hm.matchmaker.bottom_cta": "ম্যাচিং কুইজটি দিন",
      "hm.matchmaker.bottom_note": "কোনো ক্রেডিট কার্ড লাগবে না · ১০ মিনিট · যেকোনো সময় আনম্যাচ করুন",
      /* AUTO-TRANSLATED hm.* END */
    },
  },
  ta: {
    translation: {
      /* AUTO-TRANSLATED finder.* (added) */
      "finder.title": "HustleFinder",
      "finder.tagline": "அருகிலுள்ள பொது இடத்தைக் கண்டறியுங்கள் — உண்மையான தரவு, ஆஸ்திரேலியா முழுவதும்.",
      "finder.search_placeholder": "இடங்கள், தெருக்கள் அல்லது புறநகர்ப் பகுதிகளைத் தேடுங்கள்…",
      "finder.hint": "மேலே தட்டச்சு செய்யத் தொடங்குங்கள், வகையைத் தட்டவும் அல்லது \\\"என் அருகில்\\\" பயன்படுத்தவும்.",
      "finder.near_me": "என் அருகில்",
      "finder.searching": "தேடுகிறது…",
      "finder.suburb.placeholder": "புறநகர்ப் பகுதி மூலம் வடிகட்டவும் (எ.கா. Darwin, NT)…",
      "finder.suburb.clear": "அழி",
      "finder.search_in": "{{cat}} இல் தேடு",
      "finder.empty": "முடிவுகள் இல்லை — வேறு வகை அல்லது தேடல் சொல்லை முயற்சிக்கவும்.",
      "finder.meta.title": "HustleFinder — இலவச Aussie புறநகர் அடைவு",
      "finder.meta.desc": "உள்ளூர் வணிகங்கள், சார்ஜர்கள், கஃபேக்கள், கழிப்பறைகள் மற்றும் பலவற்றை ஒவ்வொரு ஆஸ்திரேலிய புறநகர்ப் பகுதியிலும் தேடுங்கள் — 100% இலவசம்.",
      "finder.footer.credits": "தரவு வரவுகள் & பண்புக்கூறு",
      "finder.footer.terms": "விதிமுறைகள்",
      "finder.cat.toilet": "கழிப்பறைகள்",
      "finder.cat.fountain": "குடிநீர் நீரூற்றுகள்",
      "finder.cat.transport": "போக்குவரத்து",
      "finder.cat.park": "பூங்காக்கள்",
      "finder.cat.medical": "மருத்துவம்",
      "finder.cat.school": "பள்ளிகள்",
      "finder.cat.library": "நூலகங்கள்",
      "finder.cat.petrol": "பெட்ரோல்",
      "finder.cat.kidzone": "குழந்தைகள் மண்டலம்",
      "finder.cat.puzzles": "புதிர்கள்",
      "finder.cat.roadtrip": "சாலைப் பயணம்",
      "finder.hotels.title": "அருகில் ஹோட்டலைக் கண்டறியுங்கள்",
      "finder.hotels.sub": "ஒரு தேடலில் 200+ முன்பதிவு தளங்களை ஒப்பிடுகிறது — Trivago மூலம் இயக்கப்படுகிறது.",
      "finder.hotels.where": "எங்கே",
      "finder.hotels.where_ph": "நகரம் அல்லது புறநகர் (எ.கா. Byron Bay, NSW)",
      "finder.hotels.checkin": "செக்-இன்",
      "finder.hotels.checkout": "செக்-அவுட்",
      "finder.hotels.adults": "பெரியவர்கள்",
      "finder.hotels.search": "ஹோட்டல்களைத் தேடு",
      "finder.hotels.disclosure": "Trivago திறக்கிறது — முன்பதிவுகளில் நாங்கள் கமிஷன் பெறலாம்.",
      /* AUTO-TRANSLATED rt.* START */
      "rt.brand_name": "Roadtrip Planner",
      "rt.page_title": "Aussie EV சாலைப்பயணத் திட்டம்",
      "rt.from": "புறப்படும் இடம்",
      "rt.to": "சேருமிடம்",
      "rt.from_placeholder": "புறநகர், நகரம் அல்லது மாநகரம் (உ.ம். Newcastle NSW)",
      "rt.to_placeholder": "புறநகர், நகரம் அல்லது மாநகரம் (உ.ம். Hobart TAS)",
      "rt.stop": "நிறுத்தம்",
      "rt.stop_placeholder": "இடைநிலை நிறுத்தம்",
      "rt.add_stop": "நிறுத்தத்தைச் சேர்",
      "rt.upgrade_pro": "20 நிறுத்தங்கள் வரை சேர்க்க Pro-விற்கு மேம்படுத்தவும்",
      "rt.reverse": "தலைகீழ் (வீட்டிற்குச் செல்லும் வழியைக் கணக்கிடு)",
      "rt.from_current": "தற்போதைய இடத்திலிருந்து",
      "rt.range_per_charge": "ஒரு மின்னேற்றத்திற்குச் செல்லும் தூரம் (கி.மீ)",
      "rt.connector": "இணைப்பான்",
      "rt.connector_any": "ஏதேனும்",
      "rt.drive_hours_day": "ஓட்டும் நேரம்/நாள்",
      "rt.accommodation": "தங்குமிடம்",
      "rt.accom_any": "ஏதேனும் (BnB · Hotel · Caravan)",
      "rt.accom_bnb": "BnB",
      "rt.accom_hotel": "Hotel",
      "rt.accom_caravan": "Caravan Park",
      "rt.min_rating": "குறைந்தபட்ச தரம்",
      "rt.any_rating": "எந்த தரமும்",
      "rt.only_5star": "5★ மட்டும்",
      "rt.schedule": "அட்டவணை:",
      "rt.depart_at": "புறப்படும் நேரம்",
      "rt.arrive_by": "சேரும் நேரம்",
      "rt.hotel_checkin": "Hotel செக்-இன்",
      "rt.hotel_checkout": "Hotel செக்-அவுட்",
      "rt.overnight_hint": "இரவு நேரப் பயணங்கள் இந்த நேரங்களுக்கு வெளியே திட்டமிடப்படாது.",
      "rt.whos_in_car": "காரில் யாரெல்லாம் உள்ளனர்?",
      "rt.adults": "பெரியவர்கள்",
      "rt.kids": "குழந்தைகள்",
      "rt.ages": "வயது",
      "rt.show_attractions": "சுற்றுலாத் தலங்களைக் காட்டு",
      "rt.show_kids": "குழந்தைகளுக்கு ஏற்ற இடங்களைக் காட்டு",
      "rt.overnight_stays": "இரவு தங்கல்கள்",
      "rt.fast_chargers_only": "ஃபாஸ்ட் சார்ஜர்கள் மட்டும்",
      "rt.hotel_must_charger": "ஹோட்டலில் சார்ஜர் இருக்க வேண்டும்",
      "rt.pet_only": "செல்லப்பிராணிகளுக்கு அனுமதி உள்ள இடங்கள் மட்டும்",
      "rt.safety": "பாதுகாப்பு",
      "rt.safety_90": "90% (சமதளம்)",
      "rt.safety_85": "85% (இயல்பு)",
      "rt.safety_80": "80% (மலைகள் / இழுவை)",
      "rt.safety_70": "70% (அதிக பாரம்)",
      "rt.save_trip": "பயணத்தைச் சேமி",
      "rt.plan_my_trip": "பயணத்தைத் திட்டமிடு",
      "rt.refresh_trip": "பயணத்தைப் புதுப்பி",
      "rt.planning": "திட்டமிடப்படுகிறது…",
      "rt.resume_title": "இங்கிருந்து பயணத்தைத் தொடரவும்",
      "rt.resume_body": "உங்கள் தற்போதைய இடத்தை நாங்கள் கண்டறிந்துள்ளோம். அடுத்த கட்டத்தைத் திட்டமிட, உங்கள் வாகனத்தில் உள்ள மீதமுள்ள தூரத்தை உள்ளிடவும் — முழு மின்னேற்றமாகக் கணக்கிட வேண்டாம்.",
      "rt.remaining_range": "மீதமுள்ள தூரம் (கி.மீ)",
      "rt.cancel": "ரத்து செய்",
      "rt.replan_from_here": "இங்கிருந்து மீண்டும் திட்டமிடு",
      "rt.save_modal_title": "இப்பயணத்திற்குப் பெயரிடவும்",
      "rt.save_modal_body": "சேமித்த பயணங்கள் உங்கள் சுயவிவரத்தில் இருக்கும் — எப்போது வேண்டுமானாலும் திறந்து 'மீண்டும் திட்டமிடு' என்பதை அழுத்தவும்.",
      "rt.save_name_placeholder": "உ.ம். Brisbane → Sydney via Byron",
      "rt.your_saved_trips": "உங்கள் சேமிக்கப்பட்ட பயணங்கள்",
      "rt.custom_location": "தனிப்பயன் இடம்",
      "rt.trip_summary": "பயணச் சுருக்கம்",
      "rt.itinerary": "பயண விவரம்",
      "rt.no_stops_needed": "நிறுத்தங்கள் தேவையில்லை — முழுப் பயணமும் உங்கள் வாகனத் திறனுக்குள்ளேயே அடங்கும்.",
      "rt.chargers_in_corridor": "வழித்தடத்தில் உள்ள சார்ஜர்கள்",
      "rt.planned_stops": "திட்டமிடப்பட்ட நிறுத்தங்கள்",
      "rt.overnight_stop": "இரவு தங்கும் நிறுத்தம்",
      "rt.overnight_stops": "இரவு தங்கும் நிறுத்தங்கள்",
      "rt.suggested_detours": "பரிந்துரைக்கப்படும் வழிமாற்றங்கள் — சேர்க்க டிக் செய்யவும்",
      "rt.replan_with_detours": "வழிமாற்றங்களுடன் மீண்டும் திட்டமிடு",
      "rt.attractions_label": "சுற்றுலாத் தலம்",
      "rt.kids_label": "குழந்தைகள்",
      "rt.km_from_start": "தொடக்கத்திலிருந்து கி.மீ",
      "rt.open_driving": "டிரைவிங் மோடைத் திற",
      "rt.big_font_hint": "பெரிய எழுத்துக்கள், ஃபோன் ஹோல்டருக்கு ஏற்றது. CarPlay/Android Auto பிரவுசர் மிரருடன் வேலை செய்யும்.",
      "rt.check_price_book": "விலையைச் சரிபார்த்து Trivago-இல் முன்பதிவு செய்யவும் ↗",
      "rt.visit_site": "இணையதளத்தைப் பார்க்க ↗",
      "rt.charger_onsite": "இங்கேயே சார்ஜர் உள்ளது",
      "rt.leg_exceeds_range": "இந்தப் பயணம் உங்கள் வாகனத்தின் வரம்பைத் தாண்டுகிறது — இந்த சார்ஜரை அடையும் முன்பே மின்னேற்றம் தீர்ந்துவிடும். வரம்பைக் குறைக்கவும் அல்லது ஒரு இடைநிலை சார்ஜிங்கைச் சேர்க்கவும்.",
      "rt.add_waypoint_replan": "வழிப்புள்ளியாகச் சேர்த்து மீண்டும் திட்டமிடு",
      /* AUTO-TRANSLATED rt.* END */
                      /* AUTO-TRANSLATED hm.* START */
      "hm.nav.hustlemeet": "HustleMeet",
      "hm.nav.circles": "Circles",
      "hm.nav.matchmaker": "Matchmaker",
      "hm.nav.meet": "சந்திப்பு",
      "hm.nav.overview": "HustleMeetups",
      "hm.overview.page_title": "HustleMeetups — உங்களுக்கானவர்களைக் கண்டறிய மூன்று இதமான வழிகள்.",
      "hm.overview.kicker": "HustleSuite-இன் ஒரு பகுதி",
      "hm.overview.hero_title": "உங்களுக்கானவர்களைக் கண்டறிய மூன்று இதமான வழிகள்.",
      "hm.overview.hero_title_accent": "உங்களுக்கானவர்கள்",
      "hm.overview.hero_sub": "HustleMeetups என்பது உண்மையான நட்பை உருவாக்குவதற்கான Australia-வின் கனிவான வழியாகும் — உங்களுக்குப் பிடித்தமான சூழலைத் தேர்ந்தெடுங்கள். வாராந்திர ஓட்டப் பயிற்சி குழுவோ, உங்கள் நண்பர்களுடன் மாதாந்திர விருந்தோ அல்லது AI மூலம் பொருத்தப்பட்ட புதிய நபர்களுடன் ஒரு மேஜையோ, எதுவாக இருந்தாலும் இங்கே இடம் உண்டு.",
      "hm.overview.tile_meet_kicker": "HustleMeet",
      "hm.overview.tile_meet_title": "உங்களுக்கானவர்களைக் கண்டறியுங்கள். பிடித்ததைச் செய்யுங்கள்.",
      "hm.overview.tile_meet_body": "ஆர்வங்களுக்கு முன்னுரிமை அளிக்கும் குழுக்களுக்கான Australia-வின் தளம். ஓடுபவர்கள், கோடிங் செய்பவர்கள், முதலீட்டாளர்கள், நடப்பவர்கள், நடனக் கலைஞர்கள், காலை உணவு கிளப்புகள், புத்தகக் குழுக்கள் என நூற்றுக்கணக்கான குழுக்கள், ஒவ்வொரு வாரமும் நிஜமான நிகழ்வுகள். ஒருமுறை வாருங்கள் அல்லது ஒவ்வொரு வாரமும் வாருங்கள்.",
      "hm.overview.tile_meet_cta": "குழுக்களை ஆராயுங்கள்",
      "hm.overview.tile_circles_kicker": "Circles",
      "hm.overview.tile_circles_title": "உங்கள் நண்பர்கள் குழு. ஒவ்வொரு முறையும் ஒரு புதிய மேஜை.",
      "hm.overview.tile_circles_body": "உங்கள் வழக்கமான நண்பர்கள் குழுவை அழைத்து வாருங்கள். நாங்கள் புதிய உணவகத்தை முன்பதிவு செய்வோம், ட்ரிவியா கேம்களைத் திட்டமிடுவோம், கார்டுகளைத் தயார் செய்வோம். நீங்கள் வந்து சேர்ந்தால் மட்டும் போதும். யாரும் மெனக்கெட்டு ஏற்பாடு செய்யத் தேவையில்லாமல், ஏற்கனவே இருக்கும் நண்பர்களுடன் நெருக்கமாக இருப்பதற்கான சிறந்த வழி இது.",
      "hm.overview.tile_circles_cta": "ஒரு Circle-ஐத் தொடங்குங்கள்",
      "hm.overview.tile_matchmaker_kicker": "Matchmaker",
      "hm.overview.tile_matchmaker_title": "AI மேஜையைத் தேர்ந்தெடுக்கும். நீங்கள் மட்டும் வாருங்கள்.",
      "hm.overview.tile_matchmaker_body": "10 நிமிட வினாடி வினாவில் பங்கேற்கவும். ஒரே இரவில், உணவு முடிவதற்குள் பழைய நண்பர்களைப் போல உணரவைக்கும் அந்நியர்களுடன் உங்களை நாங்கள் அமரவைப்போம். ஒவ்வொரு முறையும் வெவ்வேறு மேஜை.",
      "hm.overview.tile_matchmaker_cta": "வினாடி வினாவில் பங்கேற்கவும்",
      "hm.overview.trust_kicker": "இது ஏன் வேலை செய்கிறது",
      "hm.overview.trust_title": "முதலில் நம்பிக்கை. பிறகு மேஜை.",
      "hm.overview.trust_1_title": "சரிபார்க்கப்பட்ட உறுப்பினர்கள்",
      "hm.overview.trust_1_body": "ஒவ்வொரு குழுவும் உண்மையான ID சரிபார்ப்பு + நம்பகத்தன்மை மதிப்பெண்ணுடன் தொடங்குகிறது. தற்காலிகக் கணக்குகள் கிடையாது.",
      "hm.overview.trust_2_title": "உண்மையான தொகுப்பாளர்கள்",
      "hm.overview.trust_2_body": "ஒவ்வொரு குழுவையும் ஒரு சரிபார்க்கப்பட்ட தொகுப்பாளர் வழிநடத்துவார். அவர் அந்தச் சூழலைக் கவனித்து, நிகழ்வுகளை ஒழுங்கமைத்து, அனைவரையும் உபசரிப்பார்.",
      "hm.overview.trust_3_title": "நட்பிற்கே முன்னுரிமை",
      "hm.overview.trust_3_body": "இங்கே உள்ள அனைத்தும் உண்மையான நட்பிற்காக வடிவமைக்கப்பட்டுள்ளன, டேட்டிங்கிற்காக அல்ல. எப்போதும்.",
      "hm.overview.trust_4_title": "சகிப்புத்தன்மையற்ற மிதமானக் கொள்கை",
      "hm.overview.trust_4_body": "ஒரு புகார், ஒரு ஆய்வு, ஒரு முடிவு. தவறாக நடப்பவர்கள் அன்றைய இரவே நீக்கப்படுவார்கள்.",
      "hm.overview.footer_line": "HustleSuite-இன் ஒரு பகுதி — Australian உரிமையாளர் & தளம்.",
      "hm.landing.page_title": "HustleMeet · உங்களுக்கானவர்களைக் கண்டறியுங்கள். பிடித்ததைச் செய்யுங்கள்.",
      "hm.landing.kicker": "Australia-வின் ஆர்வங்களுக்கான நெட்வொர்க்",
      "hm.landing.hero_title": "உங்களுக்கானவர்களைக் கண்டறியுங்கள். பிடித்ததைச் செய்யுங்கள்.",
      "hm.landing.hero_title_accent": "உங்களுக்கானவர்கள்",
      "hm.landing.hero_sub": "HustleMeet என்பது ஆர்வங்களுக்கு முன்னுரிமை அளிக்கும் குழுக்களுக்கான Australia-வின் தளமாகும். மதிய உணவின் போது மென்பொருள் பிழைகளைத் திருத்தும் டெவலப்பர்கள். காலை 6 மணிக்கு நீண்ட தூரம் ஓடுபவர்கள். மாதந்தோறும் சந்திக்கும் முதலீட்டாளர்கள். நடைப்பயிற்சி குழுக்கள், நடனக் குழுக்கள், காலை உணவு கிளப்புகள், போர்டு கேம் இரவுகள். ஒரு குழுவைத் தேர்ந்தெடுத்து, அடுத்த நிகழ்வை முன்பதிவு செய்து, கலந்துகொள்ளுங்கள்.",
      "hm.landing.cta_explore_circles": "குழுக்களைப் பாருங்கள்",
      "hm.landing.cta_try_matchmaker": "சொந்தமாக ஒரு குழுவைத் தொடங்குங்கள்",
      "hm.landing.trust_verified": "சரிபார்க்கப்பட்ட உறுப்பினர்கள்",
      "hm.landing.trust_venues": "சரிபார்க்கப்பட்ட நிகழ்வு தொகுப்பாளர்கள்",
      "hm.landing.trust_friendship": "நட்பிற்கே முன்னுரிமை — டேட்டிங் கிடையாது",
      "hm.landing.trust_moderation": "சகிப்புத்தன்மையற்ற மிதமானக் கொள்கை",
      "hm.landing.subbrand_circles_kicker": "Circles",
      "hm.landing.subbrand_circles_title": "ஏற்கனவே நண்பர்கள் குழு உள்ளதா? அவர்களுக்கு ஒரு நிரந்தர இடத்தைக் கொடுங்கள்.",
      "hm.landing.subbrand_circles_body": "ஏற்கனவே சந்திக்கும் நண்பர்கள் குழுக்களுக்கானது Circles — புத்தகக் குழு, போக்கர் இரவு, ஓட்டப் பயிற்சிக் குழு அல்லது சக பணியாளர்கள். இடம், நினைவூட்டல்கள் மற்றும் பட்டியலை நாங்கள் கவனித்துக்கொள்கிறோம். நீங்கள் வந்து சேர்ந்தால் மட்டும் போதும்.",
      "hm.landing.subbrand_circles_b1": "மாறிக்கொண்டே இருக்கும் இடங்கள், ஒரே பட்டியல்",
      "hm.landing.subbrand_circles_b2": "தானியங்கி நினைவூட்டல்கள் + எளிதான RSVPs",
      "hm.landing.subbrand_circles_b3": "ட்ரிவியா, கார்டுகள் & தீம் இரவுகள் தயார் நிலையில்",
      "hm.landing.subbrand_circles_cta": "ஒரு Circle-ஐத் தொடங்குங்கள்",
      "hm.landing.subbrand_matchmaker_kicker": "Matchmaker",
      "hm.landing.subbrand_matchmaker_title": "AI மேஜையைத் தேர்ந்தெடுக்கும். நீங்கள் மட்டும் வாருங்கள்.",
      "hm.landing.subbrand_matchmaker_body": "ஒரு 10 நிமிட வினாடி வினா — ஆளுமை, மதிப்புகள், கிடைக்கும் நேரம். எங்களின் Matchmaker, உணவு முடிவதற்குள் பழைய நண்பர்களைப் போல உணரவைக்கும் அந்நியர்களுடன் உங்களை அமரவைக்கும். ஒவ்வொரு முறையும் வெவ்வேறு மேஜை.",
      "hm.landing.subbrand_matchmaker_b1": "Big-5 ஆளுமை & மதிப்புகள் கணக்கிடப்படும்",
      "hm.landing.subbrand_matchmaker_b2": "உங்கள் பொருத்தம் 0.70 அல்லது அதற்கு மேல் இருக்கும்போது அழைப்புகள் வரும்",
      "hm.landing.subbrand_matchmaker_b3": "ஒவ்வொரு விருந்திலிருந்தும் AI கற்றுக்கொள்ளும்",
      "hm.landing.subbrand_matchmaker_cta": "வினாடி வினாவில் பங்கேற்கவும்",
      "hm.landing.how_kicker": "HustleMeet எப்படி வேலை செய்கிறது",
      "hm.landing.how_title": "நான்கு படிகள். உங்கள் குழுவைக் கண்டறியுங்கள். கலந்துகொள்ளுங்கள்.",
      "hm.landing.how_step1_ttl": "உங்கள் ஆர்வங்களைத் தேர்ந்தெடுங்கள்",
      "hm.landing.how_step1_sub": "ஓட்டம், கோடிங், முதலீடு, சமையல், இசை, நடைப்பயிற்சி — உங்களுக்கு எது பிடிக்கும், எங்கே வசிக்கிறீர்கள் என்று எங்களிடம் சொல்லுங்கள்.",
      "hm.landing.how_step2_ttl": "உங்களுக்கு அருகிலுள்ள குழுக்களைப் பாருங்கள்",
      "hm.landing.how_step2_sub": "ஒவ்வொரு குழுவும் அதன் தொகுப்பாளர், அதன் நடைமுறை மற்றும் அதன் அடுத்த நிகழ்வை வெளியிடும். சேருவதற்கு முன் அதைப் பற்றி அறிந்துகொள்ளுங்கள்.",
      "hm.landing.how_step3_ttl": "அடுத்த நிகழ்விற்கு RSVP செய்யுங்கள்",
      "hm.landing.how_step3_sub": "ஒரு கிளிக், ஒரு காலண்டர் அழைப்பு, ஒரு நாள் முன்னதாக தெளிவான நினைவூட்டல். சில நிகழ்வுகள் இலவசம், சில கட்டணம் வசூலிக்கப்படும் — விலை வெளிப்படையாகக் குறிப்பிடப்பட்டிருக்கும்.",
      "hm.landing.how_step4_ttl": "வாருங்கள் & இணையுங்கள்",
      "hm.landing.how_step4_sub": "தொகுப்பாளரைச் சந்தியுங்கள், குழுவைச் சந்தியுங்கள், பிடித்ததைச் செய்யுங்கள். உங்களுக்குப் பிடித்திருந்தால் அடுத்த வாரம் மீண்டும் வாருங்கள்.",
      "hm.landing.stats_kicker": "சந்திப்பதற்கான Australia-வின் புதிய வழி",
      "hm.landing.stats_title": "மக்கள் மீண்டும் மீண்டும் வருவதற்கு இதுவே காரணம்.",
      "hm.landing.stats_92_sub": "நிகழ்வில் குறைந்தபட்சம் ஒருவருடனாவது தங்களுக்கு நல்ல நெருக்கம் ஏற்பட்டதாக உணர்கிறார்கள்",
      "hm.landing.stats_68_sub": "ஒரு வாரத்திற்குள் தங்களின் அடுத்த நிகழ்விற்கு RSVP செய்கிறார்கள்",
      "hm.landing.stats_85_sub": "குழுவிலுள்ள ஒருவருடன் தொடர்பில் இருக்கிறார்கள்",
      "hm.landing.why_kicker": "நாங்கள் ஏன் சிறந்தவர்கள்",
      "hm.landing.why_title": "உண்மையான ஆர்வங்கள். உண்மையான தொகுப்பாளர்கள். உண்மையான நிகழ்வுகள்.",
      "hm.landing.why_1_ttl": "இடத்திற்கு அல்ல, ஆர்வத்திற்கே முன்னுரிமை",
      "hm.landing.why_1_body": "உங்கள் குழுவிலுள்ளவர்கள் நீங்கள் விரும்பும் விஷயத்தை உண்மையிலேயே விரும்புவார்கள் — இதுவே உண்மையான நட்புக்கான குறுக்குவழி.",
      "hm.landing.why_2_ttl": "உண்மையான நிகழ்வு தொகுப்பாளர்கள்",
      "hm.landing.why_2_body": "ஒவ்வொரு குழுவிற்கும் ஒரு சரிபார்க்கப்பட்ட தொகுப்பாளர் இருப்பார். அவர் சூழலைக் கவனித்து, ஒழுங்குபடுத்துவார். அவர் வெறும் Facebook அட்மின் அல்ல. ஒரு உண்மையான நபர்.",
      "hm.landing.why_3_ttl": "தொடர்ச்சி முக்கியமானது",
      "hm.landing.why_3_body": "ஒவ்வொரு வாரமும் புதிய நிகழ்வுகள். ஆவலுடன் காத்திருக்க ஏதோ ஒன்று. அந்நியர்களை உங்கள் செவ்வாய் இரவு நண்பர்களாக மாற்றும் ஒரு தொடர்ச்சி.",
      "hm.landing.why_4_ttl": "பாதுகாப்பானது & உறுதியானது",
      "hm.landing.why_4_body": "சரிபார்க்கப்பட்ட உறுப்பினர்கள், பொதுவான இடங்கள், அகௌரவத்திற்கு இடமில்லை. ஒவ்வொரு நிகழ்விற்கு முன்பும், நிகழ்வின் போதும், பின்பும் எங்களது ஆதரவு குழு உங்களுக்காக இருக்கும்.",
      "hm.landing.why_5_ttl": "நட்பிற்கே முன்னுரிமை",
      "hm.landing.why_5_body": "இது டேட்டிங் அல்ல. இது ஒரு சமூகம். உண்மையான ஆர்வங்கள், உண்மையான மக்கள், இடையில் ஸ்வைப்பிங் கிடையாது.",
      "hm.landing.why_6_ttl": "முக்கியத்துவம் அளிக்க சிறிய குழுக்கள்",
      "hm.landing.why_6_body": "பெரும்பாலான குழுக்கள் 4-15 இருக்கைகள் கொண்ட நிகழ்வுகளை மட்டுமே நடத்துகின்றன. ஆழமான உரையாடல்களைத் தொடங்க போதுமான அளவு பெரியது, அதே சமயம் யாரும் தனித்து விடப்படாத அளவுக்கு சிறியது.",
      "hm.landing.cities_kicker": "நாங்கள் எங்கெல்லாம் இருக்கிறோம்",
      "hm.landing.cities_title": "Australia & NZ முழுவதும் சந்திக்கும் குழுக்கள்.",
      "hm.landing.cities_sub_zero": "15+ நகரங்களில் முதல் குழுக்கள் தொடங்குகின்றன — முன்கூட்டியே இணைந்து சூழலை உருவாக்குங்கள்.",
      "hm.landing.cities_sub_some": "15+ பெருநகரங்களில் குழுக்கள் உள்ளன. இந்த வாரம் உங்கள் நகரத்தில் ஏதோ ஒன்று நடக்கும்.",
      "hm.landing.cities_sub_one": "{{count}} நேரடி குழு · ஒவ்வொரு வாரமும் புதிய நிகழ்வுகள் · தொடர்ந்து நகரங்களைச் சேர்த்துக் கொண்டிருக்கிறோம்.",
      "hm.landing.cities_sub_many": "{{count}} நேரடி குழுக்கள் · ஒவ்வொரு வாரமும் புதிய நிகழ்வுகள் · தொடர்ந்து நகரங்களைச் சேர்த்துக் கொண்டிருக்கிறோம்.",
      "hm.landing.forme_kicker": "இது உங்களுக்கானது என்றால்…",
      "hm.landing.forme_title": "உங்களுக்கு உண்மையான ஆர்வங்கள், உண்மையான மக்கள் மற்றும் உண்மையான நிகழ்வுகள் வேண்டும்.",
      "hm.landing.forme_1": "நீங்கள் ஸ்வைப் செய்யும் ஆப்ஸ்களால் சோர்வடைந்து, உங்களுக்குப் பிடித்தமான விஷயங்களின் மூலம் மக்களைச் சந்திக்க விரும்புகிறீர்கள்.",
      "hm.landing.forme_2": "நீங்கள் ஒரு புதிய நகரத்திற்கு குடிபெயர்ந்துள்ளீர்கள், உங்களுக்குத் தேவை ஒரு நல்ல நண்பர்கள் குழு, மற்றொரு சாட் குரூப் அல்ல.",
      "hm.landing.forme_3": "வெறும் பரந்த தொடர்புகளை விட ஆழமான நட்பை மதிக்கிறீர்கள் — ஐம்பது ஃபாலோயர்களை விட ஒரு சிறந்த குழு சிறந்தது.",
      "hm.landing.forme_4": "பகிரப்பட்ட ஆர்வங்கள் + நிஜ வாழ்க்கையில் செலவிடும் இரண்டு மணிநேரம் தான் உண்மையான நட்புக்கான வழி என்று நம்புகிறீர்கள்.",
      "hm.landing.notme_kicker": "இது உங்களுக்கானது அல்ல என்றால்…",
      "hm.landing.notme_title": "HustleMeet அனைவருக்கும் ஏற்றது அல்ல.",
      "hm.landing.notme_1": "உங்களால் வருவதாக உறுதியளிக்க முடியாது — தொகுப்பாளர்களும் உறுப்பினர்களும் உங்கள் RSVP-ஐ நம்பியிருக்கிறார்கள்.",
      "hm.landing.notme_2": "நீங்கள் ஒரு டேட்டிங் சேவையைத் தேடுகிறீர்கள் — இங்கே நட்பே முதன்மையானது.",
      "hm.landing.notme_3": "யாரையும் நேரில் சந்திக்காமல் கருத்துக்களைப் பகிர விரும்புகிறீர்கள் — இது நேரில் சந்தித்தால் மட்டுமே வேலை செய்யும்.",
      "hm.landing.notme_4": "முடிவில்லாத இலவசச் சலுகைகளை எதிர்பார்க்கிறீர்கள் — சில நிகழ்வுகளுக்குக் கட்டணம் உண்டு. விலை எப்போதும் வெளிப்படையாக இருக்கும்.",
      "hm.landing.safety_kicker": "பாதுகாப்பிற்கான எங்களது உறுதிப்பாடு",
      "hm.landing.safety_title": "இதமான சூழல், ஆனால் ஒருபோதும் கவனக்குறைவு இல்லை.",
      "hm.landing.safety_1_ttl": "பொதுவான இடங்கள்",
      "hm.landing.safety_1_body": "ஒவ்வொரு நிகழ்வும் ஒரு கஃபே, உணவகம், பூங்கா, ஜிம் அல்லது ஸ்டுடியோ போன்ற பொதுவான, பணியாளர்கள் உள்ள இடங்களில் நடக்கும். பங்கேற்பாளர்கள் அனைவரும் சம்மதித்தால் ஒழிய ஒருபோதும் ஒருவரின் வீட்டில் நடக்காது.",
      "hm.landing.safety_2_ttl": "சமூகத் தரநிலைகள்",
      "hm.landing.safety_2_body": "மரியாதைக்குறைவு, துன்புறுத்தல் அல்லது நேர்மையின்மைக்கு சகிப்புத்தன்மை கிடையாது. விதிகளை மீறும் உறுப்பினர்கள் உடனடியாக நீக்கப்படுவார்கள்.",
      "hm.landing.safety_3_ttl": "தனியுரிமை பாதுகாப்பு",
      "hm.landing.safety_3_body": "உங்கள் விவரங்கள் ஒருபோதும் பொதுவில் பகிரப்படாது. ஒவ்வொரு நிகழ்விற்கும் பிறகு நீங்கள் யாருடன் தொடர்பில் இருக்க வேண்டும் என்பதை நீங்களே தீர்மானிக்கலாம்.",
      "hm.landing.safety_4_ttl": "ஆதரவு தயார் நிலையில்",
      "hm.landing.safety_4_body": "உங்களுக்கு ஏதேனும் கேள்விகள் அல்லது கவலைகள் இருந்தால், ஒவ்வொரு நிகழ்விற்கு முன்பும், நிகழ்வின் போதும், பின்பும் எங்களது குழு உங்களுக்கு உதவும்.",
      "hm.landing.bottom_title": "உங்களுக்குப் பிடித்த அடுத்த குழு ஒரு கிளிக் தூரத்தில் உள்ளது.",
      "hm.landing.bottom_sub": "உங்கள் வழியைத் தேர்ந்தெடுங்கள் — இருக்கும் குழுவில் இணையுங்கள், உங்கள் நண்பர்களை ஒரு Circle-க்குள் அழைத்து வாருங்கள் அல்லது Matchmaker மூலம் உங்களுக்குப் பொருத்தமான மேஜையில் இணையுங்கள்.",
      "hm.landing.bottom_cta_circles": "ஒரு Circle-ஐத் தொடங்குங்கள்",
      "hm.landing.bottom_cta_mm": "Matchmaker வினாடி வினாவில் பங்கேற்கவும்",
      "hm.circles.page_title": "HustleMeet Circles · உங்கள் நண்பர்கள் குழு, ஒவ்வொரு முறையும் புதிய இடம்",
      "hm.circles.kicker": "HustleMeet · Circles",
      "hm.circles.hero_title": "உங்கள் நண்பர்கள் குழு. ஒவ்வொரு முறையும் ஒரு புதிய மேஜை.",
      "hm.circles.hero_title_accent": "உங்கள் நண்பர்கள் குழு",
      "hm.circles.hero_sub": "ஏற்கனவே நண்பர்கள் உள்ளார்களா? அவர்களைத் தொடர்ந்து சந்திக்க Circles ஒரு எளிய வழி. நாங்கள் இடத்தை முன்பதிவு செய்வோம், ட்ரிவியாவைத் திட்டமிடுவோம், கார்டுகளைத் தயார் செய்வோம். நீங்கள் வந்து சேர்ந்தால் மட்டும் போதும் — அதே நண்பர்கள் குழு, புதிய இரவு, புதிய இடம்.",
      "hm.circles.cta_find": "ஒரு Circle-ஐத் தொடங்குங்கள்",
      "hm.circles.cta_host_hero": "திறந்திருக்கும் ஒரு Circle-இல் இணையுங்கள்",
      "hm.circles.trust_venue": "மாறிக்கொண்டே இருக்கும் சரிபார்க்கப்பட்ட இடங்கள்",
      "hm.circles.trust_party": "ஒரு Circle-க்கு 4-12 நண்பர்கள்",
      "hm.circles.trust_verified": "சரிபார்க்கப்பட்ட உறுப்பினர்கள்",
      "hm.circles.trust_friendship": "உண்மையான நட்பு, என்றும் நெருக்கமாக",
      "hm.circles.how_kicker": "இது எப்படி வேலை செய்கிறது",
      "hm.circles.how_title": "நான்கு படிகள். உங்கள் நண்பர்கள் குழுவாகவே இருப்பார்கள்.",
      "hm.circles.how_step1_ttl": "உங்கள் Circle-ஐத் தொடங்குங்கள்",
      "hm.circles.how_step1_sub": "அதற்கு ஒரு பெயரிடுங்கள், உங்கள் நண்பர்களை (12 பேர் வரை) அழையுங்கள், ஒரு கால இடைவெளியைத் தேர்ந்தெடுக்கவும் — வாராந்திர, இரு வாரங்களுக்கு ஒருமுறை அல்லது மாதாந்திரம்.",
      "hm.circles.how_step2_ttl": "நாங்கள் இடத்தைத் தேர்ந்தெடுப்போம்",
      "hm.circles.how_step2_sub": "ஒவ்வொரு நிகழ்விற்கும் ஒரு புதிய இடத்தை நாங்கள் பரிந்துரைப்போம் — உணவகம், ட்ரிவியா இரவு, கார்டு ரூம், ஒயின் பார். அல்லது உங்களுக்குப் பிடித்த இடத்தையே உறுதி செய்யலாம்.",
      "hm.circles.how_step3_ttl": "அனைவரும் ஒரு கிளிக்கில் RSVP செய்யலாம்",
      "hm.circles.how_step3_sub": "தானியங்கி நினைவூட்டல்கள், செலவுப் பிரிப்பு, முன்கூட்டியே பணம் செலுத்திய இருக்கைகள் என எதுவும் திட்டத்தைக் கெடுக்காது. உணவு வருவதற்கு முன்பே பில் செட்டில் செய்யப்படும்.",
      "hm.circles.how_step4_ttl": "தொடர்ச்சியைப் பேணுங்கள்",
      "hm.circles.how_step4_sub": "டைரியில் குறிக்கப்பட்ட அந்த ஒரு இரவு, இனி ஒருபோதும் மீண்டும் திட்டமிடப்பட வேண்டியதில்லை. சில நிமிடங்களில் அல்ல, மாதக்கணக்கில் அந்த குழு நெருக்கமாகும்.",
      "hm.circles.cadence_weekly_ttl": "வாராந்திர",
      "hm.circles.cadence_weekly_sub": "ஒவ்வொரு வாரமும்",
      "hm.circles.cadence_weekly_body": "செவ்வாய் இரவு சந்திப்பை ஒரு சடங்காக மாற்ற விரும்பும் நண்பர்களுக்காக — அதே முகங்கள், ஒவ்வொரு முறையும் புதிய இடம்.",
      "hm.circles.cadence_fortnight_ttl": "இரு வாரங்களுக்கு ஒருமுறை",
      "hm.circles.cadence_fortnight_sub": "ஒவ்வொரு 2 வாரமும்",
      "hm.circles.cadence_fortnight_body": "சரியான இடைவெளி — பிஸியான நேரத்திலும் வழக்கமாகச் சந்திக்க ஏற்றது.",
      "hm.circles.cadence_monthly_ttl": "மாதாந்திர",
      "hm.circles.cadence_monthly_sub": "ஒவ்வொரு மாதமும்",
      "hm.circles.cadence_monthly_body": "விசேஷமான சந்தர்ப்பங்களுக்கான Circles — ஞாயிறு விருந்துகள், பெரிய ட்ரிவியா இரவுகள், டெகஸ்டேஷன், போக்கர்.",
      "hm.circles.feast_kicker": "உங்கள் இரவைத் தேர்ந்தெடுங்கள்",
      "hm.circles.feast_title": "செலவிட மூன்று வழிகள்",
      "hm.circles.feast_sub": "ஒவ்வொரு Circle-உம் இரவு எப்படி அமைய வேண்டும் என்பதைத் தீர்மானிக்கிறது. அவற்றை மாற்றிக் கொண்டே இருக்கலாம் அல்லது நிலையான ஒன்றைத் தேர்ந்தெடுக்கலாம்.",
      "hm.circles.feast_surprise_ttl": "புதிய உணவகத்தை முயற்சி செய்யுங்கள்",
      "hm.circles.feast_surprise_body": "உங்களுக்கு அருகிலுள்ள மூன்று உணவகங்களைப் பட்டியலிடுவோம், அனைவரும் வாக்களிக்கலாம், சமையலறை தயாராகும். செலவுப் பிரிப்பு, முன்கூட்டியே முன்பதிவு செய்த இருக்கைகள்.",
      "hm.circles.feast_preselect_ttl": "ட்ரிவியா / கார்டுகள் / கேம்கள்",
      "hm.circles.feast_preselect_body": "ட்ரிவியா இரவு, போக்கர் ரூம், போர்டு கேம் பார் அல்லது பந்துவீச்சுப் பாதையை முன்பதிவு செய்யுங்கள். இடம், நுழைவுக்கட்டணம் மற்றும் நினைவூட்டல்களை நாங்கள் கவனித்துக்கொள்கிறோம்.",
      "hm.circles.feast_per_event_ttl": "ஒரு முறை மட்டும் நடக்கும் தீம் இரவு",
      "hm.circles.feast_per_event_body": "ஒயின் ருசித்தல், செஃப்ஸ் டேபிள், காக்டெய்ல் வகுப்பு, எஸ்கேப் ரூம். எப்போதாவது ஒருமுறை விசேஷமான ஒன்று — முழுமையாக ஏற்பாடு செய்யப்பட்டது.",
      "hm.circles.live_kicker": "இப்போது",
      "hm.circles.live_title": "நீங்கள் இணையக்கூடிய Circles",
      "hm.circles.live_host_link": "உங்களுடையதைத் தொடங்குங்கள்",
      "hm.circles.live_loading": "ஏற்றப்படுகிறது…",
      "hm.circles.live_empty_title": "உங்கள் நகரத்தில் முதல் Circle-ஆக இருங்கள்.",
      "hm.circles.live_empty_sub": "மூன்று நண்பர்களுடன் ஒன்றைத் தொடங்குங்கள், மற்றவர்களை அழைக்க நாங்கள் உதவுகிறோம் — மேலும் முதல் மூன்று இடங்களுக்கான செலவை நாங்கள் ஏற்கிறோம்.",
      "hm.circles.live_empty_cta": "ஒரு Circle-ஐத் தொடங்குங்கள்",
      "hm.circles.host_kicker": "Circle தலைவர்களுக்கு",
      "hm.circles.host_title": "நண்பர்களை நீங்கள் அழைத்து வாருங்கள். நிர்வாகத்தை நாங்கள் செய்கிறோம்.",
      "hm.circles.host_body": "நீங்கள்தான் எப்போதும் திட்டமிடும் நண்பர் என்றால், Circles உங்கள் நேரத்தை மிச்சப்படுத்தும். தானியங்கி நினைவூட்டல்கள், முன்கூட்டியே பணம் செலுத்திய RSVPs, இடங்களின் பட்டியல், செலவுப் பிரிப்பு, காத்திருப்போர் பட்டியல் மேலாண்மை மற்றும் ஒவ்வொரு இரவு வெளியூட்டிற்கும் கேஷ்பேக் வாலட். நீங்கள் ஒரு சாதாரண உறுப்பினராக மட்டும் கலந்துகொண்டால் போதும்.",
      "hm.circles.host_perk_1": "தானியங்கி இடங்களின் பட்டியல் — இனி Google-இல் தேட வேண்டாம்",
      "hm.circles.host_perk_2": "முன்கூட்டியே பணம் செலுத்திய இருக்கைகள் — யாரும் வராமல் போகாது",
      "hm.circles.host_perk_3": "டெசர்ட்டிற்கு முன்பே செலவுப் பிரிப்பு முடிந்தது",
      "hm.circles.host_perk_4": "நினைவூட்டல்கள் + காலண்டர் அழைப்புகள் தானாக நடக்கும்",
      "hm.circles.host_perk_5": "யாராவது ரத்து செய்தால் தானாக முன்னுரிமை அளிக்கும் காத்திருப்போர் பட்டியல்",
      "hm.circles.host_perk_6": "ஒவ்வொரு இரவு வெளியூட்டிற்கும் கேஷ்பேக் வாலட்",
      "hm.circles.host_cta": "இப்போதே ஒரு Circle-ஐத் தொடங்குங்கள்",
      "hm.circles.why_kicker": "Circles ஏன் வேலை செய்கிறது",
      "hm.circles.why_title": "பெரியவர்களின் நட்புக்கு ஒரு கட்டமைப்பு தேவை",
      "hm.circles.why_1_ttl": "அதே நண்பர்கள், ஆழமான நம்பிக்கை",
      "hm.circles.why_1_body": "வாழ்க்கை நண்பர்களைப் பிரித்துவிடும். Circles என்பது அனைவரையும் நெருக்கமாக வைத்திருக்கும் ஒரு நிலையான தொடர்ச்சி — அதே 4-12 நண்பர்கள், அதே இரவு நேரத்தில், எது நடந்தாலும் சந்திக்கலாம்.",
      "hm.circles.why_2_ttl": "நிர்வாகச் சோர்வு இல்லை",
      "hm.circles.why_2_body": "யாரும் WhatsApp குரூப் அட்மினாக இருக்கத் தேவையில்லை. நாங்கள் இடங்களைத் தேர்ந்தெடுத்து, நினைவூட்டல்களை அனுப்பி, கட்டணங்களைப் பெற்று, அனைத்தையும் கவனித்துக்கொள்கிறோம்.",
      "hm.circles.why_3_ttl": "புதிய இடம், புதிய நினைவு",
      "hm.circles.why_3_body": "Australia-வின் சிறந்த சிறிய உணவகங்கள், ட்ரிவியா ரூம்கள் மற்றும் கேம் பார்களுக்குச் செல்லுங்கள். தெரிந்த முகங்கள், புதிய இடங்கள் — வழக்கமான நடைமுறை மற்றும் புதுமையின் சரியான கலவை.",
      "hm.circles.bottom_title": "யாரும் திட்டமிடத் தேவையில்லாமல், உங்கள் நண்பர்கள் ஆவலுடன் காத்திருக்கும் ஒரு இரவு.",
      "hm.circles.bottom_sub": "ஒரு Circle. அதே நண்பர்கள். ஒவ்வொரு முறையும் புதிய இடம்.",
      "hm.circles.bottom_cta_find": "ஒரு Circle-ஐத் தொடங்குங்கள்",
      "hm.circles.bottom_cta_mm": "Matchmaker மூலம் புதியவர்களைச் சந்தியுங்கள் →",
      "hm.circles.card_host_pill": "Circle",
      "hm.matchmaker.page_title": "HustleMeet Matchmaker · AI சரியான மேஜையைத் தேர்ந்தெடுக்கும்",
      "hm.matchmaker.kicker": "HustleMeet · Matchmaker",
      "hm.matchmaker.hero_title": "AI மேஜையைத் தேர்ந்தெடுக்கும். நீங்கள் மட்டும் வாருங்கள்.",
      "hm.matchmaker.hero_title_accent": "நீங்கள் மட்டும் வாருங்கள்.",
      "hm.matchmaker.hero_sub": "ஆளுமை மற்றும் மதிப்புகள் குறித்த ஒரு சிறிய வினாடி வினாவிற்குப் பதிலளிக்கவும். எங்களின் Matchmaker, டெசர்ட் சாப்பிடும் நேரத்திற்குள் பழைய நண்பர்களைப் போல உணரவைக்கும் நான்கு முதல் ஏழு அந்நியர்களுடன் உங்களை அமரவைக்கும். ஸ்வைப்பிங் இல்லை. வெற்றுப் பேச்சுகள் இல்லை. நீங்கள் தயாராக இருக்கும்போது உங்களுக்கான ஒரு மேஜை.",
      "hm.matchmaker.cta_quiz": "பொருத்தத்திற்கான வினாடி வினாவில் பங்கேற்கவும்",
      "hm.matchmaker.cta_see_circles": "அதற்குப் பதிலாக Circles-ஐப் பாருங்கள் →",
      "hm.matchmaker.trust_big5": "Big-5 ஆளுமை கணக்கிடப்பட்டது",
      "hm.matchmaker.trust_seats": "ஒரு மேஜைக்கு 4-8 இருக்கைகள்",
      "hm.matchmaker.trust_lock": "ஸ்வைப்பிங் இல்லை · புகைப்படம் இல்லை",
      "hm.matchmaker.trust_verified": "சரிபார்க்கப்பட்ட உறுப்பினர்கள் மட்டுமே",
      "hm.matchmaker.how_kicker": "இது எப்படி வேலை செய்கிறது",
      "hm.matchmaker.how_title": "உங்களுக்குப் பொருத்தமான ஒரு மேஜையை அடைய நான்கு படிகள்.",
      "hm.matchmaker.how_step1_ttl": "வினாடி வினாவில் பங்கேற்கவும்",
      "hm.matchmaker.how_step1_sub": "பத்து நிமிட வினாடி வினா — ஆளுமை, மதிப்புகள், உணவுப் பழக்கம், கிடைக்கும் நேரம். நீங்கள் இதை ஒருமுறை மட்டும் செய்தால் போதும்.",
      "hm.matchmaker.how_step2_ttl": "ஒரே இரவில் பொருத்தம்",
      "hm.matchmaker.how_step2_sub": "எங்கள் AI உங்கள் ப்ரொஃபைல் மற்றும் Australia-வின் நேரடி டின்னர் பட்டியலைப் படிக்கும். உங்கள் பொருத்த மதிப்பெண் 0.70 அல்லது அதற்கு மேல் இருக்கும்போது, அது தனிப்பட்ட அழைப்பைத் தயாரிக்கும்.",
      "hm.matchmaker.how_step3_ttl": "ஏற்கவும் அல்லது நிராகரிக்கவும்",
      "hm.matchmaker.how_step3_sub": "இடம், மேஜையின் கருப்பொருள் மற்றும் குழுவுடன் நீங்கள் பகிர்ந்து கொள்ளும் மூன்று ஆர்வங்கள் அடங்கிய ஒரு தெளிவான அழைப்பு மின்னஞ்சலைப் பெறுவீர்கள்.",
      "hm.matchmaker.how_step4_ttl": "அது உங்களிடமிருந்து கற்றுக்கொள்ளும்",
      "hm.matchmaker.how_step4_sub": "ஒவ்வொரு டின்னருக்குப் பிந்தைய கருத்துக்கணிப்பும் மாடலைச் சீரமைக்கும். நீங்கள் அடிக்கடி உணவருந்தச் செல்லும்போது உங்கள் அடுத்த பொருத்தம் இன்னும் துல்லியமாக இருக்கும்.",
      "hm.matchmaker.science_kicker": "ஏன் இது வேலை செய்கிறது",
      "hm.matchmaker.science_title": "Matchmaker யூகிப்பதில்லை — அது மதிப்பெண் அளிக்கிறது.",
      "hm.matchmaker.science_sub": "ஒவ்வொரு பொருத்தமும் நான்கு காரணிகளின் கலவையாகும். முழு மேஜையின் சராசரி 0.70-க்கு மேல் இருக்கும்போது, நாங்கள் அழைப்புகளை அனுப்புகிறோம். அதற்குக் குறைவானது வரிசையில் காத்திருக்கும்.",
      "hm.matchmaker.science_1_ttl": "ஆளுமை (35%)",
      "hm.matchmaker.science_1_body": "Big-5 ஆளுமை வெக்டார் முழுவதும் கொசைன் ஒற்றுமை (Cosine similarity) — திறந்த மனப்பான்மை, மனசாட்சி, புறமுகம், இணக்கத்தன்மை மற்றும் உணர்ச்சி நிலைத்தன்மை.",
      "hm.matchmaker.science_2_ttl": "மதிப்புகள் (25%)",
      "hm.matchmaker.science_2_body": "விருந்து உரையாடலைச் சிறப்பாக்கும் மதிப்புகளின் ஒற்றுமை — குடும்பம், ஆர்வம், வளர்ச்சி, சேவை, சாகசம்.",
      "hm.matchmaker.science_3_ttl": "ஆர்வங்கள் (25%)",
      "hm.matchmaker.science_3_body": "உங்கள் குறிப்பிட்ட ஆர்வங்களில் ஜக்கார்ட் ஒற்றுமை (Jaccard overlap) — Six Foot Track-இல் மலையேற்றம், Sydney திரைப்படக் கழகம், Melbourne காபி கலாச்சாரம் போன்றவை.",
      "hm.matchmaker.science_4_ttl": "கிடைக்கும் நேரம் (15%)",
      "hm.matchmaker.science_4_body": "நீங்கள் வரக்கூடிய இரவு நேரம். உங்கள் காலண்டரில் செவ்வாய் இரவு ஜிம் என்று இருந்தால், செவ்வாய் இரவு 7 மணி அழைப்பு வராது.",
      "hm.matchmaker.safety_kicker": "வடிவமைக்கப்பட்ட பாதுகாப்பு",
      "hm.matchmaker.safety_title": "Matchmaker அமைதியாகக் கவனமாக இருக்கும்.",
      "hm.matchmaker.safety_1_ttl": "நம்பிக்கை வரம்பு",
      "hm.matchmaker.safety_1_body": "குழு மதிப்பெண் 0.70-க்குக் கீழே இருந்தால், அழைப்பு அனுப்பப்படாது. பொருத்தமற்ற விருந்தை விட விருந்து இல்லாமலிருப்பதே மேல்.",
      "hm.matchmaker.safety_2_ttl": "இருதரப்பு தடுப்புப் பட்டியல்",
      "hm.matchmaker.safety_2_body": "ஒரு டின்னருக்குப் பிறகு இரண்டு உறுப்பினர்கள் தனிப்பட்ட முறையில் 'வேண்டாம்' என்று தெரிவித்தால், அவர்களை மீண்டும் ஒன்றாக அமரவைக்க மாட்டோம்.",
      "hm.matchmaker.safety_3_ttl": "தானியங்கி பாதுகாப்பு நிறுத்தம்",
      "hm.matchmaker.safety_3_body": "சமீபத்திய கருத்துக்கணிப்புகளில் 20% அல்லது அதற்கு மேற்பட்டவை ஒரு மேஜையை 2/5 அல்லது அதற்குக் குறைவாக மதிப்பிட்டால், ஒரு மேலாளர் ஆய்வு செய்யும் வரை 24 மணிநேரத்திற்கு அழைப்புகள் நிறுத்தப்படும்.",
      "hm.matchmaker.safety_4_ttl": "சமூகத் தரநிலைகள்",
      "hm.matchmaker.safety_4_body": "ஒவ்வொரு உறுப்பினரும் எளிய நடத்தை விதிகளை ஏற்றுக்கொள்கிறார்கள். அதை மீறுபவர்களை நாங்கள் நீக்குகிறோம் — மேல்முறையீடு கிடையாது.",
      "hm.matchmaker.safety_5_ttl": "பொதுவான உணவகங்கள் மட்டுமே",
      "hm.matchmaker.safety_5_body": "ஒவ்வொரு Matchmaker டின்னரும் பணியாளர்கள் இருக்கும் ஒரு நிஜமான உணவகத்தில் நடக்கும். ஒருபோதும் ஒருவரின் வீட்டில் அல்ல.",
      "hm.matchmaker.safety_6_ttl": "நிகழ்விற்குப் பிந்தைய கருத்து",
      "hm.matchmaker.safety_6_body": "உங்கள் தனிப்பட்ட கருத்துக்கணிப்பு எதிர்காலப் பொருத்தங்களை வடிவமைக்கும், மேலும் ஒரு இடம் அல்லது சக உறுப்பினர் மீதான ஆய்வைத் தூண்டும்.",
      "hm.matchmaker.loop_kicker": "கற்றல் சுழற்சி",
      "hm.matchmaker.loop_title": "ஒவ்வொரு விருந்தும் உங்களின் அடுத்த விருந்தைச் சிறப்பாக்கும்.",
      "hm.matchmaker.loop_body": "Matchmaker நிலையானது அல்ல. ஒவ்வொரு நிகழ்விற்கும் பிறகு நீங்கள் ஒரு சிறிய தனிப்பட்ட கருத்துக்கணிப்பைச் சமர்ப்பிப்பீர்கள் — உரையாடல் எப்படி இருந்தது, யாருடன் மீண்டும் உணவருந்த விரும்புவீர்கள், அந்த இடம் திருப்திகரமாக இருந்ததா போன்ற தகவல்கள். அந்த சிக்னல்கள் மாடலுக்கு அனுப்பப்பட்டு எதிர்காலப் பொருத்தங்களை மாற்றியமைக்கும்.",
      "hm.matchmaker.loop_1": "சக உறுப்பினர்களின் \"மீண்டும் சந்திப்போம்\" வாக்குகள் உங்கள் பொருத்தத்தை வலுப்படுத்தும்",
      "hm.matchmaker.loop_2": "இருதரப்பு வேண்டாம் வாக்குகள் அமைதியாக அந்த இணையைத் தவிர்க்கும்",
      "hm.matchmaker.loop_3": "இடம் & உரையாடல் மதிப்பீடுகள் இருக்கை அமைப்பைச் சீரமைக்கும்",
      "hm.matchmaker.loop_4": "நம்பகத்தன்மை மதிப்பெண் உறுப்பினர்களைத் தேவையற்ற ஆப்சென்ட்களில் இருந்து பாதுகாக்கும்",
      "hm.matchmaker.loop_5": "கற்றல் சார்பு ஒவ்வொரு மாதமும் சிறந்த பொருத்தங்களை உறுதிப்படுத்தும்",
      "hm.matchmaker.loop_6": "கட்டுப்பாடு உங்கள் கையில் — எந்த நேரத்திலும் உங்கள் ப்ரொஃபைலை மாற்றலாம் அல்லது விலகலாம்",
      "hm.matchmaker.vs_kicker": "எனக்கு எது சரியானது?",
      "hm.matchmaker.vs_title": "Matchmaker அல்லது Circles?",
      "hm.matchmaker.vs_mm_ttl": "எனக்கு மாற்றங்கள் வேண்டுமென்றால் இதைத் தேர்ந்தெடுக்கவும்.",
      "hm.matchmaker.vs_mm_1": "ஒவ்வொரு முறையும் ஒரு வெவ்வேறு மேஜை, நகரம், உணவு வகை",
      "hm.matchmaker.vs_mm_2": "AI-ஆல் தேர்ந்தெடுக்கப்பட்ட 4-8 இணக்கமான அந்நியர்களின் குழு",
      "hm.matchmaker.vs_mm_3": "வாராந்திர நடைமுறை, தொகுப்பாளர் என்ற பொறுப்பு இல்லை",
      "hm.matchmaker.vs_mm_4": "புதிய நகரத்திற்கு வருபவர்களுக்கு அல்லது பயணம் செய்பவர்களுக்குச் சிறந்தது",
      "hm.matchmaker.vs_mm_cta": "வினாடி வினாவில் பங்கேற்கவும்",
      "hm.matchmaker.vs_circles_ttl": "வழக்கமான ஒரு குழு வேண்டுமென்றால் இதைத் தேர்ந்தெடுக்கவும்.",
      "hm.matchmaker.vs_circles_1": "அதே தொகுப்பாளர் · அதே இடம் · அதே இரவு நேரம்",
      "hm.matchmaker.vs_circles_2": "உங்கள் புதன் கிழமை பாரம்பரியமாக மாறும் ஒரு குழு",
      "hm.matchmaker.vs_circles_3": "அனுபவம் வாய்ந்த தொகுப்பாளரால் மனிதர்களால் நிர்வகிக்கப்படுவது",
      "hm.matchmaker.vs_circles_4": "புதிய இடத்திற்கு வந்து நிரந்தர நண்பர்களைத் தேடுபவர்களுக்குச் சிறந்தது",
      "hm.matchmaker.vs_circles_cta": "Circles-ஐப் பாருங்கள்",
      "hm.matchmaker.bottom_title": "ஒரு வினாடி வினா. அதன் பிறகு நடக்கும் ஒவ்வொரு விருந்தும் உங்களுக்குப் பொருத்தமானதாக இருக்கும்.",
      "hm.matchmaker.bottom_sub": "இரண்டு வாரங்களுக்குள் உங்களுக்குப் பொருத்தம் கிடைக்கும். இந்த மாதம் நீங்கள் செய்யும் மிக எளிதான \"சரி\" இதுவாகத்தான் இருக்கும்.",
      "hm.matchmaker.bottom_cta": "வினாடி வினாவில் பங்கேற்கவும்",
      "hm.matchmaker.bottom_note": "கிரெடிட் கார்டு தேவையில்லை · 10 நிமிடங்கள் · எப்போது வேண்டுமானாலும் விலகலாம்",
      /* AUTO-TRANSLATED hm.* END */
    },
  },
  te: {
    translation: {
      /* AUTO-TRANSLATED finder.* (added) */
      "finder.title": "HustleFinder",
      "finder.tagline": "సమీపంలోని పబ్లిక్ స్థలాన్ని కనుగొనండి — నిజమైన డేటా, ఆస్ట్రేలియా అంతటా.",
      "finder.search_placeholder": "స్థలాలు, వీధులు లేదా సబర్బ్‌లను వెతకండి…",
      "finder.hint": "పైన టైప్ చేయడం ప్రారంభించండి, కేటగిరీని టాప్ చేయండి, లేదా \\\"నా దగ్గర\\\" ఉపయోగించండి.",
      "finder.near_me": "నా దగ్గర",
      "finder.searching": "వెతుకుతోంది…",
      "finder.suburb.placeholder": "సబర్బ్ ద్వారా ఫిల్టర్ చేయండి (ఉదా. Darwin, NT)…",
      "finder.suburb.clear": "క్లియర్ చేయి",
      "finder.search_in": "{{cat}}లో వెతకండి",
      "finder.empty": "ఫలితాలు లేవు — మరో కేటగిరీ లేదా సెర్చ్ టర్మ్ ప్రయత్నించండి.",
      "finder.meta.title": "HustleFinder — ఉచిత Aussie సబర్బ్ డైరెక్టరీ",
      "finder.meta.desc": "ప్రతి ఆస్ట్రేలియన్ సబర్బ్‌లో స్థానిక వ్యాపారాలు, ચార్జర్లు, కేఫ్‌లు, టాయిలెట్లు మరియు మరిన్ని వెతకండి — 100% ఉచితం.",
      "finder.footer.credits": "డేటా క్రెడిట్స్ & ఆట్రిబ్యూషన్",
      "finder.footer.terms": "నిబంధనలు",
      "finder.cat.toilet": "టాయిలెట్లు",
      "finder.cat.fountain": "డ్రింకింగ్ ఫౌంటెన్లు",
      "finder.cat.transport": "రవాణా",
      "finder.cat.park": "పార్కులు",
      "finder.cat.medical": "వైద్యం",
      "finder.cat.school": "పాఠశాలలు",
      "finder.cat.library": "లైబ్రరీలు",
      "finder.cat.petrol": "పెట్రోల్",
      "finder.cat.kidzone": "కిడ్ జోన్",
      "finder.cat.puzzles": "పజిల్స్",
      "finder.cat.roadtrip": "రోడ్ ట్రిప్",
      "finder.hotels.title": "సమీపంలో హోటల్ కనుగొనండి",
      "finder.hotels.sub": "ఒకే సెర్చ్‌లో 200+ బుకింగ్ సైట్లను పోల్చుతుంది — Trivago ద్వారా శక్తివంతం.",
      "finder.hotels.where": "ఎక్కడ",
      "finder.hotels.where_ph": "నగరం లేదా సబర్బ్ (ఉదా. Byron Bay, NSW)",
      "finder.hotels.checkin": "చెక్-ఇన్",
      "finder.hotels.checkout": "చెక్-అవుట్",
      "finder.hotels.adults": "పెద్దలు",
      "finder.hotels.search": "హోటల్స్ వెతకండి",
      "finder.hotels.disclosure": "Trivago తెరుస్తుంది — బుకింగ్‌లపై మేము కమీషన్ పొందవచ్చు.",
      /* AUTO-TRANSLATED rt.* START */
      "rt.brand_name": "Roadtrip Planner",
      "rt.page_title": "ఆసీ EV రోడ్‌ట్రిప్ ప్లానర్",
      "rt.from": "నుండి",
      "rt.to": "వరకు",
      "rt.from_placeholder": "శివారు ప్రాంతం, టౌన్ లేదా నగరం (ఉదా. Newcastle NSW)",
      "rt.to_placeholder": "శివారు ప్రాంతం, టౌన్ లేదా నగరం (ఉదా. Hobart TAS)",
      "rt.stop": "స్టాప్",
      "rt.stop_placeholder": "మధ్యలో ఆగే చోటు",
      "rt.add_stop": "స్టాప్ జోడించు",
      "rt.upgrade_pro": "20 స్టాప్‌ల వరకు ప్రోకి అప్‌గ్రేడ్ అవ్వండి",
      "rt.reverse": "రివర్స్ (తిరుగు ప్రయాణం లెక్కించు)",
      "rt.from_current": "ప్రస్తుత లొకేషన్ నుండి",
      "rt.range_per_charge": "ప్రతి ఛార్జ్‌కి రేంజ్ (కి.మీ)",
      "rt.connector": "కనెక్టర్",
      "rt.connector_any": "ఏదైనా",
      "rt.drive_hours_day": "ప్రయాణ గంటలు/రోజు",
      "rt.accommodation": "వసతి",
      "rt.accom_any": "ఏదైనా (BnB · హోటల్ · కారవాన్)",
      "rt.accom_bnb": "BnB",
      "rt.accom_hotel": "హోటల్",
      "rt.accom_caravan": "కారవాన్ పార్క్",
      "rt.min_rating": "కనిష్ట రేటింగ్",
      "rt.any_rating": "ఏదైనా రేటింగ్",
      "rt.only_5star": "5★ మాత్రమే",
      "rt.schedule": "షెడ్యూల్:",
      "rt.depart_at": "బయలుదేరే సమయం",
      "rt.arrive_by": "చేరుకునే సమయం",
      "rt.hotel_checkin": "హోటల్ చెక్-ఇన్",
      "rt.hotel_checkout": "హోటల్ చెక్-అవుట్",
      "rt.overnight_hint": "రాత్రిపూట ప్రయాణాలు ఈ గంటల వెలుపల షెడ్యూల్ చేయబడవు.",
      "rt.whos_in_car": "కారులో ఎవరు ఉన్నారు?",
      "rt.adults": "పెద్దలు",
      "rt.kids": "పిల్లలు",
      "rt.ages": "వయస్సు",
      "rt.show_attractions": "పర్యాటక ఆకర్షణలను చూపు",
      "rt.show_kids": "పిల్లలకు అనువైన స్టాప్‌లను చూపు",
      "rt.overnight_stays": "రాత్రి బసలు",
      "rt.fast_chargers_only": "ఫాస్ట్ ఛార్జర్‌లు మాత్రమే",
      "rt.hotel_must_charger": "హోటల్‌లో తప్పనిసరిగా ఛార్జర్ ఉండాలి",
      "rt.pet_only": "పెంపుడు జంతువులకు అనుమతి ఉన్న బసలు మాత్రమే",
      "rt.safety": "భద్రత",
      "rt.safety_90": "90% (సమతలం)",
      "rt.safety_85": "85% (సాధారణం)",
      "rt.safety_80": "80% (కొండలు / లాగడం)",
      "rt.safety_70": "70% (ఎక్కువ బరువు)",
      "rt.save_trip": "ట్రిప్ సేవ్ చేయి",
      "rt.plan_my_trip": "నా ట్రిప్ ప్లాన్ చేయి",
      "rt.refresh_trip": "ట్రిప్ రిఫ్రెష్ చేయి",
      "rt.planning": "ప్లాన్ చేస్తోంది…",
      "rt.resume_title": "ఇక్కడి నుండి ట్రిప్ కొనసాగించు",
      "rt.resume_body": "మేము మీ ప్రస్తుత లొకేషన్‌ను గుర్తించాము. మీ డ్యాష్‌బోర్డ్‌లో కనిపిస్తున్న మిగిలిన రేంజ్‌ను ఎంటర్ చేయండి, తద్వారా మేము మీ వద్ద ఉన్న ఛార్జ్ ఆధారంగా తదుపరి ప్రయాణాన్ని ప్లాన్ చేస్తాము — ఫుల్ ఛార్జ్ ఆధారంగా కాదు.",
      "rt.remaining_range": "మిగిలిన రేంజ్ (కి.మీ)",
      "rt.cancel": "రద్దు చేయి",
      "rt.replan_from_here": "ఇక్కడి నుండి మళ్లీ ప్లాన్ చేయి",
      "rt.save_modal_title": "ఈ ట్రిప్‌కి పేరు పెట్టండి",
      "rt.save_modal_body": "సేవ్ చేసిన ట్రిప్స్ మీ ప్రొఫైల్‌లో ఉంటాయి — ఎప్పుడైనా మళ్లీ తెరిచి రీప్లాన్ నొక్కండి.",
      "rt.save_name_placeholder": "ఉదా. Brisbane → Sydney via Byron",
      "rt.your_saved_trips": "మీ సేవ్ చేసిన ట్రిప్స్",
      "rt.custom_location": "కస్టమ్ లొకేషన్",
      "rt.trip_summary": "ట్రిప్ సారాంశం",
      "rt.itinerary": "ప్రయాణ ప్రణాళిక",
      "rt.no_stops_needed": "స్టాప్‌లు అవసరం లేదు — మొత్తం ట్రిప్ మీ రేంజ్ లోపలే ఉంది.",
      "rt.chargers_in_corridor": "దారిలో ఉన్న ఛార్జర్‌లు",
      "rt.planned_stops": "ప్లాన్ చేసిన స్టాప్‌లు",
      "rt.overnight_stop": "రాత్రి బస",
      "rt.overnight_stops": "రాత్రి బసలు",
      "rt.suggested_detours": "సూచించిన ప్రక్కదారులు — జోడించడానికి టిక్ చేయండి",
      "rt.replan_with_detours": "ప్రక్కదారులతో మళ్లీ ప్లాన్ చేయి",
      "rt.attractions_label": "ఆకర్షణ",
      "rt.kids_label": "పిల్లలు",
      "rt.km_from_start": "ప్రారంభం నుండి కి.మీ",
      "rt.open_driving": "డ్రైవింగ్ మోడ్ ప్రారంభించు",
      "rt.big_font_hint": "పెద్ద ఫాంట్, హోల్డర్‌కి అనువైనది. CarPlay/Android Auto బ్రౌజర్ మిర్రర్‌తో పనిచేస్తుంది.",
      "rt.check_price_book": "ధర చెక్ చేసి Trivagoలో బుక్ చేయండి ↗",
      "rt.visit_site": "సైట్‌ను సందర్శించండి ↗",
      "rt.charger_onsite": "అక్కడే ఛార్జర్ ఉంది",
      "rt.leg_exceeds_range": "ఈ ప్రయాణం మీ రేంజ్ కంటే ఎక్కువగా ఉంది — ఈ ఛార్జర్‌కు చేరుకోవడానికి ముందే పవర్ అయిపోవచ్చు. రేంజ్ అంచనాను తగ్గించండి లేదా మధ్యలో ఛార్జ్ చేయండి.",
      "rt.add_waypoint_replan": "వేపాయింట్‌గా జోడించి మళ్లీ ప్లాన్ చేయి",
      /* AUTO-TRANSLATED rt.* END */
                      /* AUTO-TRANSLATED hm.* START */
      "hm.nav.hustlemeet": "HustleMeet",
      "hm.nav.circles": "Circles",
      "hm.nav.matchmaker": "Matchmaker",
      "hm.nav.meet": "Meet",
      "hm.nav.overview": "HustleMeetups",
      "hm.overview.page_title": "HustleMeetups — మీ వాళ్లను కనుగొనడానికి మూడు ఆత్మీయ మార్గాలు.",
      "hm.overview.kicker": "HustleSuite లో భాగం",
      "hm.overview.hero_title": "మీ వాళ్లను కనుగొనడానికి మూడు ఆత్మీయ మార్గాలు.",
      "hm.overview.hero_title_accent": "మీ వాళ్లు",
      "hm.overview.hero_sub": "HustleMeetups అనేది నిజమైన స్నేహాలను ఏర్పరచుకోవడానికి Australia లోని అత్యంత స్నేహపూర్వక మార్గం — మీకు సరిపోయే వైబ్‌ని ఎంచుకోండి. మీకు వారానికోసారి రన్నింగ్ గ్రూప్ కావాలన్నా, మీ ప్రస్తుత బృందంతో నెలవారీ డిన్నర్ కావాలన్నా, లేదా AI ద్వారా మ్యాచ్ చేయబడిన కొత్త వ్యక్తులతో కలవాలన్నా, ఇక్కడ అన్నింటికీ మార్గం ఉంది.",
      "hm.overview.tile_meet_kicker": "HustleMeet",
      "hm.overview.tile_meet_title": "మీ వాళ్లను కనుగొనండి. పని మొదలుపెట్టండి.",
      "hm.overview.tile_meet_body": "ఆసక్తుల ఆధారిత సమూహాల కోసం Australia చిరునామా. రన్నర్లు, కోడర్లు, ఇన్వెస్టర్లు, వాకర్లు, డాన్సర్లు, బ్రేక్‌ఫాస్ట్ క్లబ్‌లు, బుక్ క్రూలు — వందలాది సమూహాలు, ప్రతి వారం నిజమైన ఈవెంట్‌లు. ఒకసారి రండి, లేదా ప్రతి వారం రండి.",
      "hm.overview.tile_meet_cta": "సమూహాలను అన్వేషించండి",
      "hm.overview.tile_circles_kicker": "Circles",
      "hm.overview.tile_circles_title": "మీ బృందం. ప్రతిసారీ ఒక కొత్త టేబుల్.",
      "hm.overview.tile_circles_body": "మీ రెగ్యులర్ మిత్ర బృందాన్ని తీసుకురండి. మేము కొత్త రెస్టారెంట్‌ను బుక్ చేస్తాము, ట్రివియా ప్లాన్ చేస్తాము, కార్డ్‌లను సెట్ చేస్తాము. మీరు కేవలం హాజరైతే చాలు. ఎవరూ ప్రత్యేకంగా ప్లాన్ చేయాల్సిన అవసరం లేకుండానే — ఉన్న బృందాన్ని సన్నిహితంగా ఉంచడానికి ఇది అత్యుత్తమ మార్గం.",
      "hm.overview.tile_circles_cta": "ఒక Circle ప్రారంభించండి",
      "hm.overview.tile_matchmaker_kicker": "Matchmaker",
      "hm.overview.tile_matchmaker_title": "AI టేబుల్‌ని ఎంచుకుంటుంది. మీరు వెళ్తే చాలు.",
      "hm.overview.tile_matchmaker_body": "10 నిమిషాల క్విజ్ తీసుకోండి. రాత్రికి రాత్రే, మేము మిమ్మల్ని అపరిచితులతో కూర్చోబెడతాము, డెజర్ట్ సమయానికి వారు పాత స్నేహితుల్లా అనిపిస్తారు. ప్రతిసారీ వేర్వేరు టేబుల్.",
      "hm.overview.tile_matchmaker_cta": "క్విజ్ తీసుకోండి",
      "hm.overview.trust_kicker": "ఇది ఎందుకు పని చేస్తుంది",
      "hm.overview.trust_title": "ముందు నమ్మకం. తర్వాత టేబుల్.",
      "hm.overview.trust_1_title": "ధృవీకరించబడిన సభ్యులు",
      "hm.overview.trust_1_body": "ప్రతి సమూహం నిజమైన ID తనిఖీ + రిలయబిలిటీ స్కోర్‌తో ప్రారంభమవుతుంది. ఫేక్ ఖాతాలకు చోటు లేదు.",
      "hm.overview.trust_2_title": "నిజమైన హోస్ట్‌లు",
      "hm.overview.trust_2_body": "ప్రతి సమూహానికి ఒక ధృవీకరించబడిన హోస్ట్ నాయకత్వం వహిస్తారు, వారు అక్కడి వాతావరణాన్ని పర్యవేక్షిస్తారు మరియు అందరినీ చూసుకుంటారు.",
      "hm.overview.trust_3_title": "ముందు స్నేహం",
      "hm.overview.trust_3_body": "ఇక్కడ ప్రతిదీ నిజమైన స్నేహాల కోసం రూపొందించబడింది, డేటింగ్ కోసం కాదు. ఎప్పటికీ.",
      "hm.overview.trust_4_title": "జీరో-టాలరెన్స్ మోడరేషన్",
      "hm.overview.trust_4_body": "ఒక రిపోర్ట్, ఒక సమీక్ష, ఒక నిర్ణయం. తప్పుగా ప్రవర్తించే వారు అదే రాత్రి తొలగించబడతారు.",
      "hm.overview.footer_line": "HustleSuite లో భాగం — Australia యాజమాన్యం & హోస్టింగ్.",
      "hm.landing.page_title": "HustleMeet · మీ వాళ్లను కనుగొనండి. పని మొదలుపెట్టండి.",
      "hm.landing.kicker": "Australia ఆసక్తుల నెట్‌వర్క్",
      "hm.landing.hero_title": "మీ వాళ్లను కనుగొనండి. పని మొదలుపెట్టండి.",
      "hm.landing.hero_title_accent": "మీ వాళ్లు",
      "hm.landing.hero_sub": "HustleMeet అనేది ఆసక్తుల ఆధారిత సమూహాల కోసం Australia చిరునామా. లంచ్ చేస్తూ డీబగ్గింగ్ చేసే సాఫ్ట్‌వేర్ డెవలపర్లు. ఉదయం 6 గంటలకు పరిగెత్తే రన్నర్లు. నెలవారీగా సమావేశమయ్యే ఇన్వెస్టర్లు. వాకింగ్ గ్రూపులు, డాన్స్ క్రూలు, బ్రేక్‌ఫాస్ట్ క్లబ్‌లు, బోర్డ్-గేమ్ నైట్స్. ఒక సమూహాన్ని ఎంచుకోండి, తదుపరి ఈవెంట్‌ను బుక్ చేయండి మరియు హాజరవ్వండి.",
      "hm.landing.cta_explore_circles": "సమూహాలను బ్రౌజ్ చేయండి",
      "hm.landing.cta_try_matchmaker": "మీ స్వంత సమూహాన్ని ప్రారంభించండి",
      "hm.landing.trust_verified": "ధృవీకరించబడిన సభ్యులు",
      "hm.landing.trust_venues": "ధృవీకరించబడిన ఈవెంట్ హోస్ట్‌లు",
      "hm.landing.trust_friendship": "ముందు స్నేహం — డేటింగ్ కాదు",
      "hm.landing.trust_moderation": "జీరో-టాలరెన్స్ మోడరేషన్",
      "hm.landing.subbrand_circles_kicker": "Circles",
      "hm.landing.subbrand_circles_title": "ఇప్పటికే మీ బృందం ఉందా? వారికి శాశ్వత నిలయాన్ని ఇవ్వండి.",
      "hm.landing.subbrand_circles_body": "Circles అనేది ఇప్పటికే కలుసుకునే స్నేహితుల బృందం కోసం — బుక్ క్లబ్, పోకర్ నైట్, రన్నింగ్ క్రూ, వర్క్ ఫ్రెండ్స్. వేదిక, రిమైండర్‌లు మరియు రోస్టర్‌ను మేము చూసుకుంటాము. మీరు కేవలం హాజరైతే చాలు.",
      "hm.landing.subbrand_circles_b1": "మారుతున్న వేదికలు, ఒకే రన్నింగ్ రోస్టర్",
      "hm.landing.subbrand_circles_b2": "ఆటో-రిమైండర్‌లు + సులభమైన RSVPలు",
      "hm.landing.subbrand_circles_b3": "ట్రివియా, కార్డ్‌లు & థీమ్ నైట్స్ అందుబాటులో ఉన్నాయి",
      "hm.landing.subbrand_circles_cta": "ఒక Circle ప్రారంభించండి",
      "hm.landing.subbrand_matchmaker_kicker": "Matchmaker",
      "hm.landing.subbrand_matchmaker_title": "AI టేబుల్‌ని ఎంచుకుంటుంది. మీరు వెళ్తే చాలు.",
      "hm.landing.subbrand_matchmaker_body": "ఒక 10 నిమిషాల క్విజ్ — వ్యక్తిత్వం, విలువలు, లభ్యత. మా Matchmaker మిమ్మల్ని అపరిచితులతో కూర్చోబెడుతుంది, వారు డెజర్ట్ సమయానికి పాత స్నేహితుల్లా అనిపిస్తారు. ప్రతిసారీ వేర్వేరు టేబుల్.",
      "hm.landing.subbrand_matchmaker_b1": "Big-5 వ్యక్తిత్వం & విలువల స్కోరింగ్",
      "hm.landing.subbrand_matchmaker_b2": "మీకు 0.70 లేదా అంతకంటే ఎక్కువ సరిపోతే రాత్రికి రాత్రే ఆహ్వానాలు",
      "hm.landing.subbrand_matchmaker_b3": "మీ ప్రతి డిన్నర్ నుండి నేర్చుకుంటుంది",
      "hm.landing.subbrand_matchmaker_cta": "క్విజ్ తీసుకోండి",
      "hm.landing.how_kicker": "HustleMeet ఎలా పనిచేస్తుంది",
      "hm.landing.how_title": "నాలుగు దశలు. మీ సమూహాన్ని కనుగొనండి. హాజరవ్వండి.",
      "hm.landing.how_step1_ttl": "మీ ఆసక్తులను ఎంచుకోండి",
      "hm.landing.how_step1_sub": "రన్నింగ్, కోడింగ్, ఇన్వెస్టింగ్, వంట, సంగీతం, వాకింగ్ — మీకు ఏమి ఇష్టమో మరియు మీరు ఎక్కడ నివసిస్తున్నారో మాకు చెప్పండి.",
      "hm.landing.how_step2_ttl": "మీకు సమీపంలోని సమూహాలను బ్రౌజ్ చేయండి",
      "hm.landing.how_step2_sub": "ప్రతి సమూహం దాని హోస్ట్, దాని క్రమం మరియు దాని తదుపరి ఈవెంట్‌ను ప్రచురిస్తుంది. మీరు చేరే ముందు ఆ సమూహం గురించి తెలుసుకోండి.",
      "hm.landing.how_step3_ttl": "తదుపరి ఈవెంట్‌కు RSVP చేయండి",
      "hm.landing.how_step3_sub": "ఒక ట్యాప్, ఒక క్యాలెండర్ ఆహ్వానం, ఈవెంట్‌కు ముందు రోజు ఒక రిమైండర్. కొన్ని ఈవెంట్‌లు ఉచితం, కొన్ని చెల్లింపుతో కూడుకున్నవి — ధర వివరంగా ఉంటుంది.",
      "hm.landing.how_step4_ttl": "హాజరవ్వండి & కనెక్ట్ అవ్వండి",
      "hm.landing.how_step4_sub": "హోస్ట్‌ను కలవండి, బృందాన్ని కలవండి, పని మొదలుపెట్టండి. మీకు నచ్చితే వచ్చే వారం మళ్ళీ రండి.",
      "hm.landing.stats_kicker": "కలవడానికి Australia యొక్క కొత్త మార్గం",
      "hm.landing.stats_title": "ప్రజలు మళ్ళీ మళ్ళీ రావడానికి కారణం ఇదే.",
      "hm.landing.stats_92_sub": "ఈవెంట్‌లో కనీసం ఒక్క వ్యక్తితోనైనా కనెక్ట్ అయినట్లు భావిస్తారు",
      "hm.landing.stats_68_sub": "ఒక వారంలోనే వారి తదుపరి ఈవెంట్‌కు RSVP చేస్తారు",
      "hm.landing.stats_85_sub": "సమూహంలోని వారితో సంబంధాన్ని కొనసాగిస్తారు",
      "hm.landing.why_kicker": "మేము ఎందుకు పని చేస్తాము",
      "hm.landing.why_title": "నిజమైన ఆసక్తులు. నిజమైన హోస్ట్‌లు. నిజమైన ఈవెంట్‌లు.",
      "hm.landing.why_1_ttl": "ముందు ఆసక్తి, తర్వాత ప్రాంతం",
      "hm.landing.why_1_body": "మీ సమూహంలోని వ్యక్తులు నిజంగా మీరు ఇష్టపడే వాటిపై ఆసక్తి చూపిస్తారు — నిజమైన స్నేహానికి ఇది ఒక సులభమైన మార్గం.",
      "hm.landing.why_2_ttl": "నిజమైన ఈవెంట్ హోస్ట్‌లు",
      "hm.landing.why_2_body": "ప్రతి సమూహానికి ఒక ధృవీకరించబడిన హోస్ట్ ఉంటారు, వారు వాతావరణాన్ని పర్యవేక్షిస్తారు. వారు Facebook అడ్మిన్ కాదు, ఒక నిజమైన వ్యక్తి.",
      "hm.landing.why_3_ttl": "క్రమం ముఖ్యం",
      "hm.landing.why_3_body": "ప్రతి వారం కొత్త ఈవెంట్‌లు. ఎదురుచూడడానికి ఏదో ఒకటి. అపరిచితులను మీ మిత్ర బృందంగా మార్చే ఒక క్రమబద్ధత.",
      "hm.landing.why_4_ttl": "సురక్షితం & భద్రం",
      "hm.landing.why_4_body": "ధృవీకరించబడిన సభ్యులు, బహిరంగ వేదికలు, అగౌరవానికి తావు లేదు. ప్రతి ఈవెంట్‌కు ముందు, సమయంలో మరియు తర్వాత మా సపోర్ట్ టీమ్ అందుబాటులో ఉంటుంది.",
      "hm.landing.why_5_ttl": "ముందు స్నేహం",
      "hm.landing.why_5_body": "ఇది డేటింగ్ కాదు. ఇది కమ్యూనిటీ. నిజమైన ఆసక్తులు, నిజమైన వ్యక్తులు, మధ్యలో ఎటువంటి గందరగోళం లేదు.",
      "hm.landing.why_6_ttl": "ప్రాముఖ్యతను ఇచ్చేంత చిన్నది",
      "hm.landing.why_6_body": "చాలా సమూహాలలో ఈవెంట్‌లకు 4-15 సీట్లు మాత్రమే ఉంటాయి. నిజమైన సంభాషణలను ప్రారంభించేంత పెద్దది మరియు ఎవరూ విస్మరించబడనంత చిన్నది.",
      "hm.landing.cities_kicker": "మేము ఎక్కడ ఉన్నాము",
      "hm.landing.cities_title": "Australia & NZ అంతటా సమావేశమయ్యే సమూహాలు.",
      "hm.landing.cities_sub_zero": "15+ నగరాల్లో మొదటి సమూహాలు ప్రారంభమవుతున్నాయి — త్వరగా చేరి వాతావరణాన్ని రూపొందించండి.",
      "hm.landing.cities_sub_some": "15+ మెట్రోల్లో సమూహాలు అందుబాటులో ఉన్నాయి. ఈ వారం మీ నగరంలో ఏదో ఒకటి ఉంది.",
      "hm.landing.cities_sub_one": "{{count}} ప్రత్యక్ష సమూహం · ప్రతి వారం కొత్త ఈవెంట్‌లు · ఎల్లప్పుడూ కొత్త నగరాలను జోడిస్తున్నాము.",
      "hm.landing.cities_sub_many": "{{count}} ప్రత్యక్ష సమూహాలు · ప్రతి వారం కొత్త ఈవెంట్‌లు · ఎల్లప్పుడూ కొత్త నగరాలను జోడిస్తున్నాము.",
      "hm.landing.forme_kicker": "ఇది మీ కోసమే, ఒకవేళ మీరు…",
      "hm.landing.forme_title": "మీరు నిజమైన ఆసక్తులు, నిజమైన వ్యక్తులు మరియు నిజమైన ఈవెంట్‌లు కోరుకుంటే.",
      "hm.landing.forme_1": "మీరు డేటింగ్ యాప్‌లతో విసిగిపోయి, మీకు నచ్చిన పనుల ద్వారా వ్యక్తులను కలవాలనుకుంటే.",
      "hm.landing.forme_2": "మీరు కొత్త నగరానికి మారినప్పుడు, మరో చాట్ గ్రూప్ కాకుండా ఒక ఆత్మీయ బృందం కావాలనుకుంటే.",
      "hm.landing.forme_3": "మీరు పైపైన కాకుండా లోతైన సంబంధాలకు విలువ ఇస్తే — యాభై మంది ఫాలోవర్ల కంటే ఒక మంచి గ్రూప్ మిన్న.",
      "hm.landing.forme_4": "నిజ జీవితంలో పంచుకున్న ఆసక్తులు + కేటాయించిన రెండు గంటలే నిజమైన మార్గమని మీరు నమ్మితే.",
      "hm.landing.notme_kicker": "ఇది మీ కోసం కాదు, ఒకవేళ…",
      "hm.landing.notme_title": "HustleMeet అందరికీ పని చేయకపోవచ్చు.",
      "hm.landing.notme_1": "మీరు హాజరవుతారని మాట ఇవ్వలేకపోతే — హోస్ట్‌లు మరియు సభ్యులు మీ RSVP పై ఆధారపడి ఉంటారు.",
      "hm.landing.notme_2": "మీరు డేటింగ్ సర్వీస్ కోసం వెతుకుతుంటే — ఇక్కడ స్నేహాలకే మొదటి ప్రాధాన్యత.",
      "hm.landing.notme_3": "ఎవరినీ కలవకుండా కేవలం సమాచారం ఇవ్వాలనుకుంటే — ఇది ప్రత్యక్షంగా కలిస్తేనే పని చేస్తుంది.",
      "hm.landing.notme_4": "అన్నీ ఉచిత ప్రయోజనాలే ఆశిస్తే — కొన్ని ఈవెంట్‌లకు ఖర్చవుతుంది. ధర ఎప్పుడూ ముందే తెలియజేయబడుతుంది.",
      "hm.landing.safety_kicker": "మా భద్రతా నిబద్ధత",
      "hm.landing.safety_title": "ఆత్మీయం, కానీ ఎప్పుడూ అజాగ్రత్తగా ఉండదు.",
      "hm.landing.safety_1_ttl": "బహిరంగ వేదికలు",
      "hm.landing.safety_1_body": "ప్రతి ఈవెంట్ బహిరంగ, సిబ్బంది ఉన్న వేదికలో జరుగుతుంది — కేఫ్, రెస్టారెంట్, పార్క్, జిమ్ లేదా స్టూడియో. అందరూ అంగీకరిస్తే తప్ప ఎవరి ఇంట్లోనూ జరగదు.",
      "hm.landing.safety_2_ttl": "కమ్యూనిటీ ప్రమాణాలు",
      "hm.landing.safety_2_body": "అగౌరవం, వేధింపులు లేదా నిజాయితీ లేని ప్రవర్తనకు తావు లేదు. నిబంధనలను ఉల్లంఘించే సభ్యులు వెంటనే తొలగించబడతారు.",
      "hm.landing.safety_3_ttl": "గోప్యత రక్షణ",
      "hm.landing.safety_3_body": "మీ వివరాలు ఎప్పుడూ బహిరంగంగా పంచుకోబడవు. ప్రతి ఈవెంట్ తర్వాత మీరు ఎవరితో కనెక్ట్ అయి ఉండాలో మీరే నిర్ణయించుకోవచ్చు.",
      "hm.landing.safety_4_ttl": "సపోర్ట్ అందుబాటులో ఉంది",
      "hm.landing.safety_4_body": "మీకు ఏవైనా ప్రశ్నలు లేదా ఆందోళనలు ఉంటే ప్రతి ఈవెంట్‌కు ముందు, సమయంలో మరియు తర్వాత మా బృందం అందుబాటులో ఉంటుంది.",
      "hm.landing.bottom_title": "మీకు నచ్చే తదుపరి సమూహం ఒక్క క్లిక్ దూరంలో ఉంది.",
      "hm.landing.bottom_sub": "మీ మార్గాన్ని ఎంచుకోండి — ఉన్న సమూహంలో చేరండి, మీ బృందాన్ని Circle లోకి తీసుకురండి, లేదా Matchmaker ద్వారా మీకు సరిపోయే టేబుల్ వద్ద కూర్చోండి.",
      "hm.landing.bottom_cta_circles": "ఒక Circle ప్రారంభించండి",
      "hm.landing.bottom_cta_mm": "Matchmaker క్విజ్ తీసుకోండి",
      "hm.circles.page_title": "HustleMeet Circles · మీ బృందం, ప్రతిసారీ కొత్త వేదిక",
      "hm.circles.kicker": "HustleMeet · Circles",
      "hm.circles.hero_title": "మీ బృందం. ప్రతిసారీ ఒక కొత్త టేబుల్.",
      "hm.circles.hero_title_accent": "మీ బృందం",
      "hm.circles.hero_sub": "ఇప్పటికే మీ మిత్రులు ఉన్నారా? వారిని సన్నిహితంగా ఉంచడానికి Circles సులభమైన మార్గం. మేము వేదికను బుక్ చేస్తాము, ట్రివియా ప్లాన్ చేస్తాము, కార్డ్‌లను సెట్ చేస్తాము. మీరు కేవలం హాజరవ్వండి — అదే బృందం, కొత్త రాత్రి, కొత్త ప్రదేశం.",
      "hm.circles.cta_find": "ఒక Circle ప్రారంభించండి",
      "hm.circles.cta_host_hero": "ఒక ఓపెన్ Circle లో చేరండి",
      "hm.circles.trust_venue": "మారుతున్న ధృవీకరించబడిన వేదికలు",
      "hm.circles.trust_party": "ఒక Circle కి 4-12 మంది మిత్రులు",
      "hm.circles.trust_verified": "ధృవీకరించబడిన సభ్యులు",
      "hm.circles.trust_friendship": "నిజమైన స్నేహాలు, ఎప్పటికీ సన్నిహితంగా",
      "hm.circles.how_kicker": "ఇది ఎలా పనిచేస్తుంది",
      "hm.circles.how_title": "నాలుగు దశలు. మీ బృందం ఎప్పటికీ బృందంగానే ఉంటుంది.",
      "hm.circles.how_step1_ttl": "మీ Circle ని ప్రారంభించండి",
      "hm.circles.how_step1_sub": "దానికి పేరు పెట్టండి, మీ మిత్రులను ఆహ్వానించండి (12 మంది వరకు), మరియు ఒక క్రమాన్ని ఎంచుకోండి — వారం వారం, పక్షం రోజులకు ఒకసారి లేదా నెలకు ఒకసారి.",
      "hm.circles.how_step2_ttl": "మేము వేదికను ఎంచుకుంటాము",
      "hm.circles.how_step2_sub": "ప్రతి ఈవెంట్‌కు మేము ఒక కొత్త ప్రదేశాన్ని సూచిస్తాము — రెస్టారెంట్, ట్రివియా నైట్, కార్డ్ రూమ్, వైన్ బార్. లేదా మీకు ఇష్టమైన ప్రదేశాన్ని ఖరారు చేయండి.",
      "hm.circles.how_step3_ttl": "అందరూ ఒక్క ట్యాప్‌తో RSVP చేస్తారు",
      "hm.circles.how_step3_sub": "ఆటో-రిమైండర్‌లు, ఖర్చు విభజన, ప్రీపెయిడ్ సీట్లు — కాబట్టి ఎవరైనా రాకపోయినా ప్లాన్ చెడిపోదు. భోజనం రాకముందే బిల్లు పూర్తవుతుంది.",
      "hm.circles.how_step4_ttl": "క్రమాన్ని కొనసాగించండి",
      "hm.circles.how_step4_sub": "క్యాలెండర్‌లో ఒక రాత్రిని కేటాయించండి, మళ్ళీ ఎప్పుడూ ప్లాన్ చేయాల్సిన అవసరం లేదు. నెలలు గడిచేకొద్దీ బృందం మరింత సన్నిహితంగా మారుతుంది.",
      "hm.circles.cadence_weekly_ttl": "వారానికి ఒకసారి",
      "hm.circles.cadence_weekly_sub": "ప్రతి వారం",
      "hm.circles.cadence_weekly_body": "మంగళవారం రాత్రి ఆచారంగా కలవాలనుకునే బృందాల కోసం — అదే ముఖాలు, ప్రతిసారీ కొత్త ప్రదేశం.",
      "hm.circles.cadence_fortnight_ttl": "పక్షం రోజులకు ఒకసారి",
      "hm.circles.cadence_fortnight_sub": "ప్రతి 2 వారాలకు",
      "hm.circles.cadence_fortnight_body": "బిజీగా ఉండేవారికి సరైన ఎంపిక — క్రమం తప్పకుండా, కానీ భారం అనిపించకుండా.",
      "hm.circles.cadence_monthly_ttl": "నెలకు ఒకసారి",
      "hm.circles.cadence_monthly_sub": "ప్రతి నెల",
      "hm.circles.cadence_monthly_body": "ప్రత్యేక సందర్భాల కోసం — సండే రోస్ట్‌లు, పెద్ద ట్రివియా నైట్‌లు, పోకర్.",
      "hm.circles.feast_kicker": "మీ రాత్రిని ఎంచుకోండి",
      "hm.circles.feast_title": "గడపడానికి మూడు మార్గాలు",
      "hm.circles.feast_sub": "ఆ రాత్రి ఎలా గడవాలి అనేది ప్రతి Circle ఎంచుకుంటుంది. వాటిని మారుస్తూ ఉండండి లేదా మీకు నచ్చినదాన్ని ఖరారు చేయండి.",
      "hm.circles.feast_surprise_ttl": "కొత్త రెస్టారెంట్‌ని ప్రయత్నించండి",
      "hm.circles.feast_surprise_body": "మేము మీకు సమీపంలోని మూడు వేదికలను షార్ట్-లిస్ట్ చేస్తాము, అందరూ ఓటు వేస్తారు, రెస్టారెంట్ సిద్ధంగా ఉంటుంది. ఖర్చు విభజన, ప్రీపెయిడ్ సీట్లు.",
      "hm.circles.feast_preselect_ttl": "ట్రివియా / కార్డ్‌లు / గేమ్‌లు",
      "hm.circles.feast_preselect_body": "ట్రివియా నైట్, పోకర్ రూమ్, బోర్డ్-గేమ్ బార్ లేదా బౌలింగ్ లేన్ బుక్ చేయండి. వేదిక, ఎంట్రీ ఫీజు మరియు రిమైండర్‌లను మేము చూసుకుంటాము.",
      "hm.circles.feast_per_event_ttl": "వన్-ఆఫ్ థీమ్ నైట్",
      "hm.circles.feast_per_event_body": "వైన్ టేస్టింగ్, చెఫ్ టేబుల్, కాక్టెయిల్ క్లాస్, ఎస్కేప్ రూమ్. అప్పుడప్పుడు ఏదైనా ప్రత్యేకం — పూర్తిగా ప్లాన్ చేయబడింది.",
      "hm.circles.live_kicker": "ప్రస్తుతం",
      "hm.circles.live_title": "మీరు చేరగల Circles",
      "hm.circles.live_host_link": "మీ స్వంతంగా ప్రారంభించండి",
      "hm.circles.live_loading": "లోడ్ అవుతోంది…",
      "hm.circles.live_empty_title": "మీ నగరంలో మొదటి Circle గా ఉండండి.",
      "hm.circles.live_empty_sub": "ముగ్గురు మిత్రులతో ఒకదాన్ని ప్రారంభించండి, మిగిలిన వారిని ఆహ్వానించడానికి మేము మీకు సహాయం చేస్తాము — పైగా మొదటి మూడు వేదికలను మేమే అందిస్తాము.",
      "hm.circles.live_empty_cta": "ఒక Circle ప్రారంభించండి",
      "hm.circles.host_kicker": "Circle లీడర్ల కోసం",
      "hm.circles.host_title": "మీరు బృందాన్ని తీసుకురండి. అడ్మిన్ పని మేము చేస్తాము.",
      "hm.circles.host_body": "మీరు ఎప్పుడూ ఈవెంట్‌లను ప్లాన్ చేసే వ్యక్తి అయితే, Circles మీకు గంటల సమయాన్ని ఆదా చేస్తుంది. ఆటో-రిమైండర్‌లు, ప్రీపెయిడ్ RSVPలు, వేదిక షార్ట్-లిస్ట్‌లు, ఖర్చు విభజన, వెయిట్‌లిస్ట్ నిర్వహణ మరియు ప్రతి రాత్రి మీకు క్యాష్‌బ్యాక్ వాలెట్. మీరు కేవలం ఒక సాధారణ సభ్యునిగా హాజరవ్వచ్చు.",
      "hm.circles.host_perk_1": "ఆటో వేదిక షార్ట్-లిస్ట్‌లు — మళ్ళీ ఎప్పుడూ Google లో వెతకకండి",
      "hm.circles.host_perk_2": "ప్రీపెయిడ్ సీట్లు — ఎవరూ రాకుండా ఉండరు",
      "hm.circles.host_perk_3": "డెజర్ట్ కంటే ముందే ఖర్చు విభజన పూర్తవుతుంది",
      "hm.circles.host_perk_4": "ఆటోపైలట్‌లో రిమైండర్‌లు + క్యాలెండర్ ఆహ్వానాలు",
      "hm.circles.host_perk_5": "ఎవరైనా క్యాన్సిల్ చేస్తే ఆటో-ప్రమోట్‌తో వెయిట్‌లిస్ట్",
      "hm.circles.host_perk_6": "ప్రతి రాత్రి క్యాష్‌బ్యాక్ వాలెట్",
      "hm.circles.host_cta": "ఇప్పుడే ఒక Circle ప్రారంభించండి",
      "hm.circles.why_kicker": "Circles ఎందుకు పని చేస్తాయి",
      "hm.circles.why_title": "పెద్దల స్నేహాలకు ఒక వ్యవస్థ అవసరం",
      "hm.circles.why_1_ttl": "అదే బృందం, లోతైన నమ్మకం",
      "hm.circles.why_1_body": "జీవితం స్నేహితులను దూరం చేస్తుంది. Circles అనేది అందరినీ సన్నిహితంగా ఉంచే స్థిరమైన క్రమం — అదే 4-12 మంది మిత్రులు, అదే వారపు రాత్రి సమావేశం.",
      "hm.circles.why_2_ttl": "ఎటువంటి అడ్మిన్ భారం లేదు",
      "hm.circles.why_2_body": "గ్రూప్ WhatsApp చీఫ్‌గా ఎవరూ ఉండాల్సిన అవసరం లేదు. మేము వేదికలను ఎంచుకుంటాము, రిమైండర్‌లను పంపుతాము, చెల్లింపులు తీసుకుంటాము, అంతా చూసుకుంటాము.",
      "hm.circles.why_3_ttl": "కొత్త వేదిక, కొత్త జ్ఞాపకం",
      "hm.circles.why_3_body": "Australia లోని ఉత్తమ చిన్న రెస్టారెంట్‌లు, ట్రివియా రూమ్‌లు మరియు గేమ్ బార్‌లను సందర్శించండి. తెలిసిన ముఖాలు, కొత్త వాతావరణం — అలవాటు మరియు ఉత్సాహం యొక్క సరైన కలయిక.",
      "hm.circles.bottom_title": "ఎవరూ ప్లాన్ చేయాల్సిన అవసరం లేకుండానే, మీ బృందం ఎదురుచూసే రాత్రి.",
      "hm.circles.bottom_sub": "ఒక Circle. అదే మిత్రులు. ప్రతిసారీ కొత్త వేదిక.",
      "hm.circles.bottom_cta_find": "ఒక Circle ప్రారంభించండి",
      "hm.circles.bottom_cta_mm": "Matchmaker ద్వారా కొత్త వ్యక్తులను కలవండి →",
      "hm.circles.card_host_pill": "Circle",
      "hm.matchmaker.page_title": "HustleMeet Matchmaker · AI సరైన టేబుల్‌ని ఎంచుకుంటుంది",
      "hm.matchmaker.kicker": "HustleMeet · Matchmaker",
      "hm.matchmaker.hero_title": "AI టేబుల్‌ని ఎంచుకుంటుంది. మీరు కేవలం హాజరైతే చాలు.",
      "hm.matchmaker.hero_title_accent": "మీరు కేవలం హాజరైతే చాలు.",
      "hm.matchmaker.hero_sub": "వ్యక్తిత్వం మరియు విలువలపై ఒక చిన్న క్విజ్‌కి సమాధానం ఇవ్వండి. మా Matchmaker మిమ్మల్ని నలుగురు నుండి ఏడుగురు అపరిచితులతో కూర్చోబెడుతుంది, వారు డెజర్ట్ సమయానికి పాత స్నేహితుల్లా అనిపిస్తారు. ఎటువంటి గందరగోళం లేదు, మొహమాటపు మాటలు లేవు. మీరు సిద్ధంగా ఉన్నప్పుడు ఒక షేర్డ్ టేబుల్ సిద్ధంగా ఉంటుంది.",
      "hm.matchmaker.cta_quiz": "మ్యాచింగ్ క్విజ్ తీసుకోండి",
      "hm.matchmaker.cta_see_circles": "బదులుగా Circles చూడండి →",
      "hm.matchmaker.trust_big5": "Big-5 వ్యక్తిత్వ స్కోరింగ్",
      "hm.matchmaker.trust_seats": "ఒక టేబుల్‌కు 4-8 సీట్లు",
      "hm.matchmaker.trust_lock": "నో-స్వైప్ · నో-ఫోటో",
      "hm.matchmaker.trust_verified": "ధృవీకరించబడిన సభ్యులు మాత్రమే",
      "hm.matchmaker.how_kicker": "ఇది ఎలా పనిచేస్తుంది",
      "hm.matchmaker.how_title": "మీకు సరిపోయే టేబుల్ కోసం నాలుగు దశలు.",
      "hm.matchmaker.how_step1_ttl": "క్విజ్ తీసుకోండి",
      "hm.matchmaker.how_step1_sub": "పది నిమిషాల ప్రశ్నపత్రం — వ్యక్తిత్వం, విలువలు, ఆహారపు అలవాట్లు, లభ్యత. మీరు దీన్ని ఒక్కసారి చేస్తే చాలు.",
      "hm.matchmaker.how_step2_ttl": "రాత్రికి రాత్రే మ్యాచింగ్",
      "hm.matchmaker.how_step2_sub": "మా AI మీ ప్రొఫైల్ మరియు Australia లోని డిన్నర్ స్లేట్‌ను విశ్లేషిస్తుంది. మీ ఫిట్ స్కోర్ 0.70 లేదా అంతకంటే ఎక్కువ ఉన్నప్పుడు, అది మీకు ఆహ్వానాన్ని పంపుతుంది.",
      "hm.matchmaker.how_step3_ttl": "అంగీకరించండి లేదా తిరస్కరించండి",
      "hm.matchmaker.how_step3_sub": "వేదిక, టేబుల్ థీమ్ మరియు మీరు ఆ సమూహంతో పంచుకునే మూడు ఆసక్తులతో మీకు ఒక స్పష్టమైన ఆహ్వాన ఇమెయిల్ అందుతుంది.",
      "hm.matchmaker.how_step4_ttl": "ఇది మీ నుండి నేర్చుకుంటుంది",
      "hm.matchmaker.how_step4_sub": "ప్రతి డిన్నర్ తర్వాత ఇచ్చే సర్వే మోడల్‌ను మెరుగుపరుస్తుంది. మీరు ఎంత ఎక్కువ సార్లు వెళ్తే, మీ తదుపరి మ్యాచ్ అంత మెరుగ్గా ఉంటుంది.",
      "hm.matchmaker.science_kicker": "ఇది ఎందుకు పని చేస్తుంది",
      "hm.matchmaker.science_title": "Matchmaker ఊహించదు — స్కోర్ చేస్తుంది.",
      "hm.matchmaker.science_sub": "ప్రతి మ్యాచ్ నాలుగు కారకాల ఆధారంగా లెక్కించబడుతుంది. టేబుల్ సగటు 0.70 కంటే ఎక్కువ ఉన్నప్పుడు మాత్రమే మేము ఆహ్వానాలను పంపుతాము. అంతకంటే తక్కువ ఉంటే వెయిటింగ్‌లో ఉంటుంది.",
      "hm.matchmaker.science_1_ttl": "వ్యక్తిత్వం (35%)",
      "hm.matchmaker.science_1_body": "Big-5 వ్యక్తిత్వ వెక్టార్ అంతటా కోసైన్ సారూప్యత — నిష్కపటత్వం, కర్తవ్య నిష్ఠ, బహిర్ముఖత్వం, ఆహ్లాదకరత్వం మరియు భావోద్వేగ స్థిరత్వం.",
      "hm.matchmaker.science_2_ttl": "విలువలు (25%)",
      "hm.matchmaker.science_2_body": "డిన్నర్ సంభాషణను ఆసక్తికరంగా మార్చే విలువలపై పోలిక — కుటుంబం, ఉత్సుకత, ఎదుగుదల, సేవ, సాహసం.",
      "hm.matchmaker.science_3_ttl": "ఆసక్తులు (25%)",
      "hm.matchmaker.science_3_body": "మీ నిర్దిష్ట ఆసక్తులపై Jaccard పోలిక — సిడ్నీ ఫిల్మ్ క్లబ్, మెల్బోర్న్ కాఫీ సీన్, లేదా మీకు ఇష్టమైన పుస్తకాలు.",
      "hm.matchmaker.science_4_ttl": "లభ్యత (15%)",
      "hm.matchmaker.science_4_body": "మీరు నిజంగా హాజరుకాగల రాత్రి. మీ క్యాలెండర్‌లో వేరే పని ఉంటే, ఆ సమయంలో ఆహ్వానాలు రావు.",
      "hm.matchmaker.safety_kicker": "డిజైన్ పరంగా భద్రత",
      "hm.matchmaker.safety_title": "Matchmaker అత్యంత జాగ్రత్తగా ఉంటుంది.",
      "hm.matchmaker.safety_1_ttl": "నమ్మకమైన పరిమితి",
      "hm.matchmaker.safety_1_body": "గ్రూప్ స్కోర్ 0.70 కంటే తక్కువ ఉంటే ఎటువంటి ఆహ్వానం వెళ్ళదు. సరిగ్గా లేని మ్యాచ్ కంటే అస్సలు మ్యాచ్ కాకపోవడమే మంచిది.",
      "hm.matchmaker.safety_2_ttl": "మ్యూచువల్-నో బ్లాక్‌లిస్ట్",
      "hm.matchmaker.safety_2_body": "డిన్నర్ తర్వాత ఇద్దరు సభ్యులు ప్రైవేట్‌గా 'నో' అని చెబితే, మేము వారిని మళ్ళీ ఎప్పుడూ కలిపి కూర్చోబెట్టము.",
      "hm.matchmaker.safety_3_ttl": "ఆటో-కిల్ స్విచ్",
      "hm.matchmaker.safety_3_body": "ఇటీవలి సర్వేలలో 20% లేదా అంతకంటే ఎక్కువ మంది ఒక టేబుల్‌కు 2/5 లేదా అంతకంటే తక్కువ రేటింగ్ ఇస్తే, ఆపరేటర్ సమీక్షించే వరకు ఆహ్వానాలు నిలిపివేయబడతాయి.",
      "hm.matchmaker.safety_4_ttl": "కమ్యూనిటీ ప్రమాణాలు",
      "hm.matchmaker.safety_4_body": "ప్రతి సభ్యుడు స్పష్టమైన ప్రవర్తనా నియమావళిని అంగీకరిస్తారు. వాటిని ఉల్లంఘించిన వారిని మేము తొలగిస్తాము — ఎటువంటి అప్పీళ్లకు అవకాశం లేదు.",
      "hm.matchmaker.safety_5_ttl": "బహిరంగ వేదికలు మాత్రమే",
      "hm.matchmaker.safety_5_body": "ప్రతి Matchmaker డిన్నర్ సిబ్బంది ఉన్న నిజమైన రెస్టారెంట్‌లో జరుగుతుంది. ఎవరి ఇంట్లోనూ జరగదు.",
      "hm.matchmaker.safety_6_ttl": "ఈవెంట్ తర్వాత అభిప్రాయం",
      "hm.matchmaker.safety_6_body": "మీ ప్రైవేట్ సర్వే భవిష్యత్ మ్యాచ్‌లను రూపొందిస్తుంది మరియు అవసరమైతే వేదిక లేదా ఇతర సభ్యులపై సమీక్షను ప్రేరేపిస్తుంది.",
      "hm.matchmaker.loop_kicker": "నేర్చుకునే ప్రక్రియ",
      "hm.matchmaker.loop_title": "ప్రతి డిన్నర్ మీ తదుపరి డిన్నర్‌ను మరింత మెరుగుపరుస్తుంది.",
      "hm.matchmaker.loop_body": "Matchmaker అనేది స్థిరంగా ఉండదు. ప్రతి ఈవెంట్ తర్వాత మీరు ఒక చిన్న ప్రైవేట్ సర్వేను సమర్పిస్తారు — సంభాషణ ఎలా ఉంది, మీరు మళ్ళీ ఎవరితో డిన్నర్ చేయాలనుకుంటున్నారు, వేదిక ఎలా ఉంది. ఆ వివరాలు మోడల్‌కు అందుతాయి మరియు ప్రతి భవిష్యత్ మ్యాచ్‌ను మారుస్తాయి.",
      "hm.matchmaker.loop_1": "సహచరుల \"మళ్ళీ డిన్నర్ చేయాలి\" ఓట్లు మీ పెయిరింగ్‌లను బలపరుస్తాయి",
      "hm.matchmaker.loop_2": "మ్యూచువల్-నో ఓట్లు నిశ్శబ్దంగా ఆ జంటలను తొలగిస్తాయి",
      "hm.matchmaker.loop_3": "వేదిక & సంభాషణ రేటింగ్‌లు సీట్ల ఎంపికను మెరుగుపరుస్తాయి",
      "hm.matchmaker.loop_4": "రిలయబిలిటీ స్కోర్ సభ్యులను పదే పదే గైర్హాజరయ్యే వారి నుండి రక్షిస్తుంది",
      "hm.matchmaker.loop_5": "లెర్నింగ్ బయాస్ నెలలు గడిచేకొద్దీ మంచి మ్యాచ్‌లను బలపరుస్తుంది",
      "hm.matchmaker.loop_6": "నియంత్రణ మీ చేతుల్లోనే ఉంటుంది — ఎప్పుడైనా వైదొలగవచ్చు లేదా మీ ప్రొఫైల్‌ను రీసెట్ చేయవచ్చు",
      "hm.matchmaker.vs_kicker": "నాకు ఏది సరైనది?",
      "hm.matchmaker.vs_title": "Matchmaker లేదా Circles?",
      "hm.matchmaker.vs_mm_ttl": "నాకు వైవిధ్యం కావాలంటే దీన్ని ఎంచుకోండి.",
      "hm.matchmaker.vs_mm_1": "ప్రతిసారీ వేర్వేరు టేబుల్, నగరం, వంటకాలు",
      "hm.matchmaker.vs_mm_2": "AI ద్వారా ఎంచుకోబడిన 4-8 మంది అపరిచితుల సమూహం",
      "hm.matchmaker.vs_mm_3": "వారానికో క్రమం, హోస్ట్ బాధ్యతలు లేవు",
      "hm.matchmaker.vs_mm_4": "మీరు నగరానికి కొత్తగా వచ్చినా లేదా ప్రయాణంలో ఉన్నా బాగుంటుంది",
      "hm.matchmaker.vs_mm_cta": "మ్యాచింగ్ క్విజ్ తీసుకోండి",
      "hm.matchmaker.vs_circles_ttl": "నాకు ఒక క్రమబద్ధత కావాలంటే దీన్ని ఎంచుకోండి.",
      "hm.matchmaker.vs_circles_1": "అదే హోస్ట్ · అదే వేదిక · అదే వారపు రాత్రి",
      "hm.matchmaker.vs_circles_2": "మీ బుధవారం రాత్రి అలవాటుగా మారే సమూహం",
      "hm.matchmaker.vs_circles_3": "అనుభవజ్ఞుడైన హోస్ట్ ద్వారా ఎంపిక చేయబడింది",
      "hm.matchmaker.vs_circles_4": "మీరు ఇప్పుడే ఇక్కడికి మారి, శాశ్వత బృందం కావాలనుకుంటే బాగుంటుంది",
      "hm.matchmaker.vs_circles_cta": "Circles బ్రౌజ్ చేయండి",
      "hm.matchmaker.bottom_title": "ఒక క్విజ్. ఆ తర్వాత జరిగే ప్రతి డిన్నర్ మీకు సరిపోతుంది.",
      "hm.matchmaker.bottom_sub": "మీకు రెండు వారాల్లోనే మ్యాచ్ లభిస్తుంది. ఈ నెలలో మీరు చేసే అత్యంత సులభమైన నిర్ణయం ఇదే అవుతుంది.",
      "hm.matchmaker.bottom_cta": "మ్యాచింగ్ క్విజ్ తీసుకోండి",
      "hm.matchmaker.bottom_note": "క్రెడిట్ కార్డ్ అవసరం లేదు · 10 నిమిషాలు · ఎప్పుడైనా అన్‌మ్యాచ్ చేయవచ్చు",
      /* AUTO-TRANSLATED hm.* END */
    },
  },
  "zh-HK": {
    translation: {
      /* AUTO-TRANSLATED finder.* (added) */
      "finder.title": "HustleFinder",
      "finder.tagline": "搵最近嘅公共地方 — 真實數據,全澳洲覆蓋。",
      "finder.search_placeholder": "搜尋地點、街道或地區…",
      "finder.hint": "喺上面開始輸入、點擊類別或者用「附近」。",
      "finder.near_me": "附近",
      "finder.searching": "搜尋緊…",
      "finder.suburb.placeholder": "按地區篩選(例如 Darwin, NT)…",
      "finder.suburb.clear": "清除",
      "finder.search_in": "喺{{cat}}度搜尋",
      "finder.empty": "冇結果 — 試下其他類別或搜尋詞。",
      "finder.meta.title": "HustleFinder — 免費澳洲地區指南",
      "finder.meta.desc": "搜尋本地商店、充電站、咖啡店、洗手間同更多,覆蓋全澳洲每個地區 — 100%免費。",
      "finder.footer.credits": "數據來源及版權聲明",
      "finder.footer.terms": "條款",
      "finder.cat.toilet": "洗手間",
      "finder.cat.fountain": "飲水機",
      "finder.cat.transport": "交通",
      "finder.cat.park": "公園",
      "finder.cat.medical": "醫療",
      "finder.cat.school": "學校",
      "finder.cat.library": "圖書館",
      "finder.cat.petrol": "油站",
      "finder.cat.kidzone": "兒童專區",
      "finder.cat.puzzles": "益智遊戲",
      "finder.cat.roadtrip": "公路旅行",
      "finder.hotels.title": "搵附近酒店",
      "finder.hotels.sub": "一次搜尋比較200+個訂房網站 — 由 Trivago 提供。",
      "finder.hotels.where": "地點",
      "finder.hotels.where_ph": "城市或地區(例如 Byron Bay, NSW)",
      "finder.hotels.checkin": "入住日期",
      "finder.hotels.checkout": "退房日期",
      "finder.hotels.adults": "成人",
      "finder.hotels.search": "搜尋酒店",
      "finder.hotels.disclosure": "將會開啟 Trivago — 我哋可能從訂房中賺取佣金。",
      /* AUTO-TRANSLATED rt.* START */
      "rt.brand_name": "Roadtrip Planner",
      "rt.page_title": "澳洲電動車自駕遊規劃",
      "rt.from": "起點",
      "rt.to": "終點",
      "rt.from_placeholder": "地區、市鎮或城市（例如：Newcastle NSW）",
      "rt.to_placeholder": "地區、市鎮或城市（例如：Hobart TAS）",
      "rt.stop": "STOP",
      "rt.stop_placeholder": "中途站",
      "rt.add_stop": "新增中途站",
      "rt.upgrade_pro": "升級至 Pro 以新增多達 20 個中途站",
      "rt.reverse": "反轉路線（回程）",
      "rt.from_current": "由目前位置出發",
      "rt.range_per_charge": "每充滿一次電嘅續航距離 (km)",
      "rt.connector": "充電插頭",
      "rt.connector_any": "任何",
      "rt.drive_hours_day": "每日駕駛時數",
      "rt.accommodation": "住宿",
      "rt.accom_any": "任何 (BnB · 酒店 · 露營車營地)",
      "rt.accom_bnb": "BnB",
      "rt.accom_hotel": "酒店",
      "rt.accom_caravan": "露營車營地",
      "rt.min_rating": "最低評分",
      "rt.any_rating": "任何評分",
      "rt.only_5star": "只要 5★",
      "rt.schedule": "行程時間表：",
      "rt.depart_at": "出發時間",
      "rt.arrive_by": "到達時間",
      "rt.hotel_checkin": "酒店入住",
      "rt.hotel_checkout": "酒店退房",
      "rt.overnight_hint": "跨日行程唔會喺呢段時間之外安排。",
      "rt.whos_in_car": "邊個喺車入面？",
      "rt.adults": "大人",
      "rt.kids": "小朋友",
      "rt.ages": "年齡",
      "rt.show_attractions": "顯示景點",
      "rt.show_kids": "顯示適合小朋友嘅地點",
      "rt.overnight_stays": "住宿停留",
      "rt.fast_chargers_only": "只顯示快速充電站",
      "rt.hotel_must_charger": "酒店必須有充電設施",
      "rt.pet_only": "只顯示寵物友善住宿",
      "rt.safety": "預算安全係數",
      "rt.safety_90": "90% (平地)",
      "rt.safety_85": "85% (預設)",
      "rt.safety_80": "80% (山路 / 拖車)",
      "rt.safety_70": "70% (重載)",
      "rt.save_trip": "儲存行程",
      "rt.plan_my_trip": "規劃行程",
      "rt.refresh_trip": "重新整理行程",
      "rt.planning": "規劃中…",
      "rt.resume_title": "從呢度繼續行程",
      "rt.resume_body": "我哋已經紀錄咗你嘅目前位置。請輸入錶板顯示嘅剩餘續航距離，等我哋可以按實際剩餘電量（而非滿電）嚟規劃下一段路程。",
      "rt.remaining_range": "剩餘續航距離 (km)",
      "rt.cancel": "取消",
      "rt.replan_from_here": "從呢度重新規劃",
      "rt.save_modal_title": "命名呢個行程",
      "rt.save_modal_body": "已儲存嘅行程會放喺你嘅個人檔案入面 — 你可以隨時重新開啟並點擊「重新規劃」。",
      "rt.save_name_placeholder": "例如：Brisbane → Sydney 經 Byron",
      "rt.your_saved_trips": "你已儲存嘅行程",
      "rt.custom_location": "自訂地點",
      "rt.trip_summary": "行程摘要",
      "rt.itinerary": "詳細行程",
      "rt.no_stops_needed": "唔使停站 — 現有續航距離足夠跑晒全程。",
      "rt.chargers_in_corridor": "沿途充電站",
      "rt.planned_stops": "已規劃停站",
      "rt.overnight_stop": "住宿停站",
      "rt.overnight_stops": "住宿停站",
      "rt.suggested_detours": "建議繞道景點 — 勾選以新增",
      "rt.replan_with_detours": "加入繞道景點並重新規劃",
      "rt.attractions_label": "景點",
      "rt.kids_label": "小朋友",
      "rt.km_from_start": "距離起點公里",
      "rt.open_driving": "開啟駕駛模式",
      "rt.big_font_hint": "大字體，方便車架使用。支援 CarPlay/Android Auto 瀏覽器鏡像。",
      "rt.check_price_book": "喺 Trivago 查價及預訂 ↗",
      "rt.visit_site": "瀏覽網站 ↗",
      "rt.charger_onsite": "設有充電站",
      "rt.leg_exceeds_range": "呢段路程超過咗你設定嘅續航距離 — 你未到呢個充電站就會冇電。請減少續航預算或喺中途加個位補電。",
      "rt.add_waypoint_replan": "新增為途經點並重新規劃",
      /* AUTO-TRANSLATED rt.* END */
                      /* AUTO-TRANSLATED hm.* START */
      "hm.nav.hustlemeet": "HustleMeet",
      "hm.nav.circles": "Circles",
      "hm.nav.matchmaker": "Matchmaker",
      "hm.nav.meet": "Meet",
      "hm.nav.overview": "HustleMeetups",
      "hm.overview.page_title": "HustleMeetups — 三個溫馨嘅方式，等你可以搵返你嗰範人。",
      "hm.overview.kicker": "HustleSuite 旗下成員",
      "hm.overview.hero_title": "三個溫馨嘅方式，等你可以搵返你嗰範人。",
      "hm.overview.hero_title_accent": "你嗰範人",
      "hm.overview.hero_sub": "HustleMeetups 係 Australia 最親切嘅交友方式，幫你建立真正嘅友誼 —— 總有一種氣氛啱你。無論係想每星期一齊跑步、每個月同老友聚餐，定係想同 AI 幫你配對嘅新面孔見面，呢度都幫到你。",
      "hm.overview.tile_meet_kicker": "HustleMeet",
      "hm.overview.tile_meet_title": "搵到你嗰範人，一齊做你鍾意做嘅嘢。",
      "hm.overview.tile_meet_body": "Australia 以興趣行先嘅社群之家。跑友、程式員、投資者、健行者、舞者、早餐會、讀書組 —— 呢度有數百個群組，每星期都有活動。參加一次又得，個個禮拜去亦得。",
      "hm.overview.tile_meet_cta": "探索群組",
      "hm.overview.tile_circles_kicker": "Circles",
      "hm.overview.tile_circles_title": "同你嗰班死黨，次次都有新嘅枱等緊你。",
      "hm.overview.tile_circles_body": "帶埋你平時嗰班死黨嚟。我哋會負責訂新餐廳、設計問答環節、準備好紙牌，你哋只需準時出現。呢個係維繫現有圈子最親密嘅方式 —— 而且完全唔使任何人心煩去組織。",
      "hm.overview.tile_circles_cta": "開始一個 Circle",
      "hm.overview.tile_matchmaker_kicker": "Matchmaker",
      "hm.overview.tile_matchmaker_title": "AI 幫你揀枱，你人到就得。",
      "hm.overview.tile_matchmaker_body": "只需花 10 分鐘做個測驗。到咗聽日，我哋就會安排你同幾位陌生人同枱，包你食到甜品嗰陣，大家已經好似老友咁。次次都有唔同嘅驚喜。",
      "hm.overview.tile_matchmaker_cta": "開始測驗",
      "hm.overview.trust_kicker": "點解有效",
      "hm.overview.trust_title": "信任第一，飯局第二。",
      "hm.overview.trust_1_title": "已驗證成員",
      "hm.overview.trust_1_body": "每個群組都由真實身份核實同可靠度評分開始。絕對冇隨便棄用嘅帳號。",
      "hm.overview.trust_2_title": "真實主理人",
      "hm.overview.trust_2_body": "每個群組都由經過審核嘅主理人帶領，佢哋會負責帶動氣氛、安排節奏，並照顧現場每一個人。",
      "hm.overview.trust_3_title": "友誼至上",
      "hm.overview.trust_3_body": "呢度嘅一切都係為咗建立真正嘅友誼而設，絕對唔係交友約會 (dating)。",
      "hm.overview.trust_4_title": "零容忍審核",
      "hm.overview.trust_4_body": "一個舉報，一次審查，即時決定。破壞氣氛嘅人會喺當晚被移除。",
      "hm.overview.footer_line": "HustleSuite 旗下成員 — Australia 擁有及營運。",
      "hm.landing.page_title": "HustleMeet · 搵到你嗰範人，一齊做你鍾意做嘅嘢。",
      "hm.landing.kicker": "Australia 嘅興趣社交網絡",
      "hm.landing.hero_title": "搵到你嗰範人，一齊做你鍾意做嘅嘢。",
      "hm.landing.hero_title_accent": "你嗰範人",
      "hm.landing.hero_sub": "HustleMeet 係 Australia 以興趣行先嘅社群之家。Lunch time 一齊 debug 嘅程式員、朝早 6 點跑長途嘅跑友、每個月聚會嘅投資者，仲有行山組、舞團、早餐會同枱遊之夜。揀個群組，預約下次活動，然後直接出席就得。",
      "hm.landing.cta_explore_circles": "瀏覽群組",
      "hm.landing.cta_try_matchmaker": "建立你嘅群組",
      "hm.landing.trust_verified": "已驗證成員",
      "hm.landing.trust_venues": "經過審核嘅活動主理人",
      "hm.landing.trust_friendship": "友誼至上 —— 絕非約會 (dating)",
      "hm.landing.trust_moderation": "零容忍審核機制",
      "hm.landing.subbrand_circles_kicker": "Circles",
      "hm.landing.subbrand_circles_title": "已經有自己嘅死黨？俾個固定嘅「家」佢哋。",
      "hm.landing.subbrand_circles_body": "Circles 係為咗已經會定期見面嘅朋友而設 —— 讀書會、撲克之夜、跑友、或者同事。我哋會處理好場地、提醒同名單安排。你只需出席就得。",
      "hm.landing.subbrand_circles_b1": "輪換場地，一個固定名單",
      "hm.landing.subbrand_circles_b2": "自動提醒 + 簡單 RSVP",
      "hm.landing.subbrand_circles_b3": "問答比賽、紙牌同主題之夜，應有盡有",
      "hm.landing.subbrand_circles_cta": "開始一個 Circle",
      "hm.landing.subbrand_matchmaker_kicker": "Matchmaker",
      "hm.landing.subbrand_matchmaker_title": "AI 幫你揀枱，你人到就得。",
      "hm.landing.subbrand_matchmaker_body": "一個 10 分鐘嘅測驗 —— 關於性格、價值觀、空餘時間。我哋嘅 Matchmaker 會安排你同幾位陌生人同枱，包你食到甜品嗰陣，大家已經好似老友咁。次次都有新面孔。",
      "hm.landing.subbrand_matchmaker_b1": "Big-5 性格特質同價值觀評分",
      "hm.landing.subbrand_matchmaker_b2": "契合度達 0.70 或以上，第二日即收到邀請",
      "hm.landing.subbrand_matchmaker_b3": "從你每次嘅晚餐體驗中學習",
      "hm.landing.subbrand_matchmaker_cta": "開始測驗",
      "hm.landing.how_kicker": "HustleMeet 點樣運作",
      "hm.landing.how_title": "四個步驟。搵到你嘅群組。直接出席。",
      "hm.landing.how_step1_ttl": "揀選你嘅興趣",
      "hm.landing.how_step1_sub": "跑步、寫 code、投資、煮食、音樂、行山 —— 話俾我哋聽你鍾意啲乜，同埋你住邊。",
      "hm.landing.how_step2_ttl": "瀏覽附近嘅群組",
      "hm.landing.how_step2_sub": "每個群組都會公開主理人、聚會節奏同下次活動詳情。參加之前可以先了解下氣氛。",
      "hm.landing.how_step3_ttl": "RSVP 參加下次活動",
      "hm.landing.how_step3_sub": "一撳即成，自動加入月曆，活動前一日仲有貼心提醒。有啲活動免費，有啲要收費 —— 價格清清楚楚。",
      "hm.landing.how_step4_ttl": "現身並建立聯繫",
      "hm.landing.how_step4_sub": "見下主理人，識下班朋友，一齊玩。如果覺得啱 feel，下星期再見。",
      "hm.landing.stats_kicker": "Australia 社交新方式",
      "hm.landing.stats_title": "大家願意不斷返嚟嘅原因。",
      "hm.landing.stats_92_sub": "覺得喺活動中至少同一個人「啱嘴形」",
      "hm.landing.stats_68_sub": "喺一星期內 RSVP 參加下次活動",
      "hm.landing.stats_85_sub": "同群組入面嘅人保持聯絡",
      "hm.landing.why_kicker": "點解我哋有效",
      "hm.landing.why_title": "真實興趣。真實主理人。真實活動。",
      "hm.landing.why_1_ttl": "興趣行先，而非地點行先",
      "hm.landing.why_1_body": "你群組入面嘅人係真心鍾意你所鍾意嘅嘢 —— 呢個係建立真正友誼嘅捷徑。",
      "hm.landing.why_2_ttl": "真實活動主理人",
      "hm.landing.why_2_body": "每個群組都有一位經過審核嘅主理人。佢哋負責帶動氣氛、定好節奏，仲會喺有問題時出手幫手。佢哋唔係 Facebook 管理員，而係一個真人。",
      "hm.landing.why_3_ttl": "持續性係關鍵",
      "hm.landing.why_3_body": "每星期都有新活動，等你有啲嘢可以期待。呢種節奏會令陌生人變成你每個星期二晚嘅老友。",
      "hm.landing.why_4_ttl": "安全可靠",
      "hm.landing.why_4_body": "已驗證成員、公開場地、對不尊重行為零容忍。活動前、期間同之後，我哋嘅支援團隊都隨時準備幫手。",
      "hm.landing.why_5_ttl": "友誼至上",
      "hm.landing.why_5_body": "呢度唔係約會 app。呢度係一個社群。真實興趣，真實面孔，唔使再喺中間不停劃屏。",
      "hm.landing.why_6_ttl": "小班聚會更重要",
      "hm.landing.why_6_body": "大多數群組活動人數上限係 4-15 人。夠人一齊傾偈，亦唔會多人到令人覺得被忽略。",
      "hm.landing.cities_kicker": "服務覆蓋範圍",
      "hm.landing.cities_title": "喺 Australia 同 NZ 各地舉行嘅群組聚會。",
      "hm.landing.cities_sub_zero": "首批群組喺 15 個以上嘅城市開放 —— 提早加入，一齊塑造呢個空間。",
      "hm.landing.cities_sub_some": "群組已遍佈 15 個以上大城市。今個星期，你嗰區就有活動。",
      "hm.landing.cities_sub_one": "{{count}} 個活躍群組 · 每星期都有新活動 · 持續增加服務城市。",
      "hm.landing.cities_sub_many": "{{count}} 個活躍群組 · 每星期都有新活動 · 持續增加服務城市。",
      "hm.landing.forme_kicker": "如果你有以下想法，呢度就啱晒你……",
      "hm.landing.forme_title": "你想要真實嘅興趣、真實嘅人、真實嘅活動。",
      "hm.landing.forme_1": "你已經厭倦咗不斷劃屏嘅交友 app，想真正同志同道合嘅人見面。",
      "hm.landing.forme_2": "你剛搬到新城市，需要一班溫馨嘅朋友，而唔係多一個聊天群組。",
      "hm.landing.forme_3": "你重視深度交流多過社交媒體廣播 —— 一個好群組勝過五十個 follow。",
      "hm.landing.forme_4": "你相信「共同興趣 + 現實生活兩小時」先至係社交嘅真正秘訣。",
      "hm.landing.notme_kicker": "如果係咁，呢度未必啱你……",
      "hm.landing.notme_title": "HustleMeet 唔係對每個人都有效。",
      "hm.landing.notme_1": "你無法承諾出席 —— 主理人同其他成員都非常重視你嘅 RSVP。",
      "hm.landing.notme_2": "你正在尋找約會服務 (dating) —— 喺呢度，友誼永遠係第一位。",
      "hm.landing.notme_3": "你只想做廣播而唔想同人見面 —— 呢度只喺現實中有效。",
      "hm.landing.notme_4": "你期待有無窮無盡嘅免費福利 —— 部分活動係要收費嘅，而且價格永遠公開透明。",
      "hm.landing.safety_kicker": "我哋嘅安全承諾",
      "hm.landing.safety_title": "親切之餘，絕不馬虎。",
      "hm.landing.safety_1_ttl": "公開場地",
      "hm.landing.safety_1_body": "每個活動都會喺有員工嘅公開場地進行 —— Cafe、餐廳、公園、健身室或工作室。除非每位參加者都同意，否則絕唔會喺任何人家中舉行。",
      "hm.landing.safety_2_ttl": "社群準則",
      "hm.landing.safety_2_body": "對不尊重、騷擾或不誠實行為零容忍。違反準則嘅成員會被即時移除。",
      "hm.landing.safety_3_ttl": "私隱受保護",
      "hm.landing.safety_3_body": "你嘅個人資料絕唔會公開分享。活動之後，你可以決定同邊個繼續保持聯繫。",
      "hm.landing.safety_4_ttl": "支援隨時待命",
      "hm.landing.safety_4_body": "如果你有任何問題或擔憂，我哋嘅團隊喺活動前、期間同之後都可以提供協助。",
      "hm.landing.bottom_title": "撳一下，搵到你下一個最愛嘅群組。",
      "hm.landing.bottom_sub": "揀選你嘅方式 —— 加入現有群組、將你嘅老友帶入 Circles，或者等 Matchmaker 幫你安排一張啱你嘅枱。",
      "hm.landing.bottom_cta_circles": "開始一個 Circle",
      "hm.landing.bottom_cta_mm": "參加 Matchmaker 測驗",
      "hm.circles.page_title": "HustleMeet Circles · 同你嗰班死黨，次次都有新場地",
      "hm.circles.kicker": "HustleMeet · Circles",
      "hm.circles.hero_title": "同你嗰班死黨，次次都有新嘅枱等緊你。",
      "hm.circles.hero_title_accent": "你嗰班死黨",
      "hm.circles.hero_sub": "已經有咗一班好朋友？Circles 係維繫佢哋最簡單嘅方式。我哋會負責訂場、設計問答、準備遊戲。你只需人到就得 —— 同一班人，新嘅夜晚，新嘅地方。",
      "hm.circles.cta_find": "開始一個 Circle",
      "hm.circles.cta_host_hero": "加入一個開放嘅 Circle",
      "hm.circles.trust_venue": "定期更換經過審核嘅場地",
      "hm.circles.trust_party": "每個 Circle 4-12 位好友",
      "hm.circles.trust_verified": "已驗證成員",
      "hm.circles.trust_friendship": "維繫親密嘅真正友誼",
      "hm.circles.how_kicker": "點樣運作",
      "hm.circles.how_title": "四個步驟。等死黨永遠係死黨。",
      "hm.circles.how_step1_ttl": "開始你嘅 Circle",
      "hm.circles.how_step1_sub": "改個名，邀請你嘅朋友 (最多 12 人)，揀個節奏 —— 每週、每兩週定係每月。",
      "hm.circles.how_step2_ttl": "我哋負責搵場地",
      "hm.circles.how_step2_sub": "每次活動我哋都會建議新地點 —— 餐廳、問答之夜、撲克室、酒廊。或者你亦可以固定揀返最愛嗰間。",
      "hm.circles.how_step3_ttl": "一撳即 RSVP",
      "hm.circles.how_step3_sub": "自動提醒、自動分帳、預付座位，以免有人失約毀咗成個計劃。前菜都未上，張單就已經搞掂。",
      "hm.circles.how_step4_ttl": "保持節奏",
      "hm.circles.how_step4_sub": "月曆入面固定咗嗰一晚，以後都唔使再煩點組織。友誼會隨住月份增加而日益深厚。",
      "hm.circles.cadence_weekly_ttl": "每週",
      "hm.circles.cadence_weekly_sub": "每個星期",
      "hm.circles.cadence_weekly_body": "啱晒想喺星期二晚建立固定儀式感嘅朋友 —— 同樣嘅面孔，次次新地方。",
      "hm.circles.cadence_fortnight_ttl": "每兩週",
      "hm.circles.cadence_fortnight_sub": "每 2 個星期",
      "hm.circles.cadence_fortnight_body": "最完美嘅平衡 —— 保持聯繫之餘，又唔會對繁忙嘅生活造成太大負擔。",
      "hm.circles.cadence_monthly_ttl": "每月",
      "hm.circles.cadence_monthly_sub": "每個月",
      "hm.circles.cadence_monthly_body": "特別場合嘅 Circles —— 星期日大餐、大型問答之夜、品味餐單、撲克局。",
      "hm.circles.feast_kicker": "揀選你嘅夜晚",
      "hm.circles.feast_title": "三種慶祝方式",
      "hm.circles.feast_sub": "每個 Circle 都可以決定當晚點玩。可以輪流轉，或者固定揀你哋最鍾意嗰種。",
      "hm.circles.feast_surprise_ttl": "嘗試新餐廳",
      "hm.circles.feast_surprise_body": "我哋會列出附近三間場地，大家投票，廚房會為你哋預備好大枱。自動分帳，預付座位。",
      "hm.circles.feast_preselect_ttl": "問答比賽 / 紙牌 / 遊戲",
      "hm.circles.feast_preselect_body": "預訂問答之夜、撲克室、桌遊酒吧或者保齡球道。場地、報名費同提醒，全部由我哋搞掂。",
      "hm.circles.feast_per_event_ttl": "一次性主題之夜",
      "hm.circles.feast_per_event_body": "品酒、名廚餐桌、雞尾酒班、密室逃脫。偶爾嚟啲特別嘢 —— 全部安排妥當。",
      "hm.circles.live_kicker": "目前進行中",
      "hm.circles.live_title": "你可以加入嘅 Circles",
      "hm.circles.live_host_link": "開始你自己嘅 Circle",
      "hm.circles.live_loading": "載入中……",
      "hm.circles.live_empty_title": "成為你城市入面第一個 Circle。",
      "hm.circles.live_empty_sub": "同三個朋友一齊開始，我哋會幫你邀請其餘嘅人 —— 仲會請你哋去頭三間場地。",
      "hm.circles.live_empty_cta": "開始一個 Circle",
      "hm.circles.host_kicker": "致 Circle 領導者",
      "hm.circles.host_title": "你帶人嚟，瑣碎事交俾我哋。",
      "hm.circles.host_body": "如果你係嗰個成日負責組織聚會嘅朋友，Circles 可以幫你慳返好多時間。自動提醒、預付 RSVP、場地清單、分帳、候補名單管理，仲有每次聚會嘅現金回贈。你只需好似普通成員咁出席就得。",
      "hm.circles.host_perk_1": "自動場地清單 —— 以後唔使再喺 Google 慢慢搵",
      "hm.circles.host_perk_2": "預付座位 —— 零放飛機率",
      "hm.circles.host_perk_3": "甜品未上，帳單已經分好",
      "hm.circles.host_perk_4": "自動提醒同月曆邀請",
      "hm.circles.host_perk_5": "候補名單：有人取消即自動補上",
      "hm.circles.host_perk_6": "每次聚會都有現金回贈",
      "hm.circles.host_cta": "立即開始一個 Circle",
      "hm.circles.why_kicker": "點解 Circles 有效",
      "hm.circles.why_title": "成年人嘅友誼，都需要一個妥善嘅安排",
      "hm.circles.why_1_ttl": "原班人馬，更深厚嘅信任",
      "hm.circles.why_1_body": "生活總會令朋友漸行漸遠。Circles 係一種固定嘅節奏，令大家保持親密 —— 同樣嘅 4-12 位好友，無論如何，喺同一個平日夜晚見面。",
      "hm.circles.why_2_ttl": "行政瑣事零負擔",
      "hm.circles.why_2_body": "唔使再有人做 WhatsApp 群組負責人。我哋搵場地、發提醒、處理付款、搞掂所有細節。",
      "hm.circles.why_3_ttl": "新場地，新回憶",
      "hm.circles.why_3_body": "輪流體驗 Australia 最正嘅特色餐廳、問答室同遊戲酒吧。熟悉嘅面孔，新鮮嘅背景 —— 完美結合咗規律同新奇感。",
      "hm.circles.bottom_title": "唔使任何人辛苦計劃，大家最期待嘅聚會就搞掂。",
      "hm.circles.bottom_sub": "一個 Circle。原班好友。次次新場地。",
      "hm.circles.bottom_cta_find": "開始一個 Circle",
      "hm.circles.bottom_cta_mm": "透過 Matchmaker 識新朋友 →",
      "hm.circles.card_host_pill": "Circle",
      "hm.matchmaker.page_title": "HustleMeet Matchmaker · AI 幫你揀最啱嘅枱",
      "hm.matchmaker.kicker": "HustleMeet · Matchmaker",
      "hm.matchmaker.hero_title": "AI 幫你揀枱，你人到就得。",
      "hm.matchmaker.hero_title_accent": "你人到就得。",
      "hm.matchmaker.hero_sub": "完成一個簡單嘅性格同價值觀測驗。我哋嘅 Matchmaker 會安排你同四到七位陌生人同枱，包你食到甜品嗰陣，大家已經好似老友咁。唔使劃屏，唔使夾硬搵話題。當你準備好，一張啱你嘅枱就喺度。",
      "hm.matchmaker.cta_quiz": "參加配對測驗",
      "hm.matchmaker.cta_see_circles": "睇下 Circles →",
      "hm.matchmaker.trust_big5": "Big-5 性格特質評分",
      "hm.matchmaker.trust_seats": "每枱 4-8 個位",
      "hm.matchmaker.trust_lock": "零劃屏 · 零相片揀人",
      "hm.matchmaker.trust_verified": "只限已驗證成員",
      "hm.matchmaker.how_kicker": "點樣運作",
      "hm.matchmaker.how_title": "四個步驟，搵到一張已經「夾好晒」嘅枱。",
      "hm.matchmaker.how_step1_ttl": "完成測驗",
      "hm.matchmaker.how_step1_sub": "十分鐘嘅問卷 —— 性格、價值觀、飲食習慣、空餘時間。只需做一次。",
      "hm.matchmaker.how_step2_ttl": "隔夜配對",
      "hm.matchmaker.how_step2_sub": "我哋嘅 AI 會分析你嘅檔案同 Australia 嘅實時飯局情況。當契合度分數達到 0.70 或以上，系統就會發出個人邀請。",
      "hm.matchmaker.how_step3_ttl": "接受或拒絕",
      "hm.matchmaker.how_step3_sub": "你會收到一封清晰嘅邀請電郵，寫明場地、餐桌主題，同埋你同呢班人共同擁有嘅三個興趣。",
      "hm.matchmaker.how_step4_ttl": "系統會持續學習",
      "hm.matchmaker.how_step4_sub": "每次聚餐後嘅調查都會幫助模型微調。你食得越多，下一次配對就會越精準。",
      "hm.matchmaker.science_kicker": "點解有效",
      "hm.matchmaker.science_title": "Matchmaker 唔係靠估 —— 佢係靠數據評分。",
      "hm.matchmaker.science_sub": "每次配對都係由四個因素加權而成。當全枱平均分超過 0.70，我哋先會發出邀請。未達標嘅會繼續留喺隊列等待。",
      "hm.matchmaker.science_1_ttl": "性格 (35%)",
      "hm.matchmaker.science_1_body": "Big-5 性格向量嘅餘弦相似度 —— 包含開放性、盡責性、外向性、宜人性同情緒穩定性。",
      "hm.matchmaker.science_2_ttl": "價值觀 (25%)",
      "hm.matchmaker.science_2_body": "衡量能令聚餐對話真正產生共鳴嘅價值觀重疊度 —— 家庭、好奇心、成長、服務、冒險。",
      "hm.matchmaker.science_3_ttl": "興趣 (25%)",
      "hm.matchmaker.science_3_body": "衡量你特定興趣嘅 Jaccard 重疊度 —— 例如行 Six Foot Track、Sydney 影迷會、Melbourne 咖啡文化、或者你今年睇緊嘅第三本書。",
      "hm.matchmaker.science_4_ttl": "空餘時間 (15%)",
      "hm.matchmaker.science_4_body": "你真正可以出席嘅夜晚。如果你個 calendar 話星期二晚永遠要去做 gym，我哋就唔會發星期二 7pm 嘅邀請俾你。",
      "hm.matchmaker.safety_kicker": "安全設計",
      "hm.matchmaker.safety_title": "Matchmaker 始終保持謹慎。",
      "hm.matchmaker.safety_1_ttl": "信心門檻",
      "hm.matchmaker.safety_1_body": "群組評分低於 0.70，就唔會發出邀請。我哋寧願你唔食呢餐，都唔想俾一個勉強嘅配對你。",
      "hm.matchmaker.safety_2_ttl": "互不相見名單",
      "hm.matchmaker.safety_2_body": "如果兩位成員喺聚餐後私下表示「唔想再見」，我哋永遠唔會再安排佢哋同枱。",
      "hm.matchmaker.safety_3_ttl": "自動停機機制",
      "hm.matchmaker.safety_3_body": "如果最近嘅調查中有 20% 或以上嘅人評分低於 2/5，該枱嘅邀請會暫停 24 小時，由專人審查。",
      "hm.matchmaker.safety_4_ttl": "社群準則",
      "hm.matchmaker.safety_4_body": "每位成員都必須接受一套簡單易明嘅行為守則。違規者會被即時移除，且不設上訴。",
      "hm.matchmaker.safety_5_ttl": "只限公開場地",
      "hm.matchmaker.safety_5_body": "所有 Matchmaker 晚餐都喺有員工嘅真實餐廳舉行，絕唔會喺任何人家中進行。",
      "hm.matchmaker.safety_6_ttl": "活動後回饋",
      "hm.matchmaker.safety_6_body": "你嘅私下調查會決定未來嘅配對，亦可以觸發對場地或同伴嘅管理員審查。",
      "hm.matchmaker.loop_kicker": "學習閉環",
      "hm.matchmaker.loop_title": "每一餐都會令下一餐更好。",
      "hm.matchmaker.loop_body": "Matchmaker 並唔係一成不變嘅。每次活動後，你都會填一份簡單嘅私下調查 —— 對話感覺點、你想同邊個再食飯、場地係咪滿意。呢啲訊號會反饋到模型中，重新塑造未來嘅每一次配對。",
      "hm.matchmaker.loop_1": "同伴嘅「想再食飯」投票會強化你哋嘅配對",
      "hm.matchmaker.loop_2": "互相不感興趣嘅投票會默默取消配對",
      "hm.matchmaker.loop_3": "場地同對話評分會調整座位組合",
      "hm.matchmaker.loop_4": "可靠度評分保護成員免受重複放飛機嘅困擾",
      "hm.matchmaker.loop_5": "學習偏差會隨住月份增加而強化優質配對",
      "hm.matchmaker.loop_6": "你始終擁有控制權 —— 可以隨時退出或重設檔案",
      "hm.matchmaker.vs_kicker": "邊個至啱我？",
      "hm.matchmaker.vs_title": "Matchmaker 定係 Circles？",
      "hm.matchmaker.vs_mm_ttl": "如果你追求多樣性，揀我。",
      "hm.matchmaker.vs_mm_1": "每次都有唔同嘅餐桌、城市、菜式",
      "hm.matchmaker.vs_mm_2": "由 AI 挑選 4-8 位志趣相投嘅陌生人",
      "hm.matchmaker.vs_mm_3": "每週節奏，唔使負擔主理人嘅承諾",
      "hm.matchmaker.vs_mm_4": "好適合啱到新城市或旅行中嘅你",
      "hm.matchmaker.vs_mm_cta": "參加配對測驗",
      "hm.matchmaker.vs_circles_ttl": "如果你追求穩定性，揀我。",
      "hm.matchmaker.vs_circles_1": "固定主理人 · 固定場地 · 固定平日晚",
      "hm.matchmaker.vs_circles_2": "一個會變成你星期三傳統嘅群組",
      "hm.matchmaker.vs_circles_3": "由經驗豐富嘅主理人手動挑選",
      "hm.matchmaker.vs_circles_4": "好適合剛搬嚟想紮根嘅你",
      "hm.matchmaker.vs_circles_cta": "瀏覽 Circles",
      "hm.matchmaker.bottom_title": "一個測驗。之後每一餐都「啱晒咀形」。",
      "hm.matchmaker.bottom_sub": "你將會喺兩星期內獲得配對。呢將會係你今個月最容易講「好」嘅決定。",
      "hm.matchmaker.bottom_cta": "參加配對測驗",
      "hm.matchmaker.bottom_note": "無需信用卡 · 10 分鐘完成 · 隨時取消配對",
      /* AUTO-TRANSLATED hm.* END */
    },
  },
  th: {
    translation: {
      /* AUTO-TRANSLATED finder.* (added) */
      "finder.title": "HustleFinder",
      "finder.tagline": "ค้นหาสถานที่สาธารณะใกล้ที่สุด — ข้อมูลจริง ทั่วออสเตรเลีย",
      "finder.search_placeholder": "ค้นหาสถานที่ ถนน หรือย่าน…",
      "finder.hint": "เริ่มพิมพ์ด้านบน แตะหมวดหมู่ หรือใช้ \\\"ใกล้ฉัน\\",
      "finder.near_me": "ใกล้ฉัน",
      "finder.searching": "กำลังค้นหา…",
      "finder.suburb.placeholder": "กรองตามย่าน (เช่น Darwin, NT)…",
      "finder.suburb.clear": "ล้าง",
      "finder.search_in": "ค้นหาใน {{cat}}",
      "finder.empty": "ไม่มีผลลัพธ์ — ลองหมวดหมู่อื่นหรือคำค้นหาอื่น",
      "finder.meta.title": "HustleFinder — ไดเรกทอรีย่านออสซี่ฟรี",
      "finder.meta.desc": "ค้นหาธุรกิจท้องถิ่น จุดชาร์จ คาเฟ่ ห้องน้ำ และอื่นๆ ทั่วทุกย่านในออสเตรเลีย — ฟรี 100%",
      "finder.footer.credits": "เครดิตข้อมูลและการระบุแหล่งที่มา",
      "finder.footer.terms": "เงื่อนไข",
      "finder.cat.toilet": "ห้องน้ำ",
      "finder.cat.fountain": "น้ำดื่ม",
      "finder.cat.transport": "การเดินทาง",
      "finder.cat.park": "สวนสาธารณะ",
      "finder.cat.medical": "การแพทย์",
      "finder.cat.school": "โรงเรียน",
      "finder.cat.library": "ห้องสมุด",
      "finder.cat.petrol": "น้ำมัน",
      "finder.cat.kidzone": "โซนเด็ก",
      "finder.cat.puzzles": "ปริศนา",
      "finder.cat.roadtrip": "ทริปถนน",
      "finder.hotels.title": "ค้นหาโรงแรมใกล้เคียง",
      "finder.hotels.sub": "เปรียบเทียบเว็บจอง 200+ แห่งในการค้นหาครั้งเดียว — ขับเคลื่อนโดย Trivago",
      "finder.hotels.where": "ที่ไหน",
      "finder.hotels.where_ph": "เมืองหรือย่าน (เช่น Byron Bay, NSW)",
      "finder.hotels.checkin": "เช็คอิน",
      "finder.hotels.checkout": "เช็คเอาท์",
      "finder.hotels.adults": "ผู้ใหญ่",
      "finder.hotels.search": "ค้นหาโรงแรม",
      "finder.hotels.disclosure": "เปิด Trivago — เราอาจได้รับค่าคอมมิชชั่นจากการจอง",
      /* AUTO-TRANSLATED rt.* START */
      "rt.brand_name": "Roadtrip Planner",
      "rt.page_title": "เครื่องมือวางแผนโรดทริป EV ในออสเตรเลีย",
      "rt.from": "จาก",
      "rt.to": "ถึง",
      "rt.from_placeholder": "ชานเมือง, เมือง หรือจังหวัด (เช่น Newcastle NSW)",
      "rt.to_placeholder": "ชานเมือง, เมือง หรือจังหวัด (เช่น Hobart TAS)",
      "rt.stop": "STOP",
      "rt.stop_placeholder": "จุดแวะระหว่างทาง",
      "rt.add_stop": "เพิ่มจุดแวะ",
      "rt.upgrade_pro": "อัปเกรดเป็น Pro เพื่อเพิ่มจุดแวะได้สูงสุด 20 แห่ง",
      "rt.reverse": "สลับเส้นทาง (คำนวณขากลับ)",
      "rt.from_current": "จากตำแหน่งปัจจุบัน",
      "rt.range_per_charge": "ระยะทางต่อการชาร์จ (กม.)",
      "rt.connector": "หัวชาร์จ",
      "rt.connector_any": "ทั้งหมด",
      "rt.drive_hours_day": "ชั่วโมงขับรถ/วัน",
      "rt.accommodation": "ที่พัก",
      "rt.accom_any": "ทุกประเภท (BnB · โรงแรม · คาร์ราแวนพาร์ค)",
      "rt.accom_bnb": "BnB",
      "rt.accom_hotel": "โรงแรม",
      "rt.accom_caravan": "คาร์ราแวนพาร์ค",
      "rt.min_rating": "คะแนนขั้นต่ำ",
      "rt.any_rating": "ทุกคะแนน",
      "rt.only_5star": "5★ เท่านั้น",
      "rt.schedule": "ตารางเวลา:",
      "rt.depart_at": "ออกเดินทางเวลา",
      "rt.arrive_by": "ถึงที่หมายเวลา",
      "rt.hotel_checkin": "เช็คอินโรงแรม",
      "rt.hotel_checkout": "เช็คเอาท์โรงแรม",
      "rt.overnight_hint": "ช่วงการเดินทางข้ามคืนจะไม่ถูกกำหนดไว้นอกเหนือเวลาเหล่านี้",
      "rt.whos_in_car": "ใครอยู่ในรถบ้าง?",
      "rt.adults": "ผู้ใหญ่",
      "rt.kids": "เด็ก",
      "rt.ages": "อายุ",
      "rt.show_attractions": "แสดงสถานที่ท่องเที่ยว",
      "rt.show_kids": "แสดงจุดแวะพักสำหรับเด็ก",
      "rt.overnight_stays": "พักค้างคืน",
      "rt.fast_chargers_only": "เฉพาะเครื่องชาร์จความเร็วสูง",
      "rt.hotel_must_charger": "โรงแรมต้องมีที่ชาร์จ",
      "rt.pet_only": "เฉพาะที่พักที่ให้นำสัตว์เลี้ยงเข้าได้",
      "rt.safety": "ความปลอดภัย",
      "rt.safety_90": "90% (ทางราบ)",
      "rt.safety_85": "85% (ค่าเริ่มต้น)",
      "rt.safety_80": "80% (ทางเขา / ลากจูง)",
      "rt.safety_70": "70% (บรรทุกหนัก)",
      "rt.save_trip": "บันทึกการเดินทาง",
      "rt.plan_my_trip": "วางแผนการเดินทาง",
      "rt.refresh_trip": "รีเฟรชการเดินทาง",
      "rt.planning": "กำลังวางแผน…",
      "rt.resume_title": "เดินทางต่อจากที่นี่",
      "rt.resume_body": "เราบันทึกตำแหน่งปัจจุบันของคุณแล้ว โปรดระบุระยะทางที่เหลือซึ่งแสดงบนหน้าปัดรถ เพื่อให้เราวางแผนช่วงถัดไปตามจำนวนที่คุณมีอยู่จริง ไม่ใช่ชาร์จเต็ม",
      "rt.remaining_range": "ระยะทางที่เหลือ (กม.)",
      "rt.cancel": "ยกเลิก",
      "rt.replan_from_here": "วางแผนใหม่จากที่นี่",
      "rt.save_modal_title": "ตั้งชื่อการเดินทางนี้",
      "rt.save_modal_body": "ทริปที่บันทึกไว้จะอยู่ในโปรไฟล์ของคุณ — เปิดดูได้ทุกเมื่อแล้วกด วางแผนใหม่",
      "rt.save_name_placeholder": "เช่น Brisbane → Sydney ผ่าน Byron",
      "rt.your_saved_trips": "ทริปที่คุณบันทึกไว้",
      "rt.custom_location": "สถานที่กำหนดเอง",
      "rt.trip_summary": "สรุปการเดินทาง",
      "rt.itinerary": "แผนการเดินทาง",
      "rt.no_stops_needed": "ไม่จำเป็นต้องแวะพัก — ทริปนี้อยู่ในระยะทางการวิ่งของคุณ",
      "rt.chargers_in_corridor": "ที่ชาร์จตามเส้นทาง",
      "rt.planned_stops": "จุดแวะที่วางแผนไว้",
      "rt.overnight_stop": "จุดพักค้างคืน",
      "rt.overnight_stops": "จุดพักค้างคืน",
      "rt.suggested_detours": "เส้นทางอ้อมที่แนะนำ — ทำเครื่องหมายเพื่อเพิ่ม",
      "rt.replan_with_detours": "วางแผนใหม่พร้อมเส้นทางอ้อม",
      "rt.attractions_label": "สถานที่ท่องเที่ยว",
      "rt.kids_label": "สำหรับเด็ก",
      "rt.km_from_start": "กม. จากจุดเริ่มต้น",
      "rt.open_driving": "เปิดโหมดขับขี่",
      "rt.big_font_hint": "ตัวอักษรใหญ่ เหมาะกับที่วางโทรศัพท์ รองรับการสะท้อนหน้าจอผ่านเบราว์เซอร์บน CarPlay/Android Auto",
      "rt.check_price_book": "เช็คราคาและจองบน Trivago ↗",
      "rt.visit_site": "ไปยังเว็บไซต์ ↗",
      "rt.charger_onsite": "มีที่ชาร์จในสถานที่",
      "rt.leg_exceeds_range": "การเดินทางช่วงนี้เกินระยะที่กำหนดไว้ — พลังงานจะหมดก่อนถึงที่ชาร์จนี้ โปรดปรับลดระยะทางที่คาดไว้หรือเพิ่มจุดชาร์จระหว่างทาง",
      "rt.add_waypoint_replan": "เพิ่มเป็นจุดแวะและวางแผนใหม่",
      /* AUTO-TRANSLATED rt.* END */
                      /* AUTO-TRANSLATED hm.* START */
      "hm.nav.hustlemeet": "HustleMeet",
      "hm.nav.circles": "Circles",
      "hm.nav.matchmaker": "Matchmaker",
      "hm.nav.meet": "Meet",
      "hm.nav.overview": "HustleMeetups",
      "hm.overview.page_title": "HustleMeetups — 3 วิธีที่แสนอบอุ่นในการพบกับกลุ่มคนของคุณ.",
      "hm.overview.kicker": "ส่วนหนึ่งของ HustleSuite",
      "hm.overview.hero_title": "3 วิธีที่แสนอบอุ่นในการพบกับกลุ่มคนของคุณ.",
      "hm.overview.hero_title_accent": "กลุ่มคนของคุณ",
      "hm.overview.hero_sub": "HustleMeetups คือวิธีที่อ่อนโยนที่สุดใน Australia ในการสร้างมิตรภาพที่แท้จริง — เลือกบรรยากาศที่เหมาะกับคุณ ไม่ว่าคุณจะต้องการกลุ่มวิ่งประจำสัปดาห์ มื้อค่ำรายเดือนกับก๊วนเพื่อนเก่า หรือโต๊ะอาหารที่มีสมาชิกใหม่จากการจับคู่ด้วย AI ก็มีทางเลือกให้คุณเสมอ",
      "hm.overview.tile_meet_kicker": "HustleMeet",
      "hm.overview.tile_meet_title": "พบคนที่ใช่ ทำในสิ่งที่ชอบ.",
      "hm.overview.tile_meet_body": "แหล่งรวมกลุ่มที่เน้นความสนใจเป็นหลักใน Australia ไม่ว่าจะเป็นกลุ่มนักวิ่ง, นักเขียนโปรแกรม, นักลงทุน, กลุ่มเดินเล่น, นักเต้น, ชมรมอาหารเช้า, กลุ่มอ่านหนังสือ — มีกลุ่มนับร้อยและกิจกรรมจริงทุกสัปดาห์ จะมาร่วมครั้งเดียวหรือมาทุกสัปดาห์ก็ได้",
      "hm.overview.tile_meet_cta": "สำรวจกลุ่มต่างๆ",
      "hm.overview.tile_circles_kicker": "Circles",
      "hm.overview.tile_circles_title": "ก๊วนของคุณ กับโต๊ะใหม่ในทุกครั้ง.",
      "hm.overview.tile_circles_body": "พาเพื่อนกลุ่มประจำของคุณมา เราจะจองร้านอาหารใหม่ วางแผนกิจกรรมตอบคำถาม จัดเตรียมเกมให้ คุณแค่ปรากฏตัวพอ นี่คือวิธีที่กระชับที่สุดในการรักษาความสัมพันธ์ของก๊วนเพื่อน โดยไม่มีใครต้องเหนื่อยจัดการเอง",
      "hm.overview.tile_circles_cta": "เริ่มสร้าง Circle",
      "hm.overview.tile_matchmaker_kicker": "Matchmaker",
      "hm.overview.tile_matchmaker_title": "AI จัดโต๊ะให้ คุณแค่ไปตามนัด.",
      "hm.overview.tile_matchmaker_body": "ทำแบบทดสอบ 10 นาที เพียงชั่วข้ามคืน เราจะจัดให้คุณร่วมนั่งกับคนแปลกหน้าที่คุณจะรู้สึกสนิทเหมือนเพื่อนเก่าเมื่อถึงเวลาของหวาน โต๊ะใหม่เพื่อนใหม่ทุกครั้ง",
      "hm.overview.tile_matchmaker_cta": "ทำแบบทดสอบ",
      "hm.overview.trust_kicker": "ทำไมถึงได้ผล",
      "hm.overview.trust_title": "ความไว้วางใจต้องมาก่อน มื้ออาหารตามมาทีหลัง.",
      "hm.overview.trust_1_title": "สมาชิกที่ผ่านการยืนยันตัวตน",
      "hm.overview.trust_1_body": "ทุกกลุ่มเริ่มต้นด้วยการตรวจสอบ ID จริง + คะแนนความน่าเชื่อถือ ไม่มีการใช้บัญชีปลอมทิ้งขว้าง",
      "hm.overview.trust_2_title": "โฮสต์ที่มีตัวตนจริง",
      "hm.overview.trust_2_body": "ทุกกลุ่มนำโดยโฮสต์ที่ผ่านการคัดสรร ผู้ดูแลบรรยากาศ กำหนดจังหวะ และดูแลทุกคนในห้อง",
      "hm.overview.trust_3_title": "มิตรภาพต้องมาก่อน",
      "hm.overview.trust_3_body": "ทุกอย่างที่นี่ถูกออกแบบมาเพื่อมิตรภาพที่แท้จริง ไม่ใช่การหาคู่ ไม่ว่าจะกรณีใดก็ตาม",
      "hm.overview.trust_4_title": "การดูแลจัดการแบบยอมความไม่ได้",
      "hm.overview.trust_4_body": "หนึ่งการรายงาน หนึ่งการตรวจสอบ หนึ่งการตัดสินใจ ผู้ที่ทำตัวไม่เหมาะสมจะถูกเชิญออกในคืนนั้นทันที",
      "hm.overview.footer_line": "ส่วนหนึ่งของ HustleSuite — ดำเนินการและโฮสต์โดยคน Australian",
      "hm.landing.page_title": "HustleMeet · พบคนที่ใช่ ทำในสิ่งที่ชอบ.",
      "hm.landing.kicker": "เครือข่ายตามความสนใจของ Australia",
      "hm.landing.hero_title": "พบคนที่ใช่ ทำในสิ่งที่ชอบ.",
      "hm.landing.hero_title_accent": "กลุ่มคนของคุณ",
      "hm.landing.hero_sub": "HustleMeet คือแหล่งรวมกลุ่มที่เน้นความสนใจเป็นหลักใน Australia นักพัฒนาซอฟต์แวร์ที่คุยเรื่องแก้บัคระหว่างมื้อเที่ยง นักวิ่งระยะไกลตอนตี 5 นักลงทุนที่พบกันทุกเดือน กลุ่มเดินออกกำลังกาย ก๊วนเต้น ชมรมอาหารเช้า คืนเล่นบอร์ดเกม เลือกกลุ่ม จองกิจกรรมถัดไป แล้วไปสนุกกัน",
      "hm.landing.cta_explore_circles": "เรียกดูกลุ่มต่างๆ",
      "hm.landing.cta_try_matchmaker": "เริ่มสร้างกลุ่มของคุณเอง",
      "hm.landing.trust_verified": "สมาชิกที่ผ่านการยืนยันตัวตน",
      "hm.landing.trust_venues": "โฮสต์กิจกรรมที่ผ่านการคัดสรร",
      "hm.landing.trust_friendship": "มิตรภาพต้องมาก่อน — ไม่ใช่การหาคู่",
      "hm.landing.trust_moderation": "การดูแลจัดการแบบยอมความไม่ได้",
      "hm.landing.subbrand_circles_kicker": "Circles",
      "hm.landing.subbrand_circles_title": "มีก๊วนเพื่อนอยู่แล้วใช่ไหม? สร้างบ้านถาวรให้พวกเขาสิ.",
      "hm.landing.subbrand_circles_body": "Circles มีไว้สำหรับกลุ่มเพื่อนที่เจอกันเป็นประจำอยู่แล้ว — ชมรมอ่านหนังสือ คืนเล่นโป๊กเกอร์ ก๊วนวิ่ง เพื่อนที่ทำงาน เราจัดการเรื่องสถานที่ การเตือนความจำ และรายชื่อสมาชิก คุณแค่ปรากฏตัวพอ",
      "hm.landing.subbrand_circles_b1": "หมุนเวียนสถานที่ จัดการรายชื่อสมาชิกในที่เดียว",
      "hm.landing.subbrand_circles_b2": "ระบบเตือนอัตโนมัติ + ตอบรับคำเชิญ (RSVP) ได้ง่าย",
      "hm.landing.subbrand_circles_b3": "มีกิจกรรมตอบคำถาม เกมไพ่ และคืนธีมพิเศษพร้อมให้บริการ",
      "hm.landing.subbrand_circles_cta": "เริ่มสร้าง Circle",
      "hm.landing.subbrand_matchmaker_kicker": "Matchmaker",
      "hm.landing.subbrand_matchmaker_title": "AI จัดโต๊ะให้ คุณแค่ไปตามนัด.",
      "hm.landing.subbrand_matchmaker_body": "ทำแบบทดสอบ 10 นาที — ทั้งบุคลิกภาพ ค่านิยม และเวลาที่สะดวก Matchmaker ของเราจะจัดให้คุณร่วมนั่งกับคนแปลกหน้าที่คุณจะรู้สึกสนิทเหมือนเพื่อนเก่าเมื่อถึงเวลาของหวาน เปลี่ยนโต๊ะใหม่ทุกครั้ง",
      "hm.landing.subbrand_matchmaker_b1": "วัดผลบุคลิกภาพแบบ Big-5 และค่านิยม",
      "hm.landing.subbrand_matchmaker_b2": "ส่งคำเชิญข้ามคืนเมื่อผลลัพธ์ความเข้ากันได้อยู่ที่ 0.70 ขึ้นไป",
      "hm.landing.subbrand_matchmaker_b3": "เรียนรู้จากทุกมื้อค่ำที่คุณไปร่วม",
      "hm.landing.subbrand_matchmaker_cta": "ทำแบบทดสอบ",
      "hm.landing.how_kicker": "HustleMeet ทำงานอย่างไร",
      "hm.landing.how_title": "สี่ขั้นตอน พบกลุ่มของคุณ แล้วไปร่วมสนุก.",
      "hm.landing.how_step1_ttl": "เลือกสิ่งที่คุณสนใจ",
      "hm.landing.how_step1_sub": "วิ่ง, เขียนโค้ด, ลงทุน, ทำอาหาร, ดนตรี, เดินเล่น — บอกเราว่าคุณชอบอะไรและอาศัยอยู่ที่ไหน",
      "hm.landing.how_step2_ttl": "เรียกดูกลุ่มใกล้คุณ",
      "hm.landing.how_step2_sub": "ทุกกลุ่มจะแสดงข้อมูลโฮสต์ จังหวะการนัดหมาย และกิจกรรมถัดไป ทำความรู้จักบรรยากาศก่อนตัดสินใจเข้าร่วม",
      "hm.landing.how_step3_ttl": "ตอบรับคำเชิญกิจกรรมถัดไป",
      "hm.landing.how_step3_sub": "แตะครั้งเดียว เพิ่มลงปฏิทิน พร้อมระบบเตือนล่วงหน้าหนึ่งวัน บางกิจกรรมฟรี บางกิจกรรมมีค่าใช้จ่าย — ราคาทุกอย่างระบุไว้อย่างชัดเจน",
      "hm.landing.how_step4_ttl": "ปรากฏตัวและเชื่อมต่อ",
      "hm.landing.how_step4_sub": "พบโฮสต์ พบเพื่อนใหม่ ทำกิจกรรมร่วมกัน ถ้าถูกใจก็กลับมาใหม่ในสัปดาห์หน้า",
      "hm.landing.stats_kicker": "วิธีใหม่ในการพบปะผู้คนใน Australia",
      "hm.landing.stats_title": "เหตุผลที่ผู้คนยังคงกลับมาหาเรา.",
      "hm.landing.stats_92_sub": "รู้สึกเข้ากันได้ดีกับอย่างน้อยหนึ่งคนในกิจกรรม",
      "hm.landing.stats_68_sub": "ตอบรับคำเชิญ (RSVP) กิจกรรมถัดไปภายในหนึ่งสัปดาห์",
      "hm.landing.stats_85_sub": "ยังคงติดต่อกับบางคนจากกลุ่มนั้น",
      "hm.landing.why_kicker": "ทำไมเราถึงได้ผล",
      "hm.landing.why_title": "สนใจจริง โฮสต์จริง กิจกรรมจริง.",
      "hm.landing.why_1_ttl": "ความสนใจต้องมาก่อน ไม่ใช่สถานที่",
      "hm.landing.why_1_body": "คนในกลุ่มของคุณสนใจในสิ่งที่คุณสนใจจริงๆ นั่นคือทางลัดไปสู่มิตรภาพที่แท้จริง",
      "hm.landing.why_2_ttl": "โฮสต์กิจกรรมที่มีตัวตนจริง",
      "hm.landing.why_2_body": "ทุกกลุ่มมีโฮสต์ที่ผ่านการตรวจสอบซึ่งเป็นเจ้าของบรรยากาศ กำหนดจังหวะ และคอยดูแลความเรียบร้อย ไม่ใช่แค่แอดมิน Facebook แต่เป็นคนจริงๆ",
      "hm.landing.why_3_ttl": "ความสม่ำเสมอคือสิ่งสำคัญ",
      "hm.landing.why_3_body": "มีกิจกรรมใหม่ทุกสัปดาห์ มีบางอย่างให้ตั้งตารอ จังหวะชีวิตที่จะเปลี่ยนคนแปลกหน้าให้กลายเป็นก๊วนคืนวันอังคารของคุณ",
      "hm.landing.why_4_ttl": "ปลอดภัยและมั่นใจ",
      "hm.landing.why_4_body": "สมาชิกผ่านการยืนยันตัวตน สถานที่สาธารณะ และไม่ยอมรับการไม่ให้เกียรติกัน ทีมสนับสนุนของเราพร้อมแสตนบายทั้งก่อน ระหว่าง และหลังจบกิจกรรม",
      "hm.landing.why_5_ttl": "มิตรภาพต้องมาก่อน",
      "hm.landing.why_5_body": "นี่ไม่ใช่การหาคู่ แต่นี่คือชุมชน ความสนใจที่แท้จริง ผู้คนจริงๆ ไม่มีการปัดแอปไปมาระหว่างนั้น",
      "hm.landing.why_6_ttl": "ขนาดกลุ่มที่พอดี",
      "hm.landing.why_6_body": "กลุ่มส่วนใหญ่จำกัดที่นั่งไว้ที่ 4-15 ที่ ใหญ่พอที่จะเกิดบทสนทนาที่น่าสนใจ และเล็กพอที่จะไม่มีใครถูกลืม",
      "hm.landing.cities_kicker": "พื้นที่ที่เราให้บริการ",
      "hm.landing.cities_title": "กลุ่มต่างๆ นัดพบกันทั่ว Australia & NZ.",
      "hm.landing.cities_sub_zero": "กลุ่มแรกเริ่มเปิดแล้วในกว่า 15+ เมือง — เข้าร่วมก่อนเพื่อร่วมสร้างบรรยากาศ",
      "hm.landing.cities_sub_some": "กลุ่มเปิดให้บริการแล้วในกว่า 15+ เขตเมืองใหญ่ มีบางอย่างในเมืองของคุณในสัปดาห์นี้",
      "hm.landing.cities_sub_one": "{{count}} กลุ่มที่เปิดอยู่ · กิจกรรมใหม่ทุกสัปดาห์ · กำลังเพิ่มเมืองใหม่ๆ เสมอ",
      "hm.landing.cities_sub_many": "{{count}} กลุ่มที่เปิดอยู่ · กิจกรรมใหม่ทุกสัปดาห์ · กำลังเพิ่มเมืองใหม่ๆ เสมอ",
      "hm.landing.forme_kicker": "สิ่งนี้เหมาะสำหรับคุณ ถ้า…",
      "hm.landing.forme_title": "คุณต้องการความสนใจที่แท้จริง คนจริงๆ กิจกรรมจริงๆ.",
      "hm.landing.forme_1": "คุณเหนื่อยกับการปัดแอปหาคู่ และต้องการพบปะผู้คนผ่านสิ่งที่คนชอบทำจริงๆ",
      "hm.landing.forme_2": "คุณย้ายมาเมืองใหม่และต้องการก๊วนเพื่อนที่อบอุ่น ไม่ใช่แค่กลุ่มแชทอีกกลุ่ม",
      "hm.landing.forme_3": "คุณให้ความสำคัญกับความลึกซึ้งมากกว่าการกระจายข่าว — กลุ่มที่ดีกลุ่มเดียวชนะยอดฟอลโลว์ห้าสิบคน",
      "hm.landing.forme_4": "คุณเชื่อว่าความสนใจที่แชร์ร่วมกัน + เวลาที่กำหนดไว้สองชั่วโมงในชีวิตจริงคือสูตรลัดที่แท้จริง",
      "hm.landing.notme_kicker": "สิ่งนี้ไม่เหมาะสำหรับคุณ ถ้า…",
      "hm.landing.notme_title": "HustleMeet อาจไม่ได้เหมาะกับทุกคน.",
      "hm.landing.notme_1": "คุณไม่สามารถรักษาสัญญาที่จะไปร่วมงาน — โฮสต์และสมาชิกคนอื่นๆ หวังพึ่งพาการตอบรับ (RSVP) ของคุณ",
      "hm.landing.notme_2": "คุณกำลังมองหาบริการหาคู่ — ที่นี่มิตรภาพต้องมาก่อนเสมอ",
      "hm.landing.notme_3": "คุณต้องการแค่ป่าวประกาศโดยไม่คิดจะเจอใครจริงๆ — สิ่งนี้จะได้ผลก็ต่อเมื่อเจอหน้ากันเท่านั้น",
      "hm.landing.notme_4": "คุณคาดหวังของฟรีตลอดเวลา — บางกิจกรรมมีค่าใช้จ่าย ซึ่งเราจะแจ้งราคาล่วงหน้าเสมอ",
      "hm.landing.safety_kicker": "คำมั่นสัญญาด้านความปลอดภัย",
      "hm.landing.safety_title": "อบอุ่น แต่ไม่เคยละเลย.",
      "hm.landing.safety_1_ttl": "สถานที่สาธารณะ",
      "hm.landing.safety_1_body": "ทุกกิจกรรมจัดขึ้นในสถานที่สาธารณะที่มีพนักงานดูแล — คาเฟ่, ร้านอาหาร, สวนสาธารณะ, ยิม หรือสตูดิโอ ไม่มีการจัดที่บ้านส่วนตัว เว้นแต่ผู้เข้าร่วมทุกคนจะตกลงร่วมกัน",
      "hm.landing.safety_2_ttl": "มาตรฐานชุมชน",
      "hm.landing.safety_2_body": "ไม่ยอมรับการไม่ให้เกียรติกัน การคุกคาม หรือความไม่ซื่อสัตย์ สมาชิกที่ละเมิดจะถูกเชิญออกทันที",
      "hm.landing.safety_3_ttl": "ปกป้องความเป็นส่วนตัว",
      "hm.landing.safety_3_body": "รายละเอียดของคุณจะไม่ถูกแชร์ต่อสาธารณะ คุณควบคุมได้ว่าจะเลือกเชื่อมต่อกับใครต่อหลังจากจบกิจกรรม",
      "hm.landing.safety_4_ttl": "ทีมสนับสนุนพร้อมช่วย",
      "hm.landing.safety_4_body": "ทีมงานของเราพร้อมให้บริการก่อน ระหว่าง และหลังจบกิจกรรมหากคุณมีคำถามหรือข้อกังวล",
      "hm.landing.bottom_title": "กลุ่มโปรดกลุ่มใหม่ของคุณอยู่แค่เพียงเอื้อมมือ.",
      "hm.landing.bottom_sub": "เลือกเส้นทางของคุณ — เข้าร่วมกลุ่มที่มีอยู่, พาเพื่อนของคุณเข้าสู่ Circle หรือให้ Matchmaker จัดที่นั่งในโต๊ะที่เหมาะกับคุณ",
      "hm.landing.bottom_cta_circles": "เริ่มสร้าง Circle",
      "hm.landing.bottom_cta_mm": "ทำแบบทดสอบ Matchmaker",
      "hm.circles.page_title": "HustleMeet Circles · ก๊วนของคุณ สถานที่ใหม่ในทุกครั้ง",
      "hm.circles.kicker": "HustleMeet · Circles",
      "hm.circles.hero_title": "ก๊วนของคุณ กับโต๊ะใหม่ในทุกครั้ง.",
      "hm.circles.hero_title_accent": "ก๊วนของคุณ",
      "hm.circles.hero_sub": "มีเพื่อนอยู่แล้วใช่ไหม? Circles คือวิธีที่ง่ายที่สุดในการรักษาความสัมพันธ์ เราจองสถานที่ วางแผนกิจกรรมตอบคำถาม จัดเตรียมเกมให้ คุณแค่ปรากฏตัวพอ — ก๊วนเดิม คืนใหม่ สถานที่ใหม่",
      "hm.circles.cta_find": "เริ่มสร้าง Circle",
      "hm.circles.cta_host_hero": "เข้าร่วม Circle ที่เปิดอยู่",
      "hm.circles.trust_venue": "หมุนเวียนสถานที่ที่ผ่านการคัดสรร",
      "hm.circles.trust_party": "เพื่อน 4-12 คนต่อ Circle",
      "hm.circles.trust_verified": "สมาชิกที่ผ่านการยืนยันตัวตน",
      "hm.circles.trust_friendship": "มิตรภาพที่แท้จริง รักษาไว้ให้แน่นแฟ้น",
      "hm.circles.how_kicker": "ทำงานอย่างไร",
      "hm.circles.how_title": "สี่ขั้นตอน ก๊วนเดิมที่เพิ่มเติมคือความสัมพันธ์.",
      "hm.circles.how_step1_ttl": "เริ่มสร้าง Circle ของคุณ",
      "hm.circles.how_step1_sub": "ตั้งชื่อ, ชวนเพื่อน (สูงสุด 12 คน) และเลือกจังหวะการนัดหมาย — ทุกสัปดาห์, ทุกสองสัปดาห์ หรือรายเดือน",
      "hm.circles.how_step2_ttl": "เราเลือกสถานที่ให้",
      "hm.circles.how_step2_sub": "ทุกกิจกรรมเราจะแนะนำสถานที่เป็นจุดใหม่ๆ — ร้านอาหาร, คืนตอบคำถาม, ห้องเล่นไพ่, ไวน์บาร์ หรือจะเลือกที่ประจำก็ได้",
      "hm.circles.how_step3_ttl": "ทุกคนตอบรับคำเชิญในคลิกเดียว",
      "hm.circles.how_step3_sub": "ระบบเตือนอัตโนมัติ, การหารค่าใช้จ่าย, การชำระเงินล่วงหน้าเพื่อไม่ให้การเบี้ยวงานทำแผนพัง บิลถูกจัดการเสร็จก่อนที่จานเรียกน้ำย่อยจะมาเสิร์ฟ",
      "hm.circles.how_step4_ttl": "รักษาจังหวะความสัมพันธ์",
      "hm.circles.how_step4_sub": "คืนหนึ่งในตารางเวลาที่ไม่ต้องเหนื่อยจัดการอีกต่อไป ความสัมพันธ์ในกลุ่มจะแน่นแฟ้นขึ้นตามกาลเวลา",
      "hm.circles.cadence_weekly_ttl": "ทุกสัปดาห์",
      "hm.circles.cadence_weekly_sub": "ทุกๆ สัปดาห์",
      "hm.circles.cadence_weekly_body": "สำหรับก๊วนที่ต้องการพิธีกรรมคืนวันอังคาร — หน้าเดิม สถานที่ใหม่ทุกครั้ง",
      "hm.circles.cadence_fortnight_ttl": "ทุกสองสัปดาห์",
      "hm.circles.cadence_fortnight_sub": "ทุกๆ 2 สัปดาห์",
      "hm.circles.cadence_fortnight_body": "จังหวะที่พอดี — สม่ำเสมอแต่ไม่รบกวนตารางที่ยุ่งเหยิงเกินไป",
      "hm.circles.cadence_monthly_ttl": "รายเดือน",
      "hm.circles.cadence_monthly_sub": "ทุกๆ เดือน",
      "hm.circles.cadence_monthly_body": "Circles สำหรับโอกาสพิเศษ — มื้อค่ำวันอาทิตย์, คืนตอบคำถามใหญ่, คอร์สชิมอาหาร, โป๊กเกอร์",
      "hm.circles.feast_kicker": "เลือกคืนของคุณ",
      "hm.circles.feast_title": "สามวิธีในการใช้เวลาร่วมกัน",
      "hm.circles.feast_sub": "แต่ละ Circle เลือกได้ว่าอยากให้คืนนั้นเป็นแบบไหน จะสลับไปมาหรือจะล็อคแบบที่ชอบที่สุดไว้ก็ได้",
      "hm.circles.feast_surprise_ttl": "ลองร้านอาหารใหม่ๆ",
      "hm.circles.feast_surprise_body": "เราคัดรายชื่อร้านอาหารใกล้คุณมาให้ 3 แห่ง ให้ทุกคนโหวต ร้านเตรียมพร้อมสำหรับโต๊ะของคุณ หารค่าใช้จ่ายและจ่ายล่วงหน้าเรียบร้อย",
      "hm.circles.feast_preselect_ttl": "ตอบคำถาม / ไพ่ / เกม",
      "hm.circles.feast_preselect_body": "จองคืนตอบคำถาม ห้องเล่นโป๊กเกอร์ บอร์ดเกมบาร์ หรือเลนโบว์ลิ่ง เราจัดการเรื่องสถานที่ ค่าเข้า และการเตือนความจำ",
      "hm.circles.feast_per_event_ttl": "คืนธีมพิเศษครั้งเดียว",
      "hm.circles.feast_per_event_body": "การชิมไวน์, โต๊ะเชฟส่วนตัว, คลาสทำค็อกเทล, ห้องหลบหนี (Escape room) บางสิ่งที่พิเศษเป็นครั้งคราว — จัดการให้ครบจบในที่เดียว",
      "hm.circles.live_kicker": "ตอนนี้",
      "hm.circles.live_title": "Circles ที่คุณเข้าร่วมได้",
      "hm.circles.live_host_link": "เริ่มสร้าง Circle ของคุณเอง",
      "hm.circles.live_loading": "กำลังโหลด…",
      "hm.circles.live_empty_title": "เป็น Circle แรกในเมืองของคุณสิ.",
      "hm.circles.live_empty_sub": "เริ่มสร้างกลุ่มกับเพื่อนสามคน แล้วเราจะช่วยคุณชวนสมาชิกที่เหลือ — พร้อมมอบสิทธิพิเศษสำหรับ 3 สถานที่แรกจากเรา",
      "hm.circles.live_empty_cta": "เริ่มสร้าง Circle",
      "hm.circles.host_kicker": "สำหรับผู้นำ Circle",
      "hm.circles.host_title": "คุณพาก๊วนมา เราจัดการธุรการให้.",
      "hm.circles.host_body": "ถ้าคุณคือเพื่อนที่คอยจัดการนัดหมายเสมอ Circles จะช่วยประหยัดเวลาให้คุณหลายชั่วโมง ทั้งระบบเตือนอัตโนมัติ, การจ่ายล่วงหน้า, การคัดเลือกสถานที่, การหารบิล, การจัดการคิวรอ และกระเป๋าเงินเงินคืนในทุกคืนที่ออกไป คุณแค่ไปร่วมสนุกในฐานะสมาชิกปกติคนหนึ่ง",
      "hm.circles.host_perk_1": "คัดเลือกสถานที่ให้อัตโนมัติ — ไม่ต้องเสียเวลาไถ Google อีกต่อไป",
      "hm.circles.host_perk_2": "จองที่นั่งล่วงหน้า — ไม่มีใครเบี้ยวนัด",
      "hm.circles.host_perk_3": "หารค่าใช้จ่ายเสร็จสรรพก่อนของหวานมาเสิร์ฟ",
      "hm.circles.host_perk_4": "ระบบเตือนและนัดหมายในปฏิทินแบบอัตโนมัติ",
      "hm.circles.host_perk_5": "ระบบคิวรอพร้อมเลื่อนลำดับอัตโนมัติหากมีใครยกเลิก",
      "hm.circles.host_perk_6": "รับเงินคืนเข้ากระเป๋าเงินในทุกคืนที่ออกไป",
      "hm.circles.host_cta": "เริ่มสร้าง Circle ตอนนี้",
      "hm.circles.why_kicker": "ทำไม Circles ถึงได้ผล",
      "hm.circles.why_title": "มิตรภาพวัยผู้ใหญ่ต้องการระบบจัดการที่ดี",
      "hm.circles.why_1_ttl": "ก๊วนเดิม ความเชื่อใจที่ลึกซึ้งขึ้น",
      "hm.circles.why_1_body": "ชีวิตอาจดึงเพื่อนให้ห่างกัน Circles คือจังหวะที่แน่นอนที่จะช่วยรักษาความสัมพันธ์ — เพื่อน 4-12 คนเดิม พบกันในคืนวันธรรมดาเดิม ไม่ว่าอะไรจะเกิดขึ้น",
      "hm.circles.why_2_ttl": "ไม่มีภาระในการจัดการ",
      "hm.circles.why_2_body": "ไม่มีใครต้องเป็นหัวหน้ากลุ่มใน WhatsApp เราเลือกสถานที่ ส่งคำเชิญ รับชำระเงิน และดูแลจนจบกระบวนการ",
      "hm.circles.why_3_ttl": "สถานที่ใหม่ ความทรงจำใหม่",
      "hm.circles.why_3_body": "หมุนเวียนไปตามร้านอาหารเล็กๆ ที่ดีที่สุด ห้องตอบคำถาม และเกมบาร์ใน Australia หน้าเดิมในบรรยากาศใหม่ — ความลงตัวระหว่างความคุ้นเคยและความแปลกใหม่",
      "hm.circles.bottom_title": "คืนที่ก๊วนเพื่อนตั้งตารอ โดยไม่มีใครต้องเหนื่อยจัดเตรียม.",
      "hm.circles.bottom_sub": "Circle เดียว เพื่อนกลุ่มเดิม สถานที่ใหม่ทุกครั้ง.",
      "hm.circles.bottom_cta_find": "เริ่มสร้าง Circle",
      "hm.circles.bottom_cta_mm": "พบคนใหม่ๆ ด้วย Matchmaker →",
      "hm.circles.card_host_pill": "Circle",
      "hm.matchmaker.page_title": "HustleMeet Matchmaker · AI เลือกโต๊ะที่ใช่ให้คุณ",
      "hm.matchmaker.kicker": "HustleMeet · Matchmaker",
      "hm.matchmaker.hero_title": "AI จัดโต๊ะให้ คุณแค่ปรากฏตัวพอ.",
      "hm.matchmaker.hero_title_accent": "คุณแค่ปรากฏตัวพอ.",
      "hm.matchmaker.hero_sub": "ตอบแบบทดสอบบุคลิกภาพและค่านิยมสั้นๆ Matchmaker ของเราจะจัดที่นั่งให้คุณร่วมกับคนแปลกหน้าอีก 4-7 คนที่คุณจะรู้สึกเหมือนเพื่อนเก่าเมื่อถึงเวลาของหวาน ไม่มีการปัดแอป ไม่ต้องคุยสัพเพเหระ แค่ร่วมโต๊ะอาหารที่พร้อมรอคุณอยู่",
      "hm.matchmaker.cta_quiz": "ทำแบบทดสอบการจับคู่",
      "hm.matchmaker.cta_see_circles": "ดู Circles แทน →",
      "hm.matchmaker.trust_big5": "วัดผลบุคลิกภาพแบบ Big-5",
      "hm.matchmaker.trust_seats": "4-8 ที่นั่งต่อโต๊ะ",
      "hm.matchmaker.trust_lock": "ไม่ต้องปัดแอป · ไม่ต้องใช้รูปถ่าย",
      "hm.matchmaker.trust_verified": "เฉพาะสมาชิกที่ยืนยันตัวตนแล้ว",
      "hm.matchmaker.how_kicker": "ทำงานอย่างไร",
      "hm.matchmaker.how_title": "สี่ขั้นตอนสู่โต๊ะอาหารที่ใช่สำหรับคุณ.",
      "hm.matchmaker.how_step1_ttl": "ทำแบบทดสอบ",
      "hm.matchmaker.how_step1_sub": "แบบสอบถาม 10 นาที — บุคลิกภาพ, ค่านิยม, อาหารที่ทาน, เวลาที่สะดวก ทำเพียงครั้งเดียว",
      "hm.matchmaker.how_step2_ttl": "การจับคู่ข้ามคืน",
      "hm.matchmaker.how_step2_sub": "AI ของเราจะอ่านโปรไฟล์ของคุณและตารางมื้อค่ำใน Australia เมื่อคะแนนความเข้ากันได้ถึง 0.70 ขึ้นไป ระบบจะร่างคำเชิญส่วนตัวให้คุณ",
      "hm.matchmaker.how_step3_ttl": "ตอบรับหรือปฏิเสธ",
      "hm.matchmaker.how_step3_sub": "คุณจะได้รับอีเมลคำเชิญที่ชัดเจนพร้อมระบุสถานที่ ธีมของโต๊ะอาหาร และ 3 ความสนใจที่คุณแชร์ร่วมกับกลุ่ม",
      "hm.matchmaker.how_step4_ttl": "ระบบเรียนรู้จากคุณ",
      "hm.matchmaker.how_step4_sub": "ทุกการสำรวจหลังมื้อค่ำจะช่วยปรับจูนโมเดล การจับคู่ครั้งต่อไปของคุณจะฉลาดขึ้นเรื่อยๆ ตามจำนวนครั้งที่คุณไปทานอาหาร",
      "hm.matchmaker.science_kicker": "ทำไมถึงได้ผล",
      "hm.matchmaker.science_title": "Matchmaker ไม่ได้เดาสุ่ม — แต่ใช้การคำนวณคะแนน.",
      "hm.matchmaker.science_sub": "ทุกการจับคู่คือผลรวมถ่วงน้ำหนักจากปัจจัย 4 ประการ เมื่อค่าเฉลี่ยทั้งโต๊ะสูงกว่า 0.70 เราจึงส่งคำเชิญ หากต่ำกว่านั้นจะยังอยู่ในคิว",
      "hm.matchmaker.science_1_ttl": "บุคลิกภาพ (35%)",
      "hm.matchmaker.science_1_body": "ความคล้ายคลึงกันเชิงเวกเตอร์ของบุคลิกภาพแบบ Big-5 — ความเปิดรับประสบการณ์, ความถี่ถ้วน, การแสดงตัว, ความเป็นมิตร และความมั่นคงทางอารมณ์",
      "hm.matchmaker.science_2_ttl": "ค่านิยม (25%)",
      "hm.matchmaker.science_2_body": "ความสอดคล้องของค่านิยมที่จะทำให้บทสนทนาบนโต๊ะอาหารลื่นไหล — ครอบครัว, ความอยากรู้อยากเห็น, การเติบโต, การบริการ, การผจญภัย",
      "hm.matchmaker.science_3_ttl": "ความสนใจ (25%)",
      "hm.matchmaker.science_3_body": "ความทับซ้อนแบบ Jaccard ในความสนใจเฉพาะของคุณ — เช่น การเดินป่าที่ Six Foot Track, ชมรมหนังใน Sydney, วัฒนธรรมกาแฟใน Melbourne",
      "hm.matchmaker.science_4_ttl": "เวลาที่สะดวก (15%)",
      "hm.matchmaker.science_4_body": "คืนที่คุณไปได้จริงๆ จะไม่มีคำเชิญวันอังคารตอน 1 ทุ่ม ถ้าปฏิทินของคุณระบุว่าต้องไปยิมในวันอังคารตลอดไป",
      "hm.matchmaker.safety_kicker": "ความปลอดภัยจากการออกแบบ",
      "hm.matchmaker.safety_title": "Matchmaker ทำงานอย่างระมัดระวัง.",
      "hm.matchmaker.safety_1_ttl": "เกณฑ์ความมั่นใจ",
      "hm.matchmaker.safety_1_body": "หากคะแนนกลุ่มต่ำกว่า 0.70 จะไม่มีคำเชิญส่งออกไป การจับคู่ที่คลุมเครือไม่สู้การไม่มีมื้อค่ำเลยเสียดีกว่า",
      "hm.matchmaker.safety_2_ttl": "รายการบล็อกแบบไม่ถูกชะตาทั้งคู่",
      "hm.matchmaker.safety_2_body": "หากสมาชิกสองคนส่งสัญญาณ 'ไม่' เป็นส่วนตัวหลังมื้อค่ำ เราจะไม่จัดให้พวกเขานั่งด้วยกันอีก",
      "hm.matchmaker.safety_3_ttl": "ระบบตัดไฟอัตโนมัติ",
      "hm.matchmaker.safety_3_body": "หากผลสำรวจล่าสุด 20% หรือมากกว่าให้คะแนนโต๊ะ 2/5 หรือต่ำกว่า ระบบจะหยุดส่งคำเชิญชั่วคราว 24 ชั่วโมงเพื่อให้เจ้าหน้าที่ตรวจสอบ",
      "hm.matchmaker.safety_4_ttl": "มาตรฐานชุมชน",
      "hm.matchmaker.safety_4_body": "สมาชิกทุกคนยอมรับกฎกติกาที่เข้าใจง่าย เราจะลบผู้ที่ละเมิดออกทันทีโดยไม่มีการอุทธรณ์",
      "hm.matchmaker.safety_5_ttl": "เฉพาะสถานที่สาธารณะ",
      "hm.matchmaker.safety_5_body": "มื้อค่ำของ Matchmaker ทุกมื้อจัดที่ร้านอาหารจริงที่มีพนักงานดูแล ไม่มีการจัดที่บ้านส่วนตัว",
      "hm.matchmaker.safety_6_ttl": "ผลตอบรับหลังกิจกรรม",
      "hm.matchmaker.safety_6_body": "แบบสำรวจส่วนตัวของคุณจะกำหนดการจับคู่ในอนาคต และสามารถส่งเรื่องให้ตรวจสอบสถานที่หรือเพื่อนร่วมโต๊ะได้",
      "hm.matchmaker.loop_kicker": "วงจรการเรียนรู้",
      "hm.matchmaker.loop_title": "ทุกมื้อค่ำจะทำให้มื้อต่อไปของคุณดียิ่งขึ้น.",
      "hm.matchmaker.loop_body": "Matchmaker ไม่ได้หยุดนิ่ง หลังจบแต่ละกิจกรรมคุณจะทำแบบสำรวจสั้นๆ ส่วนตัว — บรรยากาศบทสนทนาเป็นอย่างไร ใครที่คุณอยากทานข้าวด้วยอีกครั้ง หรือร้านอาหารถูกใจไหม สัญญาณเหล่านี้จะถูกส่งกลับไปยังโมเดลเพื่อปรับปรุงการจับคู่ในอนาคต",
      "hm.matchmaker.loop_1": "คะแนนโหวต \"ทานข้าวด้วยกันอีก\" ช่วยเสริมการจับคู่ของคุณให้แข็งแกร่ง",
      "hm.matchmaker.loop_2": "การโหวตไม่ถูกชะตาทั้งคู่จะแยกคู่เหล่านั้นออกจากกันอย่างเงียบๆ",
      "hm.matchmaker.loop_3": "คะแนนร้านอาหารและบทสนทนาจะช่วยปรับส่วนผสมของที่นั่ง",
      "hm.matchmaker.loop_4": "คะแนนความน่าเชื่อถือช่วยปกป้องสมาชิกจากการเบี้ยวนัดซ้ำซาก",
      "hm.matchmaker.loop_5": "อคติการเรียนรู้ช่วยตอกย้ำการจับคู่ที่ดีขึ้นเมื่อเวลาผ่านไปหลายเดือน",
      "hm.matchmaker.loop_6": "คุณเป็นผู้ควบคุม — เลือกออกหรือรีเซ็ตโปรไฟล์ของคุณได้ตลอดเวลา",
      "hm.matchmaker.vs_kicker": "แบบไหนที่เหมาะกับฉัน?",
      "hm.matchmaker.vs_title": "Matchmaker หรือ Circles?",
      "hm.matchmaker.vs_mm_ttl": "เลือกฉัน ถ้าต้องการความหลากหลาย.",
      "hm.matchmaker.vs_mm_1": "เปลี่ยนทั้งโต๊ะ, เมือง และประเภทอาหารในทุกครั้ง",
      "hm.matchmaker.vs_mm_2": "กลุ่มคนแปลกหน้าที่เข้ากันได้ 4-8 คน คัดสรรโดย AI",
      "hm.matchmaker.vs_mm_3": "จังหวะรายสัปดาห์ โดยไม่ต้องมีภาระผูกพันในการเป็นโฮสต์",
      "hm.matchmaker.vs_mm_4": "เหมาะมากถ้าคุณเพิ่งย้ายมาเมืองใหม่หรือกำลังเดินทาง",
      "hm.matchmaker.vs_mm_cta": "ทำแบบทดสอบการจับคู่",
      "hm.matchmaker.vs_circles_ttl": "เลือกฉัน ถ้าต้องการกลุ่มประจำ.",
      "hm.matchmaker.vs_circles_1": "โฮสต์เดิม · สถานที่เดิม · คืนวันเดิม",
      "hm.matchmaker.vs_circles_2": "กลุ่มที่จะกลายเป็นประเพณีคืนวันพุธของคุณ",
      "hm.matchmaker.vs_circles_3": "คัดสรรโดยโฮสต์ที่มีประสบการณ์",
      "hm.matchmaker.vs_circles_4": "เหมาะมากถ้าคุณเพิ่งย้ายมาและต้องการเริ่มสร้างรากฐาน",
      "hm.matchmaker.vs_circles_cta": "เรียกดู Circles",
      "hm.matchmaker.bottom_title": "แบบทดสอบเดียว แล้วมื้อต่อๆ ไปก็จะใช่สำหรับคุณเสมอ.",
      "hm.matchmaker.bottom_sub": "คุณจะได้รับการจับคู่ภายในสองสัปดาห์ นี่คือการตอบตกลงที่ง่ายที่สุดที่คุณจะทำในเดือนนี้",
      "hm.matchmaker.bottom_cta": "ทำแบบทดสอบการจับคู่",
      "hm.matchmaker.bottom_note": "ไม่ต้องใช้บัตรเครดิต · ใช้เวลา 10 นาที · ยกเลิกการจับคู่ได้ทุกเมื่อ",
      /* AUTO-TRANSLATED hm.* END */
    },
  },
  ms: {
    translation: {
      /* AUTO-TRANSLATED finder.* (added) */
      "finder.title": "HustleFinder",
      "finder.tagline": "Cari tempat awam terdekat — data sebenar, seluruh Australia.",
      "finder.search_placeholder": "Cari tempat, jalan atau suburb…",
      "finder.hint": "Mula menaip di atas, ketik kategori, atau guna \"Dekat saya\".",
      "finder.near_me": "Dekat saya",
      "finder.searching": "Mencari…",
      "finder.suburb.placeholder": "Tapis mengikut suburb (cth. Darwin, NT)…",
      "finder.suburb.clear": "Padam",
      "finder.search_in": "Cari dalam {{cat}}",
      "finder.empty": "Tiada hasil — cuba kategori lain atau kata carian.",
      "finder.meta.title": "HustleFinder — direktori suburb Aussie percuma",
      "finder.meta.desc": "Cari perniagaan tempatan, pengecas, kafe, tandas dan lain-lain di setiap suburb Australia — 100% percuma.",
      "finder.footer.credits": "Kredit data & atribusi",
      "finder.footer.terms": "Terma",
      "finder.cat.toilet": "Tandas",
      "finder.cat.fountain": "Air Pancut Minuman",
      "finder.cat.transport": "Pengangkutan",
      "finder.cat.park": "Taman",
      "finder.cat.medical": "Perubatan",
      "finder.cat.school": "Sekolah",
      "finder.cat.library": "Perpustakaan",
      "finder.cat.petrol": "Petrol",
      "finder.cat.kidzone": "Zon Kanak-kanak",
      "finder.cat.puzzles": "Teka-teki",
      "finder.cat.roadtrip": "Perjalanan jalan raya",
      "finder.hotels.title": "Cari hotel berdekatan",
      "finder.hotels.sub": "Membandingkan 200+ laman tempahan dalam satu carian — dikuasakan oleh Trivago.",
      "finder.hotels.where": "Di mana",
      "finder.hotels.where_ph": "Bandar atau suburb (cth. Byron Bay, NSW)",
      "finder.hotels.checkin": "Daftar masuk",
      "finder.hotels.checkout": "Daftar keluar",
      "finder.hotels.adults": "Dewasa",
      "finder.hotels.search": "Cari hotel",
      "finder.hotels.disclosure": "Membuka Trivago — kami mungkin mendapat komisen daripada tempahan.",
      /* AUTO-TRANSLATED rt.* START */
      "rt.brand_name": "Perancang Roadtrip",
      "rt.page_title": "Perancang Roadtrip EV Australia",
      "rt.from": "Dari",
      "rt.to": "Ke",
      "rt.from_placeholder": "Pinggir bandar, pekan atau bandar (cth. Newcastle NSW)",
      "rt.to_placeholder": "Pinggir bandar, pekan atau bandar (cth. Hobart TAS)",
      "rt.stop": "HENTIAN",
      "rt.stop_placeholder": "Hentian antara",
      "rt.add_stop": "Tambah hentian",
      "rt.upgrade_pro": "Naik taraf ke Pro untuk sehingga 20 hentian",
      "rt.reverse": "Terbalikkan (kira jalan pulang)",
      "rt.from_current": "Dari lokasi semasa",
      "rt.range_per_charge": "Jarak setiap cas (km)",
      "rt.connector": "Penyambung",
      "rt.connector_any": "Mana-mana",
      "rt.drive_hours_day": "Jam memandu/hari",
      "rt.accommodation": "Penginapan",
      "rt.accom_any": "Mana-mana (BnB · Hotel · Karavan)",
      "rt.accom_bnb": "BnB",
      "rt.accom_hotel": "Hotel",
      "rt.accom_caravan": "Taman Karavan",
      "rt.min_rating": "Penarafan minimum",
      "rt.any_rating": "Mana-mana penarafan",
      "rt.only_5star": "5★ sahaja",
      "rt.schedule": "Jadual:",
      "rt.depart_at": "Bertolak pada",
      "rt.arrive_by": "Tiba sebelum",
      "rt.hotel_checkin": "Daftar masuk hotel",
      "rt.hotel_checkout": "Daftar keluar hotel",
      "rt.overnight_hint": "Perjalanan bermalam tidak akan dijadualkan di luar waktu ini.",
      "rt.whos_in_car": "Siapa di dalam kereta?",
      "rt.adults": "Dewasa",
      "rt.kids": "Kanak-kanak",
      "rt.ages": "Umur",
      "rt.show_attractions": "Tunjukkan tarikan pelancong",
      "rt.show_kids": "Tunjukkan hentian mesra kanak-kanak",
      "rt.overnight_stays": "Penginapan bermalam",
      "rt.fast_chargers_only": "Pengecas pantas sahaja",
      "rt.hotel_must_charger": "Hotel mesti ada pengecas",
      "rt.pet_only": "Penginapan mesra haiwan sahaja",
      "rt.safety": "Keselamatan",
      "rt.safety_90": "90% (rata)",
      "rt.safety_85": "85% (lalai)",
      "rt.safety_80": "80% (bukit / menunda)",
      "rt.safety_70": "70% (muatan berat)",
      "rt.save_trip": "Simpan perjalanan",
      "rt.plan_my_trip": "Rancang perjalanan saya",
      "rt.refresh_trip": "Segarkan semula",
      "rt.planning": "Merancang…",
      "rt.resume_title": "Sambung perjalanan dari sini",
      "rt.resume_body": "Kami telah mengesan lokasi semasa anda. Masukkan baki jarak pada paparan pemuka anda supaya kami dapat merancang perjalanan seterusnya berdasarkan baki sebenar — bukan cas penuh.",
      "rt.remaining_range": "Baki jarak (km)",
      "rt.cancel": "Batal",
      "rt.replan_from_here": "Rancang semula dari sini",
      "rt.save_modal_title": "Namakan perjalanan ini",
      "rt.save_modal_body": "Perjalanan yang disimpan ada di bawah profil anda — buka semula bila-bila masa dan tekan Rancang Semula.",
      "rt.save_name_placeholder": "cth. Brisbane → Sydney melalui Byron",
      "rt.your_saved_trips": "Perjalanan disimpan anda",
      "rt.custom_location": "Lokasi tersuai",
      "rt.trip_summary": "Ringkasan perjalanan",
      "rt.itinerary": "Itinerari",
      "rt.no_stops_needed": "Tiada hentian diperlukan — keseluruhan perjalanan mencukupi dengan jarak anda.",
      "rt.chargers_in_corridor": "pengecas di koridor",
      "rt.planned_stops": "hentian dirancang",
      "rt.overnight_stop": "hentian bermalam",
      "rt.overnight_stops": "hentian bermalam",
      "rt.suggested_detours": "Lencongan dicadangkan — tanda untuk tambah",
      "rt.replan_with_detours": "Rancang semula dengan lencongan",
      "rt.attractions_label": "Tarikan",
      "rt.kids_label": "Kanak-kanak",
      "rt.km_from_start": "km dari mula",
      "rt.open_driving": "Buka mod pemanduan",
      "rt.big_font_hint": "Fon besar, mesra pemegang telefon. Berfungsi dengan cermin pelayar CarPlay/Android Auto.",
      "rt.check_price_book": "Semak harga & tempah di Trivago ↗",
      "rt.visit_site": "Lawati tapak ↗",
      "rt.charger_onsite": "Pengecas di lokasi",
      "rt.leg_exceeds_range": "Bahagian perjalanan ini melebihi jarak ditetapkan — anda akan kehabisan kuasa sebelum sampai ke pengecas ini. Kurangkan jangkaan jarak atau tambah cas tambahan di antaranya.",
      "rt.add_waypoint_replan": "Tambah sebagai titik jalan & rancang semula",
      /* AUTO-TRANSLATED rt.* END */
                      /* AUTO-TRANSLATED hm.* START */
      "hm.nav.hustlemeet": "HustleMeet",
      "hm.nav.circles": "Circles",
      "hm.nav.matchmaker": "Matchmaker",
      "hm.nav.meet": "Meet",
      "hm.nav.overview": "HustleMeetups",
      "hm.overview.page_title": "HustleMeetups — tiga cara mesra untuk mencari komuniti anda.",
      "hm.overview.kicker": "Sebahagian daripada HustleSuite",
      "hm.overview.hero_title": "Tiga cara mesra untuk mencari komuniti anda.",
      "hm.overview.hero_title_accent": "komuniti anda",
      "hm.overview.hero_sub": "HustleMeetups adalah cara paling baik di Australia untuk membina persahabatan sejati — pilih suasana yang sesuai dengan anda. Sama ada anda mahukan kumpulan larian mingguan, makan malam bulanan dengan rakan sedia ada, atau meja yang dipadankan oleh AI dengan wajah-wajah baharu, ada jalan untuk anda.",
      "hm.overview.tile_meet_kicker": "HustleMeet",
      "hm.overview.tile_meet_title": "Cari komuniti anda. Lakukan aktiviti kegemaran.",
      "hm.overview.tile_meet_body": "Pusat Australia untuk kumpulan berasaskan minat. Pelari, pengatur cara, pelabur, pejalan kaki, penari, kelab sarapan, kru buku — ratusan kumpulan, acara sebenar setiap minggu. Hadir sekali, atau setiap minggu.",
      "hm.overview.tile_meet_cta": "Teroka kumpulan",
      "hm.overview.tile_circles_kicker": "Circles",
      "hm.overview.tile_circles_title": "Kru anda. Meja baharu setiap kali.",
      "hm.overview.tile_circles_body": "Bawa kumpulan rakan tetap anda. Kami tempah restoran baharu, rancang trivia, sediakan permainan. Anda hanya perlu hadir. Cara paling erat untuk mengekalkan hubungan kru sedia ada — tanpa sesiapa perlu menguruskannya.",
      "hm.overview.tile_circles_cta": "Mulakan Circle",
      "hm.overview.tile_matchmaker_kicker": "Matchmaker",
      "hm.overview.tile_matchmaker_title": "AI pilih meja. Anda hanya hadir.",
      "hm.overview.tile_matchmaker_body": "Ambil kuiz 10 minit. Dalam sekelip mata, kami tempatkan anda bersama orang asing yang terasa seperti kawan lama menjelang pencuci mulut. Meja berbeza setiap kali.",
      "hm.overview.tile_matchmaker_cta": "Ambil kuiz",
      "hm.overview.trust_kicker": "Mengapa ia berkesan",
      "hm.overview.trust_title": "Kepercayaan didahulukan. Meja kemudian.",
      "hm.overview.trust_1_title": "Ahli yang disahkan",
      "hm.overview.trust_1_body": "Setiap kumpulan bermula dengan pemeriksaan ID sebenar + skor kebolehpercayaan. Tiada akaun pakai buang.",
      "hm.overview.trust_2_title": "Hos sebenar",
      "hm.overview.trust_2_body": "Setiap kumpulan diketuai oleh hos yang telah disaring yang mengawal suasana, menetapkan ritma, dan menjaga keadaan ruang.",
      "hm.overview.trust_3_title": "Persahabatan diutamakan",
      "hm.overview.trust_3_body": "Segala-galanya di sini direka untuk persahabatan sejati, bukan temu janji. Selamanya.",
      "hm.overview.trust_4_title": "Moderasi toleransi sifar",
      "hm.overview.trust_4_body": "Satu laporan, satu ulasan, satu keputusan. Pelaku buruk disingkirkan pada malam yang sama.",
      "hm.overview.footer_line": "Sebahagian daripada HustleSuite — dimiliki & dihoskan di Australia.",
      "hm.landing.page_title": "HustleMeet · Cari komuniti anda. Lakukan aktiviti kegemaran.",
      "hm.landing.kicker": "Rangkaian minat Australia",
      "hm.landing.hero_title": "Cari komuniti anda. Lakukan aktiviti kegemaran.",
      "hm.landing.hero_title_accent": "komuniti anda",
      "hm.landing.hero_sub": "HustleMeet adalah pusat Australia untuk kumpulan berasaskan minat. Pembangun perisian yang mencari pepijat semasa makan tengah hari. Pelari jarak jauh pada jam 6 pagi. Pelabur yang bertemu setiap bulan. Kumpulan berjalan kaki, kru tarian, kelab sarapan, malam permainan papan. Pilih kumpulan, tempah acara seterusnya, dan hadirkan diri.",
      "hm.landing.cta_explore_circles": "Layari kumpulan",
      "hm.landing.cta_try_matchmaker": "Mulakan kumpulan anda sendiri",
      "hm.landing.trust_verified": "Ahli yang disahkan",
      "hm.landing.trust_venues": "Hos acara yang disaring",
      "hm.landing.trust_friendship": "Persahabatan diutamakan — bukan temu janji",
      "hm.landing.trust_moderation": "Moderasi toleransi sifar",
      "hm.landing.subbrand_circles_kicker": "Circles",
      "hm.landing.subbrand_circles_title": "Sudah mempunyai kru sendiri? Berikan mereka tempat tetap.",
      "hm.landing.subbrand_circles_body": "Circles adalah untuk kumpulan rakan yang sudah biasa bertemu — kelab buku, malam poker, kru larian, rakan sekerja. Kami uruskan tempat, peringatan, dan senarai kehadiran. Anda hanya perlu hadir.",
      "hm.landing.subbrand_circles_b1": "Lokasi berganti, satu senarai kehadiran tetap",
      "hm.landing.subbrand_circles_b2": "Peringatan automatik + RSVP mudah",
      "hm.landing.subbrand_circles_b3": "Trivia, permainan kad & malam bertema tersedia",
      "hm.landing.subbrand_circles_cta": "Mulakan Circle",
      "hm.landing.subbrand_matchmaker_kicker": "Matchmaker",
      "hm.landing.subbrand_matchmaker_title": "AI pilih meja. Anda hanya hadir.",
      "hm.landing.subbrand_matchmaker_body": "Satu kuiz 10 minit — personaliti, nilai, ketersediaan. Matchmaker kami menempatkan anda bersama orang asing yang terasa seperti kawan lama menjelang pencuci mulut. Meja berbeza setiap kali.",
      "hm.landing.subbrand_matchmaker_b1": "Skor personaliti & nilai Big-5",
      "hm.landing.subbrand_matchmaker_b2": "Jemputan semalaman apabila kesesuaian anda adalah 0.70 atau lebih tinggi",
      "hm.landing.subbrand_matchmaker_b3": "Belajar daripada setiap makan malam anda",
      "hm.landing.subbrand_matchmaker_cta": "Ambil kuiz",
      "hm.landing.how_kicker": "Bagaimana HustleMeet berfungsi",
      "hm.landing.how_title": "Empat langkah. Cari kumpulan anda. Hadir.",
      "hm.landing.how_step1_ttl": "Pilih minat anda",
      "hm.landing.how_step1_sub": "Larian, pengaturcaraan, pelaburan, memasak, muzik, berjalan kaki — beritahu kami apa yang anda suka dan di mana anda tinggal.",
      "hm.landing.how_step2_ttl": "Layari kumpulan berhampiran anda",
      "hm.landing.how_step2_sub": "Setiap kumpulan memaparkan hosnya, ritma pertemuannya dan acara seterusnya. Kenali suasana sebelum anda komited.",
      "hm.landing.how_step3_ttl": "RSVP untuk acara seterusnya",
      "hm.landing.how_step3_sub": "Satu ketikan, satu jemputan kalendar, satu peringatan jelas sehari sebelum acara. Sesetengah acara adalah percuma, sesetengah berbayar — harga dinyatakan dengan jelas.",
      "hm.landing.how_step4_ttl": "Hadir & berhubung",
      "hm.landing.how_step4_sub": "Temui hos, temui kru, lakukan aktiviti kegemaran. Kembali minggu depan jika anda rasa serasi.",
      "hm.landing.stats_kicker": "Cara baharu Australia untuk bertemu",
      "hm.landing.stats_title": "Sebab orang terus kembali.",
      "hm.landing.stats_92_sub": "Rasa serasi dengan sekurang-kurangnya seorang di acara tersebut",
      "hm.landing.stats_68_sub": "RSVP untuk acara seterusnya dalam tempoh seminggu",
      "hm.landing.stats_85_sub": "Terus berhubung dengan seseorang daripada kumpulan",
      "hm.landing.why_kicker": "Mengapa kami berjaya",
      "hm.landing.why_title": "Minat sebenar. Hos sebenar. Acara sebenar.",
      "hm.landing.why_1_ttl": "Utamakan minat, bukan lokasi",
      "hm.landing.why_1_body": "Orang dalam kumpulan anda benar-benar mengambil berat tentang perkara yang anda minati — itu adalah jalan pintas kepada persahabatan sejati.",
      "hm.landing.why_2_ttl": "Hos acara sebenar",
      "hm.landing.why_2_body": "Setiap kumpulan mempunyai hos yang disaring yang mengawal suasana, menetapkan ritma, dan bertindak jika ada sesuatu yang tidak kena. Bukan sekadar admin Facebook. Manusia sebenar.",
      "hm.landing.why_3_ttl": "Konsistensi itu penting",
      "hm.landing.why_3_body": "Acara baharu setiap minggu. Sesuatu untuk dinantikan. Ritma yang menukarkan orang asing menjadi kru malam Selasa anda.",
      "hm.landing.why_4_ttl": "Selamat & terjamin",
      "hm.landing.why_4_body": "Ahli yang disahkan, lokasi awam, toleransi sifar untuk rasa tidak hormat. Pasukan sokongan kami sentiasa bersedia sebelum, semasa dan selepas setiap acara.",
      "hm.landing.why_5_ttl": "Persahabatan diutamakan",
      "hm.landing.why_5_body": "Ini bukan temu janji. Ini adalah komuniti. Minat sebenar, orang sebenar, tanpa perlu meleret skrin.",
      "hm.landing.why_6_ttl": "Kecil tetapi bermakna",
      "hm.landing.why_6_body": "Kebanyakan kumpulan mengehadkan acara kepada 4-15 tempat duduk. Cukup besar untuk mencetuskan perbualan sebenar. Cukup kecil supaya tiada sesiapa yang terpinggir.",
      "hm.landing.cities_kicker": "Lokasi kami beroperasi",
      "hm.landing.cities_title": "Kumpulan yang bertemu di seluruh Australia & NZ.",
      "hm.landing.cities_sub_zero": "Kumpulan pertama dibuka di lebih 15+ bandar — sertai awal dan corakkan suasana ruang tersebut.",
      "hm.landing.cities_sub_some": "Kumpulan aktif di lebih 15+ kawasan metro. Sesuatu yang menarik di bandar anda, minggu ini.",
      "hm.landing.cities_sub_one": "{{count}} kumpulan aktif · acara baharu setiap minggu · sentiasa menambah bandar.",
      "hm.landing.cities_sub_many": "{{count}} kumpulan aktif · acara baharu setiap minggu · sentiasa menambah bandar.",
      "hm.landing.forme_kicker": "Ini untuk anda jika…",
      "hm.landing.forme_title": "Anda mahukan minat sebenar. Orang sebenar. Acara sebenar.",
      "hm.landing.forme_1": "Anda bosan dengan keletihan aplikasi leret dan mahu benar-benar bertemu orang yang meminati perkara yang sama.",
      "hm.landing.forme_2": "Anda baru berpindah ke bandar baharu dan memerlukan kru yang mesra, bukan sekadar kumpulan sembang lain.",
      "hm.landing.forme_3": "Anda menghargai kedalaman perhubungan — satu kumpulan yang hebat lebih baik daripada lima puluh pengikut.",
      "hm.landing.forme_4": "Anda percaya minat bersama + dua jam yang ditetapkan dalam kehidupan sebenar adalah rahsia sebenar.",
      "hm.landing.notme_kicker": "Ini bukan untuk anda jika…",
      "hm.landing.notme_title": "HustleMeet tidak sesuai untuk semua orang.",
      "hm.landing.notme_1": "Anda tidak boleh memberi komitmen untuk hadir — hos dan ahli mengharapkan RSVP anda.",
      "hm.landing.notme_2": "Anda mencari perkhidmatan temu janji — persahabatan diutamakan di sini.",
      "hm.landing.notme_3": "Anda mahu menyiarkan sesuatu tanpa bertemu sesiapa — ini hanya berkesan secara bersemuka.",
      "hm.landing.notme_4": "Anda mengharapkan kelebihan percuma tanpa henti — sesetengah acara memerlukan kos. Harga sentiasa dinyatakan terlebih dahulu.",
      "hm.landing.safety_kicker": "Komitmen keselamatan kami",
      "hm.landing.safety_title": "Mesra, tetapi tidak pernah cuai.",
      "hm.landing.safety_1_ttl": "Lokasi awam",
      "hm.landing.safety_1_body": "Setiap acara berlangsung di lokasi awam yang mempunyai kakitangan — kafe, restoran, taman, gim atau studio. Tidak pernah di rumah sesiapa melainkan setiap peserta bersetuju.",
      "hm.landing.safety_2_ttl": "Standard komuniti",
      "hm.landing.safety_2_body": "Toleransi sifar untuk rasa tidak hormat, gangguan atau ketidakjujuran. Ahli yang melanggar peraturan akan dikeluarkan serta-merta.",
      "hm.landing.safety_3_ttl": "Privasi dilindungi",
      "hm.landing.safety_3_body": "Butiran anda tidak pernah dikongsi secara terbuka. Anda mengawal dengan siapa anda ingin terus berhubung selepas setiap acara.",
      "hm.landing.safety_4_ttl": "Sokongan sedia membantu",
      "hm.landing.safety_4_body": "Pasukan kami tersedia sebelum, semasa dan selepas setiap acara jika anda mempunyai soalan atau kebimbangan.",
      "hm.landing.bottom_title": "Kumpulan kegemaran anda yang seterusnya hanya satu ketikan sahaja.",
      "hm.landing.bottom_sub": "Pilih jalan anda — sertai kumpulan sedia ada, bawa kru anda ke dalam Circle, atau biarkan Matchmaker menempatkan anda di meja yang sesuai.",
      "hm.landing.bottom_cta_circles": "Mulakan Circle",
      "hm.landing.bottom_cta_mm": "Ambil kuiz Matchmaker",
      "hm.circles.page_title": "HustleMeet Circles · Kru anda, lokasi baharu setiap kali",
      "hm.circles.kicker": "HustleMeet · Circles",
      "hm.circles.hero_title": "Kru anda. Meja baharu setiap kali.",
      "hm.circles.hero_title_accent": "Kru anda",
      "hm.circles.hero_sub": "Sudah mempunyai rakan-rakan? Circles adalah cara termudah untuk mengekalkan hubungan erat. Kami tempah lokasi, rancang trivia, sediakan permainan. Anda hanya perlu hadir — kru sama, malam baharu, tempat baharu.",
      "hm.circles.cta_find": "Mulakan Circle",
      "hm.circles.cta_host_hero": "Sertai Circle yang terbuka",
      "hm.circles.trust_venue": "Lokasi yang disaring secara bergilir",
      "hm.circles.trust_party": "4-12 rakan setiap Circle",
      "hm.circles.trust_verified": "Ahli yang disahkan",
      "hm.circles.trust_friendship": "Persahabatan sejati, dikekalkan erat",
      "hm.circles.how_kicker": "Bagaimana ia berfungsi",
      "hm.circles.how_title": "Empat langkah. Kru anda kekal sebagai kru.",
      "hm.circles.how_step1_ttl": "Mulakan Circle anda",
      "hm.circles.how_step1_sub": "Namakannya, jemput rakan anda (sehingga 12 orang), dan pilih ritma — mingguan, dwi-mingguan atau bulanan.",
      "hm.circles.how_step2_ttl": "Kami pilih lokasi",
      "hm.circles.how_step2_sub": "Setiap acara kami cadangkan tempat baharu — restoran, malam trivia, bilik permainan kad, bar wain. Atau tetapkan tempat kegemaran.",
      "hm.circles.how_step3_ttl": "Semua orang RSVP dalam satu ketikan",
      "hm.circles.how_step3_sub": "Peringatan automatik, pembahagian kos, tempat duduk prabayar supaya ketidakhadiran tidak merosakkan rancangan. Bil diselesaikan sebelum pembuka selera dihidangkan.",
      "hm.circles.how_step4_ttl": "Kekalkan ritma",
      "hm.circles.how_step4_sub": "Satu malam dalam diari yang tidak perlu diuruskan lagi. Kumpulan menjadi lebih erat selama berbulan-bulan, bukan minit.",
      "hm.circles.cadence_weekly_ttl": "Mingguan",
      "hm.circles.cadence_weekly_sub": "Setiap minggu",
      "hm.circles.cadence_weekly_body": "Untuk kru yang mahukan ritual malam Selasa — wajah yang sama, tempat baharu setiap kali.",
      "hm.circles.cadence_fortnight_ttl": "Dwi-mingguan",
      "hm.circles.cadence_fortnight_sub": "Setiap 2 minggu",
      "hm.circles.cadence_fortnight_body": "Pilihan paling seimbang — tetap tanpa terlalu membebankan jadual yang sibuk.",
      "hm.circles.cadence_monthly_ttl": "Bulanan",
      "hm.circles.cadence_monthly_sub": "Setiap bulan",
      "hm.circles.cadence_monthly_body": "Circles untuk acara istimewa — makan besar hari Ahad, malam trivia besar, degustasi, poker.",
      "hm.circles.feast_kicker": "Pilih malam anda",
      "hm.circles.feast_title": "Tiga cara untuk meluangkannya",
      "hm.circles.feast_sub": "Setiap Circle memilih cara malam itu dijalankan. Putarkannya, atau tetapkan kegemaran kru anda.",
      "hm.circles.feast_surprise_ttl": "Cuba restoran baharu",
      "hm.circles.feast_surprise_body": "Kami senarai pendekkan tiga lokasi berhampiran anda, semua orang mengundi, dapur bersedia untuk satu meja penuh. Kos dibahagikan, tempat duduk prabayar.",
      "hm.circles.feast_preselect_ttl": "Trivia / kad / permainan",
      "hm.circles.feast_preselect_body": "Tempah malam trivia, bilik poker, bar permainan papan atau lorong boling. Kami uruskan lokasi, kos penyertaan dan peringatan.",
      "hm.circles.feast_per_event_ttl": "Malam bertema sekali-sekala",
      "hm.circles.feast_per_event_body": "Rasa wain, meja chef, kelas koktel, bilik lepasan diri. Sesuatu yang istimewa sekali-sekala — diuruskan sepenuhnya.",
      "hm.circles.live_kicker": "Sekarang",
      "hm.circles.live_title": "Circles yang boleh anda sertai",
      "hm.circles.live_host_link": "Mulakan sendiri",
      "hm.circles.live_loading": "Memuatkan…",
      "hm.circles.live_empty_title": "Jadi Circle pertama di bandar anda.",
      "hm.circles.live_empty_sub": "Mulakan satu dengan tiga rakan dan kami akan bantu anda menjemput yang lain — ditambah kami belanja tiga lokasi pertama.",
      "hm.circles.live_empty_cta": "Mulakan Circle",
      "hm.circles.host_kicker": "Untuk pemimpin Circle",
      "hm.circles.host_title": "Anda bawa kru. Kami uruskan pentadbiran.",
      "hm.circles.host_body": "Jika anda adalah rakan yang sentiasa menganjurkan perjumpaan malam, Circles menjimatkan masa anda. Peringatan automatik, RSVP prabayar, senarai pendek lokasi, pembahagian kos, pengurusan senarai menunggu dan dompet pulangan tunai pada setiap malam keluar. Anda hanya hadir sebagai ahli biasa.",
      "hm.circles.host_perk_1": "Senarai pendek lokasi automatik — tidak perlu cari di Google lagi",
      "hm.circles.host_perk_2": "Tempat duduk prabayar — sifar ketidakhadiran",
      "hm.circles.host_perk_3": "Pembahagian kos selesai sebelum pencuci mulut",
      "hm.circles.host_perk_4": "Peringatan + jemputan kalendar secara autopilot",
      "hm.circles.host_perk_5": "Senarai menunggu dengan kenaikan automatik jika rakan membatalkan",
      "hm.circles.host_perk_6": "Dompet pulangan tunai pada setiap malam keluar",
      "hm.circles.host_cta": "Mulakan Circle sekarang",
      "hm.circles.why_kicker": "Mengapa Circles berkesan",
      "hm.circles.why_title": "Persahabatan dewasa memerlukan infrastruktur",
      "hm.circles.why_1_ttl": "Kru yang sama, kepercayaan lebih mendalam",
      "hm.circles.why_1_body": "Kehidupan sering memisahkan rakan-rakan. Circles adalah ritma tetap yang mengekalkan semua orang dekat — 4-12 rakan yang sama, bertemu pada malam minggu yang sama, walau apa pun yang berlaku.",
      "hm.circles.why_2_ttl": "Sifar keletihan pentadbiran",
      "hm.circles.why_2_body": "Tiada siapa yang perlu menjadi ketua WhatsApp kumpulan. Kami pilih lokasi, hantar peringatan, ambil bayaran, selesaikan semuanya.",
      "hm.circles.why_3_ttl": "Lokasi baharu, kenangan baharu",
      "hm.circles.why_3_body": "Bergilir-gilir melalui restoran kecil terbaik di Australia, bilik trivia dan bar permainan. Wajah-wajah biasa, latar belakang segar — gabungan sempurna antara rutin dan kebaharuan.",
      "hm.circles.bottom_title": "Malam yang dinantikan oleh kru anda, tanpa perlu sesiapa merancangnya.",
      "hm.circles.bottom_sub": "Satu Circle. Rakan yang sama. Lokasi baharu setiap kali.",
      "hm.circles.bottom_cta_find": "Mulakan Circle",
      "hm.circles.bottom_cta_mm": "Temui orang baharu dengan Matchmaker →",
      "hm.circles.card_host_pill": "Circle",
      "hm.matchmaker.page_title": "HustleMeet Matchmaker · AI pilih meja yang sesuai",
      "hm.matchmaker.kicker": "HustleMeet · Matchmaker",
      "hm.matchmaker.hero_title": "AI pilih meja. Anda hanya hadir.",
      "hm.matchmaker.hero_title_accent": "Anda hanya hadir.",
      "hm.matchmaker.hero_sub": "Jawab kuiz personaliti & nilai yang singkat. Matchmaker kami menempatkan anda bersama empat hingga tujuh orang asing yang akan terasa seperti kawan lama menjelang pencuci mulut. Tiada leretan. Tiada perbualan kosong. Hanya meja yang dikongsi, tersedia apabila anda bersedia.",
      "hm.matchmaker.cta_quiz": "Ambil kuiz pemadanan",
      "hm.matchmaker.cta_see_circles": "Lihat Circles sebaliknya →",
      "hm.matchmaker.trust_big5": "Skor personaliti Big-5",
      "hm.matchmaker.trust_seats": "4-8 tempat duduk setiap meja",
      "hm.matchmaker.trust_lock": "Sifar leret · sifar foto",
      "hm.matchmaker.trust_verified": "Ahli yang disahkan sahaja",
      "hm.matchmaker.how_kicker": "Bagaimana ia berfungsi",
      "hm.matchmaker.how_title": "Empat langkah ke meja yang sudah sesuai.",
      "hm.matchmaker.how_step1_ttl": "Ambil kuiz",
      "hm.matchmaker.how_step1_sub": "Soal selidik sepuluh minit — personaliti, nilai, diet, ketersediaan. Anda lakukannya sekali sahaja.",
      "hm.matchmaker.how_step2_ttl": "Pemadanan semalaman",
      "hm.matchmaker.how_step2_sub": "AI kami membaca profil anda dan senarai makan malam aktif di Australia. Apabila skor kesesuaian anda 0.70 atau lebih tinggi, ia merangka jemputan peribadi.",
      "hm.matchmaker.how_step3_ttl": "Terima atau tolak",
      "hm.matchmaker.how_step3_sub": "Anda mendapat satu emel jemputan yang jelas dengan lokasi, tema meja, dan tiga minat yang anda kongsi bersama kumpulan tersebut.",
      "hm.matchmaker.how_step4_ttl": "Ia belajar daripada anda",
      "hm.matchmaker.how_step4_sub": "Setiap tinjauan selepas makan malam memperkemaskan model. Padanan anda seterusnya menjadi lebih bijak setiap kali anda makan malam.",
      "hm.matchmaker.science_kicker": "Mengapa ia berkesan",
      "hm.matchmaker.science_title": "Matchmaker tidak meneka — ia memberi skor.",
      "hm.matchmaker.science_sub": "Setiap padanan adalah komposit wajaran daripada empat faktor. Apabila purata seluruh meja melebihi 0.70, kami hantar jemputan. Apa-apa yang di bawah itu kekal dalam barisan menunggu.",
      "hm.matchmaker.science_1_ttl": "Personaliti (35%)",
      "hm.matchmaker.science_1_body": "Kesamaan kosinus merentasi vektor personaliti Big-5 — keterbukaan, sifat berhati-hati, ekstraversi, sifat menyenangkan, dan kestabilan emosi.",
      "hm.matchmaker.science_2_ttl": "Nilai (25%)",
      "hm.matchmaker.science_2_body": "Pertindihan pada nilai-nilai yang menjadikan perbualan makan malam benar-benar bermakna — keluarga, sifat ingin tahu, pertumbuhan, perkhidmatan, pengembaraan.",
      "hm.matchmaker.science_3_ttl": "Minat (25%)",
      "hm.matchmaker.science_3_body": "Pertindihan Jaccard pada minat khusus anda — mendaki Six Foot Track, kelab filem Sydney, suasana kopi Melbourne, buku ketiga anda tahun ini.",
      "hm.matchmaker.science_4_ttl": "Ketersediaan (15%)",
      "hm.matchmaker.science_4_body": "Malam yang anda benar-benar boleh hadir. Tiada jemputan jam 7 malam Selasa jika kalendar anda menyatakan waktu gim-Selasa-selamanya.",
      "hm.matchmaker.safety_kicker": "Keselamatan melalui reka bentuk",
      "hm.matchmaker.safety_title": "Matchmaker sentiasa berwaspada.",
      "hm.matchmaker.safety_1_ttl": "Ambang keyakinan",
      "hm.matchmaker.safety_1_body": "Di bawah skor kumpulan 0.70, tiada jemputan akan dihantar. Padanan yang tidak stabil lebih buruk daripada tiada makan malam.",
      "hm.matchmaker.safety_2_ttl": "Sekatan 'tidak' bersama",
      "hm.matchmaker.safety_2_body": "Jika dua ahli secara peribadi memberi isyarat 'tidak' selepas makan malam, kami tidak akan menempatkan mereka bersama lagi.",
      "hm.matchmaker.safety_3_ttl": "Suis pemati automatik",
      "hm.matchmaker.safety_3_body": "Jika 20% atau lebih tinjauan terbaru memberikan skor meja 2/5 atau lebih rendah, jemputan dihentikan selama 24 jam sementara pengendali menyemaknya.",
      "hm.matchmaker.safety_4_ttl": "Standard komuniti",
      "hm.matchmaker.safety_4_body": "Setiap ahli menerima kod tingkah laku bahasa mudah. Kami mengeluarkan orang yang melanggarnya — tiada rayuan.",
      "hm.matchmaker.safety_5_ttl": "Lokasi awam sahaja",
      "hm.matchmaker.safety_5_body": "Setiap makan malam Matchmaker dijalankan di restoran sebenar dengan kakitangan. Tidak pernah di rumah sesiapa.",
      "hm.matchmaker.safety_6_ttl": "Maklum balas selepas acara",
      "hm.matchmaker.safety_6_body": "Tinjauan peribadi anda membentuk padanan masa hadapan, dan boleh mencetuskan semakan moderator terhadap lokasi atau rakan.",
      "hm.matchmaker.loop_kicker": "Gelung pembelajaran",
      "hm.matchmaker.loop_title": "Setiap makan malam menjadikan makan malam seterusnya lebih baik.",
      "hm.matchmaker.loop_body": "Matchmaker tidak statik. Selepas setiap acara anda menghantar tinjauan peribadi yang singkat — bagaimana perasaan perbualan tersebut, siapa yang anda ingin makan bersama lagi, sama ada lokasi memuaskan. Isyarat tersebut dimasukkan semula ke dalam model dan membentuk setiap padanan masa hadapan.",
      "hm.matchmaker.loop_1": "Undian \"makan lagi\" daripada rakan menguatkan gandingan anda",
      "hm.matchmaker.loop_2": "Undian 'tidak' bersama menamatkan gandingan secara senyap",
      "hm.matchmaker.loop_3": "Penarafan lokasi & perbualan memperkemas gabungan tempat duduk",
      "hm.matchmaker.loop_4": "Skor kebolehpercayaan melindungi ahli daripada ketidakhadiran berulang",
      "hm.matchmaker.loop_5": "Bias pembelajaran memperkukuh padanan yang baik dari bulan ke bulan",
      "hm.matchmaker.loop_6": "Anda kekal dalam kawalan — tarik diri atau tetapkan semula profil anda pada bila-bila masa",
      "hm.matchmaker.vs_kicker": "Mana satu yang sesuai untuk saya?",
      "hm.matchmaker.vs_title": "Matchmaker atau Circles?",
      "hm.matchmaker.vs_mm_ttl": "Pilih saya jika mahukan kepelbagaian.",
      "hm.matchmaker.vs_mm_1": "Meja, bandar, masakan yang berbeza setiap kali",
      "hm.matchmaker.vs_mm_2": "Kumpulan yang dikurasi AI terdiri daripada 4-8 orang asing yang serasi",
      "hm.matchmaker.vs_mm_3": "Ritma mingguan, tiada komitmen hos",
      "hm.matchmaker.vs_mm_4": "Sesuai jika anda baru di bandar atau sedang melancong",
      "hm.matchmaker.vs_mm_cta": "Ambil kuiz pemadanan",
      "hm.matchmaker.vs_circles_ttl": "Pilih saya jika mahukan rutin tetap.",
      "hm.matchmaker.vs_circles_1": "Hos yang sama · lokasi yang sama · malam minggu yang sama",
      "hm.matchmaker.vs_circles_2": "Kumpulan yang menjadi tradisi malam Rabu anda",
      "hm.matchmaker.vs_circles_3": "Dikurasi secara manusia oleh hos yang berpengalaman",
      "hm.matchmaker.vs_circles_4": "Sesuai jika anda baru berpindah dan mahu bertapak",
      "hm.matchmaker.vs_circles_cta": "Layari Circles",
      "hm.matchmaker.bottom_title": "Satu kuiz. Setiap makan malam selepas itu sudah serasi.",
      "hm.matchmaker.bottom_sub": "Anda akan dipadankan dalam masa dua minggu. Ia adalah \"ya\" termudah yang anda akan buat bulan ini.",
      "hm.matchmaker.bottom_cta": "Ambil kuiz pemadanan",
      "hm.matchmaker.bottom_note": "Tiada kad kredit · 10 minit · Batalkan padanan bila-bila masa",
      /* AUTO-TRANSLATED hm.* END */
    },
  },
  tl: {
    translation: {
      /* AUTO-TRANSLATED finder.* (added) */
      "finder.title": "HustleFinder",
      "finder.tagline": "Maghanap ng pinakamalapit na pampublikong lugar — tunay na datos, buong Australia.",
      "finder.search_placeholder": "Maghanap ng mga lugar, kalye o suburb…",
      "finder.hint": "Magsimulang mag-type sa itaas, pindutin ang kategorya, o gamitin ang \\\"Malapit sa akin\\\".",
      "finder.near_me": "Malapit sa akin",
      "finder.searching": "Naghahanap…",
      "finder.suburb.placeholder": "I-filter ayon sa suburb (hal. Darwin, NT)…",
      "finder.suburb.clear": "I-clear",
      "finder.search_in": "Maghanap sa {{cat}}",
      "finder.empty": "Walang resulta — subukan ang ibang kategorya o search term.",
      "finder.meta.title": "HustleFinder — libreng Aussie suburb directory",
      "finder.meta.desc": "Maghanap ng mga lokal na negosyo, charger, café, palikuran at iba pa sa bawat suburb ng Australia — 100% libre.",
      "finder.footer.credits": "Data credits & attribution",
      "finder.footer.terms": "Mga Tuntunin",
      "finder.cat.toilet": "Mga Palikuran",
      "finder.cat.fountain": "Mga Inumang Gripo",
      "finder.cat.transport": "Transportasyon",
      "finder.cat.park": "Mga Parke",
      "finder.cat.medical": "Medikal",
      "finder.cat.school": "Mga Paaralan",
      "finder.cat.library": "Mga Aklatan",
      "finder.cat.petrol": "Gasolinahan",
      "finder.cat.kidzone": "Kid Zone",
      "finder.cat.puzzles": "Mga Puzzle",
      "finder.cat.roadtrip": "Road trip",
      "finder.hotels.title": "Maghanap ng hotel na malapit",
      "finder.hotels.sub": "Inihahambing ang 200+ booking sites sa isang paghahanap — powered by Trivago.",
      "finder.hotels.where": "Saan",
      "finder.hotels.where_ph": "Lungsod o suburb (hal. Byron Bay, NSW)",
      "finder.hotels.checkin": "Check-in",
      "finder.hotels.checkout": "Check-out",
      "finder.hotels.adults": "Matatanda",
      "finder.hotels.search": "Maghanap ng mga hotel",
      "finder.hotels.disclosure": "Bubuksan ang Trivago — maaari kaming makakuha ng commission sa mga booking.",
      /* AUTO-TRANSLATED rt.* START */
      "rt.brand_name": "Roadtrip Planner",
      "rt.page_title": "Aussie EV Road-trip Planner",
      "rt.from": "Mula",
      "rt.to": "Papunta",
      "rt.from_placeholder": "Suburbo, bayan o lungsod (hal. Newcastle NSW)",
      "rt.to_placeholder": "Suburbo, bayan o lungsod (hal. Hobart TAS)",
      "rt.stop": "STOP",
      "rt.stop_placeholder": "Pansamantalang hinto",
      "rt.add_stop": "Magdagdag ng hinto",
      "rt.upgrade_pro": "Mag-upgrade sa Pro para sa hanggang 20 hinto",
      "rt.reverse": "Baligtarin (kalkulahin ang pauwi)",
      "rt.from_current": "Mula sa kasalukuyang lokasyon",
      "rt.range_per_charge": "Distansya bawat karga (km)",
      "rt.connector": "Konektor",
      "rt.connector_any": "Kahit ano",
      "rt.drive_hours_day": "Oras ng pagmamaneho/araw",
      "rt.accommodation": "Akomodasyon",
      "rt.accom_any": "Kahit ano (BnB · Hotel · Caravan)",
      "rt.accom_bnb": "BnB",
      "rt.accom_hotel": "Hotel",
      "rt.accom_caravan": "Caravan Park",
      "rt.min_rating": "Minimum na rating",
      "rt.any_rating": "Kahit anong rating",
      "rt.only_5star": "5★ lang",
      "rt.schedule": "Iskedyul:",
      "rt.depart_at": "Aalis sa",
      "rt.arrive_by": "Darating sa",
      "rt.hotel_checkin": "Hotel check-in",
      "rt.hotel_checkout": "Hotel check-out",
      "rt.overnight_hint": "Hindi ka i-iskedyul sa labas ng mga oras na ito para sa overnight na biyahe.",
      "rt.whos_in_car": "Sino ang nasa loob ng sasakyan?",
      "rt.adults": "Mga matanda",
      "rt.kids": "Mga bata",
      "rt.ages": "Mga edad",
      "rt.show_attractions": "Ipakita ang mga atraksyong panturista",
      "rt.show_kids": "Ipakita ang mga hintong pambata",
      "rt.overnight_stays": "Overnight na pananatili",
      "rt.fast_chargers_only": "Fast chargers lang",
      "rt.hotel_must_charger": "Dapat may charger ang hotel",
      "rt.pet_only": "Pet-friendly lang",
      "rt.safety": "Kaligtasan",
      "rt.safety_90": "90% (patag)",
      "rt.safety_85": "85% (default)",
      "rt.safety_80": "80% (burol / paghatak)",
      "rt.safety_70": "70% (mabigat na karga)",
      "rt.save_trip": "I-save ang biyahe",
      "rt.plan_my_trip": "I-plano ang aking biyahe",
      "rt.refresh_trip": "I-refresh ang biyahe",
      "rt.planning": "Nagpaplano…",
      "rt.resume_title": "Ituloy ang biyahe mula rito",
      "rt.resume_body": "Nakuha namin ang iyong kasalukuyang lokasyon. Ilagay ang natitirang distansya sa iyong dash para ma-plano ang susunod na bahagi base sa kung ano ang mayroon ka — hindi ang punong karga.",
      "rt.remaining_range": "Natitirang distansya (km)",
      "rt.cancel": "Kanselahin",
      "rt.replan_from_here": "I-plano muli mula rito",
      "rt.save_modal_title": "Pangalanan ang biyaheng ito",
      "rt.save_modal_body": "Ang mga naka-save na biyahe ay nasa iyong profile — buksan muli anumang oras at i-click ang I-plano muli.",
      "rt.save_name_placeholder": "hal. Brisbane → Sydney via Byron",
      "rt.your_saved_trips": "Ang iyong mga naka-save na biyahe",
      "rt.custom_location": "Custom na lokasyon",
      "rt.trip_summary": "Buod ng biyahe",
      "rt.itinerary": "Itinerary",
      "rt.no_stops_needed": "Walang kailangang hinto — kasya ang buong biyahe sa iyong karga.",
      "rt.chargers_in_corridor": "mga charger sa daraanan",
      "rt.planned_stops": "mga nakaplanong hinto",
      "rt.overnight_stop": "overnight na hinto",
      "rt.overnight_stops": "mga overnight na hinto",
      "rt.suggested_detours": "Mga mungkahi na detour — lagyan ng check para idagdag",
      "rt.replan_with_detours": "I-plano muli kasama ang mga detour",
      "rt.attractions_label": "Atraksyon",
      "rt.kids_label": "Mga bata",
      "rt.km_from_start": "km mula simula",
      "rt.open_driving": "Buksan ang driving mode",
      "rt.big_font_hint": "Malaking font, madaling gamitin sa cradle. Gumagana sa CarPlay/Android Auto browser mirror.",
      "rt.check_price_book": "Suriin ang presyo at mag-book sa Trivago ↗",
      "rt.visit_site": "Bisitahin ang site ↗",
      "rt.charger_onsite": "May charger sa lokasyon",
      "rt.leg_exceeds_range": "Lalampas ang bahaging ito sa iyong itinakdang distansya — mawawalan ka ng karga bago makarating sa charger na ito. Bawasan ang inaasahang distansya o magdagdag ng pansamantalang karga.",
      "rt.add_waypoint_replan": "Idagdag bilang waypoint at i-plano muli",
      /* AUTO-TRANSLATED rt.* END */
                      /* AUTO-TRANSLATED hm.* START */
      "hm.nav.hustlemeet": "HustleMeet",
      "hm.nav.circles": "Circles",
      "hm.nav.matchmaker": "Matchmaker",
      "hm.nav.meet": "Meet",
      "hm.nav.overview": "HustleMeetups",
      "hm.overview.page_title": "HustleMeetups — tatlong mainit na paraan para mahanap ang iyong mga tao.",
      "hm.overview.kicker": "Bahagi ng HustleSuite",
      "hm.overview.hero_title": "Tatlong mainit na paraan para mahanap ang iyong mga tao.",
      "hm.overview.hero_title_accent": "iyong mga tao",
      "hm.overview.hero_sub": "Ang HustleMeetups ay ang pinakamabait na paraan sa Australia para bumuo ng mga tunay na pagkakaibigan — piliin ang vibe na nababagay sa iyo. Gusto mo man ng weekly na running group, monthly na dinner kasama ang dati mo nang barkada, o isang AI-matched na mesa ng mga bagong mukha, may puwang para sa iyo.",
      "hm.overview.tile_meet_kicker": "HustleMeet",
      "hm.overview.tile_meet_title": "Hanapin ang iyong mga tao. Gawin ang bagay na gusto mo.",
      "hm.overview.tile_meet_body": "Ang tahanan sa Australia para sa mga grupong nakatuon sa interes. Mga runner, coder, investor, walker, dancer, breakfast club, book crew — daan-daang grupo, tunay na mga event linggo-linggo. Sumipot nang minsan, o linggu-linggo.",
      "hm.overview.tile_meet_cta": "Mag-explore ng mga grupo",
      "hm.overview.tile_circles_kicker": "Circles",
      "hm.overview.tile_circles_title": "Ang barkada mo. Bagong mesa bawat pagkakataon.",
      "hm.overview.tile_circles_body": "Isama ang iyong regular na grupo ng mga kaibigan. Kami ang magbu-book ng bagong restaurant, magpaplano ng trivia, at maghahanda ng mga baraha. Sisipot ka lang. Ang pinakamadaling paraan para mapanatiling malapit ang dati nang barkada — nang walang kailangang mag-organisa.",
      "hm.overview.tile_circles_cta": "Magsimula ng Circle",
      "hm.overview.tile_matchmaker_kicker": "Matchmaker",
      "hm.overview.tile_matchmaker_title": "AI ang pipili ng mesa. Sisipot ka lang.",
      "hm.overview.tile_matchmaker_body": "Sagutan ang isang 10-minutong quiz. Kinabukasan, pauupuin ka namin kasama ang mga estranghero na parang matagal mo nang kaibigan pagdating ng dessert. Iba't ibang mesa bawat pagkakataon.",
      "hm.overview.tile_matchmaker_cta": "Sagutan ang quiz",
      "hm.overview.trust_kicker": "Bakit ito epektibo",
      "hm.overview.trust_title": "Tiwala muna. Mesa ang kasunod.",
      "hm.overview.trust_1_title": "Mga verified na miyembro",
      "hm.overview.trust_1_body": "Bawat grupo ay nagsisimula sa isang tunay na ID check + reliability score. Walang mga throwaway account.",
      "hm.overview.trust_2_title": "Mga tunay na host",
      "hm.overview.trust_2_body": "Bawat grupo ay pinamumunuan ng isang vetted na host na nagpapanatili ng vibe, nagtatakda ng daloy, at nag-aalaga sa lahat sa silid.",
      "hm.overview.trust_3_title": "Pagkakaibigan muna",
      "hm.overview.trust_3_body": "Ang lahat dito ay idinisenyo para sa tunay na pagkakaibigan, hindi para sa pakikipag-date. Kailanman.",
      "hm.overview.trust_4_title": "Zero-tolerance na moderation",
      "hm.overview.trust_4_body": "Isang report, isang review, isang desisyon. Ang mga pasaway ay tinatanggal sa mismong gabi.",
      "hm.overview.footer_line": "Bahagi ng HustleSuite — pagmamay-ari at hosted sa Australia.",
      "hm.landing.page_title": "HustleMeet · Hanapin ang iyong mga tao. Gawin ang bagay na gusto mo.",
      "hm.landing.kicker": "Ang network para sa mga interes sa Australia",
      "hm.landing.hero_title": "Hanapin ang iyong mga tao. Gawin ang bagay na gusto mo.",
      "hm.landing.hero_title_accent": "iyong mga tao",
      "hm.landing.hero_sub": "Ang HustleMeet ay ang tahanan sa Australia para sa mga grupong nakatuon sa interes. Software devs na nagde-debug habang nanananghalian. Long-course runners tuwing 6am. Mga investor na nagtitipon buwan-buwan. Mga walking group, dance crew, breakfast club, board-game nights. Pumili ng grupo, i-book ang susunod na event, at sumipot.",
      "hm.landing.cta_explore_circles": "Mag-browse ng mga grupo",
      "hm.landing.cta_try_matchmaker": "Simulan ang sarili mong grupo",
      "hm.landing.trust_verified": "Mga verified na miyembro",
      "hm.landing.trust_venues": "Mga vetted na event host",
      "hm.landing.trust_friendship": "Pagkakaibigan muna — hindi kailanman pakikipag-date",
      "hm.landing.trust_moderation": "Zero-tolerance na moderation",
      "hm.landing.subbrand_circles_kicker": "Circles",
      "hm.landing.subbrand_circles_title": "Mayroon na bang barkada? Bigyan sila ng permanenteng tahanan.",
      "hm.landing.subbrand_circles_body": "Ang Circles ay para sa grupo ng mga magkakaibigan na dati nang nagkikita — book club, poker night, running crew, work friends. Kami ang bahala sa venue, sa mga reminder, at sa roster. Sisipot ka lang.",
      "hm.landing.subbrand_circles_b1": "Pabago-bagong mga venue, iisang listahan ng kasali",
      "hm.landing.subbrand_circles_b2": "Mga auto-reminder + madaling RSVP",
      "hm.landing.subbrand_circles_b3": "Trivia, cards & themed nights on tap",
      "hm.landing.subbrand_circles_cta": "Magsimula ng Circle",
      "hm.landing.subbrand_matchmaker_kicker": "Matchmaker",
      "hm.landing.subbrand_matchmaker_title": "AI ang pipili ng mesa. Sisipot ka lang.",
      "hm.landing.subbrand_matchmaker_body": "Isang 10-minutong quiz — personalidad, values, availability. Ang aming Matchmaker ay pauupuin ka kasama ang mga estranghero na parang matagal mo nang kaibigan pagdating ng dessert. Iba't ibang mesa bawat pagkakataon.",
      "hm.landing.subbrand_matchmaker_b1": "Big-5 personality & values scored",
      "hm.landing.subbrand_matchmaker_b2": "Mga overnight invite kapag ang fit mo ay 0.70 o mas mataas pa",
      "hm.landing.subbrand_matchmaker_b3": "Natututo mula sa bawat dinner mo",
      "hm.landing.subbrand_matchmaker_cta": "Sagutan ang quiz",
      "hm.landing.how_kicker": "Paano gumagana ang HustleMeet",
      "hm.landing.how_title": "Apat na hakbang. Hanapin ang iyong grupo. Sumipot.",
      "hm.landing.how_step1_ttl": "Piliin ang iyong mga interes",
      "hm.landing.how_step1_sub": "Running, coding, investing, cooking, music, walking — sabihin sa amin kung ano ang gusto mo at kung saan ka nakatira.",
      "hm.landing.how_step2_ttl": "Mag-browse ng mga grupo na malapit sa iyo",
      "hm.landing.how_step2_sub": "Bawat grupo ay nilalathala ang host nito, ang daloy nito, at ang susunod nitong event. Kilalanin ang vibe bago ka sumali.",
      "hm.landing.how_step3_ttl": "Mag-RSVP sa susunod na event",
      "hm.landing.how_step3_sub": "Isang tap, isang calendar invite, isang malinaw na reminder sa araw bago ang event. May mga event na libre, mayroon ding may bayad — ang presyo ay laging malinaw.",
      "hm.landing.how_step4_ttl": "Sumipot & kumonekta",
      "hm.landing.how_step4_sub": "Kilalanin ang host, kilalanin ang crew, gawin ang bagay na gusto niyo. Bumalik sa susunod na linggo kung nagustuhan mo.",
      "hm.landing.stats_kicker": "Ang bagong paraan ng pagkikita sa Australia",
      "hm.landing.stats_title": "Ang dahilan kung bakit patuloy na bumabalik ang mga tao.",
      "hm.landing.stats_92_sub": "Nararamdaman nilang may nakasundo silang kahit isang tao sa event",
      "hm.landing.stats_68_sub": "Nag-RSVP sa kanilang susunod na event sa loob ng isang linggo",
      "hm.landing.stats_85_sub": "Nanatiling nakikipag-ugnayan sa isang tao mula sa grupo",
      "hm.landing.why_kicker": "Bakit kami epektibo",
      "hm.landing.why_title": "Tunay na interes. Tunay na host. Tunay na event.",
      "hm.landing.why_1_ttl": "Interes muna, hindi lokasyon muna",
      "hm.landing.why_1_body": "Ang mga tao sa iyong grupo ay talagang may pakialam sa mga bagay na mahalaga sa iyo — iyon ang shortcut sa tunay na pagkakaibigan.",
      "hm.landing.why_2_ttl": "Mga tunay na event host",
      "hm.landing.why_2_body": "Bawat grupo ay may vetted na host na nagpapanatili ng vibe, nagtatakda ng daloy, at kumikilos kung may hindi tama. Hindi isang Facebook admin. Kundi isang tunay na tao.",
      "hm.landing.why_3_ttl": "Mahalaga ang consistency",
      "hm.landing.why_3_body": "Mga bagong event linggo-linggo. Isang bagay na maaari mong abangan. Isang daloy na gagawing Tuesday-night crew mo ang mga estranghero.",
      "hm.landing.why_4_ttl": "Ligtas & secure",
      "hm.landing.why_4_body": "Verified na mga miyembro, mga pampublikong venue, zero tolerance para sa kawalan ng respeto. Ang aming support team ay nakaabang bago, habang, at pagkatapos ng bawat event.",
      "hm.landing.why_5_ttl": "Pagkakaibigan muna",
      "hm.landing.why_5_body": "Hindi ito pakikipag-date. Ito ay komunidad. Tunay na mga interes, tunay na mga tao, walang swiping sa gitna.",
      "hm.landing.why_6_ttl": "Sapat lang ang laki para maging makabuluhan",
      "hm.landing.why_6_body": "Karamihan sa mga grupo ay limitado ang upuan sa 4-15. Sapat na laki para magkaroon ng tunay na usapan. Sapat na liit para walang sinumang mawawala sa usapan.",
      "hm.landing.cities_kicker": "Saan kami matatagpuan",
      "hm.landing.cities_title": "Mga grupong nagtitipon sa buong Australia & NZ.",
      "hm.landing.cities_sub_zero": "Mga unang grupong magbubukas sa 15+ na lungsod — sumali nang maaga at hubugin ang inyong silid.",
      "hm.landing.cities_sub_some": "May mga grupong live na sa mahigit 15 na lungsod. Mayroong magaganap sa iyong lungsod ngayong linggo.",
      "hm.landing.cities_sub_one": "{{count}} live na grupo · mga bagong event linggo-linggo · palaging nagdadagdag ng mga lungsod.",
      "hm.landing.cities_sub_many": "{{count}} live na mga grupo · mga bagong event linggo-linggo · palaging nagdadagdag ng mga lungsod.",
      "hm.landing.forme_kicker": "Para sa iyo ito kung…",
      "hm.landing.forme_title": "Gusto mo ng tunay na interes. Tunay na tao. Tunay na event.",
      "hm.landing.forme_1": "Pagod ka na sa pag-swipe sa mga app at gusto mo talagang makakilala ng mga tao base sa mga bagay na mahal mong gawin.",
      "hm.landing.forme_2": "Lumipat ka sa isang bagong lungsod at kailangan mo ng isang mainit na barkada, hindi lang basta isang chat group.",
      "hm.landing.forme_3": "Pinapahalagahan mo ang lalim kaysa sa paramihan — ang isang magandang grupo ay mas mainam kaysa sa limampung follow.",
      "hm.landing.forme_4": "Naniniwala ka na ang shared interests + isang nakatakdang dalawang oras sa totoong buhay ang tunay na shortcut.",
      "hm.landing.notme_kicker": "Hindi ito para sa iyo kung…",
      "hm.landing.notme_title": "Hindi para sa lahat ang HustleMeet.",
      "hm.landing.notme_1": "Hindi mo kayang mangakong susipot — umaasa ang mga host at miyembro sa iyong RSVP.",
      "hm.landing.notme_2": "Naghahanap ka ng dating service — pagkakaibigan ang prayoridad dito.",
      "hm.landing.notme_3": "Gusto mong mag-post nang hindi nakikipagkita sa sinuman — gumagana lang ito nang personal.",
      "hm.landing.notme_4": "Inaasahan mong laging may libreng perks — may bayad ang ilang event. Ang presyo ay laging malinaw.",
      "hm.landing.safety_kicker": "Ang aming pangako sa kaligtasan",
      "hm.landing.safety_title": "Mainit ang pagtanggap, pero hindi pabaya.",
      "hm.landing.safety_1_ttl": "Pampublikong venues",
      "hm.landing.safety_1_body": "Bawat event ay nagaganap sa isang pampubliko at may mga staff na venue — isang cafe, restaurant, parke, gym o studio. Hindi kailanman sa bahay ng kung sino maliban na lang kung sumang-ayon ang bawat dadalo.",
      "hm.landing.safety_2_ttl": "Pamantayan ng komunidad",
      "hm.landing.safety_2_body": "Zero tolerance para sa kawalan ng respeto, harassment o kawalan ng katapatan. Ang mga miyembrong lalabag ay tatanggalin agad.",
      "hm.landing.safety_3_ttl": "Protektadong privacy",
      "hm.landing.safety_3_body": "Ang iyong mga detalye ay hindi kailanman ibabahagi nang madla. Ikaw ang may kontrol kung kanino ka mananatiling konektado pagkatapos ng bawat event.",
      "hm.landing.safety_4_ttl": "Support na madaling tawagan",
      "hm.landing.safety_4_body": "Ang aming team ay laging handa bago, habang, at pagkatapos ng bawat event kung mayroon kang mga katanungan o alalahanin.",
      "hm.landing.bottom_title": "Ang susunod mong paboritong grupo ay isang tap lang ang layo.",
      "hm.landing.bottom_sub": "Piliin ang iyong tatahakin — sumali sa isang dati nang grupo, dalhin ang iyong barkada sa isang Circle, o hayaan ang Matchmaker na paupuin ka sa isang mesang nababagay sa iyo.",
      "hm.landing.bottom_cta_circles": "Magsimula ng Circle",
      "hm.landing.bottom_cta_mm": "Sagutan ang Matchmaker quiz",
      "hm.circles.page_title": "HustleMeet Circles · Barkada mo, bagong venue bawat pagkakataon",
      "hm.circles.kicker": "HustleMeet · Circles",
      "hm.circles.hero_title": "Ang barkada mo. Bagong mesa bawat pagkakataon.",
      "hm.circles.hero_title_accent": "Barkada mo",
      "hm.circles.hero_sub": "Mayroon na bang mga kaibigan? Circles ang pinakamadaling paraan para mapanatili silang malapit. Kami ang magbu-book ng venue, magpaplano ng trivia, at maghahanda ng mga baraha. Sisipot ka lang — iisang barkada, bagong gabi, bagong lugar.",
      "hm.circles.cta_find": "Magsimula ng Circle",
      "hm.circles.cta_host_hero": "Sumali sa isang open na Circle",
      "hm.circles.trust_venue": "Pabago-bagong vetted na venues",
      "hm.circles.trust_party": "4-12 na magkakaibigan bawat Circle",
      "hm.circles.trust_verified": "Mga verified na miyembro",
      "hm.circles.trust_friendship": "Tunay na pagkakaibigan, pinanatiling malapit",
      "hm.circles.how_kicker": "Paano ito gumagana",
      "hm.circles.how_title": "Apat na hakbang. Ang barkada mo ay mananatiling barkada.",
      "hm.circles.how_step1_ttl": "Simulan ang iyong Circle",
      "hm.circles.how_step1_sub": "Pangalanan ito, i-invite ang iyong mga kaibigan (hanggang 12), at pumili ng daloy — weekly, fortnightly o monthly.",
      "hm.circles.how_step2_ttl": "Kami ang pipili ng venue",
      "hm.circles.how_step2_sub": "Bawat event ay magmumungkahi kami ng bagong lugar — restaurant, trivia night, card room, wine bar. O kaya naman ay i-lock in ang isang paborito.",
      "hm.circles.how_step3_ttl": "Lahat ay mag-RSVP sa isang tap lang",
      "hm.circles.how_step3_sub": "Mga auto-reminder, hatian sa bayad, prepaid na mga upuan para hindi masira ang plano ng mga no-show. Ang bill ay ayos na bago pa dumating ang entrée.",
      "hm.circles.how_step4_ttl": "Panatilihin ang daloy",
      "hm.circles.how_step4_sub": "Isang gabi sa kalendaryo na hindi na kailangang i-organisa muli. Mas tumitibay ang grupo sa paglipas ng mga buwan, hindi lang minuto.",
      "hm.circles.cadence_weekly_ttl": "Weekly",
      "hm.circles.cadence_weekly_sub": "Linggo-linggo",
      "hm.circles.cadence_weekly_body": "Para sa mga barkadang gusto ng Tuesday-night ritual — iisang mga mukha, bagong lugar bawat pagkakataon.",
      "hm.circles.cadence_fortnight_ttl": "Fortnightly",
      "hm.circles.cadence_fortnight_sub": "Tuwing 2 linggo",
      "hm.circles.cadence_fortnight_body": "Ang tamang balanse — regular pero hindi masyadong demanding sa mga busy na schedule.",
      "hm.circles.cadence_monthly_ttl": "Monthly",
      "hm.circles.cadence_monthly_sub": "Buwan-buwan",
      "hm.circles.cadence_monthly_body": "Mga Circles para sa special-occasion — Sunday roasts, malalaking trivia nights, degustation, poker.",
      "hm.circles.feast_kicker": "Piliin ang iyong gabi",
      "hm.circles.feast_title": "Tatlong paraan para gugulin ito",
      "hm.circles.feast_sub": "Bawat Circle ay pumipili kung paano tatakbo ang gabi. Pagpalit-palitin ang mga ito, o i-lock in ang paborito ng inyong barkada.",
      "hm.circles.feast_surprise_ttl": "Subukan ang isang bagong restaurant",
      "hm.circles.feast_surprise_body": "Gagawa kami ng short-list ng tatlong venue na malapit sa iyo, boboto ang lahat, at maghahanda ang kusina para sa isang punong mesa. Hatian sa bayad, prepaid na mga upuan.",
      "hm.circles.feast_preselect_ttl": "Trivia / cards / games",
      "hm.circles.feast_preselect_body": "Mag-book ng trivia night, isang poker room, board-game bar o bowling lane. Kami ang bahala sa venue, sa buy-in at sa mga reminder.",
      "hm.circles.feast_per_event_ttl": "Isang espesyal na themed night",
      "hm.circles.feast_per_event_body": "Wine tasting, chef's table, cocktail class, escape room. Isang espesyal na bagay paminsan-minsan — organisadong maigi.",
      "hm.circles.live_kicker": "Sa ngayon",
      "hm.circles.live_title": "Mga Circles na maaari mong salihan",
      "hm.circles.live_host_link": "Simulan ang iyong sarili",
      "hm.circles.live_loading": "Naglo-load…",
      "hm.circles.live_empty_title": "Maging ang kauna-unahang Circle sa iyong lungsod.",
      "hm.circles.live_empty_sub": "Magsimula ng isa kasama ang tatlong kaibigan at tutulungan ka naming mag-invite ng iba pa — plus sagot na namin ang unang tatlong venue ninyo.",
      "hm.circles.live_empty_cta": "Magsimula ng Circle",
      "hm.circles.host_kicker": "Para sa mga Circle leader",
      "hm.circles.host_title": "Ikaw ang bahala sa barkada. Kami ang bahala sa admin.",
      "hm.circles.host_body": "Kung ikaw ang kaibigan na laging nag-oorganisa ng gabi, makakatipid ka ng maraming oras sa Circles. Auto-reminders, prepaid RSVPs, venue short-lists, hatian sa bayad, waitlist management at isang cashback wallet sa bawat gabi ninyo. Sisipot ka lang bilang isang normal na miyembro.",
      "hm.circles.host_perk_1": "Auto venue short-lists — hindi na kailangang mag-scroll sa Google kailanman",
      "hm.circles.host_perk_2": "Prepaid na mga upuan — zero no-shows",
      "hm.circles.host_perk_3": "Hatian sa bayad bago pa ang dessert",
      "hm.circles.host_perk_4": "Mga reminder + calendar invites na naka-autopilot",
      "hm.circles.host_perk_5": "Waitlist na may auto-promote kung may kaibigang nag-cancel",
      "hm.circles.host_perk_6": "Cashback wallet sa bawat gabing lalabas",
      "hm.circles.host_cta": "Magsimula ng Circle ngayon",
      "hm.circles.why_kicker": "Bakit epektibo ang Circles",
      "hm.circles.why_title": "Kailangan ng imprastraktura ng mga adult friendship",
      "hm.circles.why_1_ttl": "Iisang barkada, mas malalim na tiwala",
      "hm.circles.why_1_body": "Ang buhay ay nagpapalayo sa magkakaibigan. Ang Circles ang nakatakdang daloy na nagpapanatili sa lahat na malapit — ang parehong 4-12 na magkakaibigan, nagkikita sa parehong gabi ng linggo, anuman ang mangyari.",
      "hm.circles.why_2_ttl": "Zero admin fatigue",
      "hm.circles.why_2_body": "Walang kailangang maging WhatsApp chief ng grupo. Kami ang pipili ng mga venue, magpapadala ng mga reminder, tatanggap ng mga bayad, at tatapos sa proseso.",
      "hm.circles.why_3_ttl": "Bagong venue, bagong alaala",
      "hm.circles.why_3_body": "Magpalit-palit sa pinakamagagandang maliliit na restaurant, trivia rooms at game bars sa Australia. Mga pamilyar na mukha, mga bagong kapaligiran — ang tamang timpla ng routine at bago.",
      "hm.circles.bottom_title": "Ang gabing inaabangan ng iyong barkada, nang walang kailangang magplano.",
      "hm.circles.bottom_sub": "Isang Circle. Iisang barkada. Bagong venue bawat pagkakataon.",
      "hm.circles.bottom_cta_find": "Magsimula ng Circle",
      "hm.circles.bottom_cta_mm": "Makakilala ng mga bagong tao gamit ang Matchmaker →",
      "hm.circles.card_host_pill": "Circle",
      "hm.matchmaker.page_title": "HustleMeet Matchmaker · AI ang pipili ng tamang mesa",
      "hm.matchmaker.kicker": "HustleMeet · Matchmaker",
      "hm.matchmaker.hero_title": "AI ang pipili ng mesa. Sisipot ka lang.",
      "hm.matchmaker.hero_title_accent": "Sisipot ka lang.",
      "hm.matchmaker.hero_sub": "Sagutan ang isang maikling quiz tungkol sa personalidad & values. Ang aming Matchmaker ay pauupuin ka kasama ang apat hanggang pitong estranghero na parang matagal mo nang kaibigan pagdating ng dessert. Walang swiping. Walang small talk. Isang shared table lang, handa na kapag handa ka na.",
      "hm.matchmaker.cta_quiz": "Sagutan ang matching quiz",
      "hm.matchmaker.cta_see_circles": "Tingnan ang Circles sa halip →",
      "hm.matchmaker.trust_big5": "Big-5 personality scored",
      "hm.matchmaker.trust_seats": "4-8 na upuan bawat mesa",
      "hm.matchmaker.trust_lock": "Zero-swipe · zero-photo",
      "hm.matchmaker.trust_verified": "Mga verified na miyembro lamang",
      "hm.matchmaker.how_kicker": "Paano ito gumagana",
      "hm.matchmaker.how_title": "Apat na hakbang tungo sa mesang nababagay na sa iyo.",
      "hm.matchmaker.how_step1_ttl": "Sagutan ang quiz",
      "hm.matchmaker.how_step1_sub": "Sampung minutong questionnaire — personalidad, values, dietary, availability. Gagawin mo ito nang minsan lang.",
      "hm.matchmaker.how_step2_ttl": "Overnight matching",
      "hm.matchmaker.how_step2_sub": "Babasahin ng aming AI ang iyong profile at ang live na listahan ng dinner sa Australia. Kapag ang fit score mo ay 0.70 o mas mataas pa, gagawa ito ng personal na invite.",
      "hm.matchmaker.how_step3_ttl": "Tanggapin o tanggihan",
      "hm.matchmaker.how_step3_sub": "Makakatanggap ka ng isang malinaw na invite email kasama ang venue, ang tema ng mesa, at ang tatlong interes na ibinabahagi mo sa grupo.",
      "hm.matchmaker.how_step4_ttl": "Natututo ito mula sa iyo",
      "hm.matchmaker.how_step4_sub": "Bawat post-dinner survey ay nag-a-adjust sa model. Ang susunod mong match ay nagiging mas matalino habang mas madalas kang kumakain sa labas.",
      "hm.matchmaker.science_kicker": "Bakit ito epektibo",
      "hm.matchmaker.science_title": "Hindi nanghuhula ang Matchmaker — nagbibigay ito ng score.",
      "hm.matchmaker.science_sub": "Bawat match ay isang weighted composite ng apat na factors. Kapag ang buong mesa ay may average na higit sa 0.70, nagpapadala kami ng mga invite. Anumang mas mababa ay mananatili sa queue.",
      "hm.matchmaker.science_1_ttl": "Personalidad (35%)",
      "hm.matchmaker.science_1_body": "Cosine similarity sa buong Big-5 personality vector — openness, conscientiousness, extraversion, agreeableness, at emotional stability.",
      "hm.matchmaker.science_2_ttl": "Values (25%)",
      "hm.matchmaker.science_2_body": "Overlap sa mga values na nagpapatakbo sa usapan sa dinner — pamilya, pag-usisa, paglago, serbisyo, pakikipagsapalaran.",
      "hm.matchmaker.science_3_ttl": "Interes (25%)",
      "hm.matchmaker.science_3_body": "Jaccard overlap sa iyong mga partikular na interes — pagha-hike sa Six Foot Track, Sydney film club, Melbourne coffee scene, ang iyong ikatlong libro sa taong ito.",
      "hm.matchmaker.science_4_ttl": "Availability (15%)",
      "hm.matchmaker.science_4_body": "Ang gabing maaari ka talagang makasipot. Walang 7pm Tuesday invite kung ang kalendaryo mo ay nagsasabing gym-Tuesday-forever.",
      "hm.matchmaker.safety_kicker": "Ligtas by design",
      "hm.matchmaker.safety_title": "Tahimik na maingat ang Matchmaker.",
      "hm.matchmaker.safety_1_ttl": "Confidence threshold",
      "hm.matchmaker.safety_1_body": "Kapag mababa sa 0.70 group score, walang invite na ipapadala. Mas mabuti ang isang siguradong match kaysa sa basta-bastang dinner.",
      "hm.matchmaker.safety_2_ttl": "Mutual-no blocklist",
      "hm.matchmaker.safety_2_body": "Kung dalawang miyembro ay lihim na nag-signal ng 'no' pagkatapos ng dinner, hindi na namin sila muling pauupuin nang magkasama.",
      "hm.matchmaker.safety_3_ttl": "Auto-kill switch",
      "hm.matchmaker.safety_3_body": "Kung 20% o higit pa ng mga kamakailang survey ay nag-rate sa mesa ng 2/5 o mas mababa, pansamantalang hihinto ang mga invite sa loob ng 24h habang sinusuri ito ng isang operator.",
      "hm.matchmaker.safety_4_ttl": "Pamantayan ng komunidad",
      "hm.matchmaker.safety_4_body": "Bawat miyembro ay sumasang-ayon sa isang malinaw na code of conduct. Tinatanggal namin ang mga taong lumalabag dito — walang apela.",
      "hm.matchmaker.safety_5_ttl": "Pampublikong venues lamang",
      "hm.matchmaker.safety_5_body": "Bawat Matchmaker dinner ay nagaganap sa isang tunay na restaurant na may mga staff. Hindi kailanman sa bahay ng kung sino.",
      "hm.matchmaker.safety_6_ttl": "Post-event feedback",
      "hm.matchmaker.safety_6_body": "Ang iyong pribadong survey ang huhubog sa mga susunod na match, at maaaring mag-trigger ng mod review ng venue o ng isang kasama.",
      "hm.matchmaker.loop_kicker": "Ang learning loop",
      "hm.matchmaker.loop_title": "Bawat dinner ay ginagawang mas maganda ang susunod mong dinner.",
      "hm.matchmaker.loop_body": "Ang Matchmaker ay hindi static. Pagkatapos ng bawat event ay magpapasa ka ng isang maikling pribadong survey — kumusta ang pakiramdam ng usapan, sino ang gusto mong makasama ulit sa dinner, kung naging maayos ba ang venue. Ang mga signal na iyon ay bumabalik sa model at humuhubog sa bawat susunod na match.",
      "hm.matchmaker.loop_1": "Ang mga peer \"dine again\" votes ay nagpapatibay sa iyong mga pairing",
      "hm.matchmaker.loop_2": "Ang mga mutual-no votes ay tahimik na nagtatanggal sa mga pares",
      "hm.matchmaker.loop_3": "Ang mga rating sa venue & usapan ay nag-a-adjust sa seat mix",
      "hm.matchmaker.loop_4": "Ang reliability score ay nagpoprotekta sa mga miyembro mula sa mga paulit-ulit na no-show",
      "hm.matchmaker.loop_5": "Ang learning bias ay nagpapatibay sa mga magagandang match buwan-buwan",
      "hm.matchmaker.loop_6": "Manatiling may kontrol — mag-opt-out o i-reset ang iyong profile anumang oras",
      "hm.matchmaker.vs_kicker": "Alin ang tama para sa akin?",
      "hm.matchmaker.vs_title": "Matchmaker o Circles?",
      "hm.matchmaker.vs_mm_ttl": "Piliin ako kung gusto ko ng variety.",
      "hm.matchmaker.vs_mm_1": "Iba't ibang mesa, lungsod, cuisine bawat pagkakataon",
      "hm.matchmaker.vs_mm_2": "AI-curated na grupo ng 4-8 compatible na mga estranghero",
      "hm.matchmaker.vs_mm_3": "Lingguhang daloy, walang host commitment",
      "hm.matchmaker.vs_mm_4": "Mainam kung bago ka sa isang lungsod o naglalakbay",
      "hm.matchmaker.vs_mm_cta": "Sagutan ang matching quiz",
      "hm.matchmaker.vs_circles_ttl": "Piliin ako kung gusto ko ng regular.",
      "hm.matchmaker.vs_circles_1": "Iisang host · iisang venue · iisang gabi ng linggo",
      "hm.matchmaker.vs_circles_2": "Isang grupong magiging tradisyon mo tuwing Wednesday",
      "hm.matchmaker.vs_circles_3": "Human-curated ng isang makaranasang host",
      "hm.matchmaker.vs_circles_4": "Mainam kung kalilipat mo lang at gusto mong magkaroon ng matibay na samahan",
      "hm.matchmaker.vs_circles_cta": "Mag-browse ng Circles",
      "hm.matchmaker.bottom_title": "Isang quiz. Bawat dinner pagkatapos niyon ay nababagay na sa iyo.",
      "hm.matchmaker.bottom_sub": "Maka-match ka sa loob ng dalawang linggo. Ito ang pinakamadaling \"oo\" na gagawin mo ngayong buwan.",
      "hm.matchmaker.bottom_cta": "Sagutan ang matching quiz",
      "hm.matchmaker.bottom_note": "Walang credit card · 10 minuto · Mag-unmatch anumang oras",
      /* AUTO-TRANSLATED hm.* END */
    },
  },
  my: {
    translation: {
      /* AUTO-TRANSLATED finder.* (added) */
      "finder.title": "HustleFinder",
      "finder.tagline": "အနီးဆုံးအများပြည်သူသုံးနေရာကို ရှာဖွေပါ — အစစ်အမှန်ဒေတာ၊ ဩစတြေးလျတစ်ဝန်း။",
      "finder.search_placeholder": "နေရာများ၊ လမ်းများ သို့မဟုတ် ဆင်ခြေဖုံးများကို ရှာဖွေပါ…",
      "finder.hint": "အပေါ်တွင် စာရိုက်ရန် စတင်ပါ၊ အမျိုးအစားတစ်ခုကို နှိပ်ပါ သို့မဟုတ် \\\"ကျွန်ုပ်အနီးတွင်\\\" ကို အသုံးပြုပါ။",
      "finder.near_me": "ကျွန်ုပ်အနီးတွင်",
      "finder.searching": "ရှာဖွေနေသည်…",
      "finder.suburb.placeholder": "ဆင်ခြေဖုံးဖြင့် စစ်ထုတ်ပါ (ဥပမာ Darwin, NT)…",
      "finder.suburb.clear": "ရှင်းလင်းရန်",
      "finder.search_in": "{{cat}} တွင် ရှာဖွေရန်",
      "finder.empty": "ရလဒ်မရှိပါ — အခြားအမျိုးအစား သို့မဟုတ် ရှာဖွေမှုစကားလုံးကို စမ်းကြည့်ပါ။",
      "finder.meta.title": "HustleFinder — အခမ့် Aussie ဆင်ခြေဖုံးလမ်းညွှန်",
      "finder.meta.desc": "ဩစတြေးလျဆင်ခြေဖုံးတိုင်းရှိ ဒေသခံစီးပွားရေးလုပ်ငန်းများ၊ အားသွင်းစက်များ၊ ကော်ဖီဆိုင်များ၊ အိမ်သာများ နှင့် အခြားအရာများကို ရှာဖွေပါ — ၁၀၀% အခမ့်။",
      "finder.footer.credits": "ဒေတာခရက်ဒစ်များနှင့် အသိအမှတ်ပြုချက်",
      "finder.footer.terms": "စည်းကမ်းချက်များ",
      "finder.cat.toilet": "အိမ်သာများ",
      "finder.cat.fountain": "သောက်ရေစမ်းများ",
      "finder.cat.transport": "သယ်ယူပို့ဆောင်ရေး",
      "finder.cat.park": "ပန်းခြံများ",
      "finder.cat.medical": "ဆေးဝါး",
      "finder.cat.school": "ကျောင်းများ",
      "finder.cat.library": "စာကြည့်တိုက်များ",
      "finder.cat.petrol": "ဓာတ်ဆီ",
      "finder.cat.kidzone": "ကလေးဇုန်",
      "finder.cat.puzzles": "ပဟေဠိများ",
      "finder.cat.roadtrip": "လမ်းခရီး",
      "finder.hotels.title": "အနီးနားရှိ ဟိုတယ်တစ်ခုကို ရှာဖွေပါ",
      "finder.hotels.sub": "တစ်ခုတည်းသောရှာဖွေမှုတွင် ဘွတ်ကင်ဆိုဒ် ၂၀၀+ ကို နှိုင်းယှဉ်ပါ — Trivago မှ စွမ်းအားပေး။",
      "finder.hotels.where": "ဘယ်မှာ",
      "finder.hotels.where_ph": "မြို့ သို့မဟုတ် ဆင်ခြေဖုံး (ဥပမာ Byron Bay, NSW)",
      "finder.hotels.checkin": "ဝင်ရောက်မည့်ရက်",
      "finder.hotels.checkout": "ထွက်မည့်ရက်",
      "finder.hotels.adults": "လူကြီးများ",
      "finder.hotels.search": "ဟိုတယ်များကို ရှာဖွေရန်",
      "finder.hotels.disclosure": "Trivago ကို ဖွင့်ပါ — ကျွန်ုပ်တို့သည် ဘွတ်ကင်များမှ ကော်မရှင်ရရှိနိုင်ပါသည်။",
      /* AUTO-TRANSLATED rt.* START */
      "rt.brand_name": "ခရီးစဉ်စီစဉ်သူ",
      "rt.page_title": "Aussie EV ခရီးစဉ်စီစဉ်သူ",
      "rt.from": "မှ",
      "rt.to": "သို့",
      "rt.from_placeholder": "ရပ်ကွက်၊ မြို့နယ် သို့မဟုတ် မြို့ (ဥပမာ- Newcastle NSW)",
      "rt.to_placeholder": "ရပ်ကွက်၊ မြို့နယ် သို့မဟုတ် မြို့ (ဥပမာ- Hobart TAS)",
      "rt.stop": "STOP",
      "rt.stop_placeholder": "ကြားနားရန်နေရာ",
      "rt.add_stop": "နားမည့်နေရာထည့်ရန်",
      "rt.upgrade_pro": "နားမည့်နေရာ ၂၀ အထိ သုံးနိုင်ရန် Pro သို့ အဆင့်မြှင့်ပါ",
      "rt.reverse": "ပြောင်းပြန် (အပြန်လမ်းအတွက် တွက်ချက်ရန်)",
      "rt.from_current": "လက်ရှိတည်နေရာမှ",
      "rt.range_per_charge": "တစ်ခါအားသွင်းလျှင် မောင်းနိုင်သည့်အကွာအဝေး (ကီလိုမီတာ)",
      "rt.connector": "ခေါင်းအမျိုးအစား",
      "rt.connector_any": "အားလုံး",
      "rt.drive_hours_day": "တစ်နေ့မောင်းချိန် (နာရီ)",
      "rt.accommodation": "တည်းခိုရန်နေရာ",
      "rt.accom_any": "အားလုံး (BnB · ဟိုတယ် · Caravan)",
      "rt.accom_bnb": "BnB",
      "rt.accom_hotel": "ဟိုတယ်",
      "rt.accom_caravan": "Caravan ကွင်း",
      "rt.min_rating": "အနည်းဆုံး အဆင့်သတ်မှတ်ချက်",
      "rt.any_rating": "ကြိုက်သည့်အဆင့်",
      "rt.only_5star": "5★ သာ",
      "rt.schedule": "အချိန်ဇယား:",
      "rt.depart_at": "ထွက်ခွာချိန်",
      "rt.arrive_by": "ဆိုက်ရောက်ချိန်",
      "rt.hotel_checkin": "ဟိုတယ်ဝင်ချိန်",
      "rt.hotel_checkout": "ဟိုတယ်ထွက်ချိန်",
      "rt.overnight_hint": "ညအိပ်ခရီးစဉ်များကို ဤအချိန်ပြင်ပတွင် ထည့်သွင်းမည်မဟုတ်ပါ။",
      "rt.whos_in_car": "ကားထဲတွင် မည်သူပါသနည်း?",
      "rt.adults": "လူကြီး",
      "rt.kids": "ကလေး",
      "rt.ages": "အသက်",
      "rt.show_attractions": "လည်ပတ်စရာနေရာများ ပြရန်",
      "rt.show_kids": "ကလေးများအတွက် သင့်တော်သည့်နေရာများ ပြရန်",
      "rt.overnight_stays": "ညအိပ်နားမည့်နေရာများ",
      "rt.fast_chargers_only": "အမြန်အားသွင်းစက်များသာ",
      "rt.hotel_must_charger": "ဟိုတယ်တွင် အားသွင်းစက် ရှိရမည်",
      "rt.pet_only": "အိမ်မွေးတိရစ္ဆာန်လက်ခံသည့် နေရာများသာ",
      "rt.safety": "စိတ်ချရမှု",
      "rt.safety_90": "90% (မြေပြန့်)",
      "rt.safety_85": "85% (ပုံမှန်)",
      "rt.safety_80": "80% (ကုန်းတက် / ဝန်ဆွဲခြင်း)",
      "rt.safety_70": "70% (ဝန်အလေးကြီး)",
      "rt.save_trip": "ခရီးစဉ် သိမ်းရန်",
      "rt.plan_my_trip": "ခရီးစဉ် စီစဉ်ရန်",
      "rt.refresh_trip": "ခရီးစဉ် အသစ်ပြန်လုပ်ရန်",
      "rt.planning": "စီစဉ်နေသည်…",
      "rt.resume_title": "ဤနေရာမှ ခရီးစဉ် ပြန်စရန်",
      "rt.resume_body": "ကျွန်ုပ်တို့သည် သင်၏ လက်ရှိတည်နေရာကို ရယူထားပါသည်။ အားအပြည့်မဟုတ်ဘဲ လက်ရှိရှိနေသည့် ပမာဏအတိုင်း နောက်ခရီးစဉ်ကို စီစဉ်နိုင်ရန် ဒက်ရှ်ဘုတ်တွင် ပြထားသည့် ကျန်ရှိသော အကွာအဝေးကို ရိုက်ထည့်ပါ။",
      "rt.remaining_range": "ကျန်ရှိသော အကွာအဝေး (ကီလိုမီတာ)",
      "rt.cancel": "ပယ်ဖျက်ရန်",
      "rt.replan_from_here": "ဤနေရာမှ ပြန်စီစဉ်ရန်",
      "rt.save_modal_title": "ဤခရီးစဉ်အား အမည်ပေးပါ",
      "rt.save_modal_body": "သိမ်းထားသော ခရီးစဉ်များကို သင့်ပရိုဖိုင်တွင် တွေ့နိုင်ပါသည် — အချိန်မရွေး ပြန်ဖွင့်ပြီး ပြန်စီစဉ်နိုင်ပါသည်။",
      "rt.save_name_placeholder": "ဥပမာ- Brisbane → Sydney (Byron မှတစ်ဆင့်)",
      "rt.your_saved_trips": "သင်သိမ်းထားသော ခရီးစဉ်များ",
      "rt.custom_location": "စိတ်ကြိုက်တည်နေရာ",
      "rt.trip_summary": "ခရီးစဉ်အကျဉ်းချုပ်",
      "rt.itinerary": "ခရီးစဉ်လမ်းညွှန်",
      "rt.no_stops_needed": "နားရန်မလိုပါ — ခရီးစဉ်တစ်ခုလုံးကို အားတစ်ခါသွင်းရုံဖြင့် မောင်းနိုင်ပါသည်။",
      "rt.chargers_in_corridor": "လမ်းတစ်လျှောက် အားသွင်းစက်များ",
      "rt.planned_stops": "စီစဉ်ထားသော နားမည့်နေရာများ",
      "rt.overnight_stop": "ညအိပ်နားမည့်နေရာ",
      "rt.overnight_stops": "ညအိပ်နားမည့်နေရာများ",
      "rt.suggested_detours": "အကြံပြုထားသော လမ်းခွဲများ — ထည့်ရန် အမှန်ခြစ်ပေးပါ",
      "rt.replan_with_detours": "လမ်းခွဲများနှင့်အတူ ပြန်စီစဉ်ရန်",
      "rt.attractions_label": "လည်ပတ်စရာ",
      "rt.kids_label": "ကလေးများ",
      "rt.km_from_start": "km from start",
      "rt.open_driving": "ကားမောင်းစနစ်ကို ဖွင့်ရန်",
      "rt.big_font_hint": "စာလုံးကြီးကြီးနှင့် ကားဖုန်းတင်စင်တွင် အသုံးပြုရန် လွယ်ကူသည်။ CarPlay/Android Auto ဘရောက်ဇာ မေရာတွင် သုံးနိုင်သည်။",
      "rt.check_price_book": "ဈေးနှုန်းစစ်ဆေးပြီး Trivago တွင် ဘိုကင်တင်ရန် ↗",
      "rt.visit_site": "ဝဘ်ဆိုဒ်သို့ သွားရန် ↗",
      "rt.charger_onsite": "ထိုနေရာတွင် အားသွင်းစက် ရှိသည်",
      "rt.leg_exceeds_range": "ဤခရီးစဉ်သည် သင်သတ်မှတ်ထားသော အကွာအဝေးထက် ကျော်လွန်နေပါသည် — ဤအားသွင်းစက်သို့ မရောက်မီ အားကုန်သွားပါလိမ့်မည်။ ခန့်မှန်းအကွာအဝေးကို လျှော့ချပါ သို့မဟုတ် ကြားတွင် အားဖြည့်ရန် နေရာတစ်ခု ထည့်ပါ။",
      "rt.add_waypoint_replan": "ကြားဖြတ်နေရာအဖြစ် ထည့်သွင်းပြီး ပြန်စီစဉ်ရန်",
      /* AUTO-TRANSLATED rt.* END */
                      /* AUTO-TRANSLATED hm.* START */
      "hm.nav.hustlemeet": "HustleMeet",
      "hm.nav.circles": "Circles",
      "hm.nav.matchmaker": "Matchmaker",
      "hm.nav.meet": "တွေ့ဆုံပါ",
      "hm.nav.overview": "HustleMeetups",
      "hm.overview.page_title": "HustleMeetups — သင့်စိတ်ကြိုက်မိတ်ဆွေများ ရှာဖွေနိုင်မည့် နွေးထွေးသော နည်းလမ်းသုံးသွယ်။",
      "hm.overview.kicker": "HustleSuite ၏ အစိတ်အပိုင်းတစ်ခု",
      "hm.overview.hero_title": "သင့်စိတ်ကြိုက်မိတ်ဆွေများ ရှာဖွေနိုင်မည့် နွေးထွေးသော နည်းလမ်းသုံးသွယ်။",
      "hm.overview.hero_title_accent": "သင့်စိတ်ကြိုက်မိတ်ဆွေများ",
      "hm.overview.hero_sub": "HustleMeetups သည် စစ်မှန်သော ခင်မင်မှုများ တည်ဆောက်ရန် Australia ၏ အရင်းနှီးဆုံးသော နည်းလမ်းတစ်ခုဖြစ်ပြီး သင်နှင့် ကိုက်ညီမည့် ပုံစံကို ရွေးချယ်နိုင်ပါသည်။ သင်က အပတ်စဉ် ပြေးခုန်ကစားသည့် အဖွဲ့၊ လစဉ် ညစာအတူစားမည့် မိတ်ဆွေဟောင်းများအဖွဲ့ သို့မဟုတ် AI အကူအညီဖြင့် မိတ်ဆွေသစ်များနှင့် ဆုံတွေ့မည့် ဝိုင်းလေးဖြစ်စေ၊ သင့်အတွက် နည်းလမ်းတစ်ခု ရှိနေပါသည်။",
      "hm.overview.tile_meet_kicker": "HustleMeet",
      "hm.overview.tile_meet_title": "သင့်စိတ်ကြိုက်လူတွေကို ရှာဖွေပါ။ အတူတူ လုပ်ဆောင်ပါ။",
      "hm.overview.tile_meet_body": "စိတ်ဝင်စားမှုတူရာ အဖွဲ့အစည်းများအတွက် Australia ၏ အဓိကနေရာ။ အပြေးသမားများ၊ ကုဒ်ရေးသူများ၊ ရင်းနှီးမြှုပ်နှံသူများ၊ လမ်းလျှောက်သူများ၊ ကချေသည်များ၊ နံနက်စာကလပ်များ၊ စာဖတ်ဝိုင်းများ — အဖွဲ့ပေါင်း ရာနှင့်ချီ၍ အပတ်စဉ် ပွဲများရှိပါသည်။ တစ်ကြိမ်တည်းဖြစ်စေ၊ အပတ်စဉ်ဖြစ်စေ တက်ရောက်နိုင်ပါသည်။",
      "hm.overview.tile_meet_cta": "အဖွဲ့များကို လေ့လာပါ",
      "hm.overview.tile_circles_kicker": "Circles",
      "hm.overview.tile_circles_title": "သင့်သူငယ်ချင်းအဖွဲ့။ အချိန်တိုင်း နေရာသစ်။",
      "hm.overview.tile_circles_body": "သင့်မိတ်ဆွေအဖွဲ့ကို ခေါ်လာခဲ့ပါ။ ကျွန်ုပ်တို့က စားသောက်ဆိုင်အသစ် ကြိုတင်မှာယူပေးခြင်း၊ ဂိမ်းအစီအစဉ်များ ပြင်ဆင်ပေးခြင်းတို့ကို လုပ်ဆောင်ပေးပါမည်။ သင်က လာရုံပါပဲ။ အဖွဲ့အစည်းအတွက် ဘယ်သူမှ ပင်ပန်းစရာမလိုဘဲ ရင်းနှီးမှုကို ပိုမိုခိုင်မာစေမည့် နည်းလမ်းကောင်းတစ်ခုဖြစ်ပါသည်။",
      "hm.overview.tile_circles_cta": "Circles တစ်ခု စတင်ပါ",
      "hm.overview.tile_matchmaker_kicker": "Matchmaker",
      "hm.overview.tile_matchmaker_title": "AI က စီစဉ်ပေးမည်။ သင်က လာရုံပါပဲ။",
      "hm.overview.tile_matchmaker_body": "၁၀ မိနစ်ခန့် မေးခွန်းများ ဖြေဆိုပါ။ အချိုပွဲစားချိန်ရောက်လျှင် မိတ်ဆွေဟောင်းများကဲ့သို့ ရင်းနှီးသွားစေမည့် သူစိမ်းများနှင့်အတူ ကျွန်ုပ်တို့က နေရာချပေးပါမည်။ အချိန်တိုင်း ဝိုင်းအသစ် ဖြစ်နေပါလိမ့်မည်။",
      "hm.overview.tile_matchmaker_cta": "မေးခွန်းများ ဖြေဆိုပါ",
      "hm.overview.trust_kicker": "ဘာကြောင့် အလုပ်ဖြစ်သလဲ",
      "hm.overview.trust_title": "ယုံကြည်မှုကို အရင် တည်ဆောက်သည်။",
      "hm.overview.trust_1_title": "စိစစ်ပြီးသော အဖွဲ့ဝင်များ",
      "hm.overview.trust_1_body": "အဖွဲ့တိုင်းကို အိုင်ဒီစစ်ဆေးခြင်းနှင့် ယုံကြည်စိတ်ချရမှု အဆင့်သတ်မှတ်ခြင်းတို့ဖြင့် စတင်ပါသည်။ အကောင့်တုများ မရှိပါ။",
      "hm.overview.trust_2_title": "တာဝန်ယူမှုရှိသော အိမ်ရှင်များ",
      "hm.overview.trust_2_body": "အဖွဲ့တိုင်းကို စိစစ်ထားသော အိမ်ရှင်တစ်ဦးက ဦးဆောင်ပြီး အစီအစဉ်များကို စနစ်တကျ ထိန်းသိမ်းပေးပါသည်။",
      "hm.overview.trust_3_title": "ခင်မင်မှုကိုသာ ဦးစားပေးသည်",
      "hm.overview.trust_3_body": "ဤနေရာရှိ အရာအားလုံးသည် စစ်မှန်သော ခင်မင်မှုအတွက်သာ ရည်ရွယ်ပြီး ချိန်းတွေ့ခြင်း (dating) အတွက် လုံးဝမဟုတ်ပါ။",
      "hm.overview.trust_4_title": "တင်းကျပ်သော စည်းကမ်းချက်များ",
      "hm.overview.trust_4_body": "တိုင်ကြားချက်တစ်ခု၊ သုံးသပ်ချက်တစ်ခု၊ ဆုံးဖြတ်ချက်တစ်ခု။ မလိုက်နာသူများကို ထိုညတွင်းချင်း ဖယ်ရှားပါမည်။",
      "hm.overview.footer_line": "HustleSuite ၏ အစိတ်အပိုင်းတစ်ခု — Australia ပိုင်ရှင်ကိုယ်တိုင် စီစဉ်တင်ဆက်သည်။",
      "hm.landing.page_title": "HustleMeet · သင့်စိတ်ကြိုက်လူတွေကို ရှာဖွေပါ။ အတူတူ လုပ်ဆောင်ပါ။",
      "hm.landing.kicker": "Australia ၏ စိတ်ဝင်စားမှုတူသူများ ကွန်ရက်",
      "hm.landing.hero_title": "သင့်စိတ်ကြိုက်လူတွေကို ရှာဖွေပါ။ အတူတူ လုပ်ဆောင်ပါ။",
      "hm.landing.hero_title_accent": "သင့်စိတ်ကြိုက်လူတွေကို",
      "hm.landing.hero_sub": "HustleMeet သည် စိတ်ဝင်စားမှုတူရာ အဖွဲ့အစည်းများအတွက် Australia ၏ အဓိကနေရာဖြစ်သည်။ Software developer များ၊ မနက် ၆ နာရီတွင် အပြေးလေ့ကျင့်သူများ၊ လစဉ်ဆုံတွေ့ကြသော ရင်းနှီးမြှုပ်နှံသူများ။ လမ်းလျှောက်အဖွဲ့များ၊ ကချေသည်များ၊ နံနက်စာကလပ်များ၊ ဘုတ်ဂိမ်းညများ။ အဖွဲ့တစ်ခုကို ရွေးချယ်ပါ၊ နောက်ထပ်အစီအစဉ်ကို စာရင်းသွင်းပါ၊ ထို့နောက် တက်ရောက်လိုက်ပါ။",
      "hm.landing.cta_explore_circles": "အဖွဲ့များကို ရှာဖွေပါ",
      "hm.landing.cta_try_matchmaker": "ကိုယ်ပိုင်အဖွဲ့တစ်ခု စတင်ပါ",
      "hm.landing.trust_verified": "စိစစ်ပြီးသော အဖွဲ့ဝင်များ",
      "hm.landing.trust_venues": "စိစစ်ထားသော ပွဲစီစဉ်သူများ",
      "hm.landing.trust_friendship": "ခင်မင်မှုကိုသာ ဦးစားပေးသည် — ချိန်းတွေ့ခြင်း (dating) အတွက် မဟုတ်ပါ",
      "hm.landing.trust_moderation": "တင်းကျပ်သော စည်းကမ်းချက်များ",
      "hm.landing.subbrand_circles_kicker": "Circles",
      "hm.landing.subbrand_circles_title": "သင့်မှာ အဖွဲ့ရှိပြီးသားလား? သူတို့အတွက် အမြဲတမ်း နေရာတစ်ခု ဖန်တီးပေးပါ။",
      "hm.landing.subbrand_circles_body": "Circles သည် စာဖတ်ဝိုင်း၊ ဖဲကစားဝိုင်း၊ အပြေးအဖွဲ့၊ လုပ်ဖော်ကိုင်ဖက်များကဲ့သို့ ဆုံတွေ့လေ့ရှိသော မိတ်ဆွေအဖွဲ့များအတွက် ဖြစ်သည်။ ကျွန်ုပ်တို့က နေရာစီစဉ်ခြင်း၊ သတိပေးချက်များ ပေးခြင်းနှင့် လူစာရင်းထိန်းသိမ်းခြင်းတို့ကို လုပ်ဆောင်ပေးပါမည်။ သင်က လာရုံပါပဲ။",
      "hm.landing.subbrand_circles_b1": "နေရာသစ်များ၊ စာရင်းတစ်ခုတည်း",
      "hm.landing.subbrand_circles_b2": "အလိုအလျောက် သတိပေးချက်များ + လွယ်ကူသော RSVP",
      "hm.landing.subbrand_circles_b3": "ဂိမ်းများ၊ ဖဲကစားဝိုင်းများနှင့် အထူးညချမ်းများ အဆင်သင့်ရှိသည်",
      "hm.landing.subbrand_circles_cta": "Circles တစ်ခု စတင်ပါ",
      "hm.landing.subbrand_matchmaker_kicker": "Matchmaker",
      "hm.landing.subbrand_matchmaker_title": "AI က စီစဉ်ပေးမည်။ သင်က လာရုံပါပဲ။",
      "hm.landing.subbrand_matchmaker_body": "၁၀ မိနစ်ခန့် မေးခွန်းများ ဖြေဆိုပါ — ကိုယ်ရည်ကိုယ်သွေး၊ တန်ဖိုးထားမှုများနှင့် အားလပ်ချိန်။ ကျွန်ုပ်တို့၏ Matchmaker သည် အချိုပွဲစားချိန်တွင် မိတ်ဆွေဟောင်းများကဲ့သို့ ခံစားရစေမည့် သူစိမ်းများနှင့်အတူ သင့်ကို နေရာချပေးပါမည်။ အချိန်တိုင်း ဝိုင်းအသစ် ဖြစ်နေပါလိမ့်မည်။",
      "hm.landing.subbrand_matchmaker_b1": "Big-5 ကိုယ်ရည်ကိုယ်သွေးနှင့် တန်ဖိုးထားမှုများကို တိုင်းတာသည်",
      "hm.landing.subbrand_matchmaker_b2": "ကိုက်ညီမှုနှုန်း ၀.၇၀ နှင့်အထက် ရှိပါက ချက်ချင်း ဖိတ်ကြားမည်",
      "hm.landing.subbrand_matchmaker_b3": "သင်တက်ရောက်သည့် ညစာစားပွဲတိုင်းမှ သင်ယူလေ့လာပေးသည်",
      "hm.landing.subbrand_matchmaker_cta": "မေးခွန်းများ ဖြေဆိုပါ",
      "hm.landing.how_kicker": "HustleMeet မည်သို့ အလုပ်လုပ်သနည်း",
      "hm.landing.how_title": "အဆင့် လေးဆင့်။ သင့်အဖွဲ့ကို ရှာဖွေပါ။ တက်ရောက်ပါ။",
      "hm.landing.how_step1_ttl": "သင့်စိတ်ဝင်စားမှုများကို ရွေးချယ်ပါ",
      "hm.landing.how_step1_sub": "အပြေး၊ ကုဒ်ရေးခြင်း၊ ရင်းနှီးမြှုပ်နှံခြင်း၊ ချက်ပြုတ်ခြင်း၊ ဂီတ၊ လမ်းလျှောက်ခြင်း — သင် ဘာကို စိတ်ဝင်စားသလဲနှင့် ဘယ်မှာ နေထိုင်သလဲဆိုတာ ပြောပြပါ။",
      "hm.landing.how_step2_ttl": "သင့်အနီးအနားရှိ အဖွဲ့များကို ရှာဖွေပါ",
      "hm.landing.how_step2_sub": "အဖွဲ့တိုင်းသည် ၎င်းတို့၏ အိမ်ရှင်၊ ပွဲပုံစံနှင့် နောက်ထပ်အစီအစဉ်တို့ကို ဖော်ပြထားပါသည်။ မတက်ရောက်မီ အခြေအနေကို လေ့လာနိုင်ပါသည်။",
      "hm.landing.how_step3_ttl": "နောက်ထပ်အစီအစဉ်အတွက် RSVP လုပ်ပါ",
      "hm.landing.how_step3_sub": "တစ်ချက်နှိပ်ရုံဖြင့် ပြက္ခဒိန်တွင် မှတ်သားနိုင်ပြီး ပွဲမတိုင်မီ တစ်ရက်အလိုတွင် သတိပေးပါမည်။ အချို့ပွဲများမှာ အခမဲ့ဖြစ်ပြီး အချို့မှာ အခပေးရပါသည် — ဈေးနှုန်းကို ရှင်းလင်းစွာ ဖော်ပြထားပါသည်။",
      "hm.landing.how_step4_ttl": "Show up & connect",
      "hm.landing.how_step4_sub": "အိမ်ရှင်နှင့် တွေ့ဆုံပါ၊ အဖွဲ့သားများနှင့် ဆုံတွေ့ပါ၊ ပျော်ရွှင်စွာ ဆင်နွှဲပါ။ အဆင်ပြေပါက နောက်အပတ်တွင် ထပ်မံလာရောက်ပါ။",
      "hm.landing.stats_kicker": "ဆုံတွေ့ရန် Australia ၏ နည်းလမ်းသစ်",
      "hm.landing.stats_title": "လူတွေ ဘာကြောင့် ထပ်ခါတလဲလဲ လာကြသလဲ။",
      "hm.landing.stats_92_sub": "ပွဲတွင် အနည်းဆုံး လူတစ်ဦးနှင့် ရင်းနှီးသွားသည်ဟု ခံစားရသည်",
      "hm.landing.stats_68_sub": "တစ်ပတ်အတွင်း နောက်ထပ်အစီအစဉ်အတွက် RSVP လုပ်ကြသည်",
      "hm.landing.stats_85_sub": "အဖွဲ့ထဲမှ တစ်စုံတစ်ဦးနှင့် ဆက်သွယ်မှု ရှိနေကြသည်",
      "hm.landing.why_kicker": "ဘာကြောင့် အလုပ်ဖြစ်သလဲ",
      "hm.landing.why_title": "စစ်မှန်သော စိတ်ဝင်စားမှု။ အိမ်ရှင်။ အစီအစဉ်များ။",
      "hm.landing.why_1_ttl": "တည်နေရာထက် စိတ်ဝင်စားမှုကို ဦးစားပေးသည်",
      "hm.landing.why_1_body": "သင့်အဖွဲ့ရှိ လူများသည် သင်စိတ်ဝင်စားသည့်အရာကို အမှန်တကယ် စိတ်ဝင်စားကြသူများ ဖြစ်သည် — ၎င်းသည် စစ်မှန်သော ခင်မင်မှုအတွက် အတိုဆုံးလမ်းစဖြစ်သည်။",
      "hm.landing.why_2_ttl": "တာဝန်ယူမှုရှိသော ပွဲစီစဉ်သူများ",
      "hm.landing.why_2_body": "အဖွဲ့တိုင်းတွင် စိစစ်ထားသော အိမ်ရှင်ရှိပြီး ၎င်းတို့က အစီအစဉ်များကို ထိန်းသိမ်းပေးပါသည်။ Facebook admin များ မဟုတ်ပါ။ လူသားစစ်စစ်များ ဖြစ်ပါသည်။",
      "hm.landing.why_3_ttl": "အမြဲမပြတ်ရှိနေခြင်းက အရေးကြီးသည်",
      "hm.landing.why_3_body": "အပတ်စဉ် အစီအစဉ်သစ်များ ရှိသည်။ မျှော်လင့်စရာ တစ်ခုခု။ သူစိမ်းများကို သင်၏ အပတ်စဉ် မိတ်ဆွေများအဖြစ် ပြောင်းလဲပေးမည့် ပုံမှန်အစီအစဉ်။",
      "hm.landing.why_4_ttl": "လုံခြုံစိတ်ချရမှု",
      "hm.landing.why_4_body": "စိစစ်ပြီးသော အဖွဲ့ဝင်များ၊ လူအများရှေ့ရှိ နေရာများ၊ မလေးစားမှုများအပေါ် တင်းကျပ်စွာ အရေးယူခြင်း။ ပွဲမတိုင်မီ၊ ပွဲအတွင်းနှင့် ပွဲအပြီးတွင် ကျွန်ုပ်တို့၏ အကူအညီပေးရေးအဖွဲ့ ရှိနေပါသည်။",
      "hm.landing.why_5_ttl": "ခင်မင်မှုကိုသာ ဦးစားပေးသည်",
      "hm.landing.why_5_body": "ဒါက ချိန်းတွေ့ခြင်း မဟုတ်ပါ။ လူမှုအသိုင်းအဝိုင်း ဖြစ်သည်။ စစ်မှန်သော စိတ်ဝင်စားမှု၊ စစ်မှန်သော လူသားများ၊ ကြားထဲတွင် ရွေးချယ်ရသည့် ပင်ပန်းမှုများ မရှိပါ။",
      "hm.landing.why_6_ttl": "အရွယ်အစား အနေတော်ဖြစ်ခြင်း",
      "hm.landing.why_6_body": "အဖွဲ့အများစုသည် ၄-၁၅ ယောက်သာ ကန့်သတ်ထားသည်။ စကားပြောဆိုရန် အနေတော် ဖြစ်သည်။ မည်သူမျှ ဘေးရောက်မသွားစေရန် အဖွဲ့ငယ်လေးများ စီစဉ်ထားခြင်း ဖြစ်သည်။",
      "hm.landing.cities_kicker": "ကျွန်ုပ်တို့ ရှိနေသည့် နေရာများ",
      "hm.landing.cities_title": "Australia နှင့် NZ တစ်ဝှမ်းရှိ အဖွဲ့များ။",
      "hm.landing.cities_sub_zero": "မြို့ပေါင်း ၁၅ မြို့ကျော်တွင် ပထမဆုံးအဖွဲ့များ စတင်နေပါပြီ — စောစောစီးစီး ပါဝင်လိုက်ပါ။",
      "hm.landing.cities_sub_some": "မြို့ကြီး ၁၅ မြို့ကျော်တွင် အဖွဲ့များရှိနေပါပြီ။ သင့်မြို့တွင်လည်း ယခုအပတ်အတွင်း တစ်ခုခု ရှိနေနိုင်ပါသည်။",
      "hm.landing.cities_sub_one": "{{count}} အဖွဲ့ ရှိသည် · အပတ်စဉ် အစီအစဉ်သစ်များ · မြို့အသစ်များ အမြဲထပ်တိုးနေသည်။",
      "hm.landing.cities_sub_many": "{{count}} အဖွဲ့များ ရှိသည် · အပတ်စဉ် အစီအစဉ်သစ်များ · မြို့အသစ်များ အမြဲထပ်တိုးနေသည်။",
      "hm.landing.forme_kicker": "ဒါဟာ သင့်အတွက်ပါ…",
      "hm.landing.forme_title": "စစ်မှန်သော စိတ်ဝင်စားမှု၊ လူသားများ၊ အစီအစဉ်များကို သင် အလိုရှိသည်။",
      "hm.landing.forme_1": "သင်သည် app များပေါ်တွင် ရွေးချယ်ရသည့် ဒုက္ခများမှ လွတ်ကင်းလိုပြီး သင်ဝါသနာပါရာ အရာများဖြင့် လူများနှင့် အမှန်တကယ် ဆုံတွေ့လိုသည်။",
      "hm.landing.forme_2": "သင်သည် မြို့သစ်သို့ ပြောင်းရွှေ့လာသူဖြစ်ပြီး chat group များထက် နွေးထွေးသော မိတ်ဆွေအဖွဲ့ကို လိုအပ်နေသည်။",
      "hm.landing.forme_3": "သင်သည် အရေအတွက်ထက် အရည်အသွေးကို တန်ဖိုးထားသည် — ကောင်းမွန်သော အဖွဲ့တစ်ခုသည် follower ၅၀ ထက် ပိုတန်ဖိုးရှိသည်။",
      "hm.landing.forme_4": "တူညီသော စိတ်ဝင်စားမှုနှင့် လူကိုယ်တိုင် တွေ့ဆုံချိန် ၂ နာရီသည် အရင်းနှီးဆုံးသော နည်းလမ်းဖြစ်သည်ဟု သင်ယုံကြည်သည်။",
      "hm.landing.notme_kicker": "ဒါဟာ သင့်အတွက် မဟုတ်ပါ…",
      "hm.landing.notme_title": "HustleMeet သည် လူတိုင်းအတွက် မဟုတ်ပါ။",
      "hm.landing.notme_1": "သင်က တက်ရောက်ဖို့ ကတိမတည်နိုင်ဘူးဆိုရင် — အိမ်ရှင်များနှင့် အဖွဲ့ဝင်များသည် သင်၏ RSVP အပေါ်တွင် မူတည်နေပါသည်။",
      "hm.landing.notme_2": "သင်က dating service ရှာနေတာဆိုရင် — ဤနေရာတွင် ခင်မင်မှုက ပထမပါ။",
      "hm.landing.notme_3": "လူကိုယ်တိုင် မတွေ့ဘဲ အွန်လိုင်းကနေပဲ လုပ်ချင်တယ်ဆိုရင် — ဒါက လူကိုယ်တိုင်တွေ့မှ အဆင်ပြေမှာပါ။",
      "hm.landing.notme_4": "အခမဲ့ပဲ အမြဲလိုချင်တယ်ဆိုရင် — အချို့ပွဲများက အခပေးရပါမည်။ ဈေးနှုန်းကို အမြဲတမ်း ကြိုတင်အသိပေးထားပါသည်။",
      "hm.landing.safety_kicker": "ကျွန်ုပ်တို့၏ လုံခြုံရေး ကတိကဝတ်",
      "hm.landing.safety_title": "နွေးထွေးသော်လည်း မပေါ့ဆပါ။",
      "hm.landing.safety_1_ttl": "လူအများရှေ့ရှိ နေရာများ",
      "hm.landing.safety_1_body": "အစီအစဉ်တိုင်းကို ကော်ဖီဆိုင်၊ စားသောက်ဆိုင်၊ ပန်းခြံ၊ အားကစားရုံကဲ့သို့သော လူအများရှေ့ရှိ နေရာများတွင်သာ ပြုလုပ်ပါသည်။ တက်ရောက်သူအားလုံး သဘောတူမှသာ နေအိမ်များတွင် ပြုလုပ်ပါမည်။",
      "hm.landing.safety_2_ttl": "လူမှုအသိုင်းအဝိုင်း စံနှုန်းများ",
      "hm.landing.safety_2_body": "မလေးစားမှု၊ နှောင့်ယှက်မှု သို့မဟုတ် မရိုးသားမှုများကို လုံးဝခွင့်မပြုပါ။ စည်းကမ်းဖောက်ဖျက်သူများကို ချက်ချင်း ဖယ်ရှားပါမည်။",
      "hm.landing.safety_3_ttl": "ကိုယ်ရေးအချက်အလက် ကာကွယ်မှု",
      "hm.landing.safety_3_body": "သင့်အချက်အလက်များကို လူအများထံ မမျှဝေပါ။ ပွဲအပြီးတွင် မည်သူနှင့် ဆက်သွယ်မည်ကို သင်ကိုယ်တိုင် ဆုံးဖြတ်နိုင်ပါသည်။",
      "hm.landing.safety_4_ttl": "အကူအညီပေးရေးအဖွဲ့",
      "hm.landing.safety_4_body": "သင့်တွင် မေးခွန်းများ သို့မဟုတ် စိုးရိမ်မှုများရှိပါက ကျွန်ုပ်တို့အဖွဲ့သည် အချိန်မရွေး အကူအညီပေးရန် အဆင်သင့်ရှိပါသည်။",
      "hm.landing.bottom_title": "သင့်နောက်ထပ် စိတ်ကြိုက်အဖွဲ့သည် တစ်ချက်နှိပ်ရုံသာ လိုတော့သည်။",
      "hm.landing.bottom_sub": "သင့်လမ်းကြောင်းကို ရွေးချယ်ပါ — ရှိပြီးသားအဖွဲ့တွင် ပါဝင်ပါ၊ သင့်သူငယ်ချင်းများကို Circle သို့ ခေါ်လာပါ သို့မဟုတ် Matchmaker က သင့်အတွက် နေရာစီစဉ်ပေးပါစေ။",
      "hm.landing.bottom_cta_circles": "Start a Circle",
      "hm.landing.bottom_cta_mm": "Take the Matchmaker quiz",
      "hm.circles.page_title": "HustleMeet Circles · သင့်သူငယ်ချင်းအဖွဲ့၊ အချိန်တိုင်း နေရာသစ်",
      "hm.circles.kicker": "HustleMeet · Circles",
      "hm.circles.hero_title": "သင့်သူငယ်ချင်းအဖွဲ့။ အချိန်တိုင်း နေရာသစ်။",
      "hm.circles.hero_title_accent": "သင့်သူငယ်ချင်းအဖွဲ့",
      "hm.circles.hero_sub": "မိတ်ဆွေတွေ ရှိပြီးသားလား? Circles သည် သူတို့နှင့် အမြဲရင်းနှီးနေစေရန် အလွယ်ကူဆုံး နည်းလမ်းဖြစ်သည်။ ကျွန်ုပ်တို့က နေရာစီစဉ်ပေးခြင်း၊ ဂိမ်းများ ပြင်ဆင်ပေးခြင်းတို့ကို လုပ်ဆောင်ပေးပါမည်။ သင်က လာရုံပါပဲ — အဖွဲ့ဟောင်း၊ ညသစ်၊ နေရာသစ်။",
      "hm.circles.cta_find": "Circles တစ်ခု စတင်ပါ",
      "hm.circles.cta_host_hero": "ဖွင့်လှစ်ထားသော Circle တွင် ပါဝင်ပါ",
      "hm.circles.trust_venue": "အလှည့်ကျ စိစစ်ထားသော နေရာများ",
      "hm.circles.trust_party": "Circle တစ်ခုလျှင် မိတ်ဆွေ ၄-၁၂ ယောက်",
      "hm.circles.trust_verified": "စိစစ်ပြီးသော အဖွဲ့ဝင်များ",
      "hm.circles.trust_friendship": "စစ်မှန်သော ခင်မင်မှုကို ထိန်းသိမ်းပါ",
      "hm.circles.how_kicker": "မည်သို့ အလုပ်လုပ်သနည်း",
      "hm.circles.how_title": "အဆင့် လေးဆင့်။ သင့်အဖွဲ့ကို အမြဲရှင်သန်စေပါ။",
      "hm.circles.how_step1_ttl": "သင့် Circle ကို စတင်ပါ",
      "hm.circles.how_step1_sub": "အမည်ပေးပါ၊ သင့်မိတ်ဆွေများကို ဖိတ်ခေါ်ပါ (၁၂ ယောက်အထိ)၊ ထို့နောက် အပတ်စဉ်၊ နှစ်ပတ်တစ်ကြိမ် သို့မဟုတ် လစဉ် ရွေးချယ်ပါ။",
      "hm.circles.how_step2_ttl": "ကျွန်ုပ်တို့က နေရာရွေးချယ်ပေးပါမည်",
      "hm.circles.how_step2_sub": "အစီအစဉ်တိုင်းအတွက် နေရာသစ်များ — စားသောက်ဆိုင်၊ ဂိမ်းည၊ ဝိုင်ဘား စသည်ဖြင့် အကြံပြုပါမည်။ သို့မဟုတ် စိတ်ကြိုက်နေရာကို သတ်မှတ်ပါ။",
      "hm.circles.how_step3_ttl": "လူတိုင်း တစ်ချက်နှိပ်ရုံဖြင့် RSVP လုပ်နိုင်သည်",
      "hm.circles.how_step3_sub": "အလိုအလျောက် သတိပေးချက်များ၊ ကုန်ကျစရိတ်ခွဲဝေမှု၊ ကြိုတင်ပေးချေမှုစနစ်တို့ကြောင့် အစီအစဉ် ပျက်ကွက်မှု မရှိစေရပါ။ အစားအစာ မရောက်ခင်ကတည်းက ငွေရှင်းမှု ပြီးစီးနေပါမည်။",
      "hm.circles.how_step4_ttl": "ပုံမှန်အစီအစဉ်ကို ထိန်းသိမ်းပါ",
      "hm.circles.how_step4_sub": "ဘယ်တော့မှ စီစဉ်စရာမလိုတော့တဲ့ ပုံမှန်ညချမ်းလေး တစ်ခု။ အချိန်ကြာလာသည်နှင့်အမျှ အဖွဲ့သားများ ပိုမိုရင်းနှီးလာပါလိမ့်မည်။",
      "hm.circles.cadence_weekly_ttl": "အပတ်စဉ်",
      "hm.circles.cadence_weekly_sub": "အပတ်တိုင်း",
      "hm.circles.cadence_weekly_body": "အင်္ဂါနေ့ညတိုင်း ဆုံတွေ့ချင်သည့် အဖွဲ့များအတွက် — လူဟောင်းများ၊ နေရာသစ်များ။",
      "hm.circles.cadence_fortnight_ttl": "နှစ်ပတ်တစ်ကြိမ်",
      "hm.circles.cadence_fortnight_sub": "၂ ပတ်လျှင် တစ်ကြိမ်",
      "hm.circles.cadence_fortnight_body": "အလုပ်များသူများအတွက် ပုံမှန်လည်းဖြစ်၊ ဝန်လည်းမပိစေမည့် အနေတော် အချိန်။",
      "hm.circles.cadence_monthly_ttl": "လစဉ်",
      "hm.circles.cadence_monthly_sub": "လတိုင်း",
      "hm.circles.cadence_monthly_body": "အထူးအစီအစဉ်များအတွက် — တနင်္ဂနွေနေ့ ညစာစားပွဲများ၊ ဂိမ်းညကြီးများ သို့မဟုတ် ဖဲကစားဝိုင်းများ။",
      "hm.circles.feast_kicker": "သင့်ညချမ်းကို ရွေးချယ်ပါ",
      "hm.circles.feast_title": "သုံးဆောင်နိုင်မည့် ပုံစံသုံးမျိုး",
      "hm.circles.feast_sub": "Circle တိုင်းသည် ညချမ်းကို မည်သို့ ကုန်ဆုံးမည်ကို ရွေးချယ်နိုင်သည်။ အလှည့်ကျ ပြောင်းလဲနိုင်သည် သို့မဟုတ် တစ်ခုတည်းကိုပဲ ပုံသေထားနိုင်သည်။",
      "hm.circles.feast_surprise_ttl": "စားသောက်ဆိုင်အသစ်ကို စမ်းကြည့်ပါ",
      "hm.circles.feast_surprise_body": "သင့်အနီးအနားရှိ နေရာ ၃ ခုကို ရွေးချယ်ပေးမည်၊ လူတိုင်း မဲပေးမည်၊ ဆိုင်က ကြိုတင်ပြင်ဆင်ပေးပါမည်။ ကုန်ကျစရိတ်ခွဲဝေပြီး ကြိုတင်ပေးချေနိုင်ပါသည်။",
      "hm.circles.feast_preselect_ttl": "ဂိမ်းများ / ဖဲကစားခြင်း",
      "hm.circles.feast_preselect_body": "ဂိမ်းည၊ ဖဲကစားဝိုင်း သို့မဟုတ် ဘိုလင်းကစားခြင်းတို့ကို ကြိုတင်မှာယူပါ။ နေရာ၊ ဝင်ကြေးနှင့် သတိပေးချက်များကို ကျွန်ုပ်တို့ ကိုင်တွယ်ပေးပါမည်။",
      "hm.circles.feast_per_event_ttl": "အထူးအစီအစဉ် ညချမ်းများ",
      "hm.circles.feast_per_event_body": "ဝိုင်မြည်းစမ်းခြင်း၊ စားဖိုမှူး၏ အထူးဟင်းလျာများ၊ ကော့တေးသင်တန်းများ။ တစ်ခါတစ်ရံတွင် ထူးခြားသောအရာများ — အားလုံးကို စနစ်တကျ စီစဉ်ပေးပါမည်။",
      "hm.circles.live_kicker": "ယခုလက်ရှိ",
      "hm.circles.live_title": "သင်ပါဝင်နိုင်သော Circles များ",
      "hm.circles.live_host_link": "ကိုယ်ပိုင်တစ်ခု စတင်ပါ",
      "hm.circles.live_loading": "လုပ်ဆောင်နေပါသည်…",
      "hm.circles.live_empty_title": "သင့်မြို့တွင် ပထမဆုံး Circle စတင်သူ ဖြစ်လိုက်ပါ။",
      "hm.circles.live_empty_sub": "မိတ်ဆွေ ၃ ယောက်နှင့် စတင်ပါ၊ ကျန်ရှိသူများကို ဖိတ်ခေါ်ရန် ကျွန်ုပ်တို့ ကူညီပါမည် — ပထမဆုံး နေရာ ၃ ခုအတွက်လည်း ကျွန်ုပ်တို့က စီစဉ်ပေးပါမည်။",
      "hm.circles.live_empty_cta": "Circle တစ်ခု စတင်ပါ",
      "hm.circles.host_kicker": "Circle ခေါင်းဆောင်များအတွက်",
      "hm.circles.host_title": "သင်က လူခေါ်လာပါ၊ ကျန်တာ ကျွန်ုပ်တို့ လုပ်ပါမည်။",
      "hm.circles.host_body": "သင်ဟာ အမြဲတမ်း ပွဲစီစဉ်ပေးရတဲ့သူဆိုရင် Circles က သင့်အချိန်တွေကို သက်သာစေပါလိမ့်မယ်။ အလိုအလျောက် သတိပေးချက်များ၊ ကြိုတင်ငွေပေးချေမှု၊ နေရာရွေးချယ်မှု၊ ကုန်ကျစရိတ်ခွဲဝေမှုနှင့် cashback စနစ်တို့ ပါဝင်ပါသည်။ သင်က အဖွဲ့ဝင်တစ်ယောက်လိုပဲ လာရုံပါပဲ။",
      "hm.circles.host_perk_1": "အလိုအလျောက် နေရာရွေးချယ်မှု — Google မှာ ထပ်ရှာစရာမလိုတော့ပါ",
      "hm.circles.host_perk_2": "ကြိုတင်ပေးချေထားသော နေရာများ — လာမယ့်သူ မရှိမှာ စိတ်ပူစရာမလို",
      "hm.circles.host_perk_3": "အချိုပွဲ မစားခင် ငွေရှင်းပြီးသား ဖြစ်နေမည်",
      "hm.circles.host_perk_4": "သတိပေးချက်များနှင့် ပြက္ခဒိန်ဖိတ်ကြားမှုများ အလိုအလျောက် လုပ်ဆောင်ပေးသည်",
      "hm.circles.host_perk_5": "တစ်ယောက်ယောက် ပျက်ကွက်ပါက စောင့်ဆိုင်းစာရင်းမှ အလိုအလျောက် အစားထိုးသည်",
      "hm.circles.host_perk_6": "ညတိုင်းအတွက် Cashback ရရှိမည့် အိတ်ကပ်",
      "hm.circles.host_cta": "ယခုပဲ Circle တစ်ခု စတင်ပါ",
      "hm.circles.why_kicker": "ဘာကြောင့် Circles အလုပ်ဖြစ်သလဲ",
      "hm.circles.why_title": "အရွယ်ရောက်ပြီးသူများ၏ ခင်မင်မှုအတွက် အခြေခံအဆောက်အအုံ လိုအပ်သည်",
      "hm.circles.why_1_ttl": "လူဟောင်းများ၊ ပိုမိုခိုင်မာသော ယုံကြည်မှု",
      "hm.circles.why_1_body": "ဘဝက သူငယ်ချင်းတွေကို ဝေးကွာစေတတ်သည်။ Circles သည် လူတိုင်းကို နီးစပ်စေမည့် ပုံမှန်အစီအစဉ်တစ်ခုဖြစ်သည် — ၄-၁၂ ယောက်သော မိတ်ဆွေများ၊ တစ်ပတ်တည်းတွင် ဆုံတွေ့ခြင်း။",
      "hm.circles.why_2_ttl": "စီစဉ်ရသည့် ပင်ပန်းမှု လုံးဝမရှိ",
      "hm.circles.why_2_body": "WhatsApp အဖွဲ့ထဲမှာ ဘယ်သူမှ ဦးဆောင်နေစရာ မလိုပါ။ ကျွန်ုပ်တို့က နေရာရွေးခြင်း၊ သတိပေးခြင်းနှင့် ငွေပေးချေခြင်းတို့ကို အားလုံးလုပ်ဆောင်ပေးပါမည်။",
      "hm.circles.why_3_ttl": "နေရာသစ်၊ အမှတ်တရသစ်",
      "hm.circles.why_3_body": "Australia ၏ အကောင်းဆုံး စားသောက်ဆိုင်များ၊ ဂိမ်းဆိုင်များကို အလှည့်ကျ သွားရောက်ပါ။ ရင်းနှီးသော မျက်နှာများ၊ ဆန်းသစ်သော ပတ်ဝန်းကျင် — ၎င်းသည် ထူးခြားသော ခံစားမှုကို ပေးပါလိမ့်မည်။",
      "hm.circles.bottom_title": "ဘယ်သူမှ စီစဉ်စရာမလိုဘဲ သင့်အဖွဲ့သားတွေ မျှော်လင့်နေမယ့် ညချမ်းလေး။",
      "hm.circles.bottom_sub": "Circle တစ်ခု။ မိတ်ဆွေဟောင်းများ။ အချိန်တိုင်း နေရာသစ်။",
      "hm.circles.bottom_cta_find": "Circle တစ်ခု စတင်ပါ",
      "hm.circles.bottom_cta_mm": "Matchmaker ဖြင့် လူသစ်များနှင့် တွေ့ဆုံပါ →",
      "hm.circles.card_host_pill": "Circle",
      "hm.matchmaker.page_title": "HustleMeet Matchmaker · AI က သင့်တော်သည့်ဝိုင်းကို ရွေးချယ်ပေးပါမည်",
      "hm.matchmaker.kicker": "HustleMeet · Matchmaker",
      "hm.matchmaker.hero_title": "AI က စီစဉ်ပေးမည်။ သင်က လာရုံပါပဲ။",
      "hm.matchmaker.hero_title_accent": "သင်က လာရုံပါပဲ။",
      "hm.matchmaker.hero_sub": "ကိုယ်ရည်ကိုယ်သွေးနှင့် တန်ဖိုးထားမှုဆိုင်ရာ မေးခွန်းတိုလေးများကို ဖြေဆိုပါ။ Matchmaker သည် အချိုပွဲစားချိန်တွင် မိတ်ဆွေဟောင်းများကဲ့သို့ ရင်းနှီးသွားစေမည့် သူစိမ်း ၄ ယောက်မှ ၇ ယောက်နှင့်အတူ သင့်ကို နေရာချပေးပါမည်။ ရွေးချယ်ရသည့် ပင်ပန်းမှုမရှိ၊ အပေါ်ယံစကားများ ပြောစရာမလို။ သင်အဆင်သင့်ဖြစ်သည့်အချိန်တွင် ဝိုင်းတစ်ခုက အဆင်သင့်ရှိနေပါမည်။",
      "hm.matchmaker.cta_quiz": "ကိုက်ညီမှုစစ်ဆေးရန် မေးခွန်းများ ဖြေဆိုပါ",
      "hm.matchmaker.cta_see_circles": "See Circles instead →",
      "hm.matchmaker.trust_big5": "Big-5 personality scored",
      "hm.matchmaker.trust_seats": "4-8 seats per table",
      "hm.matchmaker.trust_lock": "Zero-swipe · zero-photo",
      "hm.matchmaker.trust_verified": "Verified members only",
      "hm.matchmaker.how_kicker": "How it works",
      "hm.matchmaker.how_title": "Four steps to a table that already fits.",
      "hm.matchmaker.how_step1_ttl": "Take the quiz",
      "hm.matchmaker.how_step1_sub": "Ten-minute questionnaire — personality, values, dietary, availability. You do it once.",
      "hm.matchmaker.how_step2_ttl": "Overnight matching",
      "hm.matchmaker.how_step2_sub": "Our AI reads your profile and Australia's live dinner slate. When your fit score is 0.70 or higher, it drafts a personal invite.",
      "hm.matchmaker.how_step3_ttl": "Accept or decline",
      "hm.matchmaker.how_step3_sub": "You get one clean invite email with the venue, the table theme, and the three interests you share with the group.",
      "hm.matchmaker.how_step4_ttl": "It learns from you",
      "hm.matchmaker.how_step4_sub": "Every post-dinner survey tunes the model. Your next match gets smarter the more you dine.",
      "hm.matchmaker.science_kicker": "Why it works",
      "hm.matchmaker.science_title": "The Matchmaker doesn't guess — it scores.",
      "hm.matchmaker.science_sub": "Every match is a weighted composite of four factors. When the whole table averages above 0.70, we send the invites. Anything below sits in the queue.",
      "hm.matchmaker.science_1_ttl": "Personality (35%)",
      "hm.matchmaker.science_1_body": "Cosine similarity across the Big-5 personality vector — openness, conscientiousness, extraversion, agreeableness, and emotional stability.",
      "hm.matchmaker.science_2_ttl": "Values (25%)",
      "hm.matchmaker.science_2_body": "Overlap on the values that make a dinner conversation actually land — family, curiosity, growth, service, adventure.",
      "hm.matchmaker.science_3_ttl": "Interests (25%)",
      "hm.matchmaker.science_3_body": "Jaccard overlap on your specific interests — hiking Six Foot Track, Sydney film club, Melbourne coffee scene, your third book of the year.",
      "hm.matchmaker.science_4_ttl": "Availability (15%)",
      "hm.matchmaker.science_4_body": "The night you can actually make. No 7pm Tuesday invite if your calendar says gym-Tuesday-forever.",
      "hm.matchmaker.safety_kicker": "Safety by design",
      "hm.matchmaker.safety_title": "The Matchmaker is quietly cautious.",
      "hm.matchmaker.safety_1_ttl": "Confidence threshold",
      "hm.matchmaker.safety_1_body": "Below a 0.70 group score, no invite goes out. A shaky match beats no dinner — until it doesn't.",
      "hm.matchmaker.safety_2_ttl": "Mutual-no blocklist",
      "hm.matchmaker.safety_2_body": "If two members privately signal 'no' after a dinner, we never seat them together again.",
      "hm.matchmaker.safety_3_ttl": "Auto-kill switch",
      "hm.matchmaker.safety_3_body": "If 20% or more of recent surveys rate a table 2/5 or lower, invites pause for 24h while an operator reviews.",
      "hm.matchmaker.safety_4_ttl": "Community standards",
      "hm.matchmaker.safety_4_body": "Every member accepts a plain-English code of conduct. We remove people who breach it — no appeals.",
      "hm.matchmaker.safety_5_ttl": "Public venues only",
      "hm.matchmaker.safety_5_body": "Every Matchmaker dinner runs at a real restaurant with staff. Never anyone's home.",
      "hm.matchmaker.safety_6_ttl": "Post-event feedback",
      "hm.matchmaker.safety_6_body": "Your private survey shapes future matches, and can trigger a mod review of the venue or a peer.",
      "hm.matchmaker.loop_kicker": "The learning loop",
      "hm.matchmaker.loop_title": "Every dinner makes your next dinner better.",
      "hm.matchmaker.loop_body": "The Matchmaker isn't static. After each event you submit a short private survey — how the conversation felt, who you'd dine with again, whether the venue delivered. Those signals feed back into the model and reshape every future match.",
      "hm.matchmaker.loop_1": "Peer \"dine again\" votes strengthen your pairings",
      "hm.matchmaker.loop_2": "Mutual-no votes silently retire pairs",
      "hm.matchmaker.loop_3": "Venue & conversation ratings tune the seat mix",
      "hm.matchmaker.loop_4": "Reliability score protects members from repeat no-shows",
      "hm.matchmaker.loop_5": "Learning bias reinforces good matches month over month",
      "hm.matchmaker.loop_6": "You stay in control — opt-out or reset your profile anytime",
      "hm.matchmaker.vs_kicker": "Which is right for me?",
      "hm.matchmaker.vs_title": "Matchmaker or Circles?",
      "hm.matchmaker.vs_mm_ttl": "Pick me if I want variety.",
      "hm.matchmaker.vs_mm_1": "A different table, city, cuisine every time",
      "hm.matchmaker.vs_mm_2": "AI-curated group of 4-8 compatible strangers",
      "hm.matchmaker.vs_mm_3": "Weekly rhythm, no host commitment",
      "hm.matchmaker.vs_mm_4": "Great if you're new to a city or travelling",
      "hm.matchmaker.vs_mm_cta": "Take the matching quiz",
      "hm.matchmaker.vs_circles_ttl": "Pick me if I want a regular.",
      "hm.matchmaker.vs_circles_1": "Same host · same venue · same weeknight",
      "hm.matchmaker.vs_circles_2": "A group that turns into your Wednesday tradition",
      "hm.matchmaker.vs_circles_3": "Human-curated by an experienced host",
      "hm.matchmaker.vs_circles_4": "Great if you've just moved and want roots",
      "hm.matchmaker.vs_circles_cta": "Browse Circles",
      "hm.matchmaker.bottom_title": "One quiz. Every dinner after that already fits.",
      "hm.matchmaker.bottom_sub": "You'll get matched inside a fortnight. It's the easiest \"yes\" you'll make this month.",
      "hm.matchmaker.bottom_cta": "Take the matching quiz",
      "hm.matchmaker.bottom_note": "No credit card · 10 minutes · Unmatch anytime",
      /* AUTO-TRANSLATED hm.* END */
    },
  },
};

// ─── Supported languages (30) ─────────────────────────────────────
// Each shows the language picker. Languages WITHOUT a full RESOURCES
// translation fall back to English UI strings BUT dynamic content
// (Plaza products, Hustle Voice replies, AI-translated emails) is
// auto-translated via Claude in the backend — `plaza_translate.py` +
// `sales_translate.py` populate per-document `*_i18n` maps.
//
// Adding a UI translation later is purely a RESOURCES object addition;
// no app change, no Apple/Google review, ships OTA.
export const SUPPORTED_LANGUAGES = [
  { code: "en",    label: "English",            native: "English",       flag: "🇦🇺", voice: "en-AU" },
  { code: "es",    label: "Spanish",            native: "Español",       flag: "🇪🇸", voice: "es-ES" },
  { code: "fr",    label: "French",             native: "Français",      flag: "🇫🇷", voice: "fr-FR" },
  { code: "it",    label: "Italian",            native: "Italiano",      flag: "🇮🇹", voice: "it-IT" },
  { code: "de",    label: "German",             native: "Deutsch",       flag: "🇩🇪", voice: "de-DE" },
  { code: "pt",    label: "Portuguese",         native: "Português",     flag: "🇧🇷", voice: "pt-BR" },
  { code: "nl",    label: "Dutch",              native: "Nederlands",    flag: "🇳🇱", voice: "nl-NL" },
  { code: "pl",    label: "Polish",             native: "Polski",        flag: "🇵🇱", voice: "pl-PL" },
  { code: "ru",    label: "Russian",            native: "Русский",       flag: "🇷🇺", voice: "ru-RU" },
  { code: "tr",    label: "Turkish",            native: "Türkçe",        flag: "🇹🇷", voice: "tr-TR" },
  { code: "el",    label: "Greek",              native: "Ελληνικά",      flag: "🇬🇷", voice: "el-GR" },
  { code: "ar",    label: "Arabic",             native: "العربية",         flag: "🇸🇦", voice: "ar-SA" },
  { code: "he",    label: "Hebrew",             native: "עברית",         flag: "🇮🇱", voice: "he-IL" },
  { code: "fa",    label: "Persian (Farsi)",    native: "فارسی",          flag: "🇮🇷", voice: "fa-IR" },
  { code: "ur",    label: "Urdu",               native: "اردو",          flag: "🇵🇰", voice: "ur-PK" },
  { code: "hi",    label: "Hindi",              native: "हिन्दी",          flag: "🇮🇳", voice: "hi-IN" },
  { code: "pa",    label: "Punjabi",            native: "ਪੰਜਾਬੀ",          flag: "🇮🇳", voice: "pa-IN" },
  { code: "bn",    label: "Bengali",            native: "বাংলা",          flag: "🇧🇩", voice: "bn-IN" },
  { code: "ta",    label: "Tamil",              native: "தமிழ்",          flag: "🇮🇳", voice: "ta-IN" },
  { code: "te",    label: "Telugu",             native: "తెలుగు",          flag: "🇮🇳", voice: "te-IN" },
  { code: "zh-CN", label: "Chinese (Simplified)", native: "简体中文",      flag: "🇨🇳", voice: "zh-CN" },
  { code: "zh-HK", label: "Chinese (Cantonese)", native: "粵語",          flag: "🇭🇰", voice: "zh-HK" },
  { code: "ja",    label: "Japanese",           native: "日本語",         flag: "🇯🇵", voice: "ja-JP" },
  { code: "ko",    label: "Korean",             native: "한국어",         flag: "🇰🇷", voice: "ko-KR" },
  { code: "vi",    label: "Vietnamese",         native: "Tiếng Việt",    flag: "🇻🇳", voice: "vi-VN" },
  { code: "th",    label: "Thai",               native: "ไทย",           flag: "🇹🇭", voice: "th-TH" },
  { code: "id",    label: "Indonesian",         native: "Bahasa Indonesia", flag: "🇮🇩", voice: "id-ID" },
  { code: "ms",    label: "Malay",              native: "Bahasa Melayu", flag: "🇲🇾", voice: "ms-MY" },
  { code: "tl",    label: "Tagalog (Filipino)", native: "Filipino",      flag: "🇵🇭", voice: "fil-PH" },
  { code: "my",    label: "Burmese",            native: "မြန်မာ",         flag: "🇲🇲", voice: "my-MM" },
];

/** Find the BCP-47 voice locale for the current i18n language. */
export function voiceLocaleFor(langCode) {
  const found = SUPPORTED_LANGUAGES.find((l) => l.code === langCode);
  return found?.voice || "en-AU";
}


const RTL_LANGS = new Set(["ar", "he", "fa", "ur"]);

function applyDirAndLang(code) {
  const html = document.documentElement;
  html.setAttribute("lang", code);
  html.setAttribute("dir", RTL_LANGS.has(code) ? "rtl" : "ltr");
}

/** Normalise a raw browser locale ("en-US", "zh-Hans-CN", "es-419") into
 *  one of our SUPPORTED_LANGUAGES codes. Prefer exact match, then
 *  region-specific Chinese scripts (Hans → zh-CN, Hant → zh-HK), then
 *  language-only match. */
function normaliseBrowserLang(raw) {
  if (!raw) return null;
  const lower = raw.toLowerCase();
  const supported = SUPPORTED_LANGUAGES.map((l) => l.code);
  // Exact match (case-insensitive).
  const exact = supported.find((c) => c.toLowerCase() === lower);
  if (exact) return exact;
  // Chinese script hints — most browsers now report zh-Hans-* / zh-Hant-*.
  if (lower.startsWith("zh")) {
    if (lower.includes("hant") || lower.includes("-hk") || lower.includes("-tw") || lower.includes("-mo")) return "zh-HK";
    return "zh-CN";
  }
  // Language-only prefix (en-US → en, es-419 → es, pt-BR → pt).
  const prefix = lower.split(/[-_]/)[0];
  const prefixMatch = supported.find((c) => c.toLowerCase() === prefix);
  if (prefixMatch) return prefixMatch;
  return null;
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: RESOURCES,
    fallbackLng: "en",
    // Cover en-US / en-GB / fr-FR / etc. — i18next will strip region for
    // resource lookup but the raw code stays available for the picker.
    supportedLngs: [
      "en", "es", "fr", "it", "de", "pt", "nl", "pl", "ru", "tr",
      "el", "ar", "he", "fa", "ur", "hi", "pa", "bn", "ta", "te",
      "zh-CN", "zh-HK", "ja", "ko", "vi", "th", "id", "ms", "tl", "my",
    ],
    nonExplicitSupportedLngs: false,
    load: "currentOnly",
    interpolation: { escapeValue: false },
    detection: {
      // iter255: Anonymous users ALWAYS default to English.
      //   Do NOT read `navigator` / browser Accept-Language — users
      //   were being auto-flipped to Spanish / French / etc. even
      //   after clicking English. Only honour an explicit choice the
      //   user has made (persisted to localStorage by the picker).
      order: ["localStorage"],
      caches: ["localStorage"],
      lookupLocalStorage: "bella_lang",
    },
  });

// If the detector picked a raw locale we don't ship UI for (e.g. `pt-BR`,
// `zh-Hans-CN`, `en-AU`) — collapse it to the closest SUPPORTED code so
// the picker highlights the right row AND our resources bundle loads.
try {
  const detected = i18n.language;
  const normalised = normaliseBrowserLang(detected);
  if (normalised && normalised !== detected) {
    i18n.changeLanguage(normalised);
  }
} catch (_e) { /* SSR / no window — ignore */ }

// Sync `<html lang>` + `<html dir>` (for RTL like Arabic) at startup and on
// every language change so screen readers + CSS logical properties Just Work.
applyDirAndLang(i18n.language || "en");
i18n.on("languageChanged", (code) => applyDirAndLang(code));

export default i18n;
